// Etcher — annotation layer for Fresco-powered viewers.
//
// Drop a `<div phx-hook="EtcherLayer" data-fresco-id="...">` into your
// template (or, more typically, use the `<Etcher.layer>` Phoenix
// component) and this hook will:
//
//   1. Look up the named Fresco viewer via `window.Fresco.onViewerReady`.
//   2. Append a pencil button to the viewer's nav column via the
//      `handle.appendNavButton(...)` extension point (Fresco 0.2+).
//   3. Toggle a bottom toolbar with drawing tools when the pencil is
//      clicked.
//   4. Render shapes as an SVG overlay anchored to image pixel
//      coordinates — pan/zoom of the viewer rescales them for free.
//   5. Emit LiveView events (`etcher:created`, `:updated`, `:deleted`,
//      `:selected`) at each lifecycle moment so the consumer's LiveView
//      decides what to persist.
//
// Wire it once in your `app.js`:
//
//   import "../../deps/fresco/priv/static/fresco.js"
//   import "../../deps/etcher/priv/static/etcher.js"
//
//   let liveSocket = new LiveSocket("/live", Socket, {
//     hooks: { ...window.FrescoHooks, ...window.EtcherHooks, ...colocatedHooks }
//   });

(function() {
  if (window.EtcherLoaded) return;
  window.EtcherLoaded = true;

  // ===========================================================================
  // Public extension surface — `window.Etcher`
  //
  // Consumer-facing API surface, all optional. None of these need to be
  // set for a basic install to work — they're hooks for host apps and
  // future libraries to customize Etcher without forking.
  //
  //   window.Etcher.tooltipSlots = { header, body, footer }
  //     Override tooltip content per-slot. See "Customizing the tooltip"
  //     in the README. Returning null falls back to Etcher's default.
  //
  //   window.Etcher.colorSwatches = [{ key, color, title }, ...]
  //     Replace the color picker palette. Falls back to the bundled
  //     pastel rainbow + white + black if not set.
  //
  //   window.Etcher.defaultColor = "#93c5fd"
  //     Initial active color. Falls back to the first swatch's color.
  //
  //   window.Etcher.escapeHtml(value) → escaped string
  //     Stable escape helper consumer slot impls can reuse.
  //
  //   window.Etcher.layerFor(frescoId) → { ... } | null
  //     Programmatic control surface for a mounted layer. Returns null
  //     for unknown ids. Every built-in button / nav button delegates
  //     to a method on this object so consumers can drive the layer
  //     headlessly (e.g. render their own toolbar):
  //
  //       mode:        getMode(), setMode(on), toggleMode()
  //       visibility:  isVisible(), setVisible(on), toggleVisible()
  //       tool:        getTool(), selectTool(toolKey), tools(),
  //                    exitDrawing()  // alias for selectTool(null)
  //       color:       getColor(), setColor(c), swatches()
  //       palette:     getColors(), setColors([hex,...]),
  //                    setSlotColor(i, hex)   // 5 editable slots
  //       history:     undo(), redo(), canUndo(), canRedo()
  //       shapes:      getShapes(), getShape(uuid),
  //                    selectShape(uuid), unselectShape(),
  //                    enterEditMode(uuid), exitEditMode(),
  //                    deleteShape(uuid)
  //
  // Lifecycle CustomEvents are dispatched on the layer's host element
  // (the `<div phx-hook="EtcherLayer">`), bubbling up so consumers can
  // listen at any ancestor:
  //   etcher:tooltip-show / -hide / -pin / -unpin
  //   etcher:mode-changed       { detail: { annotationMode } }
  //   etcher:tool-changed       { detail: { tool } }
  //   etcher:color-changed      { detail: { color } }
  //   etcher:colors-changed     { detail: { colors } }  // slot palette edited; also pushed to LiveView
  //   etcher:line-params-changed { detail: { line_params } }  // global stroke default edited; also pushed to LiveView
  //   etcher:visibility-changed { detail: { visible } }
  //   etcher:history-changed    { detail: { canUndo, canRedo } }
  // ===========================================================================

  window.Etcher = window.Etcher || {};
  window.Etcher.tooltipSlots = window.Etcher.tooltipSlots || {};

  // Registry of mounted layers, keyed by fresco_id. Populated on hook
  // mount, cleared on destroyed. `layerFor` reads this.
  var layerRegistry = {};

  window.Etcher.layerFor = function(frescoId) {
    var entry = layerRegistry[frescoId];
    return entry ? entry.api : null;
  };

  // Selectors for DOM nodes that own their own input. When a click /
  // pointerdown / dblclick lands inside one of these, Etcher's
  // doc-level hit-test handlers bail — otherwise the gesture would
  // shadow the owner's handler (a modal's button would never see its
  // own click, a tooltip's delete button would tear down its own
  // pin, etc.).
  //
  // The first five are Etcher's own internals. The last three cover
  // the established modal / dialog conventions so consumers don't
  // have to register their composer / settings / share-sheet
  // dialogs by hand:
  //
  //   dialog[open]       — native <dialog> shown via .showModal() / .show()
  //   .modal-open        — daisyUI / Bootstrap convention
  //   [role='dialog']    — ARIA-compliant custom modals
  //
  // Consumers shipping a non-conventional input-owning overlay can
  // append a selector via `Etcher.registerInputOwnerSelector(...)`.
  // The toolbar carries this many fixed, editable color slots. Clicking
  // a slot selects it; editing in the hue picker overwrites that slot's
  // color in place. The palette is seeded per-layer (the `data-colors`
  // attr, else `extensions.etcher.colors`, else the presets) and
  // persisted by the consumer through the `etcher:colors-changed` hook
  // — Etcher keeps no localStorage copy and never reorders the row.
  var COLOR_SLOTS = 5;

  var DEFAULT_INPUT_OWNER_SELECTORS = [
    ".etcher-handle",
    ".etcher-title-group",
    ".etcher-tooltip",
    ".etcher-toolbar",
    ".etcher-text-editor",
    ".etcher-popup",
    "dialog[open]",
    ".modal-open",
    "[role='dialog']"
  ];
  var inputOwnerSelectors = DEFAULT_INPUT_OWNER_SELECTORS.slice();
  var inputOwnerSelectorString = inputOwnerSelectors.join(", ");

  window.Etcher.registerInputOwnerSelector = function(selector) {
    if (typeof selector !== "string" || !selector.trim()) return;
    if (inputOwnerSelectors.indexOf(selector) !== -1) return;
    inputOwnerSelectors.push(selector);
    inputOwnerSelectorString = inputOwnerSelectors.join(", ");
  };

  function isInputOwner(target, overlayWrapper) {
    if (!target || typeof target.closest !== "function") return false;
    var match = target.closest(inputOwnerSelectorString);
    if (!match) return false;
    // A matched input-owner that contains our own overlay isn't a modal
    // "over us" — Etcher is rendered INSIDE that container (e.g. a daisyUI
    // `.modal-open` photo viewer). Don't gate: the cursor is genuinely over
    // our canvas, not over a separate modal layered above it.
    if (overlayWrapper && match.contains(overlayWrapper)) return false;
    return true;
  }

  // ===========================================================================
  // Icons (Heroicons, outline, 24×24, stroke="currentColor")
  // ===========================================================================

  // The select arrow, shared by the toolbar's cursor button and
  // `cursorToolCursor` below — one path, so the button and the pointer
  // cannot drift apart. Closed (Z), so it fills solid without a
  // silhouette underlay.
  var ARROW_PATH =
    '<path stroke-linecap="round" stroke-linejoin="round" d="M4 4 11.07 21l2.51-7.39L20.97 11.1 4 4Z"/>';

  // Where Etcher's buttons sit at the start of the viewer's nav (Fresco's
  // `slot`), in this order whichever attaches first: the pencil, the style
  // panel's chevron beside it, then the eye. A host adding its own pencil or
  // eye to the same nav (phoenix_kit does, on the finished picture) uses the
  // same numbers so the row reads the same in both modes.
  // Fresco's zoom control, open: the width of the corner it may need, and
  // how far up from the bottom a bar has to reach before it is in the way.
  var ZOOM_CONTROL_OPEN_W = 250;
  var ZOOM_CONTROL_REACH = 160;

  // A nav grows away from the corner it is pinned to, so a button shown
  // between that corner and the pencil pushes the pencil along. The chevron
  // only shows while annotating, so it goes on the pencil's far side and
  // the pencil stays put to be pressed again: after it in an ordinary nav,
  // before it in a mirrored row (`nav_reverse`), whose first button sits
  // furthest from the corner.
  // Point text's handle frame is never smaller than this on screen, and an
  // edge grabber needs a side at least this long to sit between its corners.
  var POINT_HANDLE_MIN_PX = 24;
  var POINT_HANDLE_COLOR = "#111111";
  // How near (screen px) the cursor comes to a point text's side before
  // that side's grabber shows.
  var POINT_EDGE_REACH_PX = 24;
  // The grip band along point text's outline while it is typed into, in
  // screen px outside and inside the box's edge.
  var POINT_FRAME_OUT_PX = 7;
  var POINT_FRAME_IN_PX = 4;
  var POINT_EDGE_MIN_PX = 44;

  var NAV_SLOTS = { chevronMirrored: -1, pencil: 0, chevron: 1, eye: 2 };

  var ICONS = {
    pencil:   '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"/></svg>',
    trash:    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>',
    paperclip:'<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"/></svg>',
    cursor:   '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">' + ARROW_PATH + '</svg>',
    undo:     '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 15 4 10l5-5"/><path d="M4 10h11a5 5 0 0 1 0 10h-4"/></svg>',
    redo:     '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 15l5-5-5-5"/><path d="M20 10H9a5 5 0 0 0 0 10h4"/></svg>',
    // Arrange (z-order). One metaphor across all four: the tinted square is
    // the object, the chevrons are the direction it travels through the
    // stack, and doubling them means "all the way" (⏭ against ▶). An
    // earlier pass used a horizontal arrow for the single steps, which read
    // as "move right" rather than "move a layer up".
    toFront:  '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.7" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="14" width="16" height="7" rx="2" fill="currentColor" fill-opacity="0.18"/><path d="M8 10.5 12 6.5l4 4"/><path d="M8 6 12 2l4 4"/></svg>',
    toBack:   '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.7" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="3" width="16" height="7" rx="2" fill="currentColor" fill-opacity="0.18"/><path d="M8 13.5 12 17.5l4-4"/><path d="M8 18 12 22l4-4"/></svg>',
    forward:  '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.7" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="12" width="16" height="9" rx="2" fill="currentColor" fill-opacity="0.18"/><path d="M8 8 12 4l4 4"/></svg>',
    backward: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.7" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="3" width="16" height="9" rx="2" fill="currentColor" fill-opacity="0.18"/><path d="M8 16 12 20l4-4"/></svg>',
    // Heroicons eye / eye-slash.
    eye:      '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/></svg>',
    eyeSlash: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 11.683a1.012 1.012 0 0 0 0 .639C3.423 16.49 7.36 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639a10.51 10.51 0 0 1-4.193 5.371M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"/></svg>',
    rectangle:'<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><rect x="4" y="6" width="16" height="12" rx="1.5"/></svg>',
    circle:   '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><circle cx="12" cy="12" r="7.5"/></svg>',
    polygon:  '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 3.5 21 9.5 18 20H6L3 9.5 12 3.5Z"/></svg>',
    // Freehand-selection glyph, mirroring what the tool produces: a drawn
    // half-circle arc with node dots at its start and end, the open side
    // closed by a faint dashed edge, and a half-transparent fill in the
    // enclosed region. Tilted a few degrees so it reads as a hand-drawn
    // stroke, not a geometric arc primitive.
    freehand: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><g transform="rotate(-10 12 12)"><path fill="currentColor" fill-opacity="0.25" stroke="none" d="M4.5 15.5A7.6 7.6 0 0 1 19.5 15.5Z"/><path stroke-opacity="0.45" stroke-dasharray="2.4 2" d="M4.5 15.5 19.5 15.5"/><path stroke-linecap="round" d="M4.5 15.5A7.6 7.6 0 0 1 19.5 15.5"/><circle cx="4.5" cy="15.5" r="1.5" fill="currentColor" stroke="none"/><circle cx="19.5" cy="15.5" r="1.5" fill="currentColor" stroke="none"/></g></svg>',
    // Felt-tip marker (body, tapered shoulder, nib) leaving an ink trail —
    // raw mark-making, as opposed to freehand's editable spline. Not a
    // paint brush and not a chisel highlighter, both of which read wrong.
    marker: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><g stroke-linecap="round" stroke-linejoin="round" transform="rotate(45 12 12)"><path d="M10 2h4a1 1 0 0 1 1 1v7h-6V3a1 1 0 0 1 1-1Z"/><path d="M9 10h6l-1 4h-4l-1-4Z"/><path d="M11.2 14h1.6v3h-1.6z"/></g><path stroke-linecap="round" d="M3 21c3-1.5 6-1.5 8-1"/></svg>',
    highlighter: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><g stroke-linecap="round" stroke-linejoin="round" transform="rotate(45 12 12)"><path d="M10 2h4a1 1 0 0 1 1 1v7h-6V3a1 1 0 0 1 1-1Z"/><path d="M9 10h6l-1 4h-4l-1-4Z"/><path d="M10.4 14h3.2v2.4h-3.2z"/></g><path stroke-linecap="round" stroke-width="4" opacity="0.45" d="M4 20.5h9"/></svg>',
    // Eraser wedge over a surface line — the trash can it replaced reads
    // as "delete row", not "rub shapes out by sweeping".
    eraser: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21"/><path stroke-linecap="round" stroke-linejoin="round" d="M22 21H7"/><path stroke-linecap="round" stroke-linejoin="round" d="m5 11 9 9"/></svg>',
    grabber: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M18 11V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2"/><path stroke-linecap="round" stroke-linejoin="round" d="M14 10V4a2 2 0 0 0-2-2 2 2 0 0 0-2 2v2"/><path stroke-linecap="round" stroke-linejoin="round" d="M10 10.5V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v8"/><path stroke-linecap="round" stroke-linejoin="round" d="m7 15-1.76-1.76a2 2 0 0 0-2.83 2.82l3.6 3.6C7.5 21.14 9.2 22 12 22h2a8 8 0 0 0 8-8V7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v3"/></svg>',
    // A 3x3 of dots for the background grid, and a square with anchor dots on
    // its corners for the connector anchors — each says what it switches.
    grid: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="6" cy="6" r="1.6"/><circle cx="12" cy="6" r="1.6"/><circle cx="18" cy="6" r="1.6"/><circle cx="6" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="18" cy="12" r="1.6"/><circle cx="6" cy="18" r="1.6"/><circle cx="12" cy="18" r="1.6"/><circle cx="18" cy="18" r="1.6"/></svg>',
    inkZoom: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="10" cy="10" r="6"/><path d="M14.5 14.5 20 20"/><path stroke-width="3" stroke-linecap="round" d="M7 10h6"/></svg>',
    // Two boxes centered on a shared dashed guide — the snap toggle.
    snap: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><line x1="12" y1="2.5" x2="12" y2="21.5" stroke-dasharray="2.4 2.2" stroke-opacity="0.6"/><rect x="5" y="5.5" width="14" height="5" rx="1.2"/><rect x="8" y="14" width="8" height="5" rx="1.2"/></svg>',
    // Two rows of dots — the drag handle every reorderable list uses.
    grip: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="9" cy="6" r="1.6"/><circle cx="15" cy="6" r="1.6"/><circle cx="9" cy="12" r="1.6"/><circle cx="15" cy="12" r="1.6"/><circle cx="9" cy="18" r="1.6"/><circle cx="15" cy="18" r="1.6"/></svg>',
    // Heroicons chevron-up / chevron-down — the style panel's size control.
    chevronUp:   '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5"/></svg>',
    // The style panel's switch in the viewer's nav: a window with the panel
    // down its right side — wide and filled at full size, a thin filled
    // strip for the one-column mode, an empty dashed edge when folded away.
    // It shows the state, rather than which way an arrow would move
    // something it is no longer next to.
    panelCompact: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.6" stroke="currentColor" aria-hidden="true"><rect x="3.75" y="4.5" width="16.5" height="15" rx="2.25"/><path d="M15.75 4.5v15"/><path fill="currentColor" stroke="none" d="M15.75 4.5h2.25a2.25 2.25 0 0 1 2.25 2.25v10.5a2.25 2.25 0 0 1-2.25 2.25h-2.25z"/></svg>',
    panelShown:  '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.6" stroke="currentColor" aria-hidden="true"><rect x="3.75" y="4.5" width="16.5" height="15" rx="2.25"/><path d="M9 4.5v15"/><path fill="currentColor" stroke="none" d="M9 4.5h9a2.25 2.25 0 0 1 2.25 2.25v10.5a2.25 2.25 0 0 1-2.25 2.25h-9z"/></svg>',
    panelHidden: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.6" stroke="currentColor" aria-hidden="true"><rect x="3.75" y="4.5" width="16.5" height="15" rx="2.25"/><path stroke-dasharray="2 2" d="M15.75 4.5v15"/></svg>',
    chevronDown: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/></svg>',
    // Heroicons cursor-arrow-rays — a pointer with rays coming off it, which
    // is what a laser pointer looks like when you are trying to draw one.
    pointer: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15.042 21.672 13.684 16.6m0 0-2.51 2.225.569-9.47 5.227 7.917-3.286-.672ZM12 2.25V4.5m5.834.166-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243-1.59-1.59"/></svg>',
    // Heroicons photo — framed picture with a mountain horizon and a sun.
    image:   '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"/></svg>',
    sliders: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"/></svg>',
    // Callout / leader line — small filled dot at the anchor, a thin
    // diagonal line, and a sample "T" at the text endpoint. Mimics
    // the blueprint-callout shape so the toolbar icon advertises what
    // the tool draws.
    // Callout — anchor dot at the bottom-left, leader line up to the
    // bottom-left of an underlined "Aa" label. Mirrors the shape the
    // tool now draws (leader + underline + text bbox) so the toolbar
    // button matches the on-canvas output.
    callout:  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="3.5" cy="20" r="1.5" fill="currentColor" stroke="none"/><path d="M4 19.5 L8.5 14 L21 14"/><text x="9" y="13" font-size="9.5" font-weight="700" fill="currentColor" stroke="none">Aa</text></svg>',
    // Text tool — a typeset "T", filled: a full-width bar with its ends
    // dropped and a stem standing on a foot. Two round-capped strokes read
    // as the ⊤ symbol (or an intersection), not as a letter.
    text:     '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true"><path d="M4 4H20V9H18.6L18.1 6.9H13.5V18.4H15.6V20H8.4V18.4H10.5V6.9H5.9L5.4 9H4Z"/></svg>',
    textbox:  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4.5" width="18" height="15" rx="2"/><path d="M7 9.5h10M7 13.5h6"/></svg>',
    // Dimension — horizontal shaft with V-arrows on both ends. Mirrors
    // the architectural dimension-line annotation the tool draws (line
    // + 2 arrows + black label sliding along the shaft).
    dimension:'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12 L19 12 M5 12 L8 9 M5 12 L8 15 M19 12 L16 9 M19 12 L16 15"/></svg>',
    // Line — bare diagonal stroke. The line annotation is the
    // arrow-and-label-free sibling of dimension: same two-endpoint
    // geometry, title rendered via the standard sibling-above-shape
    // path (not inline on the line).
    line:     '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 19 L19 5"/></svg>',
    // The line tool's diagonal with one head on it, so the pair reads as
    // what it is: the same stroke, pointed or not. The head's arms are the
    // shaft direction turned +/-25 deg, which is what `_vArrowPoints` draws
    // on the canvas — the button is a miniature of the shape, not a
    // separate drawing of an arrow.
    arrow:    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 19 L19 5 M19 5 L12.4 7.4 M19 5 L16.6 11.6"/></svg>',
    close:    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>',
    // Three horizontal dots — overflow / "more" trigger in the
    // compact mobile toolbar. Heroicons solid `EllipsisHorizontal`.
    paste: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.6" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1.5"/></svg>',
    duplicate: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.6" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M15 5.5A1.5 1.5 0 0 0 13.5 4h-8A1.5 1.5 0 0 0 4 5.5v8A1.5 1.5 0 0 0 5.5 15"/></svg>',
    // Stacked sheets: the arrange (layer order) button.
    layers:   '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.6" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6.43 9.75 2.25 12l4.18 2.25m0-4.5 5.57 3 5.57-3m-11.14 0L2.25 7.5 12 2.25l9.75 5.25-4.18 2.25m0 0L21.75 12l-4.18 2.25m0 0 4.18 2.25L12 21.75 2.25 16.5l4.18-2.25m11.14 0-5.57 3-5.57-3"/></svg>',
    more:     '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm8 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm6 2a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/></svg>',
    // Artist's palette — the colors `[⋯]` trigger (opens the picker).
    palette: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21a9 9 0 1 1 0-18 9 8 0 0 1 9 8 4.5 4 0 0 1-4.5 4H14a2 2 0 0 0-1 3.75A1.3 1.3 0 0 1 12 21Z"/><circle cx="7.5" cy="10.5" r="1.1" fill="currentColor" stroke="none"/><circle cx="12" cy="7.5" r="1.1" fill="currentColor" stroke="none"/><circle cx="16.5" cy="10.5" r="1.1" fill="currentColor" stroke="none"/></svg>'
  };

  // ===========================================================================
  // Styles
  // ===========================================================================

  var stylesInjected = false;

  // The SVG filters the selection CSS references. Filled shapes can't use
  // the stacked drop-shadow outline: a drop-shadow paints BEHIND the
  // element, and a semi-transparent fill lets it show through — selecting
  // a red shape visibly tinted its body toward blue. These filters build
  // the ring from the silhouette instead: saturate the alpha (so a
  // translucent fill counts as solid body), dilate it, subtract the
  // original — leaving a band strictly OUTSIDE the shape — and flood that
  // band blue under the untouched source. The interior never changes.
  //
  // They live in one hidden svg on <body>, never torn down: `filter:
  // url(#id)` resolves document-wide, and an id that disappears (an
  // overlay's own <defs> being removed with its page) doesn't degrade
  // gracefully — Chrome stops rendering the element entirely.
  function ensureSelectionFilters() {
    if (typeof document === "undefined") return;
    if (document.getElementById("etcher-selection-filters")) return;

    function outlineFilter(id, radius, opacity) {
      // The region must reach past the bbox: getBBox excludes stroke, so
      // the stroke's overhang plus the dilate radius live outside it, and
      // a region that stops at the bbox clips the ring off small shapes.
      var f = svgEl("filter", {
        id: id, x: "-60%", y: "-60%", width: "220%", height: "220%"
      });
      var solid = svgEl("feComponentTransfer", {
        in: "SourceAlpha", result: "solid"
      });
      solid.appendChild(svgEl("feFuncA", {
        type: "linear", slope: "255", intercept: "0"
      }));
      f.appendChild(solid);
      f.appendChild(svgEl("feMorphology", {
        in: "solid", operator: "dilate", radius: String(radius),
        result: "dilated"
      }));
      f.appendChild(svgEl("feComposite", {
        in: "dilated", in2: "solid", operator: "out", result: "band"
      }));
      f.appendChild(svgEl("feFlood", {
        "flood-color": "#3b82f6", "flood-opacity": String(opacity),
        result: "blue"
      }));
      f.appendChild(svgEl("feComposite", {
        in: "blue", in2: "band", operator: "in", result: "ring"
      }));
      var merge = svgEl("feMerge");
      merge.appendChild(svgEl("feMergeNode", { in: "ring" }));
      merge.appendChild(svgEl("feMergeNode", { in: "SourceGraphic" }));
      f.appendChild(merge);
      return f;
    }

    var holder = svgEl("svg", { id: "etcher-selection-filters", "aria-hidden": "true" });
    holder.style.position = "absolute";
    holder.style.width = "0";
    holder.style.height = "0";
    holder.style.overflow = "hidden";
    var defs = svgEl("defs");
    defs.appendChild(outlineFilter("etcher-selection-outline", 1.6, 1));
    defs.appendChild(outlineFilter("etcher-hover-outline", 1.4, 0.55));
    holder.appendChild(defs);
    document.body.appendChild(holder);
  }

  function injectStyles() {
    if (stylesInjected) return;
    stylesInjected = true;
    ensureSelectionFilters();

    var css = [
      // Every chrome surface paints on its own near-black background, in
      // both light and dark hosts — none of them follow the page theme. So
      // the foreground has to be pinned here rather than inherited: an icon
      // drawn in `currentColor` was otherwise picking up the HOST page's
      // text colour, which on a light page is near-black on near-black.
      // The dash icons hid this by hardcoding `stroke="#fff"`; the fill
      // icons, which do use `currentColor`, came out invisible.
      //
      // Pinned at the container rather than per-icon so anything added
      // later inherits a sane colour instead of rediscovering this.
      ".etcher-toolbar, .etcher-actionbar, .etcher-stylepanel,",
      ".etcher-popup, .etcher-style-trigger {",
      "  color: #fff;",
      "}",
      ".etcher-toolbar {",
      "  position: absolute; left: 50%; bottom: 16px;",
      "  transform: translateX(-50%); z-index: 11;",
      "  display: none; gap: 6px; padding: 6px;",
      "  background: rgba(0, 0, 0, 0.7); border-radius: 10px;",
      "  pointer-events: auto;",
      "}",
      // Squared off only where the auxiliary tier is resting on it, so the
      // two meet as one shape. Alone, the bar keeps its full radius.
      ".etcher-toolbar.is-stacked { border-radius: 4px 4px 10px 10px; }",
      // Strip mode: the scroll container IS the scrolling element, so
      // anchor the toolbar to the viewport instead of the container.
      // Otherwise it scrolls out of view with the content.
      ".etcher-toolbar[data-strip] {",
      "  position: fixed; bottom: 16px;",
      "}",
      // Same reasoning for the rest of the chrome. 0.4.1 fixed the tool bar;
      // the action bar, style panel and style trigger arrived later, were
      // stamped `data-strip`, and never got the rule — so they stayed
      // `absolute` and scrolled off the top of the chapter.
      ".etcher-actionbar[data-strip],",
      ".etcher-stylepanel[data-strip],",
      ".etcher-style-trigger[data-strip] {",
      "  position: fixed;",
      "}",
      // Strip mode + drawing tool active: suppress native scroll on
      // the strip container so iOS Safari hands every touchmove to
      // the app instead of claiming the gesture for the scroller.
      // Without this, finger-drawing on iOS commits a single
      // oversized shape spanning from finger-down to finger-up
      // (the OS classifies the gesture as scroll at touchstart,
      // before `pointerdown` runs, so `preventDefault` arrives too
      // late). Cursor mode (annotation mode + no drawing tool)
      // keeps `touch-action: auto` so the reader can still scroll
      // the chapter to reach existing shapes.
      ".etcher-strip-drawing { touch-action: none; }",
      // Vertex / midpoint handles + the actively-edited or actively-
      // dragging shape body all need `touch-action: none` for the
      // same reason as the draw-tool fix above: without it, the
      // iOS scroll classifier claims the gesture at `touchstart`
      // (before our `pointerdown` + `setPointerCapture` runs) and
      // the user's drag becomes a page scroll. Scoped to interactive
      // states only so static shapes don't block native scroll
      // past them.
      ".etcher-handle, .etcher-handle-midpoint, .etcher-grab,",
      ".etcher-shape.is-editing, .etcher-shape.is-moving {",
      "  touch-action: none;",
      "}",
      // The finger-sized half of an edit handle. Invisible always, and
      // inert unless the pointer is coarse — on a mouse the dot IS the
      // target, and a 44px disc around each one would swallow the shape
      // underneath and the handle next door.
      ".etcher-grab {",
      "  fill: transparent; stroke: none; pointer-events: none;",
      "}",
      "@media (pointer: coarse) {",
      "  .etcher-grab { pointer-events: all; cursor: grab; }",
      "}",
      ".etcher-toolbar.is-active { display: flex; }",
      ".etcher-toolbar button {",
      "  width: 36px; height: 36px;",
      "  display: inline-flex; align-items: center; justify-content: center;",
      "  border: none; padding: 0; cursor: pointer;",
      "  background: transparent; color: #fff; border-radius: 6px;",
      "  transition: background 120ms ease;",
      "}",
      ".etcher-toolbar button:hover { background: rgba(255, 255, 255, 0.12); }",
      ".etcher-toolbar button.is-selected { background: rgba(255, 255, 255, 0.24); }",
      ".etcher-toolbar button:disabled {",
      "  opacity: 0.35; cursor: not-allowed;",
      "}",
      ".etcher-toolbar button:disabled:hover { background: transparent; }",
      ".etcher-toolbar button:focus-visible {",
      "  outline: 2px solid rgba(255, 255, 255, 0.7); outline-offset: 1px;",
      "}",
      ".etcher-toolbar svg { width: 18px; height: 18px; }",
      // The line-parameters trigger (sliders) sits where the palette used
      // to be; give it a slightly larger glyph so it reads as a control.
      ".etcher-toolbar .etcher-more[data-more='params'] svg { width: 22px; height: 22px; }",
      ".etcher-toolbar .etcher-divider {",
      "  width: 1px; background: rgba(255, 255, 255, 0.2); margin: 4px 2px;",
      "}",
      // Color swatches — small circles inline in the toolbar. Picked
      // swatch gets a white ring so the choice is visible even when
      // the swatch is pastel-blue and the highlight is subtle.
      ".etcher-swatch {",
      "  width: 22px; height: 22px; border-radius: 6px;",
      "  border: 1px solid rgba(255, 255, 255, 0.4); padding: 0;",
      "  cursor: pointer; transition: transform 80ms ease;",
      "}",
      ".etcher-swatch:hover { transform: scale(1.15); }",
      ".etcher-swatch.is-selected {",
      "  box-shadow: 0 0 0 2px #fff, 0 0 0 4px rgba(0, 0, 0, 0.5);",
      "}",
      // The progressive-overflow `[⋯]` buttons. Hidden by default;
      // `_layoutToolbar` toggles `.is-active` when at least one
      // tool or swatch had to be collapsed into the popup. The
      // popups they trigger are positioned absolutely against the
      // toolbar's container.
      // Scoped under `.etcher-toolbar` so these beat the generic
      // `.etcher-toolbar button { display: inline-flex }` rule above
      // (0,2,0 / 0,3,0 vs 0,1,1) — otherwise the `[⋯]` trigger would
      // stay visible regardless of `.is-active` and open an empty popup
      // when nothing actually overflowed.
      ".etcher-toolbar .etcher-more {",
      "  display: none;",
      "}",
      ".etcher-toolbar .etcher-more.is-active {",
      "  display: inline-flex;",
      "}",
      // The params `[⋯]` (line thickness / opacity / dash) is a permanent
      // toolbar entry — always visible, independent of `.is-active`. (The
      // hue picker now opens from the swatches; the tools `[⋯]` stays
      // overflow-gated above.)
      ".etcher-toolbar .etcher-more[data-more='params'] {",
      "  display: inline-flex;",
      "}",
      // Secondary action bar — undo/redo/delete/duplicate/more, floating just
      // above the tool bar. Split out so the tool bar stays a row of tools
      // and nothing else: a first-time user reading it left-to-right meets
      // only things that change what they are about to draw.
      // The auxiliary tier: sits directly on top of the tool bar, rounded
      // only along its outer edge so the two meet as one shape rather than
      // two stacked pills. Lighter than the bar below it — tldraw does the
      // same, and it says "this belongs to what is selected" without needing
      // a label or a border.
      ".etcher-actionbar {",
      "  position: absolute; z-index: 11; display: none;",
      "  align-items: center; gap: 2px;",
      "  padding: 4px 6px;",
      "  border-radius: 10px 10px 4px 4px;",
      "  background: rgba(48, 48, 54, 0.86);",
      "  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);",
      "  pointer-events: auto;",
      "}",
      ".etcher-actionbar.is-active { display: flex; }",
      ".etcher-actionbar button {",
      "  width: 30px; height: 30px; display: inline-flex;",
      "  align-items: center; justify-content: center;",
      "  border: 0; background: none; color: #fff; cursor: pointer;",
      "  border-radius: 7px; padding: 0;",
      "}",
      ".etcher-actionbar button svg { width: 17px; height: 17px; }",
      ".etcher-actionbar button:hover:not(:disabled) {",
      "  background: rgba(255, 255, 255, 0.14);",
      "}",
      ".etcher-actionbar button:disabled { opacity: 0.35; cursor: default; }",
      // The arrange menu. Popup buttons are styled per `data-kind`, and this
      // kind had no rules — its buttons laid out at 0x0, so the menu opened
      // as an empty pill.
      ".etcher-actionbar .etcher-divider {",
      "  width: 1px; height: 18px; margin: 0 3px;",
      "  background: rgba(255, 255, 255, 0.2);",
      "}",
      // Style panel — colours and stroke settings, docked to the top-right.
      // Off the tool bar entirely: those are properties of what you draw,
      // not tools, and mixing the two is what made the old single bar hard
      // to scan. Mirrors tldraw's placement.
      ".etcher-stylepanel {",
      "  position: absolute; z-index: 11;",
      "  top: calc(var(--etcher-panel-anchor-top, 12px) + 40px);",
      "  right: var(--etcher-panel-anchor-right, 12px);",
      "  display: none; flex-direction: column; gap: 10px;",
      "  padding: 10px; border-radius: 12px;",
      "  background: rgba(0, 0, 0, 0.72);",
      "  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);",
      "  pointer-events: auto; width: 190px; max-width: calc(100vw - 24px);",
      "}",
      ".etcher-stylepanel.is-active { display: flex; }",
      // Three sizes, because the panel is in the way at different amounts
      // for different people. Full is everything. Compact keeps the two
      // things reached for constantly — the colour and the line type — and
      // drops the sliders, which are set once and left. Hidden is for
      // presenting, where the board is the point and the chrome is not.
      // Compact is a narrow vertical strip, not a shrunken panel: the colour
      // and the line type stacked in one column, everything else gone. The
      // point is to give the board back its width, which a panel merely a
      // bit smaller does not do.
      ".etcher-stylepanel[data-size=\"compact\"] {",
      "  width: auto; max-width: calc(100vw - 24px); padding: 6px; gap: 6px;",
      "}",
      // Sliders are set once and left; fills are a shape property rather than
      // something reached for mid-stroke. Both go.
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-marker-row { display: none; }",
      // …except the label size, which is a number you type rather than a
      // slider you need room to aim along — it fits the strip, and being
      // able to set one size everywhere is most of the point of having it.
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-font-row {",
      "  display: flex;",
      "}",
      // The row's caption has nowhere to go at this width; the input's
      // `title` carries the naming instead.
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-font-row .etcher-marker-row-head {",
      "  display: none;",
      "}",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-num {",
      "  width: 30px; padding: 0 2px; text-align: center; font-size: 11px;",
      "}",
      // (Native spinners are off for every .etcher-num in the base rules —
      // the compact strip used to carry its own copy of that, from when the
      // full panel still showed them.)
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-stylepanel-divider { display: none; }",
      // Everything is off in compact, then the chosen parts are put back.
      // Written this way round so a part nobody asked for cannot appear
      // through some other rule winning on source order — which is exactly
      // how the fills stayed visible the first time.
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-stylepanel-swatches,",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-marker-dash {",
      "  display: none;",
      "}",
      ".etcher-stylepanel[data-size=\"compact\"][data-compact-parts~=\"colors\"] .etcher-stylepanel-swatches {",
      "  display: grid; grid-template-columns: 1fr; gap: 6px;",
      "}",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-swatch {",
      "  width: 30px; height: 30px;",
      "}",
      // Compact is a one-column strip; the labelled row would set its width.
      // Keep just the chip with the "A" in it, sized like the swatches above.
      // One column, so the pair stacks: a square for the label's colour and
      // a square for what sits behind it.
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-label-row {",
      "  flex-direction: column; gap: 6px;",
      "}",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-label-swatch {",
      "  flex: 0 0 auto;",
      "  width: 30px; height: 30px; padding: 0; gap: 0; justify-content: center;",
      "}",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-label-swatch-text {",
      "  display: none;",
      "}",
      // `:not(.etcher-marker-fill)` because the fill row carries the dash
      // row's class too. Turning it off in a later rule does not work: the
      // dash selector has an extra attribute on it and therefore wins on
      // specificity regardless of order, so asking for line types was also
      // showing fills. Excluding it here means each part answers only for
      // itself.
      ".etcher-stylepanel[data-size=\"compact\"][data-compact-parts~=\"dash\"] .etcher-marker-dash:not(.etcher-marker-fill) {",
      "  display: grid; grid-template-columns: 1fr; gap: 6px;",
      "}",
      ".etcher-stylepanel[data-size=\"compact\"][data-compact-parts~=\"fills\"] .etcher-marker-fill {",
      "  display: grid; grid-template-columns: 1fr; gap: 6px;",
      "}",
      ".etcher-stylepanel[data-size=\"hidden\"] { display: none; }",
      // Hung from the top-left (`panel_offset` with `left`). The popup on a
      // narrow container positions itself inline and is unaffected.
      ".etcher-stylepanel[data-side=\"left\"] {",
      "  right: auto; left: var(--etcher-panel-anchor-left, 12px);",
      "}",
      // The chevron that cycles the three. Pinned above the panel rather
      // than attached to its edge so it stays exactly where it was when the
      // panel goes away — a control that moves when you use it is one you
      // have to hunt for the second time.
      // The nav pencil while annotation mode is on. The button itself is
      // Fresco's chrome; this class is Etcher's, toggled by _syncNavPencil.
      // The style panel's switch in the nav while the panel shows — lit the
      // same way, so "on" looks like "on" across the row.
      ".etcher-pencil-active, .etcher-pencil-active:hover,",
      ".etcher-nav-on, .etcher-nav-on:hover {",
      "  background: #3b82f6 !important; color: #fff !important;",
      "}",
      ".etcher-panel-toggle {",
      "  position: absolute; z-index: 12;",
      "  top: var(--etcher-panel-anchor-top, 12px);",
      "  right: var(--etcher-panel-anchor-right, 12px);",
      "  display: none; align-items: center; justify-content: center;",
      "  width: 30px; height: 30px; padding: 0; border: 0; cursor: pointer;",
      "  border-radius: 8px; background: rgba(0, 0, 0, 0.72); color: #fff;",
      "  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3); pointer-events: auto;",
      "}",
      ".etcher-panel-toggle.is-active { display: inline-flex; }",
      ".etcher-panel-toggle:hover { background: rgba(0, 0, 0, 0.85); }",
      ".etcher-panel-toggle svg { width: 18px; height: 18px; display: block; }",
      // Customise dialog — what rides on the toolbar, in what order, and
      // what the compact style strip keeps.
      ".etcher-customise-backdrop {",
      "  position: absolute; inset: 0; z-index: 20; display: none;",
      "  align-items: center; justify-content: center;",
      "  background: rgba(0, 0, 0, 0.45); pointer-events: auto;",
      "}",
      ".etcher-customise-backdrop.is-open { display: flex; }",
      ".etcher-customise {",
      "  width: 360px; max-width: calc(100% - 32px);",
      "  max-height: calc(100% - 48px); overflow-y: auto;",
      "  padding: 16px; border-radius: 14px;",
      "  background: rgba(24, 24, 27, 0.98); color: #fff;",
      "  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);",
      "  font: 500 13px ui-sans-serif, system-ui, -apple-system, sans-serif;",
      "}",
      ".etcher-customise h3 {",
      "  margin: 0 0 4px; font-size: 14px; font-weight: 600;",
      "}",
      ".etcher-customise p {",
      "  margin: 0 0 12px; color: rgba(255, 255, 255, 0.6); font-size: 12px;",
      "}",
      ".etcher-customise-list { display: flex; flex-direction: column; gap: 4px; }",
      ".etcher-customise-row {",
      "  display: flex; align-items: center; gap: 10px;",
      "  padding: 7px 8px; border-radius: 8px;",
      "  background: rgba(255, 255, 255, 0.06); cursor: pointer;",
      "}",
      ".etcher-customise-row span { flex: 1; min-width: 0; }",
      ".etcher-customise-row input { flex: none; cursor: pointer; }",
      // The two trays are drawn as the toolbar is: a dark rounded bar of
      // 36px icon buttons. The point of the dialog is to show what the bar
      // will look like, so it shows a bar.
      ".etcher-customise-tray {",
      "  display: flex; flex-wrap: wrap; gap: 4px; min-height: 44px;",
      "  padding: 4px; border-radius: 10px;",
      "  background: rgba(0, 0, 0, 0.45);",
      "  border: 1px dashed rgba(255, 255, 255, 0.14);",
      "}",
      // Somewhere to aim at when a tray is empty, rather than a 4px sliver.
      ".etcher-customise-tray:empty::after {",
      "  content: \"Drag tools here\"; display: flex; align-items: center;",
      "  padding: 0 8px; color: rgba(255, 255, 255, 0.35); font-size: 12px;",
      "}",
      ".etcher-customise-chip {",
      "  width: 36px; height: 36px; border-radius: 8px; cursor: grab;",
      "  display: inline-flex; align-items: center; justify-content: center;",
      "  background: rgba(255, 255, 255, 0.08); color: #fff;",
      "  border: 0; padding: 0; box-sizing: border-box; flex: none;",
      "  user-select: none; -webkit-user-select: none; touch-action: none;",
      "}",
      ".etcher-customise-chip:hover { background: rgba(255, 255, 255, 0.18); }",
      ".etcher-customise-chip svg { width: 18px; height: 18px; display: block; }",
      // Sortable\'s own classes: the placeholder left behind, and the chip
      // being carried. The placeholder is an outline rather than a faded
      // copy, so the gap reads as "it will land here".
      ".etcher-customise-chip.is-ghost {",
      "  background: transparent; outline: 2px dashed #3b82f6; opacity: 1;",
      "}",
      ".etcher-customise-chip.is-ghost svg { opacity: 0.25; }",
      ".etcher-customise-chip.is-chosen { cursor: grabbing; }",
      // The copy that follows the pointer under `forceFallback`. Sortable
      // appends it to the body, so it needs a z-index of its own rather than
      // inheriting the dialog\'s.
      ".etcher-customise-chip.is-flying {",
      "  z-index: 30; opacity: 0.9; cursor: grabbing;",
      "  background: rgba(60, 60, 66, 0.98);",
      "  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);",
      "}",
      ".etcher-customise-actions {",
      "  display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px;",
      "}",
      ".etcher-customise-actions button {",
      "  padding: 7px 14px; border: 0; border-radius: 8px; cursor: pointer;",
      "  font: 600 13px ui-sans-serif, system-ui, -apple-system, sans-serif;",
      "  background: rgba(255, 255, 255, 0.12); color: #fff;",
      "}",
      ".etcher-customise-actions button[data-primary] {",
      "  background: #3b82f6;",
      "}",
      ".etcher-customise-actions button:hover { filter: brightness(1.15); }",
      // Narrow container: the docked panel would eat the canvas, so it
      // becomes a popup over the tool bar, opened by `.etcher-style-trigger`.
      // JS owns the offsets (the tool bar's height and width both move), so
      // only the things CSS can settle live here.
      ".etcher-stylepanel[data-compact] {",
      "  top: auto; right: auto; width: 232px;",
      "  max-width: calc(100% - 24px); max-height: calc(100% - 96px);",
      "  overflow-y: auto;",
      "}",
      ".etcher-stylepanel[data-compact]:not(.is-open) { display: none; }",
      // A popup ignores the size pref for VISIBILITY. That pref answers
      // "how much of the docked panel do I want", and a popup is not docked
      // — it is opened deliberately, one press at a time. Without this,
      // someone who hid the panel on a desktop and then opened the board on
      // a phone would press the style button and get nothing, with the
      // chevron that would have undone it no longer on screen there.
      ".etcher-stylepanel[data-compact].is-open { display: flex; }",
      ".etcher-style-trigger {",
      "  position: absolute; z-index: 11; display: none;",
      "  align-items: center; justify-content: center;",
      "  padding: 0; border: 0; cursor: pointer; border-radius: 10px;",
      "  background: rgba(0, 0, 0, 0.7); color: #fff;",
      "  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3); pointer-events: auto;",
      "}",
      ".etcher-style-trigger.is-active { display: inline-flex; }",
      ".etcher-style-trigger.is-open { background: rgba(255, 255, 255, 0.22); }",
      // The stroke previews the active colour, so the button says what it
      // opens without needing a label next to a canvas.
      ".etcher-style-trigger svg { display: block; }",
      // Status line. Top-centre: the tool bar, action bar and style panel
      // own every other edge, and it must not cover the thing being worked
      // on. Never a pointer target — it reports, it doesn't ask.
      ".etcher-toast {",
      "  position: absolute; top: 16px; left: 50%; z-index: 14;",
      "  transform: translateX(-50%) translateY(-6px);",
      "  padding: 8px 14px; border-radius: 999px;",
      "  background: rgba(0, 0, 0, 0.78); color: #fff;",
      "  font: 500 13px ui-sans-serif, system-ui, -apple-system, sans-serif;",
      "  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);",
      "  pointer-events: none; opacity: 0;",
      "  transition: opacity 140ms ease, transform 140ms ease;",
      "}",
      ".etcher-toast.is-open {",
      "  opacity: 1; transform: translateX(-50%) translateY(0);",
      "}",
      // Link card chrome: the `⋯` in the card's top-right and its menu.
      ".etcher-link-menu {",
      "  position: absolute; z-index: 12; width: 30px; height: 30px;",
      "  display: inline-flex; align-items: center; justify-content: center;",
      "  padding: 0; border: 0; border-radius: 8px; cursor: pointer;",
      "  background: rgba(0, 0, 0, 0.6); color: #fff; pointer-events: auto;",
      "}",
      ".etcher-link-menu:hover { background: rgba(0, 0, 0, 0.78); }",
      ".etcher-link-popup {",
      "  position: absolute; z-index: 12; width: 190px;",
      "  display: flex; flex-direction: column; padding: 4px;",
      "  border-radius: 10px; background: rgba(0, 0, 0, 0.82);",
      "  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35); pointer-events: auto;",
      "}",
      ".etcher-link-popup button {",
      "  border: 0; background: none; color: #fff; cursor: pointer;",
      "  text-align: left; padding: 8px 10px; border-radius: 6px;",
      "  font: 500 13px ui-sans-serif, system-ui, -apple-system, sans-serif;",
      "}",
      ".etcher-link-popup button:hover { background: rgba(255, 255, 255, 0.14); }",
      ".etcher-link-editor {",
      "  position: absolute; z-index: 13; pointer-events: auto;",
      "}",
      ".etcher-link-editor input {",
      "  width: 100%; box-sizing: border-box; padding: 8px 10px;",
      "  border-radius: 8px; border: 2px solid #3b82f6; outline: none;",
      "  background: #fff; color: #111;",
      "  font: 500 13px ui-sans-serif, system-ui, -apple-system, sans-serif;",
      "}",
      ".etcher-stylepanel-swatches {",
      "  display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px;",
      "}",
      // Rounded squares, not pills: one tile per colour, square like the
      // chips beside them.
      ".etcher-stylepanel .etcher-swatch { width: 100%; height: auto; aspect-ratio: 1 / 1; border-radius: 8px; }",
      // The label-colour row: full menu width under the palette - a colour
      // chip (the "A" in the remembered colour) beside a text label, so it
      // reads as "the colour labels get", not as a sixth palette slot.
      // The two label colours share one row: what the label is written in,
      // and what it is written ON. A flex row inside the swatch grid rather
      // than two grid cells, because the grid is five columns wide and
      // halves of five are not columns.
      ".etcher-label-row {",
      "  grid-column: 1 / -1;",
      "  display: flex; gap: 6px;",
      "}",
      ".etcher-label-swatch {",
      "  flex: 1 1 0; min-width: 0;",
      "  display: flex; align-items: center; gap: 8px;",
      "  height: 28px; padding: 0 6px;",
      "  border: 0; border-radius: 8px; cursor: pointer;",
      "  background: rgba(255, 255, 255, 0.08);",
      "}",
      // The plate chip previews the PAIR — the label's own colour written
      // on the plate colour — since contrast is a property of the two
      // together and neither swatch can show it alone.
      ".etcher-label-bg-chip {",
      "  border-style: solid; border-color: rgba(255, 255, 255, 0.35);",
      "}",
      // Off: back to the dashed empty chip the text swatch uses for \"none
      // picked\", so the two read the same way when neither is set.
      ".etcher-label-bg-chip.is-off {",
      "  border-style: dashed; background: none;",
      "}",
      ".etcher-label-swatch:hover { background: rgba(255, 255, 255, 0.16); }",
      ".etcher-label-swatch-chip {",
      "  display: inline-flex; align-items: center; justify-content: center;",
      "  width: 22px; height: 22px; border-radius: 6px;",
      "  border: 1px dashed rgba(255, 255, 255, 0.35);",
      "}",
      ".etcher-label-swatch-glyph {",
      "  font: 700 13px/1 ui-sans-serif, system-ui, sans-serif;",
      "  text-decoration: underline; text-underline-offset: 2px;",
      "}",
      ".etcher-label-swatch-text {",
      "  font: 500 12px/1 ui-sans-serif, system-ui, sans-serif;",
      "  color: rgba(255, 255, 255, 0.85);",
      "}",
      // The params block is the existing popup, re-parented and stripped of
      // its floating chrome so it reads as a section of the panel.
      // Beats `.etcher-popup[data-kind="params"]`'s own fixed width, which
      // was sized for a floating popup and overflows the panel otherwise.
      ".etcher-stylepanel .etcher-popup,",
      ".etcher-stylepanel .etcher-popup[data-kind=\"params\"] {",
      "  position: static; display: flex; flex-direction: column;",
      "  width: 100%; min-width: 0; padding: 0; gap: 8px;",
      "  background: none; box-shadow: none; z-index: auto;",
      "}",
      ".etcher-stylepanel .etcher-popup input[type=\"range\"] {",
      "  width: 100%; min-width: 0;",
      "}",
      // The dash choices are a row of equal cells rather than a wrapping
      // strip, so they can't push past the panel edge.
      ".etcher-stylepanel .etcher-marker-dash {",
      "  display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px;",
      "}",
      ".etcher-stylepanel .etcher-marker-dash > * { width: 100%; min-width: 0; }",
      // Five corner choices: sharp, then the four shaped kinds.
      ".etcher-stylepanel .etcher-corner-row { grid-template-columns: repeat(5, 1fr); }",
      ".etcher-corner-row svg { width: 20px; height: 14px; }",
      ".etcher-stylepanel .etcher-corner-row { margin: 2px 0; }",
      ".etcher-stylepanel .etcher-corner-row button { height: 30px; }",
      // The choice buttons: one slim row, each a small square showing its
      // current pick with a caret, opening a pop-up of the options.
      ".etcher-stylepanel .etcher-in-chips { display: none !important; }",
      // The compact strip: the same buttons, stacked as small squares with
      // just their icon, their pop-ups opening beside the strip. Its parts
      // setting still decides whether line type and fill are there; opacity,
      // a slider, stays out of the strip as it always has.
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-chips { flex-direction: column; gap: 6px; }",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-chip { flex: 0 0 auto; width: 30px; height: 30px; padding: 0; }",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-chip-face b,",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-chip[data-chip=\"opacity\"],",
      ".etcher-stylepanel[data-size=\"compact\"]:not([data-compact-parts~=\"dash\"]) .etcher-chip[data-chip=\"stroke\"],",
      ".etcher-stylepanel[data-size=\"compact\"]:not([data-compact-parts~=\"fills\"]) .etcher-chip[data-chip=\"fill\"] { display: none !important; }",
      // A strip button's caption appears beside it on hover, a small tag
      // pointing at what the square is for; hidden otherwise, and while
      // its pop-up is open (the pop-up says it).
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-chip { position: relative; overflow: visible; }",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-chip-caption {",
      "  display: none; position: absolute; top: 50%; right: calc(100% + 8px); transform: translateY(-50%);",
      "  padding: 5px 8px; border-radius: 6px; background: rgba(24, 24, 27, 0.97); color: #fff;",
      "  font-size: 11px; opacity: 1; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35); z-index: 14;",
      "}",
      ".etcher-stylepanel[data-size=\"compact\"][data-side=\"left\"] .etcher-chip-caption { right: auto; left: calc(100% + 8px); }",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-chip:hover:not(.is-open) .etcher-chip-caption { display: block; }",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-chip-pop { left: auto; right: calc(100% + 8px); width: 190px; box-sizing: border-box; }",
      ".etcher-stylepanel[data-size=\"compact\"][data-side=\"left\"] .etcher-chip-pop { right: auto; left: calc(100% + 8px); }",
      // Inside a pop-up the strip's own hiding rules stand down: the
      // controls lay out as they do in the full panel.
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-chip-pop .etcher-marker-row { display: flex !important; }",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-chip-pop .etcher-marker-dash { display: grid !important; grid-template-columns: repeat(4, 1fr) !important; }",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-chip-pop .etcher-corner-row { grid-template-columns: repeat(5, 1fr) !important; }",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-chip-pop .etcher-align-row { grid-template-columns: repeat(3, 1fr) !important; }",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-chip-pop .etcher-label-section { display: flex !important; }",
      // One row, always: the buttons share its width, growing to fill it
      // and shrinking (never wrapping) when four are up.
      ".etcher-chips { display: flex; flex-wrap: nowrap; gap: 6px; }",
      ".etcher-chip {",
      "  flex: 1 1 0; min-width: 0; height: 42px; padding: 4px 2px 3px; overflow: hidden;",
      "  flex-direction: column; gap: 3px;",
      "  display: inline-flex; align-items: center; justify-content: center; gap: 4px;",
      "  border: 0; border-radius: 8px; cursor: pointer; color: #fff;",
      "  background: rgba(255, 255, 255, 0.08); transition: background 120ms ease;",
      "}",
      ".etcher-chip:hover { background: rgba(255, 255, 255, 0.16); }",
      ".etcher-chip.is-open { background: rgba(255, 255, 255, 0.24); }",
      ".etcher-chip-face { display: inline-flex; align-items: center; gap: 5px; pointer-events: none; }",
      ".etcher-chip-face svg { max-width: 20px; max-height: 16px; }",
      ".etcher-chip-face b { font: 600 12px/1 ui-sans-serif, system-ui, sans-serif; font-variant-numeric: tabular-nums; }",
      ".etcher-chip-caption { font: 500 9.5px/1 ui-sans-serif, system-ui, sans-serif; letter-spacing: 0.01em; opacity: 0.7; white-space: nowrap; pointer-events: none; }",
      ".etcher-chip:hover .etcher-chip-caption, .etcher-chip.is-open .etcher-chip-caption { opacity: 0.95; }",
      ".etcher-chip-aa { font: 600 12px/1 ui-sans-serif, system-ui, sans-serif; opacity: 0.75; }",
      ".etcher-chip-pop {",
      "  position: absolute; left: 8px; right: 8px; z-index: 13; display: none;",
      "  flex-direction: column; gap: 10px; padding: 12px;",
      "  background: rgba(24, 24, 27, 0.97); border-radius: 10px;",
      "  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45); color: #fff;",
      "}",
      ".etcher-chip-pop.is-open { display: flex; }",
      ".etcher-chip-pop-head { display: flex; justify-content: space-between; font-size: 11px; opacity: 0.85; }",
      ".etcher-chip-pop-grid { display: grid; gap: 4px; }",
      ".etcher-chip-pop-grid button {",
      "  height: 30px; min-width: 0; border: 1px solid rgba(255, 255, 255, 0.2);",
      "  background: transparent; border-radius: 6px; cursor: pointer; color: #fff;",
      "  display: inline-flex; align-items: center; justify-content: center;",
      "}",
      ".etcher-chip-pop-grid button:hover { background: rgba(255, 255, 255, 0.12); }",
      ".etcher-chip-pop-grid button.is-selected { background: rgba(255, 255, 255, 0.22); border-color: rgba(255, 255, 255, 0.6); }",
      ".etcher-chip-pop-grid svg { max-width: 22px; max-height: 18px; }",
      ".etcher-chip-pop input[type=\"range\"] { width: 100%; }",
      // The thickness readout, typeable: a faint box on hover says so, and
      // the field that stands in while typing is the same size and type.
      ".etcher-num-typeable { cursor: text; border-radius: 4px; padding: 1px 4px; margin: -1px -4px; min-width: 2ch; text-align: right; outline: none; }",
      ".etcher-num-typeable:hover, .etcher-num-typeable:focus-visible { background: rgba(255, 255, 255, 0.14); }",
      ".etcher-num-field {",
      "  width: 4ch; box-sizing: content-box; padding: 1px 4px; margin: -1px -4px; border: 0; border-radius: 4px;",
      "  background: rgba(255, 255, 255, 0.92); color: #111; text-align: right; outline: none;",
      "  order: 3; font: 600 12px/1 ui-sans-serif, system-ui, sans-serif; font-variant-numeric: tabular-nums;",
      "}",
      // Thickness and label size, one slim line each: icon, control, value.
      ".etcher-inline-icon { display: none; }",
      ".etcher-stylepanel:not([data-size=\"compact\"]) .etcher-inline-row {",
      "  display: grid; grid-template-columns: 22px 1fr auto; align-items: center; gap: 8px;",
      "}",
      ".etcher-stylepanel:not([data-size=\"compact\"]) .etcher-inline-row .etcher-marker-row-head { display: contents; }",
      ".etcher-stylepanel:not([data-size=\"compact\"]) .etcher-inline-row .etcher-marker-row-head > span:not(.etcher-inline-icon) { display: none; }",
      ".etcher-stylepanel:not([data-size=\"compact\"]) .etcher-inline-row .etcher-marker-row-head > span:nth-child(3) {",
      "  display: inline; order: 3; min-width: 22px; text-align: right;",
      "  font: 600 12px/1 ui-sans-serif, system-ui, sans-serif; font-variant-numeric: tabular-nums; opacity: 0.9;",
      "}",
      ".etcher-stylepanel:not([data-size=\"compact\"]) .etcher-inline-row input[type=\"range\"] { order: 2; }",
      ".etcher-stylepanel:not([data-size=\"compact\"]) .etcher-inline-icon {",
      "  display: inline-flex; align-items: center; justify-content: center; color: #fff; opacity: 0.8; order: 0;",
      "}",
      ".etcher-stylepanel:not([data-size=\"compact\"]) .etcher-font-row.etcher-inline-row { grid-template-columns: 22px 1fr; }",
      ".etcher-stylepanel:not([data-size=\"compact\"]) .etcher-inline-row .etcher-stepper { order: 2; }",
      ".etcher-stylepanel:not([data-size=\"compact\"]) .etcher-inline-row .etcher-step,",
      ".etcher-stylepanel:not([data-size=\"compact\"]) .etcher-inline-row .etcher-num { height: 26px; }",
      ".etcher-stylepanel:not([data-size=\"compact\"]) .etcher-inline-row .etcher-step { width: 26px; border-color: rgba(255, 255, 255, 0.18); }",
      ".etcher-stylepanel:not([data-size=\"compact\"]) .etcher-inline-row .etcher-num { border-color: rgba(255, 255, 255, 0.18); }",
      // The Behind chip's quick row in the colour popup.
      ".etcher-bg-choices { display: flex; flex-wrap: wrap; gap: 6px; width: 100%; padding-bottom: 6px; border-bottom: 1px solid rgba(255, 255, 255, 0.14); }",
      ".etcher-bg-none { flex: 1 1 auto; height: 28px; padding: 0 10px; border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 7px; cursor: pointer; color: #fff;",
      "  background: transparent; display: inline-flex; align-items: center; gap: 8px; font: 500 12px ui-sans-serif, system-ui, sans-serif; }",
      ".etcher-bg-none:hover { background: rgba(255, 255, 255, 0.1); }",
      ".etcher-bg-none.is-selected { background: rgba(255, 255, 255, 0.2); border-color: rgba(255, 255, 255, 0.6); }",
      // The thin rule between the panel's groups.
      ".etcher-panel-sep { height: 1px; background: rgba(255, 255, 255, 0.14); margin: 2px 0; flex: 0 0 auto; }",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-panel-sep { display: none; }",
      // Horizontal label alignment is three choices, not four.
      ".etcher-stylepanel .etcher-align-row {",
      "  grid-template-columns: repeat(3, 1fr);",
      "}",
      ".etcher-label-section {",
      "  display: flex; flex-direction: column; gap: 4px;",
      "}",
      ".etcher-params-label {",
      "  color: #fff; font-size: 11px; opacity: 0.85;",
      "}",
      ".etcher-stylepanel-divider {",
      "  height: 1px; background: rgba(255, 255, 255, 0.18);",
      "}",
      // The live sample beside a thickness or size control while it is
      // being changed: the stroke or the letters at the size the canvas
      // will draw them, so the change is seen, not guessed.
      ".etcher-value-preview {",
      "  position: absolute; z-index: 40; pointer-events: none;",
      "  display: flex; align-items: center; justify-content: center;",
      "  min-width: 96px; min-height: 44px; max-width: 220px; padding: 10px 14px; box-sizing: border-box;",
      "  background: #fff; color: #111; border-radius: 10px;",
      "  box-shadow: 0 6px 20px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,0,0,0.08);",
      "  opacity: 0; transform: translateX(4px); transition: opacity 120ms ease, transform 120ms ease;",
      "}",
      ".etcher-value-preview.is-on { opacity: 1; transform: none; }",
      ".etcher-value-preview::after {",
      "  content: ''; position: absolute; top: calc(var(--etcher-pv-arrow, 50%) - 7px); width: 14px; height: 14px;",
      "  background: #fff; transform: rotate(45deg); box-shadow: 2px -2px 3px rgba(0,0,0,0.06);",
      "}",
      ".etcher-value-preview[data-side='left']::after { right: -6px; }",
      ".etcher-value-preview[data-side='right']::after { left: -6px; box-shadow: -2px 2px 3px rgba(0,0,0,0.06); }",
      ".etcher-value-preview svg { display: block; overflow: visible; }",
      ".etcher-value-preview .etcher-pv-text { font-weight: 500; line-height: 1.1; white-space: nowrap; font-family: ui-sans-serif, system-ui, -apple-system, sans-serif; }",
      ".etcher-value-preview .etcher-pv-note { position: absolute; bottom: 3px; right: 8px; font: 500 9px/1 ui-sans-serif, system-ui, sans-serif; color: #888; }",
      ".etcher-popup {",
      "  position: absolute; z-index: 12; display: none;",
      "  background: rgba(0, 0, 0, 0.85); border-radius: 10px;",
      "  padding: 6px; gap: 6px; pointer-events: auto;",
      "  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);",
      "}",
      ".etcher-popup.is-open { display: flex; flex-wrap: wrap; }",
      // Tools popup: 5-col grid so 9 tools + cursor fit in 2 rows.
      // Tools popup: a 4-column grid, the shape-picker shape users expect
      // from every other canvas app. Sized so the extras land in tidy rows
      // rather than a ragged wrap.
      ".etcher-popup[data-kind=\"tools\"] {",
      "  display: none; width: auto;",
      "  grid-template-columns: repeat(4, 36px);",
      "}",
      ".etcher-popup[data-kind=\"tools\"].is-open { display: grid; }",
      // Dividers and the history/arrange rows span the grid rather than
      // eating a single cell.
      ".etcher-popup[data-kind=\"tools\"] .etcher-popup-divider {",
      "  grid-column: 1 / -1; height: 1px; margin: 4px 0;",
      "  background: rgba(255, 255, 255, 0.18);",
      "}",
      // Colors popup: row of preset swatches + recents row + custom
      // picker. Fixed width so the hue ring lays out predictably and
      // the popup doesn't jitter when recents toggle visibility.
      ".etcher-popup[data-kind=\"colors\"] {",
      "  width: 192px; align-items: center;",
      "}",
      // Marker style popup: stacked rows (weight slider, opacity slider, dash
      // picker) opened by re-clicking the active marker tool button.
      ".etcher-popup[data-kind=\"marker\"], .etcher-popup[data-kind=\"params\"] {",
      "  width: 200px; flex-direction: column; align-items: stretch;",
      "  gap: 10px; padding: 10px;",
      "}",
      ".etcher-marker-row { display: flex; flex-direction: column; gap: 4px; }",
      // The font row's number box. Full width and the same 30px height as
      // the dash buttons below it, so the row carries the same weight as
      // every other control rather than reading as an afterthought tucked
      // into a corner.
      ".etcher-num {",
      "  width: 100%; box-sizing: border-box; height: 30px;",
      "  padding: 0 8px; border-radius: 6px;",
      "  border: 1px solid rgba(255, 255, 255, 0.25);",
      "  background: transparent; color: #fff;",
      "  font: 500 13px ui-sans-serif, system-ui, sans-serif;",
      "  transition: background 120ms ease, border-color 120ms ease;",
      "}",
      // No native spinners, anywhere. The stepper's − / + beside the box
      // ARE the increase / decrease, so the arrows a desktop browser draws
      // inside the box (Firefox always, WebKit on hover) were the same two
      // buttons twice — and the second pair cramped the digits. A phone
      // never drew them, which is why the box only looked wrong on
      // desktop.
      ".etcher-num { -moz-appearance: textfield; appearance: textfield; }",
      ".etcher-num::-webkit-outer-spin-button,",
      ".etcher-num::-webkit-inner-spin-button {",
      "  -webkit-appearance: none; margin: 0;",
      "}",
      ".etcher-num:hover { background: rgba(255, 255, 255, 0.12); }",
      // Label size, plus and minus. A number box spins with the arrows a
      // browser draws inside it — on a desktop. A phone draws none, so the
      // only way to change a label's size there was to type a number into
      // it, one hand holding the phone. These are the same step, as a thing
      // to press.
      ".etcher-stepper {",
      "  display: flex; align-items: center; gap: 4px; width: 100%;",
      "}",
      ".etcher-stepper .etcher-num { flex: 1 1 auto; min-width: 0; }",
      // The − / + buttons are the one stepping control, on every device —
      // the box's native spinners are suppressed above.
      ".etcher-step {",
      "  flex: none; width: 30px; height: 30px; padding: 0;",
      "  display: inline-flex; align-items: center; justify-content: center;",
      "  border-radius: 6px; border: 1px solid rgba(255, 255, 255, 0.25);",
      "  background: transparent; color: #fff; cursor: pointer;",
      "  font: 600 15px ui-sans-serif, system-ui, sans-serif; line-height: 1;",
      "  transition: background 120ms ease, border-color 120ms ease;",
      "  -webkit-user-select: none; user-select: none;",
      "  -webkit-touch-callout: none; touch-action: manipulation;",
      "}",
      ".etcher-step:hover { background: rgba(255, 255, 255, 0.12); }",
      ".etcher-step:active { background: rgba(255, 255, 255, 0.22); }",
      ".etcher-step:disabled { opacity: 0.35; cursor: default; }",
      // A finger is not a cursor: the same button, big enough to hit.
      "@media (pointer: coarse) {",
      "  .etcher-step { width: 36px; height: 36px; font-size: 17px; }",
      "}",
      // The compact strip is one column — that is the whole point of it, to
      // give the board back its width — so the pair stacks instead of
      // sitting either side of the number and making the strip three
      // controls wide. Plus on top, minus underneath, the way a stepper
      // reads when it is vertical.
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-stepper {",
      "  flex-direction: column; gap: 2px; width: auto;",
      "}",
      // All three ordered, not just the plus: ordering one leaves the other
      // two in DOM order behind it, which puts the number underneath the
      // minus instead of between the pair. Laid out by `order` rather than
      // by reversing the column, so reading and tab order still follow the
      // DOM.
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-step-up { order: 1; }",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-stepper .etcher-num { order: 2; }",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-step-down { order: 3; }",
      // As wide as the number they belong to, so the column has one edge.
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-step {",
      "  width: 30px; height: 22px; font-size: 13px; border-radius: 5px;",
      "}",
      // On/off for the label plate. Sits where the other rows' read-outs
      // sit, so the row reads as \"setting: value\" like the ones above it.
      ".etcher-toggle {",
      "  min-width: 42px; height: 20px; padding: 0 8px;",
      "  border: 1px solid rgba(255, 255, 255, 0.25); border-radius: 10px;",
      "  background: transparent; color: #fff; cursor: pointer;",
      "  font: 500 11px ui-sans-serif, system-ui, sans-serif;",
      "  transition: background 120ms ease, border-color 120ms ease;",
      "}",
      ".etcher-toggle:hover { background: rgba(255, 255, 255, 0.12); }",
      ".etcher-toggle.is-on {",
      "  background: rgba(255, 255, 255, 0.22);",
      "  border-color: rgba(255, 255, 255, 0.6);",
      "}",
      ".etcher-num:focus {",
      "  outline: none; background: rgba(255, 255, 255, 0.12);",
      "  border-color: rgba(255, 255, 255, 0.6);",
      "}",
      ".etcher-marker-row-head {",
      "  display: flex; justify-content: space-between; align-items: center;",
      "  color: #fff; font-size: 11px; opacity: 0.85;",
      "}",
      ".etcher-marker-row input[type=\"range\"] {",
      "  width: 100%; accent-color: #fff; cursor: pointer; margin: 0;",
      "}",
      ".etcher-marker-dash { display: flex; gap: 6px; }",
      ".etcher-marker-dash button {",
      "  flex: 1 1 0; height: 30px; border: 1px solid rgba(255, 255, 255, 0.25);",
      "  background: transparent; border-radius: 6px; cursor: pointer;",
      "  display: inline-flex; align-items: center; justify-content: center;",
      "  transition: background 120ms ease, border-color 120ms ease;",
      "}",
      ".etcher-marker-dash button:hover { background: rgba(255, 255, 255, 0.12); }",
      ".etcher-marker-dash button.is-selected {",
      "  background: rgba(255, 255, 255, 0.22); border-color: rgba(255, 255, 255, 0.6);",
      "}",
      ".etcher-marker-dash svg { display: block; }",
      ".etcher-popup button[data-tool] {",
      "  width: 36px; height: 36px; border: none; padding: 0;",
      "  display: inline-flex; align-items: center; justify-content: center;",
      "  background: transparent; color: #fff; border-radius: 6px;",
      "  cursor: pointer; transition: background 120ms ease;",
      "}",
      ".etcher-popup button[data-tool]:hover {",
      "  background: rgba(255, 255, 255, 0.12);",
      "}",
      ".etcher-popup button[data-tool].is-selected {",
      "  background: rgba(255, 255, 255, 0.24);",
      "}",
      ".etcher-popup button[data-tool] svg { width: 18px; height: 18px; }",
      // The popup's non-tool buttons — the view switches and Customise —
      // match the tool buttons exactly. They sit in the same grid, and a
      // button of a different size in a row of equal ones reads as a
      // mistake rather than as a different kind of thing.
      ".etcher-popup button[data-popup-action] {",
      "  width: 36px; height: 36px; border: none; padding: 0;",
      "  display: inline-flex; align-items: center; justify-content: center;",
      "  background: transparent; color: #fff; border-radius: 6px;",
      "  cursor: pointer; transition: background 120ms ease;",
      "}",
      ".etcher-popup button[data-popup-action]:hover {",
      "  background: rgba(255, 255, 255, 0.12);",
      "}",
      // An enabled switch reads as pressed, the same as the active tool.
      ".etcher-popup button[data-popup-action][aria-pressed=\"true\"] {",
      "  background: rgba(255, 255, 255, 0.24);",
      "}",
      ".etcher-popup button[data-popup-action] svg { width: 18px; height: 18px; }",
      ".etcher-popup .etcher-swatch { width: 26px; height: 26px; }",
      // Full-width hairline divider used inside flex-wrap popups to
      // force the items after it onto a new row + visually section
      // them off (e.g., \"history\" buttons below the tools grid).
      // The ⋯ menu over the action bar: the style controls (borrowed from the
      // panel, which can be minimized or hidden) laid out exactly as the full
      // panel lays them — the slim thickness line, the row of choice buttons
      // with their pop-ups — and under them Arrange, a row of the same
      // captioned buttons. One column, the panel's width, nothing spilling.
      ".etcher-popup[data-kind=\"actions\"] {",
      "  flex-direction: column; flex-wrap: nowrap; align-items: stretch;",
      "  width: 206px; box-sizing: border-box; padding: 10px; gap: 10px; border-radius: 12px;",
      "}",
      ".etcher-popup[data-kind=\"actions\"].is-open { flex-wrap: nowrap; }",
      // The holder that makes the borrowed controls lay out as the full
      // panel (every panel rule keys on `.etcher-stylepanel`), without any of
      // the floating panel's own box.
      ".etcher-stylepanel.etcher-menu-host {",
      "  position: static; display: flex; width: auto; max-width: none;",
      "  padding: 0; background: none; box-shadow: none; border-radius: 0; z-index: auto;",
      "}",
      ".etcher-menu-host .etcher-popup[data-kind=\"params\"] {",
      "  position: static; display: flex; flex-direction: column;",
      "  width: 100%; min-width: 0; padding: 0; gap: 10px;",
      "  background: none; box-shadow: none; z-index: auto;",
      "}",
      ".etcher-menu-host:empty { display: none; }",
      // With the compact strip up, only what the strip leaves out: never the
      // thickness / label-size lines or the corner, alignment and label
      // buttons (all in the strip); opacity always (never in it); line type
      // and fill when the strip's parts setting leaves them out.
      ".etcher-menu-host[data-show=\"missing\"] .etcher-inline-row,",
      ".etcher-menu-host[data-show=\"missing\"] .etcher-font-row,",
      ".etcher-menu-host[data-show=\"missing\"] .etcher-chip:not([data-chip=\"opacity\"]):not([data-chip=\"stroke\"]):not([data-chip=\"fill\"]),",
      ".etcher-menu-host[data-show=\"missing\"][data-compact-parts~=\"dash\"] .etcher-chip[data-chip=\"stroke\"],",
      ".etcher-menu-host[data-show=\"missing\"][data-compact-parts~=\"fills\"] .etcher-chip[data-chip=\"fill\"] { display: none !important; }",
      ".etcher-popup[data-kind=\"actions\"] .etcher-popup-divider { flex: 0 0 auto; margin: 0; }",
      ".etcher-popup[data-kind=\"layers\"] { width: 216px; box-sizing: border-box; padding: 8px; border-radius: 12px; }",
      ".etcher-popup[data-kind=\"layers\"].is-open { flex-wrap: nowrap; }",
      ".etcher-popup[data-kind=\"layers\"] .etcher-arrange-row { flex: 1 1 auto; }",
      ".etcher-arrange-row { display: flex; gap: 6px; }",
      ".etcher-arrange-row .etcher-chip:disabled { opacity: 0.35; cursor: default; }",
      ".etcher-arrange-row .etcher-chip:disabled:hover { background: rgba(255, 255, 255, 0.08); }",
      ".etcher-arrange-row .etcher-chip-face svg { width: 17px; height: 17px; max-width: none; max-height: none; }",
      ".etcher-popup-divider {",
      "  flex: 0 0 100%; height: 1px; margin: 4px 0;",
      "  background: rgba(255, 255, 255, 0.15);",
      "}",
      // History buttons share the tool-button visual but carry a
      // distinct data attribute so the layout logic can target them.
      ".etcher-popup button[data-history] {",
      "  width: 36px; height: 36px; border: none; padding: 0;",
      "  display: inline-flex; align-items: center; justify-content: center;",
      "  background: transparent; color: #fff; border-radius: 6px;",
      "  cursor: pointer; transition: background 120ms ease;",
      "}",
      ".etcher-popup button[data-history]:hover {",
      "  background: rgba(255, 255, 255, 0.12);",
      "}",
      ".etcher-popup button[data-history]:disabled {",
      "  opacity: 0.35; cursor: not-allowed;",
      "}",
      ".etcher-popup button[data-history]:disabled:hover {",
      "  background: transparent;",
      "}",
      ".etcher-popup button[data-history] svg { width: 18px; height: 18px; }",
      // -- Custom color picker (hue ring + lightness slider) ----------------
      // The whole picker takes a single full-width row inside the
      // colors popup. Layout: hue ring on top, lightness slider +
      // preview swatch below it.
      ".etcher-picker {",
      "  flex: 0 0 100%;",
      "  display: flex; flex-direction: column;",
      "  align-items: center; gap: 8px;",
      "  padding: 4px 2px 2px;",
      "}",
      // Preset swatches inside the picker — horizontal row tucked
      // above the hue ring as bootstrap colors. Same swatch styling
      // as the recents row.
      ".etcher-presets {",
      "  display: flex; gap: 6px; flex-wrap: wrap;",
      "  align-items: center; justify-content: center;",
      "  padding: 0 2px;",
      "}",
      ".etcher-picker-ring-wrap {",
      "  position: relative; line-height: 0;",
      "}",
      ".etcher-picker-ring {",
      "  display: block; cursor: pointer;",
      "  touch-action: none;",
      "}",
      // Small filled dot that tracks the picked hue on the ring.
      // Position is updated via inline `left`/`top` in JS; transform
      // centers it on its anchor coords.
      ".etcher-picker-ring-knob {",
      "  position: absolute; width: 12px; height: 12px;",
      "  border: 2px solid #fff; border-radius: 999px;",
      "  box-shadow: 0 0 0 1px rgba(0,0,0,0.6);",
      "  transform: translate(-50%, -50%);",
      "  pointer-events: none;",
      "}",
      ".etcher-picker-slider-row {",
      "  display: flex; align-items: center; gap: 8px;",
      "  width: 100%;",
      "}",
      ".etcher-picker-slider-wrap {",
      "  position: relative; flex: 1; line-height: 0;",
      "}",
      ".etcher-picker-slider {",
      "  display: block; width: 100%; height: 14px;",
      "  border-radius: 8px; cursor: pointer;",
      "  touch-action: none;",
      "}",
      ".etcher-picker-slider-knob {",
      "  position: absolute; top: 50%;",
      "  width: 10px; height: 18px;",
      "  border: 2px solid #fff; border-radius: 4px;",
      "  box-shadow: 0 0 0 1px rgba(0,0,0,0.6);",
      "  transform: translate(-50%, -50%);",
      "  pointer-events: none;",
      "}",
      // Preview chip — solid swatch showing the currently picked
      // color. Sized to match the in-toolbar swatches so the
      // \"this is your color\" affordance is consistent.
      ".etcher-picker-preview {",
      "  width: 22px; height: 22px; border-radius: 999px;",
      "  border: 1px solid rgba(255, 255, 255, 0.4);",
      "  flex: 0 0 auto;",
      "}",
      // -- Recent custom colors row ----------------------------------------
      // Lives between the preset swatches and the picker. The row
      // itself collapses (display:none) when there are zero recents.
      ".etcher-recents {",
      "  flex: 0 0 100%;",
      "  display: flex; gap: 6px; flex-wrap: wrap;",
      "  align-items: center; padding: 0 2px;",
      "}",
      ".etcher-recents.is-empty { display: none; }",
      ".etcher-recents-label {",
      "  font-size: 10px; letter-spacing: 0.05em;",
      "  text-transform: uppercase;",
      "  color: rgba(255, 255, 255, 0.55);",
      "  margin-right: 2px;",
      "}",
      // Compact mode: below the `sm` breakpoint, hide everything in
      // the toolbar except the currently-active tool, undo/redo, the
      // currently-active swatch, the close button, and the two
      // overflow triggers. Dividers and the standalone cursor button
      // collapse — cursor moves into the tools popup as a regular
      // option.
      // Progressive overflow: `_layoutToolbar` walks the tool buttons
      // right-to-left and tags overflowed ones with `.etcher-overflow-
      // hidden` until the toolbar fits its container. Same path runs
      // for swatches when the tools group alone can't free enough
      // space. The active tool / swatch is pinned — it never collapses
      // into the popup, so the user always knows what's selected.
      ".etcher-toolbar .etcher-overflow-hidden { display: none !important; }",
      ".etcher-overlay {",
      "  position: absolute; inset: 0; pointer-events: none;",
      // Said here rather than inherited from the host. A drawing surface has
      // no text on it to select and nothing to look up, and iOS otherwise
      // treats a slow stroke as a long press: the copy / look-up / share bar
      // comes up mid-line. Fresco's viewer carries the same three, but etcher
      // also hangs off hosts that do not (a strip reader, a bare <img>), and
      // the surface under the finger is this one.
      "  -webkit-user-select: none; user-select: none;",
      "  -webkit-touch-callout: none;",
      "}",
      // …except where there IS text to select: a label being edited is a real
      // input, and selecting inside it is how anyone fixes a typo.
      ".etcher-overlay input, .etcher-overlay textarea,",
      ".etcher-overlay [contenteditable] {",
      "  -webkit-user-select: text; user-select: text;",
      "  -webkit-touch-callout: default;",
      "}",
      ".etcher-overlay.is-drawing { cursor: crosshair; }",
      // Document-level hit-test marks the overlay with this class
      // when the cursor is over a shape, so the cursor changes to a
      // pointer without the SVG itself catching events (which would
      // block OSD's wheel + drag-pan from reaching the canvas below).
      ".etcher-overlay.is-shape-hovered { cursor: pointer; }",
      ".etcher-shape {",
      "  fill: rgba(59, 130, 246, 0.12); stroke: #3b82f6;",
      // Shapes are non-interactive at the DOM level — `pointer-events:
      // none` lets every mouse/wheel/touch event fall through to OSD's
      // canvas sibling, so scroll-zoom and click-drag pan work even
      // when the cursor is over an annotation. Hover styling, tooltips,
      // and click-to-edit are driven by document-level mousemove +
      // pointerdown listeners (`_wireGlobalShapeListeners`) that
      // hit-test the cursor against `self.shapes` in image-px space.
      "  pointer-events: none;",
      "}",
      // EXCEPT: the shape currently in edit mode catches pointer
      // events on its painted body so a click-drag from inside the
      // shape moves it (`_startShapeMove`). The user is actively
      // editing this one — they'd rather drag than pan past it; the
      // non-editing siblings stay pointer-events: none so pan/zoom
      // works everywhere else.
      ".etcher-shape.is-editing {",
      "  pointer-events: visiblePainted;",
      "}",
      // Marker strokes: never filled (a marker is a stroke), round caps/joins
      // so the line reads like a felt-tip. Width / opacity / dash come from
      // the per-stroke style applied inline by `_applyMarkerStyle`.
      ".etcher-marker {",
      "  fill: none;",
      "  stroke-linecap: round; stroke-linejoin: round;",
      "}",
      // Discussion-count bubble (`metadata.badge`). Blue like the selection
      // language so it reads as chrome, not as part of the drawing; never
      // interactive — the shape underneath owns hover and click.
      ".etcher-badge { pointer-events: none; }",
      ".etcher-badge circle {",
      "  fill: #3b82f6; stroke: #fff; stroke-width: 1.25;",
      "}",
      ".etcher-badge-count {",
      "  fill: #fff; stroke: none; user-select: none;",
      "  font: 600 9px ui-sans-serif, system-ui, sans-serif;",
      "}",
      // Reveal-pulse: a brief halo flash triggered by
      // `handle.revealShape(uuid, { pulse: true })` so users
      // can spot the just-navigated-to shape against a busy page.
      // Uses currentColor's outer drop-shadow over the existing
      // fill / stroke; doesn't disturb the shape's geometry, so
      // pointer-events behavior is unchanged.
      ".etcher-shape--pulse {",
      "  animation: etcher-shape-pulse 1.5s ease-out;",
      "}",
      "@keyframes etcher-shape-pulse {",
      "  0%   { filter: drop-shadow(0 0 0    rgba(59, 130, 246, 0.0)); }",
      "  20%  { filter: drop-shadow(0 0 14px rgba(59, 130, 246, 0.95)); }",
      "  60%  { filter: drop-shadow(0 0 10px rgba(59, 130, 246, 0.55)); }",
      "  100% { filter: drop-shadow(0 0 0    rgba(59, 130, 246, 0.0)); }",
      "}",
      // Callout: the <g> container picks up `color` (default blue,
      // overridden by the picker via `style.color`); children resolve
      // `currentColor` against it. No halo behind the glyphs — it read as
      // an outline on the type. (The dimension label keeps its one: there
      // it masks the shaft line running behind the number.)
      ".etcher-callout { color: #3b82f6; }",
      ".etcher-callout text { stroke: none; }",
      // Text shape — bordered box (visible only when hovered/selected/
      // editing) wrapping a content <text> that fills the box. Border
      // inherits the shape's color via currentColor. Default state is
      // text-only (no border) so the label looks freestanding.
      ".etcher-text { color: #3b82f6; }",
      ".etcher-text .etcher-text-rect {",
      "  fill: transparent; stroke: transparent;",
      "  pointer-events: all;",
      "  transition: stroke 120ms ease, fill 120ms ease;",
      "}",
      // Hover and draft keep the dashed currentColor box (\"there is an
      // invisible bbox here\"); selected/editing switch to the same solid
      // blue as every other selected shape, so text doesn't have its own
      // private idea of what selection looks like.
      ".etcher-text.is-hovered .etcher-text-rect,",
      ".etcher-text.is-draft   .etcher-text-rect {",
      "  stroke: currentColor;",
      "  stroke-dasharray: 5 4;",
      "}",
      // `stroke-dasharray: none` so selection reads solid even while the
      // cursor is still hovering — without it the hover rule above keeps
      // its dash and a just-clicked box renders blue-dashed.
      ".etcher-text.is-selected .etcher-text-rect,",
      ".etcher-text.is-editing .etcher-text-rect {",
      "  stroke: #3b82f6;",
      "  stroke-dasharray: none;",
      "}",
      // The rotation knob: a dot on a short stem above the shape. The open
      // hand over it, closed while turning.
      ".etcher-rotate-stem { stroke: currentColor; stroke-width: 1; opacity: 0.55; pointer-events: none; vector-effect: non-scaling-stroke; }",
      ".etcher-overlay svg .etcher-rotate-handle { cursor: grab; }",
      ".etcher-overlay svg.is-rotating, .etcher-overlay svg.is-rotating * { cursor: grabbing !important; }",
      // The text box's scale dot: filled, so it reads as a different thing
      // from the corner dots it sits beyond.
      ".etcher-overlay svg .etcher-scale-handle { fill: currentColor; cursor: nwse-resize; }",
      ".etcher-overlay svg .etcher-xf-frame { fill: none; stroke: #3b82f6; stroke-width: 1.25px; pointer-events: none; vector-effect: non-scaling-stroke; }",
      ".etcher-overlay svg .etcher-xf-corner[data-corner='0'], .etcher-overlay svg .etcher-xf-corner[data-corner='2'] { cursor: nwse-resize; }",
      ".etcher-overlay svg .etcher-xf-corner[data-corner='1'], .etcher-overlay svg .etcher-xf-corner[data-corner='3'] { cursor: nesw-resize; }",
      // A text box shows its frame (shaped corners) instead of its rect,
      // which stays the hit area: same states, same looks.
      ".etcher-text.is-textbox .etcher-text-rect { fill: transparent !important; stroke: transparent !important; }",
      ".etcher-textbox-frame { fill: transparent; stroke: transparent; pointer-events: none; transition: stroke 120ms ease, fill 120ms ease; }",
      ".etcher-text.is-hovered .etcher-textbox-frame, .etcher-text.is-draft .etcher-textbox-frame { stroke: currentColor; stroke-dasharray: 5 4; }",
      ".etcher-text.is-selected .etcher-textbox-frame, .etcher-text.is-editing .etcher-textbox-frame { stroke: #3b82f6; stroke-dasharray: none; }",
      // A callout's frame is point text's box: the thin black dash.
      ".etcher-text.is-point.is-selected .etcher-textbox-frame, .etcher-text.is-point.is-editing .etcher-textbox-frame, .etcher-text.is-typing .etcher-textbox-frame { stroke: #111111; stroke-width: 1.25px; stroke-dasharray: 4 3; vector-effect: non-scaling-stroke; }",
      // Picked with a box-select (or shift-click): the same blue as every
      // other selected shape, solid, around the text's box. Text opts out of
      // the outline filter (it would bold the glyphs), so without this a
      // selected text looked exactly like an unselected one.
      ".etcher-text.is-multi-selected .etcher-textbox-frame { stroke: #3b82f6; stroke-width: 1.5px; stroke-dasharray: none; vector-effect: non-scaling-stroke; }",
      // Point text, selected or being typed into: a thin black dashed box,
      // as Affinity draws one, matching its black handles. The same width
      // whatever the zoom or the text's stretch.
      // The grip over point text's outline while it is typed into: the open
      // hand a selected shape shows, closed for the whole drag once taken.
      // Not `move` — many Linux cursor themes have none, and Firefox there
      // falls back to the plain arrow.
      ".etcher-overlay svg .etcher-text-grip { fill: transparent; fill-rule: evenodd; stroke: none; pointer-events: all; cursor: grab; }",
      ".etcher-overlay svg.is-over-text-frame, .etcher-overlay svg.is-over-text-frame * { cursor: grabbing !important; }",
      ".etcher-text.is-point.is-selected .etcher-text-rect,",
      ".etcher-text.is-point.is-editing .etcher-text-rect,",
      ".etcher-text.is-typing .etcher-text-rect {",
      "  stroke: #111111; stroke-width: 1.25px; stroke-dasharray: 4 3;",
      "  vector-effect: non-scaling-stroke;",
      "}",
      // No halo behind the glyphs. A 2px white stroke under every letter was
      // there to hold contrast over a photograph, but it reads as an outline
      // on the type itself and thickens small text into mush. Colour alone
      // carries it on the canvases these are actually used on.
      ".etcher-text .etcher-text-content,",
      ".etcher-text .etcher-text-content tspan {",
      "  fill: currentColor;",
      "  stroke: none;",
      "  pointer-events: none;",
      "  user-select: none;",
      "}",
      // The foreignObject editor sits above the shape — its inner
      // <input> handles its own focus/blur, but a fallback z-index keeps
      // it clear of any overlapping shape.
      // pointer-events back on: the editor lives inside the overlay SVG,
      // which is pointer-events:none so drags reach the canvas — without
      // this override a click INTO the open editor fell through to the
      // document handler and committed it. Typing only ever worked because
      // focus was set programmatically; clicking to reposition the caret,
      // or to come back after a trip to the style panel, closed the very
      // box being written.
      ".etcher-text-editor { z-index: 10; pointer-events: auto; }",
      // Eraser mid-sweep: shapes the cursor has touched get
      // de-saturated + dimmed so the user can see what's about to
      // disappear when they release.
      ".etcher-shape.is-erasing,",
      ".etcher-title-group.is-erasing {",
      "  opacity: 0.35; filter: grayscale(1);",
      "  transition: opacity 80ms ease, filter 80ms ease;",
      "}",
      // Title group: a satellite text bbox attached to a parent shape.
      // Cursor changes to "grab" so users know they can drag it; the
      // leader line stays subtle so the parent shape remains the
      // primary visual.
      // The WYSIWYG label editor's placeholder: the label's own ink,
      // dimmed — a grey placeholder would be invisible on some plates and
      // garish on others.
      ".etcher-text-editor input::placeholder {",
      "  color: currentColor; opacity: 0.45;",
      "}",
      ".etcher-title-group { cursor: grab; }",
      ".etcher-title-group.is-dragging { cursor: grabbing; }",
      ".etcher-title-leader {",
      "  stroke-dasharray: 3 3;",
      "  opacity: 0.6;",
      "  pointer-events: none;",
      "}",
      // Inline title text for non-callout shapes. Rendered as a sibling
      // `<text>` of the shape (not a child) so it doesn't inherit the
      // shape's fill/stroke. Uses currentColor so `_applyShapeColor` can
      // recolor by setting style.color on the title element. No halo, same
      // as callout text.
      ".etcher-title {",
      "  font-size: 12px;",
      "  font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;",
      "  font-weight: 500;",
      "  fill: currentColor;",
      "  stroke: none;",
      "  pointer-events: none;",
      "  color: #3b82f6;",
      "  user-select: none;",
      "}",
      // While a drawing tool is active, shapes step out of the way so a
      // drag started over an existing shape opens a new one instead of
      // getting trapped by the shape's pointer-events.
      ".etcher-overlay.is-drawing .etcher-shape {",
      "  pointer-events: none; cursor: crosshair;",
      "}",
      // A draft looks like the shape it is about to become — it is drawn
      // with the same style (`_styleForNewShape`), so this rule must not
      // repaint any of it. It used to force orange + `stroke-dasharray: 5 4`
      // + an orange body, back when draft and edit shared one "currently
      // mine" look. The stroke and fill were already dead on any coloured
      // shape (`_applyShapeColor` paints inline, which beats a class rule),
      // but the dash LANDED: `_applyLineParams` writes dasharray as a
      // presentation attribute, and any class rule outranks one. So every
      // shape previewed dashed whatever the user had set, then snapped to
      // its real style on release.
      ".etcher-shape.is-draft {",
      "  pointer-events: none;",
      "}",
      // Hover / selected / editing / multi-selected leave the shape's own
      // paint completely alone. The old rules recolored the stroke orange
      // and — the only part that actually painted — forced
      // `stroke-dasharray: 5 4`, so selecting a solid line looked like it
      // CHANGED the line to a dashed one. (The stroke/fill overrides were
      // dead on any colored shape: `_applyShapeColor` paints via inline
      // styles, which beat these class rules. Only the dasharray landed,
      // because `_applyLineParams` writes it as a presentation attribute,
      // which loses to any class rule.)
      //
      // Selection is now what it is in every canvas tool users compare us
      // to: a blue outline traced around the vector, built by stacking
      // offset drop-shadows of the shape's rendered pixels. That follows
      // the true silhouette — dashes, arrowheads, labels, fills — with no
      // outline geometry to keep in sync during drags, and it works
      // unchanged on the `<g>` kinds (arrow / line / callout) whose
      // children own their paint. Hover is the same idea, softer, so
      // \"can select\" and \"is selected\" read as one visual language.
      ".etcher-shape.is-hovered {",
      "  filter: drop-shadow(0 0 2.5px rgba(59, 130, 246, 0.9));",
      "}",
      ".etcher-shape.is-selected,",
      ".etcher-shape.is-editing,",
      ".etcher-shape.is-multi-selected {",
      "  filter: drop-shadow(1.2px 0 0 #3b82f6) drop-shadow(-1.2px 0 0 #3b82f6)",
      "          drop-shadow(0 1.2px 0 #3b82f6) drop-shadow(0 -1.2px 0 #3b82f6);",
      "}",
      // The kinds that can hold a FILL override those with the silhouette
      // filters (see `ensureSelectionFilters`): a drop-shadow paints behind
      // the element, and a semi-transparent fill lets it show through, so
      // selecting a red shape tinted its body toward blue. The silhouette
      // ring is drawn strictly outside the shape — the outline goes blue,
      // the fill stays exactly the shape's own color. Matched by element
      // type since CSS can't ask the shape kind: rect / circle / polygon
      // are the closed shapes, path / polyline are freehand (`:not` keeps
      // markers — fill-less by definition — on the cheap drop-shadows).
      "rect.etcher-shape.is-selected, rect.etcher-shape.is-editing,",
      "rect.etcher-shape.is-multi-selected,",
      "circle.etcher-shape.is-selected, circle.etcher-shape.is-editing,",
      "circle.etcher-shape.is-multi-selected,",
      "polygon.etcher-shape.is-selected, polygon.etcher-shape.is-editing,",
      "polygon.etcher-shape.is-multi-selected,",
      "path.etcher-shape.is-selected:not(.etcher-marker),",
      "path.etcher-shape.is-editing:not(.etcher-marker),",
      "path.etcher-shape.is-multi-selected:not(.etcher-marker),",
      "polyline.etcher-shape.is-selected:not(.etcher-marker),",
      "polyline.etcher-shape.is-editing:not(.etcher-marker),",
      "polyline.etcher-shape.is-multi-selected:not(.etcher-marker) {",
      "  filter: url(#etcher-selection-outline);",
      "}",
      "rect.etcher-shape.is-hovered, circle.etcher-shape.is-hovered,",
      "polygon.etcher-shape.is-hovered,",
      "path.etcher-shape.is-hovered:not(.etcher-marker),",
      "polyline.etcher-shape.is-hovered:not(.etcher-marker) {",
      "  filter: url(#etcher-hover-outline);",
      "}",
      ".etcher-shape.is-editing { cursor: grab; }",
      ".etcher-shape.is-editing.is-moving { cursor: grabbing; }",
      // Images opt out of every one of those. `stroke` and `fill` don't
      // paint on an `<image>` at all, and the drop-shadow is drawn outside
      // the element's box — which `clip-path` (the rounded corners) then
      // clips straight back off, since clipping happens after filtering.
      // So the whole rule was invisible on an image, and a filter fighting a
      // clip-path on one element is a rendering path best not relied on.
      // `_renderShape` draws them a real ring instead.
      "image.etcher-shape.is-multi-selected,",
      "image.etcher-shape.is-selected,",
      "image.etcher-shape.is-editing,",
      "image.etcher-shape.is-hovered {",
      "  filter: none;",
      "}",
      // A hatch fill is PAINTED PIXELS, and the outline filter rings every
      // one of them — so selecting a hatched shape drew a blue ring around
      // each diagonal stripe inside it. Dozens of rings where one was meant:
      // noise, not selection. Hatched shapes opt out here and get a traced
      // perimeter instead (`_syncHatchOutline`), which is the outline the
      // filter was standing in for all along.
      ".etcher-shape.etcher-hatched.is-multi-selected,",
      ".etcher-shape.etcher-hatched.is-selected,",
      ".etcher-shape.etcher-hatched.is-editing,",
      ".etcher-shape.etcher-hatched.is-hovered {",
      "  filter: none;",
      "}",
      // The traced perimeter itself: the shape's own geometry, stroked, sat
      // behind the shape so its own stroke covers the middle of the band and
      // what shows is an edge on either side — the same read as the filter's
      // ring. Never a hit target; the shape it copies owns that.
      ".etcher-hatch-outline {",
      "  pointer-events: none;",
      "}",
      // Text (and callout text boxes) opt out of the outline filter too —
      // they already show selection via the solid blue bbox stroke above,
      // and outlining every glyph reads as bolding the type, not as
      // selecting it.
      ".etcher-shape.etcher-text.is-multi-selected,",
      ".etcher-shape.etcher-text.is-selected,",
      ".etcher-shape.etcher-text.is-editing,",
      ".etcher-shape.etcher-text.is-hovered {",
      "  filter: none;",
      "}",
      // ...but a callout's POINTING parts — leader, underline, anchor dot —
      // are shafts, and a selected callout whose line stays plain looks
      // half-selected next to its blue box. The filter goes on the
      // children, not the <g>: SVG filters apply per element, so the
      // line/circle children take the outline while the glyphs (the reason
      // for the opt-out above) stay clean.
      ".etcher-shape.etcher-callout.is-selected line,",
      ".etcher-shape.etcher-callout.is-editing line,",
      ".etcher-shape.etcher-callout.is-multi-selected line,",
      ".etcher-shape.etcher-callout.is-selected circle,",
      ".etcher-shape.etcher-callout.is-editing circle,",
      ".etcher-shape.etcher-callout.is-multi-selected circle {",
      "  filter: drop-shadow(1.2px 0 0 #3b82f6) drop-shadow(-1.2px 0 0 #3b82f6)",
      "          drop-shadow(0 1.2px 0 #3b82f6) drop-shadow(0 -1.2px 0 #3b82f6);",
      "}",
      ".etcher-shape.etcher-callout.is-hovered line,",
      ".etcher-shape.etcher-callout.is-hovered circle {",
      "  filter: drop-shadow(0 0 2.5px rgba(59, 130, 246, 0.9));",
      "}",
      // The ring image shapes get instead: a stroked rect tracking the
      // image's box and corner radius, drawn as a sibling so nothing
      // clips it. Blue like every other selected shape — hover is the
      // same ring, thinner and translucent, matching the soft hover glow
      // on vector shapes.
      ".etcher-image-ring {",
      "  fill: none; stroke: #3b82f6; stroke-width: 2.5;",
      "  pointer-events: none;",
      "}",
      ".etcher-image-ring[data-state=\"hovered\"] {",
      "  stroke: rgba(59, 130, 246, 0.65); stroke-width: 2;",
      "}",
      ".etcher-shape.is-multi-selected.is-moving { cursor: grabbing; }",
      // Snap guides — the cyan lines shown while a dragged shape's edge or
      // center is magnetized to another shape's. Cyan deliberately: blue is
      // selection and the warm tones belong to shapes and drafts, so the
      // guides read as chrome, not content. Dashed and hairline-thin, gone
      // the moment the drag ends.
      ".etcher-snap-guide {",
      "  stroke: #06b6d4; stroke-width: 1; stroke-dasharray: 4 3;",
      "  pointer-events: none;",
      "}",
      // Marquee rectangle drawn while box-selecting on the cursor tool.
      ".etcher-marquee {",
      "  position: absolute; pointer-events: none; z-index: 11;",
      "  border: 1px solid rgba(59, 130, 246, 0.9);",
      "  background: rgba(59, 130, 246, 0.12);",
      "  border-radius: 1px;",
      "}",
      // One box around everything the release would take, drawn while the
      // marquee is still open. The shapes themselves light up in the normal
      // selection blue, but a blue outline on a blue shape over a blue
      // photograph is exactly where an outline gets lost — so the hull is
      // white-dashed between two dark hairlines, which has contrast against
      // anything underneath it, and its job is to be readable at a glance
      // rather than to trace any one shape.
      ".etcher-marquee-hull {",
      "  position: absolute; pointer-events: none; z-index: 12;",
      "  border: 1.5px dashed #fff;",
      "  border-radius: 2px;",
      "  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.55),",
      "              inset 0 0 0 1px rgba(0, 0, 0, 0.55);",
      "}",
      ".etcher-handle {",
      // Stroke + interactive fills bind to `currentColor` so a handle
      // inherits the shape's painted color (set via `style.color`
      // when the handle is created). Defaults to the inherited
      // element color (blue) when no custom color is picked.
      "  fill: #fff; stroke: currentColor; stroke-width: 2;",
      "  pointer-events: auto; cursor: grab;",
      // `transform-box: fill-box` anchors `transform-origin` to the
      // element's own box rather than the SVG viewport, so `scale()`
      // grows the dot around its own center instead of warping it
      // toward (0, 0). Bumping `transform` rather than `r` because
      // CSS-set `r` doesn't always win over the attribute-set `r="5"`
      // across all browsers.
      "  transform-box: fill-box; transform-origin: center;",
      "  transition: transform 80ms ease, stroke-width 80ms ease, fill 80ms ease, fill-opacity 80ms ease;",
      "}",
      ".etcher-handle:hover {",
      "  transform: scale(1.6); stroke-width: 3;",
      "  fill: currentColor; fill-opacity: 0.35;",
      "}",
      ".etcher-handle.is-dragging {",
      "  cursor: grabbing; transform: scale(1.8); stroke-width: 3;",
      "  fill: currentColor; fill-opacity: 0.55;",
      "}",
      // Vertex selected (click without drag). Highlights the vertex so
      // the user can see which point Backspace / Delete will remove.
      // Red outline reads as \"about to delete\" without making the
      // dot disappear on the canvas.
      ".etcher-handle.is-selected {",
      "  stroke: #dc2626; stroke-width: 3;",
      "  fill: #dc2626; fill-opacity: 0.45;",
      "  transform: scale(1.6);",
      "}",
      // While drafting a polygon the first vertex doubles as the close
      // button — highlight it when the cursor is near so the user knows
      // a click there finishes the shape. Same look as `:hover` for
      // consistency.
      ".etcher-handle.is-close-target {",
      "  transform: scale(1.6); stroke-width: 3;",
      "  fill: currentColor; fill-opacity: 0.4;",
      "}",
      // Freehand pen editor: thin tether from each anchor to its bezier
      // control dots, and the control dots themselves (smaller + filled so
      // they read as "handles" distinct from the white anchor dots). A
      // corner anchor is shaded to set it apart from a smooth (white) one.
      ".etcher-handle-line {",
      "  stroke: currentColor; stroke-width: 1.25; stroke-opacity: 0.6;",
      "  fill: none; pointer-events: none;",
      "}",
      ".etcher-bezier-handle {",
      "  fill: currentColor; fill-opacity: 0.85; stroke: #fff; stroke-width: 1.5;",
      "  pointer-events: auto; cursor: grab;",
      "  transform-box: fill-box; transform-origin: center;",
      "  transition: transform 80ms ease;",
      "}",
      ".etcher-bezier-handle:hover { transform: scale(1.5); }",
      ".etcher-bezier-handle.is-dragging { cursor: grabbing; transform: scale(1.7); }",
      ".etcher-anchor-handle.is-corner { fill: currentColor; fill-opacity: 0.5; }",
      // Audio card. Painted from `currentColor` like every other shape, so
      // it picks up the colour picker and the selected/hover states without
      // any special casing.
      // The DOM layer that holds video frames. Transparent to the pointer
      // throughout: pan, zoom, drawing and the SVG transport drawn over the
      // top all have to reach through it.
      ".etcher-media {",
      "  position: absolute; inset: 0; overflow: hidden; pointer-events: none;",
      "}",
      ".etcher-media video {",
      "  position: absolute; object-fit: fill; pointer-events: none;",
      "  background: #000;",
      "}",
      // No `stroke-width` here: the card scales as one object, so its own
      // outline has to as well, and the render sets it from `cardK` like
      // every other length in the layout. Left in CSS it stayed 1.5px while
      // the card shrank away from it.
      ".etcher-audio-bg {",
      "  fill: rgba(17, 17, 20, 0.92); stroke: currentColor;",
      "}",
      ".etcher-audio-btn { fill: currentColor; stroke: none; pointer-events: all; cursor: pointer; }",
      // The glyph must not eat the press — the disc under it owns that, and
      // a triangle's own hit area is a poor target.
      ".etcher-audio-glyph { fill: #111114; stroke: none; pointer-events: none; }",
      ".etcher-audio-title, .etcher-audio-time {",
      "  fill: #f5f5f7; stroke: none; pointer-events: none;",
      "  font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;",
      "}",
      ".etcher-audio-time { fill: rgba(245, 245, 247, 0.6); }",
      ".etcher-audio-track { fill: rgba(245, 245, 247, 0.22); stroke: none; pointer-events: none; }",
      ".etcher-audio-fill { fill: currentColor; stroke: none; pointer-events: none; }",
      ".etcher-audio-scrub { fill: transparent; stroke: none; pointer-events: all; cursor: pointer; }",
      // On a video the same transport becomes a scrim over the picture
      // rather than a card of its own: no outline (the video's edge is the
      // edge) and darker, so white text and a coloured bar stay readable
      // over whatever frame is underneath.
      // Hidden entirely rather than faded: a translucent bar over the picture
      // still covers it.
      ".etcher-audio.is-chrome-hidden > * { display: none; }",
      // Uploading: the card is there, but nothing on it can be used yet.
      ".etcher-audio.is-uploading .etcher-audio-btn,",
      ".etcher-audio.is-uploading .etcher-audio-glyph { display: none; }",
      ".etcher-audio.is-uploading .etcher-audio-track { fill: rgba(245,245,247,0.18); }",
      ".etcher-audio.is-uploading .etcher-audio-fill { fill: rgba(245,245,247,0.75); }",
      // No progress reported: a stub that slides, so the bar still says
      // "working" instead of sitting at zero looking stalled.
      "@keyframes etcher-indeterminate {",
      "  0% { transform: translateX(0); }",
      "  100% { transform: translateX(300%); }",
      "}",
      ".etcher-audio.is-indeterminate .etcher-audio-fill {",
      "  animation: etcher-indeterminate 1.1s ease-in-out infinite alternate;",
      "}",
      ".etcher-audio.is-uploading .etcher-audio-title,",
      ".etcher-audio.is-uploading .etcher-audio-time { fill: rgba(245,245,247,0.55); }",
      ".etcher-audio.is-uploading .etcher-audio-scrub { pointer-events: none; }",
      // Scrub preview — hidden until the pointer is on the bar.
      ".etcher-audio-peek { display: none; pointer-events: none; }",
      ".etcher-audio-peek[data-show] { display: block; }",
      ".etcher-audio-peek-bg { fill: rgba(0, 0, 0, 0.85); stroke: none; }",
      ".etcher-audio-peek-text {",
      "  fill: #f5f5f7; stroke: none;",
      "  font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;",
      "}",
      ".etcher-video .etcher-audio-bg {",
      "  fill: rgba(0, 0, 0, 0.55); stroke: none;",
      "}",
      // Connector dots — the eight bindable points that appear on a shape's
      // box when the cursor tool hovers it. Drag one to pull an arrow out.
      // Named apart from `.etcher-anchor-handle` above, which is the bezier
      // editor's node anchor and unrelated.
      //
      // Deliberately quieter than `.etcher-handle`: these show on mere hover,
      // so eight loud dots on every shape the cursor crosses would make the
      // canvas twitch. They read as a hint until you approach one.
      ".etcher-connector-dot {",
      "  fill: " + CONNECTOR_DOT_CORE + ";",
      "  stroke: " + CONNECTOR_DOT_RING + "; stroke-width: 3;",
      "  pointer-events: none; cursor: crosshair;",
      "  transform-box: fill-box; transform-origin: center;",
      "  transition: transform 80ms ease;",
      "}",
      // The grab zone. Invisible and much larger than the dot it sits under,
      // so the press doesn't have to be pixel-perfect. It owns
      // `pointer-events` for the pair — the dot itself is purely visual.
      ".etcher-connector-hit {",
      "  fill: transparent; stroke: none;",
      "  pointer-events: all; cursor: crosshair;",
      "}",
      // Adjacent-sibling selector: each hit disc is inserted immediately
      // before its own dot, so hovering the zone lights the right dot.
      ".etcher-connector-hit:hover + .etcher-connector-dot {",
      "  transform: scale(1.5);",
      "}",
      // Anchors of a candidate target, shown while an arrow is being routed.
      // Rendered without a hit disc of their own — they're a readout, and
      // the drop is decided by proximity in `_arrowSnapState`, not by
      // hitting them.
      //
      // The one the end will land on if released now.
      ".etcher-connector-dot.is-snap {",
      "  transform: scale(1.9);",
      "}",
      // Midpoint "ghost" dot for polygon edges — faintly visible
      // whenever the polygon is in edit mode, so the user can see at
      // a glance where a new vertex would land. Fades to full
      // opacity on direct hover. Cursor: copy hints "click to add".
      ".etcher-handle-midpoint {",
      "  fill: currentColor; fill-opacity: 0;",
      "  stroke: currentColor; stroke-width: 2; stroke-opacity: 0;",
      "  pointer-events: all; cursor: copy;",
      "  transition: stroke-opacity 80ms ease, fill-opacity 80ms ease, transform 80ms ease;",
      "}",
      // `.is-active` is set on the closest midpoint to the cursor by
      // `_updateClosestMidpoint`. Only one shows at a time so the
      // polygon's edges don't get crowded with dots.
      //
      // Solid enough to notice from across the shape: this fires on
      // proximity, not on being over the dot, so it has to announce itself
      // before the pointer arrives — a wash at 0.2 alpha did not.
      ".etcher-handle-midpoint.is-active {",
      "  fill: #fff; fill-opacity: 1;",
      "  stroke-opacity: 1; stroke-width: 2.5;",
      "  transform: scale(1.15);",
      "}",
      ".etcher-handle-midpoint:hover {",
      "  stroke-opacity: 1;",
      "  fill: currentColor; fill-opacity: 0.35;",
      "  transform: scale(1.4); stroke-width: 2;",
      "}",
      ".etcher-handle-midpoint.is-dragging {",
      "  stroke-opacity: 1;",
      "  fill: currentColor; fill-opacity: 0.55;",
      "  transform: scale(1.6); stroke-width: 2;",
      "}",
      // Rectangle edge "grabbers" — small rounded rect aligned along
      // the edge. Different visual + cursor from polygon midpoints
      // (`+`/copy) so the UX reads as "drag this edge to resize",
      // not "add a vertex here". Same closest-only highlight via
      // `.is-active` driven by `_updateClosestMidpoint`.
      ".etcher-handle-edge {",
      "  fill: currentColor; fill-opacity: 0;",
      "  stroke: currentColor; stroke-width: 1.25; stroke-opacity: 0;",
      "  pointer-events: all;",
      "  transition: stroke-opacity 80ms ease, fill-opacity 80ms ease, transform 80ms ease;",
      "}",
      ".etcher-handle-edge--h { cursor: ns-resize; }",
      ".etcher-handle-edge--v { cursor: ew-resize; }",
      ".etcher-handle-edge.is-cramped { display: none; }",
      // Point text's grabbers dress like its corner dots — white, with the
      // black outline — rather than as a tint of the text under them, and
      // like a rectangle's they show only when the cursor comes near.
      ".etcher-handle.etcher-handle-edge.is-point { fill: #fff; stroke-width: 2; }",
      ".etcher-handle.etcher-handle-edge.is-point.is-active,",
      ".etcher-handle.etcher-handle-edge.is-point:hover,",
      ".etcher-handle.etcher-handle-edge.is-point.is-dragging { fill-opacity: 1; stroke-opacity: 1; }",
      ".etcher-handle-edge.is-active {",
      "  fill-opacity: 0.35; stroke-opacity: 0.9;",
      "}",
      ".etcher-handle-edge:hover {",
      "  fill-opacity: 0.7; stroke-opacity: 1;",
      "  transform: scale(1.1);",
      "}",
      ".etcher-handle-edge.is-dragging {",
      "  fill-opacity: 0.85; stroke-opacity: 1;",
      "  transform: scale(1.15);",
      "}",
      // While a drawing tool is active, vector dots on the in-progress
      // draft are markers, not grab targets — let pointer events fall
      // through to the wrapper so the user can keep dragging the
      // active tool over them. Scoped to the inert (draft) dots only:
      // with same-tool editing, a committed shape selected under its
      // own tool wears REAL handles, and those must stay grabbable.
      ".etcher-overlay.is-drawing .etcher-handle--inert {",
      "  pointer-events: none; cursor: crosshair;",
      "}",
      ".etcher-tooltip {",
      // pointer-events: auto so the user can move from shape to tooltip
      // and interact with the delete button. The tooltip is positioned
      // above the shape so it doesn't normally block hover on the shape
      // itself.
      "  position: absolute; z-index: 12; pointer-events: auto;",
      "  background: rgba(0, 0, 0, 0.85); color: #fff;",
      "  padding: 6px 10px; border-radius: 6px;",
      "  font-size: 12px; line-height: 1.35; max-width: 260px;",
      "  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);",
      "  display: none;",
      // Fades in and out rather than appearing and vanishing. `display`
      // still carries "is it up" — every other read in the file asks
      // that — and the class carries the paint, one frame later, so the
      // transition has two states to move between.
      "  opacity: 0; transform: translateY(3px);",
      "  transition: opacity " + TOOLTIP_FADE_MS + "ms ease," +
        " transform " + TOOLTIP_FADE_MS + "ms ease;",
      "  will-change: opacity, transform;",
      "}",
      ".etcher-tooltip.is-visible { opacity: 1; transform: translateY(0); }",
      // Docked: parked in the bottom-left corner instead of following the
      // shape. The corner is free by construction — fresco's nav is
      // top-left, the toolbar bottom-centre, the style panel top-right —
      // and a tooltip there covers none of the drawing, which is the whole
      // point of the mode. Wider than the anchored one: it is not sitting
      // on top of anything, so it can afford to be read.
      // Docked: the tooltip is a SECTION of the style panel, not a box
      // floating over the drawing — so it drops the absolute positioning,
      // the shadow and the dark plate, and wears the panel's own surface.
      // Full width of the panel, with a rule above it to separate the
      // shape's actions from the stroke controls they sit under.
      ".etcher-tooltip.is-docked {",
      "  position: static; max-width: none; width: 100%;",
      "  background: transparent; box-shadow: none;",
      "  color: inherit; border-radius: 0;",
      "  padding: 8px 0 0; margin-top: 8px;",
      "  border-top: 1px solid rgba(255, 255, 255, 0.12);",
      "  transform: none;",
      "}",
      // The panel is the anchor now, so the fade is opacity alone — a
      // transform would shift the rows below it.
      ".etcher-tooltip.is-docked.is-visible { transform: none; }",
      // In the one-column strip the section is just the shape's action
      // buttons, stacked like everything else in the strip. The name, date
      // and comment preview stretched the strip to full width; the full
      // panel is where they are read. Never floated over the drawing
      // instead — the whole point of docking is a clear picture.
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-tooltip.is-docked {",
      "  padding: 6px 0 0; margin-top: 6px; width: auto;",
      "}",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-tooltip.is-docked > :not(.etcher-tooltip-header),",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-tooltip.is-docked .etcher-tooltip-kind {",
      "  display: none;",
      "}",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-tooltip.is-docked .etcher-tooltip-header {",
      "  flex-direction: column; gap: 6px;",
      "}",
      ".etcher-stylepanel[data-size=\"compact\"] .etcher-tooltip.is-docked .etcher-tooltip-btn {",
      "  width: 30px; height: 30px;",
      "}",
      // Motion is decoration here; the information is the point.
      "@media (prefers-reduced-motion: reduce) {",
      "  .etcher-tooltip { transition: none; transform: none; }",
      "  .etcher-tooltip.is-visible { transform: none; }",
      "}",
      ".etcher-tooltip-header {",
      "  display: flex; align-items: center; gap: 10px;",
      "}",
      ".etcher-tooltip-kind {",
      "  font-weight: 600; text-transform: capitalize; flex: 1;",
      "}",
      // Shared base for every header action button (host-declared via
      // `window.Etcher.tooltipActions` and the built-in trash alike);
      // `.etcher-tooltip-delete` layers its red accent on top.
      ".etcher-tooltip-btn {",
      "  background: rgba(255, 255, 255, 0.08); border: none;",
      "  color: rgba(255, 255, 255, 0.85);",
      "  width: 24px; height: 24px; padding: 0;",
      "  border-radius: 4px; cursor: pointer;",
      "  display: inline-flex; align-items: center; justify-content: center;",
      "  transition: background 120ms ease, color 120ms ease;",
      "}",
      ".etcher-tooltip-btn svg { width: 14px; height: 14px; }",
      ".etcher-tooltip-btn:hover { background: rgba(255, 255, 255, 0.18); }",
      ".etcher-tooltip-btn:focus-visible {",
      "  outline: 2px solid rgba(147, 197, 253, 0.9); outline-offset: 1px;",
      "}",
      ".etcher-tooltip-delete {",
      "  color: rgba(252, 165, 165, 0.95);",
      "}",
      ".etcher-tooltip-delete:hover {",
      "  background: rgba(239, 68, 68, 0.32); color: #fff;",
      "}",
      ".etcher-tooltip-delete:focus-visible {",
      "  outline: 2px solid rgba(255, 255, 255, 0.7); outline-offset: 1px;",
      "}",
      ".etcher-tooltip-delete svg { width: 14px; height: 14px; }",
      // Opt-in styling primitives consumers can use inside their
      // `tooltipSlots.body` HTML — Etcher's defaults don't apply
      // these automatically, so a consumer slot returning plain HTML
      // is laid out plainly. Used as `<div class="etcher-tooltip-body">`
      // → flex row with thumb + text columns.
      ".etcher-tooltip-body {",
      "  display: flex; gap: 8px; margin-top: 6px; max-width: 260px;",
      "}",
      ".etcher-tooltip-thumb {",
      "  flex: 0 0 40px; width: 40px; height: 40px;",
      "  border-radius: 4px; object-fit: cover;",
      "  background: rgba(255, 255, 255, 0.08);",
      "}",
      // Paperclip fallback when the comment has an attachment but no
      // image thumbnail to render. Same 40x40 box; centered icon.
      ".etcher-tooltip-thumb-icon {",
      "  display: inline-flex; align-items: center; justify-content: center;",
      "  color: rgba(255, 255, 255, 0.85);",
      "}",
      ".etcher-tooltip-thumb-icon svg { width: 20px; height: 20px; }",
      ".etcher-tooltip-text { flex: 1; min-width: 0; }",
      ".etcher-tooltip-quote {",
      "  font-style: italic; opacity: 0.9;",
      "  display: -webkit-box; -webkit-box-orient: vertical;",
      "  -webkit-line-clamp: 2; overflow: hidden;",
      "  word-break: break-word;",
      "}",
      // Date · count subheader, sits between the header and the
      // comment body. Subtle so it doesn't compete with the comment
      // preview's actual content.
      ".etcher-tooltip-meta {",
      "  margin-top: 2px; opacity: 0.7; font-size: 11px;",
      "}",
      // Cross-component highlight: when an annotation is pinned, any
      // element in the document carrying `data-annotation-uuid="<uuid>"`
      // (typically a comment row in the consumer's discussion thread)
      // glows orange so the user can see the linked context at the
      // same time. The selector is generic — a consumer just needs to
      // stamp `data-annotation-uuid` on the element it wants
      // highlighted; no Etcher-side wiring required.
      ".etcher-comment-highlight {",
      "  outline: 2px solid #f59e0b; outline-offset: 2px;",
      "  border-radius: 0.5rem;",
      "  background-color: rgba(245, 158, 11, 0.12);",
      "  transition: background-color 200ms ease, outline-color 200ms ease;",
      "}"
    ].join("\n");

    var style = document.createElement("style");
    style.setAttribute("data-etcher", "");
    style.textContent = css;
    document.head.appendChild(style);
  }

  // ===========================================================================
  // Tool descriptors
  // ===========================================================================

  // Tools that earn a permanent slot on the bar. Everything else is still
  // available — it lives in the `[⋯]` grid — but a first-time user meets a
  // short, legible row instead of a wall of twelve icons. Ordered as they
  // appear; `cursor` is rendered separately and always leads.
  // What the "last used" slot holds before the user has picked anything
  // from the `[⋯]` grid. A square is the thing people reach for first.
  var DEFAULT_PINNED_TOOL = "rectangle";

  // Etcher's own UI. A pointer event landing on any of it must not reach the
  // canvas handlers, which would clear the selection the controls act on.
  // Kept as one constant because it is only ever wrong by omission.
  // Everything that is Etcher's own UI rather than the drawing. A click that
  // lands on any of it belongs to that control, and must not also be
  // hit-tested against whatever shape happens to be underneath — the
  // controls float over the canvas, so there is nearly always something.
  //
  // Any new piece of chrome has to be added here. Leaving one out does not
  // break the control, which is what makes it easy to miss: the button works
  // AND the shape beneath it reacts, so pressing a button over a link card
  // opens the link.
  var CHROME_SELECTOR =
    ".etcher-toolbar, .etcher-actionbar, .etcher-stylepanel, " +
    ".etcher-style-trigger, .etcher-panel-toggle, " +
    ".etcher-customise-backdrop, .etcher-customise, " +
    ".etcher-link-menu, .etcher-link-popup, " +
    ".etcher-link-editor, " +
    ".etcher-popup, .etcher-tooltip, " +
    // The viewer's own controls float over the canvas too: Fresco's nav
    // (its ⋯ popover is a `.fresco-nav` as well) and its zoom control.
    // Left out, a zoom button pressed over a shape selected the shape,
    // and a quick second press opened its label editor instead of zooming.
    ".fresco-nav, .fresco-zoom";


  // The size every text measurement is taken at, whatever size the text will
  // be drawn at. Text metrics are not linear in font size, so measuring at
  // the rendered size makes line breaks and font-fitting depend on the zoom.
  var TEXT_MEASURE_FONT_PX = 100;

  // ── Red pointer ────────────────────────────────────────────────────────────
  //
  // A shared laser pointer: while the tool is armed, the pointer's position
  // goes out to everyone on the board and is drawn as a red dot with a trail
  // behind it. It is not an annotation — nothing is stored, nothing is undone,
  // and it disappears the moment the person stops pointing.
  // How long to wait before writing preferences out. A host may be saving
  // them over the network, and a dragged slider is dozens of changes a
  // second; nobody needs each one of those to be a request.
  var PREFS_SAVE_DEBOUNCE_MS = 400;

  // Kept in step with phoenix_kit's own pin, so a host running both loads
  // one copy of the library rather than two versions of it.
  var SORTABLE_CDN = "https://cdn.jsdelivr.net/npm/sortablejs@1.15.0/Sortable.min.js";

  var POINTER_COLOR = "#ef4444";
  // How long a trail lingers. Long enough to read the direction of a sweep,
  // short enough that it doesn't smear across the board on a fast movement.
  var POINTER_TRAIL_MS = 650;
  // Radius of the head, in SCREEN px. A pointer is not part of the drawing —
  // it is someone's finger on the board — so it stays the same size at every
  // zoom, and everyone sees it the same size whatever zoom they are at.
  var POINTER_DOT_R = 7;
  // Minimum gap between broadcasts. Pointer moves arrive far faster than
  // anyone can see, and every one of them is a message to every viewer.
  var POINTER_SEND_MS = 40;
  // A remote pointer with nothing heard from it for this long is dropped, so
  // a closed tab doesn't leave a dot stuck on everyone else's board.
  var POINTER_STALE_MS = 4000;
  // How long a finished stroke is held on screen while its real shape makes
  // the round trip to the server and back. The ghost is normally handed over
  // the instant that shape is built, and this is only the backstop for the
  // edit that never lands — an undo on the way, a save that failed, a
  // dropped socket. Long enough to cover a bad connection, short enough that
  // a stroke nobody can select or move is not left lying there.
  var GHOST_HANDOVER_MS = 5000;

  // What the bar shows, and in what order, before anyone customises it.
  // Everything else the host offers is still there, one press of `⋯` away.
  //
  // The test is "would a first-time user reach for this while marking up a
  // picture", not "is this a good tool" — the grid is not a demotion.
  //
  // Read as a sentence: get around, scribble on things and rub the scribble
  // out, point at them or rule a line between them, label. The marker and
  // the eraser lead (right after the grabber) because scribble-and-correct
  // is the loop a first-time user actually lives in — this order is the
  // head dev's spec, replacing the rectangle/circle pair that used to hold
  // those two slots.
  //
  // What is NOT here, and why:
  //   rectangle, circle   — real shapes, but drawn on purpose; the marker
  //                         covers the "ring around the thing" job they
  //                         were on the bar for.
  //   polygon             — precise work. A real tool, reached for on
  //                         purpose, by someone who knows they want it.
  //                         (Dimension was here on the same argument until
  //                         2026-09-20: measuring a photograph turns out to
  //                         be a common enough job to meet on the bar, next
  //                         to the arrow it is drawn like.)
  //   freehand            — the second hand-drawing tool, and the more
  //                         specialised of the two: its stroke is an
  //                         editable curve whose nodes you drag afterwards.
  //                         The marker is the one you reach for to scribble
  //                         on something; freehand is the one you graduate
  //                         to when the scribble has to be exact.
  //   callout             — a leader and a label welded together, which an
  //                         arrow and a text label already are separately.
  //                         Worth having, not worth meeting first.
  //   image               — needs host wiring to do anything, and inserting
  //                         a picture INTO a picture is not the common job.
  //   pointer             — draws nothing and stores nothing on its own: it
  //                         is for presenting, and only does something when
  //                         the host relays it to other people. It used to
  //                         be here on the argument that a presenter cannot
  //                         hunt for it mid-sentence, which is true — and
  //                         still leaves it dead weight on every board that
  //                         is not being presented. A host that presents can
  //                         put it back in one line.
  var ESSENTIAL_TOOLS = [
    "grabber",
    "marker", "highlighter", "eraser",
    "arrow", "dimension", "line",
    "text"
  ];

  // Corner radius on image shapes, as a fraction of the shorter rendered
  // side, clamped. Proportional rather than a fixed px because a constant
  // radius is a different look at every zoom — 8px on a board zoomed out far
  // enough that an image is 56px across turns it into a button, and the same
  // 8px vanishes once you zoom in. This ratio is roughly the link card's own
  // (20 units on a 480-tall card), so a photo and a preview card sitting side
  // by side round the same amount.
  var IMAGE_RADIUS_RATIO = 0.045;
  var IMAGE_RADIUS_MIN = 2;
  var IMAGE_RADIUS_MAX = 16;

  // Connector anchors — the eight points on a shape an arrow can bind to:
  // the four corners and four side midpoints of its bounding box, held as
  // fractions of that box. Expressing them against the bbox rather than the
  // outline is what lets every kind be bindable through one code path; the
  // cost is that a circle binds at its box's corners rather than on its
  // curve, which is the same trade tldraw and Figma make.
  //
  // Listed clockwise from the top-left, which is the order the hover dots
  // are created in and therefore their DOM order.
  var ARROW_ANCHORS = [
    { id: "nw", fx: 0,   fy: 0   },
    { id: "n",  fx: 0.5, fy: 0   },
    { id: "ne", fx: 1,   fy: 0   },
    { id: "e",  fx: 1,   fy: 0.5 },
    { id: "se", fx: 1,   fy: 1   },
    { id: "s",  fx: 0.5, fy: 1   },
    { id: "sw", fx: 0,   fy: 1   },
    { id: "w",  fx: 0,   fy: 0.5 }
  ];
  var ARROW_ANCHOR_IDS = ARROW_ANCHORS.map(function(a) { return a.id; });

  // How near (screen px) the pointer has to bring a dragging arrow end
  // before it snaps onto an anchor. Generous, because the snap is the
  // point of the gesture — you aim at a shape, not at a 5px dot.
  var ARROW_SNAP_PX = 34;

  // Radius of the invisible disc that accepts the press on a connector dot,
  // in container px. The visible dot is 4.5px — a pixel-perfect target — so
  // the grab zone is a separate, much larger element underneath it.
  //
  // Clamped to a fraction of the shape's rendered size: eight discs at the
  // full radius would swallow a small shape whole, leaving no part of it
  // clickable for selecting or moving.
  var CONNECTOR_HIT_RADIUS = 15;
  var CONNECTOR_HIT_RADIUS_MIN = 6;
  var CONNECTOR_HIT_SHAPE_RATIO = 0.26;

  // The same idea for edit handles, sized for a fingertip: a touch screen
  // needs ~44px of target where a mouse needs five. The DOT stays five —
  // growing it would put a row of blobs over the drawing you are editing —
  // so the area rides an invisible disc under each one, exactly the pairing
  // the connector anchors above use.
  //
  // Clamped for the same reason as those, but against the gap between the
  // handles themselves (see `_syncGrabRadii`): four 44px zones on a shape
  // 50px across merge into one, and then no corner can be picked at all.
  // Recomputed as they move, so zooming out tightens them rather than
  // letting them swallow each other.
  var HANDLE_GRAB_RADIUS = 22;
  var HANDLE_GRAB_RADIUS_MIN = 8;

  // How far outside a shape's box (screen px) the pointer can wander while
  // that shape still counts as a snap candidate and shows its anchors. Wider
  // than the snap radius so the dots appear *before* the end jumps to one,
  // which is what makes the magnetism feel announced rather than abrupt.
  var ARROW_TARGET_PAD_PX = 48;

  // Screen-px geometry of the V at an arrow's head.
  var ARROW_HEAD_LEN = 11;
  var ARROW_HEAD_HALF_WIDTH = 6;
  // A connector's stroke weight, as a fraction of the board's own scale (the
  // median shape's bounding-box geometric mean). Calibrated so the shapes a
  // board is usually built from — a media card, a block a few hundred px
  // across — carry the 2px line connectors have always been drawn with.
  var ARROW_WEIGHT_RATIO = 0.011;
  // The weight a connector's shaft and a dimension's shaft are designed at,
  // in screen px. Everything else fixed in either of them — arrowheads, the
  // dimension's label and its halo — is kept in proportion to this, so a
  // whole connector or dimension scales as one object.
  var LINE_WEIGHT_PX = 2;


  // Connector dots are green rather than the shape's own colour: they're a
  // fixed piece of interface, like a cursor, and taking the shape's colour
  // made them read as part of the drawing. Matches yEd's palette — a pale
  // ring around a saturated core, which stays legible on both a light canvas
  // and a dark one.
  var CONNECTOR_DOT_CORE = "#6cb04f";
  var CONNECTOR_DOT_RING = "#c3e6b0";

  // Knot-spacing exponent for the marker's Catmull-Rom spline. 0 is uniform
  // (loops on sharp corners), 1 is chordal (slack on tight ones), 0.5 is
  // centripetal — the one that's provably cusp-free.
  var CATMULL_ALPHA = 0.5;
  // Dense samples per bend segment when flattening an arrow's curve for
  // geometry (hit test, label arc-length, bbox). Eight is plenty: bends
  // are sparse and the tolerances that consume this are generous.
  var ROUTE_SAMPLES = 8;
  // Breathing room past the outermost out-of-image stroke when the pan
  // bounds are loosened to reach it, in image px.
  var PAN_BOUNDS_PAD = 48;

  // How much of a marker's incoming pointer position is carried over from the
  // previous smoothed one: `smoothed += (raw - smoothed) * (1 - STREAMLINE)`.
  // The same exponential filter perfect-freehand calls "streamline", at the
  // same default. Raw pointer input carries hand tremor and, on a mouse,
  // device quantization; a spline drawn straight through those samples
  // reproduces every wobble faithfully. Higher = smoother but laggier and
  // more prone to cutting corners.
  var MARKER_STREAMLINE = 0.5;

  // …but only over distances a tremor covers. A constant filter cannot
  // tell a 1px wobble from an 8px stride, so it lags both — and the lag
  // rounds a corner off in the SAMPLES, before any later rule can see it
  // was a corner. Measured off a right angle: 3.2px of rounding at a
  // coarse pointer rate, 0.4px once the filter stands aside for movement
  // this size. The carry-over fades to nothing across this distance in
  // SCREEN px, so it behaves the same at every zoom.
  var MARKER_TREMOR_PX = 4;

  // The length a click's dot is given, in image px. See `_commitFreehand`:
  // it exists so the browser has something to hit-test, not so anything is
  // visible — the disc a round cap paints is the whole mark.
  var DOT_LENGTH = 0.01;

  // The smallest a stroke is allowed to be drawn, in SCREEN px, whatever the
  // zoom says. `_applyLineParams` has floored the outline kinds at 0.4 for a
  // while — "a shape you can no longer see is worse than one drawn slightly
  // heavier than the maths says" — and a marker had no floor at all.
  var MARKER_MIN_SCREEN_PX = 0.4;
  // The length a dot's path is DRAWN with, in device px — one pixel, the
  // smallest a rasteriser must treat as a real subpath.
  //
  // It is not a size. A dot's size is its round cap, which is the stroke
  // width, which scales with the zoom like every other bit of ink: a dot on
  // the board gets smaller as you pull away from it, exactly as the stroke
  // beside it does. This is only the hair of LENGTH underneath that cap, and
  // it exists because `DOT_LENGTH` — a hundredth of an IMAGE px — becomes a
  // ten-thousandth of a screen px on the way out, and a subpath that short is
  // one engines disagree about: Chrome paints it as a quarter-covered smudge
  // (measured, 64/255 against a real segment's 0/255) and WebKit drops
  // degenerate subpaths altogether, which is why an iPhone placed dots it
  // never drew until you zoomed in far enough to give the thing some length.
  // One device pixel is the least that always paints and the least that can
  // be seen, so the cap — not this — is what anyone is looking at.
  //
  // Stored geometry is untouched: this is how a dot is PAINTED, not what it
  // is.
  var DOT_RENDER_DEVICE_PX = 1;

  // The smallest a text box minted by a TAP may appear, in SCREEN px.
  //
  // The same 16 the ink default is, on purpose: the rule it makes is "never
  // smaller on screen than it would be at 1:1". A tap at 1:1 or zoomed in is
  // untouched by it — ink wins there, and boards keep the sizes they have —
  // and on the way out the box stops shrinking rather than vanishing.
  var NEW_TEXT_MIN_SCREEN_PX = 16;

  // The font size below which mobile Safari zooms the PAGE when an input is
  // focused. It is a fixed number in the engine, not a preference.
  var INPUT_PAGE_ZOOM_PX = 16;

  // How far the pointer must travel, in SCREEN px, before the marker takes
  // another sample. Screen rather than image px so a stroke has the same
  // fidelity at every zoom — the old image-px threshold sampled a
  // zoomed-out stroke far more finely than it could ever be drawn, and a
  // zoomed-in one too coarsely to look smooth.
  var MARKER_SAMPLE_PX = 1.5;

  // How sharp a turn has to be before the marker's spline treats it as a
  // corner rather than a curve: the cosine of the angle between the two
  // chords meeting at a sample. 0.5 is a 60-degree turn — gentle enough to
  // leave real curves alone (a circle sampled this finely turns a couple of
  // degrees a step), sharp enough to catch the angles a hand means.
  var CORNER_COS = 0.5;

  // Default size of an audio card, in image px. Resizable afterwards like
  // any box — this is just what a freshly dropped file lands at.
  var AUDIO_CARD_W = 360;
  var AUDIO_CARD_H = 88;
  // Default size of a video box, in image px — 16:9, big enough to watch.
  var VIDEO_BOX_W = 640;
  var VIDEO_BOX_H = 360;

  // Below this rendered height the card drops its title and timecode and
  // keeps only the transport, rather than overlapping its own text.
  var AUDIO_COMPACT_H = 54;

  // Gap between the dots of a "dotted" stroke, as a multiple of its width.
  // Shared by shapes and markers so the two read as the same dot pattern.
  var DOT_GAP_RATIO = 2.4;

  // A bare click with a shape tool places a default-sized shape centered on
  // the point, the way every comparable canvas tool does — the alternatives
  // were placing a degenerate shape whose corners all sat within a few
  // pixels (unusable: nothing to grab, nothing to see) or silently placing
  // nothing, which reads as the tool being broken. The threshold is SCREEN
  // px so a click means the same gesture at every zoom; the size is screen
  // px too, converted to image px at placement, so a click-placed square
  // looks the same on screen wherever the canvas happens to be.
  var CLICK_PLACE_THRESHOLD_PX = 5;
  var CLICK_PLACE_SIZE_PX = 120;

  // The shortest two-ended shape worth making, in screen px. Placing the far
  // end of a dimension, line or callout nearer than this is not a small
  // measurement, it is an accident — a double-click, or a second click that
  // landed about where the first did. Well clear of
  // `CLICK_PLACE_THRESHOLD_PX`, which answers a different question: that one
  // is hand jitter, "was this press-and-release a drag at all", and 5px of
  // slop is all it takes to answer it. This is "is there a shape here", and
  // a 10px dimension is not one however deliberately it was clicked.
  var MIN_SPAN_PX = 24;

  // How close (screen px) a dragged shape's edge or center has to come to
  // another shape's before it magnetizes. Screen px so the pull feels the
  // same at every zoom.
  var SNAP_THRESHOLD_PX = 6;

  // Stroke and fill styles, in the order they appear in the panel. Kept as
  // lists so the buttons, the persisted-value validation and the emitted
  // payload can't drift apart.
  var DASH_MODES = ["solid", "dashed", "dotted", "none"];
  var FILL_MODES = ["none", "semi", "solid", "pattern"];

  // Font size limits, in screen px. The floor is where text stops being
  // readable at 1:1; the ceiling is a label the size of a small shape.
  // Corner radius of a label's background plate, as a fraction of its
  // height. Well under 0.5, so it reads as a rounded card rather than a
  // pill however tall the label is set.
  var LABEL_BG_RADIUS_RATIO = 0.18;
  // A text box's corners, as Affinity offers them: rounded, cut straight
  // across, scooped inward, or notched out square. `style.corner` is the
  // type and `style.corner_size` how far it reaches, in canvas units like
  // a pinned font size — so it holds its size when the box is resized and
  // scales with the drawing on zoom. No size (the default) is a sharp
  // corner whatever the type.
  var TEXTBOX_CORNERS = ["round", "chamfer", "concave", "notch"];
  // How far in, as a fraction of the corner's reach, a text box's words
  // must start so their own corner stays inside the cut: the inner box's
  // corner (d, d) has to clear a quarter circle about the inside point
  // (round: d >= r(1 - 1/sqrt2)), the 45° cut (d >= r/2), a quarter circle
  // about the corner itself (d >= r/sqrt2), and a square bite (d >= r).
  var TEXTBOX_CORNER_INSET = { round: 1 - Math.SQRT1_2, chamfer: 0.5, concave: Math.SQRT1_2, notch: 1 };
  // The corner slider's reach, in on-screen px at the ink scale.
  var TEXTBOX_CORNER_MAX = 80;
  // The reach a corner type gets when it is picked on a sharp box: at no
  // reach every type looks the same, so a pick alone would show nothing.
  var TEXTBOX_CORNER_PICK = 12;
  // How far the rotation knob sits above a shape's top edge, and the text
  // box's scale dot out past its bottom-right corner, in screen px.
  var ROTATE_KNOB_PX = 26;
  var SCALE_DOT_PX = 16;

  // The weight sliders travel 0-100 and curve onto WEIGHT_MIN..WEIGHT_MAX,
  // so the weights people actually draw with sit around the middle of the
  // track instead of bunched in its first fifth. Squared: at the thin end
  // one notch is a hair, at the heavy end it is a step — which is how
  // thickness reads to the eye, and it leaves the same ceiling reachable
  // as the old linear 1..40.
  var WEIGHT_MIN = 1;
  var WEIGHT_MAX = 40;
  var WEIGHT_CURVE = 2.2;

  // The canvas size the panel's numbers are quoted against: a thickness of
  // 5 means "5 as it would look on a 1000px canvas", on every image. See
  // _canvasInkScale.
  var REFERENCE_CANVAS_PX = 1000;
  var FONT_SIZE_MIN = 6;
  var FONT_SIZE_MAX = 200;
  // What a new label starts at when the user has not chosen a size. There
  // was no default before — an unset size meant box-drives-font, which
  // sized the editor off the default title box at the current zoom and the
  // committed label off ANOTHER box measurement: the size felt random
  // while typing and then visibly shrank at placement. A real number,
  // stored through the same ink-scaled pipeline as a hand-picked size, is
  // stable against the canvas and identical in the editor and the commit.
  var DEFAULT_LABEL_FONT_SIZE = 16;

  // The blue every selected thing is outlined in, and how far the outline
  // stands proud of the shape's own stroke on each side — matched to the
  // `feMorphology` dilate radius the silhouette filter uses, so a hatched
  // shape and a plain one read as equally selected.
  var SELECTION_BLUE = "#3b82f6";
  var OUTLINE_BAND_PX = 1.6;
  // Copied from the shape onto its traced outline — except these. The dash
  // and width are the shape's own look, `class`/`style` are the outline's
  // own, `filter` would ring the tracing itself, and a second element
  // answering to the same uuid would confuse every lookup that goes by it.
  var SKIP_OUTLINE_ATTRS = {
    "class": 1, style: 1, filter: 1,
    "stroke-dasharray": 1, "stroke-width": 1, "data-uuid": 1
  };

  // A pasted image is persisted immediately as a reduced copy, so the shape
  // has something to draw before its upload finishes — on a reload
  // mid-upload, on a peer's screen, and permanently if the upload never
  // lands (a closed tab strands it there). That last case is why this is
  // sized to be *looked at* rather than to be a thumbnail: 1600px is twice
  // the 800 canvas px a placed image occupies, so it holds up zoomed in.
  //
  // The budget is the counterweight. This copy rides in the annotation list,
  // which is re-sent on every edit, so it must stay far below the original —
  // `_encodePreview` steps down through cheaper encodings until it fits.
  // The local canvas draws the full-quality original throughout; this is
  // what gets *saved*, not what you are looking at.
  // Shared by the compact layout's two halves — the width `_computeToolbar-
  // Overflow` leaves free and the offsets `_positionStyleChrome` measures.
  // They have to agree or the trigger lands on top of the bar.
  var CHROME_MARGIN = 12;
  var CHROME_GAP = 8;

  // A pasted, dropped or inserted image is drawn this share of what the person
  // can see right now (each way, aspect kept), so it is usable at whatever zoom
  // they happen to be at. A fixed size in canvas units cannot be: 800 units is
  // most of a 1600-wide picture and a speck on a 12000-wide board at its
  // default zoom.
  var INSERT_VIEW_SHARE = 0.5;
  // Used only where nothing can be measured (no viewport yet): the longest side
  // in canvas units, and never above the picture's own size.
  var INSERT_FALLBACK_MAX_SIDE = 800;
  var PREVIEW_MAX_SIDE = 1600;
  var PREVIEW_QUALITY = 0.85;
  var PREVIEW_BYTE_BUDGET = 400 * 1024;

  // Where a shape's label sits, as `metadata.title_align = {h, v}`. Absent
  // means the label floats above the shape with a leader line — the default,
  // and where dragging or resizing the label puts it back.
  var TITLE_H_ALIGNS = ["left", "center", "right"];
  var TITLE_V_ALIGNS = ["top", "middle", "bottom"];

  function normalizeTitleAlign(raw) {
    if (!raw || typeof raw !== "object") return null;
    if (TITLE_H_ALIGNS.indexOf(raw.h) === -1) return null;
    if (TITLE_V_ALIGNS.indexOf(raw.v) === -1) return null;
    return { h: raw.h, v: raw.v };
  }

  // Place a `size`-sized label inside `bbox` at the `align` anchor. The
  // inset keeps an edge-anchored label off the outline it would otherwise
  // sit on. It's a fraction of the shape rather than a pixel count because
  // these are image px: a fixed inset renders proportionally to the zoom,
  // and on a large canvas shown small it collapses to nothing.
  function alignedBox(bbox, size, align) {
    var inset = Math.min(bbox.w, bbox.h) * 0.05;
    var x = align.h === "left"  ? bbox.x + inset
          : align.h === "right" ? bbox.x + bbox.w - size.w - inset
          :                       bbox.x + (bbox.w - size.w) / 2;
    var y = align.v === "top"    ? bbox.y + inset
          : align.v === "bottom" ? bbox.y + bbox.h - size.h - inset
          :                        bbox.y + (bbox.h - size.h) / 2;
    return { x: x, y: y, w: size.w, h: size.h };
  }

  // Hatch geometry for "pattern" fill, in screen px (like stroke width —
  // shapes re-render on zoom, so a userSpaceOnUse pattern holds its size
  // on screen instead of growing with the image).
  // Tile is square so a single corner-to-corner diagonal tiles seamlessly;
  // the lines end up SIZE/sqrt(2) apart, about 5.7px.
  var HATCH_SIZE = 8;
  var HATCH_WIDTH = 1.4;

  // Bumped per Etcher instance that ends up needing a hatch, to keep the
  // `<pattern>` ids of two overlays on one page apart.
  var HATCH_SEQ = 0;
  // Same per-instance uniqueness story for the shaft-label gap masks.
  var SHAFT_GAP_SEQ = 0;

  // How much of the computed margin a shaft-riding label actually takes
  // out of its line. One dial, being tuned by eye against real drawings —
  // the shape of the margin (proportional to the label, floored at the
  // line's own half-width) is settled; how much of it reads as neat is
  // not. See `_syncShaftLabelGap`.
  var SHAFT_LABEL_GAP_SCALE = 0.25;

  var TOOL_DEFS = {
    rectangle: { icon: ICONS.rectangle, title: "Rectangle" },
    circle:    { icon: ICONS.circle,    title: "Circle" },
    polygon:   { icon: ICONS.polygon,   title: "Polygon (double-click to close)" },
    freehand:  { icon: ICONS.freehand,  title: "Freehand curve (editable — drag its nodes after drawing)" },
    marker:    { icon: ICONS.marker,    title: "Marker (freehand ink stroke)" },
    highlighter: {
      icon: ICONS.highlighter,
      title: "Highlighter (marker ink at half opacity)"
    },
    // `styleless` tools take no stroke or fill, so the style panel has
    // nothing to offer while one is armed — _syncStylePanel hides it.
    grabber:   { icon: ICONS.grabber,   title: "Grab (pan only)", styleless: true },
    callout:   { icon: ICONS.callout,   title: "Callout (point at something, write a label)" },
    text:      { icon: ICONS.text,      title: "Text (click and type, or drag to set the size)" },
    // Distinct from `text`, whose box hugs its content: this one's box is
    // the user's — drawn once, resized only by its handles. The text wraps
    // and sizes INSIDE it (Label size in the panel), and typing more or
    // picking a bigger font never moves the walls. Same kind on the wire
    // (`text` + `style.box: "fixed"`), so every consumer stores it today.
    textbox:   { icon: ICONS.textbox,   title: "Text box (a fixed box; the text wraps and sizes inside it)" },
    dimension: { icon: ICONS.dimension, title: "Dimension (line with arrows + slidable label)" },
    line:      { icon: ICONS.line,      title: "Line" },
    // Distinct from `dimension`, which is a MEASUREMENT: two heads and a
    // label it drops you into writing. This one points — one head, no
    // label unless you double-click for one like any other shape.
    arrow:     { icon: ICONS.arrow,     title: "Arrow (line with one head)" },
    eraser:    { icon: ICONS.eraser,    title: "Eraser (click and drag to wipe shapes)" },
    pointer:   { icon: ICONS.pointer,   title: "Red pointer (everyone on the board sees where you point)" },
    // `momentary` tools are one-shot actions, not persistent drawing modes:
    // clicking fires an action and leaves the current tool selection alone
    // (see `_invokeMomentaryTool`). Image insertion opens the OS file picker
    // (or hands off to the host) rather than arming a draw gesture.
    image:     { icon: ICONS.image,     title: "Insert image", momentary: true }
  };

  // Single-key tool shortcuts, tldraw/Figma dialect where the tools
  // overlap: V select, H hand, D draw, R rectangle, O circle, L line,
  // T text, E eraser, A arrow. The rest take the tool's initial (M marker,
  // P polygon, C callout) and N for dimensioN, D being taken. `null`
  // means the cursor. `image` has no key on purpose: it is a momentary
  // action that opens the OS file picker, which is a startling thing for
  // a keystroke to do.
  var TOOL_SHORTCUTS = {
    v: null,
    h: "grabber",
    d: "freehand",
    m: "marker",
    g: "highlighter",
    e: "eraser",
    r: "rectangle",
    o: "circle",
    p: "polygon",
    l: "line",
    a: "arrow",
    n: "dimension",
    c: "callout",
    t: "text"
  };

  // ===========================================================================
  // Tool glyphs and the cursors built from them.
  //
  // Every drawing tool shares ONE cursor: a small crosshair, drawn twice
  // (wide white underlay, then black) so it stays legible over any imagery.
  // It used to carry the armed tool's glyph as a badge at bottom-right;
  // that read as clutter hanging off the pointer, and the toolbar already
  // says which tool is held. The glyphs stay — `toolBadge(key)` hands them
  // to collaborative hosts for drawing what a PEER is holding, where the
  // question is real because that toolbar is on someone else's screen.
  //
  // Built lazily into data-URI cursor values; plain `crosshair` remains the
  // fallback for browsers that reject SVG cursors. The cursor tool and the
  // grabber are the two exceptions, each taking its own full-size glyph as
  // its pointer (`cursorToolCursor` / `grabberCursor`) — for those two the
  // cursor IS the tool, rather than a badge hung off a crosshair.
  // ===========================================================================

  // The highlighter's fixed transparency. A constant, not a setting: the
  // whole reason it is a separate tool is that the user should never have
  // to dial the marker's opacity back and forth between writing and
  // highlighting. Half, per the head dev's spec.
  var HIGHLIGHT_OPACITY = 0.5;
  // …and the colour a first-time highlighter starts in. The marker
  // inherits whatever the palette held when it was first armed, but a
  // highlighter that starts in the same blue as the pen is the complaint
  // this answers — out of the box the two ink tools must not match.
  var HIGHLIGHT_DEFAULT_COLOR = "#facc15";
  // …and its starting palette: the classic highlighter set, the
  // transparent-marker colours, yellow first to match the default above.
  var HIGHLIGHT_DEFAULT_SLOTS =
    ["#facc15", "#4ade80", "#f472b6", "#fb923c", "#38bdf8"];

  // The pen's own set, and its first colour: black to write with, then
  // strong contrasting hues that hold up over a photograph. Seeding the
  // marker from the shared palette (what it used to do) meant the pen came
  // up in whatever the boxes happened to be drawn in — back when those
  // were pastels, usually a pale outline colour, which is the wrong thing
  // to write with. The shapes draw in strong colours too now, so the two
  // sets agree; what the pen still owns is leading with black.
  // …and the weights the two come up at. A highlighter is a chisel tip: at
  // the pen's weight it was a thin line that happened to be see-through,
  // which reads as a faded marker rather than as highlighting. Three times
  // the pen, so the tool announces what it is on the first stroke — and
  // both stay ordinary panel weights the user can dial from there (the
  // slider's curve puts them at roughly 40% and 68% of its travel).
  var MARKER_DEFAULT_WIDTH = 6;
  var HIGHLIGHT_DEFAULT_WIDTH = 18;

  var MARKER_DEFAULT_COLOR = "#111111";
  var MARKER_DEFAULT_SLOTS =
    ["#111111", "#ef4444", "#2563eb", "#16a34a", "#ffffff"];

  var CURSOR_BADGES = {
    rectangle: '<rect x="4" y="6" width="16" height="12" rx="1.5"/>',
    circle:    '<circle cx="12" cy="12" r="7.5"/>',
    polygon:   '<path d="M12 3.5 21 9.5 18 20H6L3 9.5 12 3.5Z"/>',
    freehand:  '<g transform="rotate(-10 12 12)"><path d="M4.5 15.5A7.6 7.6 0 0 1 19.5 15.5"/><path stroke-dasharray="2.4 2" d="M4.5 15.5 19.5 15.5"/><circle cx="4.5" cy="15.5" r="1.5"/><circle cx="19.5" cy="15.5" r="1.5"/></g>',
    marker:    '<g transform="rotate(45 12 12)"><path d="M10 2h4a1 1 0 0 1 1 1v7h-6V3a1 1 0 0 1 1-1Z"/><path d="M9 10h6l-1 4h-4l-1-4Z"/><path d="M11.2 14h1.6v3h-1.6z"/></g><path d="M3 21c3-1.5 6-1.5 8-1"/>',
    highlighter: '<g transform="rotate(45 12 12)"><path d="M10 2h4a1 1 0 0 1 1 1v7h-6V3a1 1 0 0 1 1-1Z"/><path d="M9 10h6l-1 4h-4l-1-4Z"/><path d="M10.4 14h3.2v2.4h-3.2z"/></g><path stroke-width="4" opacity="0.45" d="M4 20.5h9"/>',
    // Shared by two consumers: hosts drawing OTHER people (without it,
    // someone panning is the only tool that shows as an anonymous arrow)
    // and `grabberCursor` below, which builds the LOCAL pointer from it —
    // so toolbar button, remote badge and cursor are one hand by
    // construction. The grabber keeps its hand cursor: it is the whole
    // pointer, not a badge on a crosshair.
    grabber:   '<path stroke-linecap="round" stroke-linejoin="round" d="M18 11V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2"/><path stroke-linecap="round" stroke-linejoin="round" d="M14 10V4a2 2 0 0 0-2-2 2 2 0 0 0-2 2v2"/><path stroke-linecap="round" stroke-linejoin="round" d="M10 10.5V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v8"/><path stroke-linecap="round" stroke-linejoin="round" d="m7 15-1.76-1.76a2 2 0 0 0-2.83 2.82l3.6 3.6C7.5 21.14 9.2 22 12 22h2a8 8 0 0 0 8-8V7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v3"/>',
    callout:   '<circle cx="3.5" cy="20" r="2"/><path d="M4 19.5 8.5 14 21 14"/>',
    text:      '<path d="M5 6h14M12 6v12"/>',
    dimension: '<path d="M5 12h14M5 12l3-3M5 12l3 3M19 12l-3-3M19 12l-3 3"/>',
    line:      '<path d="M5 19 19 5"/>',
    arrow:     '<path d="M5 19 19 5M19 5 12.4 7.4M19 5 16.6 11.6"/>',
    eraser:    '<path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21"/><path d="M22 21H7"/><path d="m5 11 9 9"/>'
  };

  // The grabber's pointer is the toolbar hand itself — the parity every
  // drawing app keeps, where arming the hand tool puts the same hand under
  // your finger. Native `grab` draws the OS's hand, which matches nothing
  // in the toolbar. Same white-underlay/black-stroke legibility trick as
  // toolCursor, but full-size and hotspot-centered: a hand has no
  // precision point, so its middle is the pointer's middle. `grab` stays
  // as the fallback for browsers that reject SVG cursors.
  // The grabbing fist: the open hand's palm verbatim, fingers curled to
  // knuckle loops (tops pulled from y2-4 down to y8-10). Shown while a
  // grabber drag is actually panning — a grab cursor that never closes
  // reads as a static picture, not a hand holding the canvas.
  var GRAB_CLOSED =
    '<path stroke-linecap="round" stroke-linejoin="round" d="M18 12v-2a2 2 0 0 0-2-2 2 2 0 0 0-2 2"/>' +
    '<path stroke-linecap="round" stroke-linejoin="round" d="M14 11v-1a2 2 0 0 0-2-2 2 2 0 0 0-2 2v1"/>' +
    '<path stroke-linecap="round" stroke-linejoin="round" d="M10 11v-1a2 2 0 0 0-2-2 2 2 0 0 0-2 2v4"/>' +
    '<path stroke-linecap="round" stroke-linejoin="round" d="m7 15-1.76-1.76a2 2 0 0 0-2.83 2.82l3.6 3.6C7.5 21.14 9.2 22 12 22h2a8 8 0 0 0 8-8v-3a2 2 0 0 0-2-2 2 2 0 0 0-2 2v1"/>';

  var grabberCursorCache = {};
  function grabberCursor(closed) {
    var key = closed ? "closed" : "open";
    if (grabberCursorCache[key]) return grabberCursorCache[key];
    var hand = closed ? GRAB_CLOSED : CURSOR_BADGES.grabber;
    // Solid white glove with a black contour. The glyph's own paths are
    // OPEN (a fill closes each along a chord and leaves palm gaps), so a
    // dedicated CLOSED silhouette underlay covers the whole hand —
    // fingers block, pinky column, palm bowl, thumb heel — and the glyph
    // passes draw silhouette edge + finger lines on top of it.
    // Palm-only, tucked INSIDE the glyph: the 4.5 white understrokes are
    // wide enough that adjacent finger strokes overlap and fill the
    // fingers themselves — the palm pocket is the one true hole. A full
    // outer silhouette was tried first and its top slab stuck out past
    // the shorter fingers, whose tips step at different heights.
    var silhouette =
      '<path d="M5 11.5H20.5V14Q20.5 20.5 14 20.5H12' +
      'Q9 20.5 7.2 18.2L4.2 15Z"/>';
    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">' +
      '<g transform="translate(2 2)">' +
      '<g fill="#fff" stroke="#fff" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round">' + silhouette + hand + '</g>' +
      '<g fill="none" stroke="#000" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' + hand + '</g>' +
      '</g></svg>';
    grabberCursorCache[key] =
      'url("data:image/svg+xml,' + encodeURIComponent(svg) + '") 14 14, ' +
      (closed ? "grabbing" : "grab");
    return grabberCursorCache[key];
  }

  // The armed cursor tool shows its own toolbar arrow, not the OS one —
  // the same parity the grabber got. Solid white with a black contour
  // (the path is closed, so it fills without a silhouette), hotspot on
  // the arrow's TIP at glyph (4,4) → svg (6,6): unlike the hand, the
  // select arrow has a precision point and it must be where clicks land.
  var cursorToolCursorCache = null;
  function cursorToolCursor() {
    if (cursorToolCursorCache) return cursorToolCursorCache;
    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">' +
      '<g transform="translate(2 2)">' +
      '<g fill="#fff" stroke="#fff" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">' + ARROW_PATH + '</g>' +
      '<g fill="#fff" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' + ARROW_PATH + '</g>' +
      '</g></svg>';
    cursorToolCursorCache =
      'url("data:image/svg+xml,' + encodeURIComponent(svg) + '") 6 6, default';
    return cursorToolCursorCache;
  }

  // One value for every tool that gets one, so it is built once. The
  // `CURSOR_BADGES` lookup is kept purely as the "is this a tool we draw a
  // cursor for" test — a tool with no glyph still answers `null` here and
  // falls back to the stylesheet's crosshair, exactly as before.
  var toolCursorCache = null;
  function toolCursor(key) {
    if (!key || !CURSOR_BADGES[key]) return null;
    if (toolCursorCache) return toolCursorCache;
    var cross = '<path d="M6 1.5v9M1.5 6h9"/>';
    // The 30x30 canvas is inherited from the badged version, where the
    // glyph sat at (14,14). Only the top-left corner is inked now, which
    // costs nothing and keeps the 6,6 hotspot exactly where it was.
    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">' +
      '<g fill="none" stroke="#fff" stroke-width="3.5" stroke-linecap="round">' + cross + '</g>' +
      '<g fill="none" stroke="#000" stroke-width="1.5" stroke-linecap="round">' + cross + '</g>' +
      '</svg>';
    toolCursorCache =
      'url("data:image/svg+xml,' + encodeURIComponent(svg) + '") 6 6, crosshair';
    return toolCursorCache;
  }

  // Default color palette — a full-strength rainbow plus monochrome
  // bookends. The first five are the five slots a new user starts with
  // (see `_sanitizeColorSlots`), so they carry most of the weight: they
  // have to be told apart at a glance AND hold up drawn over a
  // photograph, which is the whole job here.
  //
  // These were pastels until 2026-09-20 — Tailwind's 300s, chosen to look
  // calm in the panel. On a picture they read as washed out, and a thin
  // outline in one is close to invisible against a light background; the
  // marker had already been given ink colours of its own for exactly that
  // reason. Strong hues now, from the same family the marker draws in, so
  // a rectangle and a scribble around the same thing match.
  //
  // Consumers override the set via `window.Etcher.colorSwatches`. The
  // default active color is the blue so the picker has a non-empty
  // selected state on first open; consumers override via
  // `window.Etcher.defaultColor`.
  var DEFAULT_COLOR_SWATCHES = [
    { key: "red",    color: "#ef4444", title: "Red" },
    { key: "orange", color: "#f97316", title: "Orange" },
    { key: "yellow", color: "#facc15", title: "Yellow" },
    { key: "green",  color: "#16a34a", title: "Green" },
    { key: "blue",   color: "#2563eb", title: "Blue" },
    { key: "indigo", color: "#4f46e5", title: "Indigo" },
    { key: "violet", color: "#9333ea", title: "Violet" },
    { key: "white",  color: "#ffffff", title: "White" },
    { key: "black",  color: "#000000", title: "Black" }
  ];

  function resolveColorSwatches() {
    var custom = window.Etcher && window.Etcher.colorSwatches;
    return Array.isArray(custom) && custom.length ? custom : DEFAULT_COLOR_SWATCHES;
  }

  function resolveDefaultColor() {
    var swatches = resolveColorSwatches();
    if (window.Etcher && typeof window.Etcher.defaultColor === "string") {
      return window.Etcher.defaultColor;
    }
    // Prefer the blue swatch (back-compat with the pre-pluggable
    // default — the hue, not the shade: it went from pastel to full
    // strength with the rest of the set), then fall back to the first.
    var blue = swatches.find(function(s) { return s.key === "blue"; });
    return blue ? blue.color : swatches[0].color;
  }

  var SVG_NS = "http://www.w3.org/2000/svg";

  function svgEl(name, attrs) {
    var el = document.createElementNS(SVG_NS, name);
    if (attrs) {
      for (var k in attrs) {
        if (Object.prototype.hasOwnProperty.call(attrs, k)) {
          el.setAttribute(k, attrs[k]);
        }
      }
    }
    return el;
  }

  // UUIDv7 generator — 48 bits of unix-ms timestamp + 74 bits of random
  // (with 4 bits version + 2 bits variant in their reserved positions).
  // Replaces 0.2.x's tmp-id round-trip: every shape gets its permanent
  // uuid at draw time, so the server never has to assign one. UUIDv7
  // sorts lexicographically by creation time, which is also nice for
  // debugging.
  function genUuidV7() {
    var nowMs = Date.now();
    var hexTs = nowMs.toString(16).padStart(12, "0");           // 48 bits = 12 hex chars
    var rand = new Uint8Array(10);
    (window.crypto || window.msCrypto).getRandomValues(rand);
    // Version = 7 in the top nibble of byte 6 (after the timestamp).
    rand[0] = (rand[0] & 0x0f) | 0x70;
    // Variant = 10 in the top two bits of byte 8.
    rand[2] = (rand[2] & 0x3f) | 0x80;
    var hexRand = "";
    for (var i = 0; i < rand.length; i++) {
      hexRand += rand[i].toString(16).padStart(2, "0");
    }
    return (
      hexTs.slice(0, 8) + "-" +
      hexTs.slice(8, 12) + "-" +
      hexRand.slice(0, 4) + "-" +
      hexRand.slice(4, 8) + "-" +
      hexRand.slice(8, 20)
    );
  }

  // Parse a `data-*-buttons`-style allowlist with three states:
  //   - attribute absent (`undefined`) → null (caller treats as "all on")
  //   - `"none"` sentinel → empty Set (caller treats as "everything hidden")
  //   - CSV → Set of trimmed names
  // Mirrors the Fresco-side convention so consumers see the same
  // semantics across both libraries.
  function parseAllowlistAttr(value) {
    if (value == null) return null;
    if (value === "none") return new Set();
    return new Set(
      value.split(",").map(function(s) { return s.trim(); }).filter(Boolean)
    );
  }

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // Stable escape helper for consumer slot impls so they don't have to
  // duplicate one.
  window.Etcher.escapeHtml = escapeHtml;

  // ---------------------------------------------------------------------------
  // Color math — minimal HSL ⇄ hex helpers for the custom-color picker.
  // Saturation is fixed at 100% throughout (the picker only varies hue +
  // lightness), but the conversions take the full HSL triple for clarity.
  // ---------------------------------------------------------------------------

  function hslToRgb(h, s, l) {
    h = ((h % 360) + 360) % 360;
    s = Math.max(0, Math.min(100, s)) / 100;
    l = Math.max(0, Math.min(100, l)) / 100;
    var c = (1 - Math.abs(2 * l - 1)) * s;
    var hp = h / 60;
    var x = c * (1 - Math.abs((hp % 2) - 1));
    var r1 = 0, g1 = 0, b1 = 0;
    if (hp < 1)      { r1 = c; g1 = x; b1 = 0; }
    else if (hp < 2) { r1 = x; g1 = c; b1 = 0; }
    else if (hp < 3) { r1 = 0; g1 = c; b1 = x; }
    else if (hp < 4) { r1 = 0; g1 = x; b1 = c; }
    else if (hp < 5) { r1 = x; g1 = 0; b1 = c; }
    else             { r1 = c; g1 = 0; b1 = x; }
    var m = l - c / 2;
    return [
      Math.round((r1 + m) * 255),
      Math.round((g1 + m) * 255),
      Math.round((b1 + m) * 255)
    ];
  }

  function hslToHex(h, s, l) {
    var rgb = hslToRgb(h, s, l);
    function pad(n) { var s = n.toString(16); return s.length === 1 ? "0" + s : s; }
    return "#" + pad(rgb[0]) + pad(rgb[1]) + pad(rgb[2]);
  }

  // Best-effort hex parse → HSL. Used to position the picker's knobs
  // when a recent color is re-selected. Returns null on malformed
  // input — callers fall back to a sensible default (hue 0, l 50).
  function hexToHsl(hex) {
    if (typeof hex !== "string") return null;
    var m = hex.trim().match(/^#?([0-9a-f]{6})$/i);
    if (!m) return null;
    var n = parseInt(m[1], 16);
    var r = ((n >> 16) & 0xff) / 255;
    var g = ((n >> 8) & 0xff) / 255;
    var b = (n & 0xff) / 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var d = max - min;
    var l = (max + min) / 2;
    var h = 0, s = 0;
    if (d !== 0) {
      s = d / (1 - Math.abs(2 * l - 1));
      switch (max) {
        case r: h = ((g - b) / d) % 6; break;
        case g: h = (b - r) / d + 2; break;
        default: h = (r - g) / d + 4;
      }
      h *= 60;
      if (h < 0) h += 360;
    }
    return { h: h, s: s * 100, l: l * 100 };
  }

  // How long a hover-shown tooltip dwells before it auto-closes. The
  // timer starts when the tooltip is shown and is independent of where
  // the cursor goes afterward — move off the shape and it still rides
  // out the full window. Pinned tooltips ignore this entirely (they
  // close only on an explicit click). Hovering the tooltip itself
  // pauses the countdown so the delete button stays reachable.
  var TOOLTIP_DWELL_MS = 1600;

  // How far the cursor may travel before a shown tooltip takes it as "the
  // user has moved on". A tooltip is a PEEK: it arrives when the cursor
  // settles and leaves when the cursor doesn't — sitting out a timer while
  // someone is plainly doing something else is what made it feel like it
  // was hanging around waiting. Bigger than hand jitter, smaller than a
  // deliberate reach; it closes through the same fade as everything else,
  // and through the usual bridge, so a reach TOWARD the tooltip still
  // lands on it.
  var TOOLTIP_PEEK_PX = 14;

  // Hover INTENT: the cursor has to settle on a shape for this long before
  // a tooltip appears at all. Crossing a drawing to reach something else
  // used to flash a tooltip per shape on the way — the single biggest
  // reason the thing felt busy. Once one IS open, moving between shapes
  // updates it immediately (see _hoverTooltip): the delay buys quiet, and
  // charging it twice would just feel slow.
  var TOOLTIP_OPEN_DELAY_MS = 350;

  // Fade length, in and out. Long enough to read as a fade, short enough
  // that dismissing never feels like waiting.
  var TOOLTIP_FADE_MS = 110;

  // Neutral defaults read from generic, non-comment-specific metadata
  // keys — a consumer who just populates these gets a working tooltip
  // without registering any custom slots.
  //   metadata.title    → header (else capitalized shape.kind)
  //   metadata.body     → body (raw HTML; the consumer owns escaping)
  //   metadata.subtitle → footer ("date · count"-style sub-line)
  var DEFAULT_TOOLTIP_SLOTS = {
    header: function(shape) {
      var m = shape.metadata || {};
      return escapeHtml(m.title || shape.kind || "");
    },
    body: function(shape) {
      var m = shape.metadata || {};
      return m.body || null;
    },
    footer: function(shape) {
      var m = shape.metadata || {};
      return m.subtitle ? escapeHtml(m.subtitle) : null;
    }
  };

  // Slot resolver: custom > default > null. Errors in consumer slots
  // are swallowed (warn-only) so a broken override can't take down
  // the whole tooltip; the corresponding default kicks in instead.
  function resolveSlot(name, shape) {
    var slots = window.Etcher.tooltipSlots || {};
    var custom = slots[name];
    if (typeof custom === "function") {
      try {
        var result = custom(shape);
        if (result != null) return result;
      } catch (e) {
        if (window.console && console.warn) {
          console.warn("[Etcher] tooltipSlots." + name + " threw:", e);
        }
      }
    }
    return DEFAULT_TOOLTIP_SLOTS[name](shape);
  }

  // ===========================================================================
  // EtcherLayer LiveView hook
  // ===========================================================================

  window.EtcherHooks = window.EtcherHooks || {};

  window.EtcherHooks.EtcherLayer = {
    mounted: function() {
      injectStyles();

      var self = this;
      self.frescoId = self.el.dataset.frescoId;

      // Another etcher layer on this page saving its prefs: take them. All
      // instances share one store, but each caches it in memory, so without
      // this a label colour picked on one layer never reached a second live
      // layer (same-tab writes fire no 'storage' event). Peer events only —
      // our own dispatch starts on our el, and hydrating from it would be a
      // no-op that risks loops.
      self._peerPrefsHandler = function(e) {
        if (self.el && (e.target === self.el || self.el.contains(e.target))) return;
        self._hydratePrefs(e.detail);
      };
      document.addEventListener("etcher:prefs-changed", self._peerPrefsHandler);

      try {
        self.tools = JSON.parse(self.el.dataset.tools || "[]");
      } catch (_) { self.tools = ["rectangle", "circle", "polygon", "freehand"]; }

      // Chrome-allowlist parsing — same `"none"` sentinel convention
      // as Fresco's `:nav_buttons` / `:gestures`. `null` here means
      // "no allowlist set; everything enabled"; an empty Set means
      // "explicit hide-all." `_chromeEnabled(name)` consults the
      // Set for per-button gates.
      self._navButtonAllowlist = parseAllowlistAttr(self.el.dataset.navButtons);
      self.showToolbar = self.el.dataset.toolbar !== "false";

      // Image tool + paste behavior.
      //   image_source: "file_picker" (default) — the image tool opens the OS
      //     file picker and inserts the chosen file as a data URL. "custom" —
      //     the tool instead emits `etcher:image-insert-requested` (LiveView
      //     hook event + bubbling DOM CustomEvent) so the host opens its own
      //     uploader/modal, then calls `layerFor(id).insertImage(href)`.
      //   paste_images (default true): an image pasted onto the canvas is
      //     inserted automatically. Disable per-layer to opt out.
      self.imageSource = self.el.dataset.imageSource === "custom" ? "custom" : "file_picker";
      self.pasteImages = self.el.dataset.pasteImages !== "false";

      self.shapes = [];           // { uuid, kind, geometry, style?, metadata?, el }
      self.activeTool = null;     // null = cursor mode
      self.annotationMode = false;
      // Default color comes from `window.Etcher.defaultColor` (else the
      // blue swatch in the active palette, else the first swatch) so
      // the picker has a non-empty selected state on first open and
      // consumers can override the starting color.
      // Fixed editable color slots — seeded for real in `_buildToolbar`
      // (and `_renderInitial` for headless layers) once the handle and
      // its `extensions.etcher.colors` are readable. `activeColor` starts
      // at the configured default and is re-pointed at the selected slot
      // when the palette seeds.
      self._colorSlots = [];      // up to COLOR_SLOTS hex strings
      self._activeSlot = 0;       // index of the selected slot
      self.activeColor = resolveDefaultColor();
      self.draftState = null;     // per-tool drawing state

      if (!self.frescoId) {
        console.warn("[Etcher] Missing data-fresco-id on layer host", self.el);
        return;
      }

      if (!window.Fresco || !window.Fresco.onReady) {
        console.warn("[Etcher] Fresco not loaded — load fresco.js before etcher.js");
        return;
      }

      window.Fresco.onReady(self.frescoId, function(handle) {
        self.handle = handle;
        self._whenCanvasReady(function() { self._init(); });
      });

      // Register this layer in the public `window.Etcher.layerFor`
      // registry so external code can drive it programmatically.
      // Every toolbar / nav button delegates to the same primitives
      // these methods call, so a consumer can run the whole layer
      // without rendering the built-in UI if they want.
      layerRegistry[self.frescoId] = {
        api: {
          // Mode + visibility ----------------------------------------
          getMode: function() { return self.annotationMode; },
          setMode: function(on) { self._setAnnotationMode(!!on); },
          toggleMode: function() { self._setAnnotationMode(!self.annotationMode); },
          isVisible: function() { return self.annotationsVisible !== false; },
          setVisible: function(on) {
            var want = !!on;
            if ((self.annotationsVisible !== false) !== want) {
              self._toggleAnnotationsVisible();
            }
          },
          toggleVisible: function() { self._toggleAnnotationsVisible(); },

          // Tool select -----------------------------------------------
          // `null` selects the cursor (no drawing tool active).
          getTool: function() { return self.activeTool; },
          selectTool: function(toolKey) {
            self._selectTool(toolKey == null ? null : toolKey);
          },
          tools: function() { return (self.tools || []).slice(); },
          exitDrawing: function() { self._selectTool(null); },

          // Color -----------------------------------------------------
          getColor: function() { return self.activeColor; },
          setColor: function(color) { self._selectColor(color); },
          // The fixed editable palette. `getColors` returns a copy;
          // `setColors` replaces the whole palette (clamped/backfilled to
          // COLOR_SLOTS); `setSlotColor` overwrites one slot. Programmatic
          // setters don't auto-persist — the consumer decides when to
          // call them and owns storage (they fire no `etcher:colors-changed`).
          getColors: function() { return (self._colorSlots || []).slice(); },
          setColors: function(arr) {
            self._colorSlots = self._sanitizeColorSlots(arr);
            if (self._activeSlot >= self._colorSlots.length) self._activeSlot = 0;
            self._refreshToolbarSwatches();
            self._selectColor(self._colorSlots[self._activeSlot]);
          },
          setSlotColor: function(i, hex) { self._setSlotColor(i, hex); },
          swatches: function() {
            return resolveColorSwatches().map(function(s) {
              return { color: s.color, title: s.title };
            });
          },

          // Line params (global stroke defaults) — parity with the colors
          // API. `getLineParams` returns a copy of the current overrides;
          // `setLineParams` applies a `{width, opacity, dash}` map (clamped,
          // per-key fallback) WITHOUT firing `etcher:line-params-changed` —
          // the consumer drives it and owns storage, same as `setColors`.
          getLineParams: function() {
            return Object.assign({}, self.lineParams || {});
          },
          setLineParams: function(map) { self._setLineParamsDirect(map); },

          // History ---------------------------------------------------
          undo: function() { self._undo(); },
          redo: function() { self._redo(); },
          canUndo: function() { return (self._undoStack || []).length > 0; },
          canRedo: function() { return (self._redoStack || []).length > 0; },

          // Shape selection + edit ------------------------------------
          getShapes: function() {
            return self.shapes.map(function(s) { return self._shapeDescriptor(s); });
          },
          getShape: function(uuid) {
            var s = self.shapes.find(function(x) { return x.uuid === uuid; });
            return s ? self._shapeDescriptor(s) : null;
          },

          // Hit-test a point against the current shapes. Returns the
          // top-most shape under `pt` as a shape descriptor (same
          // shape as `getShape`), or `null`. Coordinate space matches
          // the active Fresco handle:
          //   • strip:  pt = { imageIdx, x, y }  (per-image source px)
          //   • canvas: pt = { x, y }            (canvas px)
          //
          // Intended for consumers that wire their own tap-zone
          // handlers (page navigation on left/right thirds, sidebars,
          // mini-maps) and need to know whether the tap landed on an
          // annotation. Etcher's internal pointer pipeline still
          // runs — this is just exposing the same per-kind hit-test
          // (`_shapeContainsPoint`) consumers would otherwise
          // re-implement against `getShapes()`.
          shapeAt: function(pt) {
            if (!pt) return null;
            var s = self._shapeAt(pt);
            return s ? self._shapeDescriptor(s) : null;
          },
          selectShape: function(uuid) {
            var shape = self.shapes.find(function(s) { return s.uuid === uuid; });
            if (!shape) return;
            if (shape.readonly) {
              console.warn("[Etcher] selectShape ignored — shape is readonly:", uuid);
              return;
            }
            self._pinTooltipFor(shape);
          },
          // Flip a shape's render-time lock without a re-render (parity with
          // setColors). `true` disables edit / move / delete / pen-editor /
          // box-select; hover + tooltip still work. Returns false if the uuid
          // isn't on the layer. The flag is NOT persisted — re-renders that
          // omit `readonly` reset it to false.
          setShapeReadonly: function(uuid, bool) {
            return self._setShapeReadonly(uuid, !!bool);
          },
          unselectShape: function() { self._unpinTooltip(); },
          enterEditMode: function(uuid) {
            var shape = self.shapes.find(function(s) { return s.uuid === uuid; });
            if (shape) self._enterEditMode(shape);
          },
          exitEditMode: function() { self._exitEditMode(); },
          // Open the inline label editor on a shape — the same editor
          // double-clicking it opens. Lets a host wire label editing into
          // its own chrome (e.g. an "Edit" tooltip action) without
          // synthesizing dblclick events. No-ops on unknown uuids and on
          // readonly shapes; `_startTextEdit` itself owns kind rules.
          editLabel: function(uuid) {
            var shape = self.shapes.find(function(s) { return s.uuid === uuid; });
            if (shape && !shape.readonly) self._startTextEdit(shape);
          },
          deleteShape: function(uuid) {
            var shape = self.shapes.find(function(s) { return s.uuid === uuid; });
            if (shape) self._deleteShape(shape);
          },

          // Splice a single shape into the live layer without
          // remounting. Mirrors the persisted-annotation payload
          // shape: `{kind, geometry, image_idx?, image_id?, style?,
          // metadata?, uuid?}`. Returns the shape's uuid (generated
          // when not supplied) or `null` if validation fails.
          //
          // Strip mode REQUIRES `image_idx` — the renderer can't
          // pick the right per-image overlay without it. Canvas
          // multi-image hosts auto-resolve `image_id` from the
          // centroid when omitted (same path hydrated shapes use).
          //
          // Use case: multi-chapter strip readers that fetch the
          // next chapter's annotations on scroll. The previous
          // workaround — full-layer remount — wiped in-flight UI
          // state (active tool, multi-selection, undo stack,
          // pinned tooltip). `addShape` keeps all of that intact.
          //
          // Multiple sibling `addShape` / `addShapes` calls in the
          // same microtask collapse to one
          // `etcher:annotations-changed` emit so the consumer's
          // server-sync handler doesn't see a flurry of full-array
          // replays.
          addShape: function(payload) {
            return self._addShape(payload);
          },

          // Bulk variant of `addShape`. Returns the array of uuids
          // (in input order, with any rejected payloads filtered
          // out). Same microtask-batch emit semantics.
          addShapes: function(payloads) {
            return self._addShapes(payloads);
          },

          // Inspect the currently-shown tooltip. Returns `null` when
          // no tooltip is up, otherwise `{shape, pinned}` — the
          // shape descriptor (`{uuid, kind, geometry, style?,
          // metadata?}`, same shape as `getShape`) and whether the
          // tooltip is in pinned state. Consumers driving custom
          // chrome that need to react to "user just opened the
          // tooltip on shape X" can poll this from
          // `etcher:tooltip-show` / `:tooltip-pin` listeners (or
          // anywhere else).
          //
          // The raw `tooltipEl` reference is intentionally omitted
          // — direct DOM access would couple consumers to internal
          // structure that's free to change between releases. Use
          // `repositionTooltip()` for the most common need (re-
          // anchor after a layout change).
          tooltip: function() {
            if (!self._tooltipShape) return null;
            return {
              shape: {
                uuid: self._tooltipShape.uuid,
                kind: self._tooltipShape.kind,
                geometry: self._tooltipShape.geometry,
                style: self._tooltipShape.style || null,
                metadata: self._tooltipShape.metadata || null
              },
              pinned: !!self.tooltipPinned
            };
          },

          // Re-anchor the currently-shown tooltip to its shape (no-
          // op when no tooltip is up). Useful after a consumer-
          // driven layout change — toggling a side panel, adjusting
          // strip padding, etc. — where the tooltip's last
          // computed position has drifted from its shape.
          repositionTooltip: function() {
            if (self._tooltipShape) self._positionTooltip(self._tooltipShape);
          },

          // Strip mode: re-query the strip's `getImages()` and
          // create overlays for any pages appended to the container
          // since the initial mount. Existing overlays have their
          // layout refreshed in place. No-op on canvas hosts.
          //
          // Internally just calls the same path the window-`resize`
          // / `image-loaded` listeners use, so consumers who already
          // dispatch a synthetic resize don't need this. Use it
          // when the resize side-channel doesn't fit — e.g. the
          // consumer hydrates the next chapter's annotations
          // synchronously after appending its `<img>`s and wants
          // overlays in place before the first `addShape` call.
          refreshPages: function() {
            if (typeof self._onResize === "function") self._onResize();
          },

          // Bring a shape into the viewport. Strip mode scrolls the
          // strip so the shape's image is centered (smooth by default);
          // canvas mode calls `handle.fitBounds` on the shape's image-
          // px bounding box. Useful for reveal-comment-on-page flows
          // where the consumer LV navigates by uuid.
          //
          // Returns a Promise that resolves with
          //   { uuid, image_idx?, image_id?, scrollTop?, cameraBounds? }
          // once the reveal action has been issued (the Promise does
          // NOT wait for scroll to settle — pulse / consumer follow-up
          // can rely on the resolution). Rejects with
          //   { reason: "timeout" | "no_geometry" | "no_image_idx" |
          //             "scroll_failed" | "fitBounds_failed" |
          //             "unsupported_handleKind" }.
          //
          // Polls for late-mounted shapes for `opts.timeout` ms
          // (default 10000) — chapters that hydrate on scroll, async
          // annotation backfills. Also fires an `etcher:shape-revealed`
          // DOM event on the layer host with the same payload, so
          // LiveView hooks / event-bus consumers can react without
          // owning the Promise.
          //
          // Options:
          //   { behavior: "smooth" | "instant" }    // default "smooth"
          //   { align: "center" | "top" | "bottom" } // strip only, default "center"
          //   { pulse: boolean }                    // flash the shape, default false
          //   { pulseDuration: <ms> }               // default 1500
          //   { timeout: <ms> }                     // poll budget, default 10000
          //   { padding: <natural-px> }             // canvas fitBounds padding
          revealShape: function(uuid, opts) {
            return self._revealShape(uuid, opts || {});
          },

          // Patch in-place: merge `fields` into the shape's existing
          // values. Currently honors `metadata` and `style`; other
          // fields are no-ops (geometry edits need geometry-specific
          // re-rendering; uuid/kind are immutable identity).
          //
          // Purpose: consumers that drive annotation state from the
          // server (`<Fresco.canvas>` with `phx-update="ignore"`, so
          // `handle.getExtension("etcher")` returns the initial-mount
          // value forever) need a way to refresh in-DOM metadata when
          // server-side state changes (e.g., a user posts a comment
          // and the tooltip should now show comment_* fields). Without
          // this API the only options were full layer remount or
          // mutating private state.
          patchShape: function(uuid, fields) {
            var shape = self.shapes.find(function(s) { return s.uuid === uuid; });
            if (!shape || !fields) return;
            // Geometry is REPLACED, not merged: it is a whole description of
            // where a shape is, and merging would leave stale keys behind —
            // an arrow re-pointed at a different shape keeps `from`, a
            // straightened line keeps its bend points.
            //
            // Patching it at all is what lets a consumer applying a peer's
            // move update the shape in place. Deleting and re-adding is the
            // alternative, and it destroys the element: media reloads,
            // images re-decode, and the shape visibly flashes.
            if (fields.geometry && typeof fields.geometry === "object") {
              shape.geometry = fields.geometry;
            }
            if (fields.metadata && typeof fields.metadata === "object") {
              shape.metadata = Object.assign({}, shape.metadata || {}, fields.metadata);
            }
            if (fields.style && typeof fields.style === "object") {
              shape.style = Object.assign({}, shape.style || {}, fields.style);
              if (shape.style.color && shape.el) {
                self._applyShapeColor(shape.el, shape.style.color, shape.style);
              }
            }
            // Re-render the shape so DOM that derives from metadata
            // (dimension labels, callout text, title siblings with
            // their leader lines) reflects the patched values. Without
            // this, server-pushed titles arrive in shape.metadata.title
            // but the on-shape text never redraws. _renderShape is
            // idempotent; for tooltip-only patches (comment_* fields
            // that only affect hover state) this is a no-op visually.
            if (shape.el) {
              self._renderShape(shape);
            }
          },

          // Coordinate helpers — expose the Fresco handle's screen ↔ image
          // round-trip so consumers can place shapes programmatically
          // (drop an image under the cursor, paste one at the viewport
          // center). Canvas handle: `screenToImage({x, y})` (client px) →
          // `{x, y}` (canvas px); `imageToScreen` is the inverse. Strip
          // handle returns/accepts the `{imageIdx, x, y}` shape instead.
          // `null` if the handle lacks the method (defensive; both current
          // handles implement both).
          screenToImage: function(pt) {
            return self.handle && typeof self.handle.screenToImage === "function"
              ? self.handle.screenToImage(pt) : null;
          },
          imageToScreen: function(pt) {
            return self.handle && typeof self.handle.imageToScreen === "function"
              ? self.handle.imageToScreen(pt) : null;
          },
          // Image-space coordinate at the center of the visible viewport —
          // the natural drop point for a pasted image or an "Add image"
          // button. Canvas mode only (returns `null` on strip / no handle).
          viewportCenterImage: function() { return self._viewportCenterImage(); },

          // Image insertion --------------------------------------------
          // Place an image shape from a URL / data URL. `opts.at = {x, y}`
          // (image coords, default viewport center); `opts.width`/`height`
          // force a size (else natural size is measured and the image is
          // sized to half of what is visible; `opts.maxSide` caps the longest
          // side in canvas px instead). Returns the uuid when a
          // size is supplied, else null (shape still appears on decode).
          // This is the entry point a `image_source: "custom"` host calls
          // after its own uploader/modal resolves to a URL.
          insertImage: function(href, opts) { return self._insertImageHref(href, opts || {}); },

          // ── Audio ────────────────────────────────────────────────────
          // `insertAudio` places a card for an already-stored file.
          insertAudio: function(href, opts) { return self._insertAudioHref(href, opts || {}); },
          insertVideo: function(href, opts) { return self._insertVideoHref(href, opts || {}); },
          // Local controls. Each emits `etcher:media-command` so the host can
          // share the action with the room.
          playAudio:   function(uuid, position) { return self._applyAudio(uuid, "play", position, true); },
          pauseAudio:  function(uuid, position) { return self._applyAudio(uuid, "pause", position, true); },
          toggleAudio: function(uuid) { return self.toggleAudio(uuid); },
          seekAudio:   function(uuid, frac) { return self.seekAudio(uuid, frac); },
          // The inbound half: apply what the room decided, WITHOUT emitting.
          // `{playing, position}`; correction strategy is the host's call —
          // see `applyMediaState`.
          applyMediaState: function(uuid, state) { return self._applyMediaState(uuid, state || {}); },
          audioState: function(uuid) { return self._audioState(uuid); },
          // Every media shape's transport state, for answering "what is the
          // room playing right now?" — see `mediaStates`.
          mediaStates: function() { return self._mediaStates(); },
          // Local to this listener — never shared with the room.
          setMediaVolume: function(uuid, v) { return self._setMediaVolume(uuid, v); },
          setMediaMuted: function(uuid, m) { return self._setMediaMuted(uuid, m); },

          // Hand image FILES (pasted, dropped, or picked) to the host instead
          // of embedding them. `fn(file, ctx)` returns a Promise of a URL,
          // which becomes the shape's `href`; ctx carries `{frescoId}`.
          //
          // Worth setting whenever the annotation list is persisted over a
          // socket: without it a pasted screenshot lives in the shape as a
          // base64 data URL, and since the whole list is re-emitted on every
          // edit, that image is re-sent in full every time anything changes.
          // A couple of screenshots is enough to push ordinary edits past a
          // socket frame limit, at which point edits fail silently.
          //
          // Rejecting (or resolving to a non-string) falls back to embedding,
          // so a failed upload costs size rather than the user's work. Pass
          // null to clear. `window.Etcher.imageUploader` sets the same thing
          // for every layer; this one wins.
          setImageUploader: function(fn) {
            self._uploadImageFn = typeof fn === "function" ? fn : null;
          },

          // ── Red pointer ───────────────────────────────────────────────
          //
          // Etcher owns what a pointer LOOKS like — a red dot with a trail,
          // held in canvas coordinates so it lands on the same spot for
          // everyone whatever they have zoomed to, and the same size on
          // every screen. The host owns who is pointing and how the
          // position reaches the other viewers; it already knows both.
          //
          // The local user's own pointer is drawn from the tool itself. The
          // host feeds in everyone else's through these.
          // ── Preferences ───────────────────────────────────────────────
          //
          // How this user likes the board set up — background dots, connector
          // anchors, the style panel's size, which tools are on the bar.
          // Saved to localStorage on their own; hand over an adapter and the
          // host stores them instead, which is the only way they can follow
          // a user between devices.
          getPrefs: function() {
            return Object.assign({}, self._loadPrefs());
          },
          setPref: function(name, value) { self._setPref(name, value); return true; },

          // Hand back what was stored for this user on this board. Merged
          // over whatever is already there, so a host that keeps three of
          // these keys does not blank the others, and NOT echoed back — it
          // just told us, and saving it again would be a write on every load.
          //
          // This is the whole contract for a LiveView host: listen for
          // `etcher:prefs-changed`, store it wherever you like, and push it
          // back through here on mount. No JavaScript adapter needed, and
          // nothing here assumes what "wherever you like" means.
          setPrefs: function(prefs) { return self._hydratePrefs(prefs); },

          // For hosts that would rather own storage in JS. `load()` may
          // return an object or a promise of one; `save(prefs)` is called on
          // a short debounce rather than on every frame of a drag.
          setPrefsAdapter: function(adapter) { self._setPrefsAdapter(adapter); },

          isPointerMode: function() { return self.activeTool === "pointer"; },

          // Someone else pointed at `{x, y}` (canvas coords, as they arrived
          // from that person's `etcher:pointer-moved`). `id` is whatever the
          // host calls that person; passing the same id again moves their
          // pointer rather than adding another.
          applyRemotePointer: function(id, state) {
            if (id == null) return false;
            var st = state || {};
            if (typeof st.x !== "number" || typeof st.y !== "number") return false;
            self._notePointer(String(id), st.x, st.y, {
              color: st.color, name: st.name
            });
            return true;
          },

          // They stopped pointing, or left. Pointers also time themselves out,
          // so a lost connection cleans up on its own — this is for the case
          // where the host knows sooner.
          removeRemotePointer: function(id) {
            if (id == null) return false;
            self._dropPointer(String(id));
            return true;
          },

          // Turn a pasted URL into a preview card. `fn(url, ctx)` returns a
          // Promise of `{ href, width, height }` — an image of the page,
          // which becomes an image shape carrying the URL in
          // `metadata.link`. Etcher can't build one itself: reading a page's
          // OpenGraph tags means fetching it, which the browser won't allow
          // cross-origin and which needs answering for anyway (SSRF, size
          // caps, timeouts) somewhere that has a server.
          //
          // Without it — or if it rejects — a pasted URL stays a text shape.
          // `window.Etcher.linkUnfurler` sets the same thing for every
          // layer; this one wins.
          // The little glyph that marks out this tool — the same one riding
          // the local cursor while the tool is held. SVG markup drawn in a
          // 24x24 box, unstroked and uncoloured, so a caller styles it to
          // suit: a collaborative host draws it beside a peer's cursor in
          // that peer's colour, which is how everyone can see what the other
          // people are holding.
          //
          // `null` for tools that have no glyph (and for the plain pointer),
          // which a caller should read as "just the arrow".
          toolBadge: function(key) {
            return (key && CURSOR_BADGES[key]) || null;
          },

          // ── In-flight moves ─────────────────────────────────────────────
          //
          // Shapes are emitted for persistence when a gesture ends, which
          // leaves a peer watching nothing happen for the length of a drag
          // and then the shape somewhere new. Register here and `fn` is
          // called with `[{uuid, geometry}, …]` once a frame while shapes are
          // being dragged, so a collaborative host can show the movement as
          // it happens.
          //
          // These are NOT edits: nothing is stored, no undo entry is made,
          // and the authoritative `etcher:annotations-changed` still fires on
          // release exactly as before. Pass `null` to stop.
          //
          // `onMoveEnd` is called once when the gesture finishes, which is a
          // host's cue to drop whatever it was showing and wait for the real
          // edit — so an abandoned drag snaps back rather than leaving peers
          // looking at a position nobody recorded.
          onShapesMoving: function(fn, onMoveEnd) {
            self._liveMoveHandler = typeof fn === "function" ? fn : null;
            self._liveMoveEndHandler = typeof onMoveEnd === "function" ? onMoveEnd : null;
            if (self._liveMoveHandler) self._wireLiveMoveTracking();
            return true;
          },

          // Show where a peer's shapes are while they are still dragging
          // them. Transient by nature — overwritten by the next report and
          // settled by the edit that arrives on release.
          applyShapesMoving: function(list) {
            self._applyLiveMoves(list);
            return true;
          },

          // ── In-flight drawing ───────────────────────────────────────────
          //
          // The same problem as a drag, one step earlier: a shape is emitted
          // when it is FINISHED, so a peer watches nothing happen for the
          // length of a stroke and then a finished stroke appears. Register
          // here and `fn` is called with `{kind, geometry, style}` once a
          // frame while a shape is being drawn.
          //
          // Not an edit, like the move reports: nothing is stored, no undo
          // entry, no `etcher:annotations-changed`. The real shape still
          // arrives the usual way when the gesture ends.
          //
          // `onDrawEnd` fires once when the pointer goes up — the host's cue
          // to tell peers to drop the provisional shape and wait for the
          // edit, so an abandoned draw leaves nothing behind.
          onDrawing: function(fn, onDrawEnd) {
            self._liveDrawHandler = typeof fn === "function" ? fn : null;
            self._liveDrawEndHandler = typeof onDrawEnd === "function" ? onDrawEnd : null;
            if (self._liveDrawHandler) self._wireLiveMoveTracking();
            return true;
          },

          // Show what a peer is drawing, right now. `key` is whatever the
          // host uses to tell peers apart — one person draws one shape at a
          // time, so the key is all the identity a provisional shape needs,
          // and the next report for that key replaces it.
          //
          // These shapes are GHOSTS: absent from `getShapes()`, from every
          // emit, from undo and from hit-testing. They exist on screen and
          // nowhere else, and `applyDrawingEnd` takes them away.
          applyDrawing: function(key, draft) {
            self._applyGhostDraw(key, draft);
            return true;
          },

          applyDrawingEnd: function(key) {
            self._retireGhostDraw(key);
            return true;
          },

          setLinkUnfurler: function(fn) {
            self._unfurlLinkFn = typeof fn === "function" ? fn : null;
          },

          // Z-order -----------------------------------------------------
          // Move the current selection (multi-selection, else the shape in
          // edit mode). Each returns true when something actually moved —
          // false at the edge, or with nothing selected — so a consumer
          // driving its own buttons can reflect that. Undoable, and they
          // emit `etcher:annotations-changed` with the new order, so a
          // host that persists the list persists the layering for free.
          bringToFront: function() { return self._arrange("front"); },
          sendToBack: function() { return self._arrange("back"); },
          bringForward: function() { return self._arrange("forward"); },
          sendBackward: function() { return self._arrange("backward"); },
          // True when there is a selection to arrange — for enabling
          // external buttons without reaching into internals.
          canArrange: function() { return self._canArrange(); },
          // Force an explicit order by uuid. Shapes absent from the list
          // keep their relative position at the end. Does NOT record undo
          // or re-emit — intended for applying an order that came from
          // somewhere authoritative (a peer, a server load), where echoing
          // it straight back would loop.
          setShapeOrder: function(uuids) { self._applyShapeOrder(uuids); },
          // Open the built-in OS file picker and insert the chosen file —
          // the same action the image tool performs in file_picker mode.
          openImagePicker: function() { self._openImagePicker(); }
        }
      };
    },

    destroyed: function() {
      this._closeActionMenu();
      if (this.actionBar && this.actionBar.parentNode) {
        this.actionBar.parentNode.removeChild(this.actionBar);
      }
      if (this.actionMenu && this.actionMenu.parentNode) {
        this.actionMenu.parentNode.removeChild(this.actionMenu);
      }
      this.actionMenu = null;
      this._closeLayersMenu();
      if (this.layersMenu && this.layersMenu.parentNode) {
        this.layersMenu.parentNode.removeChild(this.layersMenu);
      }
      this.layersMenu = null;
      if (this.stylePanel && this.stylePanel.parentNode) {
        this.stylePanel.parentNode.removeChild(this.stylePanel);
      }
      this.stylePanel = null;
      this._wireGrabberCursor(false);
      this.swatchHost = null;
      this.actionBar = null;

      this._exitEditMode();
      // An editor left open at teardown holds a document-level pointerdown
      // listener; _endTextEdit is where it is unwired.
      this._endTextEdit();
      this._unwireImagePaste();
      this._removeTooltipOutsideClickHandler();
      this._clearCommentHighlights();
      this._unwireGlobalShapeListeners();
      if (this._undoKeyHandler) {
        document.removeEventListener("keydown", this._undoKeyHandler);
        this._undoKeyHandler = null;
      }
      if (this._escapeCaptureHandler) {
        window.removeEventListener("keydown", this._escapeCaptureHandler, true);
        this._escapeCaptureHandler = null;
      }
      if (this._spaceUpHandler) {
        document.removeEventListener("keyup", this._spaceUpHandler);
        this._spaceUpHandler = null;
      }
      if (this._spaceBlurHandler) {
        window.removeEventListener("blur", this._spaceBlurHandler);
        this._spaceBlurHandler = null;
      }
      if (this._peerPrefsHandler) {
        document.removeEventListener("etcher:prefs-changed", this._peerPrefsHandler);
        this._peerPrefsHandler = null;
      }
      if (this._styleInspectorFrame) {
        try { cancelAnimationFrame(this._styleInspectorFrame); } catch (_) {}
        this._styleInspectorFrame = null;
      }
      if (this.removeNavBtn) { try { this.removeNavBtn(); } catch (_) {} }
      if (this.visibilityBtn) { try { this.visibilityBtn(); } catch (_) {} }
      if (this.removePanelToggleBtn) {
        try { this.removePanelToggleBtn(); } catch (_) {}
        this.removePanelToggleBtn = null;
        this.panelToggle = null;
      }
      if (this.toolbar && this.toolbar.parentNode) {
        this.toolbar.parentNode.removeChild(this.toolbar);
      }
      this._closePopup();
      if (this.toolsPopup && this.toolsPopup.parentNode) {
        this.toolsPopup.parentNode.removeChild(this.toolsPopup);
        this.toolsPopup = null;
      }
      if (this.colorsPopup && this.colorsPopup.parentNode) {
        this.colorsPopup.parentNode.removeChild(this.colorsPopup);
        this.colorsPopup = null;
      }
      if (this.markerPopup && this.markerPopup.parentNode) {
        this.markerPopup.parentNode.removeChild(this.markerPopup);
        this.markerPopup = null;
      }
      if (this.paramsPopup && this.paramsPopup.parentNode) {
        this.paramsPopup.parentNode.removeChild(this.paramsPopup);
        this.paramsPopup = null;
      }
      if (this._toolbarResizeObserver) {
        try { this._toolbarResizeObserver.disconnect(); } catch (_) {}
        this._toolbarResizeObserver = null;
      }
      if (this._toolbarFallbackResize) {
        window.removeEventListener("resize", this._toolbarFallbackResize);
        this._toolbarFallbackResize = null;
      }
      if (this.overlayWrapper && this.overlayWrapper.parentNode) {
        this.overlayWrapper.parentNode.removeChild(this.overlayWrapper);
      }
      // Strip-mode teardown: pointer handlers on `handle.container`,
      // per-image SVG siblings, the container-level tooltip, and the
      // resize listeners.
      if (this.handleKind === "strip") {
        var container = this.handle && this.handle.container;
        if (container) {
          if (this._stripPointerDown) container.removeEventListener("pointerdown", this._stripPointerDown);
          if (this._stripPointerMove) container.removeEventListener("pointermove", this._stripPointerMove);
          if (this._stripPointerUp) {
            container.removeEventListener("pointerup", this._stripPointerUp);
            container.removeEventListener("pointercancel", this._stripPointerUp);
          }
          if (this._stripDblClick) container.removeEventListener("dblclick", this._stripDblClick);
          if (this._stripTouchStart) {
            container.removeEventListener("touchstart", this._stripTouchStart,
              { passive: false });
          }
          container.classList.remove("etcher-strip-drawing");
          container.style.cursor = "";
        }
        if (this.pageOverlays) {
          this.pageOverlays.forEach(function(entry) {
            if (entry && entry.svg && entry.svg.parentNode) {
              entry.svg.parentNode.removeChild(entry.svg);
            }
          });
          this.pageOverlays = null;
        }
        if (this.tooltipEl && this.tooltipEl.parentNode) {
          this.tooltipEl.parentNode.removeChild(this.tooltipEl);
          this.tooltipEl = null;
        }
        if (this._onResize) {
          window.removeEventListener("resize", this._onResize);
          window.removeEventListener("orientationchange", this._onResize);
          this._onResize = null;
        }
        if (this._unsubImageLoaded) {
          try { this._unsubImageLoaded(); } catch (_) {}
          this._unsubImageLoaded = null;
        }
      }
      if (this._unsubViewport) {
        this._unsubViewport.forEach(function(fn) { try { fn(); } catch (_) {} });
        this._unsubViewport = null;
      }
      if (this._unsubImageVisibility) {
        try { this._unsubImageVisibility(); } catch (_) {}
        this._unsubImageVisibility = null;
      }
      if (this.frescoId) delete layerRegistry[this.frescoId];
      this._setAnnotationMode(false);
    },

    // -------------------------------------------------------------------------
    // Initialization
    // -------------------------------------------------------------------------

    _whenCanvasReady: function(cb) {
      // Fresco 0.5's <Fresco.canvas> exposes canvas dimensions at mount
      // — no async wait needed (unlike OSD's tile-load gate). The
      // helper stays for API symmetry; future per-image swap support
      // could wire it back through `handle.on("open", ...)`.
      cb();
    },

    _init: function() {
      var self = this;
      var handle = self.handle;

      // Seed the global stroke defaults from `data-line-params` before any
      // drawing (independent of the handle type).
      self._seedLineParams();

      // Re-init on a fresh handle (the media viewer remounts Fresco per
      // file switch) with the grabber still armed: the new container has
      // no inline cursor yet, so Fresco's native `cursor: grab` class
      // would show the OS hand until the next press or re-arm. Re-seat
      // the relaxed hand immediately.
      if (self.activeTool === "grabber" && handle && handle.container) {
        handle.container.style.cursor = grabberCursor(false);
      }

      // Handle-type dispatch (added in 0.4 with Fresco 0.5.3).
      //
      // Fresco's `<Fresco.canvas>` and `<Fresco.scroll_strip>` both publish
      // their handle through the same `window.Fresco.onReady` registry,
      // but expose different surfaces:
      //
      //   - canvas handle has `getCanvasSize()` + image positions in a
      //     single canvas-pixel coordinate space (`imageToScreen({x, y})`).
      //   - strip handle has `scrollTo({imageIdx, y})` + per-image
      //     coordinates (`imageToScreen({imageIdx, x, y})`).
      //
      // The renderers are quite different (strip = per-image SVG
      // siblings of the imgs, scrolled by native browser scroll; canvas
      // = one stage-anchored SVG that scales/translates with the
      // transform engine), so each gets its own init path. Both end
      // up calling `_buildNavButton` / `_buildVisibilityButton` so the
      // pencil + eye buttons attach via `appendNavButton` (which works
      // identically on both handles).
      self.handleKind = ("scrollTo" in handle && typeof handle.scrollTo === "function")
        ? "strip"
        : (typeof handle.getCanvasSize === "function" ? "canvas" : null);

      if (self.handleKind === "strip") {
        self._initStripRenderer(handle);
      } else if (self.handleKind === "canvas") {
        self._initCanvasRenderer(handle);
      } else {
        console.warn(
          "[Etcher] Unknown Fresco handle shape — needs <Fresco.canvas> or " +
          "<Fresco.scroll_strip>."
        );
      }
    },

    _initCanvasRenderer: function(handle) {
      var self = this;

      // Fresco 0.5 canvas-pixel extent — replaces OSD's
      // `world.getItemAt(0).getContentSize()`. `imageSize` stays as the
      // variable name throughout the file; the math is identical, only
      // the source of the dimensions changed.
      var size = handle.getCanvasSize();
      if (!size.width || !size.height) {
        console.warn(
          "[Etcher] Canvas handle reports zero size; check <Fresco.canvas> :canvas dimensions."
        );
        return;
      }
      self.imageSize = { x: size.width, y: size.height };

      self._buildOverlay();
      if (self.showToolbar) { self._buildToolbar(); self._buildActionBar(); self._buildStylePanel(); }
      // Visibility toggle goes above the annotation-mode pencil so
      // it reads "look first, edit second" top-to-bottom. Both are
      // gated by the `:nav_buttons` allowlist so consumers shipping
      // their own chrome can hide them and wire `handle.toggleMode()`
      // / `handle.toggleVisible()` to their own buttons.
      if (self._chromeEnabled("visibility")) self._buildVisibilityButton();
      if (self._chromeEnabled("pencil")) self._buildNavButton();
      // Preferences are read once the chrome they drive exists.
      self._applyPrefs();
      self._wireUndoKeyboard();
      self._wireGlobalShapeListeners();
      self._wireImagePaste();
      self._wireShapeClipboard();
      self._wireFileDrop();

      // Multi-image canvases (paged readers, lookbooks): subscribe
      // to Fresco's `image-visibility-change` and hide shapes whose
      // host image is currently `display: none`. Seed from
      // `handle.getHiddenImageIds()` so an extension mounting after
      // the host already toggled images off still picks up the
      // current state — the event is fire-and-forget, not replayed.
      self._hiddenImageIds = new Set();
      if (typeof handle.getHiddenImageIds === "function") {
        try {
          handle.getHiddenImageIds().forEach(function(id) {
            self._hiddenImageIds.add(id);
          });
        } catch (_) { /* older fresco — no-op, the on-hex package may not have it */ }
      }
      if (typeof handle.on === "function") {
        self._unsubImageVisibility = handle.on(
          "image-visibility-change",
          function(payload) {
            if (!payload || typeof payload.imageId !== "string") return;
            if (payload.visible) self._hiddenImageIds.delete(payload.imageId);
            else self._hiddenImageIds.add(payload.imageId);
            self._applyImageVisibility();
          }
        );
      }

      self._renderInitial();
      // `_applyImageVisibility` runs after `_renderInitial` so the
      // hydrated shapes are present to be toggled.
      self._applyImageVisibility();
    },

    // ─────────────────────────────────────────────────────────────────
    // Strip renderer (0.4+, requires Fresco ~> 0.5.3).
    //
    // <Fresco.scroll_strip> renders N <img> elements as direct children of
    // a scrollable container, sized by aspect-ratio CSS so memory windowing
    // can evict src without collapsing the layout. Etcher attaches one SVG
    // overlay per image as a sibling of that image; the overlay shares the
    // scroll container so native browser scroll moves it in lockstep with
    // its image. Overlays default to `pointer-events: none` so native
    // scroll keeps working — drawing-mode pointer events are captured on
    // `handle.container` instead (added on enter-draw-mode, removed on
    // exit) so the rest of the strip stays scrollable.
    //
    // Each overlay SVG has `viewBox="0 0 naturalWidth naturalHeight"`, so
    // shape attributes can be written directly in image-pixel coordinates
    // without per-frame scaling. The viewBox + percentage sizing combo
    // also means overlays automatically rescale on container width change
    // (orientation flip, window resize) — only `top`/`height` need
    // repositioning, which the resize listener handles.
    //
    // Strip annotations carry an extra `image_idx` field identifying which
    // image they live on; canvas annotations don't. The `etcher:annotations-
    // changed` payload stays a single array — consumers' handle_event
    // doesn't need to branch on mode.
    // ─────────────────────────────────────────────────────────────────

    _initStripRenderer: function(handle) {
      var self = this;

      // Cache page layout once at mount; re-query on resize. getImages
      // forces a sync layout flush so we don't want to call it per scroll
      // tick — but it's stable across memory-windowing evict/restore
      // because aspect-ratio CSS holds each image's slot.
      self.pages = handle.getImages();
      if (!self.pages || self.pages.length === 0) {
        console.warn(
          "[Etcher] Strip handle returned no images; check <Fresco.scroll_strip> :sources."
        );
        return;
      }

      // pageOverlays[image_idx] = { svg, page, titleLayer? }
      // The SVG is the drawable surface; shapes attach as children.
      self.pageOverlays = [];

      self._buildStripOverlays();
      self._buildStripTooltip();
      if (self.showToolbar) { self._buildToolbar(); self._buildActionBar(); self._buildStylePanel(); }
      if (self._chromeEnabled("visibility")) self._buildVisibilityButton();
      if (self._chromeEnabled("pencil")) self._buildNavButton();
      // Preferences are read once the chrome they drive exists.
      self._applyPrefs();
      self._wireUndoKeyboard();
      self._wireStripPointerInput();
      self._wireStripResize();
      // Shared shape-interaction listeners: hover → tooltip,
      // tap → _onShapeTap (→ pin in browse, edit-mode in cursor).
      // The handlers route through `_toImage` + `_shapeAt`, both of
      // which already understand strip's `{imageIdx, x, y}` shape.
      self._wireGlobalShapeListeners();
      self._renderInitial();
    },

    // Build one SVG overlay per image. The overlay is inserted into the
    // scroll container as a sibling immediately after its image, so
    // native browser scroll moves them together — no per-frame
    // repositioning needed during scroll. viewBox is image-natural so
    // shape coords are 1:1 with the geometry stored in extensions.
    _buildStripOverlays: function() {
      var self = this;
      var container = self.handle.container;

      // Make sure the container can position absolute children.
      // Scroll containers usually have `overflow: auto` but no explicit
      // position; we need `position: relative` so our absolute overlays
      // anchor to the container (not the page).
      if (getComputedStyle(container).position === "static") {
        container.style.position = "relative";
      }

      self.pages.forEach(function(page) { self._buildStripOverlay(page); });
    },

    // Build a strip overlay for a single page and stash it in
    // `pageOverlays[page.idx]`. Idempotent: if an overlay already
    // exists for `page.idx`, refresh its layout in place (preserving
    // any shape children) and return the existing entry.
    //
    // Shared by mount-time iteration (`_buildStripOverlays`) and the
    // post-mount resync (`_onResize`), which encounters pages
    // appended to the strip after the initial build — multi-chapter
    // infinite-scroll readers fetching the next chapter's `<img>`s
    // on demand. Without this path, appended pages have no overlay
    // and `addShape` / draw-tool taps for those pages silently fail.
    _buildStripOverlay: function(page) {
      var self = this;
      var existing = self.pageOverlays && self.pageOverlays[page.idx];
      if (existing && existing.svg) {
        existing.page = page;
        self._applyStripOverlayLayout(existing.svg, page);
        return existing;
      }
      var svg = svgEl("svg", {
        "data-etcher-strip-overlay": "",
        "data-image-idx": String(page.idx)
      });
      self._applyStripOverlayLayout(svg, page);
      // Critical: never consume pointer events. Native scroll on the
      // container must keep working when the user touches inside an
      // overlay region; draw-mode capture lives on the container.
      svg.style.pointerEvents = "none";
      svg.style.overflow = "visible";
      // No `preserveAspectRatio` attribute — the SVG default
      // (`xMidYMid meet`) letterboxes on momentary mismatches
      // between the overlay element box and the viewBox (during
      // image-load, aspect-ratio correction, container-padding
      // changes). The previous `"none"` value would stretch shapes
      // during those windows, which the user sees as a flash of
      // distorted geometry.

      // Insert as sibling immediately after the image so DOM order
      // matches z-order and the overlay scrolls with its image
      // naturally.
      if (page.element && page.element.parentNode) {
        page.element.parentNode.insertBefore(svg, page.element.nextSibling);
      }
      if (!self.pageOverlays) self.pageOverlays = [];
      self.pageOverlays[page.idx] = { svg: svg, page: page };
      return self.pageOverlays[page.idx];
    },

    // Apply geometry + viewBox to a single overlay. Shared by
    // `_buildStripOverlays` (initial mount) and `_onResize` (window
    // resize, image-loaded, consumer-dispatched layout-changed). All
    // four positioning values come from `getImages()` so any
    // consumer-side CSS that shifts each `<img>` (centered narrow
    // pages, horizontal padding, aspect-ratio correction) flows
    // through transparently. The viewBox refresh handles the case
    // where the consumer fixes natural dimensions after the image
    // bitmap loads — without refreshing, shapes would render against
    // a stale natural ratio and visibly stretch.
    _applyStripOverlayLayout: function(svg, page) {
      svg.setAttribute(
        "viewBox",
        "0 0 " + (page.naturalWidth || 1) + " " + (page.naturalHeight || 1)
      );
      svg.style.position = "absolute";
      svg.style.top = page.top + "px";
      svg.style.left = ((page.left != null) ? page.left : 0) + "px";
      svg.style.width = ((page.width != null) ? page.width : "100%") +
        (typeof page.width === "number" ? "px" : "");
      svg.style.height = page.height + "px";
    },

    // Tooltip element lives in the scroll container too (not in a per-
    // image overlay) so it can be positioned freely with respect to
    // any shape. Visibility toggles via opacity; pointer-events: auto
    // when shown so the delete button is clickable.
    _buildStripTooltip: function() {
      var self = this;
      var container = self.handle.container;
      var tip = document.createElement("div");
      tip.className = "etcher-tooltip";
      tip.style.position = "absolute";
      tip.style.zIndex = "12";
      // Hovering the tooltip pauses the dwell countdown so the user can
      // read it / reach the delete button; leaving restarts the full
      // window. Same behavior as canvas mode.
      // The cursor arriving on the tooltip is the user reaching for it:
      // both clocks stop. The hide is the one that matters now that
      // leaving a shape starts one — without cancelling it here, the
      // tooltip would vanish mid-reach for its own delete button.
      tip.addEventListener("mouseenter", function() {
        self._cancelHideTooltip();
        self._cancelTooltipAutoClose();
      });
      tip.addEventListener("mouseleave", function() {
        if (self.tooltipPinned) return;
        // Leaving the tooltip closes it on the same short bridge as
        // leaving a shape; the dwell stays armed as the backstop.
        self._scheduleHideTooltip();
        self._startTooltipAutoClose();
      });
      tip.addEventListener("click", function(e) {
        e.stopPropagation();
        var btn = e.target.closest("[data-etcher-action]");
        if (!btn) return;
        if (btn.dataset.etcherAction === "delete") {
          self._deleteShape(self._tooltipShape);
        } else {
          self._dispatchTooltipAction(btn.dataset.etcherAction);
        }
      });
      container.appendChild(tip);
      self.tooltipEl = tip;
    },

    // Re-query page positions and resize each overlay. Called on window
    // resize / orientation change. The viewBox stays the same (image
    // natural dimensions don't change); only `top`/`height` shift to
    // match the new display size.
    _wireStripResize: function() {
      var self = this;
      // Universal re-sync path: refreshes every overlay's viewBox +
      // position + size from the live `getImages()` snapshot. Consumers
      // who mutate page layout outside of `image-loaded` (toggling a
      // padding slider, swapping an aspect-ratio correction class)
      // can trigger this manually by dispatching a `resize` event on
      // the window — same hook the browser uses.
      self._onResize = function() {
        if (!self.pageOverlays) return;
        var pages = self.handle.getImages();
        pages.forEach(function(page) {
          var entry = self.pageOverlays[page.idx];
          if (!entry || !entry.svg) {
            // Page appended to the strip after initial mount —
            // multi-chapter infinite-scroll readers fetching the next
            // chapter's `<img>`s. Build the overlay now so subsequent
            // `addShape` calls and draw-tool taps for this page can
            // land on it.
            self._buildStripOverlay(page);
            return;
          }
          entry.page = page;
          self._applyStripOverlayLayout(entry.svg, page);
        });
        self.pages = pages;
        // Tooltip might be showing — reposition to its anchor shape.
        if (self._tooltipShape && self.tooltipEl &&
            self.tooltipEl.style.display !== "none") {
          self._positionTooltip(self._tooltipShape);
        }
      };
      window.addEventListener("resize", self._onResize);
      window.addEventListener("orientationchange", self._onResize);

      // Fresco's strip emits `image-loaded` when each <img> finishes
      // loading (and after memory-windowing restores an evicted src).
      // In normal operation the aspect-ratio CSS already holds the slot
      // so offsetTop / offsetHeight don't shift on load — but if a
      // consumer ever ships an image whose actual intrinsic ratio
      // diverges from the width/height attrs, the slot resizes when
      // the bitmap arrives. Re-syncing on image-loaded covers that
      // edge case cheaply; it's a single getImages() call per load.
      if (typeof self.handle.on === "function") {
        self._unsubImageLoaded = self.handle.on("image-loaded", function() {
          if (self._onResize) self._onResize();
        });
      }
    },

    // Capture drawing gestures on the scroll container. Overlays are
    // pointer-events: none so native scroll keeps working in every other
    // state. When `activeTool` is set we hit-test, snapshot the starting
    // image (so cross-image drag clamps to that image's screen rect),
    // activate the matching per-image overlay so the existing
    // `self.svg.appendChild(...)` paths land in the right SVG, and forward
    // to the shared `_onPointerDown / Move / Up` handlers.
    _wireStripPointerInput: function() {
      var self = this;
      var container = self.handle.container;

      self._stripPointerDown = function(e) {
        if (!self.annotationMode || !self.activeTool) return;
        if (e.button !== 0) return;
        var pt;
        try { pt = self.handle.screenToImage({ x: e.clientX, y: e.clientY }); }
        catch (_) { return; }
        if (!pt || typeof pt.imageIdx !== "number") return;

        // Multi-click flows (polygon, callout) lock to the page where
        // the first click landed. Subsequent clicks on other images
        // are ignored — all vertices must share one image's natural-
        // pixel space. ESC clears the draft if the user wants to
        // restart on a different page.
        if (self.draftPolygon || self.draftCallout) {
          if (!self._stripActiveDraw) return;
          if (pt.imageIdx !== self._stripActiveDraw.imageIdx) return;
        } else {
          var entry = self.pageOverlays && self.pageOverlays[pt.imageIdx];
          if (!entry) return;

          // Lock subsequent moves to this image's screen rect so a drag
          // that wanders into the next image still records into the
          // page it started on.
          var elRect = entry.page.element.getBoundingClientRect();
          self._stripActiveDraw = {
            imageIdx: pt.imageIdx,
            rect: {
              left: elRect.left,
              right: elRect.right,
              top: elRect.top,
              bottom: elRect.bottom
            }
          };
          self._activateOverlayForImage(pt.imageIdx);
        }

        // Block native scroll while drawing; the user is intentionally
        // gesturing inside the canvas and a competing scroll feels wrong.
        try { container.setPointerCapture(e.pointerId); } catch (_) {}
        e.preventDefault();
        // Stop the pointerdown from bubbling to the doc-level shape
        // listeners. Without this, `_docPointerDown` would set a
        // `_pendingTap` on the shape under the cursor and a stationary
        // click (polygon vertex, callout anchor) would fire
        // `_onShapeTap` on release — pinning a tooltip mid-draw. The
        // canvas wrapper does the same `stopPropagation()` for the
        // identical reason; we mirror it here on the container.
        e.stopPropagation();
        self._onPointerDown(e);
      };

      self._stripPointerMove = function(e) {
        // Tool-active hover previews (eraser, polygon next-vertex)
        // need to flow through `_onPointerMove` too, but only over the
        // active image. Without an active draw we don't clamp.
        if (!self.annotationMode || !self.activeTool) return;
        var ev = e;
        var lock = self._stripActiveDraw;
        if (lock && self.draftState) {
          // Freeform strokes (freehand / marker) may cross image boundaries —
          // `_appendFreehand` translates the cross-image point into the
          // starting image's space, so they're NOT clamped here. Bounded
          // shapes (rect / circle / text / dimension / line) still clamp the
          // drag to the starting image's screen rect.
          var k = self.draftState.kind;
          if (k !== "freehand" && k !== "marker") {
            var r = lock.rect;
            var cx = Math.max(r.left, Math.min(r.right - 1, e.clientX));
            var cy = Math.max(r.top, Math.min(r.bottom - 1, e.clientY));
            if (cx !== e.clientX || cy !== e.clientY) {
              ev = {
                clientX: cx,
                clientY: cy,
                pointerId: e.pointerId,
                pointerType: e.pointerType,
                button: e.button,
                buttons: e.buttons,
                target: e.target,
                preventDefault: function() { try { e.preventDefault(); } catch (_) {} },
                stopPropagation: function() { try { e.stopPropagation(); } catch (_) {} }
              };
            }
          }
          e.preventDefault();
        }
        self._onPointerMove(ev);
      };

      self._stripPointerUp = function(e) {
        if (!self.annotationMode) return;
        self._onPointerUp(e);
        // Keep the per-image lock alive during multi-click polygon /
        // callout flows. Clear once the draft is gone (commit / cancel
        // both clear draftPolygon / draftCallout / draftState).
        if (self._stripActiveDraw && !self.draftPolygon &&
            !self.draftCallout && !self.draftState) {
          try { container.releasePointerCapture(e.pointerId); } catch (_) {}
          self._stripActiveDraw = null;
        }
      };

      self._stripDblClick = function(e) {
        if (!self.annotationMode) return;
        self._onDoubleClick(e);
      };

      // iOS Safari: cursor-mode finger drags on a shape get claimed by
      // the strip's native scroller before `pointerdown`'s
      // `preventDefault` runs. By that point `setPointerCapture` is a
      // no-op for scroll cancellation. The OS bakes the
      // scroll-vs-app decision into `touchstart`, so a same-frame
      // `preventDefault` there is the only API that defers
      // classification long enough for app hit-testing.
      //
      // Only act when the finger lands on an existing shape — every
      // other tap should keep scrolling so the reader can still
      // navigate the chapter without exiting annotation mode. Skipped
      // when a drawing tool is active (0.4.4's `.etcher-strip-drawing`
      // already sets `touch-action: none` for that case).
      //
      // `{ passive: false }` is required: modern browsers silently
      // ignore `preventDefault` on passive listeners.
      self._stripTouchStart = function(e) {
        if (!self.annotationMode || self.activeTool) return;
        if (!e.touches || e.touches.length !== 1) return;
        var t = e.touches[0];
        var pt;
        try {
          pt = self.handle.screenToImage({ x: t.clientX, y: t.clientY });
        } catch (_) { return; }
        if (!pt || typeof pt.imageIdx !== "number") return;
        if (self._shapeAt(pt)) e.preventDefault();
      };

      container.addEventListener("pointerdown", self._stripPointerDown);
      container.addEventListener("pointermove", self._stripPointerMove);
      container.addEventListener("pointerup", self._stripPointerUp);
      container.addEventListener("pointercancel", self._stripPointerUp);
      container.addEventListener("dblclick", self._stripDblClick);
      container.addEventListener("touchstart", self._stripTouchStart,
        { passive: false });
    },

    // Switch the "current overlay" so the existing draw / render /
    // handle-creation code paths (which all `appendChild` to `self.svg`)
    // land in the right per-image SVG. No-op for canvas mode.
    _activateOverlayForImage: function(imageIdx) {
      if (this.handleKind !== "strip") return;
      var entry = this.pageOverlays && this.pageOverlays[imageIdx];
      if (entry && entry.svg) this.svg = entry.svg;
    },

    // Activate the overlay that owns `shape.el`. Used by interaction
    // entry points (edit-mode enter, shape-move start, midpoint refresh)
    // so handles / title labels render in the same SVG as the shape
    // they decorate.
    _activateOverlayForShape: function(shape) {
      if (this.handleKind !== "strip") return;
      if (shape && shape.el && shape.el.ownerSVGElement) {
        this.svg = shape.el.ownerSVGElement;
      }
    },

    // ⌘Z / Ctrl+Z to undo, +Shift to redo. Only handled while
    // annotation mode is on — keeps the layer from hijacking shortcuts
    // when the user is just browsing the image. Skips when focus is
    // inside a text input (the inline editor, a form on the page).
    _wireUndoKeyboard: function() {
      var self = this;
      self._undoKeyHandler = function(e) {
        if (!self.annotationMode) return;
        var t = e.target;
        if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" ||
                  (t.isContentEditable === true))) return;

        // An OPEN inline text editor owns the keyboard, focused or not.
        // The flow this serves: place a text box, style it from the panel
        // (focus is now on a panel control or nowhere), start typing — the
        // first letter used to fall through HERE and arm a tool by
        // shortcut ("d" switched to dimension mid-sentence). A printable
        // key or Backspace refocuses the editor mid-keydown, so the
        // default action types into the textarea it was meant for; Enter
        // and Escape do what they do inside it; and nothing falls through
        // to shortcuts, delete, or space-pan while the editor is up.
        if (self._textEditor && self._textEditor.input) {
          if (e.key === "Escape") {
            e.preventDefault();
            self._cancelTextEdit();
          } else if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            self._commitTextEdit();
          } else if (e.key === "Backspace" ||
              (e.key && e.key.length === 1 &&
               !e.ctrlKey && !e.metaKey && !e.altKey)) {
            try { self._textEditor.input.focus(); } catch (_) {}
            // No preventDefault: the default action now lands in the
            // just-focused textarea.
          }
          return;
        }

        // Backspace / Delete deletes the currently-selected shape(s).
        // Multi-selection wins when present (one batched delete +
        // single bulk-undo entry); otherwise falls back to the
        // single edit-mode shape. Goes through the same path as the
        // eraser so undo + server sync work identically. No meta key
        // required — the !INPUT/!TEXTAREA gate above keeps it from
        // firing while the user is typing in a form.
        if (e.key === "Backspace" || e.key === "Delete") {
          // A focused LABEL first: clicking a label clears the shape
          // selection on the way into title-edit mode, so this state and
          // the ones below are mutually exclusive — and without this
          // branch, Delete on a label fell through every check and did
          // nothing at all. On a text-kind shape (text, callout,
          // dimension) the text IS the shape, so deleting the label
          // deletes it, same as selecting it would; everywhere else the
          // label clears — text and its box, alignment, offset, colour —
          // and the shape stays, one undo entry for the lot.
          if (self.editingTitleShape) {
            e.preventDefault();
            var tShape = self.editingTitleShape;
            self._exitTitleEditMode();
            if (self._isTextKind(tShape.kind)) {
              self._deleteShape(tShape);
            } else {
              var tBefore = self._snapshotShape(tShape);
              tShape.metadata = Object.assign({}, tShape.metadata || {}, {
                title: "",
                title_box: null,
                title_align: null,
                title_offset: null,
                title_color: null
              });
              self._renderShape(tShape);
              self._pushUndo(tShape.uuid, tBefore, self._snapshotShape(tShape));
              self._emitChanged();
            }
            return;
          }
          // Vertex selection wins over shape selection — clicking a
          // polygon vertex first scopes the next delete to just those
          // points. Falls through if removing the selected count would
          // drop the polygon below 3 vertices so the user can still
          // delete the shape via the regular path.
          if (
            self.editingShape &&
            self.selectedVertexIndices &&
            self.selectedVertexIndices.size > 0
          ) {
            if (self._deleteSelectedVertex()) {
              e.preventDefault();
              return;
            }
          }
          if (self.selectedShapes && self.selectedShapes.length > 0) {
            e.preventDefault();
            self._deleteSelectedShapes();
            return;
          }
          if (self.editingShape) {
            e.preventDefault();
            self._deleteShape(self.editingShape);
            return;
          }
        }

        // Arrange the selection: ] / [ step one layer, ⌘/Ctrl + ] / [ jump
        // to the extremes. Matches Figma / Illustrator / Sketch, so it's
        // muscle memory for anyone who arranges things for a living.
        // `e.key` is the bracket regardless of modifier on every layout we
        // care about; falls through untouched when nothing is selected so
        // the host page keeps the keystroke.
        if (e.key === "]" || e.key === "[") {
          if (!self._canArrange()) return;
          var toExtreme = e.metaKey || e.ctrlKey;
          var where = e.key === "]"
            ? (toExtreme ? "front" : "forward")
            : (toExtreme ? "back" : "backward");
          if (self._arrange(where)) e.preventDefault();
          return;
        }

        // Space held = pan, then back to whatever you were using. The
        // convention in every comparable tool, and worth having for the
        // same reason they all do: reaching for the hand tool means losing
        // your place in what you were drawing with.
        //
        // Bare space only — a modifier makes it a chord belonging to
        // something else. Typing a space into a label is already safe: the
        // INPUT / TEXTAREA / contentEditable gate at the top of this
        // handler returns long before here.
        if ((e.key === " " || e.code === "Space") &&
            !e.metaKey && !e.ctrlKey && !e.altKey) {
          // Held, not tapped: the key repeats while down, and every repeat
          // after the first must do nothing.
          if (!e.repeat && !self._spacePanning) self._beginSpacePan();
          // Swallowed whether or not the pan engaged — space scrolls the
          // page, which is never what someone holding it over a canvas
          // meant.
          if (self._spacePanning) e.preventDefault();
          return;
        }

        // Single-key tool shortcuts. Bare keys only — a modifier means
        // the keystroke is a chord that belongs to something else (⌘R is
        // reload, alt-drag is duplicate). Consumed only when the key maps
        // to a tool this board actually offers.
        if (!e.metaKey && !e.ctrlKey && !e.altKey && e.key &&
            TOOL_SHORTCUTS.hasOwnProperty(e.key.toLowerCase())) {
          if (self._selectToolByShortcut(TOOL_SHORTCUTS[e.key.toLowerCase()])) {
            e.preventDefault();
          }
          return;
        }

        // Escape steps down one rung — draft, then armed tool, then
        // selection. Consumed only when a rung actually fired, so the
        // host page keeps an idle Escape. (The text editor and the
        // arrow-drag machinery handle their own Escapes before this: the
        // editor's target is an INPUT, gated above; the drag listens in
        // the capture phase and stops propagation.)
        if (e.key === "Escape") {
          if (self._escapeLadder()) e.preventDefault();
          return;
        }

        // Arrow keys nudge the selection: 1 screen px, 10 with shift.
        if (e.key === "ArrowLeft" || e.key === "ArrowRight" ||
            e.key === "ArrowUp" || e.key === "ArrowDown") {
          var step = e.shiftKey ? 10 : 1;
          var ndx = e.key === "ArrowLeft" ? -step
                  : e.key === "ArrowRight" ? step : 0;
          var ndy = e.key === "ArrowUp" ? -step
                  : e.key === "ArrowDown" ? step : 0;
          if (self._nudgeSelection(ndx, ndy)) e.preventDefault();
          return;
        }

        var meta = e.metaKey || e.ctrlKey;
        if (!meta) return;
        if (e.key === "z" || e.key === "Z") {
          e.preventDefault();
          if (e.shiftKey) self._redo();
          else self._undo();
        } else if (e.key === "y" || e.key === "Y") {
          // Windows-style redo (Ctrl+Y) for users who don't reach
          // for Shift.
          e.preventDefault();
          self._redo();
        } else if (e.key === "d" || e.key === "D") {
          // The duplicate button has advertised ⌘D in its tooltip since it
          // shipped; this makes it true. Consumed only when something was
          // actually duplicated — otherwise the browser keeps its
          // bookmark shortcut, which is rude to steal for a no-op.
          if (self._duplicateSelection()) e.preventDefault();
        } else if (e.key === "a" || e.key === "A") {
          if (self._selectAll()) e.preventDefault();
        }
      };
      document.addEventListener("keydown", self._undoKeyHandler);

      // Escape, claimed ahead of the page. A host lays its own Escape on the
      // viewer — phoenix_kit's closes the viewer or ends the annotating —
      // and that listener ran before this layer's, so cancelling a shape
      // half-placed with two clicks also threw the user out of drawing.
      // Here, in the capture phase on the WINDOW — ahead of every listener
      // on the document, capture-phase ones included (phoenix_kit's burn
      // hook reads any Escape as "closing the viewer" and burned the
      // drawing on the spot): when Escape has something of ours to cancel
      // (a menu, the inline text editor, a draft, the armed tool, the
      // selection) it does that and the key stops here. An idle Escape is
      // left alone for the page.
      self._escapeCaptureHandler = function(e) {
        if (e.key !== "Escape" || !self.annotationMode) return;
        // A style pop-up open: Escape closes it, and only it.
        if (self._openChip) {
          self._closeChipPop();
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        var t = e.target;
        var editing = !!(self._textEditor && self._textEditor.input);
        if (!editing && t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" ||
                  t.isContentEditable === true)) return;
        var did = false;
        if (editing) {
          self._cancelTextEdit();
          did = true;
        } else if (self._openPopupKind ||
                   (self.layersMenu && self.layersMenu.classList.contains("is-open")) ||
                   (self.actionMenu && self.actionMenu.classList.contains("is-open"))) {
          // One of this layer's menus is open (the tool bar's ⋯, colours,
          // layers): Escape closes the menu and nothing else.
          self._closePopup();
          self._closeLayersMenu();
          self._closeActionMenu();
          did = true;
        } else {
          did = self._escapeLadder();
        }
        if (did) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
      window.addEventListener("keydown", self._escapeCaptureHandler, true);

      self._spaceUpHandler = function(e) {
        if (e.key === " " || e.code === "Space") self._endSpacePan();
      };
      document.addEventListener("keyup", self._spaceUpHandler);
      // A keyup that never arrives: alt-tab away with space held and the
      // browser hands the release to whatever has focus next, leaving the
      // grabber armed with no way to know why.
      self._spaceBlurHandler = function() { self._endSpacePan(); };
      window.addEventListener("blur", self._spaceBlurHandler);
    },

    // -------------------------------------------------------------------------
    // SVG overlay — absolutely positioned over the viewer container, rendered
    // in *screen pixels*. Shape geometry is stored in image px and converted
    // to screen px on every viewport change via `handle.imageToScreen`.
    //
    // (We don't use OSD's `viewer.addOverlay` because OSD's MouseTracker
    // captures pointer events on the canvas before they reach DOM overlays,
    // which silently breaks drawing input.)
    // -------------------------------------------------------------------------

    _buildOverlay: function() {
      var self = this;
      var handle = self.handle;
      var container = handle.container;

      if (getComputedStyle(container).position === "static") {
        container.style.position = "relative";
      }

      var wrapper = document.createElement("div");
      wrapper.className = "etcher-overlay";
      // `inset: 0` from CSS already covers the container; absolute + top/left
      // are belt-and-braces for older browsers.
      wrapper.style.position = "absolute";
      wrapper.style.top = "0";
      wrapper.style.left = "0";
      wrapper.style.right = "0";
      wrapper.style.bottom = "0";

      // Video frames can't be drawn into SVG — <video> is an HTML element,
      // and the only way to nest one is a <foreignObject>, which is
      // transform-buggy across engines. So they live in a plain div layer,
      // positioned by the same image→container transform the shapes use.
      //
      // Inserted BEFORE the svg, and so painted under it: annotations are
      // for marking things up, and a mark you can't see over the thing it
      // marks is useless. The consequence is that layering between the two
      // is all-or-nothing — no arrow can sit behind one video and in front
      // of another. Every canvas app that renders video as DOM has the same
      // limitation.
      var media = document.createElement("div");
      media.className = "etcher-media";
      wrapper.appendChild(media);

      var svg = svgEl("svg", { width: "100%", height: "100%" });
      svg.style.position = "absolute";
      svg.style.inset = "0";
      svg.style.overflow = "visible";
      wrapper.appendChild(svg);

      self.overlayWrapper = wrapper;
      self.mediaLayer = media;
      self.svg = svg;

      container.appendChild(wrapper);

      // Tooltip — sibling of the SVG, positioned in container-px so it
      // doesn't move with the image (it follows the cursor's shape, but
      // is anchored to the viewport, not the annotation). Interactive
      // (`pointer-events: auto`) so the user can move from the shape to
      // the tooltip and click the delete button — a short hide-delay
      // bridges the gap so the tooltip doesn't snap closed mid-traverse.
      var tip = document.createElement("div");
      tip.className = "etcher-tooltip";
      wrapper.appendChild(tip);
      self.tooltipEl = tip;

      // The cursor arriving on the tooltip is the user reaching for it:
      // both clocks stop. The hide is the one that matters now that
      // leaving a shape starts one — without cancelling it here, the
      // tooltip would vanish mid-reach for its own delete button.
      tip.addEventListener("mouseenter", function() {
        self._cancelHideTooltip();
        self._cancelTooltipAutoClose();
      });
      tip.addEventListener("mouseleave", function() {
        if (self.tooltipPinned) return;
        // Leaving the tooltip closes it on the same short bridge as
        // leaving a shape; the dwell stays armed as the backstop.
        self._scheduleHideTooltip();
        self._startTooltipAutoClose();
      });
      tip.addEventListener("click", function(e) {
        // Keep clicks from bubbling to OSD's mouse tracker so the
        // delete button never doubles as a click-to-zoom.
        e.stopPropagation();
        var btn = e.target.closest("[data-etcher-action]");
        if (!btn) return;
        if (btn.dataset.etcherAction === "delete") {
          self._deleteShape(self._tooltipShape);
        } else {
          self._dispatchTooltipAction(btn.dataset.etcherAction);
        }
      });

      // Drawing input — only listens when we're in annotation mode with a
      // tool other than cursor. `pointer-events: auto` is toggled on the
      // wrapper to gate this.
      //
      // `data-fresco-no-capture` tells Fresco's pointerdown handler to bail
      // when an event originates inside the overlay, so drawing never
      // triggers Fresco's pan/zoom even though the wrapper sits inside the
      // Fresco host's event tree. A mouse press is also stopped dead in
      // our handler; a TOUCH is not, and travels on so Fresco can count
      // fingers. One claimed finger draws, two are a pinch and Fresco's —
      // see the press handler below, and the matching exemption in
      // Fresco's `onPointerDown`.
      wrapper.setAttribute("data-fresco-no-capture", "");

      // …but that attribute is read by Fresco's WHEEL handler too, and the
      // wheel was never ours to claim. Arming a drawing tool switches the
      // overlay to `pointer-events: auto`, which makes it the target of
      // wheel events as well as presses — so Fresco saw every scroll as
      // coming from a no-capture overlay and bailed, and zoom silently
      // stopped working for as long as a tool was held. Panning kept
      // working (it is a drag, which the overlay genuinely does claim, and
      // the grabber hands it back deliberately), which is what made the
      // gap look like a quirk of drawing rather than a dead scroll wheel.
      //
      // Fresco's check is on the event TARGET, so there is nothing to opt
      // out of from here short of dropping the attribute and losing pan
      // suppression with it. Instead the scroll is handed BACK to Fresco as
      // if it had landed on its host element, which is what it would have
      // done if the overlay weren't in the way.
      //
      // Re-dispatched rather than reimplemented. Fresco's wheel handler
      // anchors the zoom on the cursor, honours its own gesture allowlist,
      // and cancels any animation in flight — reproducing that here would
      // mean copying its rate and its maths, and a zoom that drifts out of
      // step with the untooled one is worse than no zoom at all. (An
      // earlier version did exactly that, against a `zoomAt` the public
      // handle does not expose. It silently did nothing.)
      //
      // No loop: the synthetic event is dispatched on the host with
      // `bubbles: false`, and this listener is on a DESCENDANT of it, so
      // only the host's own handler sees it.
      wrapper.addEventListener("wheel", function(e) {
        var host = self.handle && self.handle.container;
        if (!host || typeof host.dispatchEvent !== "function") return;

        if (typeof WheelEvent === "function") {
          e.preventDefault();
          host.dispatchEvent(new WheelEvent("wheel", {
            deltaX: e.deltaX, deltaY: e.deltaY, deltaZ: e.deltaZ,
            deltaMode: e.deltaMode,
            clientX: e.clientX, clientY: e.clientY,
            ctrlKey: e.ctrlKey, shiftKey: e.shiftKey,
            altKey: e.altKey, metaKey: e.metaKey,
            bubbles: false, cancelable: true
          }));
          return;
        }

        // No constructable WheelEvent: fall back to the handle's own zoom.
        // Centred on the viewport rather than the cursor, which is worse —
        // but a scroll that zooms the wrong centre still beats one that
        // does nothing.
        var h = self.handle;
        if (typeof h.zoomIn !== "function" || typeof h.zoomOut !== "function") return;
        e.preventDefault();
        if (e.deltaY < 0) h.zoomIn(); else h.zoomOut();
      }, { passive: false });

      wrapper.addEventListener("pointerdown", function(e) {
        // Non-primary buttons are not ours. The `stopPropagation` below is
        // there so a left-press that's drawing or grabbing a handle doesn't
        // ALSO pan the canvas underneath — the overlay has no use for the
        // middle button, and swallowing it would break Fresco's middle-drag
        // pan everywhere except blank canvas.
        if (e.button != null && e.button !== 0) return;

        // A finger is not just another pointer. Two of them are a pinch, and
        // that gesture belongs to the canvas whatever tool is armed —
        // otherwise moving around a board on a phone means putting the pen
        // down, switching to the grabber, panning, and switching back.
        //
        // Swallowing the press here left Fresco holding no pointers at all,
        // so a second finger had nothing to pinch WITH and simply fed the
        // same stroke: the line jumped between the two fingers, back and
        // forth, for as long as they both moved. Touches travel on and
        // Fresco counts them; `_applyPanLock` is what stops a single finger
        // panning while a tool is armed, and a pinch is exempt from that
        // lock by Fresco's own design.
        //
        // A mouse still stops here: there is no second pointer coming, and a
        // drag that draws must not also pan.
        if (e.pointerType !== "touch") e.stopPropagation();

        self._trackTouch(e, true);
        // Two fingers down: whatever was being drawn is abandoned, and the
        // gesture is the canvas's. A half-stroke left on screen while the
        // board moves under it is worse than no stroke.
        if (self._multiTouch) {
          self._cancelDraft();
          self._cancelErase();
          return;
        }
        self._onPointerDown(e);
      });
      // iOS decides what a long press MEANS at `touchstart`, before any
      // pointer event runs — the same ordering that already forces
      // `touch-action: none` on the handles above. Under that classifier a
      // stroke drawn slowly is a long press on the page, and it answers with
      // the selection bar: copy, look up, share, over the top of the drawing.
      // `user-select: none` does not reach it, because by then the gesture
      // has already been claimed.
      //
      // So the default is refused at the only moment it can be. Only while a
      // drawing tool is armed — that is the only time this wrapper catches
      // touches at all — and never over a label editor, where a press is
      // meant to place a cursor and select words.
      //
      // Pointer events are unaffected: they are dispatched separately, so
      // drawing, the two-finger pan and Fresco's pinch all still see every
      // finger. Native scroll and double-tap zoom are already off here
      // (`touch-action: none` on the viewer).
      //
      // The text and callout tools open a real input and want the keyboard
      // with it. Refusing a touch's default does not spend the user gesture
      // the keyboard needs — `focus()` still runs inside it, on the pointerup
      // that follows — so those tools are not exempted here. A press that
      // lands ON an existing editor is, above: that one is the browser's.
      wrapper.addEventListener("touchstart", function(e) {
        if (!e.cancelable) return;
        var t = e.target;
        if (t && t.closest && t.closest("input, textarea, [contenteditable]")) return;
        e.preventDefault();
        // A selection made before the tool was armed would still have a bar
        // to show. Nothing on a canvas is selectable, so dropping it costs
        // the user nothing and leaves that bar with nothing to render.
        self._dropStraySelection();
      }, { passive: false });

      // iOS stops sending pointer events part-way through a gesture whenever
      // its own classifier claims the touch — the same classifier the
      // `touchstart` above is arguing with, and the reason the handles need
      // `touch-action` set in CSS rather than at `pointerdown`. A press whose
      // release never arrives leaves the drawing it started uncommitted, and
      // the gesture with nothing BUT a release to go on is the press that
      // never moves: the dot under a question mark, the dot on an i. A stroke
      // of any length still lands, so what the user sees is a marker that
      // draws lines but will not place a dot.
      //
      // Touch events do not have that problem: `touchend` is the model iOS
      // actually implements, and it fires on the element the touch STARTED
      // on. So the release is taken from there as well. Both halves below are
      // no-ops when the pointer events did arrive — the tracking is already
      // clear, and the draft is already a shape.
      var onTouchRelease = function(e) {
        // Not the last finger up: the gesture is still going.
        if (e.touches && e.touches.length) return;
        var drawId = self._drawPointerId;
        // Whatever the pointer stream did or did not send, no fingers are
        // down now. A `_multiTouch` left standing here would swallow the
        // next stroke whole.
        self._resetTouchTracking();
        // `touchcancel` is the gesture being taken AWAY rather than
        // finished. Nothing is committed from it; what happens to the draft
        // is left to the pointer stream, which cancels it in its own time.
        if (e.type !== "touchend") return;
        self._commitStrandedDraft(e, drawId);
      };
      wrapper.addEventListener("touchend", onTouchRelease);
      wrapper.addEventListener("touchcancel", onTouchRelease);

      wrapper.addEventListener("pointermove", function(e) {
        // Only the finger that started the drawing draws it.
        if (self._multiTouch) return;
        // Only for a finger. `_drawPointerId` is cleared when a finger comes
        // off, and a mouse never reports one coming off — so on a laptop with
        // a touch screen the id left behind by the last touch stroke would
        // block every mouse move that followed it, and the hover the tooltips
        // and handles live on would go dead until the next press.
        if (e.pointerType === "touch" &&
            self._drawPointerId != null && e.pointerId !== self._drawPointerId) return;
        self._onPointerMove(e);
      });
      wrapper.addEventListener("pointerup", function(e) {
        // A finger coming off a pinch is not the end of a drawing. Both
        // drafts and the eraser were abandoned when the second finger
        // landed, so there is nothing here to finish — and `_onPointerUp`
        // is where the eraser commits, which is the one thing that must
        // not happen on the way out of a two-finger gesture.
        if (self._multiTouch) return;
        self._onPointerUp(e);
      });
      wrapper.addEventListener("pointerleave", function() { self._onPointerLeave(); });
      wrapper.addEventListener("dblclick",    function(e) { self._onDoubleClick(e); });

      // Re-render shapes in lockstep with the viewer. `animation` fires on
      // every spring-interpolation tick during a zoom or pan, so the
      // annotations follow OSD's smooth motion frame-for-frame. The other
      // events catch one-off cases (resize, source swap) that don't go
      // through the animation loop.
      function render() { self._renderAll(); }

      // Fresco 0.5 emits `animation` on every transform-write frame
      // (and `resize`/`open` on lifecycle moments). That's all the
      // overlay needs — no separate fast-pan path. The previous
      // `pan_optimized` machinery was an OSD-era workaround for the
      // canvas-redraw-per-frame cost that the new CSS-transform engine
      // doesn't have.
      self._unsubViewport = [
        handle.on("animation", render),
        handle.on("resize",    render),
        handle.on("open",      render)
      ];

      // Fresco 0.7's `handle.setSources(...)` replaces the canvas image set
      // (and, optionally, the `extensions` map) in place. Re-hydrate the
      // overlay from the new `extensions.etcher` so chapter N's shapes don't
      // linger on chapter N+1's images. No-op on Fresco < 0.7 (event never
      // fires) and on strip mode (event is canvas-only).
      if (typeof handle.on === "function") {
        self._unsubViewport.push(
          handle.on("sources-changed", function() { self._rehydrateFromExtension(); })
        );
      }
    },

    // -------------------------------------------------------------------------
    // Bottom toolbar — drawing-tool buttons + close button.
    // -------------------------------------------------------------------------

    _buildToolbar: function() {
      var self = this;
      var container = self.handle.container;

      if (getComputedStyle(container).position === "static") {
        container.style.position = "relative";
      }

      var bar = document.createElement("div");
      bar.className = "etcher-toolbar";

      // Strip mode: the scroll container IS the scrolling element, so an
      // absolutely-positioned toolbar anchored to it would scroll away
      // with the content. Tag the toolbar so the stylesheet can switch
      // it to `position: fixed`. Canvas mode keeps the existing
      // absolute positioning (the canvas container is itself fixed in
      // the viewport).
      if (self.handleKind === "strip") {
        bar.setAttribute("data-strip", "");
      }

      // `data-fresco-no-capture` tells Fresco 0.5's pointerdown handler
      // to bail when the user clicks a toolbar button — otherwise the
      // host's `e.preventDefault()` cancels the button's click event
      // and the gesture handler captures the pointer.
      bar.setAttribute("data-fresco-no-capture", "");

      // Cursor (deselect any active drawing tool). Lives in the
      // tools group and follows the same active/hidden rule in
      // compact mode (visible only while it's the selected
      // \"tool\" — i.e., when no drawing tool is active).
      bar.appendChild(self._makeToolButton("cursor", ICONS.cursor, "Cursor"));

      // The grabber (hand) is a navigation tool, so it sits beside the cursor
      // — in the same group, before the divider — not with the drawing tools.
      if (self.tools.indexOf("grabber") !== -1 && TOOL_DEFS.grabber) {
        bar.appendChild(
          self._makeToolButton("grabber", ICONS.grabber, TOOL_DEFS.grabber.title)
        );
      }

      // Intra-group divider between the cursor/grabber nav group and the
      // drawing tools. Kept on wide viewports for visual rhythm; hidden by
      // `_layoutToolbar` once the tools group starts collapsing —
      // a divider next to a single visible item reads as a stray
      // mark, not a group separator.
      var divider = document.createElement("div");
      divider.className = "etcher-divider";
      bar.appendChild(divider);
      self._cursorToolsDivider = divider;

      self.tools.forEach(function(toolKey) {
        if (toolKey === "grabber") return; // rendered beside the cursor above
        var def = TOOL_DEFS[toolKey];
        if (!def) return;
        // Non-essentials are reachable only through the `[⋯]` grid, so they
        // get no bar button at all. `_syncToolsPopup` keeps them permanently
        // visible there rather than only on overflow.
        // A button is built for EVERY tool the host offers, and
        // `_refreshToolbarTools` decides which are shown. Building only the
        // essentials meant a tool promoted onto the bar later had no button
        // to promote — it saved, and nothing appeared.
        bar.appendChild(self._makeToolButton(toolKey, def.icon, def.title));
      });

      // The "last used" slot: whichever non-essential tool was picked most
      // recently from the `[⋯]` grid, kept one click away. Drawing three
      // rectangles in a row otherwise means three trips through the menu.
      self.pinnedToolBtn = self._makePinnedToolButton();
      bar.appendChild(self.pinnedToolBtn);
      self._renderPinnedTool();

      // Compact-mode overflow trigger for tools. Sits right after the
      // tool buttons so the layout reads `[active_tool] [⋯]`
      // when only one tool is visible. Tap to open the tools popup.
      self.toolsMoreBtn = self._makeMoreButton("tools", "More tools");
      bar.appendChild(self.toolsMoreBtn);

      // Undo / redo — inline whenever the whole bar fits; treated as a
      // single unit that collapses into the tools `[⋯]` popup BOTH AT
      // ONCE the moment the bar runs short (before any tool/swatch — see
      // `_computeToolbarOverflow`). Disabled when there's nothing to
      // undo/redo. Shortcuts (Cmd/Ctrl+Z, +Shift) are wired in
      // `_wireKeyboard`.
      self.undoBtn = document.createElement("button");
      self.undoBtn.type = "button";
      self.undoBtn.title = "Undo (⌘Z)";
      self.undoBtn.setAttribute("aria-label", "Undo");
      self.undoBtn.innerHTML = ICONS.undo;
      self.undoBtn.addEventListener("click", function(e) {
        e.preventDefault();
        self._undo();
      });

      self.redoBtn = document.createElement("button");
      self.redoBtn.type = "button";
      self.redoBtn.title = "Redo (⌘⇧Z)";
      self.redoBtn.setAttribute("aria-label", "Redo");
      self.redoBtn.innerHTML = ICONS.redo;
      self.redoBtn.addEventListener("click", function(e) {
        e.preventDefault();
        self._redo();
      });

      self._refreshUndoButtons();

      // Compact-mode overflow trigger for colors. Sits right after
      // the swatches so the layout reads `[active_swatch] [⋯]` when
      // only one is visible; swatches are inserted before it by
      // `_refreshToolbarSwatches`.
      // The rightmost colors-group button is now the line-parameters entry
      // (thickness / opacity / dash). The hue picker moved onto the swatches
      // (click the active swatch again, or double-tap any swatch). Aliased to
      // `colorsMoreBtn` so the overflow layout + swatch-insertion anchor keep
      // working against the same element.
      // Colours and line parameters moved to the docked style panel
      // (`_buildStylePanel`), so the bar carries neither the swatch row nor
      // a params trigger any more. The references are cleared rather than
      // left dangling — every remaining use of them is guarded.
      self.paramsBtn = null;
      self.colorsMoreBtn = null;

      // Inline toolbar swatches reflect the user's effective palette:
      // recents first (MRU), then preset colors backfilling any
      // unused slots. New users see the static presets until they
      // pick anything; established users see their actual usage.
      //
      // The refresh reads `self.toolbar` to find its insertion
      // anchor, so point that reference at `bar` before calling —
      // even though `bar` isn't appended to the container yet,
      // insertions on a detached element work the same and `bar`
      // ends up in the DOM at the bottom of this method.
      self.toolbar = bar;

      // The bar's only remaining separator: tools | exit. Undo/redo moved to
      // the action bar and the swatches to the style panel, so the two
      // dividers that used to bracket them had nothing left to separate and
      // rendered as stray marks.
      var divider3 = document.createElement("div");
      divider3.className = "etcher-divider";
      divider3.setAttribute("data-compact-keep", "");
      bar.appendChild(divider3);

      var closeBtn = document.createElement("button");
      closeBtn.type = "button";
      closeBtn.title = "Exit annotation mode";
      closeBtn.setAttribute("aria-label", "Exit annotation mode");
      closeBtn.innerHTML = ICONS.close;
      closeBtn.addEventListener("click", function(e) {
        e.preventDefault();
        self._setAnnotationMode(false);
      });
      bar.appendChild(closeBtn);

      container.appendChild(bar);
      // `self.toolbar = bar` was set earlier so the inline swatch
      // refresh had its insertion anchor; no need to re-assign.

      // Popups are siblings of the toolbar inside the same container so
      // they share its absolute-positioning origin. Built lazily on
      // first open isn't worth the complexity — they're cheap.
      self._buildToolsPopup();
      self._buildColorsPopup();
      self._buildMarkerPopup();
      self._buildParamsPopup();

      // Re-run the overflow layout on every container resize. The
      // observer fires immediately on attach so the initial layout
      // happens via this path too — no separate call needed. Skipped
      // entirely when the toolbar is hidden (annotation mode off);
      // measurements return 0 in that state.
      if (typeof ResizeObserver === "function") {
        self._toolbarResizeObserver = new ResizeObserver(function() {
          self._layoutToolbar();
        });
        self._toolbarResizeObserver.observe(container);
      } else {
        // Defensive fallback for older browsers: layout on window
        // resize only.
        self._toolbarFallbackResize = function() { self._layoutToolbar(); };
        window.addEventListener("resize", self._toolbarFallbackResize);
      }
    },

    // Progressive-overflow layout. Walks tools and swatches in lockstep
    // (one tool, one swatch, one tool, one swatch, …), tagging each
    // with `.etcher-overflow-hidden` until the toolbar's intrinsic
    // width fits its container. When one group runs out of hideable
    // items, the other continues alone. Active tool / swatch is pinned
    // — never collapsed, so the user always sees what's selected.
    //
    // Called from:
    //   - ResizeObserver on the container (fires on initial attach
    //     too, so initial layout flows through here)
    //   - `_setAnnotationMode(true)` after the toolbar becomes visible
    //   - `_selectTool` / `_selectColor` (active item changed → re-pin)
    _layoutToolbar: function() {
      // Compute which buttons overflow (tagging them hidden), then sync
      // the tools popup so it carries ONLY the hidden tools and the
      // `[⋯]` trigger shows only when something is actually hidden.
      this._computeToolbarOverflow();
      this._syncToolsPopup();
      this._syncColorsPopup();
      // The tool bar just changed width or height, and both the action bar
      // and the style trigger are parked against its edges.
      this._positionActionBar();
      this._syncStylePanel();
      this._liftViewerZoom();
    },

    // Fresco's zoom control (`actual_size`) sits in the viewer's bottom-right
    // corner. Where the bars come down there too — a phone, whose tool bar
    // spans nearly the width — lift it above them through
    // `--fresco-zoom-bottom`, and let it back down when they go. The width
    // checked is the control's when open, so opening it never runs it under.
    _liftViewerZoom: function() {
      var host = this.handle && this.handle.container;
      if (!host || !host.style || !host.getBoundingClientRect) return;
      var zoom = host.querySelector && host.querySelector(":scope > .fresco-zoom");
      if (!zoom) return;
      var box = host.getBoundingClientRect();
      var cs = window.getComputedStyle ? window.getComputedStyle(zoom) : null;
      var right = box.right - ((cs && parseFloat(cs.right)) || 12);
      var left = right - ZOOM_CONTROL_OPEN_W;
      var top = Infinity;
      var bars = [this.toolbar, this.actionBar, this.styleTrigger];
      for (var i = 0; i < bars.length; i++) {
        var b = bars[i];
        if (!b || !b.getBoundingClientRect) continue;
        var r = b.getBoundingClientRect();
        if (!(r.width > 0 && r.height > 0)) continue;
        if (r.right <= left || r.left >= right) continue;
        if (r.top < box.bottom - ZOOM_CONTROL_REACH) continue;
        top = Math.min(top, r.top);
      }
      if (top === Infinity) host.style.removeProperty("--fresco-zoom-bottom");
      else host.style.setProperty("--fresco-zoom-bottom", Math.ceil(box.bottom - top + 8) + "px");
    },

    // Secondary action bar: things you do TO the drawing, kept off the tool
    // bar so that stays a legible row of tools. Undo and redo live here
    // (always enabled-or-not, never selection-dependent); delete and
    // duplicate act on the selection; `⋮` holds the arrange actions, which
    // are selection-scoped and too niche to spend permanent bar space on.
    _buildActionBar: function() {
      var self = this;
      var container = self.handle.container;

      var bar = document.createElement("div");
      bar.className = "etcher-actionbar";
      bar.setAttribute("data-fresco-no-capture", "");
      if (self.handleKind === "strip") bar.setAttribute("data-strip", "");

      function mk(icon, title, onClick) {
        var b = document.createElement("button");
        b.type = "button";
        b.title = title;
        b.setAttribute("aria-label", title.replace(/\s*\(.*\)$/, ""));
        b.innerHTML = icon;
        b.addEventListener("click", function(e) {
          e.preventDefault();
          onClick();
        });
        bar.appendChild(b);
        return b;
      }

      // Reuse the existing undo/redo elements so `_refreshUndoButtons`
      // keeps driving their disabled state unchanged.
      self.undoBtn.title = "Undo (\u2318Z)";
      self.redoBtn.title = "Redo (\u2318\u21e7Z)";
      bar.appendChild(self.undoBtn);
      bar.appendChild(self.redoBtn);

      var div1 = document.createElement("div");
      div1.className = "etcher-divider";
      bar.appendChild(div1);

      self.actionDeleteBtn = mk(ICONS.trash, "Delete (\u232b)", function() {
        self._deleteSelection();
      });
      self.actionDuplicateBtn = mk(ICONS.duplicate, "Duplicate (\u2318D)", function() {
        self._duplicateSelection();
      });
      // ⌘V has no equivalent on a touch device — there is no keyboard to
      // press it on, and the OS paste callout only appears over a text
      // field. Without a button, a phone can't put anything on a board that
      // it didn't draw by hand.
      self.actionPasteBtn = mk(ICONS.paste, "Paste (\u2318V)", function() {
        self._pasteFromClipboard();
      });
      // Layer order, beside the other selection actions: one button opening
      // a small pop-up of the four arrange moves. Four more icons inline made
      // the bar noticeably wider than the tool bar under it, and these are
      // occasional actions with keyboard shortcuts for anyone who reaches
      // for them often.
      self.actionLayersBtn = mk(ICONS.layers, "Arrange layers", function() {
        self._toggleLayersMenu();
      });

      // The `⋯` is kept, hidden: with Arrange on its own button it would hold
      // nothing of its own yet, and it will again (see `_toggleActionMenu`).
      var div2 = document.createElement("div");
      div2.className = "etcher-divider";
      div2.style.display = "none";
      bar.appendChild(div2);
      self.actionMoreBtn = mk(ICONS.more, "More", function() {
        self._toggleActionMenu();
      });
      self.actionMoreBtn.style.display = "none";

      container.appendChild(bar);
      self.actionBar = bar;

      self._buildActionMenu();
      self._buildLayersMenu();
      self._syncActionBar();
      self._positionActionBar();
    },

    // The `⋯` menu: the style controls the panel isn't showing (see
    // `_hostParamsIn`). Its button is hidden for now — see `_buildActionBar`.
    _buildActionMenu: function() {
      var self = this;
      var menu = document.createElement("div");
      menu.className = "etcher-popup";
      menu.dataset.kind = "actions";
      menu.setAttribute("data-fresco-no-capture", "");

      var host = document.createElement("div");
      host.className = "etcher-stylepanel etcher-menu-host";
      menu.appendChild(host);
      self._actionParamsHost = host;


      self.handle.container.appendChild(menu);
      self.actionMenu = menu;
    },

    // The layers pop-up over the action bar: the four arrange moves as
    // captioned buttons in the style panel's own button style.
    _buildLayersMenu: function() {
      var self = this;
      var menu = document.createElement("div");
      menu.className = "etcher-popup";
      menu.dataset.kind = "layers";
      menu.setAttribute("data-fresco-no-capture", "");

      var row = document.createElement("div");
      row.className = "etcher-arrange-row";
      self.actionArrangeBtns = [
        { where: "front", icon: ICONS.toFront, caption: "Front", title: "Bring to front (\u2318])" },
        { where: "forward", icon: ICONS.forward, caption: "Raise", title: "Bring forward (])" },
        { where: "backward", icon: ICONS.backward, caption: "Lower", title: "Send backward ([)" },
        { where: "back", icon: ICONS.toBack, caption: "Back", title: "Send to back (\u2318[)" }
      ].map(function(entry) {
        var b = document.createElement("button");
        b.type = "button";
        b.className = "etcher-chip";
        b.dataset.arrange = entry.where;
        b.title = entry.title;
        b.setAttribute("aria-label", entry.title.replace(/\s*\(.*\)$/, ""));
        b.innerHTML = '<span class="etcher-chip-face">' + entry.icon + '</span>' +
          '<span class="etcher-chip-caption">' + entry.caption + '</span>';
        b.addEventListener("click", function(e) {
          e.preventDefault();
          self._arrange(entry.where);
          // Left open — nudging something forward a few steps is the common
          // case, same reasoning as the undo button.
        });
        row.appendChild(b);
        return b;
      });
      menu.appendChild(row);

      self.handle.container.appendChild(menu);
      self.layersMenu = menu;
    },

    _toggleLayersMenu: function() {
      var self = this;
      var menu = self.layersMenu;
      if (!menu) return;
      var open = menu.classList.contains("is-open");
      self._closeLayersMenu();
      if (open) return;
      self._closeActionMenu();
      menu.classList.add("is-open");
      var btnRect = self.actionLayersBtn.getBoundingClientRect();
      var barRect = self.actionBar.getBoundingClientRect();
      var contRect = self.handle.container.getBoundingClientRect();
      var mRect = menu.getBoundingClientRect();
      // Over its button, inside the container.
      var left = btnRect.left + btnRect.width / 2 - mRect.width / 2 - contRect.left;
      left = Math.max(4, Math.min(left, contRect.width - mRect.width - 4));
      menu.style.left = left + "px";
      menu.style.top = barRect.top - contRect.top - mRect.height - 6 + "px";
      self.actionLayersBtn.classList.add("is-active");
      self._onLayersMenuOutside = function(e) {
        if (menu.contains(e.target) || self.actionLayersBtn.contains(e.target)) return;
        self._closeLayersMenu();
      };
      document.addEventListener("pointerdown", self._onLayersMenuOutside, true);
    },

    _closeLayersMenu: function() {
      if (!this.layersMenu) return;
      this.layersMenu.classList.remove("is-open");
      if (this.actionLayersBtn) this.actionLayersBtn.classList.remove("is-active");
      if (this._onLayersMenuOutside) {
        document.removeEventListener("pointerdown", this._onLayersMenuOutside, true);
        this._onLayersMenuOutside = null;
      }
    },

    // Move the live params controls into `host`, with a divider above them so
    // they read as a section rather than as more arrange buttons.
    _hostParamsIn: function(host) {
      if (!host || !this.paramsPopup) return;
      if (!this._paramsDivider) {
        var d = document.createElement("div");
        d.className = "etcher-popup-divider";
        this._paramsDivider = d;
      }
      // Params first, arrange last. Sending something to the back of the
      // canvas is the occasional action; thickness and line type are what
      // you reach for between strokes, so they sit nearest the pointer —
      // this menu opens upwards out of a bar at the bottom of the screen.
      // Into the menu's panel-styled holder, so they lay out as the full
      // panel does (see `_buildActionMenu`).
      var inner = host === this.actionMenu && this._actionParamsHost ? this._actionParamsHost : host;
      if (this.paramsPopup.parentNode === inner) return;
      inner.appendChild(this.paramsPopup);
      host.insertBefore(this._paramsDivider, inner.nextSibling);
    },

    // Hand them back to the style panel. Called when the menu closes, so the
    // panel is never left with an empty slot where its controls used to be.
    _releaseParams: function() {
      if (this._paramsDivider && this._paramsDivider.parentNode) {
        this._paramsDivider.parentNode.removeChild(this._paramsDivider);
      }
      if (this._actionParamsHost) this._actionParamsHost.removeAttribute("data-show");
      this._syncStylePanel();
    },

    _toggleActionMenu: function() {
      var self = this;
      if (!self.actionMenu) return;
      var open = self.actionMenu.classList.contains("is-open");
      self._closeActionMenu();
      if (open) return;

      // The style controls that are not colours — thickness, opacity, line
      // type, fill — are hosted here while this menu is open, so they stay
      // reachable when the compact strip has been set to leave them out.
      // Nobody should have to turn a control back on in order to use it once.
      //
      // The very same element is moved rather than a second copy being built.
      // Only one of the two places can be on screen at a time, and two sets
      // of sliders for one value is a bug waiting to happen — they disagree
      // the moment anything changes either one from elsewhere.
      // Only what the panel isn't showing: nothing while the full panel is
      // up (or reachable from its own button on a narrow screen), the
      // controls the compact strip leaves out while it is the strip, and all
      // of them while the panel is hidden. The menu never repeats the panel.
      var show = self._menuParamsShow();
      var paramsHost = self._actionParamsHost;
      if (show !== "none") {
        self._hostParamsIn(self.actionMenu);
        if (paramsHost) {
          paramsHost.setAttribute("data-show", show);
          paramsHost.setAttribute("data-compact-parts",
            (self.stylePanel && self.stylePanel.getAttribute("data-compact-parts")) || "");
        }
      }

      var barRect = self.actionBar.getBoundingClientRect();
      var contRect = self.handle.container.getBoundingClientRect();
      self.actionMenu.classList.add("is-open");
      // Borrowed, but nothing of it applies here (the strip already has the
      // rest, or the selection takes none of these): hand it back rather
      // than open on an empty section.
      if (show !== "none" && paramsHost && !self._menuParamsVisible(paramsHost)) {
        self._releaseParams();
      }
      var mRect = self.actionMenu.getBoundingClientRect();
      self.actionMenu.style.left =
        Math.max(4, barRect.right - contRect.left - mRect.width) + "px";
      self.actionMenu.style.top =
        barRect.top - contRect.top - mRect.height - 6 + "px";
      self.actionMoreBtn.classList.add("is-active");

      self._onActionMenuOutside = function(e) {
        if (self.actionMenu.contains(e.target) ||
            self.actionMoreBtn.contains(e.target)) return;
        self._closeActionMenu();
      };
      document.addEventListener("pointerdown", self._onActionMenuOutside, true);
    },

    // What of the style controls the ⋯ menu carries: "none" (the panel shows
    // them all), "missing" (the compact strip is up: only what it leaves
    // out) or "all" (the panel is hidden).
    _menuParamsShow: function() {
      var panel = this.stylePanel;
      if (!panel) return "all";
      // A narrow screen: the panel is a pop-up from its own button, every
      // control a press away there.
      if (panel.hasAttribute("data-compact")) return "none";
      var size = panel.getAttribute("data-size");
      if (size === "hidden") return "all";
      if (size === "compact") return "missing";
      return "none";
    },

    // Whether the borrowed controls show anything in the menu.
    _menuParamsVisible: function(host) {
      var els = host.querySelectorAll(".etcher-chip, .etcher-inline-row, .etcher-marker-row");
      for (var i = 0; i < els.length; i++) {
        if (els[i].offsetParent !== null && !els[i].closest(".etcher-chip-pop")) return true;
      }
      return false;
    },

    _closeActionMenu: function() {
      if (!this.actionMenu) return;
      this.actionMenu.classList.remove("is-open");
      if (this.actionMoreBtn) this.actionMoreBtn.classList.remove("is-active");
      this._releaseParams();
      if (this._onActionMenuOutside) {
        document.removeEventListener("pointerdown", this._onActionMenuOutside, true);
        this._onActionMenuOutside = null;
      }
    },

    // Docked style panel: the swatch row plus the stroke parameters, both
    // taken off the tool bar.
    //
    // The params section is the existing params popup, re-parented here and
    // restyled by CSS into a static block. Reusing the element outright
    // keeps every slider, its wiring and its sync logic exactly as it was —
    // this is a relocation, not a reimplementation.
    _buildStylePanel: function() {
      var self = this;

      var panel = document.createElement("div");
      panel.className = "etcher-stylepanel";
      panel.setAttribute("data-fresco-no-capture", "");
      if (self.handleKind === "strip") panel.setAttribute("data-strip", "");

      var swatches = document.createElement("div");
      swatches.className = "etcher-stylepanel-swatches";
      panel.appendChild(swatches);
      self.swatchHost = swatches;

      var divider = document.createElement("div");
      divider.className = "etcher-stylepanel-divider";
      panel.appendChild(divider);

      // `_buildParamsPopup` runs during setup and appends to the container;
      // move that element in here if it exists yet, otherwise leave a slot
      // and adopt it on the next sync.
      if (self.paramsPopup) panel.appendChild(self.paramsPopup);

      self.handle.container.appendChild(panel);
      self._applyPanelOffset();
      self.stylePanel = panel;
      if (self._panelSide) panel.setAttribute("data-side", self._panelSide);
      self._buildStyleTrigger();
      self._buildPanelToggle();

      // Seeds `_colorSlots` (if empty) and renders the slot row into the
      // panel — previously done inline while building the tool bar.
      self._refreshToolbarSwatches();
      self._syncStylePanel();
    },

    // The chevron that cycles the style panel between its three sizes.
    //
    // A cycle rather than three buttons or a menu: there are only three, and
    // the one you want is nearly always "less than now". A menu to make a
    // panel smaller is more chrome than the panel.
    _buildPanelToggle: function() {
      var self = this;
      if (self.panelToggle) return;
      // In the viewer's own nav, when the host asks (`panel_toggle: :nav`):
      // one strip of controls instead of two clusters, which on a small
      // screen is the difference between seeing the picture and not. The
      // panel then drops from the top-left (see `_applyPanelOffset`).
      if (self._panelToggleInNav()) {
        // Right after the pencil: it folds away the pencil's panel.
        var remove = self.handle.appendNavButton(ICONS.panelShown, "Narrow the style panel to one column", function() {
          self._cyclePanelSize();
        }, { slot: self._navMirrored() ? NAV_SLOTS.chevronMirrored : NAV_SLOTS.chevron });
        self.removePanelToggleBtn = remove;
        self.panelToggle = remove.el;
        self.panelToggle.hidden = true;
        self._applyPanelPref(self._getPref("panel"));
        return;
      }
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "etcher-panel-toggle";
      btn.setAttribute("data-fresco-no-capture", "");
      btn.addEventListener("click", function(e) {
        e.preventDefault();
        e.stopPropagation();
        self._cyclePanelSize();
      });
      self.handle.container.appendChild(btn);
      self.panelToggle = btn;
      self._applyPanelPref(self._getPref("panel"));
    },

    // The tools that ride on the bar itself, in the order they sit there.
    // Everything else the host offered is still available — it lives in the
    // overflow menu — so this is about what is worth a permanent slot, not
    // about taking tools away.
    //
    // `ESSENTIAL_TOOLS` is the default rather than the rule: a board full of
    // diagrams wants the dimension tool to hand, a board of photographs wants
    // the marker, and neither is wrong.
    _essentialTools: function() {
      var want = this._getPref("tools");
      if (!Array.isArray(want)) return ESSENTIAL_TOOLS;
      return want;
    },

    _isEssentialTool: function(key) {
      return this._essentialTools().indexOf(key) !== -1;
    },

    // Applied by moving and hiding the existing buttons rather than
    // rebuilding the bar: every other part of the toolbar holds references
    // to those buttons — the pinned slot, the overflow popup, the
    // active-tool highlight — and a rebuild would have to re-thread all of
    // them for a change that is only about what is on show and in what
    // order.
    _refreshToolbarTools: function() {
      var self = this;
      if (!self.toolbar) return;
      var order = self._essentialTools();
      var btns = [].slice.call(self.toolbar.querySelectorAll("button[data-tool]"));

      btns.forEach(function(btn) {
        var key = btn.dataset.tool;
        // The cursor is not a drawing tool and is how you get out of one, so
        // it never leaves the bar. Neither does the pinned slot, which shows
        // whichever tool is active and would otherwise vanish mid-stroke.
        if (!key || key === "cursor" || btn.hasAttribute("data-pinned-tool")) return;
        btn.style.display = self._isEssentialTool(key) ? "" : "none";
      });

      // Re-order by moving each chosen tool to sit after the one before it.
      // Only the tools in the preference move; the cursor, the grabber, the
      // overflow button and the separators keep the places they were built
      // in, so the bar's furniture is not disturbed by a reorder.
      var prev = null;
      order.forEach(function(key) {
        var btn = btns.filter(function(b) {
          return b.dataset.tool === key && !b.hasAttribute("data-pinned-tool");
        })[0];
        if (!btn || !btn.parentNode) return;
        if (prev && prev.nextSibling !== btn) {
          prev.parentNode.insertBefore(btn, prev.nextSibling);
        }
        prev = btn;
      });

      if (self.toolsPopup) self._syncToolsPopup();
      self._syncViewToggles();
    },

    // SortableJS, the drag-reorder library the rest of the site uses. Loaded
    // from the same CDN pin phoenix_kit uses, so a page carrying both ends up
    // with one copy and one behaviour.
    //
    // Etcher does not depend on it: the list falls back to the browser's own
    // drag-and-drop if it cannot be fetched, so customising still works
    // offline or behind a proxy that blocks the CDN.
    //
    // A host decides where it comes from, through `window.Etcher` (read at
    // the moment it is needed, so values set before or after etcher.js
    // loads both count):
    //
    //   sortableUrl          — load SortableJS from this URL instead. It is
    //                          authoritative: if it fails, the list uses
    //                          native drag-and-drop and the CDN is NOT tried.
    //   loadSortableFromCdn  — `false` means no request at all; the list
    //                          uses native drag-and-drop straight away.
    //
    // Defaults keep the CDN, so nothing changes for a host that sets
    // neither. A strict-CSP, offline or no-third-party host sets the first
    // to its own copy, or the second to false. A script that loads but
    // leaves no usable `Sortable` constructor takes the native path too.
    _withSortable: function(cb) {
      if (typeof window.Sortable === "function") { cb(window.Sortable); return; }
      if (this._sortableFailed) { cb(null); return; }
      var cfg = window.Etcher || {};
      var custom = typeof cfg.sortableUrl === "string" ? cfg.sortableUrl.trim() : "";
      if (!custom && cfg.loadSortableFromCdn === false) { cb(null); return; }
      var self = this;
      if (self._sortableWaiting) { self._sortableWaiting.push(cb); return; }
      self._sortableWaiting = [cb];
      var done = function(lib) {
        var waiting = self._sortableWaiting || [];
        self._sortableWaiting = null;
        waiting.forEach(function(fn) { try { fn(lib); } catch (_) {} });
      };
      var script = document.createElement("script");
      script.src = custom || SORTABLE_CDN;
      script.onload = function() {
        if (typeof window.Sortable === "function") { done(window.Sortable); return; }
        self._sortableFailed = true;
        done(null);
      };
      script.onerror = function() { self._sortableFailed = true; done(null); };
      document.head.appendChild(script);
    },

    // ── Customise dialog ────────────────────────────────────────────────────
    //
    // What rides on the toolbar, in what order, and what the compact style
    // strip keeps. Nothing is taken away by any of it: a tool turned off the
    // bar still lives in the overflow menu, and the full style panel still
    // has everything.
    //
    // Edits are held in the dialog until Save. A settings panel that applies
    // as you touch it cannot be backed out of, and reordering means dropping
    // things in the wrong place on the way to the right one.
    _openCustomise: function() {
      var self = this;
      if (self.customiseBackdrop) self._closeCustomise();

      // The working copy. Every tool the host offered, in bar order first so
      // the list opens looking like the bar it edits.
      var essential = self._essentialTools();
      var offered = (self.tools || []).filter(function(t) { return !!TOOL_DEFS[t]; });
      var parts = (self._getPref("compact") || []).slice();

      var backdrop = document.createElement("div");
      backdrop.className = "etcher-customise-backdrop";
      // Claimed NOW, not after the dialog is built. Sortable's setup runs
      // synchronously once the library is cached — every open after the
      // first — and it checks this to tell "still the open dialog" from
      // "closed while the library was loading". Assigned at the end, that
      // check saw the null left by the previous close and bailed, so the
      // drag worked exactly once per page load.
      self.customiseBackdrop = backdrop;
      backdrop.setAttribute("data-fresco-no-capture", "");
      backdrop.addEventListener("pointerdown", function(e) {
        // Only a press on the backdrop itself dismisses — a press that
        // started inside the dialog and drifted out during a drag must not.
        if (e.target === backdrop) self._closeCustomise();
      });

      var box = document.createElement("div");
      box.className = "etcher-customise";
      backdrop.appendChild(box);

      var h = document.createElement("h3");
      h.textContent = "Customise";
      box.appendChild(h);
      var sub = document.createElement("p");
      sub.textContent =
        "Drag tools between the bar and the \u2026 menu, and along the bar to " +
        "order them. Nothing is removed either way.";
      box.appendChild(sub);

      function trayWithLabel(text, gapAbove) {
        var label = document.createElement("p");
        label.style.margin = (gapAbove ? "14px" : "0") + " 0 6px";
        label.textContent = text;
        box.appendChild(label);
        var tray = document.createElement("div");
        tray.className = "etcher-customise-tray";
        box.appendChild(tray);
        return tray;
      }

      // The menu is listed ABOVE the toolbar because that is where the two
      // sit on screen: the bar runs along the bottom and the \u2026 menu opens
      // upwards out of it. Listing them the other way round means dragging a
      // tool "down" into the menu when the menu is up.
      var menuTray = trayWithLabel("In the \u2026 menu");
      var barTray = trayWithLabel("On the toolbar", true);

      var dragKey = null;

      function chipFor(key) {
        // A div rather than a button. Sortable's pointer-driven fallback is
        // unreliable on interactive elements — the browser's own handling of
        // a press on a button competes with it, which showed up as a chip
        // that entered the drag state and then would not move. Nothing here
        // needs button behaviour: it is dragged, not pressed.
        var chip = document.createElement("div");
        chip.setAttribute("role", "listitem");
        chip.className = "etcher-customise-chip";
        chip.dataset.tool = key;
        chip.innerHTML = (TOOL_DEFS[key] || {}).icon || "";
        // The tool titles carry a parenthetical explanation, which is right
        // on a tooltip over a 36px square and nothing else would be.
        chip.title = ((TOOL_DEFS[key] || {}).title || key).split(" (")[0];
        chip.setAttribute("aria-label", chip.title);
        return chip;
      }

      // The browser's own drag-and-drop, wired ONLY when SortableJS could not
      // be fetched. Wiring it either way is what broke the drag: Sortable
      // sets `draggable` on its items itself, and anything else touching that
      // attribute afterwards leaves a chip that enters the drag state and
      // then cannot move.
      function enableNativeDnd() {
        [].forEach.call(box.querySelectorAll(".etcher-customise-chip"), function(chip) {
          chip.draggable = true;
          chip.addEventListener("dragstart", function(e) {
            dragKey = chip.dataset.tool;
            try { e.dataTransfer.setData("text/plain", dragKey); } catch (_) {}
          });
          chip.addEventListener("dragend", function() { dragKey = null; });
        });
        [barTray, menuTray].forEach(function(tray) {
          tray.addEventListener("dragover", function(e) {
            if (dragKey) e.preventDefault();
          });
          tray.addEventListener("drop", function(e) {
            if (!dragKey) return;
            e.preventDefault();
            var moving = box.querySelector(
              '.etcher-customise-chip[data-tool="' + dragKey + '"]'
            );
            if (!moving) return;
            // Dropped ON a chip means "before this one"; dropped on the
            // tray's empty space means the end.
            var over = e.target.closest && e.target.closest(".etcher-customise-chip");
            if (over && over !== moving) tray.insertBefore(moving, over);
            else tray.appendChild(moving);
          });
        });
      }

      essential.filter(function(t) { return offered.indexOf(t) !== -1; })
        .forEach(function(t) { barTray.appendChild(chipFor(t)); });
      offered.filter(function(t) { return essential.indexOf(t) === -1; })
        .forEach(function(t) { menuTray.appendChild(chipFor(t)); });

      // Sortable takes over when it is there — the same library the
      // reorderable lists elsewhere use. One group across both trays, so a
      // tool can be carried from one to the other.
      self._withSortable(function(Sortable) {
        // Compared against THIS backdrop rather than merely non-null, so a
        // dialog closed and reopened while the library was still loading
        // cannot have the late callback attach Sortable to its trays.
        if (self.customiseBackdrop !== backdrop) return;
        if (!Sortable) { enableNativeDnd(); return; }
        try {
          self._customiseSortable = [barTray, menuTray].map(function(tray) {
            return new Sortable(tray, {
              group: "etcher-customise",
              animation: 150,
              draggable: ".etcher-customise-chip",
              ghostClass: "is-ghost",
              chosenClass: "is-chosen"
              // Sortable's defaults otherwise, which is what the reorderable
              // lists elsewhere on the site use. Fresco exempts this dialog
              // from pointer capture already (it checks `closest` for the
              // no-capture attribute), so there is nothing here that native
              // drag-and-drop has to survive.
            });
          });
        } catch (_) {
          enableNativeDnd();
        }
      });

      // The compact strip's parts.
      var h2 = document.createElement("h3");
      h2.style.marginTop = "18px";
      h2.textContent = "Compact style panel";
      box.appendChild(h2);
      var sub2 = document.createElement("p");
      sub2.textContent = "What the narrow strip keeps. The full panel always has everything.";
      box.appendChild(sub2);

      var partsList = document.createElement("div");
      partsList.className = "etcher-customise-list";
      [
        { key: "colors", label: "Colours" },
        { key: "dash", label: "Line types" },
        { key: "fills", label: "Fills" }
      ].forEach(function(part) {
        var row = document.createElement("label");
        row.className = "etcher-customise-row";
        row.style.cursor = "pointer";
        var cb = document.createElement("input");
        cb.type = "checkbox";
        cb.checked = parts.indexOf(part.key) !== -1;
        cb.addEventListener("change", function() {
          var at = parts.indexOf(part.key);
          if (cb.checked && at === -1) parts.push(part.key);
          if (!cb.checked && at !== -1) parts.splice(at, 1);
        });
        var name = document.createElement("span");
        name.textContent = part.label;
        row.appendChild(name);
        row.appendChild(cb);
        partsList.appendChild(row);
      });
      box.appendChild(partsList);

      var actions = document.createElement("div");
      actions.className = "etcher-customise-actions";
      var reset = document.createElement("button");
      reset.type = "button";
      reset.textContent = "Reset";
      reset.addEventListener("click", function() {
        self._setPref("tools", null);
        self._setPref("compact", ["colors", "dash"]);
        self._closeCustomise();
      });
      var cancel = document.createElement("button");
      cancel.type = "button";
      cancel.textContent = "Cancel";
      cancel.addEventListener("click", function() { self._closeCustomise(); });
      var save = document.createElement("button");
      save.type = "button";
      save.setAttribute("data-primary", "");
      save.textContent = "Save";
      save.addEventListener("click", function() {
        self._setPref("tools", [].map.call(barTray.children, function(c) {
          return c.dataset.tool;
        }));
        self._setPref("compact", parts.slice());
        self._closeCustomise();
      });
      actions.appendChild(reset);
      actions.appendChild(cancel);
      actions.appendChild(save);
      box.appendChild(actions);

      self.handle.container.appendChild(backdrop);
      // Displayed in its own frame so the opening is not batched with the
      // click that asked for it.
      backdrop.classList.add("is-open");

      self._customiseEsc = function(e) {
        if (e.key === "Escape") self._closeCustomise();
      };
      document.addEventListener("keydown", self._customiseEsc, true);
    },

    _closeCustomise: function() {
      if (this._customiseSortable) {
        [].concat(this._customiseSortable).forEach(function(s) {
          try { s.destroy(); } catch (_) {}
        });
        this._customiseSortable = null;
      }
      if (this._customiseEsc) {
        document.removeEventListener("keydown", this._customiseEsc, true);
        this._customiseEsc = null;
      }
      if (this.customiseBackdrop && this.customiseBackdrop.parentNode) {
        this.customiseBackdrop.parentNode.removeChild(this.customiseBackdrop);
      }
      this.customiseBackdrop = null;
    },

    // The host's choice, and only where the viewer has a nav to join.
    _panelToggleInNav: function() {
      return !!(this.el && this.el.dataset && this.el.dataset.panelToggle === "nav" &&
        this.handle && typeof this.handle.appendNavButton === "function");
    },

    // Fresco's mirrored row: its nav says so on the element, and only a row
    // is laid out in reverse (a column ignores the flag).
    _navMirrored: function() {
      var host = this.handle && this.handle.container;
      var nav = host && host.querySelector && host.querySelector(":scope > .fresco-nav");
      return !!(nav && nav.getAttribute("data-layout") === "row" && nav.hasAttribute("data-reverse"));
    },

    // Shown with the docked panel and gone without it. The corner chevron
    // does that with a class its own CSS reads; one in the viewer's nav is
    // Fresco's button and is simply hidden, so the nav closes up behind it.
    _setPanelToggleActive: function(on) {
      var btn = this.panelToggle;
      if (!btn) return;
      if (this.removePanelToggleBtn) btn.hidden = !on;
      else btn.classList.toggle("is-active", on);
    },

    _cyclePanelSize: function() {
      var order = ["full", "compact", "hidden"];
      var now = this._getPref("panel");
      var i = order.indexOf(now);
      this._setPref("panel", order[(i === -1 ? 0 : i + 1) % order.length]);
    },

    _applyPanelPref: function(mode) {
      if (mode !== "compact" && mode !== "hidden") mode = "full";
      if (this._openChip) this._closeChipPop();
      if (this.stylePanel) this.stylePanel.setAttribute("data-size", mode);
      // What the ⋯ menu carries follows the panel's size.
      if (this.actionBar) this._syncActionBar();
      // Which buttons belong depends on the size (thickness is one only in
      // the strip).
      if (this._chips) this._syncStyleChips();
      var btn = this.panelToggle;
      if (!btn) return;
      if (this.removePanelToggleBtn) {
        // Same three steps as the corner chevron (full → one column →
        // hidden), told by the icon instead of an arrow. Lit while any of
        // the panel is showing.
        var shown = mode !== "hidden";
        btn.innerHTML = mode === "full" ? ICONS.panelShown
          : (mode === "compact" ? ICONS.panelCompact : ICONS.panelHidden);
        var label = mode === "full" ? "Narrow the style panel to one column"
          : (mode === "compact" ? "Hide the style panel" : "Show the style panel");
        btn.title = label;
        btn.setAttribute("aria-label", label);
        btn.classList.toggle("etcher-nav-on", shown);
        this._setPanelToggleActive(!!this.annotationMode && !this._isCompactLayout());
        return;
      }
      // The chevron points at what pressing it does: up while there is still
      // something to fold away, down once there is nothing left but to bring
      // it all back.
      btn.innerHTML = mode === "hidden" ? ICONS.chevronDown : ICONS.chevronUp;
      var title = mode === "full"
        ? "Shrink the style panel"
        : (mode === "compact" ? "Hide the style panel" : "Show the style panel");
      btn.title = title;
      btn.setAttribute("aria-label", title);
      // Same rule as `_syncStylePanel`: a panel that does not dock has no
      // chevron. This path runs on its own (a stored pref arriving, the
      // toggle being built), so the condition has to be repeated rather
      // than assumed.
      this._setPanelToggleActive(!!this.annotationMode && !this._isCompactLayout());
    },

    // The button that opens the style panel when it can't be docked. Lives
    // beside the tool bar rather than inside it: the bar is a row of tools
    // and this isn't one, and keeping it out means the bar's overflow logic
    // doesn't have to reason about a button that must never be collapsed.
    _buildStyleTrigger: function() {
      var self = this;
      if (self.styleTrigger) return;

      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "etcher-style-trigger";
      btn.setAttribute("data-fresco-no-capture", "");
      if (self.handleKind === "strip") btn.setAttribute("data-strip", "");
      btn.title = "Style";
      btn.setAttribute("aria-label", "Style");
      btn.setAttribute("aria-expanded", "false");
      btn.addEventListener("click", function(e) {
        e.preventDefault();
        e.stopPropagation();
        self._toggleStylePanel();
      });

      self.handle.container.appendChild(btn);
      self.styleTrigger = btn;
      self._renderStyleTriggerIcon();
    },

    // A brush stroke in the active colour, so the closed button still shows
    // what it would open onto.
    _renderStyleTriggerIcon: function() {
      if (!this.styleTrigger) return;
      var color = this.activeColor || "#ffffff";
      this.styleTrigger.innerHTML =
        '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" ' +
        'stroke="' + escapeHtml(color) + '" stroke-width="3" ' +
        'stroke-linecap="round" aria-hidden="true">' +
        '<path d="M4 16c3-6 5 4 8-2s5-6 8-4"/></svg>';
    },

    // Docked panel or popup? Decided on the CONTAINER, not the viewport —
    // the layer can be embedded in a column on an otherwise wide page, and
    // it's the space around the canvas that determines whether a permanent
    // 190px panel is affordable.
    _isCompactLayout: function() {
      var c = this.handle && this.handle.container;
      if (!c || typeof c.getBoundingClientRect !== "function") return false;
      return c.getBoundingClientRect().width < 720;
    },

    // The trigger is a square matching the tool bar's height, so the two read
    // as one row. Falls back to the button size the bar is built from when
    // the bar hasn't been laid out yet.
    _styleTriggerSize: function() {
      var h = this.toolbar ? Math.round(this.toolbar.getBoundingClientRect().height) : 0;
      return h > 0 ? h : 48;
    },

    // How wide the bar actually renders.
    //
    // NOT `scrollWidth`: the bar is a flex row sized by its own content, so
    // it never overflows itself and `scrollWidth` reports the client box —
    // measured at 448 against an offsetWidth of 490, a whole button short.
    // The desktop budget had slack enough to absorb that, so the bar just
    // collapsed a little later than intended; the compact one has to leave
    // an exact gap for the style trigger, and came up a button too wide.
    _toolbarWidth: function() {
      if (!this.toolbar) return 0;
      return Math.max(this.toolbar.scrollWidth, this.toolbar.offsetWidth);
    },

    // The docked panel's 12px top/right are CSS, which resolves against the
    // container while it is absolute — but against the VIEWPORT once strip
    // mode makes it fixed, which would float it in the window's corner
    // rather than the strip's. Measure it there instead.
    _positionDockedPanel: function() {
      if (!this.stylePanel || this.handleKind !== "strip" || !this.handle) return;
      var c = this.handle.container.getBoundingClientRect();
      this.stylePanel.style.top = Math.round(c.top + CHROME_MARGIN) + "px";
      this.stylePanel.style.right =
        Math.round(window.innerWidth - c.right + CHROME_MARGIN) + "px";
    },

    _toggleStylePanel: function(force) {
      if (!this.stylePanel) return;
      var open = typeof force === "boolean"
        ? force
        : !this.stylePanel.classList.contains("is-open");
      this.stylePanel.classList.toggle("is-open", open);
      if (this.styleTrigger) {
        this.styleTrigger.classList.toggle("is-open", open);
        this.styleTrigger.setAttribute("aria-expanded", open ? "true" : "false");
      }
      if (open) this._positionStyleChrome();
      this._wireStylePanelOutsideClick(open);
    },

    // Tapping the canvas closes the popup. Etcher's own chrome is excluded,
    // or picking a colour would close the panel the colours are in.
    _wireStylePanelOutsideClick: function(on) {
      var self = this;
      if (on && !self._stylePanelOutside) {
        self._stylePanelOutside = function(e) {
          if (e.target.closest && e.target.closest(CHROME_SELECTOR)) return;
          self._toggleStylePanel(false);
        };
        document.addEventListener("pointerdown", self._stylePanelOutside, true);
      } else if (!on && self._stylePanelOutside) {
        document.removeEventListener("pointerdown", self._stylePanelOutside, true);
        self._stylePanelOutside = null;
      }
    },

    // Park the trigger beside the tool bar and the popup above it. Measured
    // rather than expressed in CSS because the tool bar's width changes as
    // tools overflow and its height with the button size.
    _positionStyleChrome: function() {
      var self = this;
      if (!self.styleTrigger || !self.toolbar || !self.handle) return;
      var c = self.handle.container.getBoundingClientRect();
      var t = self.toolbar.getBoundingClientRect();
      if (!t.width || !c.width) return;

      var gap = CHROME_GAP;
      var margin = CHROME_MARGIN;
      var h = self._styleTriggerSize();
      self.styleTrigger.style.height = h + "px";
      self.styleTrigger.style.width = h + "px";

      // The bar centres itself on the container. Left alone it would centre
      // as if it were the only thing on the row, pushing its right edge into
      // the space the trigger needs — so shift it half the trigger's
      // footprint left and the pair ends up centred as one group.
      self.toolbar.style.transform =
        "translateX(calc(-50% - " + Math.round((gap + h) / 2) + "px))";

      // Re-measure: the shift just moved it.
      t = self.toolbar.getBoundingClientRect();

      // Beside the bar, which is what the group centring is for. The stacked
      // fallback is for a container too small even for the collapsed bar —
      // the bar bottoms out at a width it can't go under, and putting the
      // trigger on top of it would be worse than a second row.
      // Offsets are measured from whichever origin this surface is
      // positioned against — the container when absolute, the viewport when
      // fixed (strip mode). `c` still supplies the WIDTH the bar has to fit
      // in, which is the container's either way.
      var o = self._chromeOrigin();
      var right = Math.round(t.right - o.left);
      var minLeft = Math.round(c.left - o.left) + margin;
      var maxRight = Math.round(c.right - o.left) - margin;
      var top;
      var left;
      if (right + gap + h <= maxRight) {
        left = right + gap;
        top = Math.round(t.top - o.top);
      } else {
        left = maxRight - h;
        top = Math.round(t.top - o.top) - h - gap;
      }
      self.styleTrigger.style.left = Math.max(minLeft, left) + "px";
      self.styleTrigger.style.top = Math.max(margin, top) + "px";

      if (self.stylePanel && self.stylePanel.hasAttribute("data-compact")) {
        // Opens upward off the trigger, right-aligned to the container so it
        // covers canvas rather than hanging off the side. `bottom` is
        // measured from the viewport when fixed and the container when not,
        // matching the space `top` was just computed in.
        self.stylePanel.style.left = "auto";
        self.stylePanel.style.top = "auto";
        self.stylePanel.style.right =
          Math.round(self._chromeRightInset(c)) + "px";
        self.stylePanel.style.bottom =
          Math.round(self._chromeHeight() - top + gap) + "px";
      }
    },

    // The panel is only meaningful while annotating, same as the tool bar.
    // The panel + its chevron anchor to the container's top-right through
    // two CSS custom properties. A host whose own chrome lives in that
    // corner (phoenix_kit's media page keeps its details minimizer there)
    // passes `panel_offset` on <Etcher.layer> and the whole cluster —
    // chevron at the anchor, panel 40px below it — moves together.
    _applyPanelOffset: function() {
      if (!this.handle || !this.handle.container) return;
      var raw = this.el && this.el.dataset && this.el.dataset.panelOffset;
      if (!raw) return;
      var offset;
      try { offset = JSON.parse(raw); } catch (_e) { return; }
      if (!offset || typeof offset !== "object") return;
      if (typeof offset.top === "number") {
        this.handle.container.style.setProperty(
          "--etcher-panel-anchor-top", offset.top + "px");
      }
      if (typeof offset.right === "number") {
        this.handle.container.style.setProperty(
          "--etcher-panel-anchor-right", offset.right + "px");
      }
      // Anchored on the left instead: the panel hangs from the top-left,
      // under a chevron that has joined the viewer's nav there.
      if (typeof offset.left === "number") {
        this.handle.container.style.setProperty(
          "--etcher-panel-anchor-left", offset.left + "px");
        // Marked on the panel itself, never the container: the container is
        // the host's element, and a LiveView re-render of it (selecting a
        // shape is enough) strips attributes it did not render — the panel
        // jumped back to the right the moment a shape was clicked.
        this._panelSide = "left";
        if (this.stylePanel) this.stylePanel.setAttribute("data-side", "left");
      }
    },

    _syncStylePanel: function() {
      if (!this.stylePanel) return;
      // Adopt the params popup if it was built after the panel — unless the
      // arrange menu is currently borrowing it. This runs on a great many
      // events, and without the guard it would pull the controls out from
      // under the pointer the moment anything else on the board changed.
      var borrowed = !!(this.actionMenu && this.actionMenu.classList.contains("is-open") &&
        this.paramsPopup && this.actionMenu.contains(this.paramsPopup));
      if (!borrowed && this.paramsPopup && this.paramsPopup.parentNode !== this.stylePanel) {
        this.stylePanel.appendChild(this.paramsPopup);
      }
      // The panel is not gated on the tool in hand.
      //
      // It was: the grabber is declared `styleless`, and while one was armed
      // the panel went away, on the grounds that swatches beside a hand tool
      // read as "these apply to something". They do apply to something — the
      // next shape drawn — and the price of the rule was that a board, which
      // opens with the grabber armed, opened with no panel AND no control to
      // bring one back, because both the chevron and the trigger hung off
      // the same gate. It was reported twice, once per platform: "the whole
      // sidebar button next to the toolbar is gone completely and no more
      // sidebar" on a phone, and then "now desktop is missing the chevron or
      // the sidebar".
      //
      // `styleless` stays declared on the grabber — it says something true
      // about the tool, and a host reading TOOL_DEFS may want it — but
      // nothing here hides a panel over it any more.
      var wantsStyle = !!this.annotationMode;
      this.stylePanel.classList.toggle("is-active", wantsStyle);
      // Docked on a roomy container, a popup on a narrow one. Re-evaluated
      // on every sync so a rotation or a resized split view moves it without
      // the layer being rebuilt.
      var compact = this._isCompactLayout();

      // The chevron follows the panel — it is the panel's control, and
      // without a panel there is nothing to minimize.
      //
      // It also goes when the panel stops docking. On a narrow container the
      // panel is a popup opened from beside the tool bar, one press at a
      // time; how much of a docked panel to keep is not a question there,
      // and the chevron floating over the canvas cycles a setting that
      // nothing in a popup reads. Reported as "the chevron does nothing and
      // is just getting in the way".
      this._setPanelToggleActive(wantsStyle && !compact);
      if (compact) {
        this.stylePanel.setAttribute("data-compact", "");
      } else {
        this.stylePanel.removeAttribute("data-compact");
        // Leaving the popup's measured offsets behind would drag the docked
        // panel down to wherever the tool bar was, and the bar would stay
        // shifted off-centre with nothing beside it to justify the gap.
        this.stylePanel.style.left = "";
        this.stylePanel.style.right = "";
        this.stylePanel.style.top = "";
        this.stylePanel.style.bottom = "";
        if (this.toolbar) this.toolbar.style.transform = "";
        this._toggleStylePanel(false);
        this._positionDockedPanel();
      }

      if (this.styleTrigger) {
        // On a narrow container this button is the only door to the panel,
        // and a door is worth having even while the tool in hand takes no
        // style of its own: what the panel sets is what the NEXT tool draws
        // with. The DOCKED panel keeps the stricter rule — it sits open over
        // the canvas, and swatches beside a hand tool read as "these apply
        // to something".
        //
        // A board opens with the grabber armed, which is a styleless tool,
        // so that gate hid this button in the state every board starts in.
        // It went unnoticed while the chevron was still showing on compact
        // layouts; the moment that went, the report was "the whole sidebar
        // button next to the toolbar is gone completely and no more
        // sidebar".
        this.styleTrigger.classList.toggle("is-active", compact && wantsStyle);
        this._renderStyleTriggerIcon();
      }
      if (compact) this._positionStyleChrome();
    },

    // The origin the floating chrome is positioned against.
    //
    // Canvas mode: the surfaces are `absolute` inside the container, so
    // offsets are container-relative. Strip mode: the container IS the
    // scrolling element, so the chrome is `fixed` and its offsets are
    // viewport coordinates — subtracting the container's rect there would
    // be wrong by however far the strip sits from the viewport origin, and
    // right only by accident when it sits at 0,0.
    _chromeOrigin: function() {
      if (this.handleKind === "strip") return { left: 0, top: 0 };
      var c = this.handle.container.getBoundingClientRect();
      return { left: c.left, top: c.top };
    },

    // Distance from the positioning origin's RIGHT edge to the container's,
    // plus the margin — so a fixed surface pins to the strip's right edge
    // rather than the window's when the strip doesn't fill the screen.
    _chromeRightInset: function(c) {
      if (this.handleKind !== "strip") return CHROME_MARGIN;
      return window.innerWidth - c.right + CHROME_MARGIN;
    },

    // Height of whatever the chrome's `bottom` offsets are measured from —
    // the viewport when fixed, the container when absolute.
    _chromeHeight: function() {
      if (this.handleKind === "strip") return window.innerHeight;
      return this.handle.container.getBoundingClientRect().height;
    },

    // Parked just above the tool bar and flush with its left edge, the way
    // tldraw's sits. Recomputed rather than expressed in CSS because the
    // tool bar's width changes as tools overflow.
    _positionActionBar: function() {
      var self = this;
      if (!self.actionBar || !self.toolbar) return;
      var t = self.toolbar.getBoundingClientRect();
      if (!t.width) return;
      var o = self._chromeOrigin();
      self.actionBar.style.left = Math.round(t.left - o.left) + "px";
      // Flush, not floating: the two read as one control stacked in two
      // tiers, the way tldraw's do. The 4px gap made them look like two
      // things that happened to be near each other.
      self.actionBar.style.top =
        Math.round(t.top - o.top - self.actionBar.offsetHeight) + "px";
    },

    // Delete whatever is selected, matching the Backspace/Delete key path so
    // undo and server sync behave identically however it was triggered.
    _deleteSelection: function() {
      if (this.selectedShapes && this.selectedShapes.length) {
        this._deleteSelectedShapes();
        return true;
      }
      if (this.editingShape) {
        this._deleteShape(this.editingShape);
        return true;
      }
      return false;
    },

    // Copy the selection, nudged down-right so the copy is visibly its own
    // object rather than sitting exactly on the original.
    _duplicateSelection: function() {
      var self = this;
      var targets = self._arrangeTargets().filter(function(s) {
        return s && self.shapes.indexOf(s) !== -1;
      });
      if (!targets.length) return false;

      function clone(v) {
        if (v == null) return v;
        try { return JSON.parse(JSON.stringify(v)); } catch (_) { return v; }
      }

      var OFFSET = 16;
      var made = [];
      targets.forEach(function(s) {
        var payload = {
          kind: s.kind,
          geometry: self._translateGeometry(s.kind, clone(s.geometry), OFFSET, OFFSET)
        };
        if (s.style != null) payload.style = clone(s.style);
        if (s.metadata != null) payload.metadata = clone(s.metadata);
        if (typeof s.image_idx === "number") payload.image_idx = s.image_idx;
        if (typeof s.image_id === "string") payload.image_id = s.image_id;
        var uuid = self._addShape(payload);
        if (uuid) made.push(uuid);
      });
      if (!made.length) return false;

      // One history entry per copy. A bulk duplicate therefore takes as many
      // undos as it made copies — acceptable for the common single-shape
      // case, and better than the alternative of no history at all.
      self._clearSelection();
      self._exitEditMode();
      made.forEach(function(uuid) {
        var shape = self.shapes.find(function(x) { return x.uuid === uuid; });
        if (!shape) return;
        self._pushUndoCreate(shape);
        self._addToSelection(shape);
      });
      // A single copy goes straight into edit mode so it can be dragged
      // immediately, which is what duplicate is usually for.
      if (made.length === 1) {
        var only = self.shapes.find(function(x) { return x.uuid === made[0]; });
        if (only) self._enterEditMode(only);
      }
      self._syncActionBar();
      return true;
    },

    // Everything on the action bar except undo/redo (which
    // `_refreshUndoButtons` owns) is selection-scoped, so the enabled state
    // is computed in one place — including arrange, which otherwise started
    // life enabled because its own sync only ran on selection changes and
    // nothing selects anything at mount.
    _syncActionBar: function() {
      var has = !!(this.selectedShapes && this.selectedShapes.length) || !!this.editingShape;
      if (this.actionDeleteBtn) this.actionDeleteBtn.disabled = !has;
      if (this.actionDuplicateBtn) this.actionDuplicateBtn.disabled = !has;

      var canArrange = this._canArrange();
      (this.actionArrangeBtns || []).forEach(function(b) {
        b.disabled = !canArrange;
      });
      if (this.actionLayersBtn) {
        this.actionLayersBtn.disabled = !canArrange;
        if (!canArrange) this._closeLayersMenu();
      }
      // The `⋯` (hidden for now) holds whatever style controls the panel
      // isn't showing (see `_menuParamsShow`), and greys out when that is
      // nothing.
      if (this.actionMoreBtn) {
        this.actionMoreBtn.disabled = this._menuParamsShow() === "none";
        if (this.actionMoreBtn.disabled) this._closeActionMenu();
      }

      if (this.actionBar) {
        this.actionBar.classList.toggle("is-active", !!this.annotationMode);
        // The tool bar only squares off where something is actually resting
        // on it. On its own it keeps its full radius.
        if (this.toolbar) {
          this.toolbar.classList.toggle("is-stacked", !!this.annotationMode);
        }
      }
      // Whether a label is in the selection changes with the selection, and
      // this is the one sync already wired to every selection change.
      this._syncLabelSection();
      this._scheduleStyleInspectorSync();
    },

    // Coalesced inspector sync. _syncActionBar fires once per shape during a
    // marquee or a select-all, and _syncStyleInspector reads the live zoom
    // (_markerScale -> imageToScreen + getBoundingClientRect) — inline, that
    // is a forced layout per shape swept. The panel only has to show what the
    // burst SETTLES on, so fold the whole burst into one frame. Direct
    // callers (a swatch click, the picker) stay synchronous: those are single
    // events, and the panel should answer them immediately.
    _scheduleStyleInspectorSync: function() {
      var self = this;
      if (typeof requestAnimationFrame !== "function") {
        self._syncStyleInspector();
        return;
      }
      if (self._styleInspectorFrame) return;
      self._styleInspectorFrame = requestAnimationFrame(function() {
        self._styleInspectorFrame = null;
        self._syncStyleInspector();
      });
    },

    _computeToolbarOverflow: function() {
      var self = this;
      if (!self.toolbar) return;
      // Toolbar is `display: none` until annotation mode is on; bail
      // so scrollWidth/clientWidth measurements aren't taken against
      // a hidden element (which would return 0 and skew the logic).
      if (!self.toolbar.classList.contains("is-active")) return;

      var container = self.handle && self.handle.container;
      if (!container) return;

      // Reset every overflow-hidden tag from the previous pass.
      var toolBtns = Array.prototype.slice.call(
        self.toolbar.querySelectorAll("button[data-tool]")
      );
      var swatchBtns = Array.prototype.slice.call(
        self.toolbar.querySelectorAll(".etcher-swatch")
      );
      toolBtns.forEach(function(b) { b.classList.remove("etcher-overflow-hidden"); });
      swatchBtns.forEach(function(b) { b.classList.remove("etcher-overflow-hidden"); });
      // `[⋯]` is deliberately NOT reset. `_syncToolsPopup` owns it and turns
      // it back on straight after this runs — the non-essential tools live
      // only in that popup, so it is on in every real configuration. Hiding
      // it here made every measurement below one button short of the width
      // the bar actually renders at, which the desktop budget could absorb
      // and the compact one could not.

      if (self.colorsMoreBtn) self.colorsMoreBtn.classList.remove("is-active");
      if (self._cursorToolsDivider) {
        self._cursorToolsDivider.classList.remove("etcher-overflow-hidden");
      }
      if (self.undoBtn) self.undoBtn.classList.remove("etcher-overflow-hidden");
      if (self.redoBtn) self.redoBtn.classList.remove("etcher-overflow-hidden");

      // Available width: container minus a comfort margin so the
      // toolbar doesn't kiss the viewer edges — a 32px gutter each side
      // (collapses a tool into the `[⋯]` / palette menus well before the
      // bar reaches the page sides). Buttons keep their natural size;
      // overflow HIDES the extras rather than shrinking anything.
      var available = container.clientWidth - 64;
      // Compact layout: the bar and the style trigger are one centred group,
      // so the bar's budget is the container minus the trigger, minus a
      // gutter — 12px rather than the desktop 32, because on a phone that
      // margin is the difference between the trigger fitting on the row and
      // being bumped onto its own.
      if (self._isCompactLayout()) {
        available = container.clientWidth - CHROME_MARGIN * 2 -
          (CHROME_GAP + self._styleTriggerSize());
      }
      if (available <= 0) return;
      if (self._toolbarWidth() <= available) return;  // already fits

      // Undo / redo are treated as ONE unit: the moment the bar runs
      // short on room they collapse together (both at once) into the
      // tools `[⋯]` popup, before any individual tool/swatch is touched.
      // So they're inline only while the whole bar fits; any overflow
      // folds the pair first (often enough on its own, leaving every
      // tool visible).
      if (self._toolbarWidth() <= available) return;

      // Build the hideable queues — non-active items in right-to-left
      // order so the rightmost button collapses first.
      var activeToolKey = self.activeTool == null ? "cursor" : self.activeTool;
      var toolsQueue = [];
      for (var i = toolBtns.length - 1; i >= 0; i--) {
        if (toolBtns[i].dataset.tool !== activeToolKey) toolsQueue.push(toolBtns[i]);
      }
      var swatchQueue = [];
      // Pin the active slot by index (two slots can share a color, so a
      // color compare could pin the wrong/both swatches).
      for (var j = swatchBtns.length - 1; j >= 0; j--) {
        if (Number(swatchBtns[j].dataset.slot) !== self._activeSlot) {
          swatchQueue.push(swatchBtns[j]);
        }
      }

      // Hide one button at a time from whichever group currently
      // occupies MORE horizontal space, so tools and color swatches
      // converge to an even ~50/50 split instead of tools (which start
      // with far more buttons) keeping the lion's share. The pinned
      // active tool / swatch are excluded from the queues, so each group
      // always keeps at least its selected item.
      //
      // (The `[⋯]` triggers aren't toggled here: the tools trigger is
      // always shown via `_syncToolsPopup`, the colors trigger always
      // via CSS — both because each popup holds permanent content.)
      function visibleWidth(btns) {
        var w = 0;
        for (var k = 0; k < btns.length; k++) {
          if (!btns[k].classList.contains("etcher-overflow-hidden")) {
            w += btns[k].offsetWidth;
          }
        }
        return w;
      }

      var tIdx = 0, sIdx = 0;
      while (tIdx < toolsQueue.length || sIdx < swatchQueue.length) {
        var hideTool;
        if (tIdx >= toolsQueue.length) {
          hideTool = false;                       // tools exhausted
        } else if (sIdx >= swatchQueue.length) {
          hideTool = true;                        // swatches exhausted
        } else {
          hideTool = visibleWidth(toolBtns) >= visibleWidth(swatchBtns);
        }

        if (hideTool) {
          // First tool hidden: collapse the cursor/tools divider so a
          // lone visible tool doesn't sit beside a stray separator.
          if (tIdx === 0 && self._cursorToolsDivider) {
            self._cursorToolsDivider.classList.add("etcher-overflow-hidden");
          }
          toolsQueue[tIdx++].classList.add("etcher-overflow-hidden");
        } else {
          swatchQueue[sIdx++].classList.add("etcher-overflow-hidden");
        }
        if (self._toolbarWidth() <= available) return;
      }
    },

    // Keep the tools `[⋯]` popup in lockstep with the overflow state:
    // show only the popup buttons whose main-toolbar counterpart is
    // currently hidden (the active tool is pinned, so it never appears),
    // surface undo / redo only when the history group itself overflowed,
    // and light the `[⋯]` trigger iff the popup actually has something to
    // offer. Driven from `_layoutToolbar` after every overflow pass, so
    // a wider toolbar that fits everything hides the trigger outright.
    _syncToolsPopup: function() {
      var self = this;
      if (!self.toolsPopup || !self.toolbar) return;

      var anyToolHidden = false;
      (self.toolsPopupBtns || []).forEach(function(pb) {
        var main = self.toolbar.querySelector(
          'button[data-tool="' + pb.dataset.tool + '"]:not([data-pinned-tool])'
        );
        // Shown in the menu whenever it is not reachable on the bar: either
        // there is no button at all, or the user has not chosen it as an
        // essential (display cleared), or the bar ran short and collapsed it.
        // Every tool has a button now, so "no button" alone is no longer the
        // test for "lives in the menu".
        var hidden = !main ||
          main.style.display === "none" ||
          main.classList.contains("etcher-overflow-hidden");
        pb.style.display = hidden ? "" : "none";
        if (hidden) anyToolHidden = true;
      });

      // Undo / redo appear in the popup ONLY when their inline buttons
      // were collapsed off the toolbar (the pair folds into the menu when
      // the bar runs short) — otherwise they ride out on the toolbar
      // itself. The divider under them shows only when overflowed tools
      // ALSO sit below; the one under the settings only when anything does.
      var histHidden = !!(self.undoBtn &&
        self.undoBtn.classList.contains("etcher-overflow-hidden"));
      if (self.popupUndoBtn) self.popupUndoBtn.style.display = histHidden ? "" : "none";
      if (self.popupRedoBtn) self.popupRedoBtn.style.display = histHidden ? "" : "none";
      if (self._popupHistoryDivider) {
        self._popupHistoryDivider.style.display = (histHidden && anyToolHidden) ? "" : "none";
      }
      if (self._popupSettingsDivider) {
        self._popupSettingsDivider.style.display = (histHidden || anyToolHidden) ? "" : "none";
      }

      // `[⋯]` shows only when the popup has content — overflowed tools
      // and/or collapsed undo/redo.
      if (self.toolsMoreBtn) {
        self.toolsMoreBtn.classList.toggle("is-active", anyToolHidden || histHidden);
      }
      // The bar's width just changed, so the action bar above it has to
      // move with it.
      self._positionActionBar();
    },

    // Mirror the overflowed color slots into the colors popup (above the
    // preset row) so the user's customizable colors stay reachable when
    // they don't fit on the toolbar. Rebuilt from the current overflow
    // state each pass; the row + its divider hide when every slot is
    // inline. Clicking one selects that slot (it then pins back onto the
    // toolbar) and keeps the picker open so it can be tweaked.
    // The Behind chip's "No background" button, above the popup's own
    // colours (any of which turns the plate on), marked when there is none.
    // Shown only while the popup is aimed at the plate.
    _syncBgChoices: function() {
      var self = this;
      var row = this._bgChoicesRow;
      if (!row) return;
      row.style.display = this._labelBgPickTarget ? "" : "none";
      if (!this._labelBgPickTarget) return;
      while (row.firstChild) row.removeChild(row.firstChild);
      var current = this._currentLabelBg();
      var none = document.createElement("button");
      none.type = "button";
      none.className = "etcher-bg-none" + (current ? "" : " is-selected");
      none.title = "No background";
      none.setAttribute("aria-label", "No background");
      none.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">' +
        '<rect x="2" y="2" width="12" height="12" rx="3" fill="none" stroke="currentColor" stroke-width="1.3" stroke-dasharray="2 2"/>' +
        '<path d="M4 12 L12 4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>' +
        '<span>No background</span>';
      none.addEventListener("click", function(e) {
        e.preventDefault();
        self._setLabelBg(null, true);
        self._syncBgChoices();
      });
      row.appendChild(none);
    },

    _syncColorsPopup: function() {
      var self = this;
      var row = self.colorsPopupSlotsRow;
      if (!row) return;
      while (row.firstChild) row.removeChild(row.firstChild);

      var anyHidden = false;
      (self.swatchEls || []).forEach(function(toolbarSwatch, i) {
        if (!toolbarSwatch.classList.contains("etcher-overflow-hidden")) return;
        anyHidden = true;
        var color = toolbarSwatch.dataset.color;
        var b = document.createElement("button");
        b.type = "button";
        b.className = "etcher-swatch";
        b.dataset.color = color;
        b.dataset.slot = String(i);
        b.title = color;
        b.setAttribute("aria-label", "Color slot " + (i + 1) + ": " + color);
        b.style.background = color;
        b.addEventListener("click", function(e) {
          e.preventDefault();
          // Inspecting a shape: recolor the selection, leave the slot
          // selection and drawing default alone (same rule as the main
          // toolbar swatches).
          if (self._inspectedShape()) {
            self._applyColorToTargets(color);
            self._syncStyleInspector();
            return;
          }
          // Select the slot but keep the picker open so the user can
          // immediately tweak it on the wheel; re-aim the wheel at the
          // freshly-selected color.
          self._slotPickTarget = i;
          self._selectSlot(i);
          self._syncPickerToActiveColor();
        });
        row.appendChild(b);
      });

      row.style.display = anyHidden ? "" : "none";
      if (self.colorsPopupSlotsDivider) {
        self.colorsPopupSlotsDivider.style.display = anyHidden ? "" : "none";
      }
    },

    // Compact-mode `[⋯]` trigger. Calls into a kind-specific opener
    // (`_openToolsPopup` / `_openColorsPopup`); both pop above the
    // button anchored to the toolbar.
    _makeMoreButton: function(kind, title) {
      var self = this;
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "etcher-more";
      btn.dataset.more = kind;
      btn.title = title;
      btn.setAttribute("aria-label", title);
      // Params trigger wears sliders (line thickness / opacity / dash);
      // the tools trigger keeps the generic overflow dots.
      btn.innerHTML = kind === "params" ? ICONS.sliders : ICONS.more;
      btn.addEventListener("click", function(e) {
        e.preventDefault();
        e.stopPropagation();
        self._togglePopup(kind);
      });
      return btn;
    },

    // Tools popup: every drawing tool + cursor, rendered as a wrapping
    // grid above the `[⋯]` trigger. Reuses `_selectTool` so the
    // popup's selection flows through the same path as the main
    // toolbar (state stays in lockstep — the popup is just an
    // alternate UI).
    _buildToolsPopup: function() {
      var self = this;
      var popup = document.createElement("div");
      popup.className = "etcher-popup";
      popup.dataset.kind = "tools";
      popup.setAttribute("data-fresco-no-capture", "");
      // Cursor first so it's the easy reach when exiting draw mode.
      var entries = [{ key: "cursor", icon: ICONS.cursor, title: "Cursor" }];
      self.tools.forEach(function(toolKey) {
        var def = TOOL_DEFS[toolKey];
        if (!def) return;
        entries.push({ key: toolKey, icon: def.icon, title: def.title });
      });
      self.toolsPopupBtns = entries.map(function(entry) {
        var b = document.createElement("button");
        b.type = "button";
        b.dataset.tool = entry.key;
        b.title = entry.title;
        b.setAttribute("aria-label", entry.title);
        b.innerHTML = entry.icon;
        b.addEventListener("click", function(e) {
          e.preventDefault();
          var def = TOOL_DEFS[entry.key];
          if (def && def.momentary) {
            self._invokeMomentaryTool(entry.key);
            self._closePopup();
            return;
          }
          self._selectTool(entry.key === "cursor" ? null : entry.key);
          self._closePopup();
        });
        popup.appendChild(b);
        return b;
      });

      // Hairline + undo / redo. The popup is also the overflow target
      // for the history group when the toolbar narrows past the point
      // where even tools and swatches can't free enough space. Always
      // rendered (cheap) so the popup contents don't shift when the
      // user resizes the viewer.
      var historyDivider = document.createElement("div");
      historyDivider.className = "etcher-popup-divider";
      popup.appendChild(historyDivider);
      self._popupHistoryDivider = historyDivider;

      self.popupUndoBtn = document.createElement("button");
      self.popupUndoBtn.type = "button";
      self.popupUndoBtn.dataset.history = "undo";
      self.popupUndoBtn.title = "Undo (⌘Z)";
      self.popupUndoBtn.setAttribute("aria-label", "Undo");
      self.popupUndoBtn.innerHTML = ICONS.undo;
      self.popupUndoBtn.addEventListener("click", function(e) {
        e.preventDefault();
        self._undo();
        // Don't auto-close — users often undo multiple times in a row.
        self._refreshUndoButtons();
      });
      popup.appendChild(self.popupUndoBtn);

      self.popupRedoBtn = document.createElement("button");
      self.popupRedoBtn.type = "button";
      self.popupRedoBtn.dataset.history = "redo";
      self.popupRedoBtn.title = "Redo (⌘⇧Z)";
      self.popupRedoBtn.setAttribute("aria-label", "Redo");
      self.popupRedoBtn.innerHTML = ICONS.redo;
      self.popupRedoBtn.addEventListener("click", function(e) {
        e.preventDefault();
        self._redo();
        self._refreshUndoButtons();
      });
      popup.appendChild(self.popupRedoBtn);

      // Customise lives in this menu because this menu is where you end up
      // when the tool you want is not on the bar — which is the moment you
      // want to put it there.
      var customiseDivider = document.createElement("div");
      customiseDivider.className = "etcher-popup-divider";
      popup.appendChild(customiseDivider);
      self._popupSettingsDivider = customiseDivider;

      // The two view switches live here rather than in the nav column: they
      // are board chrome like the tools around them, and the nav column is
      // fresco's — zoom, rotate, home. Mixing "what is on the board" into
      // "where am I looking" made both lists harder to read.
      self.gridBtn = self._makePopupAction(
        ICONS.grid, self._gridTitle(),
        function() { self._setPref("grid", self._getPref("grid") === false); }
      );
      popup.appendChild(self.gridBtn);

      // (The connector-anchors and label-dots toggles that sat here are
      // gone: the arrow tool connects shapes itself, and every label, text
      // and callout shows its dots.)

      self.zoomAnchorBtn = self._makePopupAction(
        ICONS.inkZoom, self._zoomAnchorTitle(),
        function() { self._setPref("zoom_anchor", !self._zoomAnchorOn()); }
      );
      popup.appendChild(self.zoomAnchorBtn);

      self.snapBtn = self._makePopupAction(
        ICONS.snap, self._snapTitle(),
        function() { self._setPref("snap", !self._snapOn()); }
      );
      popup.appendChild(self.snapBtn);

      var customiseBtn = self._makePopupAction(
        ICONS.sliders, "Customise the toolbar and the style panel",
        function() { self._closePopup(); self._openCustomise(); }
      );
      popup.appendChild(customiseBtn);

      // Top to bottom: the settings, then undo / redo, then the tools. The
      // menu opens upward out of the bar, so the tools — what it is opened
      // for nearly every time — sit nearest the button, and the switches
      // that are set once and left sit at the far end instead of in the way.
      [self.gridBtn, self.zoomAnchorBtn, self.snapBtn, customiseBtn, customiseDivider,
       self.popupUndoBtn, self.popupRedoBtn, historyDivider]
        .concat(self.toolsPopupBtns)
        .forEach(function(node) { popup.appendChild(node); });

      self.handle.container.appendChild(popup);
      self.toolsPopup = popup;
    },

    // Show the arrange group only when there's something to arrange, and
    // Kept as the name the selection paths already call; the state itself
    // lives in `_syncActionBar` so there is one source of truth. Buttons are
    // disabled rather than hidden, so the bar keeps a stable shape instead
    // of shuffling sideways as the selection comes and goes.
    _syncArrangeButtons: function() {
      this._syncActionBar();
    },

    // Colors popup: preset swatches + recent-customs row + a
    // hue-ring / lightness-slider picker. Selecting anything routes
    // through `_selectColor`, which (for non-preset colors) also
    // pushes the pick onto the recents stack so the user can
    // re-grab it from a single swatch on subsequent sessions.
    _buildColorsPopup: function() {
      var self = this;
      var popup = document.createElement("div");
      popup.className = "etcher-popup";
      popup.dataset.kind = "colors";
      popup.setAttribute("data-fresco-no-capture", "");

      // Custom picker: preset swatches + hue ring + lightness slider.
      // No separate \"Recent\" row inside the popup — the toolbar's
      // inline swatches are now the recents display, so duplicating
      // them in the popup would just be redundant chrome. The static
      // preset row stays inside the picker as a quick way back to
      // default colors even after the user's palette has drifted.
      // Opened from the Behind chip, the popup leads with the plate's own
      // "none" choice ahead of the colours — the background switch folded
      // into the chip (see `_syncBgChoices`).
      var bgChoices = document.createElement("div");
      bgChoices.className = "etcher-bg-choices";
      bgChoices.style.display = "none";
      popup.appendChild(bgChoices);
      self._bgChoicesRow = bgChoices;

      var picker = document.createElement("div");
      picker.className = "etcher-picker";

      // Overflowed color slots — the user's customizable swatches that
      // don't fit on the toolbar appear here, above the permanent preset
      // row, so they stay reachable. Populated by `_syncColorsPopup`;
      // this row and its divider hide when every slot fits inline.
      var slotsRow = document.createElement("div");
      slotsRow.className = "etcher-presets etcher-popup-slots";
      slotsRow.style.display = "none";
      picker.appendChild(slotsRow);
      self.colorsPopupSlotsRow = slotsRow;

      var slotsDivider = document.createElement("div");
      slotsDivider.className = "etcher-popup-divider";
      slotsDivider.style.display = "none";
      picker.appendChild(slotsDivider);
      self.colorsPopupSlotsDivider = slotsDivider;

      var presetColors = resolveColorSwatches().map(function(s) { return s.color; });
      self._presetColors = presetColors;
      var presetRow = document.createElement("div");
      presetRow.className = "etcher-presets";
      self.colorsPopupBtns = resolveColorSwatches().map(function(s) {
        var b = document.createElement("button");
        b.type = "button";
        b.className = "etcher-swatch";
        b.dataset.color = s.color;
        b.title = s.title;
        b.setAttribute("aria-label", "Color: " + s.title);
        b.style.background = s.color;
        if (s.color === self.activeColor) b.classList.add("is-selected");
        b.addEventListener("click", function(e) {
          e.preventDefault();
          // Presets are a quick way to set the selected slot to a known
          // color — overwrite the active slot in place (same as a hue-
          // ring pick) and persist, rather than spawning a new entry.
          // Keep the picker open (it only closes on an outside click) so
          // the user can keep adjusting.
          var editsPalette = self._pickEditsPalette();
          self._applyPickedColor(s.color);
          if (editsPalette) self._emitColorsChanged();
        });
        presetRow.appendChild(b);
        return b;
      });
      picker.appendChild(presetRow);

      // Hue ring — a torus-shaped canvas painted once. Clicking /
      // dragging on it sets `_pickerHue`. Default size keeps the
      // popup compact while still leaving a comfortable thumb-tap
      // target on phones.
      var ringSize = 132;
      var ringWrap = document.createElement("div");
      ringWrap.className = "etcher-picker-ring-wrap";
      ringWrap.style.width = ringSize + "px";
      ringWrap.style.height = ringSize + "px";

      var ring = document.createElement("canvas");
      ring.className = "etcher-picker-ring";
      ring.width = ringSize;
      ring.height = ringSize;
      ringWrap.appendChild(ring);

      var ringKnob = document.createElement("div");
      ringKnob.className = "etcher-picker-ring-knob";
      ringWrap.appendChild(ringKnob);

      picker.appendChild(ringWrap);

      // Lightness slider + preview, on a single row below the ring.
      var sliderRow = document.createElement("div");
      sliderRow.className = "etcher-picker-slider-row";

      var sliderWrap = document.createElement("div");
      sliderWrap.className = "etcher-picker-slider-wrap";
      var slider = document.createElement("canvas");
      slider.className = "etcher-picker-slider";
      slider.width = 120;
      slider.height = 14;
      sliderWrap.appendChild(slider);

      var sliderKnob = document.createElement("div");
      sliderKnob.className = "etcher-picker-slider-knob";
      sliderWrap.appendChild(sliderKnob);

      var preview = document.createElement("div");
      preview.className = "etcher-picker-preview";

      sliderRow.appendChild(sliderWrap);
      sliderRow.appendChild(preview);
      picker.appendChild(sliderRow);
      popup.appendChild(picker);

      // Cache for the layout + redraw routines.
      self._pickerRing = ring;
      self._pickerRingWrap = ringWrap;
      self._pickerRingKnob = ringKnob;
      self._pickerRingSize = ringSize;
      self._pickerRingOuter = ringSize / 2 - 2;
      self._pickerRingInner = ringSize / 2 - 18;
      self._pickerSlider = slider;
      self._pickerSliderKnob = sliderKnob;
      self._pickerPreview = preview;

      // Seed the picker from the active color when it's a non-preset
      // hex (so re-opening the popup after a custom pick puts the
      // knobs where the user left them). Otherwise default to a
      // neutral starting position.
      var seed = self.activeColor &&
                 self._presetColors.indexOf(self.activeColor) === -1
        ? hexToHsl(self.activeColor)
        : null;
      self._pickerHue = seed ? seed.h : 200;
      self._pickerLightness = seed ? seed.l : 50;

      self._drawHueRing();
      self._drawLightnessSlider();
      self._positionPickerKnobs();
      self._updatePickerPreview();
      self._wirePickerInput();

      self.handle.container.appendChild(popup);
      self.colorsPopup = popup;
    },

    // Marker style popup: weight + opacity sliders and a dash picker. Opened
    // by re-clicking the active marker tool button. Edits `markerStyle` — the
    // default applied to new strokes (color stays on the shared palette).
    _buildMarkerPopup: function() {
      var self = this;
      var popup = document.createElement("div");
      popup.className = "etcher-popup";
      popup.dataset.kind = "marker";
      popup.setAttribute("data-fresco-no-capture", "");

      function sliderRow(labelText) {
        var row = document.createElement("div");
        row.className = "etcher-marker-row";
        var head = document.createElement("div");
        head.className = "etcher-marker-row-head";
        var label = document.createElement("span");
        label.textContent = labelText;
        var val = document.createElement("span");
        head.appendChild(label);
        head.appendChild(val);
        var input = document.createElement("input");
        input.type = "range";
        row.appendChild(head);
        row.appendChild(input);
        popup.appendChild(row);
        return { input: input, val: val };
      }

      // Weight (stroke width). Shown as a bare number on purpose: the
      // value is a weight relative to the canvas, not a count of pixels
      // (see _canvasInkScale), and a "px" suffix promised a pixel measure
      // that never held across resolutions.
      var w = sliderRow("Weight");
      w.input.min = "0"; w.input.max = "100"; w.input.step = "1";
      self._markerWeightInput = w.input;
      self._markerWeightVal = w.val;
      w.input.addEventListener("input", function() {
        var mw = self._weightFromSlider(w.input.value);
        w.val.textContent = String(mw);
        self._setMarkerStyleProp("width", mw, false);
      });
      w.input.addEventListener("change", function() {
        self._setMarkerStyleProp("width", self._weightFromSlider(w.input.value), true);
      });
      self._makeWeightTypeable(w.val, w.input, function(n) {
        self._setMarkerStyleProp("width", n, true);
        self._showValuePreview("width", n, w.input, self._previewStrokeStyle(true));
      });
      self._wireValuePreview(w.input, function() {
        self._showValuePreview("width", self._weightFromSlider(w.input.value), w.input, self._previewStrokeStyle(true));
      });

      // Opacity (0–100% → 0.0–1.0).
      var o = sliderRow("Opacity");
      o.input.min = "10"; o.input.max = "100"; o.input.step = "5";
      self._markerOpacityInput = o.input;
      self._markerOpacityVal = o.val;
      o.input.addEventListener("input", function() {
        o.val.textContent = o.input.value + "%";
        self._setMarkerStyleProp("opacity", parseInt(o.input.value, 10) / 100, false);
      });
      o.input.addEventListener("change", function() {
        self._setMarkerStyleProp("opacity", parseInt(o.input.value, 10) / 100, true);
      });

      // Dash style.
      var dashRow = document.createElement("div");
      dashRow.className = "etcher-marker-dash";
      function dashIcon(dash) {
        var da = dash === "dashed" ? ' stroke-dasharray="7 5"'
               : dash === "dotted" ? ' stroke-dasharray="0.01 6"' : '';
        return '<svg width="40" height="8" viewBox="0 0 40 8"><line x1="3" y1="4" x2="37" y2="4" ' +
               'stroke="#fff" stroke-width="3" stroke-linecap="round"' + da + '/></svg>';
      }
      self._markerDashBtns = ["solid", "dashed", "dotted"].map(function(dash) {
        var b = document.createElement("button");
        b.type = "button";
        b.dataset.dash = dash;
        b.title = dash;
        b.setAttribute("aria-label", "Dash: " + dash);
        b.innerHTML = dashIcon(dash);
        b.addEventListener("click", function(e) {
          e.preventDefault();
          self._setMarkerStyleProp("dash", dash, true);
          self._syncMarkerPopup();
        });
        dashRow.appendChild(b);
        return b;
      });
      popup.appendChild(dashRow);

      self.handle.container.appendChild(popup);
      self.markerPopup = popup;
    },

    // Reflect the current marker style (the editing marker's if one is
    // selected, else the tool default) onto the popup controls.
    _syncMarkerPopup: function() {
      var src = (this.editingShape && this.editingShape.kind === "marker" &&
                 this.editingShape.style)
        ? this.editingShape.style
        : this._currentMarkerStyle();
      // `src.width` is stored in document units; the slider shows the
      // panel's own weight, so convert back through _inkScale — the
      // inverse of what `_setMarkerStyleProp` stores.
      var width = Math.max(1, Math.round((src.width || 10) * this._inkScale()));
      var opacity = src.opacity == null ? 1 : src.opacity;
      var dash = src.dash || "solid";
      if (this._markerWeightInput) {
        this._markerWeightInput.value = this._sliderFromWeight(width);
        this._markerWeightVal.textContent = String(width);
      }
      if (this._markerOpacityInput) {
        var pct = Math.round(opacity * 100);
        this._markerOpacityInput.value = pct;
        this._markerOpacityVal.textContent = pct + "%";
      }
      (this._markerDashBtns || []).forEach(function(b) {
        b.classList.toggle("is-selected", b.dataset.dash === dash);
      });
    },

    // Set one marker style property. When a marker stroke is selected, edits
    // that stroke (applied live; committed with emit + undo on slider release
    // / dash click). Otherwise edits `markerStyle` — the default for new
    // strokes — so styling an existing stroke doesn't move the tool default.
    _setMarkerStyleProp: function(prop, value, commit) {
      var shape = this.editingShape;
      var onMarker = shape && shape.kind === "marker";
      if (onMarker) {
        if (!this._markerStyleBefore) this._markerStyleBefore = this._snapshotShape(shape);
        shape.style = Object.assign({}, shape.style || {});
        // The weight slider is on-screen px; store it as image px so the
        // stroke scales with zoom. Opacity/dash are zoom-independent.
        shape.style[prop] = prop === "width" ? value / this._inkScale() : value;
        this._renderShape(shape);
      } else {
        this.markerStyle = this.markerStyle || {};
        this.markerStyle[prop] = value;
      }
      if (commit) {
        if (onMarker && shape.uuid) {
          this._emitChanged();
          if (this._markerStyleBefore) {
            this._pushUndo(shape.uuid, this._markerStyleBefore, this._snapshotShape(shape));
          }
        }
        this._markerStyleBefore = null;
      }
    },

    // ----- Line parameters (global drawing style) ----------------------------
    // Thickness / opacity / dash applied to every NEW non-marker stroke shape
    // (rectangle, circle, polygon, freehand). The marker keeps its own style.

    _isStrokeShape: function(kind) {
      return kind === "rectangle" || kind === "circle" ||
             kind === "polygon" || kind === "freehand";
    },

    // The line params a new stroke shape adopts: the active color plus the
    // current global thickness / opacity / dash (on-screen px, like the slot
    // shapes have always used).
    // A width saved before widths were in canvas units is an on-screen px
    // value authored at a zoom nobody recorded. Anchoring it to the zoom it
    // is first painted at makes it look exactly as it did, and scale from
    // there like everything else — the alternative, reading a stored `2` as
    // 2 canvas px, renders it at a fifth of a pixel on a zoomed-out board.
    //
    // Marked on the style so it happens once, and rides out to peers on the
    // next edit rather than being redone by every client forever.
    _anchorLegacyWidth: function(shape) {
      var s = shape && shape.style;
      if (!s || s.width_units === "canvas") return;
      var scale = 0;
      try { scale = this._markerScale() || 0; } catch (_) {}
      if (!(scale > 0)) return;
      shape.style = Object.assign({}, s, {
        width: (s.width || 2) / scale,
        width_units: "canvas"
      });
    },

    // What a newly drawn stroke shape stores. The slider is in on-screen px;
    // the shape keeps canvas units so the line holds its weight relative to
    // the drawing — the same conversion `_currentMarkerStyle` does, for the
    // same reason.
    // What a brand-new shape of `kind` is styled with. The DRAFT is drawn
    // with this too, which is the whole point of it being one function:
    // what you see while dragging is what you get on release — same
    // thickness, same dash, same opacity, same fill — instead of the draft
    // having a look of its own that the committed shape then replaced.
    //
    // Markers persist their full appearance; stroke shapes (rect / circle /
    // polygon / freehand) adopt the global line params (color + thickness /
    // opacity / dash); every other kind just carries its color.
    _styleForNewShape: function(kind) {
      var style;
      if (kind === "marker") {
        // Stamped with the tool that drew it: the two are otherwise the
        // same shape, and "which palette does this stroke belong to" has
        // to survive a reload (see `_inkToolFor`).
        style = Object.assign({}, this._currentMarkerStyle(),
                              { ink: this.activeTool === "highlighter"
                                       ? "highlighter" : "marker" });
        // The highlighter draws MARKER shapes — same ink, same width, same
        // committed kind (so persistence, hit test and the spline all come
        // free) — at a fixed half opacity. A separate tool rather than a
        // setting, so writing and highlighting never fight over one slider.
        if (this.activeTool === "highlighter") {
          style = Object.assign({}, style, { opacity: HIGHLIGHT_OPACITY });
        }
      } else if (this._isStrokeShape(kind)) {
        style = this._lineParamsForNewShape();
      } else if (this._isShaftKind(kind) || kind === "callout") {
        // Shafts adopt the global stroke params like every other line the
        // user draws — set the thickness once, and the next line, arrow and
        // dimension all come out in it. No fill: there is nothing to fill
        // on an open shaft, and a dead key would still ride every payload.
        // A callout's leader is a line in exactly this sense.
        style = this._lineParamsForNewShape();
        delete style.fill;
      } else if (kind === "text") {
        // A text shape IS a label. It starts in the remembered label
        // colour - the panel's label swatch (or recolouring a focused
        // label) sets it - falling back to the stroke colour. This is
        // the creation-time counterpart of _commitTextEdit's title_color
        // stamp, which deliberately excludes text kinds because their
        // text takes the shape's own colour.
        var textColor = this._getPref("label_color") || this.activeColor;
        var textStyle = textColor ? { color: textColor } : {};
        var lp = this._currentLineParams();
        if (lp.font_size) {
          textStyle.font_size = lp.font_size / this._inkScale();
        }
        style = Object.keys(textStyle).length ? textStyle : null;
      } else {
        style = this.activeColor ? { color: this.activeColor } : null;
      }

      // The label plate rides EVERY kind, because any shape can carry a
      // label — so it is folded in here, once, rather than repeated in each
      // branch above. Kept to one function on purpose: this is what a new
      // shape is given, and splitting it in two put half the answer
      // somewhere a caller could reach without the other half.
      var bg = this._getPref("label_bg");
      if (typeof bg === "string" && bg) {
        style = Object.assign({}, style || {}, { label_bg: bg });
      }
      return style;
    },


    // The size a shape's text is drawn at, in container px.
    //
    // Two ways to set it, and this is where they meet. Drag the label's box
    // and the size follows the box — the original behaviour, still the way
    // to eyeball one. Type or slide a number in the panel and it is pinned,
    // so every label can be given the SAME size instead of each ending up
    // wherever its box happened to land.
    //
    // Stored in canvas units like a stroke width, so a pinned size zooms
    // with the board rather than staying a fixed number of screen pixels
    // and swamping the drawing when you zoom out.
    _fontSizeFor: function(shape, derivedPx) {
      if (!this._hasPinnedFontSize(shape)) return derivedPx;
      var scale = 1;
      try { scale = this._markerScale() || 1; } catch (_) { scale = 1; }
      return Math.max(1, shape.style.font_size * scale);
    },

    // The label size new labels start at: the user's chosen default when
    // one is set (the panel's global Label size), else the built-in.
    _defaultLabelFontSize: function() {
      var lp = this.lineParams || {};
      if (typeof lp.font_size === "number" && isFinite(lp.font_size) &&
          lp.font_size > 0) {
        return lp.font_size;
      }
      return DEFAULT_LABEL_FONT_SIZE;
    },

    // What the EDITOR types at, in screen px. The shape keeps its own size:
    // this is only the box being typed into, and nothing here is stored.
    //
    // Floored on a touch screen at the size under which mobile Safari zooms
    // the PAGE to help you type — focus an input below 16px and the whole
    // document scales up, toolbar and all. On a canvas that is a trap: the
    // pinch that would undo it belongs to the board, so the page stays
    // zoomed and there is no way back short of a reload. Reported as "every
    // single time I start creating text, it zooms me in… eventually I'm
    // zoomed into the actual canvas and I can no longer zoom out".
    //
    // Only where the pointer is coarse. A desktop browser does no such
    // thing, and there the editor should look exactly like the result.
    _editorFontPx: function(px) {
      if (!(px > 0) || px >= INPUT_PAGE_ZOOM_PX) return px;
      var coarse = false;
      try {
        coarse = !!(window.matchMedia &&
                    window.matchMedia("(pointer: coarse)").matches);
      } catch (_) {}
      return coarse ? INPUT_PAGE_ZOOM_PX : px;
    },

    _hasPinnedFontSize: function(shape) {
      var fs = shape && shape.style && shape.style.font_size;
      return typeof fs === "number" && isFinite(fs) && fs > 0;
    },

    // The plate a label sits on, so it can be read over a busy photograph.
    //
    // Absent means no plate — the label floats on whatever is behind it,
    // which is what every label did before this and is still right over
    // plain or empty canvas. Black and white are what the panel offers
    // first because they are what contrast needs 95% of the time, but it
    // is an ordinary colour: the picker is right there.
    _labelBgFor: function(shape) {
      var bg = shape && shape.style && shape.style.label_bg;
      return typeof bg === "string" && bg ? bg : null;
    },

    // Paint it onto the rect every label kind already has behind its text.
    // Inline, because the stylesheet pins that rect to `fill: transparent`
    // — and back to "" rather than "transparent" when there is no plate, so
    // the rule goes back to owning it.
    _applyLabelBg: function(rectEl, shape) {
      if (!rectEl || !rectEl.style) return;
      var bg = this._labelBgFor(shape);
      rectEl.style.fill = bg || "";

      // Rounded corners, as a fraction of the plate's own height rather
      // than a fixed number of pixels: a constant radius is a different
      // look at every size, barely visible on a big label and a lozenge on
      // a small one. Proportional keeps the same shape at any size and in
      // either coordinate space (this rect is container px on a canvas and
      // image px in a strip).
      //
      // Only while there IS a plate. Without one this rect is the invisible
      // hit box, and the dashed outline it shows on hover is a different
      // thing that nobody asked to reshape.
      if (!bg) {
        rectEl.removeAttribute("rx");
        return;
      }
      var h = parseFloat(rectEl.getAttribute("height"));
      if (!isFinite(h) || h <= 0) {
        rectEl.removeAttribute("rx");
        return;
      }
      rectEl.setAttribute("rx", h * LABEL_BG_RADIUS_RATIO);
    },

    // Set the label plate on the selected text-bearing shapes, or — with
    // nothing selected — the colour new labels start with. `null` removes
    // it. Same targeting as the size control beside it, so the two behave
    // alike: what you can size, you can plate.
    _setLabelBg: function(color, commit) {
      var self = this;
      var shapes = this._fontTargetShapes();
      if (shapes.length) {
        if (!this._labelBgBefore) {
          this._labelBgBefore = shapes.map(function(s) {
            return { uuid: s.uuid, before: self._snapshotShape(s) };
          });
        }
        shapes.forEach(function(shape) {
          shape.style = Object.assign({}, shape.style || {});
          if (color == null) delete shape.style.label_bg;
          else shape.style.label_bg = color;
          self._renderShape(shape);
        });
        // Fresh shape: the plate choice carries to the next label too.
        if (this._freshTargets(shapes)) {
          if (color) this._setPref("label_bg_last", color);
          this._setPref("label_bg", color == null ? null : color);
        }
      } else {
        // Remembered even while off, so the toggle has something to turn
        // back ON — otherwise switching it off would lose the colour and
        // switching it back on would guess.
        if (color) this._setPref("label_bg_last", color);
        this._setPref("label_bg", color == null ? null : color);
        this._restyleDrafts();
      }
      this._refreshTextEditorStyle();
      this._syncLabelBgRow();
      this._refreshLabelSwatch();
      if (!commit) return;
      if (this._labelBgBefore && this._labelBgBefore.length) {
        this._emitChanged();
        this._labelBgBefore.forEach(function(rec) {
          if (!rec.uuid) return;
          var shape = self.shapes.find(function(s) { return s.uuid === rec.uuid; });
          if (shape) self._pushUndo(rec.uuid, rec.before, self._snapshotShape(shape));
        });
      }
      this._labelBgBefore = null;
    },

    // The write half of the label-colour chip — the twin of `_setLabelBg`,
    // and shaped exactly like it: with label-bearing shapes selected the
    // colour lands on THEM (their own ink, one undo entry each, authoring
    // defaults untouched); with nothing selected it sets the default the
    // next label starts in. It used to always do only the second, which
    // made the row a liar: the chip said "this label" while the pick
    // silently edited the default — the background half updated the
    // selection, the text half did nothing you could see.
    //
    // Where the colour lives mirrors `_currentLabelColor`'s read: a text
    // or callout IS its label, so theirs is the shape's own colour; every
    // other kind carries label ink in metadata.title_color.
    _setLabelColor: function(color) {
      var self = this;
      var shapes = this._fontTargetShapes();
      if (shapes.length) {
        shapes.forEach(function(shape) {
          if (!shape.uuid) return;
          var before = self._snapshotShape(shape);
          if (shape.kind === "text" || shape.kind === "callout") {
            shape.style = Object.assign({}, shape.style || {}, { color: color });
            // The element too, not just the style: text and callout draw
            // in currentColor off their own <g>, and their render never
            // re-reads style.color — so writing the style alone changed
            // nothing visible. The WYSIWYG editor DOES read the style,
            // which is why the preview looked right and the committed
            // text popped back to the old ink on Enter (the plate chip
            // worked all along because _applyLabelBg reads style at
            // render). Same explicit call _applyColorToTargets makes.
            self._applyShapeColor(shape.el, color, shape.style);
          } else {
            shape.metadata = Object.assign({}, shape.metadata || {}, {
              title_color: color
            });
          }
          self._renderShape(shape);
          self._pushUndo(shape.uuid, before, self._snapshotShape(shape));
        });
        this._emitChanged();
        this._syncStyleInspector();
        // Fresh shape: this ink is what the next label starts in.
        if (this._freshTargets(shapes)) this._setPref("label_color", color);
      } else {
        this._setPref("label_color", color);
        this._restyleDrafts();
      }
      this._refreshTextEditorStyle();
      this._refreshLabelSwatch();
      // A text box's handles wear its text's colour; a new ink is a new
      // colour for them too, not the one it was drawn in.
      this._repaintHandles();
    },

    // Bring the handles up on screen into the colour they should be now:
    // corner dots and side grabbers of the shape in edit mode, and a
    // focused label's dots. Point frames keep their black.
    _repaintHandles: function() {
      if (this.editingShape) {
        var c = this._handleColor(this.editingShape);
        (this.handles || []).forEach(function(h) { h.style.color = c; });
        if (!this._isPointFrame(this.editingShape)) {
          (this.midpointHandles || []).forEach(function(h) { h.style.color = c; });
        }
      }
      if (this.editingTitleShape) {
        var tc = this._isPointLabel(this.editingTitleShape)
          ? POINT_HANDLE_COLOR : this._handleColor(this.editingTitleShape);
        (this.titleHandles || []).forEach(function(h) { h.style.color = tc; });
      }
    },

    // The colour the current label is WRITTEN in — the twin of
    // `_currentLabelBg`, so both halves of the swatch row answer about the
    // same thing: the label you are on, or the default when you are on
    // none. A label carries its own colour in metadata; a text shape and a
    // callout ARE the label, so theirs is the shape's colour.
    _currentLabelColor: function(shape) {
      var target = shape;
      if (!target) {
        var targets = this._fontTargetShapes();
        target = targets.length ? targets[0] : null;
      }
      if (target) {
        var own = target.metadata && target.metadata.title_color;
        if (own) return own;
        if (target.kind === "text" || target.kind === "callout") {
          return (target.style && target.style.color) || null;
        }
      }
      var pref = this._getPref("label_color");
      return (typeof pref === "string" && pref) ? pref : null;
    },

    // What the toggle reads and writes: the plate on the current targets,
    // or the default when nothing is selected.
    _currentLabelBg: function() {
      var targets = this._fontTargetShapes();
      if (targets.length) return this._labelBgFor(targets[0]);
      var pref = this._getPref("label_bg");
      return typeof pref === "string" && pref ? pref : null;
    },

    _toggleLabelBg: function() {
      if (this._currentLabelBg()) {
        this._setLabelBg(null, true);
        return;
      }
      // Back to the last colour used, black the first time — the colour a
      // label needs behind it more often than not.
      var last = this._getPref("label_bg_last");
      this._setLabelBg(
        (typeof last === "string" && last) ? last : "#000000", true
      );
    },

    // The shapes a font size applies to: the ones that draw text. Distinct
    // from `_paramsTargetShapes` — a rectangle with a label takes a font
    // size and a text shape takes no stroke width, so neither set contains
    // the other.
    _fontTargetShapes: function() {
      function draws(s) {
        if (!s) return false;
        if (s.kind === "text" || s.kind === "callout") return true;
        return !!(s.metadata && String(s.metadata.title || "").trim() !== "");
      }
      // An OPEN text editor is the most explicit focus there is — the
      // user placed a box and is styling what they are about to type.
      // Without this, the panel's size/colour controls edited the global
      // default while the box on screen kept its old dress. No draws()
      // requirement here: a shape whose FIRST label is being typed has no
      // title yet, and requiring one hid every label control during
      // exactly the moment the user most wants them — they had to type,
      // commit, and re-select before the panel would talk about the label.
      if (this._textEditor && this._textEditor.shape) {
        return [this._textEditor.shape];
      }
      if (this.selectedShapes && this.selectedShapes.length) {
        return this.selectedShapes.filter(draws);
      }
      if (draws(this.editingShape)) return [this.editingShape];
      if (draws(this.editingTitleShape)) return [this.editingTitleShape];
      return [];
    },

    // Pin a font size on the current targets, or `null` to hand the size
    // back to the box. With nothing selected it sets the default new labels
    // start at, the same way the thickness slider does.
    _setFontSize: function(value, commit) {
      var self = this;
      var shapes = this._fontTargetShapes();
      if (shapes.length) {
        if (!this._fontSizeBefore) {
          this._fontSizeBefore = shapes.map(function(s) {
            return { uuid: s.uuid, before: self._snapshotShape(s) };
          });
        }
        var scale = self._inkScale();
        shapes.forEach(function(shape) {
          shape.style = Object.assign({}, shape.style || {});
          if (value == null) delete shape.style.font_size;
          else shape.style.font_size = value / scale;
          // A turned text keeps its starting corner as it grows or shrinks.
          self._renderKeepingCorner(shape);
          // The resize handles sit on the label's corners, and the label
          // just changed size under them — so they have to be re-placed,
          // the same as after an align or a drag. Without this they hang in
          // the air where the label used to end, until something unrelated
          // re-renders them.
          if (self.editingTitleShape === shape) self._positionAllTitleHandles(shape);
          if (self.editingShape === shape) self._positionAllHandles(shape);
        });
      } else {
        this.lineParams = this.lineParams || {};
        // The default is in screen px, like the thickness slider's: it is
        // converted to canvas units when a shape is actually made.
        if (value == null) delete this.lineParams.font_size;
        else this.lineParams.font_size = value;
        this._restyleDrafts();
      }
      // Fresh shape: the size just chosen is what the NEXT label starts
      // at too (screen px, like the global branch stores).
      if (shapes.length && this._freshTargets(shapes)) {
        this.lineParams = this.lineParams || {};
        if (value == null) delete this.lineParams.font_size;
        else this.lineParams.font_size = value;
        this._fontFreshMirror = true;
      }
      // A panel edit while the inline editor is open dresses the editor
      // too — the user is styling the text they are typing.
      this._refreshTextEditorStyle();
      if (!commit) return;
      if (this._fontSizeBefore && this._fontSizeBefore.length) {
        this._emitChanged();
        this._fontSizeBefore.forEach(function(rec) {
          if (!rec.uuid) return;
          var shape = self.shapes.find(function(s) { return s.uuid === rec.uuid; });
          if (shape) self._pushUndo(rec.uuid, rec.before, self._snapshotShape(shape));
        });
        if (this._fontFreshMirror) this._emitLineParamsChanged();
        this._fontFreshMirror = false;
      } else {
        this._emitLineParamsChanged();
      }
      this._fontSizeBefore = null;
    },

    // Dragging a label's box is the OTHER way to set its size, so it has to
    // win: a pinned size would otherwise ignore the drag and the box would
    // resize around text that refused to change, which reads as the drag
    // being broken.
    _unpinFontSize: function(shape) {
      if (!this._hasPinnedFontSize(shape)) return;
      shape.style = Object.assign({}, shape.style || {});
      delete shape.style.font_size;
      // Say so in the panel on the very first frame of the drag. The number
      // in the box is no longer what the label is: leaving it there reads as
      // "still 18px" while the user watches it grow past 18, and the point
      // of the box is that you can trust what it says.
      this._syncFontRow();
    },

    // Re-derive a live draft's style from the current params and repaint it.
    //
    // A draft takes its style once, when it is created. That was fine while
    // a draft only existed for the length of a drag, but a click now ARMS
    // one — it sits there following the cursor until the second click — and
    // during that time the obvious thing to do is go and set the thickness
    // or the line type you want before committing to it. Nothing happened
    // until the placing click, at which point the shape appeared in the
    // style you had chosen a moment ago: the right answer, arriving too
    // late to be useful for judging it.
    //
    // So the draft follows the panel. `_styleForNewShape` is the same
    // function the commit uses, so what you are looking at while you slide
    // the slider is exactly what you will get.
    _restyleDrafts: function() {
      var self = this;
      [self.draftState, self.draftCallout].forEach(function(d) {
        if (!d || !d.el) return;
        d.style = self._styleForNewShape(d.kind);
        self._applyShapeColor(
          d.el, (d.style && d.style.color) || self.activeColor, d.style
        );
        self._renderShape(d);
      });
      // The polygon preview is drawn by its own path rather than
      // `_renderShape`, so it takes the params directly — as it does at
      // creation.
      var poly = self.draftPolygon;
      if (poly && poly.el) {
        var ps = self._styleForNewShape("polygon");
        self._applyShapeColor(poly.el, ps.color || self.activeColor, ps);
        self._applyLineParams(poly.el, ps, self._markerScale());
        self._renderPolygonPreview(self._lastHover || null);
      }
    },

    _lineParamsForNewShape: function() {
      var lp = this._currentLineParams();
      var scale = this._inkScale();
      if (!(scale > 0)) return lp;
      var out = Object.assign({}, lp, {
        width: (lp.width || 2) / scale,
        width_units: "canvas"
      });
      // Same conversion as the width, for the same reason: stored in canvas
      // units so it holds its size against the drawing, not against the
      // screen.
      if (out.font_size) out.font_size = out.font_size / scale;
      return out;
    },

    _currentLineParams: function() {
      var lp = this.lineParams || {};
      var out = {
        color: this.activeColor || null,
        width: lp.width || 2,
        opacity: lp.opacity == null ? 1 : lp.opacity,
        dash: lp.dash || "solid",
        fill: lp.fill || "semi"
      };
      // Only when the user has actually pinned one: absent means "size it
      // from the box", which is the default and must not become a number.
      if (typeof lp.font_size === "number" && isFinite(lp.font_size) && lp.font_size > 0) {
        out.font_size = lp.font_size;
      }
      return out;
    },

    // Paint thickness / opacity / dash onto a stroke shape's element. Width is
    // an inline style (so the hover/select stroke-width:3 rules don't override
    // and visibly re-thin the line); dash is a presentation attribute.
    // Stroke widths live in CANVAS units, like a marker's, so a line keeps
    // the weight it was drawn with relative to the drawing: zoom out and it
    // thins with everything else instead of sitting on top at a fixed
    // on-screen thickness, which at low zoom made every outline dominate the
    // shape it outlined.
    //
    // `scale` is container px per canvas px. Omitted (a draft being styled
    // before it is placed) means "draw it at face value".
    _applyLineParams: function(el, style, scale) {
      if (!el) return;
      var s = style || {};
      var w = s.width || 2;
      if (typeof scale === "number" && scale > 0 && s.width_units === "canvas") {
        // Floored, or a line becomes sub-pixel and disappears entirely at
        // extreme zoom-out — a shape you can no longer see is worse than one
        // drawn slightly heavier than the maths says.
        w = Math.max(MARKER_MIN_SCREEN_PX, w * scale);
      }
      el.style.strokeWidth = w + "px";
      el.removeAttribute("stroke-width");
      el.style.strokeOpacity = String(s.opacity == null ? 1 : s.opacity);
      this._applyStrokeDash(el, s.dash, w);
      // Opacity applies to the body as well as the outline. Fill first —
      // whether the outline may be dropped depends on the fill being there.
      this._applyFill(el, s);
      this._applyStrokeVisibility(el, s);
    },

    // The dash pattern for a stroke of width `w`, shared by the outline
    // shapes (via `_applyLineParams`) and the shaft kinds (via their render
    // cases) so "dashed" and "dotted" mean the same picture everywhere.
    _applyStrokeDash: function(el, dash, w) {
      if (dash === "none") {
        // Whether this actually hides anything is `_applyStrokeVisibility`'s
        // call; either way there's no dash pattern to draw.
        el.removeAttribute("stroke-dasharray");
        el.style.strokeLinecap = "";
      } else if (dash === "dashed") {
        el.setAttribute("stroke-dasharray", (w * 2.2) + " " + (w * 1.6));
        el.style.strokeLinecap = "";
      } else if (dash === "dotted") {
        // A zero-length dash only paints as a dot under a round linecap.
        // Shapes default to `butt`, where these rendered as nothing at all —
        // "dotted" looked identical to no line. Markers happened to work
        // because their own CSS already rounds the caps.
        //
        // Gap is 2.4× the width, not 1.8×: the round cap paints each
        // zero-length dash as a disc a full `w` across, so at 1.8× the discs
        // nearly touched and read as a bumpy solid line. Markers use the same
        // ratio so a dotted marker and a dotted outline look alike.
        el.setAttribute("stroke-dasharray", "0.01 " + (w * DOT_GAP_RATIO));
        el.style.strokeLinecap = "round";
      } else {
        el.removeAttribute("stroke-dasharray");
        el.style.strokeLinecap = "";
      }
    },

    // ----- Shaft kinds (line / arrow / dimension) ----------------------------
    // A <g> whose visible body is a stroked shaft (plus arrowheads and, for
    // dimension, a label). They take the same width / opacity / dash params
    // as the outline shapes; fill modes don't apply — there is nothing to
    // fill on an open shaft. Their stroke is applied inside their
    // `_renderShape` cases rather than by `_applyLineParams`, because the
    // arrowhead geometry has to be sized off the same number and only the
    // render case has the endpoints in hand.

    _isShaftKind: function(kind) {
      return kind === "line" || kind === "arrow" || kind === "dimension";
    },

    // Kinds whose label rides the shaft: centred on the line at
    // `metadata.title_offset` (a 0-1 fraction along a->b), dragged ALONG
    // the line rather than freely, no leader (it would be a stub pointing
    // at the line the label already sits on). The dimension has always
    // worked this way — its label is the measurement. The arrow joined it
    // (head dev's call): an arrow's label names the pointing, so a label
    // that can wander off the line reads as a separate note, not a name.
    // NOT the plain line: its label stays the ordinary float-above kind.
    // Cut the shaft out from behind (and just around) its riding label —
    // the drafting convention: a dimension's value sits IN the line, in a
    // break of its own, not printed across it. Done with a per-shape mask
    // on the shaft group rather than by splitting the path, so the cut
    // honours curves, dash patterns and the arrowheads for free, and the
    // geometry (hit-testing, handles, offsets) never learns about it —
    // the line is still there to grab, it just does not paint under the
    // label. The mask id is instance-prefixed for the same reason the
    // hatch patterns are: url(#…) resolves document-wide.
    _syncShaftLabelGap: function(shape, rectEl) {
      if (!this.svg || !shape || !shape.el || !shape.uuid || !rectEl) return;
      if (!this._defs) {
        this._defs = svgEl("defs");
        this.svg.insertBefore(this._defs, this.svg.firstChild);
      }
      if (!this._shaftGapPrefix) {
        SHAFT_GAP_SEQ += 1;
        this._shaftGapPrefix = "etcher-shaft-gap-" + SHAFT_GAP_SEQ + "-";
      }
      var id = this._shaftGapPrefix +
        String(shape.uuid).replace(/[^a-z0-9-]/gi, "");
      var mask = this._defs.querySelector('[id="' + id + '"]');
      if (!mask) {
        // userSpaceOnUse with an oversized keep-rect: the mask must cover
        // wherever pan/zoom puts the shaft, not the shaft's initial bbox
        // (the default objectBoundingBox units would also make the cut
        // rect's coordinates relative, and they are container px).
        mask = svgEl("mask", {
          id: id,
          maskUnits: "userSpaceOnUse",
          x: -100000, y: -100000, width: 200000, height: 200000
        });
        var keep = svgEl("rect", {
          x: -100000, y: -100000, width: 200000, height: 200000, fill: "#fff"
        });
        var cut = svgEl("rect", { fill: "#000" });
        cut.classList.add("etcher-shaft-gap-cut");
        mask.appendChild(keep);
        mask.appendChild(cut);
        this._defs.appendChild(mask);
      }
      // `box` is either the label's rendered <rect> or a plain
      // {x, y, w, h} — the editor passes the box the COMMITTED label will
      // occupy rather than its own, which is deliberately taller (a
      // textarea needs a full line box plus descender headroom inside
      // overflow:hidden). Cutting to the editor's box made the break
      // visibly settle at Enter, and on a diagonal shaft moved where the
      // line stopped.
      // The cut is the label's rendered rect plus a margin, so the line
      // ends a clean step short of the words instead of touching them.
      // Proportional to the label with a floor: a margin that scaled only
      // with the screen would crowd a large label and drown a small one.
      //
      // A step, not a stride. The label's own plate already pads the
      // words, and this margin sits outside that one — measured on the
      // board, a quarter of the label's height put the arrowheads a long
      // way out from a dimension that reads as one piece, so the two
      // halves of the line looked unrelated to the measurement between
      // them. Enough to see daylight is the whole job.
      //
      // The floor tracks the line's own weight rather than being a flat
      // number: what should be constant is the daylight between the ink
      // and the words, and half of a heavy shaft is already on the line
      // before any margin is counted. It matters most zoomed out, where
      // labels scale down while the stroke stays screen-thick.
      var live = rectEl && typeof rectEl.getAttribute === "function";
      var x = Number(live ? rectEl.getAttribute("x") : rectEl.x) || 0;
      var y = Number(live ? rectEl.getAttribute("y") : rectEl.y) || 0;
      var w = Number(live ? rectEl.getAttribute("width") : rectEl.w) || 0;
      var h = Number(live ? rectEl.getAttribute("height") : rectEl.h) || 0;
      var half = 1;
      try { half = this._renderedStrokePx(shape) / 2; } catch (_) { half = 1; }
      var gap = Math.max(4 + half, h * 0.14) * SHAFT_LABEL_GAP_SCALE;
      var cutEl = mask.querySelector(".etcher-shaft-gap-cut");
      cutEl.setAttribute("x", x - gap);
      cutEl.setAttribute("y", y - gap);
      cutEl.setAttribute("width", w + gap * 2);
      cutEl.setAttribute("height", h + gap * 2);
      // A turned board turns the label; the cut has to turn with it or the
      // gap sits askew of the words it is clearing space for.
      var turn = live ? rectEl.getAttribute("transform") : rectEl.transform;
      if (turn) cutEl.setAttribute("transform", turn);
      else cutEl.removeAttribute("transform");
      shape.el.setAttribute("mask", "url(#" + id + ")");
    },

    // Label gone (deleted, emptied): the line closes back up. The mask
    // node goes too, so a board that sheds labels does not accrete defs.
    _clearShaftLabelGap: function(shape) {
      if (shape && shape.el) shape.el.removeAttribute("mask");
      if (!this._defs || !shape || !shape.uuid || !this._shaftGapPrefix) return;
      var id = this._shaftGapPrefix +
        String(shape.uuid).replace(/[^a-z0-9-]/gi, "");
      var mask = this._defs.querySelector('[id="' + id + '"]');
      if (mask && mask.parentNode) mask.parentNode.removeChild(mask);
    },

    _labelRidesShaft: function(kind) {
      return kind === "dimension" || kind === "arrow";
    },

    // A shaft's stroke width in on-screen px. A styled width lives in canvas
    // units like every other stroke, so the shaft thins with the drawing on
    // zoom-out. No styled width → `fallbackPx`, which each kind supplies as
    // whatever it rendered before widths existed (line: a fixed 2px; arrow /
    // dimension: the board-anchored weight) — so untouched boards are
    // byte-for-byte unchanged.
    _shaftStrokePx: function(shape, fallbackPx) {
      var s = (shape && shape.style) || {};
      if (typeof s.width === "number" && isFinite(s.width)) {
        var w = s.width;
        if (s.width_units === "canvas") {
          var scale = 0;
          try { scale = this._markerScale() || 0; } catch (_) {}
          if (scale > 0) w = w * scale;
        }
        return Math.max(0.4, w);
      }
      return Math.max(0.4, fallbackPx);
    },

    // Width + opacity for any shaft child; dash only where asked — the shaft
    // takes the pattern, arrowheads stay solid (a dashed V reads as broken,
    // and every comparable tool leaves heads solid on a dashed connector).
    _applyShaftStroke: function(el, shape, w, withDash) {
      if (!el) return;
      el.style.strokeWidth = w + "px";
      el.removeAttribute("stroke-width");
      var s = (shape && shape.style) || {};
      el.style.strokeOpacity = String(s.opacity == null ? 1 : s.opacity);
      if (withDash) this._applyStrokeDash(el, s.dash, w);
    },

    // Params popup: weight + opacity sliders and a dash picker, opened by the
    // toolbar sliders button. Edits `lineParams` — the default for new stroke
    // shapes. (Structurally a twin of the marker popup, reusing its CSS.)
    _buildParamsPopup: function() {
      var self = this;
      var popup = document.createElement("div");
      popup.className = "etcher-popup";
      popup.dataset.kind = "params";
      popup.setAttribute("data-fresco-no-capture", "");

      function sliderRow(labelText) {
        var row = document.createElement("div");
        row.className = "etcher-marker-row";
        var head = document.createElement("div");
        head.className = "etcher-marker-row-head";
        var label = document.createElement("span");
        label.textContent = labelText;
        var val = document.createElement("span");
        head.appendChild(label);
        head.appendChild(val);
        var input = document.createElement("input");
        input.type = "range";
        row.appendChild(head);
        row.appendChild(input);
        popup.appendChild(row);
        return { row: row, input: input, val: val };
      }

      var w = sliderRow("Thickness");
      w.input.min = "0"; w.input.max = "100"; w.input.step = "1";
      self._paramsWeightRow = w.row;
      self._paramsWeightInput = w.input;
      self._paramsWeightVal = w.val;
      w.input.addEventListener("input", function() {
        var pw = self._weightFromSlider(w.input.value);
        w.val.textContent = String(pw);
        self._setLineParam("width", pw, false);
      });
      w.input.addEventListener("change", function() {
        self._setLineParam("width", self._weightFromSlider(w.input.value), true);
      });
      self._makeWeightTypeable(w.val, w.input, function(n) {
        self._setLineParam("width", n, true);
        self._showValuePreview("width", n, w.input, self._previewStrokeStyle(false));
      });
      self._wireValuePreview(w.input, function() {
        self._showValuePreview("width", self._weightFromSlider(w.input.value), w.input, self._previewStrokeStyle(false));
      });

      var o = sliderRow("Opacity");
      o.input.min = "10"; o.input.max = "100"; o.input.step = "5";
      self._paramsOpacityRow = o.row;
      self._paramsOpacityInput = o.input;
      self._paramsOpacityVal = o.val;
      o.input.addEventListener("input", function() {
        o.val.textContent = o.input.value + "%";
        self._setLineParam("opacity", parseInt(o.input.value, 10) / 100, false);
      });
      o.input.addEventListener("change", function() {
        self._setLineParam("opacity", parseInt(o.input.value, 10) / 100, true);
      });

      // Font size: a number you type or step, and nothing else. A size is a
      // number you know — 18, to match the other labels — not a position you
      // hunt for, and nothing else in this panel is a slider-plus-readout
      // pair either. Blank means "size it from the box", which is what
      // dragging the label does and what every existing label has been doing
      // all along.
      var fontRow = document.createElement("div");
      // Its own class as well: the compact strip hides every slider row,
      // and this row is a number box that still fits there.
      fontRow.className = "etcher-marker-row etcher-font-row";
      var fontHead = document.createElement("div");
      fontHead.className = "etcher-marker-row-head";
      var fontLabel = document.createElement("span");
      // "Label size", not "font size": what it sizes is the label, and the
      // panel is otherwise named after the things it acts on.
      fontLabel.textContent = "Label size";
      fontHead.appendChild(fontLabel);
      var fontNum = document.createElement("input");
      fontNum.type = "number";
      fontNum.className = "etcher-num";
      fontNum.min = String(FONT_SIZE_MIN);
      fontNum.max = String(FONT_SIZE_MAX);
      fontNum.step = "1";
      // "custom" appears while a SELECTED label is box-sized (dragged by
      // its corners); the global default is always a real number now.
      fontNum.placeholder = "custom";
      // Carries the naming in the compact strip, where the row's own label
      // has no room to be drawn.
      fontNum.title =
        "Label size — what new labels start at; clearing it returns " +
        "to the default (" + DEFAULT_LABEL_FONT_SIZE + ")";
      var fontStep = document.createElement("div");
      fontStep.className = "etcher-stepper";
      var fontDown = self._makeStepButton("−", "Smaller", "etcher-step-down");
      var fontUp = self._makeStepButton("+", "Larger", "etcher-step-up");
      fontStep.appendChild(fontDown);
      fontStep.appendChild(fontNum);
      fontStep.appendChild(fontUp);

      fontRow.appendChild(fontHead);
      fontRow.appendChild(fontStep);
      popup.appendChild(fontRow);
      self._paramsFontRow = fontRow;
      self._paramsFontNum = fontNum;

      function clampFont(n) {
        return Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, Math.round(n)));
      }

      // One step of the same thing typing a number does. An empty box means
      // the size is coming from somewhere else (a box-sized label, the
      // default) — stepping starts from the size that is actually in use, so
      // the first press nudges what is on screen rather than jumping to some
      // number the user never chose.
      function stepFont(by) {
        var n = parseInt(fontNum.value.trim(), 10);
        if (!isFinite(n)) n = Math.round(self._defaultLabelFontSize());
        var next = clampFont(n + by);
        fontNum.value = String(next);
        self._setFontSize(next, true);
        self._showValuePreview("font", next, fontNum);
      }

      fontDown.addEventListener("click", function() { stepFont(-1); });
      fontUp.addEventListener("click", function() { stepFont(1); });

      // Typed, spun, or pasted. An empty box means auto — the one way back
      // to box-sizing once a size has been pinned, short of dragging.
      fontNum.addEventListener("input", function() {
        var raw = fontNum.value.trim();
        if (raw === "") {
          self._setFontSize(null, true);
          return;
        }
        var n = parseInt(raw, 10);
        if (!isFinite(n)) return;
        self._setFontSize(clampFont(n), true);
        self._showValuePreview("font", clampFont(n), fontNum);
      });
      // Typing past the limit is corrected on the way out rather than while
      // the user is still mid-number: rewriting "2" to "6" as they type "20"
      // makes the box unusable.
      fontNum.addEventListener("blur", function() {
        var raw = fontNum.value.trim();
        if (raw === "") return;
        var n = parseInt(raw, 10);
        if (!isFinite(n)) { self._syncParamsPopup(); return; }
        fontNum.value = String(clampFont(n));
      });

      // The plate behind a label, next to the size of the label it sits
      // behind. On/off only: WHICH colour is the swatch's job, beside the
      // label's own colour where the two can be compared.
      var bgRow = document.createElement("div");
      bgRow.className = "etcher-marker-row etcher-bg-row";
      var bgHead = document.createElement("div");
      bgHead.className = "etcher-marker-row-head";
      var bgLabel = document.createElement("span");
      bgLabel.textContent = "Label background";
      var bgBtn = document.createElement("button");
      bgBtn.type = "button";
      bgBtn.className = "etcher-toggle";
      bgHead.appendChild(bgLabel);
      bgHead.appendChild(bgBtn);
      bgRow.appendChild(bgHead);
      popup.appendChild(bgRow);
      self._paramsBgRow = bgRow;
      self._paramsBgBtn = bgBtn;
      bgBtn.addEventListener("click", function(e) {
        e.preventDefault();
        self._toggleLabelBg();
      });

      // A text box's corners: which shape (rounded, cut, scooped, notched)
      // and how far they reach. Shown for text boxes, and with the text box
      // tool armed and nothing selected, where they set what new boxes get.
      var cornerTypeRow = document.createElement("div");
      cornerTypeRow.className = "etcher-marker-dash etcher-corner-row";
      var CORNER_NAMES = { sharp: "Sharp", round: "Rounded", chamfer: "Straight", concave: "Concave", notch: "Notched" };
      // Sharp first, as its own button: no corners at all is a choice you
      // should see and pick, not something found by sliding to zero.
      self._paramsCornerBtns = ["sharp"].concat(TEXTBOX_CORNERS).map(function(type) {
        var b = document.createElement("button");
        b.type = "button";
        b.dataset.corner = type;
        b.title = CORNER_NAMES[type] + " corners";
        b.setAttribute("aria-label", b.title);
        b.innerHTML = '<svg width="26" height="18" viewBox="0 0 26 18"><path d="' +
          self._cornerPath(2, 2, 22, 14, type, type === "sharp" ? 0 : 5.5) +
          '" fill="none" stroke="#fff" stroke-width="1.6" stroke-linejoin="miter"/></svg>';
        b.addEventListener("click", function(e) {
          e.preventDefault();
          if (type === "sharp") {
            self._setTextboxCorner("corner_size", 0, true);
            self._syncCornerRows();
          } else {
            self._setTextboxCorner("corner", type, true);
          }
        });
        cornerTypeRow.appendChild(b);
        return b;
      });
      self._paramsCornerTypeRow = cornerTypeRow;

      // One block: the heading with its reading, the five shapes, and the
      // slider — which only shows once there are corners to size.
      var cs = sliderRow("Corners");
      cs.row.classList.add("etcher-corner-section");
      cs.row.insertBefore(cornerTypeRow, cs.input);
      cs.input.min = "0"; cs.input.max = String(TEXTBOX_CORNER_MAX); cs.input.step = "1";
      self._paramsCornerRow = cs.row;
      self._paramsCornerInput = cs.input;
      self._paramsCornerVal = cs.val;
      cs.input.addEventListener("input", function() {
        var v = parseInt(cs.input.value, 10) || 0;
        cs.val.textContent = v ? String(v) : "Sharp";
        self._setTextboxCorner("corner_size", v, false);
      });
      cs.input.addEventListener("change", function() {
        self._setTextboxCorner("corner_size", parseInt(cs.input.value, 10) || 0, true);
      });



      var dashRow = document.createElement("div");
      dashRow.className = "etcher-marker-dash";
      function dashIcon(dash) {
        // "No line" reads as a line struck through rather than an empty
        // button, which would look like a rendering failure next to three
        // drawn ones.
        if (dash === "none") {
          return '<svg width="28" height="8.4" viewBox="0 0 40 12">' +
                 '<line x1="3" y1="6" x2="37" y2="6" stroke="#fff" stroke-width="3" ' +
                 'stroke-linecap="round" stroke-opacity="0.3"/>' +
                 '<line x1="12" y1="11" x2="28" y2="1" stroke="#fff" stroke-width="1.6" ' +
                 'stroke-linecap="round"/></svg>';
        }
        var da = dash === "dashed" ? ' stroke-dasharray="7 5"'
               : dash === "dotted" ? ' stroke-dasharray="0.01 6"' : '';
        return '<svg width="28" height="5.6" viewBox="0 0 40 8"><line x1="3" y1="4" x2="37" y2="4" ' +
               'stroke="#fff" stroke-width="3" stroke-linecap="round"' + da + '/></svg>';
      }
      self._paramsDashBtns = DASH_MODES.map(function(dash) {
        var b = document.createElement("button");
        b.type = "button";
        b.dataset.dash = dash;
        b.title = dash === "none" ? "No line" : dash;
        b.setAttribute("aria-label", "Line: " + dash);
        b.innerHTML = dashIcon(dash);
        b.addEventListener("click", function(e) {
          e.preventDefault();
          self._setLineParam("dash", dash, true);
          self._syncParamsPopup();
        });
        dashRow.appendChild(b);
        return b;
      });
      popup.appendChild(dashRow);
      self._paramsDashRow = dashRow;

      // Fill mode. Rides the same `_setLineParam` path as thickness and dash,
      // so selection targeting, live preview and undo all come for free.
      var fillRow = document.createElement("div");
      // Same layout as the dash row, but its own name as well — the compact
      // panel keeps line types and drops fills, and telling them apart by
      // position in the popup would break the moment a row moved.
      fillRow.className = "etcher-marker-dash etcher-marker-fill";

      // The explicit width/height matter: the row stretches its children to
      // full width, so an SVG sized only by `viewBox` scales up to fill the
      // whole button. The dash icons pin theirs for the same reason.
      function fillIcon(mode) {
        var body;
        if (mode === "none") {
          body = "";
        } else if (mode === "pattern") {
          // Drawn as plain diagonals clipped to the square rather than a
          // nested `<pattern>`: an icon this small needs three visible
          // lines, not a faithful miniature of the hatch.
          body = '<g clip-path="url(#pk-fill-hatch-clip)" stroke-width="1.4">' +
                 '<path d="M5 14 L14 5 M5 19 L19 5 M10 19 L19 10"/></g>' +
                 '<defs><clipPath id="pk-fill-hatch-clip">' +
                 '<rect x="4.5" y="4.5" width="15" height="15" rx="2.5"/>' +
                 '</clipPath></defs>';
        } else {
          body = '<rect x="4.5" y="4.5" width="15" height="15" rx="2.5" fill="currentColor" ' +
                 'fill-opacity="' + (mode === "solid" ? "1" : "0.28") + '" stroke="none"/>';
        }
        return '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" ' +
               'viewBox="0 0 24 24" fill="none" ' +
               'stroke="currentColor" stroke-width="1.6" aria-hidden="true">' + body +
               '<rect x="4.5" y="4.5" width="15" height="15" rx="2.5"/></svg>';
      }

      self._paramsFillBtns = FILL_MODES.map(function(mode) {
        var b = document.createElement("button");
        b.type = "button";
        b.dataset.fill = mode;
        b.title = "Fill: " + mode;
        b.setAttribute("aria-label", "Fill: " + mode);
        b.innerHTML = fillIcon(mode);
        b.addEventListener("click", function(e) {
          e.preventDefault();
          self._setLineParam("fill", mode, true);
          self._syncParamsPopup();
        });
        fillRow.appendChild(b);
        return b;
      });
      popup.appendChild(fillRow);
      // Kept so `_syncParamsPopup` can hide it when nothing in the selection
      // can hold a fill (shaft kinds, markers) — same reasoning as the label
      // section below.
      self._paramsFillRow = fillRow;

      // Label placement. Hidden unless something in the selection actually
      // carries a label — an alignment control with nothing to align is
      // just noise in a panel this narrow.
      var labelSection = document.createElement("div");
      labelSection.className = "etcher-label-section";

      var labelHead = document.createElement("div");
      labelHead.className = "etcher-params-label";
      labelHead.textContent = "Label";
      labelSection.appendChild(labelHead);

      // A label box with its text pushed to one side, so the icon shows
      // the result rather than the usual abstract three-bars glyph.
      function alignIcon(axis, value) {
        var bars = axis === "h"
          ? { left: [[5, 9, 8], [5, 13, 12]], center: [[8, 9, 8], [6, 13, 12]],
              right:  [[11, 9, 8], [7, 13, 12]] }[value]
          : { top: [[6, 8, 12], [6, 12, 8]], middle: [[6, 11, 12], [6, 15, 8]],
              bottom: [[6, 14, 12], [6, 18, 8]] }[value];
        var lines = bars.map(function(b) {
          return '<rect x="' + b[0] + '" y="' + b[1] + '" width="' + b[2] +
                 '" height="1.8" rx="0.9" fill="currentColor" stroke="none"/>';
        }).join("");
        return '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" ' +
               'viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
               'stroke-width="1.4" aria-hidden="true">' +
               '<rect x="3" y="4" width="18" height="16" rx="2.5" stroke-opacity="0.35"/>' +
               lines + '</svg>';
      }

      // A label floating above a shape on a leader line — the default, and
      // where dragging the label puts it back.
      function floatIcon() {
        return '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" ' +
               'viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
               'stroke-width="1.5" aria-hidden="true">' +
               '<rect x="4" y="3" width="16" height="6" rx="1.5"/>' +
               '<path d="M12 9 L12 13" stroke-dasharray="2 2"/>' +
               '<rect x="7" y="13" width="10" height="8" rx="1.5" stroke-opacity="0.4"/>' +
               '</svg>';
      }

      function alignBtn(row, axis, value, icon, label) {
        var b = document.createElement("button");
        b.type = "button";
        b.dataset.titleAlign = axis + ":" + value;
        b.title = label;
        b.setAttribute("aria-label", label);
        b.innerHTML = icon;
        b.addEventListener("click", function(e) {
          e.preventDefault();
          self._setTitleAlign(axis, value);
        });
        row.appendChild(b);
        return b;
      }

      var hRow = document.createElement("div");
      hRow.className = "etcher-marker-dash etcher-align-row";
      var vRow = document.createElement("div");
      vRow.className = "etcher-marker-dash";

      self._titleAlignBtns = TITLE_H_ALIGNS.map(function(v) {
        return alignBtn(hRow, "h", v, alignIcon("h", v), "Align " + v);
      }).concat(TITLE_V_ALIGNS.map(function(v) {
        return alignBtn(vRow, "v", v, alignIcon("v", v), "Align " + v);
      }));
      // `free` is the absence of an alignment, so it belongs with the
      // buttons that set one — otherwise there's no way back to a floating
      // label except dragging it.
      self._titleFreeBtn = alignBtn(vRow, "free", "free", floatIcon(), "Float above shape");

      labelSection.appendChild(hRow);
      labelSection.appendChild(vRow);
      popup.appendChild(labelSection);
      self._labelSection = labelSection;

      // A text box's text: where it sits inside the box. The same six
      // placements a label has, for the words within the walls.
      var tbAlignSection = document.createElement("div");
      tbAlignSection.className = "etcher-label-section etcher-tb-align";
      var tbHead = document.createElement("div");
      tbHead.className = "etcher-params-label";
      tbHead.textContent = "Text in the box";
      tbAlignSection.appendChild(tbHead);
      var tbH = document.createElement("div");
      tbH.className = "etcher-marker-dash etcher-align-row";
      var tbV = document.createElement("div");
      tbV.className = "etcher-marker-dash etcher-align-row";
      var tbBtn = function(row, axis, value) {
        var b = document.createElement("button");
        b.type = "button";
        b.dataset.tbAlign = axis + ":" + value;
        b.title = "Text " + value;
        b.setAttribute("aria-label", b.title);
        b.innerHTML = alignIcon(axis, value);
        b.addEventListener("click", function(e) {
          e.preventDefault();
          self._setTextboxAlign(axis, value);
        });
        row.appendChild(b);
        return b;
      };
      self._tbAlignBtns = TITLE_H_ALIGNS.map(function(v) { return tbBtn(tbH, "h", v); })
        .concat(TITLE_V_ALIGNS.map(function(v) { return tbBtn(tbV, "v", v); }));
      tbAlignSection.appendChild(tbH);
      tbAlignSection.appendChild(tbV);
      popup.appendChild(tbAlignSection);
      self._tbAlignSection = tbAlignSection;

      // The choice rows — line type, fill, corners, label position — fold
      // into one slim row of buttons, each showing its current choice and
      // opening a small pop-up with the options. The rows themselves stay
      // (hidden) as the source of truth: the pop-up's options are copies of
      // their buttons and press the originals, so wiring, selection and
      // sync are exactly theirs. The compact strip keeps the rows inline.
      var chipsRow = document.createElement("div");
      chipsRow.className = "etcher-chips";
      var chipPop = document.createElement("div");
      chipPop.className = "etcher-chip-pop";
      chipPop.setAttribute("data-fresco-no-capture", "");
      self._chipPop = chipPop;
      // Affinity's context-toolbar idea: every setting is a small button
      // showing its current value; a click opens its controls. The rows are
      // the controls themselves, moved into the pop-up while it is open and
      // back after, so their wiring and sync are untouched. The compact
      // strip keeps them inline.
      [o.row, bgRow, dashRow, fillRow, cs.row, labelSection, tbAlignSection]
        .forEach(function(r) { r.classList.add("etcher-in-chips"); });
      // Thickness and label size stay out, each one slim line — an icon,
      // the control, its value — since they are the two reached for most.
      var inlineIcon = function(row, svg, title) {
        var head = row.querySelector(".etcher-marker-row-head");
        var ic = document.createElement("span");
        ic.className = "etcher-inline-icon";
        ic.title = title;
        ic.innerHTML = svg;
        (head || row).insertBefore(ic, (head || row).firstChild);
        row.classList.add("etcher-inline-row");
      };
      inlineIcon(w.row, '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">' +
        '<path d="M2 4H14" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>' +
        '<path d="M2 8H14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
        '<path d="M2 12.5H14" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>', "Thickness");
      inlineIcon(fontRow, '<span class="etcher-chip-aa">Aa</span>', "Label size");
      var fillHead = document.createElement("div");
      fillHead.className = "etcher-marker-row-head";
      fillHead.innerHTML = "<span>Fill</span>";
      var lineHead = document.createElement("div");
      lineHead.className = "etcher-marker-row-head";
      lineHead.innerHTML = "<span>Line type</span>";
      var selIcon = function(btns) {
        var list = btns || [];
        var sel = list.filter(function(b) { return b && b.classList.contains("is-selected"); })[0] || list[0];
        return sel ? sel.innerHTML : "";
      };
      var CHIPS = [
        // Thickness is an inline line in the full panel; in the compact
        // strip, where sliders do not fit, it is a button like the rest.
        { key: "weight", title: "Thickness", caption: "Thickness", rows: [w.row], compactOnly: true,
          face: function() {
            return '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">' +
              '<path d="M2 4H14" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>' +
              '<path d="M2 8H14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
              '<path d="M2 12.5H14" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>' +
              '<b>' + (self._paramsWeightVal.textContent || "") + '</b>';
          } },
        { key: "stroke", title: "Line type", caption: "Line", rows: [lineHead, dashRow],
          face: function() { return selIcon(self._paramsDashBtns); } },
        { key: "opacity", title: "Opacity", caption: "Opacity", rows: [o.row],
          // Just the number: "70%" says what it is, and an icon beside it
          // would not fit when four buttons share the row.
          face: function() { return '<b>' + (self._paramsOpacityVal.textContent || "") + '</b>'; } },
        { key: "fill", title: "Fill", caption: "Fill", rows: [fillHead, fillRow], face: function() { return selIcon(self._paramsFillBtns); } },
        { key: "corners", title: "Corners", caption: "Corners", rows: [cs.row],
          face: function() {
            var v = self._paramsCornerInput && self._paramsCornerInput.style.display !== "none" ? self._paramsCornerInput.value : "";
            return selIcon(self._paramsCornerBtns) + (v ? '<b>' + v + '</b>' : "");
          } },
        { key: "talign", title: "Text alignment in the box", caption: "Align", rows: [tbAlignSection],
          face: function() {
            var sel = (self._tbAlignBtns || []).filter(function(b) { return b.classList.contains("is-selected") && /^h:/.test(b.dataset.tbAlign); })[0];
            return sel ? sel.innerHTML : alignIcon("h", "left");
          } },
        { key: "position", title: "Label position", caption: "Label", rows: [labelSection],
          face: function() {
            var list = (self._titleAlignBtns || []).concat(self._titleFreeBtn ? [self._titleFreeBtn] : []);
            var sel = list.filter(function(b) { return b.classList.contains("is-selected"); });
            return sel.length ? sel[sel.length - 1].innerHTML : (self._titleFreeBtn ? self._titleFreeBtn.innerHTML : "");
          } }
      ];
      self._chips = CHIPS;
      CHIPS.forEach(function(chip) {
        var b = document.createElement("button");
        b.type = "button";
        b.className = "etcher-chip";
        b.dataset.chip = chip.key;
        b.title = chip.title;
        b.setAttribute("aria-label", chip.title);
        b.setAttribute("aria-haspopup", "true");
        b.addEventListener("click", function(e) {
          e.preventDefault();
          if (self._openChip === chip) self._closeChipPop();
          else self._openChipPop(chip);
        });
        chip.el = b;
        chipsRow.appendChild(b);
      });

      // Grouped, in the order you work: the line (thickness, opacity), the
      // label (size, background), then the choice buttons. A thin rule
      // between groups — only between groups that are showing (see
      // `_syncPanelSeps`).
      self._panelSeps = [];
      [w.row, fontRow, chipsRow, o.row, bgRow, dashRow, fillRow, cs.row, labelSection, tbAlignSection]
        .forEach(function(item) {
          if (item === "|") {
            var sep = document.createElement("div");
            sep.className = "etcher-panel-sep";
            popup.appendChild(sep);
            self._panelSeps.push(sep);
          } else if (item) {
            popup.appendChild(item);
          }
        });

      popup.appendChild(chipPop);
      self._chipsRow = chipsRow;

      self.handle.container.appendChild(popup);
      self.paramsPopup = popup;
    },

    // Open a button's pop-up under the button row, holding that setting's
    // own controls (moved in, and back out on close).
    _openChipPop: function(chip) {
      var self = this;
      var pop = this._chipPop;
      if (!pop || !chip) return;
      this._closeChipPop();
      this._openChip = chip;
      chip.el.classList.add("is-open");
      chip._moved = [];
      chip.rows.forEach(function(row) {
        var isHead = !row.parentNode || row.classList.contains("etcher-marker-row-head");
        if (!isHead && row.style.display === "none") return;
        if (row.parentNode) {
          var ph = document.createComment("chip");
          row.parentNode.insertBefore(ph, row);
          chip._moved.push([row, ph]);
        } else {
          chip._moved.push([row, null]);
        }
        row.classList.remove("etcher-in-chips");
        pop.appendChild(row);
      });
      pop.classList.add("is-open");
      // Under the button row in the full panel; beside the button, level
      // with it, in the compact strip.
      var row = this._chipsRow;
      var host = pop.offsetParent || pop.parentNode;
      var compact = this.stylePanel && this.stylePanel.getAttribute("data-size") === "compact";
      // Borrowed by the ⋯ menu, which opens upwards out of the bar at the
      // bottom: the pop-up opens above the button row, never down over
      // Arrange and the bar.
      var inMenu = !!(this.actionMenu && this.actionMenu.contains(pop));
      if (inMenu) compact = false;
      if (row && host && host.getBoundingClientRect) {
        var hr = host.getBoundingClientRect();
        var rr = compact ? chip.el.getBoundingClientRect() : row.getBoundingClientRect();
        pop.style.top = inMenu
          ? (rr.top - hr.top - pop.offsetHeight - 6) + "px"
          : ((compact ? rr.top : rr.bottom + 6) - hr.top) + "px";
      }
      // The button's face follows the controls as they move.
      this._chipPopLive = function() { setTimeout(function() { self._syncStyleChips(); }, 0); };
      pop.addEventListener("input", this._chipPopLive);
      pop.addEventListener("click", this._chipPopLive);
      this._chipPopOutside = function(e) {
        if (!self._openChip) return;
        if (pop.contains(e.target) || (self._chipsRow && self._chipsRow.contains(e.target))) return;
        // The colour popup opened from inside belongs to the panel too.
        if (e.target.closest && e.target.closest(".etcher-popup[data-kind=\"colors\"]")) return;
        self._closeChipPop();
      };
      // Escape with a pop-up open closes the pop-up, and only that — not the
      // selection or annotation mode behind it.
      this._chipPopKey = function(e) {
        if (e.key !== "Escape" || !self._openChip) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        self._closeChipPop();
      };
      document.addEventListener("pointerdown", this._chipPopOutside, true);
      document.addEventListener("keydown", this._chipPopKey, true);
    },

    _closeChipPop: function() {
      var chip = this._openChip;
      var pop = this._chipPop;
      this._openChip = null;
      if (chip) {
        chip.el.classList.remove("is-open");
        (chip._moved || []).forEach(function(pair) {
          var row = pair[0], ph = pair[1];
          row.classList.add("etcher-in-chips");
          if (ph && ph.parentNode) { ph.parentNode.insertBefore(row, ph); ph.parentNode.removeChild(ph); }
          else if (row.parentNode) row.parentNode.removeChild(row);
        });
        chip._moved = [];
      }
      if (pop) {
        pop.classList.remove("is-open");
        if (this._chipPopLive) {
          pop.removeEventListener("input", this._chipPopLive);
          pop.removeEventListener("click", this._chipPopLive);
          this._chipPopLive = null;
        }
      }
      if (this._chipPopOutside) {
        document.removeEventListener("pointerdown", this._chipPopOutside, true);
        document.removeEventListener("keydown", this._chipPopKey, true);
        this._chipPopOutside = null;
        this._chipPopKey = null;
      }
    },

    // Each button shows its current value and only when its controls would
    // show; the row shows when any does. An open pop-up whose button went
    // away closes with it.
    _syncStyleChips: function() {
      var chips = this._chips;
      if (!chips) return;
      var any = false;
      var compact = this.stylePanel && this.stylePanel.getAttribute("data-size") === "compact";
      chips.forEach(function(chip) {
        var show = chip.rows.some(function(r) {
          return r.classList && !r.classList.contains("etcher-marker-row-head") && r.style.display !== "none";
        });
        if (chip.compactOnly && !compact) show = false;
        chip.el.style.display = show ? "" : "none";
        any = any || show;
        // The current value on top, what the button is for underneath — so
        // a first look reads "Line", "Fill", "Corners" without hovering.
        try {
          chip.el.innerHTML = '<span class="etcher-chip-face">' + chip.face() + '</span>' +
            (chip.caption ? '<span class="etcher-chip-caption">' + chip.caption + '</span>' : "");
        } catch (_) {}
      });
      if (this._chipsRow) this._chipsRow.style.display = any ? "" : "none";
      if (this._openChip && this._openChip.el.style.display === "none") this._closeChipPop();
    },

    // Shapes in the current selection that carry a label. Distinct from
    // `_paramsTargetShapes` — that one filters to the stroke kinds the
    // thickness / dash / fill controls apply to, and an image or a text
    // shape can be labelled without being one of those.
    _titleTargetShapes: function() {
      var shapes = [];
      if (this.selectedShapes && this.selectedShapes.length) {
        shapes = this.selectedShapes.slice();
      } else if (this.editingShape) {
        shapes = [this.editingShape];
      } else if (this.editingTitleShape) {
        shapes = [this.editingTitleShape];
      }
      // Placing a label inside its shape means nothing for text and
      // callouts (the words ARE the shape) or for a label riding its line.
      var self = this;
      return shapes.filter(function(s) {
        return s && s.metadata && String(s.metadata.title || "").trim() !== "" &&
          !self._isTextKind(s.kind) && !self._labelRidesShaft(s.kind);
      });
    },

    // Anchor every labelled shape in the selection. `axis` is "h" or "v"
    // (the other half is kept, defaulting to center/middle so one click
    // gives a sensible placement), or "free" to drop the alignment and
    // return the label to floating above the shape.
    _setTitleAlign: function(axis, value) {
      var self = this;
      var shapes = this._titleTargetShapes();
      if (!shapes.length) return;

      shapes.forEach(function(shape) {
        var before = self._snapshotShape(shape);
        var next;
        if (axis === "free") {
          next = null;
        } else {
          var cur = normalizeTitleAlign(shape.metadata && shape.metadata.title_align) ||
                    { h: "center", v: "middle" };
          next = { h: axis === "h" ? value : cur.h, v: axis === "v" ? value : cur.v };
        }
        shape.metadata = Object.assign({}, shape.metadata || {}, { title_align: next });
        self._renderShape(shape);
        // The resize handles sit on the label's corners, so they have to
        // follow it to its new anchor — same as during a drag. Without
        // this they stay where the label used to be until title-edit mode
        // is toggled off and back on.
        if (self.editingTitleShape === shape) self._positionAllTitleHandles(shape);
        if (shape.uuid) self._pushUndo(shape.uuid, before, self._snapshotShape(shape));
      });
      this._emitChanged();
      this._syncParamsPopup();
    },

    // The shapes the params popup edits: the multi-selection if there is one,
    // else the single shape in edit mode — filtered to the kinds that carry
    // stroke params (outline shapes, markers, and the shaft kinds).
    // Empty → the popup edits the global default for new shapes.
    // The FRESH shape: the one just drawn, still under the selection its
    // creation gave it. While that lasts, panel edits do double duty —
    // they restyle the shape AND become the tool's defaults, so the next
    // arrow/line/box comes out identical (head dev's flow: draw, tune,
    // keep drawing). The moment the user clicks off, freshness ends for
    // good: re-selecting the same shape later is a one-time edit that
    // leaves the defaults alone, exactly as selection edits always were.
    _freshTargets: function(targets) {
      var f = this._freshShape;
      if (!f) return false;
      if (!targets || targets.length !== 1 || targets[0] !== f) return false;
      if (this.editingShape === f) return true;
      if (this.selectedShapes && this.selectedShapes.length === 1 &&
          this.selectedShapes[0] === f) return true;
      if (this._textEditor && this._textEditor.shape === f) return true;
      return false;
    },

    _paramsTargetShapes: function() {
      var self = this;
      function eligible(s) {
        // Callouts are in: they are a leader, an underline and a dot, which
        // are strokes like any other and were silently editing the global
        // default instead of themselves. They are NOT a shaft kind — that
        // flag drives arrowheads and endpoint drags — so they are named
        // here rather than folded in there.
        return !!s && (s.kind === "marker" || s.kind === "callout" ||
                       self._isStrokeShape(s.kind) || self._isShaftKind(s.kind));
      }
      if (this.selectedShapes && this.selectedShapes.length) {
        return this.selectedShapes.filter(eligible);
      }
      if (eligible(this.editingShape)) return [this.editingShape];
      return [];
    },

    // The shape whose style the panel is showing: the selection's first
    // shape, else the edit-mode shape, else nothing (defaults).
    _inspectedShape: function() {
      // The shape whose inline text editor is open counts as inspected —
      // a palette press while styling a fresh text box must dress THAT
      // box, not repaint the authoring default under it.
      if (this._textEditor && this._textEditor.shape) {
        return this._textEditor.shape;
      }
      if (this.selectedShapes && this.selectedShapes.length) {
        return this.selectedShapes[0];
      }
      return this.editingShape || null;
    },

    // Inspector sync: while a shape is selected the panel SHOWS its style
    // — thickness, opacity, dash, fill through _syncParamsPopup's target
    // branch, its colour as the highlighted swatch (no highlight when the
    // colour matches no slot) — and falls back to the authoring defaults
    // the moment nothing is selected. Display only: the defaults survive
    // inspection untouched, so clicking around shapes to compare them
    // never changes what the next drawn shape looks like.
    _syncStyleInspector: function() {
      // Ahead of everything else: the swatches below are read (and the
      // matching one highlighted) straight after, so the row has to be the
      // right five first.
      this._syncPaletteForContext();
      this._syncParamsPopup();
      var shape = this._inspectedShape();
      var color = shape && shape.style && shape.style.color;
      var highlight = color
        ? (this._colorSlots || []).indexOf(color)
        : this._activeSlot;
      (this.swatchEls || []).forEach(function(el, i) {
        el.classList.toggle("is-selected", i === highlight);
      });
      if (this._pickerPreview) {
        this._pickerPreview.style.background = color || this.activeColor || "";
      }
    },

    _syncParamsPopup: function() {
      // Reflect the first target shape (the group shares one set of controls).
      var targets = this._paramsTargetShapes();
      var shape = targets[0];
      var width, opacity, dash, fill;
      if (shape) {
        var st = shape.style || {};
        // Markers store width in image px, and every canvas-anchored width
        // (stroke shapes, shafts) in canvas units — both zoom-anchored, both
        // shown on-screen. The slider otherwise displays the stored number
        // as px, which for a shape drawn zoomed-in can be off severalfold.
        var anchored = shape.kind === "marker" || st.width_units === "canvas";
        width = anchored
          ? Math.max(1, Math.round((st.width || 2) * this._inkScale()))
          : (st.width || 2);
        opacity = st.opacity == null ? 1 : st.opacity;
        dash = st.dash || "solid";
        fill = st.fill || "semi";
      } else {
        var lp = this.lineParams || {};
        width = lp.width || 2;
        opacity = lp.opacity == null ? 1 : lp.opacity;
        dash = lp.dash || "solid";
        fill = lp.fill || "semi";
      }
      if (this._paramsWeightInput) {
        this._paramsWeightInput.value = this._sliderFromWeight(width);
        this._paramsWeightVal.textContent = String(width);
      }
      if (this._paramsOpacityInput) {
        var pct = Math.round(opacity * 100);
        this._paramsOpacityInput.value = pct;
        this._paramsOpacityVal.textContent = pct + "%";
      }
      (this._paramsDashBtns || []).forEach(function(b) {
        b.classList.toggle("is-selected", b.dataset.dash === dash);
      });
      (this._paramsFillBtns || []).forEach(function(b) {
        b.classList.toggle("is-selected", b.dataset.fill === fill);
      });
      // "No targets" is two different situations, and the panel used to
      // conflate them. Nothing focused at all → the popup edits the global
      // authoring defaults, and every stroke row belongs. But a focus that
      // simply carries no stroke — a text shape, a focused label — used to
      // fall into the same branch, so clicking a label box made the fill
      // buttons (and sliders) appear for a thing that has neither fill nor
      // stroke, silently wired to the authoring defaults underneath.
      var strokeless = !targets.length &&
        !!((this.selectedShapes && this.selectedShapes.length) ||
           this.editingShape || this.editingTitleShape);
      [this._paramsWeightRow, this._paramsOpacityRow, this._paramsDashRow]
        .forEach(function(row) {
          if (row) row.style.display = strokeless ? "none" : "";
        });
      // A selection that cannot hold a fill (shafts, markers) hides the fill
      // row rather than offering buttons that would edit dead data. With no
      // targets AND nothing focused the popup edits the global default,
      // which IS fillable.
      if (this._paramsFillRow) {
        var self = this;
        var fillable = (!targets.length && !strokeless &&
            !this._armedInkTool()) ||
          targets.some(function(s) { return self._isStrokeShape(s.kind); });
        this._paramsFillRow.style.display = fillable ? "" : "none";
      }
      this._syncFontRow();
      this._syncLabelBgRow();
      this._syncCornerRows();
      this._syncTextboxAlign();
      this._syncLabelSection();
      this._refreshLabelSwatch();
      this._syncStyleChips();
      this._syncPanelSeps();
    },

    // A rule shows before a group only when that group is showing and
    // something above it is too — never a rule on its own, never two in a
    // row, never one at the top or the bottom.
    _syncPanelSeps: function() {
      var seps = this._panelSeps;
      if (!seps || !seps.length || !this.paramsPopup) return;
      // Rows folded into the setting buttons count as hidden.
      var shown = function(el) {
        if (el.classList && el.classList.contains("etcher-in-chips")) return false;
        return el.style.display !== "none";
      };
      var anyAbove = false, group = [], groups = [];
      var kids = this.paramsPopup.children;
      for (var i = 0; i < kids.length; i++) {
        if (seps.indexOf(kids[i]) >= 0) { groups.push({ items: group, sep: kids[i] }); group = []; }
        else group.push(kids[i]);
      }
      groups.push({ items: group, sep: null });
      for (var g = 0; g < groups.length; g++) {
        var vis = groups[g].items.some(shown);
        if (g > 0) groups[g - 1].sep.style.display = vis && anyAbove ? "" : "none";
        anyAbove = anyAbove || vis;
      }
    },

    // The text-in-the-box placement shows for text boxes only, lit for the
    // first one's alignment.
    _syncTextboxAlign: function() {
      if (!this._tbAlignSection) return;
      var self = this;
      var boxes = this._cornerTargetShapes().filter(function(s) { return s.kind === "text" && self._textboxFixed(s); });
      this._tbAlignSection.style.display = boxes.length ? "" : "none";
      if (!boxes.length) return;
      var al = this._textboxAlign(boxes[0]);
      (this._tbAlignBtns || []).forEach(function(b) {
        var parts = b.dataset.tbAlign.split(":");
        b.classList.toggle("is-selected", al[parts[0]] === parts[1]);
      });
    },

    _setTextboxAlign: function(axis, value) {
      var self = this;
      var boxes = this._cornerTargetShapes().filter(function(s) { return s.kind === "text" && self._textboxFixed(s); });
      boxes.forEach(function(shape) {
        var before = self._snapshotShape(shape);
        var al = self._textboxAlign(shape);
        al[axis] = value;
        shape.style = Object.assign({}, shape.style || {}, { text_align: al });
        if (self._textEditor && self._textEditor.shape === shape) self._refreshTextEditorStyle();
        else self._renderShape(shape);
        if (shape.uuid) self._pushUndo(shape.uuid, before, self._snapshotShape(shape));
      });
      if (boxes.length) this._emitChanged();
      this._syncTextboxAlign();
      this._syncStyleChips();
    },

    // Text boxes the corner controls act on: the one being typed into, the
    // selection's text boxes, or the one in edit mode.
    _cornerTargetShapes: function() {
      var self = this;
      var box = function(s) {
        return !!s && ((s.kind === "text" && self._textboxFixed(s)) ||
          self._isPointCallout(s) || self._isPointText(s));
      };
      if (this._textEditor && this._textEditor.shape) {
        return box(this._textEditor.shape) ? [this._textEditor.shape] : [];
      }
      if (this.selectedShapes && this.selectedShapes.length) return this.selectedShapes.filter(box);
      return box(this.editingShape) ? [this.editingShape] : [];
    },

    // With the text box tool armed and nothing focused, the controls set
    // the corners new boxes are drawn with.
    _cornerDefaultsShown: function() {
      return (this.activeTool === "textbox" || this.activeTool === "callout" ||
        this.activeTool === "text") && !this._textEditor &&
        !(this.selectedShapes && this.selectedShapes.length) &&
        !this.editingShape && !this.editingTitleShape;
    },

    // Text boxes, callouts and point text each remember their own corners.
    // `which` is a tool name ("textbox", "callout", "text") or a shape.
    _cornerPrefKey: function(which) {
      if (which && typeof which === "object") {
        which = which.kind === "callout" ? "callout"
          : (which.kind === "text" && this._textboxFixed(which)) ? "textbox" : "text";
      }
      return which === "callout" ? "callout_corner"
        : which === "text" ? "point_corner" : "textbox_corner";
    },

    _textboxCornerDefaults: function(which) {
      var key = this._cornerPrefKey(which || this.activeTool);
      var type = this._getPref(key);
      var px = this._getPref(key + "_size");
      return {
        corner: TEXTBOX_CORNERS.indexOf(type) >= 0 ? type : "round",
        size: typeof px === "number" && px > 0 ? px : 0
      };
    },

    _syncCornerRows: function() {
      if (!this._paramsCornerRow) return;
      var targets = this._cornerTargetShapes();
      var show = targets.length > 0 || this._cornerDefaultsShown();
      this._paramsCornerRow.style.display = show ? "" : "none";
      if (this._paramsCornerTypeRow) this._paramsCornerTypeRow.style.display = show ? "" : "none";
      if (!show) return;
      var type, px;
      if (targets.length) {
        var st = targets[0].style || {};
        type = TEXTBOX_CORNERS.indexOf(st.corner) >= 0 ? st.corner : "round";
        px = st.corner_size > 0 ? Math.round(st.corner_size * this._inkScale()) : 0;
      } else {
        var d = this._textboxCornerDefaults();
        type = d.corner; px = Math.round(d.size);
      }
      px = Math.min(TEXTBOX_CORNER_MAX, px);
      this._paramsCornerInput.style.display = px > 0 ? "" : "none";
      this._paramsCornerInput.value = String(px);
      this._paramsCornerVal.textContent = px ? String(px) : "Sharp";
      // Sharp lights when there are no corners; a type only once they reach.
      var shown = px > 0 ? type : "sharp";
      (this._paramsCornerBtns || []).forEach(function(b) {
        b.classList.toggle("is-selected", b.dataset.corner === shown);
      });
    },

    // Set a text box's corner type ("corner") or reach ("corner_size", in
    // on-screen px, stored in canvas units) — live while a slider moves,
    // one undo step per shape on release. With no text box in focus it
    // sets the default for new ones instead.
    _setTextboxCorner: function(prop, value, commit) {
      var self = this;
      var shapes = this._cornerTargetShapes();
      var key = this._cornerPrefKey(shapes.length ? shapes[0] : this.activeTool);
      if (!shapes.length) {
        // Saved once, on release — not on every step of the slider.
        if (prop === "corner") {
          this._setPref(key, value);
          if (!(this._textboxCornerDefaults().size > 0)) this._setPref(key + "_size", TEXTBOX_CORNER_PICK);
        }
        else if (commit) this._setPref(key + "_size", value);
        if (prop === "corner" || commit) this._syncCornerRows();
        return;
      }
      if (!this._cornerBefore) {
        this._cornerBefore = shapes.map(function(s) {
          return { uuid: s.uuid, before: self._snapshotShape(s) };
        });
      }
      shapes.forEach(function(shape) {
        var patch = {};
        if (prop === "corner") {
          patch.corner = value;
          // Picking a type on a sharp box gives it corners to see.
          if (!(shape.style && shape.style.corner_size > 0)) {
            patch.corner_size = TEXTBOX_CORNER_PICK / self._inkScale();
          }
        } else {
          patch.corner_size = value > 0 ? value / self._inkScale() : 0;
        }
        shape.style = Object.assign({}, shape.style || {}, patch);
        if (self._textEditor && self._textEditor.shape === shape) self._refreshTextEditorStyle();
        else self._renderShape(shape);
      });
      // What a fresh box is tuned to, the next one gets.
      if (commit && this._freshTargets && this._freshTargets(shapes)) {
        if (prop === "corner") this._setPref(key, value);
        else this._setPref(key + "_size", value);
      }
      if (prop === "corner") this._syncCornerRows();
      if (commit) {
        this._emitChanged();
        (this._cornerBefore || []).forEach(function(rec) {
          if (!rec.uuid) return;
          var shape = self.shapes.find(function(s) { return s.uuid === rec.uuid; });
          if (shape) self._pushUndo(rec.uuid, rec.before, self._snapshotShape(shape));
        });
        this._cornerBefore = null;
      }
    },

    // The font row shows when there is text for it to act on — the shapes
    // that draw some, or nothing selected at all, which is the case where it
    // sets what NEW labels start at. A rectangle with no label hides it:
    // a control that cannot do anything is worse than a missing one.
    _syncFontRow: function() {
      if (!this._paramsFontRow) return;
      var targets = this._fontTargetShapes();
      var global = !targets.length && !this._paramsTargetShapes().length &&
        !this._armedInkTool();
      this._paramsFontRow.style.display = (targets.length || global) ? "" : "none";
      if (!targets.length && !global) return;

      var px = null;
      if (targets.length) {
        var first = targets[0];
        if (this._hasPinnedFontSize(first)) {
          px = Math.round(first.style.font_size * this._inkScale());
        } else {
          // No pin — show the size the label is ACTUALLY rendering at,
          // in the panel's own unit (rendered container px normalised by
          // the ink scale, so the number matches what typing it would
          // pin). "custom"/blank told the user nothing; the label HAS a
          // size, and this is it. Read off the rendered <text>; a label
          // still being born (editor open, nothing painted) falls back
          // to its editor's font, and only then to blank.
          var host = this._textEditHost(first);
          var tEl = host && host.querySelector && host.querySelector("text");
          var attr = tEl && parseFloat(tEl.getAttribute("font-size"));
          if (!(attr > 0) && this._textEditor &&
              this._textEditor.shape === first) {
            attr = parseFloat(this._textEditor.input.style.fontSize);
          }
          if (attr > 0) {
            var scale = 1;
            try { scale = this._markerScale() || 1; } catch (_) { scale = 1; }
            px = Math.round(attr / scale * this._inkScale());
          }
        }
      } else {
        // The default is a real size now — show the number a new label
        // will actually get, chosen or built-in, instead of a blank that
        // told the user nothing.
        px = Math.round(this._defaultLabelFontSize());
      }

      if (this._paramsFontNum) {
        // Empty, not zero: the box says "custom" through its placeholder,
        // and a 0 in it would read as a real size that happens to be
        // invalid — one the user could then commit by pressing a spinner.
        this._paramsFontNum.value = px == null ? "" : String(px);
      }
    },

    // The toggle shows wherever the size does — they are two settings for
    // the same thing, and a panel that offers one without the other reads
    // as a missing control rather than a deliberate one.
    _syncLabelBgRow: function() {
      if (!this._paramsBgRow) return;
      var targets = this._fontTargetShapes();
      var global = !targets.length && !this._paramsTargetShapes().length &&
        !this._armedInkTool();
      this._paramsBgRow.style.display = (targets.length || global) ? "" : "none";
      // Before the early return below: the swatches live in the colour row,
      // not in this one, so they need repainting even when this row is
      // hidden — a shape with no text still changes what "the current
      // label" means.
      this._refreshLabelSwatch();
      if (!this._paramsBgBtn) return;
      var on = !!this._currentLabelBg();
      this._paramsBgBtn.textContent = on ? "On" : "Off";
      this._paramsBgBtn.classList.toggle("is-on", on);
      this._paramsBgBtn.setAttribute("aria-pressed", on ? "true" : "false");
      this._paramsBgBtn.title = on
        ? "Label background on — pick its colour beside the label colour"
        : "Label background off — labels sit straight on the image";
    },

    // Show the label controls only when there's a label to place, and mark
    // the anchor the selection is currently using. A mixed selection marks
    // nothing rather than picking a winner — the buttons still apply to all
    // of it, so a stale highlight would misreport what's about to change.
    _syncLabelSection: function() {
      if (!this._labelSection) return;
      var shapes = this._titleTargetShapes();
      this._labelSection.style.display = shapes.length ? "" : "none";
      if (!shapes.length) return;

      var first = normalizeTitleAlign(shapes[0].metadata.title_align);
      var mixed = shapes.some(function(s) {
        var a = normalizeTitleAlign(s.metadata.title_align);
        if (!a || !first) return !!a !== !!first;
        return a.h !== first.h || a.v !== first.v;
      });
      (this._titleAlignBtns || []).forEach(function(b) {
        var parts = b.dataset.titleAlign.split(":");
        b.classList.toggle(
          "is-selected",
          !mixed && !!first && first[parts[0]] === parts[1]
        );
      });
      if (this._titleFreeBtn) {
        this._titleFreeBtn.classList.toggle("is-selected", !mixed && !first);
      }
    },

    // Set one line param. When shapes are selected (single edit-mode shape OR
    // a box/shift multi-selection), edits all of them live — committed with a
    // per-shape undo entry on slider release / dash click. Otherwise edits
    // `lineParams`, the default for new shapes.
    _setLineParam: function(prop, value, commit) {
      var self = this;
      var shapes = this._paramsTargetShapes();
      if (shapes.length) {
        if (!this._lineParamBefore) {
          this._lineParamBefore = shapes.map(function(s) {
            return { uuid: s.uuid, before: self._snapshotShape(s) };
          });
        }
        shapes.forEach(function(shape) {
          shape.style = Object.assign({}, shape.style || {});
          // Slider thickness is on-screen px; shapes store canvas units.
          if (prop === "width" &&
              (shape.kind === "marker" || shape.kind === "callout" ||
               self._isStrokeShape(shape.kind) || self._isShaftKind(shape.kind))) {
            shape.style.width = value / self._inkScale();
            if (shape.kind !== "marker") shape.style.width_units = "canvas";
          } else {
            shape.style[prop] = value;
          }
          // Markers and shafts restyle inside `_renderShape` (a shaft's
          // arrowheads are sized off the stroke, and only the render case
          // has the endpoints); outline shapes take the params directly.
          if (shape.kind === "marker" || shape.kind === "callout" ||
              self._isShaftKind(shape.kind)) {
            self._renderShape(shape);
          } else {
            self._applyLineParams(shape.el, shape.style, self._markerScale());
          }
        });
        // A FRESH shape's tuning is also the tool's tuning: the user just
        // drew it and is dialling it in — the next one should come out
        // the same (see _freshTargets). A re-selected shape edits alone.
        if (this._freshTargets(shapes)) {
          this.lineParams = this.lineParams || {};
          this.lineParams[prop] = value;
          this._restyleDrafts();
          this._lineParamFreshMirror = true;
        }
      } else {
        this.lineParams = this.lineParams || {};
        this.lineParams[prop] = value;
        // Anything being drawn right now is drawn with these, so it changes
        // under the slider rather than waiting for the release.
        this._restyleDrafts();
      }
      // While an ink tool is armed the thickness slider is standing in for
      // THAT tool's weight — remember it under the tool, exactly as
      // _selectColor remembers the colour, so the next time the
      // highlighter comes up it comes up as a highlighter. Only the
      // weight: opacity, dash and fill stay shared, which is what makes
      // the highlighter's half-opacity a property of the tool rather than
      // something the user has to keep re-picking.
      if (prop === "width" && this._armedInkTool()) {
        this._setPref(this.activeTool + "_width", value);
      }
      if (commit) {
        if (this._lineParamBefore && this._lineParamBefore.length) {
          // Edited selected shapes → per-shape style persists via the
          // annotations-changed round-trip; not a global-default change.
          this._emitChanged();
          this._lineParamBefore.forEach(function(rec) {
            if (!rec.uuid) return;
            var shape = self.shapes.find(function(s) { return s.uuid === rec.uuid; });
            if (shape) self._pushUndo(rec.uuid, rec.before, self._snapshotShape(shape));
          });
          // The fresh mirror changed the global default too — persist it
          // the same way a nothing-selected edit would.
          if (this._lineParamFreshMirror) this._emitLineParamsChanged();
        } else {
          // No shapes targeted → the popup edited the GLOBAL default. Notify
          // the consumer so per-user line params can be persisted (mirror of
          // the `etcher:colors-changed` hook).
          this._emitLineParamsChanged();
        }
        this._lineParamBefore = null;
        this._lineParamFreshMirror = false;
      }
    },

    // Seed `lineParams` from the host's `data-line-params` JSON at mount
    // (set by the `:line_params` attr). Missing / invalid → built-in defaults.
    _seedLineParams: function() {
      try {
        var raw = this.el && this.el.dataset && this.el.dataset.lineParams;
        if (!raw) return;
        var parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") this._setLineParamsDirect(parsed);
      } catch (_) { /* malformed attr → built-in defaults */ }
    },

    // Apply a line-params map key-by-key with the same clamp / validation the
    // popup uses, skipping invalid keys (they fall back to the built-in
    // default). Does NOT fire `etcher:line-params-changed` — used by the seed
    // path and the programmatic `setLineParams`, which the consumer owns.
    _setLineParamsDirect: function(map) {
      if (!map || typeof map !== "object") return;
      this.lineParams = this.lineParams || {};
      if (typeof map.width === "number" && isFinite(map.width)) {
        this.lineParams.width = Math.max(1, Math.min(40, map.width));
      }
      if (typeof map.opacity === "number" && isFinite(map.opacity)) {
        this.lineParams.opacity = Math.max(0, Math.min(1, map.opacity));
      }
      if (DASH_MODES.indexOf(map.dash) !== -1) {
        this.lineParams.dash = map.dash;
      }
      if (FILL_MODES.indexOf(map.fill) !== -1) {
        this.lineParams.fill = map.fill;
      }
      // A seeded font size is a pinned default for new labels; anything
      // that isn't a usable number leaves labels sized by their box, which
      // is what every board did before there was a control for it.
      if (typeof map.font_size === "number" && isFinite(map.font_size) &&
          map.font_size > 0) {
        this.lineParams.font_size =
          Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, map.font_size));
      }
      // Reflect into an open popup if it's showing the global default.
      if (this.paramsPopup && this.paramsPopup.classList.contains("is-open")) {
        this._syncParamsPopup();
      }
    },

    // Persistence hook for the global stroke defaults — the `_emitColors-
    // Changed` analogue. Fires on every committed global-default edit (slider
    // release / dash click with no shape selected). Two channels (LiveView
    // push + bubbling DOM event); Etcher stores nothing itself. Payload uses
    // the resolved values (built-in fallback per missing key).
    _emitLineParamsChanged: function() {
      var lp = this._currentLineParams();
      var payload = {
        width: lp.width, opacity: lp.opacity, dash: lp.dash, fill: lp.fill
      };
      // Only when one is pinned. Its ABSENCE is the "size labels by their
      // box" default, and the host replaces this map wholesale rather than
      // merging into the last one — so leaving the key out is how clearing
      // it travels, and writing a null would make "auto" a value the host
      // has to know about.
      if (lp.font_size) payload.font_size = lp.font_size;
      if (this.pushEventTo) {
        this.pushEventTo(this.el, "etcher:line-params-changed", {
          fresco_id: this.frescoId || null,
          line_params: payload
        });
      }
      this._dispatch("etcher:line-params-changed", { line_params: payload });
    },

    // Paint the hue ring once. The torus is drawn pixel-by-pixel via
    // `ImageData` so each ring pixel maps to its angle's hue at full
    // saturation + 50% lightness. A 1-pixel alpha falloff at both
    // edges softens the otherwise-aliased ring boundary.
    // Move the hue-ring + lightness knobs to match the active slot's
    // color. Only repositions for custom (non-preset) colors — the
    // picker's saturation is locked at 100, so presets aren't faithfully
    // representable and the knobs are left where the user last put them.
    // The preview chip is owned by `_selectColor` (shows the real active
    // color), so we don't touch it here.
    _syncPickerToActiveColor: function() {
      if (!this._pickerRing) return;
      // While inspecting, the picker edits the inspected shape, so start
      // the knobs on ITS colour, not the drawing default.
      var inspected = this._inspectedShape();
      var color = (inspected && inspected.style && inspected.style.color) ||
        this.activeColor;
      var presets = this._presetColors || [];
      if (!color || presets.indexOf(color) !== -1) return;
      if (!/^#[0-9a-f]{6}$/i.test(color)) return;
      var hsl = hexToHsl(color);
      this._pickerHue = hsl.h;
      this._pickerLightness = hsl.l;
      this._drawLightnessSlider();
      this._positionPickerKnobs();
    },

    _drawHueRing: function() {
      var canvas = this._pickerRing;
      if (!canvas) return;
      var size = this._pickerRingSize;
      var outer = this._pickerRingOuter;
      var inner = this._pickerRingInner;
      var ctx = canvas.getContext("2d");
      var img = ctx.createImageData(size, size);
      var data = img.data;
      var cx = size / 2, cy = size / 2;
      for (var y = 0; y < size; y++) {
        for (var x = 0; x < size; x++) {
          var dx = x - cx, dy = y - cy;
          var r = Math.sqrt(dx * dx + dy * dy);
          if (r > outer || r < inner - 1) continue;
          // 0° at the top so the wheel reads like a clock.
          var ang = Math.atan2(dy, dx) * 180 / Math.PI + 90;
          if (ang < 0) ang += 360;
          var rgb = hslToRgb(ang, 100, 50);
          var alpha = 255;
          if (r > outer - 1) alpha = (outer - r) * 255;
          else if (r < inner) alpha = (r - (inner - 1)) * 255;
          if (alpha < 0) alpha = 0;
          if (alpha > 255) alpha = 255;
          var idx = (y * size + x) * 4;
          data[idx] = rgb[0];
          data[idx + 1] = rgb[1];
          data[idx + 2] = rgb[2];
          data[idx + 3] = alpha;
        }
      }
      ctx.clearRect(0, 0, size, size);
      ctx.putImageData(img, 0, 0);
    },

    // Repaint the lightness slider for the current hue. Cheap (one
    // canvas gradient fill) so we redraw it every time the user
    // moves the ring knob, keeping the slider visually anchored to
    // the actual hue they're tuning.
    _drawLightnessSlider: function() {
      var canvas = this._pickerSlider;
      if (!canvas) return;
      var w = canvas.width, h = canvas.height;
      var ctx = canvas.getContext("2d");
      var grad = ctx.createLinearGradient(0, 0, w, 0);
      grad.addColorStop(0, "hsl(" + this._pickerHue + ", 100%, 0%)");
      grad.addColorStop(0.5, "hsl(" + this._pickerHue + ", 100%, 50%)");
      grad.addColorStop(1, "hsl(" + this._pickerHue + ", 100%, 100%)");
      // Rounded corners via clip — matches the CSS border-radius so
      // the gradient doesn't leak past the slider's visible bounds.
      ctx.clearRect(0, 0, w, h);
      if (typeof ctx.roundRect === "function") {
        ctx.beginPath();
        ctx.roundRect(0, 0, w, h, h / 2);
        ctx.fillStyle = grad;
        ctx.fill();
      } else {
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
      }
    },

    _positionPickerKnobs: function() {
      var size = this._pickerRingSize;
      var cx = size / 2, cy = size / 2;
      // Midline radius of the ring band — knob hugs the center of
      // the colored donut.
      var midR = (this._pickerRingOuter + this._pickerRingInner) / 2;
      var ang = (this._pickerHue - 90) * Math.PI / 180;
      var kx = cx + midR * Math.cos(ang);
      var ky = cy + midR * Math.sin(ang);
      if (this._pickerRingKnob) {
        this._pickerRingKnob.style.left = kx + "px";
        this._pickerRingKnob.style.top = ky + "px";
      }
      if (this._pickerSliderKnob) {
        var w = this._pickerSlider.width;
        var x = (this._pickerLightness / 100) * w;
        this._pickerSliderKnob.style.left = x + "px";
      }
    },

    _updatePickerPreview: function() {
      if (!this._pickerPreview) return;
      this._pickerPreview.style.background =
        hslToHex(this._pickerHue, 100, this._pickerLightness);
    },

    // Pointer wiring for both the hue ring and the lightness slider.
    // Both support press + drag (the user can scrub continuously
    // without lifting their finger); on every move we overwrite the
    // selected slot's color and `_selectColor` immediately so the
    // in-flight draft / edit updates live. `pointerup` commits the
    // edited slot via `_emitColorsChanged` (the persistence hook).
    _wirePickerInput: function() {
      var self = this;
      var ring = self._pickerRing;
      var slider = self._pickerSlider;
      if (!ring || !slider) return;

      function ringEvent(e) {
        var rect = ring.getBoundingClientRect();
        var x = e.clientX - rect.left - rect.width / 2;
        var y = e.clientY - rect.top - rect.height / 2;
        var ang = Math.atan2(y, x) * 180 / Math.PI + 90;
        if (ang < 0) ang += 360;
        self._pickerHue = ang;
        self._drawLightnessSlider();
        self._positionPickerKnobs();
        var hex = hslToHex(self._pickerHue, 100, self._pickerLightness);
        self._updatePickerPreview();
        self._applyPickedColor(hex);
      }

      function sliderEvent(e) {
        var rect = slider.getBoundingClientRect();
        var t = (e.clientX - rect.left) / rect.width;
        if (t < 0) t = 0;
        if (t > 1) t = 1;
        self._pickerLightness = t * 100;
        self._positionPickerKnobs();
        var hex = hslToHex(self._pickerHue, 100, self._pickerLightness);
        self._updatePickerPreview();
        self._applyPickedColor(hex);
      }

      function attachDrag(el, onMove) {
        el.addEventListener("pointerdown", function(e) {
          e.preventDefault();
          e.stopPropagation();
          try { el.setPointerCapture(e.pointerId); } catch (_) {}
          onMove(e);
          function move(ev) { onMove(ev); }
          function up(ev) {
            el.removeEventListener("pointermove", move);
            el.removeEventListener("pointerup", up);
            el.removeEventListener("pointercancel", up);
            try { el.releasePointerCapture(ev.pointerId); } catch (_) {}
            // Commit the edited slot on release — persist via the hook.
            // (A label-swatch pick persists through the prefs mechanism
            // inside _applyPickedColor; the palette didn't change.)
            if (self._pickEditsPalette()) self._emitColorsChanged();
          }
          el.addEventListener("pointermove", move);
          el.addEventListener("pointerup", up);
          el.addEventListener("pointercancel", up);
        });
      }

      attachDrag(ring, ringEvent);
      attachDrag(slider, sliderEvent);
    },

    // -------------------------------------------------------------------------
    // Color slots
    //
    // The toolbar carries `COLOR_SLOTS` fixed swatches. Clicking one
    // selects it (sets the active draw color); editing in the hue picker
    // overwrites the selected slot's color in place. The palette is
    // seeded per-layer and persisted by the consumer through the
    // `etcher:colors-changed` hook — no localStorage, no MRU reordering,
    // so a slot stays put once a user customizes it.
    // -------------------------------------------------------------------------

    // Coerce an arbitrary list into exactly COLOR_SLOTS valid `#rrggbb`
    // entries, backfilling short / invalid lists from the preset palette
    // so the row is always full. Returns lowercased hex strings.
    _sanitizeColorSlots: function(list) {
      var presets = resolveColorSwatches().map(function(s) { return s.color; });
      var out = [];
      function valid(c) { return typeof c === "string" && /^#[0-9a-f]{6}$/i.test(c); }
      (Array.isArray(list) ? list : []).forEach(function(c) {
        if (out.length < COLOR_SLOTS && valid(c)) out.push(c.toLowerCase());
      });
      for (var i = 0; out.length < COLOR_SLOTS && i < presets.length; i++) {
        if (valid(presets[i])) out.push(presets[i].toLowerCase());
      }
      // Last resort if the presets were somehow unusable.
      while (out.length < COLOR_SLOTS) out.push("#000000");
      return out;
    },

    // Seed `_colorSlots` + `_activeSlot`. Priority: the layer's
    // `data-colors` attr → the viewer's `extensions.etcher.colors` (the
    // same JSON annotations ride in) → the preset palette. The starting
    // selection prefers a slot matching `window.Etcher.defaultColor`,
    // else slot 0. Idempotent at mount; never re-run after a user edit.
    _seedColorSlots: function() {
      var seed = null;
      try {
        var raw = this.el && this.el.dataset && this.el.dataset.colors;
        if (raw) {
          var parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length) seed = parsed;
        }
      } catch (_) { /* malformed attr → fall through */ }

      if (!seed && this.handle && typeof this.handle.getExtension === "function") {
        try {
          var ext = this.handle.getExtension("etcher");
          if (ext && Array.isArray(ext.colors) && ext.colors.length) seed = ext.colors;
        } catch (_) { /* no extension → fall through */ }
      }

      this._colorSlots = this._sanitizeColorSlots(seed);

      var def = window.Etcher && typeof window.Etcher.defaultColor === "string"
        ? window.Etcher.defaultColor.toLowerCase()
        : null;
      var idx = def ? this._colorSlots.indexOf(def) : -1;
      this._activeSlot = idx === -1 ? 0 : idx;
      this.activeColor = this._colorSlots[this._activeSlot];
    },

    // Select slot `i` — make it the active draw color. Pure selection:
    // no palette mutation and no persist (editing the hue picker is what
    // mutates a slot). Clamps out-of-range indices defensively.
    // HISTORY: this used to EYEDROP the clicked shape's colour into the
    // active slot — the palette and the colour new shapes draw with
    // changed just by selecting something, and deselecting did not bring
    // the old colour back. Inspection is display-only now; edits go
    // through _applyColorToTargets, which leaves the defaults alone.
    _syncToolbarColorToShape: function(_shape) {
      this._syncStyleInspector();
    },

    // Toggle the hue picker (colors popup) anchored to a swatch — the new
    // entry point for editing a slot's color now that the palette button is
    // the line-parameters button.
    _openColorsForSwatch: function(i, swatchEl) {
      if (this._openPopupKind === "colors") {
        this._closePopup();
        return;
      }
      // WHICH slot the wheel is editing. `_activeSlot` is not the same
      // question: with a shape selected the palette selection stays put
      // (a swatch click recolours the shape), so the slot you opened the
      // wheel from can be any of the five. Cleared by `_closePopup`.
      this._slotPickTarget = typeof i === "number" ? i : null;
      this._colorsTrigger = swatchEl;
      this._openPopup("colors");
    },

    // Where a picker pick lands: the active slot (the normal case), or —
    // while the picker was opened from the label swatch — the label_color
    // pref, so the swatch edits "what colour new labels start in" without
    // touching the palette.
    _applyPickedColor: function(hex) {
      // The plate behind labels. Ahead of the label-colour branch because
      // both are "label" targets and only one can be armed at a time.
      if (this._labelBgPickTarget) {
        this._setLabelBg(hex, true);
        return;
      }
      if (this._labelPickTarget) {
        this._setLabelColor(hex);
        return;
      }
      // Inspecting a shape: the pick recolors IT, and the palette stays as
      // it was — deselect and the colours you were drawing with are still
      // there (the display half lives in _syncStyleInspector). Unless the
      // wheel was opened from a slot, which is the user saying "change this
      // colour": then the slot takes the pick too. Both, not either — the
      // wheel opens from the slot the shape already matches, so leaving
      // them different is the surprise.
      if (this._inspectedShape()) {
        if (this._slotPickTarget != null) this._setSlotColor(this._slotPickTarget, hex);
        this._applyColorToTargets(hex);
        this._syncStyleInspector();
        return;
      }
      // The wheel edits the slot it was opened from, ink tool armed or
      // not — five swatches that silently stop taking edits while the
      // marker is up read as broken. (While armed, _selectColor also
      // stores the pick as the tool's colour.) With the tool in a colour
      // outside the palette no slot is active (-1), and _setSlotColor's
      // own bounds check makes the pick a pure tool recolour.
      this._setSlotColor(
        this._slotPickTarget != null ? this._slotPickTarget : this._activeSlot, hex);
      this._selectColor(hex);
    },

    // Does the pick in flight change the palette? A label swatch aims the
    // wheel somewhere else entirely, so no. A selected shape normally means
    // no either — a swatch click recolours the shape and leaves the colours
    // you were drawing with alone. But opening the wheel FROM a slot (the
    // second click on it) is a deliberate "edit this colour", and it stays
    // that whether or not something is selected: the slot you aimed at
    // keeping its old colour is the bug this answers.
    _pickEditsPalette: function() {
      if (this._labelPickTarget || this._labelBgPickTarget) return false;
      if (this._slotPickTarget != null) return true;
      return !this._inspectedShape();
    },

    _refreshLabelSwatch: function() {
      // Whose settings these are. Clicking from one label to another has to
      // move both chips with it, or the row goes on describing the last
      // label you touched — which reads as the SELECTED one, and is the
      // worst kind of wrong: confidently.
      var onLabel = this._fontTargetShapes().length > 0;
      // With a pure-ink tool armed and no label in focus, the chips
      // describe nothing the next stroke can have — hide the row with
      // the other label controls.
      // A shape in focus with no words on it has no label colours to set
      // either; they come back with nothing focused (the defaults).
      var focused = !!(this._textEditor || this.editingShape || this.editingTitleShape ||
        (this.selectedShapes && this.selectedShapes.length));
      if (this._labelChipsRow) {
        this._labelChipsRow.style.display =
          onLabel || (!focused && !this._armedInkTool()) ? "" : "none";
      }
      var color = this._currentLabelColor();
      var forWhat = onLabel ? "this label" : "new labels";

      var el = this.labelSwatchEl;
      if (el) {
        var glyph = el.querySelector(".etcher-label-swatch-glyph");
        if (glyph) glyph.style.color = color || "#ffffff";
        el.title = color
          ? "Label text color (" + forWhat + "): " + color
          : "Label text color — pick one for " + forWhat;
        el.setAttribute("aria-label", el.title);
      }

      var bgEl = this.labelBgSwatchEl;
      if (!bgEl) return;
      // The chip IS the preview: the label's own colour, written on the
      // plate colour, which is the only way to see whether the pair works.
      //
      // OFF shows nothing behind the glyph rather than the colour it would
      // use — the whole question the chip answers is "is there a plate", and
      // showing the remembered colour answers it wrong. Toggling on is what
      // reveals the colour.
      var bg = this._currentLabelBg();
      var bgChip = bgEl.querySelector(".etcher-label-bg-chip");
      var bgGlyph = bgEl.querySelector(".etcher-label-swatch-glyph");
      if (bgChip) {
        bgChip.style.background = bg || "transparent";
        bgChip.classList.toggle("is-off", !bg);
      }
      if (bgGlyph) bgGlyph.style.color = color || "#ffffff";
      bgEl.title = bg
        ? "Label background (" + forWhat + "): " + bg
        : "Label background — off for " + forWhat +
          "; click to pick one";
      bgEl.setAttribute("aria-label", bgEl.title);
    },

    _selectSlot: function(i) {
      if (!this._colorSlots || !this._colorSlots.length) return;
      if (i < 0 || i >= this._colorSlots.length) i = 0;
      this._activeSlot = i;
      this._selectColor(this._colorSlots[i]);
    },

    // Overwrite slot `i`'s color and repaint just that swatch. Used by
    // the hue ring / slider (on the active slot, as the user drags) and
    // by the programmatic API. Does not persist — `_emitColorsChanged`
    // fires once on commit (pointer-release).
    _setSlotColor: function(i, hex) {
      if (typeof hex !== "string" || !/^#[0-9a-f]{6}$/i.test(hex)) return;
      if (!this._colorSlots || i < 0 || i >= this._colorSlots.length) return;
      hex = hex.toLowerCase();
      this._colorSlots[i] = hex;
      var btn = (this.swatchEls || [])[i];
      if (btn) {
        btn.dataset.color = hex;
        btn.title = hex;
        btn.style.background = hex;
        btn.setAttribute("aria-label", "Color slot " + (i + 1) + ": " + hex);
      }
      if (i === this._activeSlot) this.activeColor = hex;
      // Saved with the rest of this user's setup, so a palette edited on one
      // board is the palette on the next one. `_emitColorsChanged` below is
      // the older, colours-only channel and stays for hosts wired to it;
      // this is the one that rides the preferences hook.
      this._setPref(this._paletteKey(), this._colorSlots.slice());
    },

    // Generic persistence hook for the color palette — fires on every
    // committed slot edit. Two channels (mirrors `etcher:annotations-
    // changed` + the lifecycle CustomEvents) so consumers can persist
    // server-side (LiveView `handle_event` → DB / user meta) or in pure
    // JS. Etcher stores nothing itself.
    _emitColorsChanged: function() {
      // While an ink tool is armed the visible slots are the TOOL's
      // palette. This colours-only channel predates per-tool palettes and
      // hosts wired to it store whatever arrives as the shared palette —
      // the prefs channel (marker_colors / highlighter_colors) already
      // persists ink palettes, so this one stays shared-only.
      if (this._paletteKey() !== "colors") return;
      var colors = (this._colorSlots || []).slice();
      if (this.pushEventTo) {
        this.pushEventTo(this.el, "etcher:colors-changed", {
          fresco_id: this.frescoId || null,
          colors: colors
        });
      }
      this._dispatch("etcher:colors-changed", { colors: colors });
    },

    // Rebuild the toolbar's inline swatch row from `_colorSlots`. Stable
    // order (slot index = position); the selected slot gets
    // `.is-selected`. Seeds the palette on first call. Rebuilt rather
    // than diffed — the row is at most COLOR_SLOTS items and rebuilds are
    // user-driven (slot picks / edits), not per-frame.
    _refreshToolbarSwatches: function() {
      var self = this;
      var host = self.swatchHost;
      if (!host) return;
      if (!self._colorSlots || !self._colorSlots.length) self._seedColorSlots();

      // Swatches live in the style panel now, so the host owns nothing else
      // and can simply be emptied. (Popup swatches live inside
      // `.etcher-popup` and are untouched by this.)
      while (host.firstChild) host.removeChild(host.firstChild);

      self.swatchEls = self._colorSlots.map(function(color, i) {
        var b = document.createElement("button");
        b.type = "button";
        b.className = "etcher-swatch";
        b.dataset.color = color;
        b.dataset.slot = String(i);
        b.title = color;
        b.setAttribute("aria-label", "Color slot " + (i + 1) + ": " + color);
        b.style.background = color;
        if (i === self._activeSlot) b.classList.add("is-selected");
        b.addEventListener("click", function(e) {
          e.preventDefault();
          // Inspecting a shape: a swatch click recolors the SELECTION and
          // the palette/active slot stay put — clicking the slot whose
          // colour the shape already has opens the picker to fine-tune it
          // (the picker edits the shape too while inspecting).
          var inspected = self._inspectedShape();
          if (inspected) {
            var slotColor = self._colorSlots[i];
            if (inspected.style && inspected.style.color === slotColor) {
              self._openColorsForSwatch(i, b);
            } else {
              self._closePopup();
              self._applyColorToTargets(slotColor);
              self._syncStyleInspector();
            }
            return;
          }
          // The picker is open → it follows the clicks. Clicking another
          // slot re-aims the wheel at that slot instead of dismissing the
          // picker, which is what the overflowed-slots row inside the popup
          // has always done. Without it, the click that should have moved
          // the picker only closed it, so editing a second colour took
          // another two clicks — and a drag on the wheel in between
          // silently went nowhere, because there was no wheel any more.
          // Clicking the slot the picker is already on still closes it
          // (the toggle below), but a picker aimed at a label colour is
          // re-aimed rather than closed: that IS a different target.
          var colorsOpen = self._openPopupKind === "colors";
          var onLabel = colorsOpen && (self._labelPickTarget || self._labelBgPickTarget);
          if (colorsOpen && (onLabel || self._activeSlot !== i)) {
            self._labelPickTarget = false;
            self._labelBgPickTarget = false;
            self._slotPickTarget = i;
            self._selectSlot(i);
            self._syncPickerToActiveColor();
            return;
          }
          // Click an inactive swatch → select it. Click the already-active
          // swatch again (or double-tap any swatch, whose 2nd click lands on
          // the now-active one) → open the hue picker to edit that color.
          if (self._activeSlot === i) {
            self._openColorsForSwatch(i, b);
          } else {
            self._closePopup();
            self._selectSlot(i);
          }
        });
        host.appendChild(b);
        return b;
      });

      // The label swatch — bottom of the color menu. Shows the colour new
      // labels start in (the label_color pref _commitTextEdit stamps at
      // creation) and opens the picker targeted at it. An "A" glyph in the
      // remembered colour, not a filled tile: it is a text-colour control,
      // and reading it as a sixth palette slot would be the wrong idea.
      // Two halves of one row: the colour the label is written in, and the
      // colour it is written ON. Side by side because that is how you judge
      // them — contrast is a property of the pair, not of either one.
      var labelRow = document.createElement("div");
      labelRow.className = "etcher-label-row";
      self._labelChipsRow = labelRow;

      var lb = document.createElement("button");
      lb.type = "button";
      lb.className = "etcher-label-swatch";
      lb.innerHTML =
        '<span class="etcher-label-swatch-chip">' +
        '<span class="etcher-label-swatch-glyph">A</span></span>' +
        '<span class="etcher-label-swatch-text">Text</span>';
      lb.addEventListener("click", function(e) {
        e.preventDefault();
        if (self._openPopupKind === "colors" && self._labelPickTarget) {
          self._closePopup();
          return;
        }
        self._closePopup();
        self._labelPickTarget = true;
        self._colorsTrigger = lb;
        self._openPopup("colors");
        // _openPopup is target-agnostic; re-arm after the generic close.
        self._labelPickTarget = true;
        self._syncBgChoices();
      });
      labelRow.appendChild(lb);

      var bgSwatch = document.createElement("button");
      bgSwatch.type = "button";
      bgSwatch.className = "etcher-label-swatch";
      bgSwatch.innerHTML =
        '<span class="etcher-label-swatch-chip etcher-label-bg-chip">' +
        '<span class="etcher-label-swatch-glyph">A</span></span>' +
        '<span class="etcher-label-swatch-text">Behind</span>';
      bgSwatch.addEventListener("click", function(e) {
        e.preventDefault();
        if (self._openPopupKind === "colors" && self._labelBgPickTarget) {
          self._closePopup();
          return;
        }
        self._closePopup();
        self._labelBgPickTarget = true;
        self._colorsTrigger = bgSwatch;
        self._openPopup("colors");
        // _openPopup is target-agnostic; re-arm after the generic close.
        self._labelBgPickTarget = true;
        self._syncBgChoices();
      });
      labelRow.appendChild(bgSwatch);

      host.appendChild(labelRow);
      self.labelSwatchEl = lb;
      self.labelBgSwatchEl = bgSwatch;
      self._refreshLabelSwatch();
    },

    _togglePopup: function(kind) {
      var open = this._openPopupKind;
      this._closePopup();
      if (open !== kind) this._openPopup(kind);
    },

    // Where a popup's top edge goes: above the trigger when it fits there,
    // below when it doesn't.
    //
    // Above is the preference because these popups hang off toolbars that
    // sit at the bottom of the canvas. But the colour swatches live in the
    // style panel, which is pinned near the TOP — opening upward from there
    // put the picker off the top of the viewport entirely.
    //
    // All coordinates are in one space, and the caller picks which: container
    // -relative for canvas mode, viewport for strip mode (where the chrome is
    // `position: fixed`).
    _popupTop: function(triggerTop, triggerBottom, popupHeight, bounds) {
      var gap = 8;
      var above = triggerTop - popupHeight - gap;
      var below = triggerBottom + gap;
      if (above >= bounds.top + gap) return above;
      if (below + popupHeight <= bounds.bottom - gap) return below;
      // Fits on neither side — a popup taller than the space it has. Take
      // whichever side has more room and clamp into view, so it's partly
      // reachable rather than entirely off-screen.
      var roomAbove = triggerTop - bounds.top;
      var roomBelow = bounds.bottom - triggerBottom;
      var top = roomBelow >= roomAbove ? below : above;
      return Math.max(
        bounds.top + gap,
        Math.min(top, bounds.bottom - popupHeight - gap)
      );
    },

    _openPopup: function(kind) {
      var popup = kind === "tools" ? this.toolsPopup
                : kind === "colors" ? this.colorsPopup
                : kind === "params" ? this.paramsPopup
                : this.markerPopup;
      // The colors (hue) popup now opens from a swatch, so it anchors to the
      // swatch that triggered it (`_colorsTrigger`); falls back to the params
      // button if opened without one.
      var trigger = kind === "tools" ? this.toolsMoreBtn
                  : kind === "colors" ? (this._colorsTrigger || this.paramsBtn)
                  : kind === "params" ? this.paramsBtn
                  : this._markerToolBtn;
      if (!popup || !trigger) return;
      if (kind === "marker") this._syncMarkerPopup();
      if (kind === "params") this._syncParamsPopup();

      // The hue picker edits the selected slot, so start its knobs at
      // that slot's color when the colors popup opens; refresh the
      // overflowed-slots row so collapsed colors are reachable.
      if (kind === "colors") {
        this._syncPickerToActiveColor();
        this._syncColorsPopup();
      }
      // Defensive: make sure the tools popup reflects the current overflow
      // set right before it's shown (in case a layout pass was missed).
      if (kind === "tools") {
        this._syncToolsPopup();
        this._syncViewToggles();
      }

      // Position relative to the trigger — above it by preference, below
      // when there isn't room. Both popup and trigger live in the same
      // container so we work in its coordinate space. Display before
      // measuring so getBoundingClientRect returns real dims
      // (display:none → zero).
      popup.classList.add("is-open");
      var container = this.handle.container;
      var cRect = container.getBoundingClientRect();
      var tRect = trigger.getBoundingClientRect();
      var pRect = popup.getBoundingClientRect();
      // Strip mode's toolbar uses `position: fixed`, so the popup
      // should too — anchor it directly to the viewport instead of
      // to the container (which scrolls). `getBoundingClientRect` on
      // the trigger returns viewport coords either way.
      if (this.handleKind === "strip") {
        popup.style.position = "fixed";
        popup.style.top = this._popupTop(
          tRect.top, tRect.bottom, pRect.height,
          { top: 0, bottom: window.innerHeight }
        ) + "px";
        popup.style.left = "auto";
        // Center horizontally on the trigger button.
        var leftViewport = tRect.left + tRect.width / 2 - pRect.width / 2;
        var maxLeft = window.innerWidth - pRect.width - 8;
        if (leftViewport < 8) leftViewport = 8;
        if (leftViewport > maxLeft) leftViewport = maxLeft;
        popup.style.left = leftViewport + "px";
      } else {
        popup.style.position = "absolute";
        popup.style.top = this._popupTop(
          tRect.top - cRect.top, tRect.bottom - cRect.top, pRect.height,
          { top: 0, bottom: cRect.height }
        ) + "px";
        // Center horizontally on the trigger button, clamped to the
        // container.
        var leftContainer = (tRect.left - cRect.left) + tRect.width / 2 - pRect.width / 2;
        var maxLeftContainer = cRect.width - pRect.width - 8;
        if (leftContainer < 8) leftContainer = 8;
        if (leftContainer > maxLeftContainer) leftContainer = maxLeftContainer;
        popup.style.left = leftContainer + "px";
      }

      this._openPopupKind = kind;
      // Outside-click closer. Capture phase so we run before
      // inner stopPropagation handlers (toolbar buttons all
      // stopPropagation), and exclude the popup + trigger itself
      // so re-clicks don't immediately reopen.
      var self = this;
      this._popupOutsideClick = function(e) {
        if (popup.contains(e.target)) return;
        if (trigger.contains(e.target)) return;
        // While the picker is up, the palette swatches are part of its
        // surface: clicking one moves the picker to that slot (see the
        // swatch handler). This fires on pointerdown, ahead of that click,
        // so closing here would turn every retarget back into a dismiss.
        if (self._openPopupKind === "colors" && e.target && e.target.closest &&
            e.target.closest(".etcher-swatch")) {
          return;
        }
        self._closePopup();
      };
      document.addEventListener("pointerdown", this._popupOutsideClick, true);
    },

    _closePopup: function() {
      this._labelPickTarget = false;
      this._labelBgPickTarget = false;
      if (this._bgChoicesRow) this._bgChoicesRow.style.display = "none";
      this._slotPickTarget = null;
      if (this.toolsPopup) this.toolsPopup.classList.remove("is-open");
      if (this.colorsPopup) this.colorsPopup.classList.remove("is-open");
      if (this.markerPopup) this.markerPopup.classList.remove("is-open");
      if (this.paramsPopup) this.paramsPopup.classList.remove("is-open");
      this._openPopupKind = null;
      if (this._popupOutsideClick) {
        document.removeEventListener("pointerdown", this._popupOutsideClick, true);
        this._popupOutsideClick = null;
      }
    },

    _makeToolButton: function(toolKey, icon, title) {
      var self = this;
      var btn = document.createElement("button");
      btn.type = "button";
      btn.dataset.tool = toolKey;
      btn.title = title;
      btn.setAttribute("aria-label", title);
      btn.innerHTML = icon;
      btn.addEventListener("click", function(e) {
        e.preventDefault();
        var def = TOOL_DEFS[toolKey];
        if (def && def.momentary) { self._invokeMomentaryTool(toolKey); return; }
        self._selectTool(toolKey === "cursor" ? null : toolKey);
      });
      return btn;
    },

    // Like `_makeToolButton`, but its tool changes over time, so the click
    // handler reads the current one off the element instead of closing over
    // a fixed key.
    _makePinnedToolButton: function() {
      var self = this;
      var btn = document.createElement("button");
      btn.type = "button";
      btn.setAttribute("data-pinned-tool", "");
      btn.addEventListener("click", function(e) {
        e.preventDefault();
        var toolKey = btn.dataset.tool;
        if (!toolKey) return;
        var def = TOOL_DEFS[toolKey];
        if (def && def.momentary) { self._invokeMomentaryTool(toolKey); return; }
        self._selectTool(toolKey);
      });
      return btn;
    },

    // Point the slot at `_pinnedTool`, falling back to the default and then
    // to whatever extra this editor actually has — a host whose `tools` list
    // omits every non-essential gets no slot at all rather than a dead one.
    _renderPinnedTool: function() {
      var self = this;
      var btn = self.pinnedToolBtn;
      if (!btn) return;

      var extras = (self.tools || []).filter(function(t) {
        return !self._isEssentialTool(t) && TOOL_DEFS[t];
      });
      if (!extras.length) {
        btn.style.display = "none";
        return;
      }

      var key = self._pinnedTool;
      if (extras.indexOf(key) === -1) {
        key = extras.indexOf(DEFAULT_PINNED_TOOL) !== -1
          ? DEFAULT_PINNED_TOOL
          : extras[0];
      }
      self._pinnedTool = key;

      var def = TOOL_DEFS[key];
      btn.style.display = "";
      btn.dataset.tool = key;
      btn.title = def.title;
      btn.setAttribute("aria-label", def.title);
      btn.innerHTML = def.icon;
      // The active ring follows `data-tool`, so re-assert it after a swap.
      btn.classList.toggle("is-selected", self.activeTool === key);
    },

    // Called whenever a tool is chosen; only non-essentials claim the slot.
    _rememberPinnedTool: function(toolKey) {
      if (!toolKey) return;
      if (this._isEssentialTool(toolKey)) return;
      if (!TOOL_DEFS[toolKey]) return;
      if (this._pinnedTool === toolKey) return;
      this._pinnedTool = toolKey;
      this._renderPinnedTool();
      this._syncToolsPopup();
    },

    _buildNavButton: function() {
      var self = this;
      // Slot 0: the pencil leads the viewer's nav — it is what people come
      // to press, and it belongs at the end the "more" button is not.
      // (Fresco before 0.13.3 ignores the slot and appends.)
      self.removeNavBtn = self.handle.appendNavButton(ICONS.pencil, "Annotate", function() {
        self._setAnnotationMode(!self.annotationMode);
      }, { slot: NAV_SLOTS.pencil });
      self._syncNavPencil();
    },

    // The pencil says at a glance whether annotation mode is on: lit in
    // the accent while active, ordinary nav chrome while not. Fresco's
    // appendNavButton hands back the element on `.el` for exactly this
    // kind of decoration.
    _syncNavPencil: function() {
      var btn = this.removeNavBtn && this.removeNavBtn.el;
      if (!btn || !btn.classList) return;
      btn.classList.toggle("etcher-pencil-active", !!this.annotationMode);
    },

    // Eye toggle — show/hide all annotations on the image. Lives
    // above the pencil button so the user reads "look (eye) → edit
    // (pencil)" top to bottom. While hidden, the entire SVG overlay
    // is display:none which automatically hides shapes, handles, the
    // tooltip, title groups, midpoint dots, and any in-flight draft.
    _buildVisibilityButton: function() {
      var self = this;
      self.annotationsVisible = self.annotationsVisible !== false;
      self.visibilityBtn = self.handle.appendNavButton(
        self.annotationsVisible ? ICONS.eye : ICONS.eyeSlash,
        self.annotationsVisible ? "Hide annotations" : "Show annotations",
        function() { self._toggleAnnotationsVisible(); },
        { slot: NAV_SLOTS.eye }
      );
    },

    // A button in the tools popup that is not a tool: the view switches and
    // Customise. Tagged so the stylesheet gives it the same box as the tools
    // it sits among.
    _makePopupAction: function(icon, title, onClick) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.setAttribute("data-popup-action", "");
      btn.innerHTML = icon;
      btn.title = title;
      btn.setAttribute("aria-label", title);
      btn.addEventListener("click", function(e) {
        e.preventDefault();
        onClick();
      });
      return btn;
    },

    // Keep the two switches saying what they currently are. Called whenever
    // the popup is shown, so they are right even if the preference changed
    // from somewhere else.
    _syncViewToggles: function() {
      if (this.gridBtn) {
        var gridOn = this._getPref("grid") !== false;
        this.gridBtn.title = this._gridTitle();
        this.gridBtn.setAttribute("aria-label", this.gridBtn.title);
        this.gridBtn.setAttribute("aria-pressed", gridOn ? "true" : "false");
      }
      if (this.snapBtn) {
        var snapOn = this._snapOn();
        this.snapBtn.title = this._snapTitle();
        this.snapBtn.setAttribute("aria-label", this.snapBtn.title);
        this.snapBtn.setAttribute("aria-pressed", snapOn ? "true" : "false");
      }
      if (this.zoomAnchorBtn) {
        var zaOn = this._zoomAnchorOn();
        this.zoomAnchorBtn.title = this._zoomAnchorTitle();
        this.zoomAnchorBtn.setAttribute("aria-label", this.zoomAnchorBtn.title);
        this.zoomAnchorBtn.setAttribute("aria-pressed", zaOn ? "true" : "false");
      }
    },

    _gridTitle: function() {
      return this._getPref("grid") === false ? "Show background dots" : "Hide background dots";
    },

    // The four corner dots on a focused label: always shown now — there is
    // no toggle. Labels, text and callouts all show their dots (older ones convert to point text when picked up, and those
    // always had them); kept as a function so the call sites read the same.
    _titleHandlesOn: function() {
      return true;
    },

    // A tool that draws pure ink — its strokes are not labelled at
    // creation and cannot hold a fill — so while it is armed the panel's
    // label rows and fill row are noise about things the next gesture
    // cannot produce. Selection always wins: clicking a labelled shape
    // brings every applicable row back regardless of the armed tool.
    // Which ink tool a stroke came off. The highlighter draws MARKER
    // shapes — same kind, same geometry, same everything but a fixed half
    // opacity — so the stamp `_styleForNewShape` puts on them is what tells
    // the two apart. Strokes drawn before that stamp existed carry only the
    // opacity, which is the signal the highlighter was defined by anyway.
    _inkToolFor: function(shape) {
      if (!shape || shape.kind !== "marker") return null;
      var st = shape.style || {};
      if (st.ink === "highlighter" || st.ink === "marker") return st.ink;
      return st.opacity != null && st.opacity <= HIGHLIGHT_OPACITY
        ? "highlighter" : "marker";
    },

    _armedInkTool: function() {
      return !!this.annotationMode &&
        (this.activeTool === "marker" || this.activeTool === "highlighter");
    },

    // Which preference the visible palette belongs to. The shapes share
    // "colors"; each ink tool owns a full five-slot set of its own
    // ("marker_colors" / "highlighter_colors") that is swapped in while
    // the tool is armed — so an edit always persists under the palette
    // the user was actually looking at.
    _paletteKey: function() {
      // A SELECTED stroke outranks the armed tool: the panel is describing
      // that stroke, so the five swatches under it have to be the five the
      // stroke was drawn from — click a highlighter and you get the
      // highlighter's colours to recolour it with and to edit, not the
      // shapes' set. (The two rarely coexist: arming a tool clears the
      // selection.)
      var ink = this._inkToolFor(this._inspectedShape());
      if (ink) return ink + "_colors";
      return this._armedInkTool() ? this.activeTool + "_colors" : "colors";
    },

    // The built-in five for a palette that has never been set.
    _defaultSlotsFor: function(key) {
      if (key === "highlighter_colors") return HIGHLIGHT_DEFAULT_SLOTS.slice();
      if (key === "marker_colors") return MARKER_DEFAULT_SLOTS.slice();
      return this._sanitizeColorSlots(null);
    },

    // Put the palette the CONTEXT asks for on screen. Selecting a stroke,
    // deselecting it, arming a tool — each can change which of the three
    // sets the panel should be showing, and this is the one place that
    // answers. A no-op unless the answer actually changed.
    //
    // Prefs first (every slot edit persists there), then what was on screen
    // when we last left this palette — which is how a shared palette seeded
    // by the host and never edited comes back intact rather than reverting
    // to the presets — then the built-ins.
    _syncPaletteForContext: function() {
      var key = this._paletteKey();
      if (key === this._paletteAppliedKey) return;
      this._bankPalette();
      this._paletteAppliedKey = key;

      var next = this._getPref(key);
      if (!(Array.isArray(next) && next.length)) {
        next = (this._paletteBank || {})[key];
      }
      if (!(Array.isArray(next) && next.length)) next = this._defaultSlotsFor(key);

      var slots = this._sanitizeColorSlots(next);
      if (!slots.length) return;
      this._colorSlots = slots;
      if (this._activeSlot >= slots.length) this._activeSlot = 0;
      this._refreshToolbarSwatches();
    },

    // Remember the slots currently on screen under the palette they belong
    // to, so swapping back does not have to go through a pref that may
    // never have been written.
    _bankPalette: function() {
      if (!this._paletteAppliedKey || !this._colorSlots) return;
      this._paletteBank = this._paletteBank || {};
      this._paletteBank[this._paletteAppliedKey] = this._colorSlots.slice();
    },

    // The scale between the panel's numbers and stored canvas units — the
    // ONE conversion every panel read/write and every new shape goes
    // through. Rendering is untouched either way (stored canvas units ×
    // the current zoom, so ink always scales with the drawing on screen).
    //
    // Default (un-anchored): the canvas's own size against a reference
    // canvas, so the number is a thickness RELATIVE TO THE PICTURE rather
    // than a count of its pixels. A 5 drawn on an 800px photo and a 5
    // drawn on an 11384px panorama look identical when the two are viewed
    // at the same size — which they did not before: widths were stored as
    // document px, so the same 5 was a fat stroke on a small image and a
    // hairline on a big one, and every drawing had to be re-tuned per
    // image. Zoom behaviour is unchanged: stored units still ride the
    // zoom, so ink thickens with the drawing as you go in.
    //
    // Zoom-anchored (the ⋯ toggle, pref "zoom_anchor"): the current zoom —
    // a value means "this many px on screen right now", which is the old
    // behaviour: what you draw looks the same as you draw it, at the cost
    // of strokes from different zooms having different real thicknesses.
    // Already resolution-independent by construction (it measures the
    // screen), so the reference does not apply.
    _inkScale: function() {
      if (this._getPref("zoom_anchor") === true) {
        var s = 0;
        try { s = this._markerScale() || 0; } catch (_) { s = 0; }
        if (s > 0) return s;
      }
      return this._canvasInkScale();
    },

    // Slider position (0-100) -> weight. Rounded to whole units: the
    // number is what the panel shows and what gets stored, and a weight of
    // 7.4 is not a thing anyone asked for.
    // The number beside a thickness slider, typeable: click it (or focus it
    // and press Enter), type the size, Enter — no aiming the handle at 37.
    // The readout stays the plain span every sync writes into; while it is
    // being edited a small field stands in its place. Enter or a click away
    // applies, Escape leaves it as it was, and the slider follows. What is
    // typed is read by `_typedWeight`: held to the slider's own range, and
    // anything that isn't a number keeps the size it had.
    // Show what a thickness or size control is doing, beside it, while it
    // is being changed. `kind` is "width" (a stroke weight, as the panel
    // shows it) or "font" (a label size); `style` carries the stroke's
    // opacity and dash. The sample is drawn at the size the canvas would
    // draw it at this zoom; one too big for the bubble is cut down and
    // says so.
    _showValuePreview: function(kind, value, anchor, style) {
      if (!anchor || !this.handle || !this.handle.container || !(value > 0)) return;
      var self = this;
      var host = this.handle.container;
      var pv = this._valuePreview;
      if (!pv || pv.parentNode !== host) {
        pv = document.createElement("div");
        pv.className = "etcher-value-preview";
        pv.setAttribute("aria-hidden", "true");
        host.appendChild(pv);
        this._valuePreview = pv;
      }
      var scale = 1, ink = 1;
      try { scale = this._markerScale() || 1; } catch (_) { scale = 1; }
      try { ink = this._inkScale() || 1; } catch (_) { ink = 1; }
      var px = value / ink * scale;
      var color = this.activeColor || "#111";
      var st = style || {};
      var capped = false, tiny = false;
      if (kind === "font") {
        var fpx = Math.max(4, px);
        if (fpx > 96) { fpx = 96; capped = true; }
        pv.innerHTML = '<span class="etcher-pv-text"></span>';
        var t = pv.firstChild;
        t.textContent = "Aa";
        t.style.fontSize = fpx + "px";
        t.style.color = color;
        tiny = fpx < 9;
      } else {
        var w = Math.max(0.5, px);
        if (w > 56) { w = 56; capped = true; }
        var len = 120, h = Math.max(20, w + 12);
        var dash = st.dash === "dashed" ? ' stroke-dasharray="' + (w * 2.4) + " " + (w * 1.8 + 3) + '"'
          : st.dash === "dotted" ? ' stroke-dasharray="0.01 ' + (w * 1.8 + 3) + '"' : "";
        var op = st.opacity == null ? 1 : st.opacity;
        pv.innerHTML = '<svg width="' + len + '" height="' + h + '" viewBox="0 0 ' + len + " " + h + '">' +
          '<line x1="' + (w / 2 + 4) + '" y1="' + h / 2 + '" x2="' + (len - w / 2 - 4) + '" y2="' + h / 2 +
          '" stroke="' + color + '" stroke-opacity="' + op + '" stroke-width="' + w + '" stroke-linecap="round"' + dash + "/></svg>";
      }
      if (capped || tiny) {
        var note = document.createElement("span");
        note.className = "etcher-pv-note";
        note.textContent = capped ? "shown smaller" : "tiny at this zoom";
        pv.appendChild(note);
      }
      // Beside the panel the control sits in, on whichever side has room,
      // its arrow pointing at the control.
      var panel = (anchor.closest && anchor.closest(".etcher-popup")) || anchor;
      var hr = host.getBoundingClientRect();
      var pr = panel.getBoundingClientRect();
      var ar = anchor.getBoundingClientRect();
      var bw = pv.offsetWidth, bh = pv.offsetHeight;
      var gap = 12;
      var left = pr.left - hr.left - gap - bw;
      var side = "left";
      if (left < 8) { left = pr.right - hr.left + gap; side = "right"; }
      var cy = ar.top + ar.height / 2 - hr.top;
      var top = Math.max(8, Math.min(hr.height - bh - 8, cy - bh / 2));
      pv.style.left = left + "px";
      pv.style.top = top + "px";
      pv.dataset.side = side;
      pv.style.setProperty("--etcher-pv-arrow", Math.max(12, Math.min(bh - 12, cy - top)) + "px");
      pv.classList.add("is-on");
      pv._panel = panel;
      // A press anywhere outside the panel it belongs to puts it away now.
      if (!this._valuePreviewOff) {
        this._valuePreviewOff = function(e) {
          var cur = self._valuePreview;
          if (!cur || !cur.classList.contains("is-on")) return;
          if (cur._panel && cur._panel.contains && cur._panel.contains(e.target)) return;
          self._valuePreviewHeld = false;
          self._hideValuePreview();
        };
        document.addEventListener("pointerdown", this._valuePreviewOff, true);
      }
      clearTimeout(this._valuePreviewTimer);
      if (!this._valuePreviewHeld) {
        this._valuePreviewTimer = setTimeout(function() { self._hideValuePreview(); }, 1100);
      }
    },

    _hideValuePreview: function() {
      clearTimeout(this._valuePreviewTimer);
      if (this._valuePreview) this._valuePreview.classList.remove("is-on");
    },

    // A slider shows its sample from the press to a moment after the
    // release, held up the whole time the thumb is down, still or not.
    _wireValuePreview: function(input, show) {
      var self = this;
      input.addEventListener("pointerdown", function() {
        self._valuePreviewHeld = true;
        show();
      });
      var release = function() {
        if (!self._valuePreviewHeld) return;
        self._valuePreviewHeld = false;
        clearTimeout(self._valuePreviewTimer);
        self._valuePreviewTimer = setTimeout(function() { self._hideValuePreview(); }, 900);
      };
      input.addEventListener("pointerup", release);
      input.addEventListener("pointercancel", release);
      input.addEventListener("lostpointercapture", release);
      input.addEventListener("input", show);
      input.addEventListener("keyup", show);
    },

    // The stroke look a weight preview draws with: the selected shape's,
    // else the defaults new shapes get.
    _previewStrokeStyle: function(marker) {
      var ed = this.editingShape;
      if (ed && ed.style && (!marker || ed.kind === "marker")) return ed.style;
      if (marker) { try { return this._currentMarkerStyle(); } catch (_) { return {}; } }
      return this.lineParams || {};
    },

    // The character offset in a textarea nearest a client point, or null.
    // Browsers only answer that question for ordinary text, so a twin of
    // the field — its computed look, its words — is laid exactly over it
    // for the moment of asking.
    _caretOffsetAt: function(input, x, y) {
      if (!input || !input.isConnected || !input.parentNode) return null;
      var twin = document.createElement("div");
      var cs = window.getComputedStyle(input);
      for (var i = 0; i < cs.length; i++) twin.style.setProperty(cs[i], cs.getPropertyValue(cs[i]));
      twin.style.position = "absolute";
      twin.style.left = input.offsetLeft + "px";
      twin.style.top = input.offsetTop + "px";
      twin.style.margin = "0";
      twin.style.overflow = "hidden";
      twin.style.pointerEvents = "auto";
      twin.style.zIndex = "2147483647";
      twin.style.color = "transparent";
      twin.style.background = "transparent";
      twin.style.caretColor = "transparent";
      // A newline at the very end makes no line of its own in a div.
      twin.textContent = input.value + "\u200b";
      input.parentNode.appendChild(twin);
      var off = null;
      try {
        // Nudged onto where the field really is, should the two differ.
        var ir = input.getBoundingClientRect(), tr = twin.getBoundingClientRect();
        if (Math.abs(ir.left - tr.left) > 0.5 || Math.abs(ir.top - tr.top) > 0.5) {
          twin.style.left = (input.offsetLeft + ir.left - tr.left) + "px";
          twin.style.top = (input.offsetTop + ir.top - tr.top) + "px";
        }
        if (document.caretPositionFromPoint) {
          var p = document.caretPositionFromPoint(x, y);
          if (p && twin.contains(p.offsetNode)) off = p.offset;
        } else if (document.caretRangeFromPoint) {
          var r = document.caretRangeFromPoint(x, y);
          if (r && twin.contains(r.startContainer)) off = r.startOffset;
        }
      } catch (_) { off = null; }
      if (twin.parentNode) twin.parentNode.removeChild(twin);
      return off == null ? null : Math.max(0, Math.min(off, input.value.length));
    },

    _makeWeightTypeable: function(val, range, apply) {
      var self = this;
      val.classList.add("etcher-num-typeable");
      val.tabIndex = 0;
      val.setAttribute("role", "button");
      val.title = "Type a thickness";
      var editing = false;
      function start(e) {
        if (editing) return;
        if (e) { e.preventDefault(); e.stopPropagation(); }
        editing = true;
        var field = document.createElement("input");
        field.type = "text";
        field.inputMode = "numeric";
        field.className = "etcher-num-field";
        field.setAttribute("aria-label", "Thickness");
        field.value = val.textContent || "";
        val.style.display = "none";
        val.parentNode.insertBefore(field, val.nextSibling);
        var done = false;
        function finish(applyIt) {
          if (done) return;
          done = true;
          editing = false;
          if (applyIt) {
            var n = self._typedWeight(field.value);
            if (n != null && String(n) !== val.textContent) {
              range.value = String(self._sliderFromWeight(n));
              val.textContent = String(n);
              apply(n);
            }
          }
          window.removeEventListener("pointerdown", onOutside, true);
          if (field.parentNode) field.parentNode.removeChild(field);
          val.style.display = "";
        }
        // A click away applies the size to what is selected NOW — on the
        // press, ahead of everything else (window, capture phase). Left to
        // the field's blur, the board's own press handling deselected the
        // shape first and the size went to the next shape instead.
        function onOutside(ev) {
          if (ev.target === field) return;
          finish(true);
        }
        window.addEventListener("pointerdown", onOutside, true);
        field.addEventListener("keydown", function(ev) {
          // The board's shortcuts and the host's keys stay out of a field
          // being typed into.
          ev.stopPropagation();
          if (ev.key === "Enter") { ev.preventDefault(); finish(true); val.focus(); }
          else if (ev.key === "Escape") { ev.preventDefault(); finish(false); val.focus(); }
        });
        field.addEventListener("blur", function() { finish(true); });
        field.focus();
        field.select();
      }
      val.addEventListener("click", start);
      val.addEventListener("keydown", function(e) {
        if (e.key === "Enter" || e.key === " ") start(e);
      });
    },

    // A typed thickness, or null to keep the one there was. A number is held
    // to the slider's range — past the top it is the top, at or under the
    // bottom it is the bottom (1) — and rounded to a whole size. Anything
    // that isn't a number ("abc", "4px", blank) is not a size: null, no
    // error, the previous value stands.
    _typedWeight: function(text) {
      var t = String(text == null ? "" : text).trim().replace(",", ".");
      if (!/^-?\d+(\.\d+)?$/.test(t)) return null;
      var n = Math.round(Number(t));
      if (!isFinite(n)) return null;
      return Math.max(WEIGHT_MIN, Math.min(WEIGHT_MAX, n));
    },

    _weightFromSlider: function(pos) {
      var p = Number(pos);
      if (!isFinite(p)) return WEIGHT_MIN;
      p = Math.max(0, Math.min(100, p)) / 100;
      var w = WEIGHT_MIN + (WEIGHT_MAX - WEIGHT_MIN) * Math.pow(p, WEIGHT_CURVE);
      return Math.max(WEIGHT_MIN, Math.round(w));
    },

    // …and back, for showing a stored weight on the track. A weight past
    // the ceiling (an older board, a programmatic style) pins the handle at
    // the end rather than vanishing off it; the readout still shows the
    // real number.
    _sliderFromWeight: function(weight) {
      var w = Number(weight);
      if (!isFinite(w)) return 0;
      w = Math.max(WEIGHT_MIN, Math.min(WEIGHT_MAX, w));
      var f = (w - WEIGHT_MIN) / (WEIGHT_MAX - WEIGHT_MIN);
      return Math.round(100 * Math.pow(f, 1 / WEIGHT_CURVE));
    },

    // REFERENCE_CANVAS_PX over the canvas's longest side. The longest side
    // is what a fit-to-view zoom is driven by, so matching it keeps a
    // stroke's on-screen weight the same for two images shown at the same
    // size whatever their pixel counts — landscape, portrait or square.
    //
    // 1 for a board that cannot report a size (a headless layer, a strip
    // before its first measure), which is exactly the old behaviour, and
    // for a canvas that IS the reference size.
    _canvasInkScale: function() {
      var sz = this.imageSize;
      var dim = sz && Math.max(sz.x || 0, sz.y || 0);
      if (!(dim > 0) || !isFinite(dim)) return 1;
      return REFERENCE_CANVAS_PX / dim;
    },

    _zoomAnchorOn: function() {
      return this._getPref("zoom_anchor") === true;
    },

    _zoomAnchorTitle: function() {
      return this._zoomAnchorOn()
        ? "Size new ink uniformly (one thickness for the whole drawing)"
        : "Size new ink by the current zoom (drawn size is what you see)";
    },

    _snapTitle: function() {
      return this._snapOn()
        ? "Turn off drag snapping"
        : "Snap dragged shapes to their neighbours";
    },

    _toggleAnnotationsVisible: function() {
      this.annotationsVisible = !this.annotationsVisible;
      if (this.svg) {
        this.svg.style.display = this.annotationsVisible ? "" : "none";
      }
      // Tooltip and the composer popover live OUTSIDE the SVG (the
      // host page positions them via assigns), so the consumer
      // governs those — we just hide our own painted overlay.
      if (this.tooltipEl) {
        this.tooltipEl.style.display = this.annotationsVisible
          ? this.tooltipEl.style.display
          : "none";
      }
      if (this.visibilityBtn) {
        if (this.visibilityBtn.setIcon) {
          this.visibilityBtn.setIcon(
            this.annotationsVisible ? ICONS.eye : ICONS.eyeSlash
          );
        }
        if (this.visibilityBtn.setTitle) {
          this.visibilityBtn.setTitle(
            this.annotationsVisible ? "Hide annotations" : "Show annotations"
          );
        }
      }
      this._dispatch("etcher:visibility-changed", {
        visible: this.annotationsVisible
      });
    },

    // -------------------------------------------------------------------------
    // Mode + tool selection
    // -------------------------------------------------------------------------

    // Push the current annotations array to the consumer's LiveView.
    // Replaces 0.2.x's per-op `etcher:created` / `etcher:updated` /
    // `etcher:deleted` events with a single bulk event — the consumer
    // pipes the payload through `Fresco.Canvas.put_extension(canvas,
    // "etcher", %{"version" => "1", "annotations" => annotations})`.
    //
    // Every commit / edit / delete in the JS hook ends with
    // `self._emitChanged()` after mutating `self.shapes` in place.
    // True iff a named piece of nav-column chrome (`"pencil"` /
    // `"visibility"`) is enabled. Mirrors Fresco's `:nav_buttons`
    // semantics — `null` allowlist means "no allowlist set, all
    // enabled"; an empty Set means "explicit hide-all."
    _chromeEnabled: function(name) {
      return this._navButtonAllowlist == null ||
             this._navButtonAllowlist.has(name);
    },

    // Microtask-batched `_emitChanged`. Multiple `_addShape` / other
    // mutations queued in the same tick collapse to one emit + one
    // network round-trip — useful when a consumer is splicing in a
    // whole chapter's worth of annotations and would otherwise
    // generate N full-array replays.
    _scheduleEmitChanged: function() {
      if (this._emitChangedScheduled) return;
      this._emitChangedScheduled = true;
      var self = this;
      var run = function() {
        self._emitChangedScheduled = false;
        self._emitChanged();
      };
      if (typeof queueMicrotask === "function") queueMicrotask(run);
      else Promise.resolve().then(run);
    },

    // Splice a single shape into the live layer without remounting.
    // The payload mirrors the persisted-annotation shape used by
    // `_renderAnnotation` (kind, geometry, image_idx / image_id,
    // style, metadata, optional uuid) — same fields the
    // `etcher:annotations-changed` event emits. Returns the shape's
    // uuid (generated if not supplied), or `null` if validation
    // fails. Strip-mode payloads MUST include `image_idx`; without
    // it the renderer can't pick the right per-image overlay.
    //
    // Multiple sibling calls in the same microtask batch into one
    // `etcher:annotations-changed` emit.
    _addShape: function(payload) {
      if (!payload || typeof payload.kind !== "string" || !payload.geometry) {
        if (typeof console !== "undefined" && console.warn) {
          console.warn("[Etcher] addShape: payload requires `kind` + `geometry`.", payload);
        }
        return null;
      }
      var uuid = (typeof payload.uuid === "string" && payload.uuid)
        ? payload.uuid : genUuidV7();
      var ann = Object.assign({}, payload, { uuid: uuid });

      // Strip mode: hand off to the right per-image overlay before
      // rendering so the SVG element lands on the correct page.
      // Without this `_renderAnnotation` would append to whatever
      // overlay was last active.
      if (this.handleKind === "strip") {
        var idx = ann.image_idx;
        if (typeof idx !== "number" || !this.pageOverlays || !this.pageOverlays[idx]) {
          if (typeof console !== "undefined" && console.warn) {
            console.warn(
              "[Etcher] addShape: strip mode requires a valid `image_idx`. " +
              "Got `" + idx + "` against " +
              (this.pageOverlays ? this.pageOverlays.length : 0) + " pages."
            );
          }
          return null;
        }
        this._activateOverlayForImage(idx);
      }

      this._renderAnnotation(ann);
      this._scheduleEmitChanged();
      return uuid;
    },

    _addShapes: function(payloads) {
      if (!Array.isArray(payloads)) return [];
      var self = this;
      var out = [];
      for (var i = 0; i < payloads.length; i++) {
        var uuid = self._addShape(payloads[i]);
        if (uuid) out.push(uuid);
      }
      return out;
    },

    // ---- Image insertion --------------------------------------------------
    //
    // The image toolbar tool is a *momentary* action, not a drawing mode:
    // clicking it either opens the OS file picker (default) or hands off to
    // the host (`image_source: "custom"`). Pasting an image onto the canvas
    // routes through the same placement path. See `insertImage` on the public
    // API for the programmatic entry point.

    _invokeMomentaryTool: function(toolKey) {
      if (toolKey === "image") this._invokeImageTool();
    },

    _invokeImageTool: function() {
      if (this.imageSource === "custom") this._emitImageInsertRequested();
      else this._openImagePicker();
    },

    // Ask the host to supply an image. Fires a LiveView hook event AND a
    // bubbling DOM CustomEvent, so server-driven and JS-only hosts can both
    // respond. The host opens its own modal / uploader, then calls
    // `window.Etcher.layerFor(id).insertImage(href)` with the result.
    _emitImageInsertRequested: function() {
      var detail = { fresco_id: this.frescoId || null };
      if (this.pushEventTo && this.el) {
        try { this.pushEventTo(this.el, "etcher:image-insert-requested", detail); } catch (_) {}
      }
      this._dispatch("etcher:image-insert-requested", { frescoId: this.frescoId || null });
    },

    // Built-in OS file picker → data URL → insert. Uses a transient hidden
    // <input> so nothing lingers in the DOM.
    _openImagePicker: function() {
      var self = this;
      var input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*,audio/*,video/*";
      input.style.display = "none";
      document.body.appendChild(input);
      input.addEventListener("change", function() {
        var file = input.files && input.files[0];
        if (file) self._insertMediaFile(file);
        if (input.parentNode) input.parentNode.removeChild(input);
      });
      input.click();
    },

    // Read a File as a data URL, then place it. `imagePt` (image coords) is
    // optional — omitted for the picker (→ viewport center), set for a drop.
    // Every path that produces an image FILE — paste, drag-drop, the file
    // picker — funnels through here. With a host uploader registered the
    // bytes go to the host's storage and the shape carries a URL; without
    // one they're embedded as a base64 data URL (see `setImageUploader` for
    // why that gets expensive).
    _insertImageFile: function(file, imagePt, opts) {
      var self = this;
      opts = opts || {};
      if (!file || (file.type && file.type.indexOf("image/") !== 0)) return;

      var upload = this._imageUploader();
      if (!upload) return this._embedImageFile(file, imagePt, opts);

      // Place the image *now* and upload behind it, so a paste is instant
      // however long the transfer takes. The shape is real from the first
      // frame — it persists, it has a position, it can be moved, resized and
      // layered while its bytes are still going up.
      //
      // Two different images are in play until the upload resolves:
      //
      //   geometry.href   a thumbnail, and therefore what gets persisted and
      //                   sent to peers. Something small but *real*, so the
      //                   shape survives a reload mid-upload instead of
      //                   coming back as an empty frame.
      //   _previewHref    a local object URL for the full-resolution bytes,
      //                   drawn on this canvas only. A blob: URL belongs to
      //                   the document that made it, so it must never reach
      //                   the store or another viewer — which is exactly why
      //                   the persisted copy has to be the thumbnail.
      //
      // Both are replaced by the stored URL on completion. Placing before
      // uploading also removes a race: the upload can't finish before there
      // is a shape to put the URL on.
      this._makeImagePreview(file, function(objectUrl, thumbnail, natW, natH) {
        // Size from the ORIGINAL, not the thumbnail — otherwise a 256px
        // preview would place a 256px shape. Fitted to the visible area exactly
        // as `_insertImageHref` does when it measures for itself; passed
        // explicitly here because we hand it the thumbnail to display.
        var size = self._insertedImageSize(natW, natH, null);
        var uuid = self._insertImageHref(thumbnail || "", {
          at: imagePt,
          previewHref: objectUrl,
          metadata: opts.metadata,
          width: size.w,
          height: size.h
        });
        if (!uuid) {
          try { URL.revokeObjectURL(objectUrl); } catch (_) {}
          return;
        }
        self._activatePasted(uuid);
        self._startTrackedUpload(uuid, file, objectUrl, upload);
      });
    },

    // Decode `file` once and produce both things the insert path needs: an
    // object URL for the full-resolution bytes, and a small data-URL
    // thumbnail cheap enough to persist. Also returns the natural size, so
    // the caller doesn't decode a second time to measure.
    //
    // `cb(objectUrl, thumbnailDataUrl|null, naturalW, naturalH)`.
    _makeImagePreview: function(file, cb) {
      var self = this;
      var objectUrl = URL.createObjectURL(file);
      var img = new Image();

      img.onload = function() {
        var natW = img.naturalWidth || 0;
        var natH = img.naturalHeight || 0;
        cb(objectUrl, self._encodePreview(img, natW, natH, file.type), natW, natH);
      };

      img.onerror = function() { cb(objectUrl, null, 0, 0); };
      img.src = objectUrl;
    },

    // Best-looking encoding of `img` that fits `PREVIEW_BYTE_BUDGET`.
    //
    // Quality first, then size: full resolution at good quality is tried
    // before anything is thrown away, and only a result that overruns the
    // budget steps down. A screenshot usually lands on the first attempt.
    //
    // Transparency decides the format, not the source MIME. A PNG that turns
    // out to be opaque — most pasted screenshots — re-encodes as JPEG for a
    // fraction of the bytes; one that actually uses its alpha channel stays
    // PNG at reduced size, because compositing it onto black to save bytes
    // would be a worse picture, not a smaller one.
    // Returns null if the canvas can't be exported at all (tainted, out of
    // memory), which leaves the shape blank until its upload lands.
    _encodePreview: function(img, natW, natH, mimeType) {
      if (!natW || !natH) return null;
      var self = this;
      var alpha = mimeType === "image/png" && this._hasAlpha(img, natW, natH);

      var attempts = alpha
        ? [[1, "image/png", undefined], [0.6, "image/png", undefined],
           [0.35, "image/png", undefined]]
        : [[1, "image/jpeg", PREVIEW_QUALITY], [1, "image/jpeg", 0.7],
           [0.6, "image/jpeg", 0.7], [0.35, "image/jpeg", 0.6]];

      var smallest = null;
      for (var i = 0; i < attempts.length; i++) {
        var out = self._drawScaled(img, natW, natH, attempts[i][0], attempts[i][1], attempts[i][2]);
        if (!out) continue;
        if (out.length <= PREVIEW_BYTE_BUDGET) return out;
        // Keep the last (smallest) as a floor: an over-budget preview still
        // beats a shape with nothing to draw.
        smallest = out;
      }
      return smallest;
    },

    _drawScaled: function(img, natW, natH, factor, mime, quality) {
      try {
        var fit = Math.min(1, PREVIEW_MAX_SIDE / Math.max(natW, natH)) * factor;
        var c = document.createElement("canvas");
        c.width = Math.max(1, Math.round(natW * fit));
        c.height = Math.max(1, Math.round(natH * fit));
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        return c.toDataURL(mime, quality);
      } catch (_) {
        return null;
      }
    },

    // Does the image actually use its alpha channel? Sampled on a small
    // downscale — a full-resolution scan of a phone screenshot is millions
    // of pixels for a yes/no answer, and downscaling preserves any
    // transparent region big enough to matter.
    _hasAlpha: function(img, natW, natH) {
      try {
        var side = 64;
        var s = Math.min(1, side / Math.max(natW, natH));
        var c = document.createElement("canvas");
        c.width = Math.max(1, Math.round(natW * s));
        c.height = Math.max(1, Math.round(natH * s));
        var ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0, c.width, c.height);
        var data = ctx.getImageData(0, 0, c.width, c.height).data;
        for (var i = 3; i < data.length; i += 4) {
          if (data[i] < 250) return true;
        }
        return false;
      } catch (_) {
        // Can't tell — assume it matters, since guessing wrong the other way
        // paints transparent pixels black.
        return true;
      }
    },

    // Run `upload` for an already-placed pending shape, then swap the local
    // preview for the stored URL. Failure embeds the bytes instead, so the
    // paste survives at the cost of payload size — the alternative is an
    // image the user can see that quietly never persists.
    _startTrackedUpload: function(uuid, file, previewUrl, upload) {
      var self = this;
      var shape = self._shapeByUuid(uuid);
      if (!shape) return;
      shape._pendingUpload = true;

      function release() {
        var s = self._shapeByUuid(uuid);
        if (s) {
          s._pendingUpload = false;
          s._previewHref = null;
        }
        // Revoking is what frees the file's memory; skip it and every paste
        // in the session leaks its bytes for as long as the tab is open.
        try { URL.revokeObjectURL(previewUrl); } catch (_) {}
        return s;
      }

      function commit(href) {
        var s = release();
        if (!s) return;                       // deleted while uploading
        // Replaces the thumbnail that has been standing in since the paste.
        s.geometry = Object.assign({}, s.geometry, { href: href });
        self._renderShape(s);
        // The second emit. The shape itself — position, size, layering, and
        // a thumbnail to draw — has been persisted since it was placed; this
        // swaps in the real image for every viewer.
        self._emitChanged();
      }

      function embed(why) {
        if (window.console && console.warn) {
          console.warn("[Etcher] image upload failed; embedding instead:", why);
        }
        var reader = new FileReader();
        reader.onload = function() {
          if (typeof reader.result === "string") commit(reader.result);
          else release();
        };
        reader.onerror = function() { release(); };
        reader.readAsDataURL(file);
      }

      var ctx = { frescoId: self.frescoId };

      var pending;
      try {
        pending = upload(file, ctx);
      } catch (e) {
        return embed(e);
      }
      if (!pending || typeof pending.then !== "function") {
        return embed("uploader did not return a promise");
      }
      pending.then(function(href) {
        if (typeof href !== "string" || !href) {
          return embed("uploader resolved without a URL");
        }
        commit(href);
      }, embed);
    },

    // ---- Link cards --------------------------------------------------------
    //
    // A link card is an ordinary image shape that remembers the address it
    // was made from. Everything else about it — move, resize, layer, delete —
    // is image behaviour, and only opening and re-pointing it are special.

    _linkOf: function(shape) {
      var link = shape && shape.metadata && shape.metadata.link;
      return typeof link === "string" && link ? link : null;
    },

    // Record a grabber press over a link card so its release can open it.
    // Returns whether there was one — the caller returns either way, since
    // the grabber must never take the gesture from the panner.
    _linkUnfurlerTapCandidate: function(e) {
      var pt;
      try { pt = this._toImage(e); } catch (_) { return false; }
      var hit = this._shapeAt(pt);
      if (!hit || !this._linkOf(hit)) return false;
      this._pendingTap = {
        shape: hit,
        startX: e.clientX,
        startY: e.clientY,
        startedAt: Date.now(),
        linkOnly: true
      };
      return true;
    },

    // A paste is the start of placing something, so the thing lands
    // selected and with the cursor tool active. Otherwise the first two
    // actions after every paste are "click the thing I just pasted" and
    // "switch off the tool I was holding" before it can be moved at all —
    // and on a board the grabber is usually what's held.
    _activatePasted: function(uuid) {
      var shape = this._shapeByUuid(uuid);
      if (!shape || !this.annotationMode || shape.readonly) return;
      if (this.activeTool != null) this._selectTool(null);
      this._enterEditMode(shape);
    },

    _maybeOpenLinkOnTap: function(shape) {
      if (this._linkOf(shape)) this._openLink(shape);
    },

    _openLink: function(shape) {
      var url = this._linkOf(shape);
      if (!url) return;
      // A double-click is two taps, and an impatient user makes more. Only
      // the first within the window opens anything.
      var now = Date.now();
      if (this._lastLinkOpen &&
          this._lastLinkOpen.uuid === shape.uuid &&
          now - this._lastLinkOpen.at < 700) {
        return;
      }
      this._lastLinkOpen = { uuid: shape.uuid, at: now };
      // `noopener` because the opened page gets a handle on this one
      // otherwise, and it is a page we did not write.
      window.open(url, "_blank", "noopener,noreferrer");
    },

    // The `⋯` on a selected card. Positioned in `_positionLinkMenu` off the
    // shape's own box so it tracks pan, zoom and resize without extra
    // bookkeeping, and torn down with the selection.
    _showLinkMenuFor: function(shape) {
      var self = this;
      if (!self._linkOf(shape)) return self._hideLinkMenu();
      if (self._linkMenuShape === shape) return self._positionLinkMenu();

      self._hideLinkMenu();
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "etcher-link-menu";
      btn.setAttribute("data-fresco-no-capture", "");
      btn.title = "Link actions";
      btn.setAttribute("aria-label", "Link actions");
      btn.innerHTML =
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" ' +
        'aria-hidden="true"><circle cx="6" cy="12" r="1.6"/>' +
        '<circle cx="12" cy="12" r="1.6"/><circle cx="18" cy="12" r="1.6"/></svg>';
      btn.addEventListener("pointerdown", function(e) { e.stopPropagation(); });
      btn.addEventListener("click", function(e) {
        e.preventDefault();
        e.stopPropagation();
        self._toggleLinkMenuPopup();
      });

      self.handle.container.appendChild(btn);
      self._linkMenuBtn = btn;
      self._linkMenuShape = shape;
      self._positionLinkMenu();
    },

    _hideLinkMenu: function() {
      this._closeLinkMenuPopup();
      if (this._linkMenuBtn && this._linkMenuBtn.parentNode) {
        this._linkMenuBtn.parentNode.removeChild(this._linkMenuBtn);
      }
      this._linkMenuBtn = null;
      this._linkMenuShape = null;
    },

    _positionLinkMenu: function() {
      var shape = this._linkMenuShape;
      if (!this._linkMenuBtn || !shape || !shape.geometry) return;
      var g = shape.geometry;
      // `_imageToContainer` is already container-relative — the same space
      // these absolutely-positioned buttons live in. Subtracting the
      // container's own rect again would offset them by its position on the
      // page.
      var tr = this._imageToContainer({ x: g.x + g.w, y: g.y });
      var size = 30;
      var inset = 8;
      this._linkMenuBtn.style.left = Math.round(tr.x - size - inset) + "px";
      this._linkMenuBtn.style.top = Math.round(tr.y + inset) + "px";
      if (this._linkMenuPopup) {
        this._linkMenuPopup.style.left = Math.round(tr.x - 190) + "px";
        this._linkMenuPopup.style.top = Math.round(tr.y + inset + size + 4) + "px";
      }
    },

    _toggleLinkMenuPopup: function() {
      if (this._linkMenuPopup) return this._closeLinkMenuPopup();
      var self = this;
      var shape = self._linkMenuShape;
      if (!shape) return;

      var pop = document.createElement("div");
      pop.className = "etcher-popup etcher-link-popup is-open";
      pop.setAttribute("data-fresco-no-capture", "");

      function item(label, onClick) {
        var b = document.createElement("button");
        b.type = "button";
        b.textContent = label;
        b.addEventListener("click", function(e) {
          e.preventDefault();
          e.stopPropagation();
          self._closeLinkMenuPopup();
          onClick();
        });
        pop.appendChild(b);
      }

      item("Open link", function() { self._openLink(shape); });
      item("Edit link…", function() { self._startLinkEdit(shape); });

      self.handle.container.appendChild(pop);
      self._linkMenuPopup = pop;
      self._positionLinkMenu();

      self._linkPopupOutside = function(e) {
        if (e.target.closest && e.target.closest(".etcher-link-popup, .etcher-link-menu")) return;
        self._closeLinkMenuPopup();
      };
      document.addEventListener("pointerdown", self._linkPopupOutside, true);
    },

    _closeLinkMenuPopup: function() {
      if (this._linkMenuPopup && this._linkMenuPopup.parentNode) {
        this._linkMenuPopup.parentNode.removeChild(this._linkMenuPopup);
      }
      this._linkMenuPopup = null;
      if (this._linkPopupOutside) {
        document.removeEventListener("pointerdown", this._linkPopupOutside, true);
        this._linkPopupOutside = null;
      }
    },

    // Re-point a card. Without this the only way to correct a mistyped or
    // stale link is to delete the card and paste again, losing its position
    // and its place in the stack.
    _startLinkEdit: function(shape) {
      var self = this;
      var current = self._linkOf(shape) || "";
      var g = shape.geometry;
      var tl = self._imageToContainer({ x: g.x, y: g.y });
      var br = self._imageToContainer({ x: g.x + g.w, y: g.y });

      var wrap = document.createElement("div");
      wrap.className = "etcher-link-editor";
      wrap.setAttribute("data-fresco-no-capture", "");
      wrap.style.left = Math.round(tl.x) + "px";
      wrap.style.top = Math.round(tl.y) + "px";
      wrap.style.width = Math.max(220, Math.round(br.x - tl.x)) + "px";

      var input = document.createElement("input");
      input.type = "url";
      input.value = current;
      input.placeholder = "https://…";
      wrap.appendChild(input);
      self.handle.container.appendChild(wrap);

      function close() {
        if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
      }

      function commit() {
        var next = input.value.trim();
        close();
        if (!next || next === current) return;
        // Rebuild the card from the new address, in place. The old one is
        // only replaced once a new one exists, so a bad edit costs nothing.
        var unfurl = self._linkUnfurler();
        if (!unfurl) return;
        var pending;
        try { pending = unfurl(next, { frescoId: self.frescoId }); } catch (_) { return; }
        if (!pending || typeof pending.then !== "function") return;
        pending.then(function(card) {
          if (!card || typeof card.svg !== "string" || !card.svg) return;
          self._svgToPngFile(card.svg, card.width, card.height, function(file) {
            if (file) self._replaceWithLinkCard(shape.uuid, next, file);
          });
        }, function(why) {
          if (window.console && console.warn) {
            console.warn("[Etcher] could not rebuild the card:", why);
          }
        });
      }

      input.addEventListener("keydown", function(e) {
        if (e.key === "Enter") { e.preventDefault(); commit(); }
        else if (e.key === "Escape") { e.preventDefault(); close(); }
      });
      input.addEventListener("blur", function() { setTimeout(commit, 0); });
      setTimeout(function() { try { input.focus(); input.select(); } catch (_) {} }, 0);
    },

    // A ring is a sibling of its image, so removing the image's element
    // doesn't take it — it would be left outlining empty canvas. Rather
    // than chase every path that deletes a shape (single, bulk, eraser,
    // undo of a create), sweep: a ring no live shape claims is orphaned.
    _sweepImageRings: function() {
      if (!this.svg) return;
      // Hatch outlines are siblings of their shape for the same reason and
      // are orphaned the same way, so they sweep together.
      var rings = this.svg.querySelectorAll(
        ".etcher-image-ring, .etcher-hatch-outline"
      );
      if (!rings.length) return;
      var owned = [];
      (this.shapes || []).forEach(function(s) {
        if (s && s._imageRing) owned.push(s._imageRing);
        if (s && s._hatchOutline) owned.push(s._hatchOutline);
      });
      for (var i = 0; i < rings.length; i++) {
        if (owned.indexOf(rings[i]) === -1 && rings[i].parentNode) {
          rings[i].parentNode.removeChild(rings[i]);
        }
      }
    },

    // Selection and hover are class toggles that don't re-render anything,
    // so the two kinds of selected-state paint that DON'T come from CSS have
    // to be asked for explicitly when they change: an image's ring, and a
    // hatched shape's traced perimeter. Every other kind paints its own
    // selected state through the filter, which the class toggle is enough
    // for on its own.
    _refreshImageRing: function(shape) {
      if (!shape || !shape.el) return;
      if (shape.kind === "image") { this._renderShape(shape); return; }
      this._syncHatchOutline(shape);
    },

    // The states a shape is outlined in. Hover is included: a hatched shape
    // hovering under the cursor should answer the same way every other shape
    // does, just without the stripe-by-stripe ringing.
    _looksSelected: function(el) {
      if (!el || !el.classList) return false;
      return el.classList.contains("is-selected") ||
             el.classList.contains("is-editing") ||
             el.classList.contains("is-multi-selected") ||
             el.classList.contains("is-hovered");
    },

    // A hatched shape's selection outline, traced rather than filtered.
    //
    // The silhouette filter rings every painted pixel, which for a hatch
    // means every stripe — so these shapes opt out of it (`.etcher-hatched`)
    // and get a copy of their own geometry instead: same element type, same
    // geometry attributes, no fill, stroked blue, sitting BEHIND the shape
    // so the shape's own stroke covers the middle of the band and only the
    // edges show. Solid, whatever the shape's dash is: a dashed selection
    // outline reads as a property of the shape rather than as selection.
    // Is this shape hatched? Asked of the STYLE, not of a class on the
    // element. The class is set by `_applyFill`, which is reached through
    // half a dozen paths — a couple of which pass no style at all and strip
    // it on the way past — so the DOM is a report of what the last painter
    // happened to do, while the style is what the shape IS.
    _isHatched: function(shape) {
      return !!(shape && shape.style && shape.style.fill === "pattern");
    },

    _syncHatchOutline: function(shape) {
      var el = shape && shape.el;
      var hatched = !!el && this._isHatched(shape);

      // Kill the ringing filter INLINE rather than through a stylesheet
      // rule. The rule is still there and still correct, but a filter is
      // exactly the kind of thing a host's own CSS, a second copy of this
      // library on the page, or a specificity surprise can put back; an
      // inline style cannot be out-ranked by any of them. This is the line
      // that stops each hatch stripe being outlined.
      if (el && el.style) {
        el.style.filter = hatched ? "none" : "";
      }

      var want = hatched && this._looksSelected(el) && !!el.parentNode;

      if (!want) {
        if (shape && shape._hatchOutline && shape._hatchOutline.parentNode) {
          shape._hatchOutline.parentNode.removeChild(shape._hatchOutline);
        }
        if (shape) shape._hatchOutline = null;
        return;
      }

      var outline = shape._hatchOutline;
      if (!outline || outline.tagName !== el.tagName) {
        // Shallow: every hatchable kind (rect / circle / polygon / path /
        // polyline) carries its whole geometry in its own attributes.
        outline = el.cloneNode(false);
        outline.setAttribute("class", "etcher-hatch-outline");
        shape._hatchOutline = outline;
      } else {
        // Geometry moves every frame under a pan, zoom or drag; copy it
        // across rather than rebuilding the node.
        for (var i = 0; i < el.attributes.length; i++) {
          var a = el.attributes[i];
          if (SKIP_OUTLINE_ATTRS[a.name]) continue;
          outline.setAttribute(a.name, a.value);
        }
      }
      for (var k in SKIP_OUTLINE_ATTRS) {
        if (SKIP_OUTLINE_ATTRS.hasOwnProperty(k)) outline.removeAttribute(k);
      }
      outline.setAttribute("class", "etcher-hatch-outline");

      // Wide enough that the band clears the shape's own stroke on both
      // sides by about what the filter's dilate radius gave.
      var own = parseFloat(el.style.strokeWidth) || 2;
      outline.style.fill = "none";
      outline.style.stroke = SELECTION_BLUE;
      outline.style.strokeWidth = (own + OUTLINE_BAND_PX * 2) + "px";
      outline.style.strokeOpacity = "1";
      outline.style.filter = "none";

      // Behind the shape, and re-seated every sync: `_syncShapeOrder`
      // rearranges shape elements for layering, and an outline left where it
      // was would end up tracing whatever moved into its place.
      if (outline.nextSibling !== el || !outline.parentNode) {
        el.parentNode.insertBefore(outline, el);
      }
    },

    // Keep an image's selection ring in step with its box, or drop it when
    // the image isn't selected.
    //
    // A separate element because none of the usual selected-state paint
    // reaches an `<image>`: `stroke` and `fill` don't apply to one, and the
    // drop-shadow lands outside the box where the rounded-corner `clip-path`
    // removes it. The ring tracks the same radius so it reads as an outline
    // of the image rather than a box around it.
    _syncImageRing: function(shape, box) {
      var selected =
        shape.el.classList.contains("is-multi-selected") ||
        shape.el.classList.contains("is-selected") ||
        shape.el.classList.contains("is-editing");
      var hovered = !selected && shape.el.classList.contains("is-hovered");
      if (!selected && !hovered) {
        if (shape._imageRing && shape._imageRing.parentNode) {
          shape._imageRing.parentNode.removeChild(shape._imageRing);
        }
        shape._imageRing = null;
        return;
      }
      var ring = shape._imageRing;
      if (!ring) {
        ring = svgEl("rect");
        ring.classList.add("etcher-image-ring");
        shape._imageRing = ring;
      }
      // Re-inserted after the image on every render: `_syncShapeOrder`
      // rearranges shape elements for layering, and a ring left behind would
      // end up decorating whatever moved into its place.
      if (ring.previousSibling !== shape.el || !ring.parentNode) {
        if (shape.el.parentNode) {
          shape.el.parentNode.insertBefore(ring, shape.el.nextSibling);
        }
      }
      ring.setAttribute("x", box.x);
      ring.setAttribute("y", box.y);
      ring.setAttribute("width", box.w);
      ring.setAttribute("height", box.h);
      ring.setAttribute("rx", box.r);
      ring.setAttribute("ry", box.r);
      // The ring traces the picture's edge, so it turns with it.
      this._setRotateTransform(ring, box.box);
      if (hovered) ring.setAttribute("data-state", "hovered");
      else ring.removeAttribute("data-state");
    },

    // ── Audio cards ─────────────────────────────────────────────────────────
    //
    // An audio annotation is a transport drawn in SVG — background, play
    // button, title, timecode, scrub bar — plus an <audio> element that does
    // the playing. The two are deliberately separate: the card has to pan,
    // zoom and layer with every other shape, which an <audio> inside a
    // <foreignObject> does badly (and does differently in each engine).
    // Nothing about the element is on screen, so it lives outside the
    // overlay and is addressed by uuid.

    _makeAudioEl: function(kind) {
      var g = svgEl("g");
      g.classList.add("etcher-audio");
      if (kind === "video") g.classList.add("etcher-video");

      var bg = svgEl("rect");
      bg.classList.add("etcher-audio-bg");
      g.appendChild(bg);

      // The button is a disc plus a glyph. The disc carries the pointer
      // events for both — a triangle is a small target and its bounding box
      // lies mostly outside the visible shape.
      var btn = svgEl("circle");
      btn.classList.add("etcher-audio-btn");
      g.appendChild(btn);
      var glyph = svgEl("path");
      glyph.classList.add("etcher-audio-glyph");
      g.appendChild(glyph);

      var title = svgEl("text", { "dominant-baseline": "middle" });
      title.classList.add("etcher-audio-title");
      g.appendChild(title);

      var time = svgEl("text", { "dominant-baseline": "middle", "text-anchor": "end" });
      time.classList.add("etcher-audio-time");
      g.appendChild(time);

      var track = svgEl("rect");
      track.classList.add("etcher-audio-track");
      g.appendChild(track);
      var fill = svgEl("rect");
      fill.classList.add("etcher-audio-fill");
      g.appendChild(fill);
      // Sits over the bar and is invisible; a 4px-tall track is not a
      // scrubbable target, and making the visible bar itself taller would
      // dominate the card.
      var hit = svgEl("rect");
      hit.classList.add("etcher-audio-scrub");
      g.appendChild(hit);

      // "You are about to seek to here." Every real player shows this, and
      // without it a click on the bar is a guess.
      var peek = svgEl("g");
      peek.classList.add("etcher-audio-peek");
      var peekBg = svgEl("rect");
      peekBg.classList.add("etcher-audio-peek-bg");
      var peekText = svgEl("text", {
        "text-anchor": "middle", "dominant-baseline": "middle"
      });
      peekText.classList.add("etcher-audio-peek-text");
      peek.appendChild(peekBg);
      peek.appendChild(peekText);
      g.appendChild(peek);

      return g;
    },

    // The <audio> element for a shape, created on first use. Appended to the
    // container rather than the overlay: the overlay is rebuilt on a source
    // swap, and a playing element torn out of the DOM stops dead.
    _audioElFor: function(shape) {
      if (!shape || !this._isMediaKind(shape.kind)) return null;
      if (shape._audioEl) return shape._audioEl;
      var href = (shape.geometry && shape.geometry.href) || "";
      if (!href) return null;
      var self = this;
      var isVideo = shape.kind === "video";
      var el = document.createElement(isVideo ? "video" : "audio");
      if (isVideo) {
        // Inline, and muted-capable, because a phone would otherwise take the
        // video fullscreen the moment it plays and drop the board.
        el.setAttribute("playsinline", "");
        el.playsInline = true;
      }
      // Metadata only: enough for a duration and a scrub bar without pulling
      // the whole file down for a card nobody has pressed play on.
      el.preload = "metadata";
      el.src = href;
      el.style.display = "none";
      el.addEventListener("loadedmetadata", function() {
        if (isFinite(el.duration) && el.duration > 0) {
          shape.geometry = Object.assign({}, shape.geometry, { duration: el.duration });
        }
        self._renderShape(shape);
      });
      el.addEventListener("timeupdate", function() { self._renderShape(shape); });
      el.addEventListener("play", function() { self._renderShape(shape); });
      el.addEventListener("pause", function() { self._renderShape(shape); });
      el.addEventListener("ended", function() { self._renderShape(shape); });
      if (isVideo) {
        // Frames go in the media layer, where the render pass positions them.
        el.style.display = "";
        (this.mediaLayer || document.body).appendChild(el);
        el.addEventListener("loadedmetadata", function() { self._renderShape(shape); });
      } else {
        // Nothing to show for audio — the card is the whole interface — so
        // the element goes on the container, out of the overlay. An overlay
        // rebuild would otherwise tear a playing element out of the DOM and
        // stop it dead.
        (this.handle && this.handle.container ? this.handle.container : document.body)
          .appendChild(el);
      }
      shape._audioEl = el;
      return el;
    },

    // One door for every file that arrives — paste, drop, picker. Images and
    // audio diverge completely after this (one embeds, the other must
    // upload), so the split belongs here rather than in each caller.
    _insertMediaFile: function(file, imagePt, opts) {
      if (this._isAudioFile(file) || this._isVideoFile(file)) {
        return this._insertAudioFile(file, imagePt, opts);
      }
      return this._insertImageFile(file, imagePt, opts);
    },

    // Place a card for an already-stored file. `opts.at` (image px) or the
    // viewport centre; `opts.title` labels it, `opts.duration` seeds the
    // timecode before metadata loads.
    _insertAudioHref: function(href, opts) {
      return this._insertMediaHref("audio", href, opts);
    },

    _insertVideoHref: function(href, opts) {
      return this._insertMediaHref("video", href, opts);
    },

    _insertMediaHref: function(kind, href, opts) {
      // An empty string is allowed and meaningful: it's the placeholder a
      // dropped file is given while it uploads. Only a missing argument is
      // an error.
      if (typeof href !== "string") return null;
      opts = opts || {};
      var at = (opts.at && typeof opts.at.x === "number")
        ? opts.at
        : (this._viewportCenterImage() || { x: 0, y: 0 });
      // Sized in image px so the card zooms with the content like an image,
      // but scaled by the current zoom so a drop is the same size on screen
      // whatever the canvas happens to be at.
      var per = 1;
      try { per = this._markerScale() || 1; } catch (_) {}
      // A video needs a picture-sized box; an audio card only needs room for
      // its transport.
      var baseW = kind === "video" ? VIDEO_BOX_W : AUDIO_CARD_W;
      var baseH = kind === "video" ? VIDEO_BOX_H : AUDIO_CARD_H;
      var w = baseW / (per > 0 ? per : 1);
      var h = baseH / (per > 0 ? per : 1);
      var geom = {
        x: at.x - w / 2, y: at.y - h / 2, w: w, h: h,
        href: href,
        title: opts.title || (kind === "video" ? "Video" : "Audio"),
        duration: typeof opts.duration === "number" ? opts.duration : null
      };
      var el = this._makeAudioEl(kind);
      el.classList.add("etcher-shape");
      this._applyShapeColor(el, this.activeColor);
      this.svg.appendChild(el);
      // `_finalizeShape` reports the new shape through its afterCreate hook
      // rather than a return value — that's the documented way to get hold of
      // it without re-finding it by uuid.
      var self = this;
      var made = null;
      this._finalizeShape(kind, geom, el, function(shape) {
        made = shape;
        // Created now so the duration lands, and the card redraws with it,
        // without waiting for someone to press play.
        self._audioElFor(shape);
      });
      return made ? made.uuid : null;
    },

    // Every path that produces an audio FILE funnels through here, mirroring
    // `_insertImageFile`. There is no embed fallback the way images have one:
    // an audio file base64'd into the annotation payload would be megabytes
    // of JSON on every save and every peer broadcast. Without a host uploader
    // the insert is refused loudly rather than half-working.
    _insertAudioFile: function(file, imagePt, opts) {
      var self = this;
      opts = opts || {};
      if (!file) return;
      var kind = this._isVideoFile(file) ? "video" : "audio";

      // Refuse what this browser can't play, BEFORE uploading it. Otherwise a
      // 300MB .mkv transfers in full, stores, and lands as a card that will
      // never do anything — and the person who dropped it has no way to tell
      // that from a slow network. Checked here rather than by extension
      // because support genuinely differs by browser.
      var playable = this._canPlayFile(kind, file);
      if (!playable) {
        console.warn(
          "[Etcher] this browser can't play " + (file.type || "that file") +
          " — not uploading it:", file.name || "(unnamed)");
        this._dispatch("etcher:media-unsupported", {
          name: file.name || null,
          type: file.type || null,
          kind: kind
        });
        return;
      }
      var upload = this._imageUploader();
      if (!upload) {
        console.warn(
          "[Etcher] no image uploader registered, so " + kind +
          " can't be inserted — see setImageUploader.");
        this._dispatch("etcher:media-upload-unavailable", {
          name: file.name || null, reason: "no_uploader"
        });
        return;
      }
      var placedAt = imagePt || this._viewportCenterImage() || { x: 0, y: 0 };

      // Placed NOW, empty, and filled in when the upload resolves. Waiting
      // for the transfer meant letting go of a file and watching nothing
      // happen for as long as it took, with no way to tell a large file from
      // a broken one. The card is real from the first frame — it can be
      // moved, resized and layered while its bytes are still going up — and
      // stays out of the annotations payload until it has something to play
      // (see `_emitChanged`).
      //
      // The uploader's contract is `fn(file, ctx) -> Promise<url>`; the
      // second argument is context, NOT a callback.
      var title = opts.title || file.name ||
        (kind === "video" ? "Video" : "Audio");
      var uuid = this._insertMediaHref(kind, "", { at: placedAt, title: title });
      var placed = this._shapeByUuid(uuid);
      if (placed) {
        placed._pendingUpload = true;
        this._renderShape(placed);
      }

      function discard(why) {
        var s = self._shapeByUuid(uuid);
        // Take the placeholder away rather than leaving a card that will
        // never play. Warned as well as dispatched: these events have no
        // default listener, so on its own a failure is a file that silently
        // doesn't arrive.
        if (s) self._deleteShape(s);
        console.warn("[Etcher] media upload failed:", why);
        self._dispatch("etcher:media-upload-failed", {
          name: file.name || null, reason: String(why)
        });
      }

      // `ctx.onProgress(0..1)` is how a host reports transfer progress. It's
      // optional — a host that ignores it leaves the bar indeterminate,
      // which still says "alive" but can't distinguish slow from stuck. That
      // distinction is the whole reason a large upload needs a bar at all,
      // so it's worth a host wiring up.
      var pending;
      try {
        pending = upload(file, {
          frescoId: self.frescoId,
          onProgress: function(fraction) {
            var s = self._shapeByUuid(uuid);
            if (!s || !s._pendingUpload) return;
            var f = Number(fraction);
            if (!isFinite(f)) return;
            // Never allowed to go backwards or reach 1 before the URL does:
            // a bar that fills and then sits there is a worse lie than one
            // that stops short.
            f = Math.max(0, Math.min(0.99, f));
            if (f < (s._uploadProgress || 0)) return;
            s._uploadProgress = f;
            self._renderShape(s);
          }
        });
      } catch (e) {
        return discard(e);
      }
      if (!pending || typeof pending.then !== "function") {
        return discard("uploader did not return a promise");
      }
      pending.then(function(href) {
        if (typeof href !== "string" || !href) {
          return discard("uploader resolved without a URL");
        }
        var s = self._shapeByUuid(uuid);
        if (!s) return;                    // deleted while uploading
        s._pendingUpload = false;
        s.geometry = Object.assign({}, s.geometry, { href: href });
        // Only now is there anything to play, so only now is there an
        // element to build.
        self._audioElFor(s);
        self._renderShape(s);
        // The second emit — the one that actually persists the card.
        self._emitChanged();
      }, discard);
    },

    // Can this browser play the file at all? `canPlayType` answers "", "maybe"
    // or "probably"; only "" is a definite no. A blank MIME type is treated as
    // playable — plenty of sources omit it, and refusing on that basis would
    // reject files that work perfectly.
    _canPlayFile: function(kind, file) {
      var type = file && file.type;
      if (!type) return true;
      try {
        var probe = document.createElement(kind === "video" ? "video" : "audio");
        if (typeof probe.canPlayType !== "function") return true;
        return probe.canPlayType(type) !== "";
      } catch (_) {
        return true;
      }
    },

    _isVideoFile: function(file) {
      if (!file) return false;
      if (file.type && file.type.indexOf("video/") === 0) return true;
      return /\.(mp4|m4v|webm|mov|ogv)$/i.test(file.name || "");
    },

    _isAudioFile: function(file) {
      if (!file) return false;
      if (file.type && file.type.indexOf("audio/") === 0) return true;
      // Some sources hand over a blank type; fall back to the extension.
      return /\.(mp3|m4a|aac|wav|ogg|oga|flac|opus)$/i.test(file.name || "");
    },

    toggleAudio: function(uuid) {
      var st = this._audioState(uuid);
      if (!st) return false;
      return this._applyAudio(uuid, st.playing ? "pause" : "play", null, true);
    },

    seekAudio: function(uuid, frac) {
      var st = this._audioState(uuid);
      if (!st || !st.duration) return false;
      var pos = Math.max(0, Math.min(1, frac)) * st.duration;
      return this._applyAudio(uuid, st.playing ? "play" : "seek", pos, true);
    },

    // Transport state for every media shape that has one. Used to answer a
    // newcomer: playback is shared as commands, so someone who arrives
    // between them has no idea what is playing or where. Only shapes whose
    // element exists are reported — one that has never been touched is at
    // zero and paused, which is also what the newcomer already has.
    _mediaStates: function() {
      var self = this;
      var out = [];
      (this.shapes || []).forEach(function(s) {
        if (!self._isMediaKind(s.kind) || !s.uuid || !s._audioEl) return;
        var st = self._audioState(s.uuid);
        if (st) out.push({ uuid: s.uuid, playing: st.playing, position: st.position });
      });
      return out;
    },

    // Volume and mute are per-listener and deliberately outside the shared
    // transport: in a room, one person turning it down for themselves is
    // normal, and one person turning it down for everyone is not. Neither
    // emits a command.
    _setMediaVolume: function(uuid, volume) {
      var shape = this._shapeByUuid(uuid);
      if (!shape || !this._isMediaKind(shape.kind)) return false;
      var el = this._audioElFor(shape);
      if (!el) return false;
      el.volume = Math.max(0, Math.min(1, volume));
      return true;
    },

    _setMediaMuted: function(uuid, muted) {
      var shape = this._shapeByUuid(uuid);
      if (!shape || !this._isMediaKind(shape.kind)) return false;
      var el = this._audioElFor(shape);
      if (!el) return false;
      el.muted = !!muted;
      this._renderShape(shape);
      return true;
    },

    _audioState: function(uuid) {
      var shape = this._shapeByUuid(uuid);
      if (!shape || !this._isMediaKind(shape.kind)) return null;
      var el = this._audioElFor(shape);
      if (!el) return null;
      var dur = isFinite(el.duration) && el.duration > 0
        ? el.duration
        : ((shape.geometry && shape.geometry.duration) || null);
      return {
        playing: !el.paused && !el.ended,
        position: el.currentTime || 0,
        duration: dur,
        volume: typeof el.volume === "number" ? el.volume : 1,
        muted: !!el.muted
      };
    },

    // Bring this client in line with the room. Only corrects when it's
    // actually out — a seek on every broadcast would stutter audibly, and
    // these arrive continuously while playing.
    _applyMediaState: function(uuid, state) {
      var st = this._audioState(uuid);
      if (!st) return false;
      var wantPos = typeof state.position === "number" ? state.position : null;
      var drift = wantPos == null ? 0 : Math.abs(wantPos - st.position);
      // A quarter second is under what a listener notices in speech or
      // music, and above the jitter of a round trip — correcting inside it
      // would mean seeking constantly for no audible gain.
      var pos = drift > 0.25 ? wantPos : null;
      if (state.playing && !st.playing) return this._applyAudio(uuid, "play", pos, false);
      if (!state.playing && st.playing) return this._applyAudio(uuid, "pause", pos, false);
      if (pos != null) return this._applyAudio(uuid, st.playing ? "play" : "seek", pos, false);
      return true;
    },

    // ── Audio transport ─────────────────────────────────────────────────────
    //
    // Etcher owns the element and the card; it does NOT own who is listening
    // to what. Every local control emits `etcher:media-command`, and the host
    // decides what that means for the room before handing state back through
    // `applyMediaState`. That keeps the shared-playback policy — who may
    // control, how drift is corrected, what happens on join — out of a
    // drawing library, and it is the same split as `setImageUploader`.

    // Apply a transport action to the element. `emit` false when the action
    // came FROM the host, so a peer's broadcast can't echo back out.
    _applyAudio: function(uuid, action, position, emit) {
      var shape = this._shapeByUuid(uuid);
      if (!shape || !this._isMediaKind(shape.kind)) return false;
      var el = this._audioElFor(shape);
      if (!el) return false;
      var self = this;

      if (typeof position === "number" && isFinite(position)) {
        try { el.currentTime = Math.max(0, position); } catch (_) {}
      }
      if (action === "play") {
        var p = null;
        try { p = el.play(); } catch (_) {}
        // Autoplay policy: a browser refuses `play()` until the user has
        // interacted with the page. Someone who joins a room where audio is
        // already running hears nothing, and nothing in the DOM says why —
        // so surface it rather than failing silently.
        if (p && typeof p.catch === "function") {
          p.catch(function(err) {
            self._dispatch("etcher:media-blocked", {
              uuid: uuid,
              reason: (err && err.name) || "NotAllowedError"
            });
          });
        }
      } else if (action === "pause") {
        try { el.pause(); } catch (_) {}
      }
      this._renderShape(shape);

      if (emit !== false) {
        this._dispatch("etcher:media-command", {
          uuid: uuid,
          action: action,
          position: el.currentTime || 0
        });
        if (this.pushEventTo) {
          this.pushEventTo(this.el, "etcher:media-command", {
            fresco_id: this.frescoId || null,
            uuid: uuid,
            action: action,
            position: el.currentTime || 0
          });
        }
      }
      return true;
    },

    // Play/pause and scrub. Both stop propagation: without it the press
    // would fall through to the shape-move gesture and the card would follow
    // the pointer instead of responding to the control.
    _attachAudioControls: function(shape) {
      var self = this;
      var el = shape.el;
      if (!el || el._etcherAudioWired) return;
      el._etcherAudioWired = true;

      var btn = el.querySelector(".etcher-audio-btn");
      if (btn) {
        btn.addEventListener("pointerdown", function(e) {
          if (e.button != null && e.button !== 0) return;
          e.preventDefault();
          e.stopPropagation();
          self.toggleAudio(shape.uuid);
        });
      }

      var scrub = el.querySelector(".etcher-audio-scrub");
      if (scrub) {
        scrub.addEventListener("pointermove", function(e) {
          self._showScrubPeek(shape, e.clientX);
        });
        scrub.addEventListener("pointerleave", function() {
          self._hideScrubPeek(shape);
        });
        scrub.addEventListener("pointerdown", function(e) {
          if (e.button != null && e.button !== 0) return;
          e.preventDefault();
          e.stopPropagation();
          var bar = shape._audioBar;
          if (!bar || !bar.w) return;
          var frac = Math.max(0, Math.min(1, (e.clientX - self._containerX(bar.x)) / bar.w));
          self.seekAudio(shape.uuid, frac);
        });
      }
    },

    // The time under the pointer, floated over the scrub bar.
    _showScrubPeek: function(shape, clientX) {
      var el = shape && shape.el;
      var bar = shape && shape._audioBar;
      if (!el || !bar || !bar.w) return;
      var peek = el.querySelector(".etcher-audio-peek");
      var bg = el.querySelector(".etcher-audio-peek-bg");
      var txt = el.querySelector(".etcher-audio-peek-text");
      if (!peek || !bg || !txt) return;
      var st = shape.uuid ? this._audioState(shape.uuid) : null;
      var dur = st && st.duration;
      if (!dur) { this._hideScrubPeek(shape); return; }

      var x = Math.max(bar.x, Math.min(bar.x + bar.w, clientX - this._containerX(0)));
      var frac = (x - bar.x) / bar.w;
      txt.textContent = this._formatTime(frac * dur);

      // Sized off the bar rather than a constant, so it stays in proportion
      // as the card is zoomed.
      var fs = Math.max(9, Math.min(13, bar.h * 2.2));
      var w = Math.max(34, (txt.textContent.length + 1) * fs * 0.6);
      var h = fs * 1.9;
      // Clamped to the bar's span: a label hanging off the end of the card
      // points at nothing.
      var cx = Math.max(bar.x + w / 2, Math.min(bar.x + bar.w - w / 2, x));
      var y = bar.y - h - 6;
      bg.setAttribute("x", cx - w / 2);
      bg.setAttribute("y", y);
      bg.setAttribute("width", w);
      bg.setAttribute("height", h);
      bg.setAttribute("rx", Math.min(6, h / 2));
      txt.setAttribute("x", cx);
      txt.setAttribute("y", y + h / 2);
      txt.setAttribute("font-size", fs);
      peek.setAttribute("data-show", "");
    },

    _hideScrubPeek: function(shape) {
      var peek = shape && shape.el && shape.el.querySelector(".etcher-audio-peek");
      if (peek) peek.removeAttribute("data-show");
    },

    // A container-space x back into viewport space, for turning a pointer
    // position into a position along the scrub bar.
    _containerX: function(containerPx) {
      var r = this.handle && this.handle.container
        ? this.handle.container.getBoundingClientRect()
        : { left: 0 };
      return r.left + containerPx;
    },

    // Lay the card's parts out inside `box` (container px) and sync them to
    // wherever playback currently is. Runs on every render — so on every pan
    // and zoom frame, and on every `timeupdate` while playing.
    _layoutAudioCard: function(shape, box) {
      var el = shape.el;
      if (!el) return;
      var g = shape.geometry || {};
      var audio = shape._audioEl || null;

      var bg = el.querySelector(".etcher-audio-bg");
      var btn = el.querySelector(".etcher-audio-btn");
      var glyph = el.querySelector(".etcher-audio-glyph");
      var titleEl = el.querySelector(".etcher-audio-title");
      var timeEl = el.querySelector(".etcher-audio-time");
      var track = el.querySelector(".etcher-audio-track");
      var fill = el.querySelector(".etcher-audio-fill");
      var scrub = el.querySelector(".etcher-audio-scrub");

      // Both flags are set BEFORE the early return below: bailing out first
      // left `is-uploading` stuck on a card whose upload had finished, so a
      // ready video came back still dressed as a placeholder.
      var uploading = this._isPendingMedia(shape);
      el.classList.toggle("is-uploading", uploading);

      // Nothing on screen when a video isn't being pointed at — the picture
      // is the point of it.
      var visible = this._showsMediaChrome(shape);
      el.classList.toggle("is-chrome-hidden", !visible);
      if (!visible) return;

      // How big this card is being drawn compared with the size it was
      // designed at. Every bare number below — paddings, the corner radius,
      // the bar heights — was chosen for a card `AUDIO_CARD_H` tall on
      // screen, which is exactly the size `_insertMediaHref` gives a new one.
      // Multiplying by this makes the card a rigid piece of design that
      // scales as a single object: at its design size the numbers mean
      // literally what they say, and at any other size everything grows and
      // shrinks together.
      //
      // The zoom does not appear here at all, and that is the point. `box.h`
      // already carries it. Reading these as px-at-1:1 (which is what
      // `_zoomPx` gives) is the same mistake the connectors made: a board is
      // rarely at 1:1, so the caps collapsed — a 14px corner radius became
      // under 3px on a card 176px tall, squaring off the corners, and the
      // scrub bar thinned to a hairline.
      var cardK = box.h / AUDIO_CARD_H;
      var pad = Math.max(6 * cardK, Math.min(14 * cardK, box.h * 0.16));
      // NOT scaled, unlike everything else here. This is a legibility
      // threshold, not a size: below it there is no room on screen for a
      // title and a timecode, whatever the card measures in canvas units.
      // Scaling it makes it zoom-invariant — the card then keeps trying to
      // draw text at every zoom, and the title runs into the timecode.
      var compact = box.h < AUDIO_COMPACT_H;
      // The disc is sized off the card so the whole thing scales as one
      // object under zoom, rather than a fixed-size button on a card that
      // grows around it — but bounded by the SHORTER side, not the height.
      // Height alone meant a card resized tall and narrow grew a disc wider
      // than the card it sits in.
      var r = Math.max(7 * cardK, Math.min(
        (box.h - pad * 2) / 2, box.h * 0.28,
        (box.w - pad * 2) / 2, box.w * 0.18
      ));
      var cx = box.x + pad + r;
      var cy = box.y + box.h / 2;

      bg.setAttribute("x", box.x);
      bg.setAttribute("y", box.y);
      bg.setAttribute("width", box.w);
      bg.setAttribute("height", box.h);
      var radius = Math.max(4 * cardK, Math.min(14 * cardK, box.h * 0.18));
      bg.setAttribute("rx", radius);
      bg.setAttribute("ry", radius);
      // Floored so the outline does not vanish entirely on a wide view —
      // the same floor every other stroke on the board has.
      bg.style.strokeWidth = Math.max(0.4, 1.5 * cardK) + "px";

      btn.setAttribute("cx", cx);
      btn.setAttribute("cy", cy);
      btn.setAttribute("r", r);

      // Play triangle or pause bars, drawn to the disc's radius.
      var playing = !!(audio && !audio.paused && !audio.ended);
      var k = r * 0.46;
      if (uploading) {
        // No transport while there is nothing to drive. An enabled-looking
        // play button that does nothing is worse than no button.
        glyph.setAttribute("d", "");
      } else
      if (playing) {
        var bw = k * 0.62, gap = k * 0.5;
        glyph.setAttribute("d",
          "M " + (cx - gap - bw) + " " + (cy - k) + " h " + bw + " v " + (k * 2) + " h " + (-bw) + " Z " +
          "M " + (cx + gap) + " " + (cy - k) + " h " + bw + " v " + (k * 2) + " h " + (-bw) + " Z");
      } else {
        glyph.setAttribute("d",
          "M " + (cx - k * 0.55) + " " + (cy - k) +
          " L " + (cx + k * 0.95) + " " + cy +
          " L " + (cx - k * 0.55) + " " + (cy + k) + " Z");
      }

      var textLeft = cx + r + pad * 0.9;
      var textRight = box.x + box.w - pad;
      var pos = audio ? audio.currentTime : 0;
      var dur = (audio && isFinite(audio.duration) && audio.duration > 0)
        ? audio.duration
        : (typeof g.duration === "number" ? g.duration : null);

      // On a video the timecode sits inline at the right of the bar rather
      // than on its own row: the strip is deliberately short, but "where am
      // I in this" is the one number a player has to show.
      var inlineTime = shape.kind === "video" && !uploading;

      if (uploading) {
        // No transport while there is nothing to drive — a disabled player
        // reads as a broken one. The name, a progress bar, and a percentage,
        // centred so it holds at any card size.
        var ufs = Math.max(10 * cardK, Math.min(16 * cardK, box.h * 0.09));
        var mid = box.x + box.w / 2;
        var prog = shape._uploadProgress;
        titleEl.removeAttribute("visibility");
        timeEl.removeAttribute("visibility");
        titleEl.setAttribute("text-anchor", "middle");
        titleEl.setAttribute("x", mid);
        titleEl.setAttribute("y", box.y + box.h / 2 - ufs * 1.4);
        titleEl.setAttribute("font-size", ufs);
        titleEl.textContent = String(g.title || "Uploading");
        timeEl.setAttribute("text-anchor", "middle");
        timeEl.setAttribute("x", mid);
        timeEl.setAttribute("y", box.y + box.h / 2 + ufs * 1.8);
        timeEl.setAttribute("font-size", ufs);
        timeEl.textContent = typeof prog === "number"
          ? Math.round(prog * 100) + "%"
          : "Uploading\u2026";

        // The bar itself, reusing the scrub track and fill so there is one
        // place that knows how a bar is drawn.
        var pw = Math.min(box.w * 0.6, 260 * cardK);
        var ph = Math.max(3 * cardK, Math.min(6 * cardK, box.h * 0.03));
        var px = mid - pw / 2;
        var py = box.y + box.h / 2 - ph / 2;
        track.setAttribute("x", px);
        track.setAttribute("y", py);
        track.setAttribute("width", pw);
        track.setAttribute("height", ph);
        track.setAttribute("rx", ph / 2);
        fill.setAttribute("x", px);
        fill.setAttribute("y", py);
        // Indeterminate until the host reports something: a quarter-width
        // stub that animates, so "no progress reported" still reads as
        // alive rather than as stalled at zero.
        fill.setAttribute("width", typeof prog === "number" ? pw * prog : pw * 0.25);
        fill.setAttribute("height", ph);
        fill.setAttribute("rx", ph / 2);
        el.classList.toggle("is-indeterminate", typeof prog !== "number");
        shape._audioBar = null;
        return;
      } else if (compact && !inlineTime) {
        // Transport only — a title and a timecode would collide with the bar
        // and each other once the card is this short.
        titleEl.setAttribute("visibility", "hidden");
        timeEl.setAttribute("visibility", "hidden");
      } else if (inlineTime) {
        titleEl.setAttribute("text-anchor", "start");
        titleEl.setAttribute("visibility", "hidden");
        timeEl.removeAttribute("visibility");
        var vfs = Math.max(9 * cardK, Math.min(14 * cardK, box.h * 0.30));
        timeEl.setAttribute("x", textRight);
        timeEl.setAttribute("y", cy);
        timeEl.setAttribute("font-size", vfs);
        timeEl.textContent = this._formatTime(pos) + " / " + this._formatTime(dur);
      } else {
        titleEl.setAttribute("text-anchor", "start");
        timeEl.setAttribute("text-anchor", "end");
        titleEl.removeAttribute("visibility");
        timeEl.removeAttribute("visibility");
        var fs = Math.max(9 * cardK, Math.min(15 * cardK, box.h * 0.19));
        var rowY = box.y + box.h * 0.36;
        titleEl.setAttribute("x", textLeft);
        titleEl.setAttribute("y", rowY);
        titleEl.setAttribute("font-size", fs);
        // Truncated by character count against the space available: SVG has
        // no ellipsis, and `textLength` would squash the glyphs instead.
        var avail = Math.max(0, textRight - textLeft - fs * 3.2);
        var maxChars = Math.max(3, Math.floor(avail / (fs * 0.55)));
        var label = String(g.title || "Audio");
        titleEl.textContent =
          label.length > maxChars ? label.slice(0, maxChars - 1) + "\u2026" : label;

        timeEl.setAttribute("x", textRight);
        timeEl.setAttribute("y", rowY);
        timeEl.setAttribute("font-size", fs);
        timeEl.textContent = this._formatTime(pos) + " / " + this._formatTime(dur);
      }

      var barY = (compact || inlineTime) ? cy - 2 : box.y + box.h * 0.68;
      var barH = Math.max(3 * cardK, Math.min(6 * cardK, box.h * 0.07));
      var barX = textLeft;
      // Room for the timecode when it shares the row.
      var timeGap = inlineTime
        ? Math.max(46 * cardK, (timeEl.textContent || "").length * Math.max(9 * cardK, Math.min(14 * cardK, box.h * 0.30)) * 0.62)
        : 0;
      var barW = Math.max(0, textRight - barX - timeGap);
      track.setAttribute("x", barX);
      track.setAttribute("y", barY);
      track.setAttribute("width", barW);
      track.setAttribute("height", barH);
      track.setAttribute("rx", barH / 2);

      var frac = (dur && dur > 0) ? Math.max(0, Math.min(1, pos / dur)) : 0;
      fill.setAttribute("x", barX);
      fill.setAttribute("y", barY);
      fill.setAttribute("width", barW * frac);
      fill.setAttribute("height", barH);
      fill.setAttribute("rx", barH / 2);

      // Generous invisible scrub target over the bar — see `_makeAudioEl`.
      var scrubH = Math.max(barH, Math.min(22 * cardK, box.h * 0.34));
      scrub.setAttribute("x", barX);
      scrub.setAttribute("y", barY + barH / 2 - scrubH / 2);
      scrub.setAttribute("width", barW);
      scrub.setAttribute("height", scrubH);

      shape._audioBar = { x: barX, y: barY, w: barW, h: barH };
    },

    // A video's controls come and go with hover, and hover is a class toggle
    // that re-renders nothing on its own. No-op for every other kind.
    _refreshMediaChrome: function(shape) {
      if (shape && shape.kind === "video" && shape.el) this._renderShape(shape);
    },

    // A media card whose file is still on its way up.
    _isPendingMedia: function(shape) {
      return !!(shape && shape._pendingUpload &&
                !(shape.geometry && shape.geometry.href));
    },

    // Should this shape's transport be on screen? Audio is nothing but its
    // transport, so always. A video shows its controls while pointed at,
    // selected, or being edited — and always while uploading, since the
    // "card" is the only thing there is at that point.
    _showsMediaChrome: function(shape) {
      if (!shape) return false;
      if (shape.kind !== "video") return true;
      if (this._isPendingMedia(shape)) return true;
      return this._hoveredShape === shape ||
             this.editingShape === shape ||
             this._isInSelection(shape);
    },

    // Height of the transport strip laid over a video, from the video's own
    // height. Proportional so it scales with the picture under zoom, clamped
    // so it stays usable on a small one and doesn't dominate a large one.
    _videoBarH: function(videoH) {
      // Referenced to the video's own design size for the same reason the
      // card's constants are: `_insertMediaHref` gives a new video a box
      // VIDEO_BOX_H tall on screen, so that is the size these numbers were
      // picked at. The strip is then a fixed fraction of the picture at
      // every size, which is what "scales with the canvas" means here.
      var k = videoH / VIDEO_BOX_H;
      return Math.max(26 * k, Math.min(48 * k, videoH * 0.22));
    },

    // Stop and discard a shape's media element.
    _releaseMediaEl: function(shape) {
      var el = shape && shape._audioEl;
      if (!el) return;
      try { el.pause(); } catch (_) {}
      // Emptying the source releases the buffered data; removing the node
      // alone can leave a decoder holding it.
      try { el.removeAttribute("src"); el.load(); } catch (_) {}
      if (el.parentNode) el.parentNode.removeChild(el);
      shape._audioEl = null;
    },

    // Media elements sit outside the SVG, so a shape list rebuilt from
    // scratch leaves them orphaned — and, for audio, audible. Same sweep as
    // the image rings, for the same reason.
    _sweepMediaEls: function() {
      if (!this.mediaLayer) return;
      var vids = this.mediaLayer.querySelectorAll("video");
      if (!vids.length) return;
      var owned = [];
      (this.shapes || []).forEach(function(s) {
        if (s && s._audioEl) owned.push(s._audioEl);
      });
      for (var i = 0; i < vids.length; i++) {
        if (owned.indexOf(vids[i]) === -1 && vids[i].parentNode) {
          try { vids[i].pause(); } catch (_) {}
          vids[i].parentNode.removeChild(vids[i]);
        }
      }
    },

    // Kinds backed by a media element and the shared transport.
    _isMediaKind: function(kind) {
      return kind === "audio" || kind === "video";
    },

    // mm:ss, or --:-- until the duration is known.
    _formatTime: function(secs) {
      if (secs == null || !isFinite(secs) || secs < 0) return "--:--";
      var t = Math.floor(secs);
      var m = Math.floor(t / 60), sec = t % 60;
      return m + ":" + (sec < 10 ? "0" : "") + sec;
    },

    _imageRadius: function(w, h) {
      var r = Math.min(w, h) * IMAGE_RADIUS_RATIO;
      return Math.round(Math.max(IMAGE_RADIUS_MIN, Math.min(IMAGE_RADIUS_MAX, r)) * 10) / 10;
    },

    _shapeByUuid: function(uuid) {
      if (!uuid) return null;
      return (this.shapes || []).find(function(s) { return s.uuid === uuid; }) || null;
    },


    _embedImageFile: function(file, imagePt, opts) {
      var self = this;
      opts = opts || {};
      var reader = new FileReader();
      reader.onload = function() {
        if (typeof reader.result === "string") {
          self._activatePasted(self._insertImageHref(reader.result, {
            at: imagePt, metadata: opts.metadata
          }));
        }
      };
      reader.readAsDataURL(file);
    },

    // Per-layer uploader wins over the global one, so a page with several
    // layers can route one of them somewhere else.
    _imageUploader: function() {
      if (typeof this._uploadImageFn === "function") return this._uploadImageFn;
      if (window.Etcher && typeof window.Etcher.imageUploader === "function") {
        return window.Etcher.imageUploader;
      }
      return null;
    },

    // The part of the canvas (or picture) the person can see right now, as a
    // width and height in image coordinates; null when it cannot be measured.
    // Clamped to the canvas, so zooming out past its edge does not make the
    // visible area — and what is inserted into it — larger than the board.
    _visibleSpan: function() {
      var h = this.handle;
      if (!h || typeof h.screenToImage !== "function") return null;
      var c = h.container;
      if (!c || typeof c.getBoundingClientRect !== "function") return null;
      var r = c.getBoundingClientRect();
      if (!(r.width > 0) || !(r.height > 0)) return null;
      var a = h.screenToImage({ x: r.left, y: r.top });
      var b = h.screenToImage({ x: r.right, y: r.bottom });
      if (!a || !b) return null;
      var w = Math.abs(b.x - a.x);
      var hh = Math.abs(b.y - a.y);
      var size = this.imageSize;
      if (size && size.x > 0 && size.y > 0) {
        w = Math.min(w, size.x);
        hh = Math.min(hh, size.y);
      }
      return w > 0 && hh > 0 ? { w: w, h: hh } : null;
    },

    // How big a newly inserted image is drawn: as large as fits in
    // `INSERT_VIEW_SHARE` of what is visible, both ways, in the picture's own
    // proportions. A small image is scaled UP to that — at the default zoom of
    // a big board a picture at its natural size is a few pixels, and making
    // someone resize every paste is the thing this is for. An explicit
    // `maxSide` keeps the old rule (shrink to fit, never enlarge).
    _insertedImageSize: function(natW, natH, maxSide) {
      var w = natW > 0 ? natW : 400;
      var h = natH > 0 ? natH : 300;
      var scale;
      if (typeof maxSide === "number") {
        scale = Math.min(1, maxSide / Math.max(w, h));
      } else {
        var span = this._visibleSpan();
        scale = span
          ? Math.min(span.w * INSERT_VIEW_SHARE / w, span.h * INSERT_VIEW_SHARE / h)
          : Math.min(1, INSERT_FALLBACK_MAX_SIDE / Math.max(w, h));
      }
      return { w: Math.max(1, Math.round(w * scale)), h: Math.max(1, Math.round(h * scale)) };
    },

    // Place an image shape from a URL / data URL. `opts.at` = { x, y } in
    // image coords (defaults to viewport center); `opts.width`/`opts.height`
    // force a size, else the natural size is measured and the image is sized
    // to the visible area (`_insertedImageSize`); `opts.maxSide` instead caps
    // the longest side in canvas px. Returns the
    // new shape uuid when a size is given up front, else null (the shape
    // still appears once the image decodes — pass width/height if you need
    // the uuid synchronously).
    _insertImageHref: function(href, opts) {
      var self = this;
      opts = opts || {};
      // `previewHref` is the internal upload path: the shape is placed with an
      // empty `href` (nothing persistable exists yet) and drawn from a local
      // preview until the real URL lands. Measuring uses the preview, since
      // that's the only copy of the image there is at this point.
      var measureHref = opts.previewHref || href;
      if (typeof measureHref !== "string" || !measureHref) return null;
      var maxSide = typeof opts.maxSide === "number" ? opts.maxSide : null;

      // `sized`: the caller already worked the size out, so it is used as given
      // rather than fitted a second time.
      function place(natW, natH, sized) {
        var size = sized
          ? { w: Math.max(1, Math.round(natW)), h: Math.max(1, Math.round(natH)) }
          : self._insertedImageSize(natW, natH, maxSide);
        var w = size.w;
        var h = size.h;
        var center = (opts.at && typeof opts.at.x === "number")
          ? opts.at
          : self._viewportCenterImage();
        if (!center) {
          center = {
            x: self.imageSize ? self.imageSize.x / 2 : 0,
            y: self.imageSize ? self.imageSize.y / 2 : 0
          };
        }
        var payload = {
          kind: "image",
          geometry: {
            x: Math.round(center.x - w / 2),
            y: Math.round(center.y - h / 2),
            w: w, h: h, href: typeof href === "string" ? href : ""
          }
        };
        if (opts.metadata) payload.metadata = opts.metadata;
        var uuid = self._addShape(payload);
        if (uuid && opts.previewHref) {
          var placed = self._shapeByUuid(uuid);
          // Set before the queued emit runs, and off `geometry` — this is
          // what the element draws from while `geometry.href` is still empty.
          if (placed) {
            placed._previewHref = opts.previewHref;
            self._renderShape(placed);
          }
        }
        // Internal: lets the upload path get hold of the shape even on the
        // async measure branch, where the uuid isn't returned to the caller.
        if (uuid && typeof opts.onPlaced === "function") opts.onPlaced(uuid);
        return uuid;
      }

      if (typeof opts.width === "number" && typeof opts.height === "number") {
        return place(opts.width, opts.height, true);
      }
      var probe = new Image();
      probe.onload = function() { place(probe.naturalWidth, probe.naturalHeight); };
      probe.onerror = function() { place(0, 0); };
      probe.src = measureHref;
      return null;
    },

    // Image-space coordinate at the center of the visible viewport. Canvas
    // mode only (returns null on strip / before the handle is ready).
    _viewportCenterImage: function() {
      if (!this.handle || typeof this.handle.screenToImage !== "function") return null;
      var c = this.handle.container;
      if (!c || typeof c.getBoundingClientRect !== "function") return null;
      var r = c.getBoundingClientRect();
      return this.handle.screenToImage({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
    },

    // Document-level paste → insert an image at the viewport center. Gated on
    // `pasteImages`, the layer being visible, and the paste NOT landing in a
    // text field (so pasting into etcher's text editor still types). One
    // caveat: with multiple visible canvas layers on a page, each would
    // insert — single-canvas is the assumed common case.
    // Drag a file from the desktop onto the canvas and it lands where it was
    // dropped. Nothing handled `drop` before this — not for images either —
    // so dragging a file onto a board did what a browser does with an
    // unhandled drop: navigated away from the page to open the file.
    //
    // Attached to the container rather than the overlay: the overlay is
    // `pointer-events: none` for most of its life so pan and zoom pass
    // through it, and the container is the element that reliably sees the
    // drag whatever is beneath the cursor.
    _wireFileDrop: function() {
      var self = this;
      var container = self.handle && self.handle.container;
      if (!container || self._dropWired) return;
      self._dropWired = true;

      function hasFiles(e) {
        var dt = e.dataTransfer;
        if (!dt) return false;
        // `types` is the only thing readable during dragover — the files
        // themselves are withheld until drop.
        var types = dt.types;
        if (!types) return false;
        for (var i = 0; i < types.length; i++) {
          if (types[i] === "Files") return true;
        }
        return false;
      }

      self._dragOverHandler = function(e) {
        if (!self.annotationMode || self.annotationsVisible === false) return;
        if (!hasFiles(e)) return;
        // Both required: without preventDefault the browser refuses the drop
        // and opens the file instead.
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      };

      self._dropHandler = function(e) {
        if (!self.annotationMode || self.annotationsVisible === false) return;
        var dt = e.dataTransfer;
        var files = (dt && dt.files) || [];
        if (!files.length) return;
        e.preventDefault();
        var at = null;
        try { at = self._toImage(e); } catch (_) {}
        for (var i = 0; i < files.length; i++) {
          // Successive files are nudged apart, or a multi-file drop stacks
          // them all on one spot and looks like a single one arrived.
          var pt = at
            ? { x: at.x + i * 24, y: at.y + i * 24, imageIdx: at.imageIdx }
            : null;
          self._insertMediaFile(files[i], pt);
        }
      };

      container.addEventListener("dragover", self._dragOverHandler);
      container.addEventListener("drop", self._dropHandler);
    },

    // ----- Shape clipboard (⌘C / ⌘X / ⌘V) ------------------------------------
    // Shapes travel as a JSON envelope on the DOM copy/paste events — the
    // one clipboard channel that needs no permission prompt, works across
    // tabs and boards, and hands the data over synchronously. Written to
    // BOTH a custom MIME type and text/plain: the custom type is the
    // honest label, the text fallback is what survives apps that only
    // carry text (and makes a paste of the JSON from anywhere work).

    _serializeSelection: function() {
      function clone(v) {
        if (v == null) return v;
        try { return JSON.parse(JSON.stringify(v)); } catch (_) { return v; }
      }
      var targets = (this.selectedShapes && this.selectedShapes.length)
        ? this.selectedShapes.slice()
        : (this.editingShape ? [this.editingShape] : []);
      if (!targets.length) return null;
      var shapes = targets.map(function(s) {
        // No uuid on purpose — a payload carrying one would make
        // `_addShape` REUSE it, and pasting onto the same board would
        // collide with the original.
        var p = { kind: s.kind, geometry: clone(s.geometry) };
        if (s.style != null) p.style = clone(s.style);
        if (s.metadata != null) p.metadata = clone(s.metadata);
        if (typeof s.image_idx === "number") p.image_idx = s.image_idx;
        if (typeof s.image_id === "string") p.image_id = s.image_id;
        return p;
      });
      return { etcher: "shapes", version: 1, shapes: shapes };
    },

    // Clipboard text → shape payloads, or null when the text is not ours.
    // The cheap indexOf pre-check keeps every ordinary text paste from
    // paying for a JSON.parse of arbitrary content; the count cap keeps a
    // hostile payload from creating shapes until the tab dies.
    _parseShapeEnvelope: function(text) {
      if (!text || typeof text !== "string") return null;
      var s = text.trim();
      if (s.charAt(0) !== "{" || s.indexOf('"etcher"') === -1) return null;
      var parsed;
      try { parsed = JSON.parse(s); } catch (_) { return null; }
      if (!parsed || parsed.etcher !== "shapes" || !Array.isArray(parsed.shapes)) {
        return null;
      }
      return parsed.shapes.slice(0, 200);
    },

    // Materialize pasted payloads: +16px offset (so a same-board paste
    // doesn't land invisibly under its source), one create-undo entry
    // each, and the result selected — a single shape straight into edit
    // mode, several as a multi-selection. Mirrors what ⌘D leaves behind.
    _pasteShapes: function(payloads) {
      var self = this;
      var OFFSET = 16;
      function clone(v) {
        if (v == null) return v;
        try { return JSON.parse(JSON.stringify(v)); } catch (_) { return v; }
      }
      var made = [];
      (payloads || []).forEach(function(p) {
        if (!p || typeof p !== "object" ||
            typeof p.kind !== "string" || !p.geometry) return;
        var payload = {
          kind: p.kind,
          geometry: self._translateGeometry(p.kind, clone(p.geometry), OFFSET, OFFSET)
        };
        if (p.style != null) payload.style = clone(p.style);
        if (p.metadata != null) payload.metadata = clone(p.metadata);
        if (typeof p.image_idx === "number") payload.image_idx = p.image_idx;
        if (typeof p.image_id === "string") payload.image_id = p.image_id;
        var uuid = self._addShape(payload);
        if (uuid) made.push(uuid);
      });
      if (!made.length) return false;
      self._clearSelection();
      self._exitEditMode();
      made.forEach(function(uuid) {
        var shape = self.shapes.find(function(x) { return x.uuid === uuid; });
        if (!shape) return;
        self._pushUndoCreate(shape);
        self._addToSelection(shape);
      });
      if (made.length === 1) {
        var only = self.shapes.find(function(x) { return x.uuid === made[0]; });
        if (only) self._enterEditMode(only);
      }
      self._syncActionBar();
      return true;
    },

    // Copy (and cut) fire from the DOM events, not from a keydown: the
    // event hands over a writable clipboardData with no permissions, and
    // firing only when the browser itself decided this was a copy keeps
    // every native behavior (address bar, devtools, text fields) intact.
    _onShapeCopy: function(e, isCut) {
      if (!this.annotationMode) return;
      var t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" ||
                t.isContentEditable === true)) return;
      // A real text selection on the page wins — the user is copying words.
      try {
        var sel = window.getSelection && window.getSelection();
        if (sel && String(sel).length) return;
      } catch (_) {}
      var envelope = this._serializeSelection();
      if (!envelope || !e.clipboardData) return;
      e.preventDefault();
      var json = JSON.stringify(envelope);
      try { e.clipboardData.setData("application/x-etcher-shapes", json); } catch (_) {}
      e.clipboardData.setData("text/plain", json);
      if (isCut) {
        if (this.selectedShapes && this.selectedShapes.length) {
          this._deleteSelectedShapes();
        } else if (this.editingShape) {
          this._deleteShape(this.editingShape);
        }
      }
    },

    _wireShapeClipboard: function() {
      var self = this;
      self._copyHandler = function(e) { self._onShapeCopy(e, false); };
      self._cutHandler = function(e) { self._onShapeCopy(e, true); };
      document.addEventListener("copy", self._copyHandler);
      document.addEventListener("cut", self._cutHandler);
    },

    _wireImagePaste: function() {
      var self = this;
      self._pasteHandler = function(e) {
        if (self.annotationsVisible === false) return;
        var t = e.target;
        if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable === true)) return;
        if (document.activeElement && document.activeElement.isContentEditable) return;
        // Etcher shapes win over everything: our own envelope on the
        // clipboard means the user copied shapes, and turning it into a
        // text label of raw JSON (the text branch below) would be the
        // worst possible reading of their intent. Checked regardless of
        // `pasteImages` — that flag governs media, not shapes.
        if (self.annotationMode) {
          var shapesJson = e.clipboardData &&
            (e.clipboardData.getData("application/x-etcher-shapes") ||
             e.clipboardData.getData("text/plain"));
          var payloads = self._parseShapeEnvelope(shapesJson);
          if (payloads) {
            e.preventDefault();
            self._pasteShapes(payloads);
            return;
          }
        }
        if (self.pasteImages === false) return;
        var items = (e.clipboardData && e.clipboardData.items) || [];
        // Images win over text. Copying from a page often puts both on the
        // clipboard — an <img> carries its markup as text/html and its alt
        // as text/plain — and the picture is what the user meant.
        for (var i = 0; i < items.length; i++) {
          var it = items[i];
          if (it.kind === "file" && it.type && it.type.indexOf("image/") === 0) {
            var file = it.getAsFile();
            if (file) { e.preventDefault(); self._insertMediaFile(file); return; }
          }
        }

        var text = e.clipboardData && e.clipboardData.getData("text/plain");
        if (text && String(text).trim()) {
          e.preventDefault();
          self._insertPastedText(String(text).trim());
        }
      };
      document.addEventListener("paste", self._pasteHandler);
    },

    // A pasted URL becomes a preview card when the host can build one, and
    // a text shape when it can't.
    //
    // The text shape goes down FIRST either way. Unfurling is a network
    // round trip on someone else's server, and putting nothing on the canvas
    // until it answers would make a paste look like it did nothing — the
    // same failure the image path was fixed for. If a card comes back it
    // takes the text shape's place; if it doesn't, the URL is already there
    // as text and nothing was lost.
    _insertPastedText: function(text) {
      var self = this;
      var unfurl = self._linkUnfurler();
      var isUrl = /^https?:\/\/\S+$/i.test(text);

      // Ordinary text is on the canvas before the key is up. Nothing to
      // decide, nothing to wait for.
      if (!unfurl || !isUrl) return self._placePastedText(text);

      // A URL is settled BEFORE anything is drawn. Placing the text first
      // and swapping it for a card a moment later meant every pasted link
      // flashed as raw text and then jumped — the canvas showing its working
      // out. So the canvas stays as it was and the status line says what is
      // happening, which is the honest place for "this is taking a moment".
      self._showToast("Link detected — building preview…", { sticky: true });

      function fallBackToText(why) {
        if (window.console && console.warn) {
          console.warn("[Etcher] link unfurl failed; pasting as text:", why);
        }
        self._showToast("No preview available — pasted as text", { ms: 2600 });
        self._placePastedText(text);
      }

      var pending;
      try {
        pending = unfurl(text, { frescoId: self.frescoId });
      } catch (e) {
        fallBackToText(e);
        return null;
      }
      if (!pending || typeof pending.then !== "function") {
        fallBackToText("uploader did not return a promise");
        return null;
      }

      pending.then(function(card) {
        if (!card || typeof card.svg !== "string" || !card.svg) {
          return fallBackToText("no svg");
        }
        // Rasterise here rather than server-side: the browser has a correct
        // SVG renderer with the right fonts already, which spares the host a
        // native rasterizer dependency. The PNG then goes through the very
        // same path as a pasted image, so it uploads to storage and gets a
        // preview while it does.
        self._svgToPngFile(card.svg, card.width, card.height, function(file) {
          if (!file) return fallBackToText("could not rasterise the card");
          self._hideToast();
          self._insertMediaFile(file, null, { metadata: { link: text } });
        });
      }, fallBackToText);
      return null;
    },

    // Read the clipboard on demand, for the toolbar's paste button.
    //
    // Deliberately NOT the same route as ⌘V: a paste event hands us its own
    // data, whereas this has to ASK, which needs a secure context, a user
    // gesture (the click is one) and permission the user can refuse. All
    // three fail in ways worth reporting rather than swallowing — a button
    // that silently does nothing is worse than one that says why.
    //
    // Images win over text, matching the paste-event path: a copy from a web
    // page usually carries both.
    _pasteFromClipboard: function() {
      var self = this;
      var clip = navigator.clipboard;

      if (!clip || (!clip.read && !clip.readText)) {
        return self._showToast("This browser won't share the clipboard", { ms: 3000 });
      }

      function pasteText() {
        if (!clip.readText) {
          return self._showToast("Nothing to paste", { ms: 2000 });
        }
        return clip.readText().then(function(text) {
          if (text && text.trim()) self._insertPastedText(text.trim());
          else self._showToast("Clipboard is empty", { ms: 2000 });
        });
      }

      function refused(err) {
        // Firefox has no `read()` for pages at all, and every browser lets
        // the user decline. Either way the clipboard stayed private, which
        // is a choice to respect and report, not an error to log.
        self._showToast("Clipboard permission denied", { ms: 3000 });
        if (window.console && console.warn) {
          console.warn("[Etcher] clipboard read refused:", err);
        }
      }

      if (!clip.read) return pasteText().catch(refused);

      clip.read().then(function(items) {
        for (var i = 0; i < items.length; i++) {
          var types = items[i].types || [];
          for (var t = 0; t < types.length; t++) {
            if (types[t].indexOf("image/") === 0) {
              var type = types[t];
              return items[i].getType(type).then(function(blob) {
                self._insertImageFile(
                  new File([blob], "pasted." + type.split("/")[1], { type: type })
                );
              });
            }
          }
        }
        return pasteText();
      }).catch(function(err) {
        // `read()` also rejects when the clipboard holds only text in some
        // browsers, so fall back before deciding it was a refusal.
        pasteText().catch(function() { refused(err); });
      });
    },

    _placePastedText: function(text) {
      var uuid = this._insertTextShape(text);
      if (uuid) this._activatePasted(uuid);
      return uuid;
    },

    // A one-line status over the canvas. Used for the things that happen
    // between a gesture and its result — currently only the link unfurl,
    // which is a round trip to someone else's server and the one paste that
    // can't be instant.
    _showToast: function(message, opts) {
      opts = opts || {};
      var self = this;
      if (!self.toastEl) {
        var el = document.createElement("div");
        el.className = "etcher-toast";
        el.setAttribute("role", "status");
        self.handle.container.appendChild(el);
        self.toastEl = el;
      }
      self.toastEl.textContent = message;
      self.toastEl.classList.add("is-open");
      clearTimeout(self._toastTimer);
      if (!opts.sticky) {
        self._toastTimer = setTimeout(function() { self._hideToast(); },
          opts.ms || 2000);
      }
    },

    _hideToast: function() {
      clearTimeout(this._toastTimer);
      if (this.toastEl) this.toastEl.classList.remove("is-open");
    },

    // SVG string → PNG File, via the browser's own renderer.
    _svgToPngFile: function(svg, w, h, cb) {
      var width = Number(w) > 0 ? Number(w) : 640;
      var height = Number(h) > 0 ? Number(h) : 480;
      var blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
      var url = URL.createObjectURL(blob);
      var img = new Image();

      function done(file) {
        try { URL.revokeObjectURL(url); } catch (_) {}
        cb(file);
      }

      img.onload = function() {
        try {
          var c = document.createElement("canvas");
          // 2x, so the card is still sharp when someone zooms into it.
          c.width = width * 2;
          c.height = height * 2;
          c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
          c.toBlob(function(png) {
            done(png ? new File([png], "link-card.png", { type: "image/png" }) : null);
          }, "image/png");
        } catch (_) {
          done(null);
        }
      };
      img.onerror = function() { done(null); };
      img.src = url;
    },

    _linkUnfurler: function() {
      if (typeof this._unfurlLinkFn === "function") return this._unfurlLinkFn;
      if (window.Etcher && typeof window.Etcher.linkUnfurler === "function") {
        return window.Etcher.linkUnfurler;
      }
      return null;
    },

    // Swap the placeholder text shape for the card, anchored where the text
    // was so the card appears at the paste rather than jumping to the middle
    // of the viewport.
    _replaceWithLinkCard: function(uuid, url, file) {
      var shape = this._shapeByUuid(uuid);
      if (!shape) return;                       // deleted while unfurling
      var at = {
        x: shape.geometry.x + shape.geometry.w / 2,
        y: shape.geometry.y + shape.geometry.h / 2
      };
      this._removeShape(uuid);
      // Straight down the pasted-image path: preview, upload, storage URL.
      // The address the card was made from rides along in metadata — it's
      // the only part of a picture-of-a-page a consumer can act on.
      this._insertImageFile(file, at, { metadata: { link: url } });
    },

    // Pasted text lands as an ordinary text shape — double-click to edit,
    // drag to move, resize by its corners, same as one drawn with the text
    // tool. It is not a special "pasted" kind: the whole point is that it
    // stops being a paste the moment it is on the canvas.
    _insertTextShape: function(text, opts) {
      opts = opts || {};
      var trimmed = String(text).trim();
      if (!trimmed) return null;

      // Long pastes are clipped rather than allowed to become a wall of
      // text on the canvas. The full string is still what got copied — the
      // user can paste the rest deliberately if they want it.
      if (trimmed.length > 500) trimmed = trimmed.slice(0, 500) + "…";

      var basePx = this._textDefaultBoxInkPx();
      // Roughly half an em per character at the default size, wrapped at a
      // comfortable measure, so a short paste gets a short box and a long
      // one gets a taller one instead of a single unreadable line.
      var perLine = 42;
      var lines = Math.max(1, Math.ceil(trimmed.length / perLine));
      var w = basePx * Math.min(perLine, Math.max(8, trimmed.length)) * 0.5;
      var h = basePx * 1.3 * lines;

      var center = (opts.at && typeof opts.at.x === "number")
        ? opts.at
        : this._viewportCenterImage();
      if (!center) {
        center = {
          x: this.imageSize ? this.imageSize.x / 2 : 0,
          y: this.imageSize ? this.imageSize.y / 2 : 0
        };
      }

      return this._addShape({
        kind: "text",
        geometry: {
          x: Math.round(center.x - w / 2),
          y: Math.round(center.y - h / 2),
          w: Math.round(w),
          h: Math.round(h)
        },
        metadata: { title: trimmed },
        style: { color: this.activeColor || null }
      });
    },

    _unwireImagePaste: function() {
      if (this._pasteHandler) {
        document.removeEventListener("paste", this._pasteHandler);
        this._pasteHandler = null;
      }
      if (this._copyHandler) {
        document.removeEventListener("copy", this._copyHandler);
        this._copyHandler = null;
      }
      if (this._cutHandler) {
        document.removeEventListener("cut", this._cutHandler);
        this._cutHandler = null;
      }
    },

    // Let pan reach ink outside the picture. Fresco clamps panning to the
    // canvas, so a stroke drawn above a wide image (or beside a tall one)
    // became unreachable the moment you zoomed in — you could SEE it at
    // fit zoom and never get to it. Whenever annotations change, the pan
    // bounds grow to the union of the canvas and everything drawn, padded
    // on the spilled sides only — no ink outside, no extra slack — and
    // collapse back to the default clamp when the last outside shape goes.
    _syncPanBounds: function() {
      if (this.handleKind !== "canvas") return;
      var h = this.handle;
      if (!h || typeof h.setPanBounds !== "function" ||
          typeof h.getCanvasSize !== "function") return;
      // An infinite canvas has no clamp to loosen — setting bounds there
      // would ADD one, turning a free board into a fenced one the moment
      // a shape strayed. Only manage bounds where Fresco clamps by
      // default, and only when the handle can say which it is.
      if (typeof h.isInfiniteCanvas !== "function" || h.isInfiniteCanvas()) {
        return;
      }
      var size = null;
      try { size = h.getCanvasSize(); } catch (_) {}
      if (!size || !(size.width > 0) || !(size.height > 0)) return;
      var minX = 0, minY = 0, maxX = size.width, maxY = size.height;
      var self = this;
      // Framed as one with the ink (see below), the ink is measured as
      // drawn: a stroke's own width round its centre line.
      var inkWidth = false;
      try { inkWidth = !!(h.getPanCentre && h.getPanCentre() === "bounds"); } catch (_) {}
      (this.shapes || []).forEach(function(sh) {
        var boxes = [];
        try {
          var b = self._shapeBBoxImagePx(sh);
          var half = inkWidth && !self._isTextKind(sh.kind) && sh.style && sh.style.width > 0
            ? sh.style.width / 2 : 0;
          if (b && half) b = { x: b.x - half, y: b.y - half, w: b.w + 2 * half, h: b.h + 2 * half };
          if (b) boxes.push(b);
        } catch (_) {}
        // Labels hang off their shapes, and a label can be the very thing
        // parked outside.
        if (sh._renderedTitleImage) boxes.push(sh._renderedTitleImage);
        boxes.forEach(function(r) {
          if (!r) return;
          if (r.x < minX) minX = r.x;
          if (r.y < minY) minY = r.y;
          if (r.x + (r.w || 0) > maxX) maxX = r.x + (r.w || 0);
          if (r.y + (r.h || 0) > maxY) maxY = r.y + (r.h || 0);
        });
      });
      var spills = minX < 0 || minY < 0 ||
        maxX > size.width || maxY > size.height;
      if (!spills) {
        // Back to the stock clamp — but only if we were the ones who
        // loosened it, so a host's own setPanBounds is never clobbered.
        if (this._panBoundsActive) {
          this._panBoundsActive = false;
          try { h.setPanBounds(null); } catch (_) {}
        }
        // And the zoom floor: restore the HOST'S floor (captured when we
        // first lowered it), not a blind null.
        if (this._zoomFloorManaged) {
          this._zoomFloorManaged = false;
          try { h.setZoomFloor(this._hostZoomFloor); } catch (_) {}
          this._hostZoomFloor = null;
        }
        return;
      }
      // Breathing room on the spilled sides, so the outermost stroke isn't
      // glued to the clamp edge — and the SAME room on the opposite side,
      // which is what keeps zooming smooth.
      //
      // The reason is a seam in how a view is positioned. Zoomed in, the
      // translate is clamped to these bounds; zoomed out past the point
      // where everything fits, there is nothing to clamp and the viewer
      // centres the PICTURE instead. Both are right, but they only agree
      // if this rect is centred on the picture too. Padded on the spilled
      // side alone it is not, so crossing that point moved the picture by
      // however lopsided the rect was — measured at 192px on a board with
      // a note above the photo, and felt as a jump mid-zoom, down on the
      // way out and back on the way in.
      //
      // Mirroring the reach makes the two positions the same number at the
      // crossover, so the seam disappears. What it costs is being able to
      // pan into empty space on the side with less ink, which is nothing:
      // there was never anything to see there, and the clamp still stops
      // at a fixed edge.
      //
      // An axis nothing spilled on keeps the exact default, so a drawing
      // inside the picture changes nothing at all.
      var spillX = Math.max(Math.max(0, -minX), Math.max(0, maxX - size.width));
      var spillY = Math.max(Math.max(0, -minY), Math.max(0, maxY - size.height));
      var padX = spillX > 0 ? spillX + PAN_BOUNDS_PAD : 0;
      var padY = spillY > 0 ? spillY + PAN_BOUNDS_PAD : 0;

      var rect = {
        x: -padX,
        y: -padY,
        width: size.width + padX * 2,
        height: size.height + padY * 2
      };
      // A host that centres the bounds themselves (`pan_centre: :bounds`)
      // has no seam to hide — zoomed in or out, the same rect places the
      // view — so the rect is the picture and the ink as they are, with
      // the same room round both ends of a spilled axis: centred, it frames
      // them as one, the way a flat copy of the drawing does.
      var centresBounds = false;
      try { centresBounds = !!(h.getPanCentre && h.getPanCentre() === "bounds"); } catch (_) {}
      if (centresBounds) {
        var lo = { x: Math.min(0, minX), y: Math.min(0, minY) };
        var hi = { x: Math.max(size.width, maxX), y: Math.max(size.height, maxY) };
        var rx = spillX > 0 ? PAN_BOUNDS_PAD : 0, ry = spillY > 0 ? PAN_BOUNDS_PAD : 0;
        rect = { x: lo.x - rx, y: lo.y - ry, width: hi.x - lo.x + 2 * rx, height: hi.y - lo.y + 2 * ry };
      }
      this._panBoundsActive = true;
      try { h.setPanBounds(rect); } catch (_) {}

      // Panning to the ink was half of it: the zoom FLOOR still only let
      // the view fit the picture, so "zoom all the way out and see
      // everything" stopped short of the very shapes the pan could reach.
      // Lower the floor to where the whole content rect fits the viewport
      // (all four ratios, so the guarantee holds under rotation too). The
      // Home/reset button is untouched — it fits the IMAGE, as ever; this
      // only extends how far the wheel may go.
      if (typeof h.setZoomFloor === "function") {
        var vw = 0, vh = 0;
        try {
          var cr = h.container.getBoundingClientRect();
          vw = cr.width;
          vh = cr.height;
        } catch (_) {}
        if (vw > 0 && vh > 0 && rect.width > 0 && rect.height > 0) {
          if (!this._zoomFloorManaged) {
            this._zoomFloorManaged = true;
            // Whatever floor the host had — null for the default — is
            // what un-spilling restores.
            this._hostZoomFloor = (typeof h.getZoomFloor === "function")
              ? h.getZoomFloor()
              : null;
          }
          h.setZoomFloor(Math.min(
            vw / rect.width, vh / rect.height,
            vw / rect.height, vh / rect.width
          ));
        }
      }
    },

    _emitChanged: function() {
      // Every change worth persisting is also a moment the reachable
      // canvas may have grown or shrunk — before the host guard, because
      // pan bounds matter on hosts that persist nothing.
      this._syncPanBounds();
      if (!this.pushEventTo) return;
      var stripMode = this.handleKind === "strip";
      // An image still uploading IS included: its position, size, layering
      // and a thumbnail are all real from the first frame, so it survives a
      // reload and shows up for peers. What it never carries is the local
      // object URL it is being *drawn* from — a blob: URL means nothing
      // outside this document. The stored URL replaces the thumbnail in a
      // second emit when the upload resolves.
      var payload = (this.shapes || []).filter(function(s) {
        // A media card placed before its upload finishes has no source yet,
        // and unlike an image there is no thumbnail to stand in — a sound has
        // nothing to draw. Persisting or broadcasting it would put a card
        // with nothing to play on every peer, and strand one for good if the
        // uploader never came back. It joins the payload on the second emit,
        // when the URL lands.
        return !(s._pendingUpload && !(s.geometry && s.geometry.href));
      }).map(function(s) {
        var entry = { uuid: s.uuid, kind: s.kind, geometry: s.geometry };
        // Strip annotations carry per-image index (which page of N).
        // Canvas multi-image annotations carry `image_id` (which
        // image on the canvas) — same purpose, different field
        // because the two coordinate models are otherwise distinct
        // (image-natural px vs canvas-pixel). Single-image canvases
        // omit `image_id` entirely.
        if (stripMode && typeof s.image_idx === "number") {
          entry.image_idx = s.image_idx;
        }
        if (!stripMode && typeof s.image_id === "string") {
          entry.image_id = s.image_id;
        }
        if (s.style != null) entry.style = s.style;
        if (s.metadata != null) entry.metadata = s.metadata;
        return entry;
      });
      this.pushEventTo(this.el, "etcher:annotations-changed", {
        fresco_id: this.frescoId || null,
        annotations: payload
      });
    },

    // Dispatch a bubbling CustomEvent on the layer host so consumer JS
    // can react without forking etcher.js. Documented in the README.
    _dispatch: function(name, detail) {
      if (!this.el || typeof CustomEvent !== "function") return;
      try {
        this.el.dispatchEvent(new CustomEvent(name, {
          detail: detail || {},
          bubbles: true
        }));
      } catch (_) {}
    },

    _setAnnotationMode: function(on) {
      var self = this;
      if (self.annotationMode === on) return;
      self.annotationMode = on;
      // The selection frame only belongs to the cursor in annotation mode.
      setTimeout(function() { self._syncTransformBox(); }, 0);
      self._syncNavPencil();
      if (self.toolbar) self.toolbar.classList.toggle("is-active", on);
      self._syncActionBar();
      self._syncStylePanel();
      if (!on) self._closeActionMenu();
      if (on) self._positionActionBar();
      // Toolbar just became visible (or just hid). When visible, run
      // the overflow layout immediately — `_layoutToolbar` bails when
      // the toolbar is hidden, so the `off` path is a no-op.
      if (on) self._layoutToolbar();
      else self._liftViewerZoom();

      // Turning the mode on ARMS the cursor tool rather than merely
      // behaving like it. The board has always started in cursor mode
      // (`activeTool == null`), but nothing said so: the toolbar opened
      // with no button lit, and a user reading it had to try one to find
      // out what the pointer would do. Selecting it for real lights the
      // button, shows the arrow cursor, and locks drag-pan the same way
      // reaching for the tool by hand does — one state, arrived at by
      // one path.
      if (on) self._selectTool(null);

      // In Fresco 0.5 pan/zoom isn't a set of toggleable gesture flags
      // on an OSD viewer — it's the engine's own pointer/wheel handlers
      // on the host. We block them while drawing by:
      //   (1) routing pointer events through the overlay wrapper (which
      //       has `pointer-events: auto` only when annotation mode is on),
      //   (2) calling `e.stopPropagation()` inside the overlay's own
      //       pointerdown handler so it never bubbles to Fresco's host
      //       and triggers `setPointerCapture` → pan.
      // No gesture-flag freeze/restore dance is needed.

      if (!on) {
        self._selectTool(null);
        self._cancelDraft();
        self._exitEditMode();
        self._clearSelection();
        self._closePopup();
      }

      // Lock/unlock drag-pan for box-select now that the mode (and thus
      // whether the cursor tool is "armed") has changed.
      self._applyPanLock();

      // The container's cursor is `_selectTool`'s business now, both
      // ways: entering calls it above (arrow while pan is locked), and
      // leaving calls it in the `!on` branch, which hands the cursor back
      // to Fresco's own CSS. The line that used to sit here ran AFTER
      // both and wrote a plain `default` over the toolbar arrow.

      self._dispatch("etcher:mode-changed", { annotationMode: on });
    },

    _selectTool: function(toolKey) {
      var self = this;
      var prevTool = self.activeTool;
      setTimeout(function() { self._syncTransformBox(); }, 0);
      if (self.activeTool !== toolKey) self._cancelDraft();
      // Leaving the eraser tool clears any in-flight hover preview
      // so a grayed shape doesn't get stuck looking "about to delete"
      // after the user picks a different tool.
      if (self.activeTool === "eraser" && toolKey !== "eraser") {
        self._clearEraserHover();
      }
      // Leaving the red pointer takes the dot away — here, and through the
      // host, on everyone else's board. A pointer left behind after someone
      // has stopped presenting is worse than never having had one.
      if (self.activeTool === "pointer" && toolKey !== "pointer") {
        self._endPointer();
      }
      self.activeTool = toolKey;
      // Connector dots are a cursor-tool affordance. Any other tool means
      // the user is drawing, and eight hit-targets sitting on the shape
      // would swallow the first click of that stroke.
      if (toolKey != null) self._removeConnectorDots();
      // Switching tools also abandons any arrow being dragged. Belt and
      // braces for the flag rather than the gesture: `_arrowDrag` suppresses
      // the dots, so a drag that somehow never saw its pointerup would turn
      // connectors off for the rest of the session with no way back. Picking
      // a tool is the one reset a user can always reach.
      if (self._arrowDrag) {
        self._arrowDrag = null;
        self._removeTargetDots();
      }
      // Drawing and editing are mutually exclusive — picking a tool
      // means we're done admiring the current edit. Same goes for
      // multi-selection: entering draw mode clears the group so a
      // stray Backspace mid-draw doesn't wipe the selected shapes.
      //
      // An open label editor closes here too. The toolbar is chrome, and
      // a press on chrome is remembered as a trip to the menus — so
      // without this, placing a dimension and then reaching for a tool
      // left the editor waiting, and the first stroke drawn afterwards
      // was spent putting the caret back in a label the user had already
      // walked away from. Reaching for a drawing tool IS walking away:
      // whatever was typed is kept, an empty box is dropped, and the
      // stroke that follows is a stroke.
      if (toolKey != null) {
        if (self._textEditor) self._commitTextEdit();
        self._exitEditMode();
        self._clearSelection();
      }

      // Each ink tool keeps its own colour AND its own weight, apart from
      // the palette and thickness the shapes share. Arming one banks the
      // shared selection (slot, colour and width — the colour may not live
      // in any slot), loads the tool's remembered pair, and disarming
      // restores the bank — so a fat yellow highlighting session never
      // leaks into the next rectangle. Marker-to-highlighter keeps the one
      // bank: only the first ink arm is a shared state worth returning to.
      var wasInk = prevTool === "marker" || prevTool === "highlighter";
      var isInk = toolKey === "marker" || toolKey === "highlighter";
      if (isInk) {
        if (!wasInk) {
          self._bankedSharedColor = { slots: (self._colorSlots || []).slice(),
                                      slot: self._activeSlot,
                                      color: self.activeColor,
                                      width: (self.lineParams || {}).width };
        }
        // Each ink tool starts on its OWN set, not on whatever the shapes
        // happen to be drawn in — so the pen comes up as a pen and the
        // highlighter as a highlighter, whichever was armed before.
        var pal = self._getPref(toolKey + "_colors");
        pal = Array.isArray(pal) && pal.length
          ? self._sanitizeColorSlots(pal)
          : (toolKey === "highlighter" ? HIGHLIGHT_DEFAULT_SLOTS.slice()
                                       : MARKER_DEFAULT_SLOTS.slice());
        self._bankPalette();
        self._colorSlots = pal;
        self._paletteAppliedKey = toolKey + "_colors";
        var inkColor = self._getPref(toolKey + "_color") ||
          (toolKey === "highlighter" ? HIGHLIGHT_DEFAULT_COLOR
                                     : MARKER_DEFAULT_COLOR);
        // A remembered colour that is not in this tool's palette is a
        // colour from somewhere else — a pref written before the tools had
        // palettes of their own, or a host-set value — and arming on it
        // left the tool drawing in a colour none of its five swatches
        // showed, with nothing selected. Everything picked while armed
        // lands in a slot (the wheel edits the slot it was opened from),
        // so an off-palette value here is stale rather than chosen: fall
        // back to the palette's own lead colour, and let _selectColor
        // below write it through so the tool heals for next time.
        var inkSlot = pal.indexOf(inkColor);
        if (inkSlot === -1) {
          inkColor = pal[0];
          inkSlot = 0;
        }
        self._activeSlot = inkSlot;
        self._refreshToolbarSwatches();
        self._selectColor(inkColor);
        // The weight, the same way: the tool's own, remembered or built-in.
        self._setInkWidth(self._getPref(toolKey + "_width") ||
          (toolKey === "highlighter" ? HIGHLIGHT_DEFAULT_WIDTH
                                     : MARKER_DEFAULT_WIDTH));
      } else if (wasInk && self._bankedSharedColor) {
        var bank = self._bankedSharedColor;
        self._bankedSharedColor = null;
        self._bankPalette();
        self._colorSlots = bank.slots;
        self._paletteAppliedKey = "colors";
        self._activeSlot = bank.slot;
        self._refreshToolbarSwatches();
        // The banked raw colour only stands when no slot was active (a
        // shared colour from outside the palette) — otherwise the slot is
        // the source of truth for what the swatch shows.
        self._selectColor(bank.slot >= 0 && bank.slot < bank.slots.length
          ? bank.slots[bank.slot] : bank.color);
        // A board that never had a width of its own gets none back: the
        // shapes fall to the same built-in they started on.
        self._setInkWidth(bank.width);
      }

      // Sync `.is-selected` across the main toolbar AND the
      // compact-mode tools popup. The popup is an alternate UI; its
      // buttons must reflect the same selected state so re-opening
      // it shows the current tool highlighted.
      self._rememberPinnedTool(toolKey);

      function syncToolButtons(root) {
        if (!root) return;
        root.querySelectorAll("button[data-tool]").forEach(function(b) {
          var match = (toolKey == null && b.dataset.tool === "cursor") ||
                      (toolKey != null && b.dataset.tool === toolKey);
          b.classList.toggle("is-selected", match);
        });
      }
      syncToolButtons(self.toolbar);
      syncToolButtons(self.toolsPopup);

      // Wrapper catches input only while a drawing tool is active. Shapes
      // catch their own hover + click independently via CSS, so the
      // wrapper can stay `pointer-events: none` in every other state and
      // let background clicks pass through to OSD's canvas.
      // The grabber (hand) tool pans only: even though it's an active tool,
      // it must NOT capture pointer events — they pass through the overlay to
      // Fresco so a drag pans. Selection is suppressed separately in the doc
      // listeners (they bail while the grabber is active). So it counts as
      // "not drawing" for capture purposes.
      var grabbing = toolKey === "grabber";
      var drawingNow = toolKey != null && !grabbing;
      if (self.overlayWrapper) {
        self.overlayWrapper.style.pointerEvents = drawingNow ? "auto" : "none";
        self.overlayWrapper.classList.toggle("is-drawing", drawingNow);
        // Per-tool cursor while a draw tool is armed; empty string falls
        // back to the .is-drawing crosshair from the injected stylesheet.
        self.overlayWrapper.style.cursor = drawingNow ? (toolCursor(toolKey) || "") : "";
      }
      // Strip mode has no overlay wrapper (per-image overlays sit on the
      // scroll container directly). Apply the crosshair cursor + is-
      // drawing class to the container so the user gets the same visual
      // affordance, and the scroll container's native gestures still
      // bubble for non-draw moments. Restore the original cursor on tool
      // exit; `''` is the spec-correct way to clear an inline style.
      if (self.handleKind === "strip" && self.handle && self.handle.container) {
        self.handle.container.classList.toggle("etcher-strip-drawing", drawingNow);
        self.handle.container.style.cursor =
          drawingNow ? (toolCursor(toolKey) || "crosshair") : "";
      }
      // Grabber shows a hand/grab cursor on the viewer; events pass through
      // the click-through overlay to Fresco for panning. Cleared when leaving
      // the tool (strip mode already manages its own cursor above).
      if (self.handle && self.handle.container) {
        if (grabbing) self.handle.container.style.cursor = grabberCursor(false);
        else if (self.handleKind !== "strip") {
          // Cursor tool while annotating: drag-pan is locked (drag means
          // box-select / shape-move), so Fresco's `grab` affordance would
          // lie — show the plain arrow. Outside annotation mode, defer to
          // Fresco's own cursor CSS.
          self.handle.container.style.cursor =
            self.annotationMode && toolKey == null ? cursorToolCursor() : "";
        }
      }
      if (drawingNow || grabbing) self._hideTooltip();

      // Active tool changed → re-pin in the overflow layout so the
      // newly-active button never sits in the collapsed set.
      self._layoutToolbar();

      // Lock Fresco's drag-pan while the cursor tool is active (canvas mode)
      // so a drag does box-select instead of panning — the grabber tool is
      // now the way to pan.
      self._applyPanLock();

      // The grab closes while it is actually holding the canvas.
      self._wireGrabberCursor(grabbing);

      // The style panel is tool-aware (styleless tools hide it) — re-sync
      // so arming/leaving the grabber shows and hides it immediately.
      self._syncStylePanel();

      self._dispatch("etcher:tool-changed", { tool: toolKey });
      // Arming or disarming a tool changes which panel rows apply
      // (a pure-ink tool hides the label rows and the fill row) — sync
      // now, not on the next unrelated refresh.
      this._syncStyleInspector();
    },

    // Lock single-pointer drag-pan when the cursor tool is active in
    // annotation mode on a canvas, freeing the drag for marquee box-select.
    // Pinch + wheel zoom are unaffected (setPanLocked only gates drag-pan).
    // Strip mode keeps native scroll; the grabber + browse mode keep pan.
    // While the grabber is armed, a pressed pointer curls the hand and
    // release relaxes it — the pair every native grab cursor shows. The
    // down listener rides capture on the container (the grabber's events
    // deliberately pass through Etcher's overlay to Fresco, so nothing of
    // Etcher's sees them otherwise); the up/cancel pair rides the window,
    // because a drag routinely ends outside the container.
    // While the grabber is armed, a pressed pointer curls the hand and
    // release relaxes it — the pair every native grab cursor shows. Both
    // listeners ride the DOCUMENT (capture for the press), resolving
    // `handle.container` at event time: the media viewer remounts Fresco
    // on every file switch, handing this layer a NEW container, and a
    // listener bound to the old element silently died — the curl worked
    // until the first prev/next and never again (the once-only wiring
    // guard saw the stale handler and refused to rewire). Document-level
    // + late binding is immune to the swap. Capture also fires before
    // any of Fresco's own handling, so the curl is immediate on press —
    // the grabber's events deliberately pass through Etcher's overlay,
    // and nothing of Etcher's would see them otherwise; up/cancel ride
    // the window because drags routinely end outside the container.
    _wireGrabberCursor: function(on) {
      var self = this;
      if (on && !self._grabberDownHandler) {
        self._grabberDownHandler = function(e) {
          if (e.button !== 0 && e.button !== 1) return;
          if (self.activeTool !== "grabber") return;
          var c = self.handle && self.handle.container;
          if (!c || !c.contains(e.target)) return;
          c.style.cursor = grabberCursor(true);
        };
        self._grabberUpHandler = function() {
          if (self.activeTool !== "grabber") return;
          var c = self.handle && self.handle.container;
          if (c) c.style.cursor = grabberCursor(false);
        };
        document.addEventListener("pointerdown", self._grabberDownHandler, true);
        window.addEventListener("pointerup", self._grabberUpHandler, true);
        window.addEventListener("pointercancel", self._grabberUpHandler, true);
      } else if (!on && self._grabberDownHandler) {
        document.removeEventListener("pointerdown", self._grabberDownHandler, true);
        window.removeEventListener("pointerup", self._grabberUpHandler, true);
        window.removeEventListener("pointercancel", self._grabberUpHandler, true);
        self._grabberDownHandler = null;
        self._grabberUpHandler = null;
      }
    },

    _applyPanLock: function() {
      if (this.handleKind !== "canvas") return;
      if (!this.handle || typeof this.handle.setPanLocked !== "function") return;
      try {
        // Locked for every tool that draws or selects on the surface, so one
        // finger (or one left-drag) belongs to the tool. The grabber is the
        // exception it exists to be. A two-pointer pinch is exempt from the
        // lock inside Fresco, which is what lets two fingers move the canvas
        // without anybody switching tools.
        this.handle.setPanLocked(!!this.annotationMode && this.activeTool !== "grabber");
      } catch (_) {}
    },

    // Color picker — affects the active draft if drawing, the editing
    // shape if one is being edited, and the default for future shapes.
    // `null` resets to the CSS default blue.
    // Put a weight into the global line params and show it, without going
    // through _setLineParam — that path decides between editing selected
    // shapes and editing the default, and a tool swap is neither: it is
    // the default being exchanged for another default. `undefined` clears
    // it, which is how a board that never had a width of its own gets
    // none back on disarm.
    _setInkWidth: function(width) {
      this.lineParams = this.lineParams || {};
      if (typeof width === "number" && isFinite(width) && width > 0) {
        this.lineParams.width = width;
      } else {
        delete this.lineParams.width;
      }
      this._restyleDrafts();
      this._syncParamsPopup();
    },

    _selectColor: function(color) {
      this.activeColor = color;
      // While an ink tool is armed, the palette is standing in for THAT
      // tool's colour — remember the pick under the tool, so the next time
      // the marker (or highlighter) comes up it comes up in its own colour,
      // not in whatever the shapes were last drawn in. The write rides the
      // prefs channel, so it survives sessions like the other toggles.
      if (this._armedInkTool()) {
        this._setPref(this.activeTool + "_color", color);
      }
      this._dispatch("etcher:color-changed", { color: color });

      // Sync `.is-selected` across the main toolbar swatches AND
      // the compact-mode colors popup. Same alternate-UI rationale
      // as the tool-button sync above.
      // Toolbar swatches highlight by slot index (two slots can hold the
      // same color, so a color match would light up both).
      var activeSlot = this._activeSlot;
      if (this.swatchEls) {
        this.swatchEls.forEach(function(el, i) {
          el.classList.toggle("is-selected", i === activeSlot);
        });
      }
      if (this.colorsPopupBtns) {
        this.colorsPopupBtns.forEach(function(el) {
          el.classList.toggle("is-selected", el.dataset.color === color);
        });
      }
      // Preview chip in the picker reflects the active color so
      // \"this is what you have selected\" reads consistently whether
      // the source was a preset, a recent, or the picker itself.
      if (this._pickerPreview && typeof color === "string") {
        this._pickerPreview.style.background = color;
      }

      // Active swatch changed → re-pin in the overflow layout so the
      // newly-active swatch is the one that stays visible.
      this._layoutToolbar();

      // Apply to the in-flight draft (if any) so the user sees the new
      // color while still drawing.
      // Every live draft, through the one restyler — the colour is part of
      // the same style the thickness and the line type come from, and a
      // callout draft was being missed by the two special cases this
      // replaces.
      this._restyleDrafts();

      // Shape/label application lives in _applyColorToTargets; calling it
      // here keeps _selectColor's historical combined meaning for the
      // non-inspect paths (in that state it can only mean a focused label
      // or the empty set). Inspect-mode edits skip _selectColor entirely,
      // which is what keeps the authoring defaults untouched.
      //
      // …unless we are being called BY that function, which is the other
      // half of a two-way street: recolouring a freshly drawn shape also
      // sets the colour to draw in next, and it says so by calling here.
      // Answering that with another application is what turned the pair
      // into infinite recursion — one stack overflow per click, with a
      // just-drawn shape still selected (drawing a dimension, labelling
      // it, then picking a colour was the reported way in). The shape has
      // already been recoloured by the caller; there is nothing here to
      // apply.
      if (!this._applyingColorToTargets) this._applyColorToTargets(color);
    },

    // Recolor the current targets — the multi-selection, the edit-mode
    // shape, or a focused label — WITHOUT touching the authoring defaults
    // (activeColor, the slots): this is the edit half of the inspector.
    // Each shape gets its own undo entry; a focused label takes the colour
    // itself and refreshes the label_color pref, leaving its shape alone.
    _applyColorToTargets: function(color) {
      var self = this;

      // A focused LABEL takes the colour itself, leaving its shape alone.
      // Clicking a label puts you in title-edit mode (which clears
      // `editingShape` on the way in), so this is unambiguous: the label is
      // the only thing selected, and it is the only thing you can have meant.
      // Colouring the shape from here would drag the dimension's line along
      // with its measurement, which is the opposite of being able to pick
      // them out from each other.
      var titleTarget =
        !(this.selectedShapes && this.selectedShapes.length) &&
        !this.editingShape && this.editingTitleShape
          ? this.editingTitleShape
          : null;

      if (titleTarget && titleTarget.uuid) {
        var tBefore = self._snapshotShape(titleTarget);
        titleTarget.metadata = Object.assign({}, titleTarget.metadata || {}, {
          title_color: color
        });
        // Remember it: colouring a label is a statement about how this
        // user's labels should look, and the next label they create starts
        // in this colour (`_commitTextEdit` stamps it at creation).
        // Persisted through the prefs mechanism, so it survives reloads
        // wherever the host stores prefs.
        self._setPref("label_color", color);
        self._renderShape(titleTarget);
        self._pushUndo(titleTarget.uuid, tBefore, self._snapshotShape(titleTarget));
        self._emitChanged();
      }

      var colorTargets = titleTarget
        ? []
        : (this.selectedShapes && this.selectedShapes.length)
          ? this.selectedShapes.slice()
          : this.editingShape
            ? [this.editingShape]
            : (this._textEditor && this._textEditor.shape)
              ? [this._textEditor.shape]
              : [];
      colorTargets.forEach(function(shape) {
        if (!shape.uuid) return;
        var before = self._snapshotShape(shape);
        shape.style = Object.assign({}, shape.style || {}, { color: color });
        if (shape.kind === "marker") self._renderShape(shape);
        else self._applyShapeColor(shape.el, color, shape.style);
        // Keep the title sibling in step — but through the same resolver the
        // render uses, so a label with a colour of its own keeps it instead
        // of being overwritten every time its shape is recoloured.
        if (shape.titleGroup) {
          shape.titleGroup.style.color = self._titleColorFor(shape);
        }
        self._pushUndo(shape.uuid, before, self._snapshotShape(shape));
      });
      if (colorTargets.length) self._emitChanged();
      // Fresh shape: its colour is also the colour the user is drawing in
      // now — the next shape matches (_selectColor updates activeColor and
      // announces it for host persistence). Flagged for the duration so
      // that call does not come straight back here: the targets are
      // already recoloured, and answering would re-apply them, push a
      // second undo entry, and recurse until the stack gives out.
      if (this._freshTargets(colorTargets)) {
        this._applyingColorToTargets = true;
        try {
          this._selectColor(color);
        } finally {
          this._applyingColorToTargets = false;
        }
      }

      // Repaint any active handles so the vertex dots match the new
      // shape color immediately instead of waiting for the next handle
      // refresh.
      var handleColor = color || "#3b82f6";
      this._repaintHandles();
      // The freehand/marker pen editor's anchors, bezier handles, and their
      // tether lines live outside `handles`, so repaint them here too — else
      // a recolor wouldn't reach the points/handles until a reselect.
      if (this.freehandEditor) {
        this.freehandEditor.controls.forEach(function(c) { c.el.style.color = handleColor; });
        this.freehandEditor.lines.forEach(function(l) { l.el.style.color = handleColor; });
      }
      this._refreshTextEditorStyle();
    },

    _applyShapeColor: function(el, color, style) {
      if (!el) return;
      // Callout shapes use a <g> with `currentColor`-bound children
      // (line stroke + dot fill + text fill). Setting `style.color` on
      // the group propagates through the SVG `currentColor` keyword so
      // every child picks it up, and we skip the rgba fill-opacity that
      // would make the dot + text semi-transparent.
      if (el.tagName && el.tagName.toLowerCase() === "g") {
        el.style.color = color || "";
        return;
      }
      // The resolved colour is stashed on the element because both the fill
      // and the stroke can be switched off ("none" fill / "none" line), and
      // whichever one comes back needs a colour to come back to. Reading it
      // off the other property only worked while at most one could be off.
      if (color) {
        el.style.stroke = color;
        el.style.fill = color;
        el.dataset.etcherColor = color;
      } else {
        el.style.stroke = "";
        el.style.fill = "";
        delete el.dataset.etcherColor;
      }
      this._applyFill(el, style);
      this._applyStrokeVisibility(el, style);
    },

    // The colour a shape's paint should return to. `style.color` is
    // authoritative when the caller has it; several draft-shape call sites
    // pass no style at all, hence the stashed fallback.
    _shapeColor: function(el, style) {
      if (style && style.color) return style.color;
      return (el && el.dataset && el.dataset.etcherColor) || "";
    },

    // Which kinds can hold a fill at all. Decided from the SVG tag rather
    // than the shape kind so this needs no plumbing at the call sites: an
    // open path (line, freehand, marker) filling itself would be nonsense.
    _isFillableEl: function(el) {
      if (!el) return false;
      var tag = el.tagName && el.tagName.toLowerCase();
      // A marker is a stroke and nothing else — filling one would turn a
      // felt-tip scribble into a solid blob.
      if (el.classList && el.classList.contains("etcher-marker")) return false;
      // `path` / `polyline` are freehand, which is a body as much as an
      // outline: lasso a region and the enclosed area is meant to be tinted.
      // SVG closes an open subpath implicitly when filling, so this works on
      // a curve whose ends don't quite meet — which is every hand-drawn loop.
      //
      // Shafts (line, dimension, arrow) are `<line>` / `<polyline>` too, but
      // never arrive here: they live inside a `<g>`, and the colour path
      // returns at the group without descending to its children.
      return tag === "rect" || tag === "circle" || tag === "polygon" ||
             tag === "path" || tag === "polyline";
    },

    // Fill mode + opacity in one place, so the colour path and the params
    // path can't disagree about it.
    //
    // `fill` is "none" | "semi" | "solid", defaulting to "semi" — what every
    // shape looked like before the mode existed, so untouched drawings are
    // unchanged. Fill opacity is multiplied by the shape's overall opacity;
    // previously the slider moved only `stroke-opacity`, so dragging it to
    // nothing still left a visible tinted body.
    _applyFill: function(el, style) {
      if (!el) return;
      if (!this._isFillableEl(el)) {
        el.style.fill = "none";
        el.style.fillOpacity = "";
        el.classList.remove("etcher-hatched");
        return;
      }
      var s = style || {};
      var mode = s.fill || "semi";
      var opacity = s.opacity == null ? 1 : s.opacity;

      if (mode === "none") {
        el.style.fill = "none";
        el.style.fillOpacity = "";
        el.classList.remove("etcher-hatched");
        return;
      }

      var color = this._shapeColor(el, s);

      // Hatch lines are already mostly gaps, so they take the shape's
      // opacity straight — damping them the way "semi" damps a flat fill
      // would wash them out to nothing.
      if (mode === "pattern") {
        var hatch = this._hatchPattern(color);
        if (hatch) {
          el.style.fill = "url(#" + hatch + ")";
          el.style.fillOpacity = String(opacity);
          // Tells the stylesheet (and `_syncHatchOutline`) that this shape's
          // painted pixels include the hatch — which changes what selecting
          // it has to look like. See `.etcher-hatched` below.
          el.classList.add("etcher-hatched");
          return;
        }
        // No SVG to hang the def on yet (a draft painted before init
        // finished) — fall through to a flat body rather than no body.
      }

      // Restore the body colour rather than assume it survived: "none" and
      // "pattern" both overwrite it, and the params path — which is where
      // the mode gets flipped — never touches the colour otherwise.
      el.style.fill = color;
      el.style.fillOpacity = String((mode === "solid" ? 1 : 0.18) * opacity);
      el.classList.remove("etcher-hatched");
    },

    // Whether "no line" actually takes effect for this element. A shape
    // needs at least one of stroke or fill painted or it can't be seen —
    // and an invisible shape can't be clicked back into existence either,
    // since selection starts from a hit-test the user has to aim. So the
    // outline is only dropped when the body is there to carry the shape,
    // and turning the fill off later brings the outline back.
    _strokeHidden: function(el, style) {
      var s = style || {};
      if (s.dash !== "none") return false;
      return this._isFillableEl(el) && (s.fill || "semi") !== "none";
    },

    _applyStrokeVisibility: function(el, style) {
      if (!el || (el.tagName && el.tagName.toLowerCase() === "g")) return;
      if (this._strokeHidden(el, style)) {
        el.style.stroke = "none";
        return;
      }
      // Coming back from hidden — `stroke: none` is inline, so it outlives
      // any later colour change unless something puts the colour back.
      if (el.style.stroke === "none") {
        el.style.stroke = this._shapeColor(el, style);
      }
    },

    // A `<pattern>` of 45° hatch lines in `color`, created once per colour
    // and returned by id. Lives in the overlay SVG's own `<defs>` under an
    // instance-scoped prefix, so two Etchers on one page can't resolve each
    // other's ids — `url(#…)` is document-wide and matches the first
    // definition in tree order, which would otherwise be a neighbour's.
    _hatchPattern: function(color) {
      if (!this.svg) return null;
      var key = String(color || "").replace(/[^a-z0-9]/gi, "");
      if (!this._hatchPrefix) {
        HATCH_SEQ += 1;
        this._hatchPrefix = "etcher-hatch-" + HATCH_SEQ + "-";
      }
      var id = this._hatchPrefix + (key || "default");
      this._hatchIds = this._hatchIds || {};
      if (this._hatchIds[id]) return id;

      if (!this._defs) {
        this._defs = svgEl("defs");
        this.svg.insertBefore(this._defs, this.svg.firstChild);
      }
      var pat = svgEl("pattern", {
        id: id,
        patternUnits: "userSpaceOnUse",
        width: HATCH_SIZE,
        height: HATCH_SIZE
      });
      // The main corner-to-corner diagonal, plus a stub at each of the two
      // corners it passes through. A stroke has width, so the half that
      // overflows the tile at a corner is clipped away — the stubs redraw
      // that half on the opposite side, which is what makes the diagonals
      // run unbroken instead of nicking at every tile boundary. (Doubling
      // the strokes to hide the nicks just doubles the density.)
      var s = HATCH_SIZE;
      pat.appendChild(svgEl("path", {
        d: "M-1,1 l2,-2 M0," + s + " l" + s + ",-" + s +
           " M" + (s - 1) + "," + (s + 1) + " l2,-2",
        stroke: color || "currentColor",
        "stroke-width": HATCH_WIDTH,
        fill: "none"
      }));
      this._defs.appendChild(pat);
      this._hatchIds[id] = true;
      return id;
    },

    // -------------------------------------------------------------------------
    // Coord helpers — canvas px ↔ container px (the SVG's coordinate space)
    //
    // Fresco 0.5's `handle.screenToImage` / `handle.imageToScreen` are
    // stable round-trips: the CSS-transform engine doesn't have OSD's
    // tile-source axis-shift (no tile pyramid) or modal-traversal drift
    // (uses standard getBoundingClientRect throughout). So Etcher
    // delegates straight to the handle here — none of the workaround
    // math from 0.2.x is needed.
    //
    // Geometry stays in canvas-pixel coords (Fresco.Canvas's coordinate
    // system) so annotations compose uniformly across multi-image
    // canvases and travel verbatim through `.fresco` files.
    // -------------------------------------------------------------------------

    // Every pointer reading the browser batched into this event, oldest
    // first. Falls back to the event itself where `getCoalescedEvents` is
    // missing or throws — Safari has shipped it returning an empty list in
    // some contexts, and an empty list here would drop the stroke entirely.
    _coalesced: function(e) {
      if (e && typeof e.getCoalescedEvents === "function") {
        try {
          var evs = e.getCoalescedEvents();
          if (evs && evs.length) return evs;
        } catch (_) {}
      }
      return [e];
    },

    _toImage: function(e) {
      // Both handle shapes expose `screenToImage({x, y})` but the
      // return is different: canvas returns `{x, y}` in canvas-pixel
      // coords; strip returns `{imageIdx, x, y}` in per-image natural
      // pixel coords. Strip draw paths consume `imageIdx`; canvas
      // draw paths ignore it (extra field is harmless).
      return this.handle.screenToImage({ x: e.clientX, y: e.clientY });
    },

    // Strip mode: translate a `{imageIdx, x, y}` point (in `imageIdx`'s own
    // natural-px space) into the natural-px space of the stroke's STARTING
    // image, so a freeform stroke that wanders into the next image keeps
    // drawing in one overlay (which paints over later images via the
    // overlays' absolute positioning + `overflow: visible`) instead of
    // snapping back to the starting image's origin. Goes via display
    // coordinates so differing per-image scales / widths are handled.
    // Returns `[x, y]` (same shape `_appendFreehand` stores). No-op when the
    // point is already in the starting image or page metadata is missing.
    _stripTranslatePoint: function(pt, startIdx) {
      if (pt.imageIdx === startIdx) return [pt.x, pt.y];
      var src = this.pageOverlays && this.pageOverlays[pt.imageIdx];
      var dst = this.pageOverlays && this.pageOverlays[startIdx];
      if (!src || !dst || !src.page || !dst.page) return [pt.x, pt.y];
      var sp = src.page, dp = dst.page;
      if (!sp.height || !dp.height) return [pt.x, pt.y];
      // Y (the cross-strip axis): point → display px down the strip →
      // starting image's natural px. `top` / `height` / `naturalHeight` are
      // always set, so this is the part that actually matters for a vertical
      // strip.
      var dispY = sp.top + (pt.y / (sp.naturalHeight || 1)) * sp.height;
      var y = (dispY - dp.top) * (dp.naturalHeight || 1) / dp.height;
      // X: only translatable when both pages carry a concrete width (a
      // 100%-width layout leaves `width` null). Otherwise keep x as-is —
      // stacked strip images share the horizontal axis closely enough.
      var x = pt.x;
      if (typeof sp.width === "number" && typeof dp.width === "number" && dp.width) {
        var dispX = (sp.left || 0) + (pt.x / (sp.naturalWidth || 1)) * sp.width;
        x = (dispX - (dp.left || 0)) * (dp.naturalWidth || 1) / dp.width;
      }
      return [x, y];
    },

    // How far the canvas is currently turned, in degrees clockwise.
    //
    // The overlay is a child of fresco's container, but fresco rotates its
    // *stage* — so the overlay never turns, and shapes only inherit rotation
    // through `_imageToContainer` transforming their points. That's enough
    // for anything drawn as a point list, and not enough for anything with
    // an orientation of its own: a picture, a line of text, a video frame.
    // Those have to be turned explicitly, which is what this feeds.
    _canvasRotation: function() {
      if (this.handleKind === "strip") return 0;
      var h = this.handle;
      if (!h || typeof h.getRotation !== "function") return 0;
      var deg = h.getRotation();
      return (typeof deg === "number" && isFinite(deg)) ? deg : 0;
    },

    // The container-px box to draw an image-space rect in, for content that
    // has to turn with the board.
    //
    // Transforming two opposite corners gives the rect's *rotated* bounding
    // box — at 90° that box has width and height swapped, which is why a
    // rotated board still laid its pictures out correctly while every
    // picture inside stayed upright. Here the box keeps the shape's own
    // proportions (from the isometric scale) and is centred on the same
    // point, so `rotate(deg, cx, cy)` lands it exactly where the bbox was
    // with its content turned to match. At 0° the two agree, which keeps the
    // unrotated case pixel-identical to what it was.
    _orientedBox: function(g) {
      // Coerced up front: a shape being created has a position before it has
      // an extent, and `g.x + undefined` poisons the corner probe with NaN —
      // which then reaches the DOM as an unparseable attribute rather than
      // failing anywhere you could see it.
      var gw = (typeof g.w === "number" && isFinite(g.w)) ? g.w : 0;
      var gh = (typeof g.h === "number" && isFinite(g.h)) ? g.h : 0;
      var tl = this._imageToContainer({ x: g.x, y: g.y });
      var br = this._imageToContainer({ x: g.x + gw, y: g.y + gh });
      var cx = (tl.x + br.x) / 2;
      var cy = (tl.y + br.y) / 2;
      var scale = this._markerScale();
      var w = Math.abs(gw) * scale;
      var h = Math.abs(gh) * scale;
      return {
        x: cx - w / 2, y: cy - h / 2, w: w, h: h,
        cx: cx, cy: cy, deg: this._canvasRotation()
      };
    },

    // Turn an SVG element about a container-px point. Removing the attribute
    // at 0° rather than writing `rotate(0)` keeps unrotated markup identical
    // to before — worth it because these run on every pan and zoom frame.
    _setRotateTransform: function(el, box) {
      if (!el) return;
      if (!box || !box.deg) {
        if (el.getAttribute("transform")) el.removeAttribute("transform");
        return;
      }
      el.setAttribute(
        "transform",
        "rotate(" + box.deg + " " + box.cx + " " + box.cy + ")"
      );
    },

    // Where a container-px point ends up once its element is turned by
    // `_setRotateTransform`. Needed wherever something OUTSIDE the turned
    // element has to meet it — a callout's leader is drawn in unturned
    // container space but has to land on the corner of a label that is
    // turning, so it needs the corner's post-rotation position.
    // ── Shape rotation ──────────────────────────────────────────────────
    //
    // Every shape you can move can be turned about its middle by the knob
    // above it (`_startRotateDrag`). Shapes built from points (polygon,
    // freehand, marker, line, arrow, dimension) simply have their points
    // turned. Box shapes (rectangle, image, text and text boxes) keep their
    // box upright in storage and carry the angle as `style.rotation`
    // (degrees, clockwise): the render turns them about the box's centre,
    // the hit test and the handles work in that turned frame, and a resize
    // works in it too, the opposite corner holding still.
    _isBoxRotatable: function(shape) {
      return !!shape && (shape.kind === "rectangle" || shape.kind === "image" || shape.kind === "text");
    },

    _canRotate: function(shape) {
      if (!shape || shape.readonly) return false;
      if (this._isBoxRotatable(shape)) return true;
      var k = shape.kind;
      return k === "polygon" || k === "freehand" || k === "marker" ||
        k === "line" || k === "arrow" || k === "dimension";
    },

    // How far a shape's label is turned: a turned box's own angle; for a
    // shape built from points (whose turns are baked into its points) the
    // turns the knob has given it, kept as `style.title_rotation`. A label
    // riding an arrow or a dimension stays upright along its line.
    _labelRot: function(shape) {
      if (!shape || this._labelRidesShaft(shape.kind) || this._isTextKind(shape.kind)) return 0;
      if (this._isBoxRotatable(shape)) return this._shapeRot(shape);
      var r = shape.style && shape.style.title_rotation;
      return typeof r === "number" && isFinite(r) ? r : 0;
    },

    // Points about a turned label's middle (image px, unturned frame →
    // turned).
    _labelRotPts: function(shape, pts) {
      var rot = this._labelRot(shape);
      var b = shape && shape._renderedTitleImage;
      if (!rot || !b) return pts;
      var cx = b.x + b.w / 2, cy = b.y + b.h / 2;
      var self = this;
      return pts.map(function(p) { return p && Object.assign({}, p, self._rotatePoint(p, cx, cy, rot)); });
    },

    // `rotate(deg cx cy)` → {deg, cx, cy}, or null.
    _parseTurn: function(t) {
      var m = t && /rotate\(\s*([-\d.e]+)[ ,]+([-\d.e]+)[ ,]+([-\d.e]+)\s*\)/.exec(t);
      if (!m) return null;
      var deg = parseFloat(m[1]);
      return deg ? { deg: deg, cx: parseFloat(m[2]), cy: parseFloat(m[3]) } : null;
    },

    // A box shape's own turn, in degrees (0 for every other kind).
    _shapeRot: function(shape) {
      if (!this._isBoxRotatable(shape)) return 0;
      var r = shape.style && shape.style.rotation;
      return typeof r === "number" && isFinite(r) ? r : 0;
    },

    // The box a box shape turns with, unturned, in image px.
    _rotBox: function(shape) {
      var g = shape.geometry || {};
      if (shape.kind === "text") return shape._renderedBox || g;
      return g;
    },

    _rotCenter: function(shape) {
      if (this._isBoxRotatable(shape)) {
        var b = this._rotBox(shape);
        return { x: b.x + b.w / 2, y: b.y + b.h / 2 };
      }
      var bb = this._shapeBBoxImagePx(shape);
      return bb ? { x: bb.x + bb.w / 2, y: bb.y + bb.h / 2 } : null;
    },

    // Points in a box shape's unturned frame, carried into the turned one.
    _rotPts: function(shape, pts) {
      var rot = this._shapeRot(shape);
      if (!rot) return pts;
      var c = this._rotCenter(shape);
      var self = this;
      return pts.map(function(p) { return p && Object.assign({}, p, self._rotatePoint(p, c.x, c.y, rot)); });
    },

    // After a resize computed in a turned shape's own frame, slide the box
    // so it turns about its new middle with the held corner where it was.
    _recenterRotated: function(shape, c0, rot) {
      var g = shape.geometry;
      if (!g || !rot || !c0) return;
      var cn = { x: g.x + g.w / 2, y: g.y + g.h / 2 };
      var cw = this._rotatePoint(cn, c0.x, c0.y, rot);
      shape.geometry = Object.assign({}, g, { x: g.x + cw.x - cn.x, y: g.y + cw.y - cn.y });
    },

    // The same for a turned label's stored box (`metadata.title_box`).
    _recenterLabel: function(shape, c0, rot) {
      var tb = shape.metadata && shape.metadata.title_box;
      if (!tb || !rot || !c0) return;
      var cn = { x: tb.x + tb.w / 2, y: tb.y + tb.h / 2 };
      var cw = this._rotatePoint(cn, c0.x, c0.y, rot);
      shape.metadata = Object.assign({}, shape.metadata, {
        title_box: { x: tb.x + cw.x - cn.x, y: tb.y + cw.y - cn.y, w: tb.w, h: tb.h }
      });
    },

    // A point shape's geometry turned by `deg` about `c`.
    _rotateGeometry: function(kind, g, c, deg) {
      var self = this;
      var P = function(p) { var r = self._rotatePoint({ x: p[0], y: p[1] }, c.x, c.y, deg); return [r.x, r.y]; };
      var V = function(v) { if (!v) return v; var r = self._rotatePoint({ x: v[0], y: v[1] }, 0, 0, deg); return [r.x, r.y]; };
      var out = Object.assign({}, g);
      if (g.points) out.points = g.points.map(P);
      if (g.nodes) {
        out.nodes = g.nodes.map(function(n) {
          return Object.assign({}, n, { p: P(n.p), hIn: V(n.hIn), hOut: V(n.hOut) });
        });
      }
      if (g.a) out.a = P(g.a);
      if (g.b) out.b = P(g.b);
      return out;
    },

    _rotatePoint: function(pt, cx, cy, deg) {
      if (!deg) return { x: pt.x, y: pt.y };
      var r = deg * Math.PI / 180;
      var c = Math.cos(r), s = Math.sin(r);
      var dx = pt.x - cx, dy = pt.y - cy;
      return { x: cx + dx * c - dy * s, y: cy + dx * s + dy * c };
    },

    // The inverse of `_imageToContainer`: a container-px point back to image
    // px. Used where something is laid out on SCREEN and its geometry has to
    // be written back to the image — a callout label stands upright whatever
    // the board's rotation, so its corners are screen facts.
    _containerToImage: function(pt) {
      if (this.handleKind === "strip") return pt;
      var r = this.handle.container.getBoundingClientRect();
      var img = this.handle.screenToImage({ x: pt.x + r.left, y: pt.y + r.top });
      return { x: img.x, y: img.y };
    },

    // A callout's label on screen: upright at every rotation, its own size,
    // centred where the stored box's centre lands. Container px, or null
    // when the board is not turned (the stored box then IS the screen box,
    // and every existing path keeps working on it unchanged).
    _calloutLabelScreen: function(shape) {
      if (!shape || shape.kind !== "callout") return null;
      if (!this._canvasRotation()) return null;
      return shape._labelRect || null;
    },

    _imageToContainer: function(pt) {
      // Strip mode: each shape's SVG element lives inside a per-image
      // overlay whose `viewBox` is set to that image's natural pixel
      // dimensions. So SVG attrs in image-pixel coords render
      // correctly without any transform — the identity case lets the
      // existing per-shape render switch in `_renderShape` work
      // unchanged.
      if (this.handleKind === "strip") return pt;

      // Canvas mode: convert image-pixel → container-pixel for the
      // single canvas-spanning overlay SVG. Fresco's imageToScreen
      // returns page coords; subtract the container origin.
      var page = this.handle.imageToScreen(pt);
      var r = this.handle.container.getBoundingClientRect();
      return { x: page.x - r.left, y: page.y - r.top };
    },

    // Render one shape (or draft) by projecting its image-px geometry into
    // container-px coordinates and writing the result onto its SVG element.
    // For non-callout shapes that carry `metadata.title`, also renders a
    // sibling `<text>` element above the shape's bounding box (or at
    // `metadata.title_offset` if the user has dragged it).
    _renderShape: function(shape) {
      if (!shape || !shape.el) return;
      var self = this;
      var g = shape.geometry;
      var el = shape.el;
      // Track the bbox top-center in IMAGE coords for non-callout shapes
      // — populated below in each switch branch, used after the switch
      // to place the title sibling. Image coords (not container) so the
      // user-saved `metadata.title_offset` survives pan/zoom.
      var bboxTopImage = null;

      switch (shape.kind) {
        case "rectangle": {
          var tl = self._imageToContainer({ x: g.x,         y: g.y });
          var br = self._imageToContainer({ x: g.x + g.w,   y: g.y + g.h });
          el.setAttribute("x", Math.min(tl.x, br.x));
          el.setAttribute("y", Math.min(tl.y, br.y));
          el.setAttribute("width",  Math.abs(br.x - tl.x));
          el.setAttribute("height", Math.abs(br.y - tl.y));
          // Its own turn, about its middle on screen.
          var rRot = self._shapeRot(shape);
          if (rRot) {
            el.setAttribute("transform", "rotate(" + rRot + " " + (tl.x + br.x) / 2 + " " + (tl.y + br.y) / 2 + ")");
          } else if (el.getAttribute("transform")) {
            el.removeAttribute("transform");
          }
          bboxTopImage = { x: g.x + g.w / 2, y: g.y };
          break;
        }
        case "image": {
          var ibox = self._orientedBox(g);
          ibox.deg = (ibox.deg || 0) + self._shapeRot(shape);
          var iw = ibox.w;
          var ih = ibox.h;
          el.setAttribute("x", ibox.x);
          el.setAttribute("y", ibox.y);
          el.setAttribute("width",  iw);
          el.setAttribute("height", ih);
          self._setRotateTransform(el, ibox);
          // Rounded corners, sized from the box just computed. `inset()`
          // rather than an SVG <clipPath>, which would need a per-shape
          // <rect> kept in step with this box on every pan and zoom.
          el.style.clipPath = "inset(0 round " + self._imageRadius(iw, ih) + "px)";
          // Source lives in geometry so it travels with the shape (and through
          // the collab delta). Only rewrite when it changes — swapping href
          // every pan/zoom frame would restart image decode. `href` + the
          // legacy `xlink:href` for older Safari.
          //
          // `_previewHref` wins while an upload is in flight: it's a local
          // object URL for the exact bytes being sent, so the image looks
          // finished from the first frame even though `geometry.href` — the
          // only part that gets persisted — is still empty.
          var imgHref = shape._previewHref || g.href || "";
          if (el.getAttribute("href") !== imgHref) {
            el.setAttribute("href", imgHref);
            el.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", imgHref);
          }
          self._syncImageRing(shape, {
            x: ibox.x, y: ibox.y, w: iw, h: ih,
            r: self._imageRadius(iw, ih), box: ibox
          });
          bboxTopImage = { x: g.x + g.w / 2, y: g.y };
          break;
        }
        case "video": {
          var vbox = self._orientedBox(g);
          var vx = vbox.x, vy = vbox.y, vw = vbox.w, vh = vbox.h;
          // The transport is a child of this <g>; turning it puts the bar
          // back along the bottom edge of the picture as the board sees it.
          self._setRotateTransform(el, vbox);
          var vEl = self._audioElFor(shape);
          if (vEl) {
            vEl.style.left = vx + "px";
            vEl.style.top = vy + "px";
            vEl.style.width = vw + "px";
            vEl.style.height = vh + "px";
            vEl.style.borderRadius = self._imageRadius(vw, vh) + "px";
            // The frames live in a plain DOM layer rather than the SVG, so
            // they turn via CSS. Origin is the box centre — the same pivot
            // the SVG transform uses — so picture and transport stay welded.
            vEl.style.transformOrigin = "50% 50%";
            vEl.style.transform = vbox.deg ? "rotate(" + vbox.deg + "deg)" : "";
          }
          // While uploading there is no picture behind the transport, so the
          // card fills the whole box and says so. A lone strip floating over
          // nothing reads as a rendering fault.
          if (self._isPendingMedia(shape)) {
            self._layoutAudioCard(shape, { x: vx, y: vy, w: vw, h: vh });
          } else {
            // The transport is drawn in SVG over the picture, not with the
            // browser's own controls: native controls are a fixed pixel size
            // that would swell as you zoom out, can't be styled to match the
            // board, and would drive playback locally without telling the
            // room.
            //
            // Shown only while the pointer is on the video (or it's
            // selected), the way every video player behaves — the point of a
            // video on a board is the picture, and a bar welded across the
            // bottom of it permanently covers what you came to look at.
            self._layoutAudioCard(shape, {
              x: vx, y: vy + Math.max(0, vh - self._videoBarH(vh)),
              w: vw, h: self._videoBarH(vh)
            });
          }
          bboxTopImage = { x: g.x + g.w / 2, y: g.y };
          break;
        }
        case "audio": {
          // Every part of the transport is a child of this <g>, so one turn
          // carries the whole card — disc, title, timecode and scrub bar
          // keep their arrangement instead of being re-laid-out sideways.
          var abox = self._orientedBox(g);
          self._setRotateTransform(el, abox);
          self._layoutAudioCard(shape, {
            x: abox.x, y: abox.y, w: abox.w, h: abox.h
          });
          bboxTopImage = { x: g.x + g.w / 2, y: g.y };
          break;
        }
        case "circle": {
          var c  = self._imageToContainer({ x: g.cx, y: g.cy });
          var rp = self._imageToContainer({ x: g.cx + g.r, y: g.cy });
          el.setAttribute("cx", c.x);
          el.setAttribute("cy", c.y);
          // Radius = full distance of the projected edge point, NOT just its
          // x-component. Rotation is isometric, so `|rp - c|` == r * scale for
          // any rotation; the old `rp.x - c.x` collapsed to ~0 at 90°/270°
          // (an image-x offset projects to a screen-y offset there), which
          // rendered the circle with radius 0 — invisible on a rotated canvas.
          el.setAttribute("r", Math.hypot(rp.x - c.x, rp.y - c.y));
          bboxTopImage = { x: g.cx, y: g.cy - g.r };
          break;
        }
        case "polygon": {
          var minIX = Infinity, maxIX = -Infinity, minIY = Infinity;
          var pts = (g.points || []).map(function(p) {
            if (p[0] < minIX) minIX = p[0];
            if (p[0] > maxIX) maxIX = p[0];
            if (p[1] < minIY) minIY = p[1];
            var s = self._imageToContainer({ x: p[0], y: p[1] });
            return s.x + "," + s.y;
          }).join(" ");
          el.setAttribute("points", pts);
          if (isFinite(minIX) && isFinite(minIY)) {
            bboxTopImage = { x: (minIX + maxIX) / 2, y: minIY };
          }
          break;
        }
        case "marker":
        case "freehand": {
          if (g.nodes) {
            // Vector curve: draw the cubic-bezier path; derive the title-
            // anchor bbox from the flattened curve so it hugs the real
            // outline, not just the sparse anchor points.
            el.setAttribute("d", self._freehandPathD(g.nodes, function(p) {
              return self._imageToContainer({ x: p[0], y: p[1] });
            }));
            var flat = self._freehandFlatten(g);
            var fMinX = Infinity, fMaxX = -Infinity, fMinY = Infinity;
            for (var fi = 0; fi < flat.length; fi++) {
              if (flat[fi][0] < fMinX) fMinX = flat[fi][0];
              if (flat[fi][0] > fMaxX) fMaxX = flat[fi][0];
              if (flat[fi][1] < fMinY) fMinY = flat[fi][1];
            }
            if (isFinite(fMinX) && isFinite(fMinY)) {
              bboxTopImage = { x: (fMinX + fMaxX) / 2, y: fMinY };
            }
          } else if (shape.kind === "marker") {
            // Marker: a smooth Catmull-Rom spline through the sparse, rounded
            // points (its element is always a <path>). The spline does the
            // smoothing, so the stored point set stays small. Bbox from the
            // points — the spline hugs them closely enough for title / box-
            // select. Drives both the live draft and the committed stroke.
            var mp = g.points || [];
            if (mp.length && self._strokeIsDot(g)) {
              // A dot: drawn as its own short segment rather than the stored
              // hair of one, so the round cap has a subpath the renderer
              // agrees is there. See `DOT_RENDER_DEVICE_PX`.
              var dc = self._imageToContainer({ x: mp[0][0], y: mp[0][1] });
              var dlen = self._dotRenderLength();
              el.setAttribute("d", "M " + dc.x + " " + dc.y +
                                   " L " + (dc.x + dlen) + " " + dc.y);
            } else {
            el.setAttribute("d", self._catmullRomPathD(mp, function(p) {
              return self._imageToContainer({ x: p[0], y: p[1] });
            }, { corners: true }));
            }
            var mMinX = Infinity, mMaxX = -Infinity, mMinY = Infinity;
            for (var mi = 0; mi < mp.length; mi++) {
              if (mp[mi][0] < mMinX) mMinX = mp[mi][0];
              if (mp[mi][0] > mMaxX) mMaxX = mp[mi][0];
              if (mp[mi][1] < mMinY) mMinY = mp[mi][1];
            }
            if (isFinite(mMinX) && isFinite(mMinY)) {
              bboxTopImage = { x: (mMinX + mMaxX) / 2, y: mMinY };
            }
          } else {
            // Legacy pre-vector freehand: raw polyline (straight segments).
            var lMinX = Infinity, lMaxX = -Infinity, lMinY = Infinity;
            var lpts = (g.points || []).map(function(p) {
              if (p[0] < lMinX) lMinX = p[0];
              if (p[0] > lMaxX) lMaxX = p[0];
              if (p[1] < lMinY) lMinY = p[1];
              var s = self._imageToContainer({ x: p[0], y: p[1] });
              return s.x + "," + s.y;
            }).join(" ");
            if ((g.points || []).length && self._strokeIsDot(g)) {
              var lc = self._imageToContainer({ x: g.points[0][0], y: g.points[0][1] });
              var llen = self._dotRenderLength();
              lpts = lc.x + "," + lc.y + " " + (lc.x + llen) + "," + lc.y;
            }
            el.setAttribute("points", lpts);
            if (isFinite(lMinX) && isFinite(lMinY)) {
              bboxTopImage = { x: (lMinX + lMaxX) / 2, y: lMinY };
            }
          }
          // Markers re-apply their style every render so the stroke width
          // (and dash) track the current zoom — `_renderShape` runs on every
          // pan/zoom frame. Drafts have no `style` yet → use the tool default.
          if (shape.kind === "marker") {
            self._applyMarkerStyle(el, shape.style || self._currentMarkerStyle(),
                                   self._markerScale());
          }
          break;
        }
        case "callout": {
          // shape.el is a <g> containing <line>, <rect> (text bbox),
          // <text>, and <circle> (anchor dot). The text endpoint is a
          // resizable bbox (`geometry.text_box`) that behaves like a
          // text shape — leader line connects the anchor to the bbox's
          // nearest edge midpoint.
          // First <line> child is the leader; the .etcher-callout-
          // underline class identifies the horizontal underline.
          var lineEls = el.querySelectorAll("line");
          var coLine = lineEls[0];
          var coUnderline = el.querySelector(".etcher-callout-underline");
          var coDot  = el.querySelector("circle");
          var coRect = el.querySelector(".etcher-text-rect");
          var coText = el.querySelector(".etcher-text-content");

          var anchor = self._imageToContainer({ x: g.anchor[0], y: g.anchor[1] });

          // Tolerate the legacy `text_at` point shape — derive a
          // default bbox at the legacy point so existing rows render.
          var box = self._calloutTextBoxImage(g);
          var cobox = self._orientedBox(box);
          var bx = cobox.x, by = cobox.y, bw = cobox.w, bh = cobox.h;

          var coPoint = self._isPointCallout(shape);
          if (coPoint) {
            // The words as point text: their own size and stretch, the
            // box written back from them. Upright, as every callout is.
            el.classList.add("is-point");
            var coScale = self._markerScale() || 1;
            var coStr = self._pointStretch(shape, "callout");
            var coM = self._pointMeasure(shape);
            // Corners, as a text box has them: the words are padded in as
            // far as the corners bite, and the box grows by that much.
            var coPad = self._textboxPadPx(shape, coM.pad, coM.natW * coStr.x, coM.natH * coStr.y);
            if (coPad > coM.pad) {
              coM.natW += 2 * (coPad - coM.pad);
              coM.natH += 2 * (coPad - coM.pad);
              coM.pad = coPad;
            }
            if (!g.text_box) g.text_box = Object.assign({}, box);
            box = g.text_box;
            box.w = coM.natW * coStr.x / coScale;
            box.h = coM.natH * coStr.y / coScale;
            cobox = self._orientedBox(box);
            bx = cobox.x; by = cobox.y;
            bw = coM.natW * coStr.x; bh = coM.natH * coStr.y;
            self._pointDraw(shape, coM, bx, by, coStr, coRect, coText, null);
            // The plate and outline are a frame with the callout's corners —
            // sharp unless chosen, so the plate meets the underline square.
            self._drawTextboxFrame(shape, el, bx, by, bw, bh);
            shape._renderedBox = { x: box.x, y: box.y, w: box.w, h: box.h };
          } else {
            if (coRect) {
              coRect.setAttribute("x", bx);
              coRect.setAttribute("y", by);
              coRect.setAttribute("width",  bw);
              coRect.setAttribute("height", bh);
            }
            if (coText) {
              var calloutText = (shape.metadata && shape.metadata.title) || "";
              // A fraction of the box rather than a fixed screen length: the font is
              // already `height * 0.65`, so a proportional pad keeps the label
              // self-similar at every size. A px-at-1:1 pad would crowd the text
              // against the edge on a zoomed-out board and balloon on a zoomed-in
              // one. 0.13 reproduces the original 4px at a typical label height.
              var coPad = bh * 0.13;
              var coFontFamily = "ui-sans-serif, system-ui, -apple-system, sans-serif";
              var coFontWeight = "500";
              var coFontSizeByHeight = self._fontSizeFor(
                shape, Math.max(self._zoomPx(10), bh * 0.65)
              );
              // Padding is a fraction of the BOX while the box drives the
              // font. A pinned size inverts that — the text drives the box —
              // so it comes off the font instead, or a small size in a box
              // that was dragged large keeps the big box's padding. The 0.2
              // is the same proportion: pad was 0.13 of the height, the font
              // 0.65 of it, and 0.13 / 0.65 = 0.2.
              if (self._hasPinnedFontSize(shape)) coPad = coFontSizeByHeight * 0.2;

              // Width-fit cap: same fix `_renderTitleSibling` got in
              // 0.2.3 — without it, callout text that overflows the box
              // width at the height-derived font-size wraps onto
              // multiple lines, `coActualH` exceeds `bh`, the snap on
              // body-drag release writes that taller height back into
              // `geometry.text_box`, the next render derives an even
              // larger font, more lines wrap, and the callout grows
              // exponentially per interaction. Capping the font-size so
              // the text fits the box width on a single line bounds
              // `coActualH` to one line of text and breaks the cycle.
              var coWidthAtHeightFont = self._measureTextWidth(
                calloutText, coFontSizeByHeight, coFontFamily, coFontWeight
              );
              var coAvailWidth = Math.max(1, bw - coPad * 2);
              var coFontSize = coFontSizeByHeight;
              // The cap exists to break a feedback loop: box height drives the
              // font, the font drives the measured height, and the measured
              // height gets written back to the box. A PINNED size is not in
              // that loop — it comes from the user, not from the box — so it
              // is honoured as given and the box grows to hold it instead.
              if (!self._hasPinnedFontSize(shape) && coWidthAtHeightFont > coAvailWidth) {
                coFontSize = Math.max(self._zoomPx(10), coFontSizeByHeight * coAvailWidth / coWidthAtHeightFont);
              }

              coText.setAttribute("x", bx + coPad);
              coText.setAttribute("y", by + coPad);
              coText.setAttribute("font-size", coFontSize);
              coText.setAttribute("font-family", coFontFamily);
              coText.setAttribute("font-weight", coFontWeight);
              // Cross-browser baseline fix: override the factory-default
              // `dominant-baseline: hanging` with `alphabetic` (the
              // default for SVG text). Safari's `hanging` interpretation
              // renders text ABOVE the hanging baseline, Firefox's
              // renders text BELOW per spec — so a callout that fits in
              // Safari shows its text below the rect in Firefox. Both
              // browsers honor `alphabetic` identically, and the wrap
              // helper's `dy="1em"` on the first tspan still positions
              // the line one em below the text element's y (the
              // alphabetic baseline lands at by + coPad + coFontSize).
              coText.setAttribute("dominant-baseline", "alphabetic");
              var coMeasured = self._fillTextWithWrappedTspans(
                coText, calloutText, coAvailWidth, coFontSize
              );

              // Shrink-wrap the callout's text bbox the same way text
              // shapes and titles do — keeps the underline + leader
              // attached to the visible text edge. `_renderedBox` is
              // the shrunk visual; geometry.text_box stays at the
              // user-set size. Handle drag math (in _startHandleDrag
              // / _applyHandleDrag) uses delta from startPt against
              // the FULL geometry, so dragging a handle never bakes
              // the visual shrink back into the storage box.
              var coActualW = Math.max(coMeasured.width + coPad * 2, coFontSize);
              var coActualH = Math.max(coMeasured.height + coPad * 2, coFontSize * 1.2);
              if (coRect) {
                coRect.setAttribute("width",  coActualW);
                coRect.setAttribute("height", coActualH);
              }
              self._applyLabelBg(coRect, shape);
              var cosx = bw > 0 ? bw / box.w : 1;
              var cosy = bh > 0 ? bh / box.h : cosx;
              shape._renderedBox = {
                x: box.x,
                y: box.y,
                w: cosx > 0 ? coActualW / cosx : box.w,
                h: cosy > 0 ? coActualH / cosy : box.h
              };
              bw = coActualW;
              bh = coActualH;
            } else {
              shape._renderedBox = null;
            }

          }

          // Underline spans the full bottom edge of the bbox.
          var bxRight = bx + bw;
          var byBottom = by + bh;
          if (coUnderline) {
            coUnderline.setAttribute("x1", bx);
            coUnderline.setAttribute("y1", byBottom);
            coUnderline.setAttribute("x2", bxRight);
            coUnderline.setAttribute("y2", byBottom);
          }

          // The label — box, text and underline — stays UPRIGHT whatever
          // the board's rotation: a callout exists to be read, and one lying
          // on its side (or upside down at 180°) is not. It keeps its own
          // size and sits centred where its stored box's centre lands, so it
          // rides the board round without turning with it. Every other text
          // (text boxes, dimension labels, titles) still turns: those are
          // drawn ON the picture, a callout is a note ABOUT it. The leader
          // already meets whichever corner is nearest, so it simply attaches
          // to the upright label.
          var coTurn = { deg: 0, cx: bx + bw / 2, cy: by + bh / 2 };
          // The label as drawn, for the handles, the hit-test and handle
          // drags — which all have to agree with what is on screen.
          shape._labelRect = { x: bx, y: by, w: bw, h: bh };
          // (Point words carry their stretch in that same attribute.)
          if (!coPoint) {
            self._setRotateTransform(coRect, coTurn);
            self._setRotateTransform(coText, coTurn);
          }
          self._setRotateTransform(coUnderline, coTurn);

          // Thickness / line type / opacity, on the parts that are lines.
          // A callout's leader and underline are strokes like any other and
          // took none of it before: the sliders moved and nothing happened.
          // The dot scales with the weight so a heavy callout doesn't end up
          // with a pinhead on the end of a fat line, and the text takes the
          // opacity so the whole thing fades together rather than leaving
          // the label at full strength over a ghosted leader.
          var coStrokePx = self._shaftStrokePx(shape, 2);
          self._applyShaftStroke(coLine, shape, coStrokePx, true);
          self._applyShaftStroke(coUnderline, shape, coStrokePx, true);
          var coOpacity = (shape.style && shape.style.opacity == null)
            ? 1 : (shape.style ? shape.style.opacity : 1);
          if (coDot) {
            coDot.setAttribute("r", Math.max(2, coStrokePx * 1.5));
            coDot.setAttribute("fill-opacity", String(coOpacity));
          }
          if (coText) coText.setAttribute("fill-opacity", String(coOpacity));

          if (coLine) {
            // Leader attaches to whichever bottom corner sits closer
            // to the anchor. Blueprint-style: anchor on the left →
            // line meets the bottom-left vertex; on the right → meets
            // the bottom-right.
            //
            // The leader itself does NOT turn: its anchor end is already in
            // the right place, and turning it would swing that end off the
            // thing the callout points at. Instead the label end is moved to
            // where the corner lands once the label turns, so the two still
            // meet.
            var bottomMidX = bx + bw / 2;
            var attachX = anchor.x < bottomMidX ? bx : bxRight;
            var attach = self._rotatePoint(
              { x: attachX, y: byBottom }, coTurn.cx, coTurn.cy, coTurn.deg
            );
            coLine.setAttribute("x1", anchor.x);
            coLine.setAttribute("y1", anchor.y);
            coLine.setAttribute("x2", attach.x);
            coLine.setAttribute("y2", attach.y);
            // Kept for the hit-test. The leader's endpoints are only known
            // here — the attach corner depends on which side the anchor is
            // on and on how the label turned — and clicking the leader used
            // to do nothing at all, because the hit-test knew about the text
            // box and the dot and nothing in between. Container px, which is
            // the space they were computed in; `_shapeContainsPoint` converts
            // its point rather than trying to invert the projection.
            shape._leaderContainer = {
              a: { x: anchor.x, y: anchor.y },
              b: { x: attach.x, y: attach.y },
              w: coStrokePx
            };
          } else {
            shape._leaderContainer = null;
          }
          if (coDot) {
            coDot.setAttribute("cx", anchor.x);
            coDot.setAttribute("cy", anchor.y);
          }
          if (coPoint) self._fitPointEditor(shape);
          break;
        }
        case "text": {
          // <g> wrapping a hit-zone <rect> and a content <text>. After
          // initial rect+text positioning, the rect shrinks to hug
          // the actual rendered text — width and height = text + pad.
          // The cached `shape._renderedBox` (image px) is what
          // handles + drag math read from so the user interacts with
          // the visible rect, not the storage envelope.
          var trect = el.querySelector(".etcher-text-rect");
          var ttext = el.querySelector(".etcher-text-content");
          if (self._isPointText(shape)) {
            self._renderPointText(shape, el, g, trect, ttext);
            break;
          }
          // The whole <g> turns, so the rect and the text inside it are
          // still laid out against an upright box and carried round
          // together — a label stays readable relative to the board
          // instead of running across it at 90°.
          var tbox = self._orientedBox(g);
          tbox.deg = (tbox.deg || 0) + self._shapeRot(shape);
          var tx = tbox.x, ty = tbox.y, tw = tbox.w, th = tbox.h;
          self._setRotateTransform(el, tbox);

          if (trect) {
            trect.setAttribute("x", tx);
            trect.setAttribute("y", ty);
            trect.setAttribute("width",  tw);
            trect.setAttribute("height", th);
          }
          if (ttext) {
            var titleText = (shape.metadata && shape.metadata.title) || "";
            // A fraction of the box rather than a fixed screen length: the font is
            // already `height * 0.65`, so a proportional pad keeps the label
            // self-similar at every size. A px-at-1:1 pad would crowd the text
            // against the edge on a zoomed-out board and balloon on a zoomed-in
            // one. 0.13 reproduces the original 4px at a typical label height.
            var pad = th * 0.13;
            var fontSize = self._fontSizeFor(shape, Math.max(self._zoomPx(10), th * 0.65));
            // Padding is a fraction of the BOX while the box drives the
            // font. A pinned size inverts that — the text drives the box —
            // so it comes off the font instead, or a small size in a box
            // that was dragged large keeps the big box's padding. The 0.2
            // is the same proportion: pad was 0.13 of the height, the font
            // 0.65 of it, and 0.13 / 0.65 = 0.2.
            if (self._hasPinnedFontSize(shape)) pad = fontSize * 0.2;
            // A text box's corners can bite into where the words would go;
            // the pad grows until they clear them.
            if (self._textboxFixed(shape)) pad = self._textboxPadPx(shape, pad, tw, th);
            ttext.setAttribute("x", tx + pad);
            ttext.setAttribute("y", ty + pad);
            ttext.setAttribute("font-size", fontSize);
            ttext.setAttribute(
              "font-family",
              "ui-sans-serif, system-ui, -apple-system, sans-serif"
            );
            ttext.setAttribute("font-weight", "500");
            // Cross-browser baseline fix — see callout render block.
            // `hanging` is interpreted differently by Safari and Firefox
            // for Latin text; switching to `alphabetic` (the SVG
            // default) keeps text positioned identically across browsers.
            ttext.setAttribute("dominant-baseline", "alphabetic");
            var measured =
              self._fillTextWithWrappedTspans(ttext, titleText, tw - pad * 2, fontSize);

            if (self._textboxFixed(shape)) {
              // A text BOX: the rect stays exactly the box the user drew
              // (set above from the geometry) — the text wraps and sizes
              // inside it, and neither typing more nor a bigger Label
              // size moves the walls. `_renderedBox` stays null so the
              // handles, the editor and every hit-test work the storage
              // geometry, which IS the visible box here.
              self._drawTextboxFrame(shape, el, tx, ty, tw, th);
              // The text placed in the box as asked: across by the anchor
              // its lines hang from, down by the room left under them.
              var tAl = self._textboxAlign(shape);
              var tAx = tAl.h === "center" ? tx + tw / 2 : tAl.h === "right" ? tx + tw - pad : tx + pad;
              ttext.setAttribute("text-anchor", tAl.h === "center" ? "middle" : tAl.h === "right" ? "end" : "start");
              ttext.setAttribute("x", tAx);
              self._shiftTspans(ttext, tAx);
              // Down: the lines laid out as CSS lays them in the editor — a
              // 1.1em box per line, the glyphs centred in each by the font's
              // own ascent and descent. Measuring from the top of the em box
              // to the last baseline (as before) left room above the letters
              // and none below them, so "centred" text sat visibly low, and
              // top and bottom were never the same distance from the walls.
              var tLines = Math.max(1, ttext.childNodes.length);
              var tMet = self._fontLineMetrics(ttext.getAttribute("font-family"), "500");
              var tFirst = (1.1 - (tMet.asc + tMet.desc)) / 2 + tMet.asc;
              var tRoom = Math.max(0, th - pad * 2 - tLines * 1.1 * fontSize);
              var tTop = ty + pad + (tAl.v === "middle" ? tRoom / 2 : tAl.v === "bottom" ? tRoom : 0);
              // The first tspan drops 1em from `y`; the baseline belongs at tFirst.
              ttext.setAttribute("y", tTop + (tFirst - 1) * fontSize);
              shape._renderedBox = null;
            } else {
              var actualW = Math.max(measured.width + pad * 2, fontSize);
              var actualH = Math.max(measured.height + pad * 2, fontSize * 1.2);
              if (trect) {
                trect.setAttribute("width",  actualW);
                trect.setAttribute("height", actualH);
              }
              self._applyLabelBg(trect, shape);
              var sx = tw > 0 ? tw / g.w : 1;
              var sy = th > 0 ? th / g.h : sx;
              shape._renderedBox = {
                x: g.x,
                y: g.y,
                w: sx > 0 ? actualW / sx : g.w,
                h: sy > 0 ? actualH / sy : g.h
              };
            }
          } else {
            shape._renderedBox = null;
          }
          break;
        }
        case "dimension": {
          // <g> with: <line> shaft, two <polyline> V-arrows at the
          // endpoints, and a <text> label that floats at a 0–1 offset
          // along the line (default midpoint). Arrow color follows
          // the shape's currentColor; label is always black with a
          // white halo for cross-color readability.
          var shaftEl = el.querySelector(".etcher-dim-shaft");
          var arrowEls = el.querySelectorAll(".etcher-dim-arrow");

          // Every fixed length below — the shaft's weight, the V-arrows, the
          // label and its halo — was chosen for a dimension drawn at the
          // board's own line weight. Taken at that weight, the whole
          // dimension scales as one object and thins with the board, instead
          // of keeping its full size while the drawing shrinks away from it.
          // The same number the connectors use, so a dimension and an arrow
          // beside each other match.
          var dimK = self._boardLineScale();
          // A styled width (canvas units) wins; without one this is exactly
          // the old `LINE_WEIGHT_PX * dimK`. Dash lands on the shaft only —
          // heads stay solid.
          var dimStroke = self._shaftStrokePx(shape, LINE_WEIGHT_PX * dimK);
          self._applyShaftStroke(shaftEl, shape, dimStroke, true);
          for (var dai = 0; dai < arrowEls.length; dai++) {
            self._applyShaftStroke(arrowEls[dai], shape, dimStroke, false);
          }

          var aImg = { x: g.a[0], y: g.a[1] };
          var bImg = { x: g.b[0], y: g.b[1] };
          var aC = self._imageToContainer(aImg);
          var bC = self._imageToContainer(bImg);

          if (shaftEl) {
            shaftEl.setAttribute("x1", aC.x);
            shaftEl.setAttribute("y1", aC.y);
            shaftEl.setAttribute("x2", bC.x);
            shaftEl.setAttribute("y2", bC.y);
          }

          // V-arrowheads at each endpoint, opening backward toward the
          // other end. `_vArrowPoints` returns "" for a degenerate line
          // (length 0) — draft state during the very first pointermove
          // tick — which blanks the heads rather than drawing a spike in
          // an arbitrary direction.
          // Heads keep their proportion to the SHAFT, not to the default
          // weight — `dimStroke / LINE_WEIGHT_PX` is `dimK` when no width is
          // styled (identical picture), and grows the V with a fattened
          // shaft instead of leaving a hairline arrowhead on a heavy line.
          var dimHeadK = dimStroke / LINE_WEIGHT_PX;
          var dimHeadLen = 10 * dimHeadK;
          var dimHeadHalf = 5 * dimHeadK;
          if (arrowEls[0]) {
            arrowEls[0].setAttribute(
              "points", self._vArrowPoints(aC, bC, dimHeadLen, dimHeadHalf)
            );
          }
          if (arrowEls[1]) {
            arrowEls[1].setAttribute(
              "points", self._vArrowPoints(bC, aC, dimHeadLen, dimHeadHalf)
            );
          }

          // The label is the standard title sibling every other shape has —
          // a box you can resize, recolour and edit — rather than a bespoke
          // <text> welded to the shaft, which could only ever have its words
          // changed. It stays magnetic to the line: `_shapeTitleBoxImage`
          // centres it on the point at `title_offset`, and dragging it slides
          // that offset rather than positioning it freely.
          bboxTopImage = {
            x: (aImg.x + bImg.x) / 2,
            y: Math.min(aImg.y, bImg.y)
          };
          break;
        }
        case "line": {
          // Bare stroked line A→B — no arrows, no inline label. Title
          // (if any) is rendered above by the standard sibling path
          // (see `_renderTitleSibling` below).
          var lineShaft = el.querySelector(".etcher-line-shaft");
          var lAImg = { x: g.a[0], y: g.a[1] };
          var lBImg = { x: g.b[0], y: g.b[1] };
          var lAC = self._imageToContainer(lAImg);
          var lBC = self._imageToContainer(lBImg);
          if (lineShaft) {
            lineShaft.setAttribute("x1", lAC.x);
            lineShaft.setAttribute("y1", lAC.y);
            lineShaft.setAttribute("x2", lBC.x);
            lineShaft.setAttribute("y2", lBC.y);
            // A styled width (canvas units) wins; without one the fallback
            // is the fixed 2px screen weight lines have always had, so
            // untouched boards don't change.
            self._applyShaftStroke(
              lineShaft, shape, self._shaftStrokePx(shape, LINE_WEIGHT_PX), true
            );
          }
          bboxTopImage = {
            x: (lAImg.x + lBImg.x) / 2,
            y: Math.min(lAImg.y, lBImg.y)
          };
          break;
        }
        case "arrow": {
          // Connector: a shaft with one V-arrowhead at B, the end that
          // points at the target. Resolve the bindings first so the arrow
          // is drawn against where its shapes are *now* — this is the step
          // that makes it follow them, and it runs on every render, so a
          // pan/zoom frame re-resolves too (harmless: same image-px answer,
          // and it costs one bbox per bound end).
          self._resolveArrowEnds(shape);
          var arShaft = el.querySelector(".etcher-arrow-shaft");
          var arHead = el.querySelector(".etcher-arrow-head");
          var arImg = self._arrowPath(g);
          var arC = arImg.map(function(p) { return self._imageToContainer(p); });
          var arK = self._boardLineScale();
          if (arShaft) {
            // Two points draw straight; bends draw the same smooth curve
            // the geometry helpers sample (_arrowRoute), so what is hit,
            // labelled and measured is what is seen.
            arShaft.setAttribute("d", arImg.length > 2
              ? self._catmullRomPathD(arImg, function(p) {
                  return self._imageToContainer(p);
                })
              : "M " + arC[0].x + " " + arC[0].y +
                " L " + arC[1].x + " " + arC[1].y);
          }
          // A styled width (canvas units) wins; without one this is exactly
          // the old `LINE_WEIGHT_PX * arK`, floored the same way every other
          // stroke is so an arrow on a very wide view stays visible rather
          // than thinning away to nothing.
          var arW = self._shaftStrokePx(shape, LINE_WEIGHT_PX * arK);
          if (arHead) {
            // The head follows the LAST segment, not the tail-to-head chord —
            // on a routed arrow those point in quite different directions,
            // and it's the final approach that has to line up with whatever
            // the arrow is pointing at.
            //
            // Sized off the SHAFT's weight — `arW / LINE_WEIGHT_PX` is `arK`
            // when no width is styled (identical picture), so the head keeps
            // its proportion to the shapes it connects by default and grows
            // with a fattened shaft instead of staying a hairline V on it.
            var arHeadK = arW / LINE_WEIGHT_PX;
            var tip = arC[arC.length - 1];
            // The V lines up with the CURVE's final approach, not the last
            // chord — on a strong bend those differ visibly, and it is the
            // drawn line the head has to cap.
            var prevImg = arImg.length > 2
              ? self._crSample(arImg, arImg.length - 2, 1 - 1 / ROUTE_SAMPLES)
              : arImg[arImg.length - 2] || arImg[arImg.length - 1];
            var prev = self._imageToContainer(prevImg);
            arHead.setAttribute(
              "points",
              self._vArrowPoints(
                tip, prev,
                ARROW_HEAD_LEN * arHeadK,
                ARROW_HEAD_HALF_WIDTH * arHeadK
              )
            );
          }
          // Dash lands on the shaft only — the head stays solid.
          self._applyShaftStroke(arShaft, shape, arW, true);
          self._applyShaftStroke(arHead, shape, arW, false);
          var arMinY = arImg[0].y, arSumX = 0;
          arImg.forEach(function(p) {
            if (p.y < arMinY) arMinY = p.y;
            arSumX += p.x;
          });
          bboxTopImage = { x: arSumX / arImg.length, y: arMinY };
          break;
        }
      }

      // Inline title sibling for non-callout shapes (rect/circle/poly/
      // freehand/line). Callout renders its title as a child <text>
      // inside its <g>; dimension paints the title inline along the
      // shaft. Text is its own free-floating-label shape kind.
      if (shape.kind !== "callout" && shape.kind !== "text") {
        // Cache so title-drag handlers can resolve the default
        // anchor without recomputing the parent's bbox.
        shape.bboxTopImage = bboxTopImage;
        self._renderTitleSibling(shape, bboxTopImage);
      }

      // Stroke width tracks zoom, so it has to be re-applied every render the
      // way a marker's is — `_renderShape` runs on every pan/zoom frame,
      // whereas `_applyLineParams` otherwise only runs when the style itself
      // changes.
      if (self._isStrokeShape(shape.kind) && shape.style) {
        self._anchorLegacyWidth(shape);
        self._applyLineParams(el, shape.style, self._markerScale());
      }

      // Connector dots are positioned in container px, so they have to be
      // re-placed whenever their shape is redrawn — a body drag renders the
      // shape without ever touching hover state, which used to leave the
      // dots stranded over the spot the shape had left.
      if (shape === self._connectorDotShape) self._positionConnectorDots();

      // Connectors bound to this shape have to be redrawn whenever it moves,
      // resizes, or is restored — and every one of those paths ends here, so
      // this is the single place that catches them all instead of a call
      // bolted onto each gesture (body drag, handle drag, undo, collab
      // apply, arrange). The guard stops the recursion at one level: the
      // arrow's own render would otherwise come straight back here.
      if (shape.kind !== "arrow" && shape.uuid && !self._renderingConnectors) {
        var connectors = self._arrowsBoundTo(shape.uuid);
        if (connectors.length) {
          self._renderingConnectors = true;
          try {
            connectors.forEach(function(c) { self._renderShape(c); });
          } finally {
            self._renderingConnectors = false;
          }
        }
      }

      // A collaborative host watches here for the same reason connectors do:
      // every path that moves or resizes a shape ends at this function, so
      // this is the one place that catches them all without a call bolted
      // onto each gesture. See `_noteLiveMove`.
      if (self._liveMoveHandler && shape.uuid) self._noteLiveMove(shape);

      // …and the shape being drawn right now, which has no uuid yet — it is
      // `draftState` until the gesture ends and `_finalizeShape` gives it
      // one. Same single choke point, same frame batching. See
      // `_noteLiveDraw`.
      if (self._liveDrawHandler && self._isLiveDraft(shape)) {
        self._noteLiveDraw(shape.kind, shape.geometry, shape.style);
      }

      // Same reason as the badge: it traces the geometry this render wrote.
      self._syncHatchOutline(shape);

      // An open text box editor rides its box through every resize, turn,
      // move and scale (point text's re-fits from `_renderPointText`) — its
      // font and padding too, read off the text just rendered, so the
      // scale dot grows the words being typed with the box, not on release.
      var tbEd = self._textEditor;
      if (tbEd && tbEd.fixed && tbEd.shape === shape && tbEd.fit) {
        self._repositionTextEditor();
        self._fitPointGrip(shape);
      }

      // A frame round a selection follows its shapes.
      self._xfNoteRender(shape);

      // Badge last, so it reads the geometry this render just wrote.
      self._renderBadge(shape);
    },

    // ── Shape badges ──────────────────────────────────────────────────────
    //
    // `metadata.badge` (a number or short string) renders as a small count
    // bubble pinned to the shape's top-right corner — a host showing "this
    // annotation has N discussion entries" sets it when hydrating metadata
    // (and keeps it fresh through `patchShape`). Absent / 0 / "" removes
    // the bubble. Screen-sized (container px), so it neither balloons nor
    // vanishes with zoom, and pointer-transparent so it never steals the
    // shape's own hover/click.
    _renderBadge: function(shape) {
      var value = shape && shape.metadata ? shape.metadata.badge : null;
      var show = value != null && value !== 0 && value !== "" && value !== "0";

      if (!show) {
        if (shape && shape._badgeEl && shape._badgeEl.parentNode) {
          shape._badgeEl.parentNode.removeChild(shape._badgeEl);
        }
        if (shape) shape._badgeEl = null;
        return;
      }

      var parent = shape.el && shape.el.parentNode;
      var corner = this._badgeAnchorContainer(shape);
      if (!corner || !parent) return;

      var label = String(value);
      if (/^\d+$/.test(label) && parseInt(label, 10) > 99) label = "99+";

      var badge = shape._badgeEl;
      if (!badge) {
        badge = svgEl("g");
        badge.classList.add("etcher-badge");
        badge.appendChild(svgEl("circle", { r: "7" }));
        var btext = svgEl("text", {
          "text-anchor": "middle",
          "dominant-baseline": "central"
        });
        btext.classList.add("etcher-badge-count");
        badge.appendChild(btext);
        shape._badgeEl = badge;
      }
      // Re-append every render: keeps the bubble above the shape (and its
      // siblings) even after z-order rearranges re-insert elements.
      parent.appendChild(badge);

      badge.setAttribute("transform", "translate(" + corner.x + "," + corner.y + ")");

      var textEl = badge.querySelector(".etcher-badge-count");
      if (textEl && textEl.textContent !== label) textEl.textContent = label;
    },

    // Where the badge sits, in container px. Not the bounding box corner —
    // on a circle or a freehand scribble that floats in empty air. The
    // precedence is:
    //
    //   1. The LABEL's top-right corner, when the shape has one — the label
    //      is the annotation's face, so the count hangs off it. Read from
    //      the rendered label rect (title group, or the shape's own text
    //      rect for text/callout), which is already laid out in container
    //      px; dimension labels have no rect, so their <text> bbox stands
    //      in.
    //   2. Otherwise the point ON the shape closest to its bbox top-right:
    //      the corner itself for boxy kinds, the 45° point on a circle,
    //      the nearest vertex/endpoint for point-built kinds — so the
    //      bubble always touches the shape it counts.
    _badgeAnchorContainer: function(shape) {
      var g = shape.geometry || {};

      var rectEl =
        (shape.titleGroup && shape.titleGroup.querySelector(".etcher-text-rect")) ||
        (shape.el && shape.el.querySelector &&
          shape.el.querySelector(".etcher-text-rect"));
      if (rectEl) {
        var rx = parseFloat(rectEl.getAttribute("x"));
        var ry = parseFloat(rectEl.getAttribute("y"));
        var rw = parseFloat(rectEl.getAttribute("width"));
        if (isFinite(rx) && isFinite(ry) && isFinite(rw) && rw > 0) {
          return { x: rx + rw, y: ry };
        }
      }

      if (shape.metadata && shape.metadata.title && shape.el &&
          shape.el.querySelector) {
        var labelEl = shape.el.querySelector(".etcher-text-content, text");
        if (labelEl && labelEl.getBBox) {
          try {
            var lb = labelEl.getBBox();
            if (lb.width > 0) return { x: lb.x + lb.width, y: lb.y };
          } catch (_) {}
        }
      }

      var anchor = null;
      if (shape.kind === "circle" && isFinite(g.cx)) {
        // 45° up-right on the circumference.
        var k = Math.SQRT1_2;
        anchor = { x: g.cx + g.r * k, y: g.cy - g.r * k };
      } else if (isFinite(g.x) && isFinite(g.w)) {
        anchor = { x: g.x + g.w, y: g.y };
      } else {
        var pts = [];
        if (Array.isArray(g.points)) pts = pts.concat(g.points);
        if (Array.isArray(g.nodes)) {
          g.nodes.forEach(function(n) { if (n && n.p) pts.push(n.p); });
        }
        if (g.a) pts.push(g.a);
        if (g.b) pts.push(g.b);
        var bbox = this._shapeBBoxImagePx(shape);
        if (!bbox) return null;
        var corner = { x: bbox.x + bbox.w, y: bbox.y };
        if (pts.length) {
          var best = null;
          var bestD = Infinity;
          pts.forEach(function(p) {
            var px = Array.isArray(p) ? p[0] : p.x;
            var py = Array.isArray(p) ? p[1] : p.y;
            if (!isFinite(px) || !isFinite(py)) return;
            var d = (px - corner.x) * (px - corner.x) + (py - corner.y) * (py - corner.y);
            if (d < bestD) { bestD = d; best = { x: px, y: py }; }
          });
          anchor = best || corner;
        } else {
          anchor = corner;
        }
      }

      return anchor ? this._imageToContainer(anchor) : null;
    },

    // ── In-flight moves ───────────────────────────────────────────────────
    //
    // Shapes are emitted for persistence when a gesture ENDS. That is right
    // for storage and wrong for everyone watching: a peer sees nothing for
    // the length of a drag and then the shape appears somewhere else.
    //
    // So while a shape is being dragged its position is also reported, at
    // frame rate, to a host that asked for it (`onShapesMoving`). Those
    // reports are not edits — nothing is stored, no undo entry is made, and
    // `_emitChanged` is not involved. The real edit still lands the usual way
    // on release, and a host that never registers a handler pays nothing but
    // one property check per render.
    _noteLiveMove: function(shape) {
      // Only while a pointer is actually down. `_renderShape` runs for plenty
      // of reasons that are not a drag — a colour change, a text edit, the
      // initial paint of a loaded board — and none of those want reporting as
      // movement.
      if (!this._pointerHeld || this._applyingLiveMoves) return;

      this._liveMoved = this._liveMoved || {};
      this._liveMoved[shape.uuid] = shape;

      var self = this;
      if (this._liveMoveFrame) return;
      this._liveMoveFrame = requestAnimationFrame(function() {
        self._liveMoveFrame = null;
        self._flushLiveMoves();
      });
    },

    // Batched per frame: a drag renders on every pointer event, and pointer
    // events can outrun the display. One report per frame is as often as it
    // can possibly matter.
    _flushLiveMoves: function() {
      var moved = this._liveMoved;
      this._liveMoved = null;
      if (!moved || !this._liveMoveHandler) return;

      var payload = [];
      for (var uuid in moved) {
        if (!Object.prototype.hasOwnProperty.call(moved, uuid)) continue;
        payload.push({ uuid: uuid, geometry: moved[uuid].geometry });
      }
      if (payload.length) {
        try { this._liveMoveHandler(payload); } catch (_) {}
      }
    },

    // Apply positions a peer reported mid-drag.
    //
    // Guarded so applying them doesn't look like local movement and come
    // straight back out: without this, holding the pointer down anywhere
    // while a peer dragged something would rebroadcast their shape as ours.
    _applyLiveMoves: function(list) {
      if (!list || !list.length) return;
      this._applyingLiveMoves = true;
      try {
        for (var i = 0; i < list.length; i++) {
          var entry = list[i];
          if (!entry || !entry.uuid || !entry.geometry) continue;
          var shape = this.shapes.find(function(s) { return s.uuid === entry.uuid; });
          if (!shape) continue;
          shape.geometry = entry.geometry;
          if (shape.el) this._renderShape(shape);
        }
      } finally {
        this._applyingLiveMoves = false;
      }
    },

    // ── In-flight drawing ─────────────────────────────────────────────────
    //
    // A stroke is one shape that does not exist yet. There is no uuid to
    // report against and nothing for a peer to update, so the report carries
    // the draft itself — kind, geometry, style — and the host says which peer
    // it came from. One person draws one shape at a time, so that is enough
    // to tell provisional shapes apart.
    // The shape being drawn right now, whichever tool is drawing it. Most
    // tools draft into `draftState`; a callout keeps its own while the label
    // is placed. A polygon is neither — it is drawn vertex by vertex through
    // `_renderPolygonPreview`, which reports for itself.
    _isLiveDraft: function(shape) {
      return !!shape && (shape === this.draftState || shape === this.draftCallout);
    },

    _noteLiveDraw: function(kind, geometry, style) {
      if (!kind || !geometry || this._applyingGhostDraw) return;

      this._liveDrawPending = { kind: kind, geometry: geometry, style: style || null };
      if (this._liveDrawFrame) return;

      var self = this;
      this._liveDrawFrame = requestAnimationFrame(function() {
        self._liveDrawFrame = null;
        self._flushLiveDraw();
      });
    },

    // One report per frame, like the moves. A point-based stroke reports its
    // whole point list each time, which grows as the stroke does: a long
    // marker line is a few hundred points, so the tail of a slow stroke is
    // the expensive end of this. Kept whole because a partial protocol needs
    // resynchronisation for something that is thrown away a moment later —
    // if it ever costs too much, sending only the points added since the
    // last report is the change, and the ghost is where it would land.
    _flushLiveDraw: function() {
      var draft = this._liveDrawPending;
      this._liveDrawPending = null;
      if (!draft || !this._liveDrawHandler) return;

      try {
        this._liveDrawHandler(draft);
      } catch (_) {}
    },

    // A peer's shape, mid-draw. Rendered through the same factory a stored
    // annotation goes through, so a ghost marker looks exactly like the
    // marker that will replace it — and then held aside: never in `shapes`,
    // so it cannot be selected, emitted, undone, hit-tested or saved.
    _applyGhostDraw: function(key, draft) {
      if (!key || !draft || !draft.kind || !draft.geometry) return;

      this._ghostDraws = this._ghostDraws || {};
      var ghost = this._ghostDraws[key];

      // A different kind means a different element: the peer let go and
      // started something else, and the report for the new shape arrived
      // before the end of the old one.
      if (ghost && ghost.kind !== draft.kind) {
        this._dropGhostDraw(key);
        ghost = null;
      }

      this._applyingGhostDraw = true;
      try {
        if (!ghost) {
          ghost = this._renderAnnotation(
            { uuid: null, kind: draft.kind, geometry: draft.geometry, style: draft.style },
            { ghost: true }
          );
          if (!ghost) return;
          this._ghostDraws[key] = ghost;
        } else {
          ghost.geometry = draft.geometry;
          this._renderShape(ghost);
        }
      } finally {
        this._applyingGhostDraw = false;
      }
    },

    // Anything batched is dropped rather than flushed: the finished shape is
    // already on its way as a real edit, and one more provisional frame
    // behind it would only draw the same line twice.
    _endLiveDraw: function() {
      if (this._liveDrawFrame) {
        cancelAnimationFrame(this._liveDrawFrame);
        this._liveDrawFrame = null;
      }
      // The last frame is SENT, not dropped. Reporting is batched a frame at
      // a time, so the points drawn after the last one had yet to go out —
      // and a peer holds the ghost on screen now until the real shape lands.
      // Without this they hold a stroke that stops a few points short of the
      // one that replaces it, and the tail of it appears at the handover:
      // the same flash, smaller.
      this._flushLiveDraw();
      this._liveDrawPending = null;
      if (this._liveDrawEndHandler) {
        try { this._liveDrawEndHandler(); } catch (_) {}
      }
    },

    // The peer let go. The shape they drew is on its way as a real edit, but
    // it has a server round trip to make, and dropping the ghost now leaves a
    // hole exactly where the stroke was: it vanishes, and reappears when the
    // edit lands. On this machine that hole is a couple of frames; across a
    // real network it is however long the trip takes. Either way it reads as
    // a flash at the end of every stroke — the one thing live drawing was
    // supposed to get rid of.
    //
    // So the ghost is not dropped, it is HANDED OVER: held on screen until
    // the real shape is built, then retired in that same task, so the frame
    // in between shows neither a gap nor two copies of the line.
    _retireGhostDraw: function(key) {
      if (!this._ghostDraws) return;
      var ghost = this._ghostDraws[key];
      if (!ghost) return;
      // Out of `_ghostDraws` first: the same peer may start their next stroke
      // before this one's edit arrives, and that stroke needs the key.
      delete this._ghostDraws[key];

      var self = this;
      var entry = { ghost: ghost };
      entry.timer = setTimeout(function() { self._dropRetiredGhost(entry); }, GHOST_HANDOVER_MS);
      this._retiringGhosts = this._retiringGhosts || [];
      this._retiringGhosts.push(entry);
    },

    _dropRetiredGhost: function(entry) {
      if (!entry || !this._retiringGhosts) return;
      var at = this._retiringGhosts.indexOf(entry);
      if (at === -1) return;
      this._retiringGhosts.splice(at, 1);
      if (entry.timer) clearTimeout(entry.timer);
      var el = entry.ghost && entry.ghost.el;
      if (el && el.parentNode) el.parentNode.removeChild(el);
    },

    // A real shape has just been built. If it is the one somebody was drawing
    // a moment ago, its ghost's work is done.
    //
    // Matched on the drawing itself rather than on who sent it: the edit
    // arrives as a shape, and nothing in it says which peer made it. The
    // geometry is the one the author reported in their last frame, so the two
    // agree to within whatever rounding the trip did to the numbers.
    _handOverGhost: function(shape) {
      if (!shape || !this._retiringGhosts || !this._retiringGhosts.length) return;

      var self = this;
      var sameKind = this._retiringGhosts.filter(function(e) {
        return e.ghost && e.ghost.kind === shape.kind;
      });
      if (!sameKind.length) return;

      var match = null;
      for (var i = 0; i < sameKind.length; i++) {
        if (self._sameGeometry(sameKind[i].ghost.geometry, shape.geometry)) {
          match = sameKind[i];
          break;
        }
      }
      // Nothing matched on geometry, but there is exactly one stroke of this
      // kind waiting to be replaced: that is the one. A shape can be moved or
      // reshaped between letting go and the edit arriving, and a ghost left
      // behind by a miss would sit there as a second copy of the line until
      // it timed out.
      if (!match && sameKind.length === 1) match = sameKind[0];
      if (match) this._dropRetiredGhost(match);
    },

    // Geometry equality, loose enough for a round trip. Numbers are compared
    // within a small tolerance rather than exactly: the trip encodes them,
    // stores them and decodes them again, and a coordinate that comes back a
    // thousandth off is the same point as far as anyone looking at it is
    // concerned.
    _sameGeometry: function(a, b) {
      if (a === b) return true;
      if (typeof a === "number" && typeof b === "number") return Math.abs(a - b) <= 0.5;
      if (Array.isArray(a) || Array.isArray(b)) {
        if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
        for (var i = 0; i < a.length; i++) if (!this._sameGeometry(a[i], b[i])) return false;
        return true;
      }
      if (a && b && typeof a === "object" && typeof b === "object") {
        var ka = Object.keys(a), kb = Object.keys(b);
        if (ka.length !== kb.length) return false;
        for (var j = 0; j < ka.length; j++) {
          if (!Object.prototype.hasOwnProperty.call(b, ka[j])) return false;
          if (!this._sameGeometry(a[ka[j]], b[ka[j]])) return false;
        }
        return true;
      }
      return a === b;
    },

    _dropGhostDraw: function(key) {
      if (!this._ghostDraws) return;
      var ghost = this._ghostDraws[key];
      if (!ghost) return;
      delete this._ghostDraws[key];
      if (ghost.el && ghost.el.parentNode) ghost.el.parentNode.removeChild(ghost.el);
    },

    // Pointer state, tracked only once a host has asked for live moves.
    // `pointerup` is on the document and in the capture phase so a drag that
    // ends outside the canvas still closes.
    _wireLiveMoveTracking: function() {
      if (this._liveMoveWired) return;
      this._liveMoveWired = true;

      var self = this;
      this._liveMoveDown = function() { self._pointerHeld = true; };
      this._liveMoveUp = function() {
        if (!self._pointerHeld) return;
        self._pointerHeld = false;
        // Anything batched but not yet reported would otherwise be dropped,
        // leaving peers a frame short of where the shape actually finished.
        if (self._liveMoveFrame) {
          cancelAnimationFrame(self._liveMoveFrame);
          self._liveMoveFrame = null;
        }
        self._flushLiveMoves();
        if (self._liveMoveEndHandler) {
          try { self._liveMoveEndHandler(); } catch (_) {}
        }

        // The draw ends on the same pointer — unless it does not. A polygon
        // and a callout are placed across several clicks, and the pointer
        // comes up after every one of them; ending there would take a peer's
        // shape away between vertices. Those finish in `_clearDrafts`.
        if (!self.draftPolygon && !self.draftCallout) self._endLiveDraw();
      };

      document.addEventListener("pointerdown", this._liveMoveDown, true);
      document.addEventListener("pointerup", this._liveMoveUp, true);
      document.addEventListener("pointercancel", this._liveMoveUp, true);
    },

    // Render a movable "title group" for shapes that carry a non-blank
    // `metadata.title`. The group is a callout-style satellite:
    // bbox + scaled+wrapped text + a thin leader line back to the
    // parent shape's nearest perimeter point. Drag the title to move
    // it (persisted as `metadata.title_box`); double-click to open
    // the inline editor; the leader auto-updates.
    _renderTitleSibling: function(shape, bboxTopImage) {
      var title =
        (shape && shape.metadata && shape.metadata.title) ||
        null;
      var trimmed = title && String(title).trim();
      // A point label being typed shows from its first keystroke on, empty
      // or not — the words on screen are the label itself.
      var pointLabel = this._isPointLabel(shape);
      var typingLabel = pointLabel && shape._draftTitle != null;

      if ((!trimmed && !typingLabel) || !bboxTopImage) {
        if (shape.titleGroup && shape.titleGroup.parentNode) {
          shape.titleGroup.parentNode.removeChild(shape.titleGroup);
        }
        shape.titleGroup = null;
        // A shaft whose label went away paints whole again — unless its
        // label is being TYPED right now: the title is still empty then,
        // but the editor's fit has already broken the line under itself,
        // and this render (finalize, enter-edit, any repaint) would close
        // it back up until the next keystroke re-broke it — a flicker on
        // exactly the first frame of typing.
        if (this._labelRidesShaft(shape.kind) &&
            !(this._textEditor && this._textEditor.shape === shape)) {
          this._clearShaftLabelGap(shape);
        }
        return;
      }

      if (!shape.titleGroup) {
        var tg = svgEl("g");
        tg.classList.add("etcher-shape", "etcher-text", "etcher-title-group");
        // Leader first so it draws under the bbox + text overlay.
        var tLine = svgEl("line", {
          "stroke-width": "1.5",
          stroke: "currentColor",
          fill: "none"
        });
        tLine.classList.add("etcher-title-leader");
        var tRect = svgEl("rect", {
          fill: "transparent",
          stroke: "transparent",
          "stroke-width": "2"
        });
        tRect.classList.add("etcher-text-rect");
        var tText = svgEl("text", {
          "text-anchor": "start",
          "dominant-baseline": "hanging",
          fill: "currentColor",
          stroke: "none"
        });
        tText.classList.add("etcher-text-content");
        tg.appendChild(tLine);
        tg.appendChild(tRect);
        tg.appendChild(tText);
        if (shape.uuid) tg.setAttribute("data-title-for", shape.uuid);
        if (shape.style && shape.style.color) tg.style.color = shape.style.color;
        this.svg.appendChild(tg);
        shape.titleGroup = tg;
        this._attachTitleInteractions(shape);
      }

      // Re-applied on every render, not just when the group is built. The
      // colour was previously frozen at creation, so recolouring a shape
      // left its label behind in the old colour with no way to catch it up.
      shape.titleGroup.style.color = this._titleColorFor(shape);

      if (pointLabel) {
        this._renderPointLabel(shape, bboxTopImage);
        return;
      }
      shape.titleGroup.classList.remove("is-point");

      var titleBox = this._shapeTitleBoxImage(shape, bboxTopImage);
      var tbox = this._orientedBox(titleBox);
      var tx = tbox.x, ty = tbox.y, tw = tbox.w, th = tbox.h;

      var rectEl = shape.titleGroup.querySelector(".etcher-text-rect");
      var textEl = shape.titleGroup.querySelector(".etcher-text-content");
      var lineEl = shape.titleGroup.querySelector(".etcher-title-leader");

      // The box and its text turn with the board; the leader does NOT. Its
      // two endpoints are computed in container space at each end, so they
      // already sit where they should — turning it as well would swing it
      // off both the label and the shape it points at.
      this._setRotateTransform(rectEl, tbox);
      this._setRotateTransform(textEl, tbox);

      if (rectEl) {
        rectEl.setAttribute("x", tx);
        rectEl.setAttribute("y", ty);
        rectEl.setAttribute("width",  tw);
        rectEl.setAttribute("height", th);
      }
      if (textEl) {
        // Proportional to the box, as in the text and callout renders.
        var pad = th * 0.13;
        var fontFamily = "ui-sans-serif, system-ui, -apple-system, sans-serif";
        var fontWeight = "500";
        var fontSizeByHeight = this._fontSizeFor(
          shape, Math.max(this._zoomPx(10), th * 0.65)
        );
        // Padding is a fraction of the BOX while the box drives the
        // font. A pinned size inverts that — the text drives the box —
        // so it comes off the font instead, or a small size in a box
        // that was dragged large keeps the big box's padding. The 0.2
        // is the same proportion: pad was 0.13 of the height, the font
        // 0.65 of it, and 0.13 / 0.65 = 0.2.
        if (this._hasPinnedFontSize(shape)) pad = fontSizeByHeight * 0.2;

        // No width cap and no auto-wrap (head dev's call): a longer label
        // gets a LONGER box at the size the user chose, never a smaller
        // font and never folded onto lines nobody asked for. Multi-line
        // exists, but only where the author put a newline (Shift+Enter in
        // the editor). The old cap/wrap pair existed to break a
        // box->font->box feedback loop; with the box always derived from
        // the text (below), the loop it guarded against is gone.
        var fontSize = fontSizeByHeight;

        textEl.setAttribute("x", tx + pad);
        textEl.setAttribute("y", ty + pad);
        textEl.setAttribute("font-size", fontSize);
        textEl.setAttribute("font-family", fontFamily);
        textEl.setAttribute("font-weight", fontWeight);
        // Cross-browser baseline fix — see callout render block.
        // `hanging` is interpreted differently by Safari and Firefox
        // for Latin text (Safari renders text above the hanging
        // baseline, Firefox renders below per spec). Switching to
        // `alphabetic` (the SVG default) keeps the title text in the
        // same position in both browsers, with the wrap helper's
        // `dy="1em"` on the first tspan dropping the alphabetic
        // baseline one em below `textEl.y`.
        textEl.setAttribute("dominant-baseline", "alphabetic");
        // At the width-fit font-size the title fits in one line, so
        // the wrap helper produces a single tspan — no multi-line
        // growth path.
        var measured = this._fillTextWithWrappedTspans(
          textEl, trimmed, Infinity, fontSize
        );

        // The rect always hugs the text now (head dev's call: text drives
        // the box). The stored `title_box` still matters twice — its
        // position anchors an un-aligned label, and its HEIGHT is the knob
        // the corner handles turn to size an un-pinned font — but its
        // drawn extent is derived from the words, so a longer label is a
        // longer label and a resize reads as a font change, never as a
        // box for lines nobody asked for.
        // Alignment is meaningless on a shaft-riding label — its position
        // IS the offset along the line. _commitTextEdit used to stamp
        // center/middle on every fresh non-text label, arrow included, and
        // the aligned re-anchor below then snapped the label back to the
        // shape's bbox centre on every render: the drag stored a new
        // offset, the render threw it away, and the label read as
        // ungrabbable. Ignored here (not just no longer stamped) so labels
        // that already carry the stamp heal instead of staying stuck.
        var titleAlign = this._labelRidesShaft(shape.kind)
          ? null
          : normalizeTitleAlign(shape.metadata && shape.metadata.title_align);
        {
          // Shrink-wrap the rect to the rendered text dimensions so
          // handles + the underline sit right at the text edge instead
          // of leaving empty space inside the default bbox.
          var actualW = Math.max(measured.width + pad * 2, fontSize);
          var actualH = Math.max(measured.height + pad * 2, fontSize * 1.2);
          if (rectEl) {
            rectEl.setAttribute("width",  actualW);
            rectEl.setAttribute("height", actualH);
          }
          this._applyLabelBg(rectEl, shape);
          // Convert the container-px shrink back to image px so handles
          // + drag math operate on the visible rect. Falls back to the
          // input bbox if the scale degenerates.
          var sx = tw > 0 ? tw / titleBox.w : 1;
          var sy = th > 0 ? th / titleBox.h : sx;
          var shrunk = {
            x: titleBox.x,
            y: titleBox.y,
            w: sx > 0 ? actualW / sx : titleBox.w,
            h: sy > 0 ? actualH / sy : titleBox.h
          };

          // Re-anchor now that the label's real size is known. The pass in
          // `_shapeTitleBoxImage` could only guess at the size, so an
          // aligned label would otherwise sit a guess-width off its anchor.
          if (titleAlign) {
            var parentBox = this._shapeBBoxImagePx(shape);
            if (parentBox) {
              shrunk = alignedBox(parentBox, { w: shrunk.w, h: shrunk.h }, titleAlign);
              var atl = this._imageToContainer({ x: shrunk.x, y: shrunk.y });
              var abr = this._imageToContainer({
                x: shrunk.x + shrunk.w, y: shrunk.y + shrunk.h
              });
              tx = Math.min(atl.x, abr.x);
              ty = Math.min(atl.y, abr.y);
              if (rectEl) {
                rectEl.setAttribute("x", tx);
                rectEl.setAttribute("y", ty);
              }
              textEl.setAttribute("x", tx + pad);
              textEl.setAttribute("y", ty + pad);
              this._shiftTspans(textEl, tx + pad);
            }
          }

          // Same re-anchor for the shaft-riding labels (dimension, arrow),
          // which are CENTRED on their line rather than anchored by a
          // corner. Shrink-wrapping narrows the box from its left edge, so
          // without this the drawn label sits half the shrink off the line
          // — which reads as the label having come unstuck from it.
          if (this._labelRidesShaft(shape.kind)) {
            var cShiftX = (tw - actualW) / 2;
            var cShiftY = (th - actualH) / 2;
            if (cShiftX || cShiftY) {
              tx += cShiftX;
              ty += cShiftY;
              if (sx > 0) shrunk.x += cShiftX / sx;
              if (sy > 0) shrunk.y += cShiftY / sy;
              if (rectEl) {
                rectEl.setAttribute("x", tx);
                rectEl.setAttribute("y", ty);
              }
              textEl.setAttribute("x", tx + pad);
              textEl.setAttribute("y", ty + pad);
              this._shiftTspans(textEl, tx + pad);
            }
          }

          shape._renderedTitleImage = shrunk;
          tw = actualW;
          th = actualH;

          // Centre the INK, not the em box. Baseline placement leaves a
          // line of digits (no descenders) sitting low: measured against
          // the painted extents, the glyphs had ~an x-height of slack
          // above and overhung the rect's bottom edge — visibly so on a
          // shaft label, where the line then grazed the digit bottoms
          // while empty margin sat above, worst zoomed out where the
          // break's floor is tightest. Measure what was actually painted
          // and shift the text so its visible centre IS the rect's
          // centre; the rect, the plate, the cut and the handles all key
          // off the rect and stay put.
          // Centre the PAINTED INK in the box, both axes, measured from
          // the text the engine just laid out. Sizing the box from the
          // advance width is right — that is what reserves the space —
          // but the glyphs do not fill it symmetrically: bearings differ
          // left to right, SVG places text by its baseline, and at small
          // sizes the painted extents round to device pixels. Those
          // leftovers are a fixed fraction of a pixel, so they vanish on
          // a big label and dominate a small one: at the viewer's zoom
          // floor a font-3 label had 0.63px of space on its left and
          // 0.33 on its right, which reads as the words sitting right of
          // centre. Measuring the real box and moving the pen makes both
          // gaps equal at every size. One translation, so it converges
          // in a single pass; a failed or degenerate measurement leaves
          // the text exactly where it was.
          try {
            var inkBox = textEl.getBBox();
            if (inkBox && inkBox.width > 0 && inkBox.height > 0) {
              var inkDx = (tx + tw / 2) - (inkBox.x + inkBox.width / 2);
              var inkDy = (ty + th / 2) - (inkBox.y + inkBox.height / 2);
              if (inkDx) {
                var inkNx = (parseFloat(textEl.getAttribute("x")) || 0) + inkDx;
                textEl.setAttribute("x", inkNx);
                this._shiftTspans(textEl, inkNx);
              }
              if (inkDy) {
                textEl.setAttribute(
                  "y", (parseFloat(textEl.getAttribute("y")) || 0) + inkDy
                );
              }
            }
          } catch (_) {}
        }
      } else {
        shape._renderedTitleImage = null;
      }
      if (lineEl) {
        // If the title sits inside the parent shape, the leader is
        // redundant — the title visibly IS part of the shape. Hide it.
        // Otherwise draw from the title-bbox bottom-center to the
        // closest point on the parent's perimeter for a clean link.
        var titleCenterImage = {
          x: titleBox.x + titleBox.w / 2,
          y: titleBox.y + titleBox.h / 2
        };
        // A shaft-riding label is centred ON its line, so a leader would be
        // a stub pointing from the label at the line it is already sitting
        // on. Same reasoning as the containment case below, but containment
        // can't see it: a line has no interior to be inside of.
        if (this._labelRidesShaft(shape.kind)) {
          lineEl.setAttribute("visibility", "hidden");
        } else if (this._shapeContainsImagePoint(shape, titleCenterImage)) {
          lineEl.setAttribute("visibility", "hidden");
        } else {
          lineEl.removeAttribute("visibility");
          var anchors = this._titleLeaderAnchors(shape, tx, ty, tw, th);
          lineEl.setAttribute("x1", anchors.title.x);
          lineEl.setAttribute("y1", anchors.title.y);
          lineEl.setAttribute("x2", anchors.parent.x);
          lineEl.setAttribute("y2", anchors.parent.y);
        }
      }
      // Last, once the rect holds its final box (shrink-wrapped, centred
      // back on the line): break the shaft around the label. Rendered
      // with no text, the branch above has already closed the line up.
      if (this._labelRidesShaft(shape.kind)) {
        this._syncShaftLabelGap(shape, rectEl);
      }
    },

    // A shape's label as point text (`style.title_point`): sized by its
    // font and stretch, placed where the label would be — its dragged
    // corner, its alignment, its spot on the line — and turned with the
    // board, as labels are. The leader and the shaft's break follow it.
    _renderPointLabel: function(shape, bboxTopImage) {
      var tg = shape.titleGroup;
      tg.classList.add("is-point");
      var rectEl = tg.querySelector(".etcher-text-rect");
      var textEl = tg.querySelector(".etcher-text-content");
      var lineEl = tg.querySelector(".etcher-title-leader");
      var scale = this._markerScale() || 1;
      var str = this._pointStretch(shape, "label");
      var M = this._pointMeasure(shape);
      var titleBox = this._shapeTitleBoxImage(shape, bboxTopImage,
        { w: M.natW * str.x / scale, h: M.natH * str.y / scale });
      // The label turns with its shape. One aligned inside a turned box
      // also travels round with it, keeping its place in the shape; a
      // label placed freely turns where it stands.
      var lRot = this._labelRot(shape);
      if (lRot && this._isBoxRotatable(shape) &&
          normalizeTitleAlign(shape.metadata && shape.metadata.title_align)) {
        var sc = this._rotCenter(shape);
        var lc = { x: titleBox.x + titleBox.w / 2, y: titleBox.y + titleBox.h / 2 };
        var nc = this._rotatePoint(lc, sc.x, sc.y, lRot);
        titleBox = { x: titleBox.x + nc.x - lc.x, y: titleBox.y + nc.y - lc.y, w: titleBox.w, h: titleBox.h };
      }
      var tbox = this._orientedBox(titleBox);
      tbox.deg = (tbox.deg || 0) + lRot;
      this._setRotateTransform(rectEl, tbox);
      var turn = rectEl ? rectEl.getAttribute("transform") : null;
      this._pointDraw(shape, M, tbox.x, tbox.y, str, rectEl, textEl, turn);
      shape._renderedTitleImage = { x: titleBox.x, y: titleBox.y, w: titleBox.w, h: titleBox.h };
      var tw = M.natW * str.x, th = M.natH * str.y;
      if (lineEl) {
        var center = { x: titleBox.x + titleBox.w / 2, y: titleBox.y + titleBox.h / 2 };
        if (this._labelRidesShaft(shape.kind) || this._shapeContainsImagePoint(shape, center)) {
          lineEl.setAttribute("visibility", "hidden");
        } else {
          lineEl.removeAttribute("visibility");
          var anchors = this._titleLeaderAnchors(shape, tbox.x, tbox.y, tw, th);
          lineEl.setAttribute("x1", anchors.title.x);
          lineEl.setAttribute("y1", anchors.title.y);
          lineEl.setAttribute("x2", anchors.parent.x);
          lineEl.setAttribute("y2", anchors.parent.y);
        }
      }
      if (this._labelRidesShaft(shape.kind)) this._syncShaftLabelGap(shape, rectEl);
      this._fitPointEditor(shape);
    },

    // Image-px point-in-shape test for rect / circle / polygon.
    // Freehand falls through to the polygon test since its geometry
    // is also a points-array. Used by _renderTitleSibling to decide
    // whether the leader line adds value.
    _shapeContainsImagePoint: function(shape, pt) {
      var g = shape.geometry;
      switch (shape.kind) {
        case "image":
        case "rectangle":
        case "audio":
        case "video":
          return (
            pt.x >= g.x && pt.x <= g.x + g.w &&
            pt.y >= g.y && pt.y <= g.y + g.h
          );
        case "circle": {
          var dx = pt.x - g.cx, dy = pt.y - g.cy;
          return dx * dx + dy * dy <= g.r * g.r;
        }
        // A marker is an open stroke, not an enclosed region — hit it by
        // proximity to the line (within half its thickness + a grab pad).
        case "marker":
          return this._strokeNearPoint(shape, pt);
        case "polygon":
        case "freehand": {
          // Freehand is an open stroke first: hit by proximity to the
          // curve, like a marker. The interior test below alone misses a
          // plain drawn line — its closed footprint is near-zero area —
          // which made freehand strokes unselectable and unmovable.
          if (shape.kind === "freehand" && this._strokeNearPoint(shape, pt)) {
            return true;
          }
          // Ray-casting: count edge crossings to the right of `pt`.
          // Freehand curves are flattened to a polyline first so the test
          // follows the rendered bezier outline, not the sparse anchors.
          // Kept for freehand too so a closed loop still hits inside.
          var pts = shape.kind === "freehand" ? this._freehandFlatten(g) : (g.points || []);
          if (pts.length < 3) return false;
          var inside = false;
          for (var i = 0, j = pts.length - 1; i < pts.length; j = i++) {
            var xi = pts[i][0], yi = pts[i][1];
            var xj = pts[j][0], yj = pts[j][1];
            var intersect =
              ((yi > pt.y) !== (yj > pt.y)) &&
              (pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi || 1e-9) + xi);
            if (intersect) inside = !inside;
          }
          return inside;
        }
        default:
          return false;
      }
    },

    // Resolve the title's image-px bbox, in precedence order:
    //   1. `metadata.title_align` — anchored inside the parent's bbox.
    //   2. `metadata.title_box`   — wherever the user dragged it.
    //   3. default                 — above the parent's top-center.
    //
    // Alignment wins over the stored box because the box is a leftover from
    // whatever the label's position was before, and an aligned label has to
    // follow the shape as it moves and resizes. The size here is only a
    // first approximation for an aligned label — it auto-sizes to its text,
    // and `_renderTitleSibling` re-anchors it once the text is measured.
    // What colour a shape's label is drawn in.
    //
    // A label carries its own colour when it has been given one, so it can be
    // read against whatever it sits on top of without dragging the shape's
    // colour along with it. Failing that it follows the shape, which is how
    // labels have always behaved — except on a dimension, where the label is
    // a measurement written on the drawing rather than part of the line, and
    // black is what it has always been.
    // A label defaults to its shape's own colour — a red arrow gets a red
    // label (head dev's call; the dimension's old black default read as an
    // annotation disconnected from the line it measures, and it was the one
    // kind that did not inherit). An explicit `title_color` always wins, so
    // any label can still be given a colour of its own.
    _titleColorFor: function(shape) {
      var meta = (shape && shape.metadata) || {};
      if (meta.title_color) return meta.title_color;
      return (shape && shape.style && shape.style.color) || "";
    },

    _shapeTitleBoxImage: function(shape, bboxTopImage, sizeOverride) {
      var meta = (shape && shape.metadata) || {};
      var stored = meta.title_box;
      // A title the user has never sized gets a default that is comfortable
      // ON SCREEN — but only once. `_textDefaultBoxImagePx` answers in image
      // px for the CURRENT zoom, so recomputing it every render (which is
      // what used to happen) pinned the title to a constant screen size: it
      // held still while the shape it belongs to shrank away beneath it,
      // ending up the largest thing on a zoomed-out board.
      //
      // Anchoring it at first paint is the same bargain the stroke widths
      // make: the title looks exactly as it did, and scales from there. Kept
      // on the shape rather than written into `metadata` because this is a
      // default, not a user decision — dragging a title still persists a real
      // `title_box`, and that path is untouched.
      var basePx;
      if (stored) {
        basePx = this._textDefaultBoxInkPx();
      } else {
        if (!(shape._titleBasePx > 0)) {
          shape._titleBasePx = this._textDefaultBoxInkPx();
        }
        basePx = shape._titleBasePx;
      }
      var size = stored
        ? { w: stored.w, h: stored.h }
        : { w: basePx * 6, h: basePx * 1.4 };
      // A point label knows its size from its words; the rest of this only
      // has to say where a box that size goes.
      if (sizeOverride) {
        size = { w: sizeOverride.w, h: sizeOverride.h };
        if (stored && !normalizeTitleAlign(meta.title_align)) {
          stored = { x: stored.x, y: stored.y, w: size.w, h: size.h };
        }
      }

      // A shaft-riding label (dimension, arrow) rides its own line. The
      // stored box supplies the SIZE — that is what makes it resizable like
      // any other label — but never the position: that comes from
      // `title_offset` along the shaft, which is what keeps it magnetic to
      // the line instead of drifting off it. Checked before `title_align`
      // and before the stored box, both of which would otherwise put it
      // somewhere the line isn't.
      if (shape && this._labelRidesShaft(shape.kind)) {
        var dg = shape.geometry;
        if (dg && dg.a && dg.b) {
          var dt = 0.5;
          if (typeof meta.title_offset === "number") {
            dt = Math.max(0, Math.min(1, meta.title_offset));
          }
          // Along the ROUTED shaft, bends included — the a->b chord stays
          // put when the middle is dragged into a bend, which left the
          // label hovering over where the line used to be.
          var dp = this._shaftPointAt(dg, dt);
          if (dp) {
            return {
              x: dp.x - size.w / 2, y: dp.y - size.h / 2, w: size.w, h: size.h
            };
          }
        }
      }

      var align = normalizeTitleAlign(meta.title_align);
      if (align) {
        var bbox = this._shapeBBoxImagePx(shape);
        if (bbox) return alignedBox(bbox, size, align);
      }
      if (stored) return stored;
      if (!bboxTopImage) return null;
      return {
        x: bboxTopImage.x - size.w / 2,
        y: bboxTopImage.y - size.h - basePx,
        w: size.w,
        h: size.h
      };
    },

    // Both ends of a label's leader line, in container px, each anchored
    // at the point FACING the other end. The label end used to be
    // hardcoded to the box's bottom-center — right for the float-above
    // default, and exactly wrong for a label dragged below its shape,
    // where the leader left from the label's far side and crossed the
    // text on its way to the parent. Clamping the parent's nearest point
    // into the label rect lands on the facing edge or corner wherever the
    // label sits; directly above, that degenerates to the old
    // bottom-center, so the default layout is unchanged.
    _titleLeaderAnchors: function(shape, tx, ty, tw, th) {
      var center = { x: tx + tw / 2, y: ty + th / 2 };
      var parentAnchor = this._shapeNearestPoint(shape, center);
      return {
        title: {
          x: Math.max(tx, Math.min(tx + tw, parentAnchor.x)),
          y: Math.max(ty, Math.min(ty + th, parentAnchor.y))
        },
        parent: parentAnchor
      };
    },

    // Return the closest point on a parent shape's perimeter (in
    // container px) to a given container-px point. Used to terminate
    // the title's leader line. Cheap approximations per kind — good
    // enough to read as "the line points at the shape".
    _shapeNearestPoint: function(shape, pt) {
      var self = this;
      var g = shape.geometry;
      switch (shape.kind) {
        case "image":
        case "rectangle":
        case "audio":
        case "video": {
          var tl = self._imageToContainer({ x: g.x,         y: g.y });
          var br = self._imageToContainer({ x: g.x + g.w,   y: g.y + g.h });
          var rx1 = Math.min(tl.x, br.x), ry1 = Math.min(tl.y, br.y);
          var rx2 = Math.max(tl.x, br.x), ry2 = Math.max(tl.y, br.y);
          var cx = Math.max(rx1, Math.min(pt.x, rx2));
          var cy = Math.max(ry1, Math.min(pt.y, ry2));
          // Snap onto the nearest edge so the leader doesn't end
          // inside the bbox when the title overlaps the parent.
          var dLeft = Math.abs(cx - rx1), dRight = Math.abs(cx - rx2);
          var dTop = Math.abs(cy - ry1), dBot = Math.abs(cy - ry2);
          var minD = Math.min(dLeft, dRight, dTop, dBot);
          if (minD === dLeft)  cx = rx1;
          else if (minD === dRight) cx = rx2;
          else if (minD === dTop)   cy = ry1;
          else                       cy = ry2;
          return { x: cx, y: cy };
        }
        case "circle": {
          var c  = self._imageToContainer({ x: g.cx, y: g.cy });
          var rp = self._imageToContainer({ x: g.cx + g.r, y: g.cy });
          // Full projected distance, not the x-component — see the circle
          // render case. `rp.x - c.x` collapsed to ~0 at 90°/270°, which
          // pinned this edge anchor to the center on a rotated canvas.
          var r  = Math.hypot(rp.x - c.x, rp.y - c.y);
          var dx = pt.x - c.x, dy = pt.y - c.y;
          var d  = Math.sqrt(dx * dx + dy * dy) || 1;
          return { x: c.x + (dx / d) * r, y: c.y + (dy / d) * r };
        }
        case "marker":
        case "polygon":
        case "freehand": {
          var isStroke = shape.kind === "freehand" || shape.kind === "marker";
          var src = isStroke ? self._freehandFlatten(g) : (g.points || []);
          var pts = src.map(function(p) {
            return self._imageToContainer({ x: p[0], y: p[1] });
          });
          var best = null;
          var bestDist = Infinity;
          for (var i = 0; i < pts.length; i++) {
            var d2 = (pts[i].x - pt.x) * (pts[i].x - pt.x) +
                     (pts[i].y - pt.y) * (pts[i].y - pt.y);
            if (d2 < bestDist) { bestDist = d2; best = pts[i]; }
          }
          return best || pt;
        }
        default:
          return pt;
      }
    },

    // Hover + double-click + drag handlers for a shape's title group.
    // Once wired, the title behaves like a satellite text shape that
    // moves independently of the parent but follows it on translate.
    _attachTitleInteractions: function(shape) {
      var self = this;
      var tg = shape.titleGroup;
      if (!tg || tg._etcherWired) return;
      tg._etcherWired = true;

      // Every guard below admits the cursor AND the shape's own tool —
      // same-tool editing: a dimension's label is double-clickable with
      // the dimension tool still in hand (see _armedToolEdits).
      tg.addEventListener("mouseenter", function() {
        if (self.annotationMode && self.activeTool != null &&
            !self._armedToolEdits(shape)) {
          return;
        }
        if (self._armedFocusBars(shape)) return;
        tg.classList.add("is-hovered");
      });
      tg.addEventListener("mouseleave", function() {
        tg.classList.remove("is-hovered");
      });
      tg.addEventListener("dblclick", function(e) {
        if (self.annotationMode && self.activeTool != null &&
            !self._armedToolEdits(shape)) {
          return;
        }
        if (!self.annotationMode) return;
        e.stopPropagation();
        e.preventDefault();
        self._startTextEdit(shape);
      });
      tg.addEventListener("click", function(e) {
        // Click the title in annotation mode + cursor tool → enter
        // title-edit-mode, which shows 4 corner handles for resizing
        // the title bbox. Drag the title body (separate pointerdown
        // listener below) still moves the whole bbox.
        if (self.annotationMode && self.activeTool != null &&
            !self._armedToolEdits(shape)) {
          return;
        }
        if (!self.annotationMode) return;
        if (self._armedFocusBars(shape)) return;
        e.stopPropagation();
        e.preventDefault();
        self._enterTitleEditMode(shape);
      });
      tg.addEventListener("pointerdown", function(e) {
        if (e.button !== 0) return;
        if (self.annotationMode && self.activeTool != null &&
            !self._armedToolEdits(shape)) {
          return;
        }
        if (!self.annotationMode) return;
        // Another shape holds the focus: the press lets go of it (the
        // canvas handlers do that), and the label stays where it is.
        if (self._armedFocusBars(shape)) return;
        // With the shape's own tool armed the drawing wrapper is live
        // and would read this press as "draw here" — the label owns it.
        if (self.activeTool != null) e.stopPropagation();
        self._startTitleDrag(shape, e);
      });
    },

    // -------------------------------------------------------------------------
    // Title edit mode — parallel to `editingShape` but operates on the
    // title bbox (`metadata.title_box`). Shows 4 corner handles that
    // resize the bbox; the font scales automatically with the new
    // height since `_renderTitleSibling` derives font-size from the
    // bbox dimensions.
    // -------------------------------------------------------------------------

    _enterTitleEditMode: function(shape) {
      if (!shape || !shape.titleGroup) return;
      if (this.editingTitleShape === shape) return;
      this._exitEditMode();
      this._exitTitleEditMode();

      // An older label becomes point text as it is picked up, looking the
      // same (see `_ensurePointLabel`).
      this._ensurePointLabel(shape);
      if (!shape.titleGroup) return;
      this.editingTitleShape = shape;
      shape.titleGroup.classList.add("is-editing");
      this._hideTooltip();
      this._renderTitleHandles(shape);

      var self = this;
      this._titleOutsideClickHandler = function(e) {
        // Clicks on a shape keep title-edit alive (the user might be
        // about to switch focus to that shape) — fall through to the
        // image-px hit-test below. Clicks on Etcher's internals or
        // any registered input-owner (modals, dialogs, ARIA dialogs)
        // also keep edit alive: the click belongs to that UI.
        if (e.target.closest(".etcher-shape")) return;
        // The label's own handles, its editor and its outline grip: a drag
        // on one ends with a click on it, and that is working on the label,
        // not leaving it.
        if (e.target.closest(".etcher-handle, .etcher-grab, .etcher-text-grip, .etcher-text-editor")) return;
        // Etcher's own chrome — the toolbar and the style panel. This is
        // what the comment above always claimed, and what the code never
        // did: only `isInputOwner` was checked, so picking a colour with a
        // label focused tore title-edit down in the capture phase and the
        // colour then had nothing left to apply to. Reaching for the palette
        // is the most obvious thing to do with a label selected.
        if (e.target.closest && e.target.closest(CHROME_SELECTOR)) return;
        if (isInputOwner(e.target, self.overlayWrapper)) return;
        // Shapes are `pointer-events: none`; fall back to image-px
        // hit-test so a click on a sibling shape doesn't tear down
        // title-edit mode before its own handler can react.
        try {
          var pt = self._toImage(e);
          if (self._shapeAt(pt)) return;
        } catch (_) {}
        self._exitTitleEditMode();
      };
      document.addEventListener("click", this._titleOutsideClickHandler, true);

      // Focusing a label is a selection change as far as the panel is
      // concerned — it is now the thing the size, the plate and the two
      // colour swatches are describing. Entering and leaving edit mode on a
      // SHAPE both sync; the label path did neither, so the panel went on
      // showing whatever was there before, and only caught up when clicking
      // away happened to trigger a sync elsewhere. Which meant you could
      // only read a label's settings after you had stopped editing it.
      this._syncActionBar();
    },

    _exitTitleEditMode: function() {
      if (!this.editingTitleShape) return;
      if (this.editingTitleShape.titleGroup) {
        this.editingTitleShape.titleGroup.classList.remove("is-editing");
      }
      this._removeTitleHandles();
      this.editingTitleShape = null;
      if (!this.editingShape) this._unwireMidpointTracker();
      if (this._titleOutsideClickHandler) {
        document.removeEventListener("click", this._titleOutsideClickHandler, true);
        this._titleOutsideClickHandler = null;
      }
      // …and stops being it. Without this the panel keeps describing a
      // label nobody is on any more.
      this._syncActionBar();
    },

    _renderTitleHandles: function(shape) {
      this._removeTitleHandles();
      // A point label always has its handles, as point text does: black
      // corner dots that scale it, side grabbers that come up as the
      // cursor nears their side (Shift stretches with either).
      var point = this._isPointLabel(shape);
      // The dots are opt-in (see _titleHandlesOn). Title-edit mode itself
      // is unchanged — the label still focuses, drags and takes the panel
      // controls — only the freehand resize corners stay away.
      if (!point && !this._titleHandlesOn()) return;
      var box = point ? this._pointHandleBox(shape, "label") :
        shape._renderedTitleImage ||
        this._shapeTitleBoxImage(shape, this._lastBboxTopImageFor(shape));
      if (!box) return;
      var self = this;
      var positions = this._labelRotPts(shape, [
        { x: box.x,           y: box.y           },  // 0: TL
        { x: box.x + box.w,   y: box.y           },  // 1: TR
        { x: box.x + box.w,   y: box.y + box.h   },  // 2: BR
        { x: box.x,           y: box.y + box.h   }   // 3: BL
      ]);
      var handleColor = point ? POINT_HANDLE_COLOR : self._handleColor(shape);
      this.titleHandles = positions.map(function(pt, idx) {
        var h = svgEl("circle", { r: 5 });
        h.classList.add("etcher-handle", "etcher-title-handle");
        h.style.color = handleColor;
        h.dataset.index = idx;
        self.svg.appendChild(h);
        var onTitleDown = function(e) {
          self._startTitleHandleDrag(shape, idx, h, e);
        };
        h.addEventListener("pointerdown", onTitleDown);
        self._addGrabHalo(h, HANDLE_GRAB_RADIUS, onTitleDown);
        self._positionHandle(h, pt);
        return h;
      });
      if (point) {
        this._renderRectEdgeHandles(shape, "label");
        this._wireMidpointTracker();
      }
    },

    _removeTitleHandles: function() {
      var self = this;
      (this.titleHandles || []).forEach(function(h) { self._dropHandleEl(h); });
      this.titleHandles = [];
      // A point label's side grabbers share the shape's midpoint set, which
      // nothing else holds while a label is the thing being worked on.
      if (this.editingTitleShape && !this.editingShape) this._removeMidpointHandles();
    },

    _positionAllTitleHandles: function(shape) {
      if (!this.titleHandles || !this.titleHandles.length) return;
      var point = this._isPointLabel(shape);
      if (point && this.midpointHandles && this.midpointHandles.length) {
        var mids = this._labelRotPts(shape, this._rectEdgeMidpoints(this._pointHandleBox(shape, "label")));
        var selfP = this;
        this.midpointHandles.forEach(function(h, i) {
          if (mids[i]) selfP._positionHandle(h, mids[i]);
        });
        this._syncPointEdgeRoom(shape);
        this._turnEdgeHandles(shape, this.midpointHandles, this._labelRot(shape));
      }
      var box = point ? this._pointHandleBox(shape, "label") :
        shape._renderedTitleImage ||
        this._shapeTitleBoxImage(shape, this._lastBboxTopImageFor(shape));
      if (!box) return;
      var positions = this._labelRotPts(shape, [
        { x: box.x,           y: box.y           },
        { x: box.x + box.w,   y: box.y           },
        { x: box.x + box.w,   y: box.y + box.h   },
        { x: box.x,           y: box.y + box.h   }
      ]);
      var self = this;
      this.titleHandles.forEach(function(h, idx) {
        if (positions[idx]) self._positionHandle(h, positions[idx]);
      });
      this._syncGrabRadii(this.titleHandles, point ? this.midpointHandles : null);
    },

    _startTitleHandleDrag: function(shape, idx, handleEl, e) {
      // Left button only. These handles sit above the canvas and swallow
      // the event, so without this a middle-press on one would start a
      // drag AND block the middle-drag pan from ever reaching Fresco.
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      try { handleEl.setPointerCapture(e.pointerId); } catch (_) {}
      handleEl.classList.add("is-dragging");
      this._hideTooltip();

      var self = this;
      // Pre-resize snapshot for the undo stack.
      var historyBefore = self._snapshotShape(shape);
      // Snapshot the rendered (shrink-wrapped) bbox so the drag math
      // starts from what the user actually sees, not the (possibly
      // larger) stored title_box that hasn't yet had a chance to
      // re-fit to the text.
      var startBox = JSON.parse(JSON.stringify(
        shape._renderedTitleImage ||
        this._shapeTitleBoxImage(shape, this._lastBboxTopImageFor(shape))
      ));
      var dragged = false;
      // A point label scales from its corners as point text does (Shift:
      // each way on its own), the opposite corner holding.
      var pointStart = this._isPointLabel(shape) ? this._pointTextDragStart(shape, "label") : null;
      if (pointStart) startBox = pointStart.box;
      // A turned label resizes in its own frame, the held corner fixed.
      var lblRot = pointStart ? this._labelRot(shape) : 0;
      var lblC0 = lblRot ? { x: startBox.x + startBox.w / 2, y: startBox.y + startBox.h / 2 } : null;

      function onMove(ev) {
        var pt = self._toImage(ev);
        if (!dragged) {
          // 3px screen-space dead zone — distinguishes "drag to
          // resize" from "I'm just clicking on a handle". Without
          // this, a bare click on a handle would fall through to
          // onUp and fire `etcher:updated`, round-tripping through
          // the LiveView and writing back possibly-normalized
          // title_box values that visibly drift the title's size
          // each click. Same gating the body-drag and title-drag
          // handlers already use.
          var aC = self._imageToContainer(startBox);
          var bC = self._imageToContainer(pt);
          if ((bC.x - aC.x) * (bC.x - aC.x) + (bC.y - aC.y) * (bC.y - aC.y) < 9) return;
          dragged = true;
        }
        if (pointStart) {
          if (lblRot) pt = self._rotatePoint(pt, lblC0.x, lblC0.y, -lblRot);
          self._resizePointText(shape, pointStart, idx, pt, !!ev.shiftKey);
          if (lblRot) self._recenterLabel(shape, lblC0, lblRot);
          self._renderShape(shape);
          self._positionAllTitleHandles(shape);
          self._syncPointSize();
          return;
        }
        var right = startBox.x + startBox.w;
        var bottom = startBox.y + startBox.h;
        var nx, ny, nw, nh;
        switch (idx) {
          case 0: nx = pt.x;        ny = pt.y;        nw = right - pt.x;        nh = bottom - pt.y;        break;
          case 1: nx = startBox.x;  ny = pt.y;        nw = pt.x - startBox.x;   nh = bottom - pt.y;        break;
          case 2: nx = startBox.x;  ny = startBox.y;  nw = pt.x - startBox.x;   nh = pt.y - startBox.y;    break;
          case 3: nx = pt.x;        ny = startBox.y;  nw = right - pt.x;        nh = pt.y - startBox.y;    break;
          default: return;
        }
        if (nw < 0) { nx += nw; nw = -nw; }
        if (nh < 0) { ny += nh; nh = -nh; }
        // Resizing sets an explicit size, which an aligned label ignores in
        // favour of auto-sizing — so like dragging, it returns the label to
        // free positioning rather than silently doing nothing.
        shape.metadata = Object.assign({}, shape.metadata || {}, {
          title_box: { x: nx, y: ny, w: nw, h: nh }, title_align: null
        });
        // Dragging the box IS setting the size, so it takes over from a
        // pinned one — otherwise the box would resize around text that
        // refused to change, which reads as a broken drag.
        self._unpinFontSize(shape);
        self._renderShape(shape);
        self._positionAllTitleHandles(shape);
      }
      function onUp(ev) {
        handleEl.classList.remove("is-dragging");
        handleEl.removeEventListener("pointermove", onMove);
        handleEl.removeEventListener("pointerup", onUp);
        handleEl.removeEventListener("pointercancel", onUp);
        try { handleEl.releasePointerCapture(ev.pointerId); } catch (_) {}
        if (!dragged) return;
        // Snap stored title_box to the rendered bbox so the
        // persisted dimensions match what's on screen. Now a no-op
        // since `_renderTitleSibling` keeps the rect at user-set
        // dims and `_renderedTitleImage` mirrors title_box, but
        // kept as a safety net in case future render changes
        // reintroduce a divergence.
        if (shape._renderedTitleImage) {
          shape.metadata = Object.assign({}, shape.metadata || {}, {
            title_box: {
              x: shape._renderedTitleImage.x,
              y: shape._renderedTitleImage.y,
              w: shape._renderedTitleImage.w,
              h: shape._renderedTitleImage.h
            }
          });
        }
        if (shape.uuid) {
          self._emitChanged();
          self._pushUndo(shape.uuid, historyBefore, self._snapshotShape(shape));
        }
        // Resizing dropped the alignment too — resync the highlight.
        self._syncLabelSection();
      }
      handleEl.addEventListener("pointermove", onMove);
      handleEl.addEventListener("pointerup", onUp);
      handleEl.addEventListener("pointercancel", onUp);
    },

    // Drag the title group to a new position. Translates the
    // image-px `metadata.title_box` by the pointer delta and persists
    // on release via `etcher:updated { metadata }`.
    _startTitleDrag: function(shape, e) {
      e.preventDefault();
      e.stopPropagation();
      var self = this;
      var tg = shape.titleGroup;
      if (!tg) return;

      try { tg.setPointerCapture(e.pointerId); } catch (_) {}
      tg.classList.add("is-dragging");

      var startPt = this._toImage(e);
      // Pre-drag snapshot for the undo stack.
      var historyBefore = self._snapshotShape(shape);
      // Snapshot the starting bbox; prefer the rendered shrink-fit
      // box so the drag matches the visible rect.
      var bboxTopImage = this._lastBboxTopImageFor(shape);
      var startBox =
        shape._renderedTitleImage ||
        this._shapeTitleBoxImage(shape, bboxTopImage);
      if (!startBox) return;
      var dragged = false;

      function onMove(ev) {
        var pt = self._toImage(ev);
        var dxI = pt.x - startPt.x;
        var dyI = pt.y - startPt.y;
        if (!dragged) {
          // Pixel-space dead-zone so a stationary click doesn't
          // commit a no-op title_box update.
          var aC = self._imageToContainer(startPt);
          var bC = self._imageToContainer(pt);
          if ((bC.x - aC.x) * (bC.x - aC.x) + (bC.y - aC.y) * (bC.y - aC.y) < 9) return;
          dragged = true;
        }
        // A shaft-riding label slides ALONG its line rather than moving
        // freely: the pointer is projected onto the shaft and the position
        // stored as a 0–1 offset. Free positioning would let the label drift
        // away from the thing it rides — and a dimension's label is the
        // measurement, an arrow's is the name of the pointing.
        if (self._labelRidesShaft(shape.kind)) {
          // Projected onto the routed shaft — segment by segment, bends
          // included — so the label follows a bent arrow instead of the
          // invisible chord between its endpoints. A zero-length shaft
          // returns null; the offset is left alone rather than guessed.
          var tProj = self._shaftOffsetFor(shape.geometry, pt);
          if (tProj != null) {
            shape.metadata = Object.assign({}, shape.metadata || {}, {
              title_offset: tProj
            });
            self._renderShape(shape);
            if (self.editingTitleShape === shape) {
              self._positionAllTitleHandles(shape);
            }
          }
          return;
        }
        var newBox = {
          x: startBox.x + dxI,
          y: startBox.y + dyI,
          w: startBox.w,
          h: startBox.h
        };
        // Dragging is the free-positioning gesture, so it drops any
        // alignment — otherwise the label would snap straight back to its
        // anchor and the drag would look broken.
        shape.metadata = Object.assign({}, shape.metadata || {}, {
          title_box: newBox, title_align: null
        });
        self._renderShape(shape);
        // Title handles (if in title-edit-mode) sit on the bbox
        // corners, so they need to track the moving bbox in lockstep.
        if (self.editingTitleShape === shape) {
          self._positionAllTitleHandles(shape);
        }
      }
      function onUp(ev) {
        tg.classList.remove("is-dragging");
        try { tg.releasePointerCapture(ev.pointerId); } catch (_) {}
        tg.removeEventListener("pointermove", onMove);
        tg.removeEventListener("pointerup", onUp);
        tg.removeEventListener("pointercancel", onUp);
        if (dragged) {
          // Snap to the shrunk-to-text bbox on release — same
          // rationale as the handle-drag path: keep storage aligned
          // with what's drawn. Safe because the width-fit font cap
          // prevents the multi-line growth this used to amplify.
          if (shape._renderedTitleImage) {
            shape.metadata = Object.assign({}, shape.metadata || {}, {
              title_box: {
                x: shape._renderedTitleImage.x,
                y: shape._renderedTitleImage.y,
                w: shape._renderedTitleImage.w,
                h: shape._renderedTitleImage.h
              }
            });
          }
          if (shape.uuid) {
            self._emitChanged();
            self._pushUndo(shape.uuid, historyBefore, self._snapshotShape(shape));
          }
          // The drag dropped the alignment, so the panel's highlight is now
          // wrong — and nothing else will notice, since the selection
          // didn't change.
          self._syncLabelSection();
        }
      }
      tg.addEventListener("pointermove", onMove);
      tg.addEventListener("pointerup", onUp);
      tg.addEventListener("pointercancel", onUp);
    },

    // The title's default bbox needs the parent's bbox-top-center in
    // IMAGE coords. _renderShape computes this every render; cache
    // the last value on the shape so drag handlers can grab it
    // without re-running the geometry math.
    _lastBboxTopImageFor: function(shape) {
      return shape && shape.bboxTopImage ? shape.bboxTopImage : null;
    },

    // Move an already-wrapped <text> horizontally. Each line is a <tspan>
    // carrying its own `x`, so setting `x` on the parent alone moves
    // nothing — the tspans keep the position they were laid out at.
    _shiftTspans: function(textEl, x) {
      if (!textEl) return;
      var spans = textEl.querySelectorAll("tspan");
      for (var i = 0; i < spans.length; i++) spans[i].setAttribute("x", x);
    },

    // Fill a <text> node with word-wrapped <tspan> lines that fit a
    // pixel-width budget. Returns `{width, height}` of the rendered
    // text in container px so callers can shrink-wrap their bbox to
    // it. Uses a canvas 2D context for measurement instead of SVG's
    // `getComputedTextLength` — canvas runs synchronously and doesn't
    // depend on the SVG element having been laid out yet, so the
    // shrink path is reliable on first render.
    // How wide `text` renders at `fontSize`.
    //
    // Measured at a FIXED reference size and scaled, never at the size it
    // will actually be drawn at. Text metrics are not linear in font size —
    // hinting and rounding make small text proportionally wider, and browsers
    // clamp very small sizes outright — so measuring at the rendered size
    // makes the answer depend on the zoom.
    //
    // Everything that reads a text width decides something the zoom must not
    // change: where the line breaks, and how far the font is shrunk to fit
    // its box. A label that fits on one line zoomed in was breaking onto two
    // zoomed out, on the same board, from the same text.
    _measureTextWidth: function(text, fontSize, fontFamily, fontWeight) {
      var s = String(text == null ? "" : text);
      if (!s) return 0;
      if (!this._measureCanvas) {
        this._measureCanvas = document.createElement("canvas");
      }
      var ctx = this._measureCanvas.getContext("2d");
      ctx.font = (fontWeight || "500") + " " + TEXT_MEASURE_FONT_PX + "px " +
        (fontFamily || "ui-sans-serif, system-ui, -apple-system, sans-serif");
      var w;
      try { w = ctx.measureText(s).width; }
      catch (_) { w = s.length * TEXT_MEASURE_FONT_PX * 0.55; }
      return w * (fontSize / TEXT_MEASURE_FONT_PX);
    },

    // The font's ascent and descent, as fractions of the font size — where a
    // line box puts the baseline. From the canvas's font metrics where the
    // browser has them; otherwise the usual proportions of a system UI face.
    _fontLineMetrics: function(fontFamily, fontWeight) {
      var key = (fontWeight || "500") + "|" + (fontFamily || "");
      this._lineMetricsCache = this._lineMetricsCache || {};
      if (this._lineMetricsCache[key]) return this._lineMetricsCache[key];
      var m = { asc: 0.92, desc: 0.24 };
      try {
        if (!this._measureCanvas) this._measureCanvas = document.createElement("canvas");
        var ctx = this._measureCanvas.getContext("2d");
        ctx.font = (fontWeight || "500") + " 100px " +
          (fontFamily || "ui-sans-serif, system-ui, -apple-system, sans-serif");
        var tm = ctx.measureText("Hg");
        if (tm && tm.fontBoundingBoxAscent > 0 && tm.fontBoundingBoxDescent >= 0) {
          m = { asc: tm.fontBoundingBoxAscent / 100, desc: tm.fontBoundingBoxDescent / 100 };
        }
      } catch (_) {}
      this._lineMetricsCache[key] = m;
      return m;
    },

    _fillTextWithWrappedTspans: function(textEl, content, maxWidth, fontSize) {
      while (textEl.firstChild) textEl.removeChild(textEl.firstChild);
      if (!content) return { width: 0, height: 0 };

      var fontFamily = textEl.getAttribute("font-family") ||
        "ui-sans-serif, system-ui, -apple-system, sans-serif";
      var fontWeight = textEl.getAttribute("font-weight") || "500";

      var self = this;
      function measure(s) {
        return self._measureTextWidth(s, fontSize, fontFamily, fontWeight);
      }

      // A newline in the content is a HARD break — the author's own line,
      // put there with Shift+Enter, kept exactly where they put it.
      // `maxWidth` wraps only within a hard line; Infinity means "never
      // wrap", which is how titles render — a longer label gets longer,
      // not folded (see the title render for why).
      var lines = [];
      String(content).split("\n").forEach(function(hard) {
        var words = hard.split(/\s+/).filter(Boolean);
        if (!words.length) {
          // A deliberately blank line holds its height.
          lines.push("\u00A0");
          return;
        }
        var current = "";
        for (var i = 0; i < words.length; i++) {
          var attempt = current ? current + " " + words[i] : words[i];
          if (measure(attempt) <= maxWidth || !current) {
            current = attempt;
          } else {
            lines.push(current);
            current = words[i];
          }
        }
        if (current) lines.push(current);
      });
      // Trailing blank lines carry no intent worth drawing.
      while (lines.length && lines[lines.length - 1] === "\u00A0") lines.pop();
      if (!lines.length) return { width: 0, height: 0 };
      var maxLine = 0;

      for (var k = 0; k < lines.length; k++) {
        var w = measure(lines[k]);
        if (w > maxLine) maxLine = w;
      }

      var x = textEl.getAttribute("x");
      // Each line is a <tspan> with dy=1.1em after the first. Fill is
      // inherited from the parent <text> via the .etcher-text-content
      // CSS rule, which targets both the text and its tspans so the
      // visible color cascades reliably across browsers (some skip
      // tspan inheritance of presentation attributes).
      lines.forEach(function(line, idx) {
        var tspan = svgEl("tspan", { x: x, dy: idx === 0 ? "1em" : "1.1em" });
        tspan.textContent = line;
        textEl.appendChild(tspan);
      });

      var height = fontSize + Math.max(0, lines.length - 1) * fontSize * 1.1;
      return { width: maxLine, height: height };
    },

    // ── Red pointer ─────────────────────────────────────────────────────────
    //
    // Everyone's pointer lives in one map keyed by whoever it belongs to.
    // The local user's own is keyed "self" and is drawn exactly like a remote
    // one — a presenter has to see where they are pointing, and seeing the
    // same dot the room sees is the only way to be sure of that.
    //
    // Positions are held in IMAGE coordinates so a point at the corner of a
    // photo is that corner for everyone, whatever each viewer has zoomed or
    // panned to. They are converted to the screen at draw time.

    // The local user pointed somewhere: draw it here, and tell the room.
    //
    // Drawn locally straight away rather than waiting for the broadcast to
    // come back — a presenter aiming at something cannot wait a round trip to
    // see where they are aiming, and the round trip may never complete if the
    // host has not wired the transport up at all.
    _movePointer: function(e) {
      var pt;
      try { pt = this._toImage(e); } catch (_) { return; }
      if (!pt || typeof pt.x !== "number") return;

      this._notePointer("self", pt.x, pt.y, {
        color: this._pointerColor, name: null
      });

      // Throttled: moves arrive far faster than anyone can see them, and each
      // one would be a message to every viewer on the board.
      var now = this._pointerNow();
      if (this._pointerSentAt && now - this._pointerSentAt < POINTER_SEND_MS) return;
      this._pointerSentAt = now;
      this._dispatch("etcher:pointer-moved", {
        x: pt.x, y: pt.y, color: this._pointerColor || POINTER_COLOR
      });
    },

    // Leaving the tool takes the dot away — for this user and, through the
    // host, for everyone else. A pointer that stays behind after someone has
    // stopped presenting is worse than no pointer.
    _endPointer: function() {
      this._pointerSentAt = 0;
      if (!this._pointers || !this._pointers.self) return;
      this._dropPointer("self");
      this._dispatch("etcher:pointer-gone", {});
    },

    // ── Preferences ─────────────────────────────────────────────────────────
    //
    // How this user likes the board set up: whether the background dots and
    // the green connector anchors are shown, how much of the style panel is
    // open, which tools are on the toolbar. Not board content — two people
    // looking at the same board can have different answers, and neither is
    // editing anything by choosing one.
    //
    // Stored in localStorage so it works with no host involvement at all,
    // and handed to the host instead when it offers somewhere better. A host
    // that knows who the user is can follow them between devices; etcher
    // cannot, and should not pretend to.
    //
    // Keyed globally rather than per board on purpose: these are answers
    // about how you work, not about a particular drawing. A host that wants
    // them per board can scope its own adapter however it likes.
    _prefsKey: "etcher:prefs",

    _defaultPrefs: function() {
      return {
        grid: true,        // fresco's background dots
        connectors: true,  // the green anchors an arrow is pulled from
        panel: "full",     // full | compact | hidden
        tools: null,       // null = whatever the host configured
        colors: null,      // null = the board's palette, unchanged
        // Which parts the compact style panel keeps. Some people reach for
        // fills constantly and never touch a line type; the opposite is just
        // as common, and neither should have to keep the full panel open.
        compact: ["colors", "dash"]
      };
    },

    // The host's store wins when it has one. `load()` returns an object (or
    // null); `save(prefs)` is told about every change.
    _setPrefsAdapter: function(adapter) {
      this._prefsAdapter =
        (adapter && typeof adapter.save === "function") ? adapter : null;
      // Re-read through the new adapter — a host that sets this after mount
      // is telling us its answers are better than the ones already loaded.
      this._prefs = null;
      this._loadPrefs();
      this._applyPrefs();
    },

    _loadPrefs: function() {
      if (this._prefs) return this._prefs;
      var stored = null;
      var adapter = this._prefsAdapter;
      if (adapter && typeof adapter.load === "function") {
        try { stored = adapter.load(); } catch (_) { stored = null; }
        // A host reading from a server answers with a promise. Carry on with
        // the defaults and apply the real answer when it lands, rather than
        // holding up the board for a round trip about chrome.
        if (stored && typeof stored.then === "function") {
          var self2 = this;
          stored.then(function(late) { self2._hydratePrefs(late); }, function() {});
          stored = null;
        }
      } else {
        try {
          var raw = window.localStorage.getItem(this._prefsKey);
          stored = raw ? JSON.parse(raw) : null;
        } catch (_) {
          // Private browsing, a disabled store, or corrupt JSON. Preferences
          // are a convenience — none of this is worth failing a board over.
          stored = null;
        }
      }
      this._prefs = Object.assign(this._defaultPrefs(), stored || {});
      return this._prefs;
    },

    _savePrefs: function() {
      var self = this;
      var prefs = self._loadPrefs();

      // The event fires immediately, every time. It is how a LiveView host
      // hears about this without writing any JavaScript at all — the most
      // generic hook there is, and the one most hosts will use.
      self._dispatch("etcher:prefs-changed", Object.assign({}, prefs));

      // The write is debounced. A host that saves to a server is one round
      // trip per change otherwise, and dragging a slider is dozens of
      // changes a second.
      if (self._prefsSaveTimer) clearTimeout(self._prefsSaveTimer);
      self._prefsSaveTimer = setTimeout(function() {
        self._prefsSaveTimer = null;
        var adapter = self._prefsAdapter;
        if (adapter) {
          try { adapter.save(Object.assign({}, self._loadPrefs())); } catch (_) {}
        } else {
          try {
            window.localStorage.setItem(self._prefsKey, JSON.stringify(self._loadPrefs()));
          } catch (_) {}
        }
      }, PREFS_SAVE_DEBOUNCE_MS);
    },

    // Take preferences from the host wholesale — what it had stored for this
    // user, on this board. Merged over the defaults rather than replacing
    // them, so a host that stores three keys does not blank the rest, and
    // NOT saved straight back out: this is the host telling us what it
    // already has, and echoing it would be a write per page load.
    _hydratePrefs: function(stored) {
      if (!stored || typeof stored !== "object") return false;
      var current = this._prefs || {};
      var merged = Object.assign(this._defaultPrefs(), current, stored);
      // A key the user set in THIS instance outranks what a slow host load
      // (or a peer layer) answers with: their pick already happened, and a
      // late round trip must not silently revert it to yesterday's value.
      var touched = this._touchedPrefs || {};
      Object.keys(touched).forEach(function(k) {
        if (k in current) merged[k] = current[k];
      });
      this._prefs = merged;
      this._applyPrefs();
      return true;
    },

    _getPref: function(name) {
      var prefs = this._loadPrefs();
      return prefs[name];
    },

    _setPref: function(name, value) {
      var prefs = this._loadPrefs();
      if (prefs[name] === value) return;
      prefs[name] = value;
      // Mark it user-set so no late _hydratePrefs answer can revert it.
      (this._touchedPrefs = this._touchedPrefs || {})[name] = true;
      this._savePrefs();
      this._applyPrefs();
    },

    // Whether connector anchors are on. Three layers, most specific wins:
    // the user's saved preference (either value — a toggle they touched is
    // their answer), else the host's declared default
    // (`connectors={true}` → `data-connectors="true"`), else OFF. Off
    // because the anchors are an affordance for boards where people draw
    // connectors — everywhere else they are eight dots that appear under
    // the cursor on every shape you pass over. (They previously defaulted
    // on, which is exactly how that felt.)
    // Connecting is the arrow tool's job now — it snaps to a shape's anchors
    // as it draws — so there is no user toggle for it any more; only a host
    // that has no use for anchors at all turns it off.
    _connectorsOn: function() {
      return !this._connectorsHardOff();
    },

    // `connectors={:off}` → `data-connectors="off"`: the host declares the
    // surface has no use for anchors AT ALL — stronger than the soft
    // default above, which a user's saved toggle overrides. Needed because
    // the preference is shared across every Etcher surface in the browser:
    // a toggle flipped on a board followed people into the media viewer,
    // where the anchors point at nothing (the arrow tool isn't even
    // offered). Hard off also hides the toggle — a switch that can't
    // switch reads as broken, not as policy.
    _connectorsHardOff: function() {
      return !!(this.el && this.el.dataset && this.el.dataset.connectors === "off");
    },

    // Whether drag snapping (edge/center magnetism + alignment guides) is
    // on. Same three layers as the connectors, for the same reason: the
    // pull is an acquired taste, so the user's own toggle wins in either
    // direction, then the host's `snap={true}` default, then off.
    _snapOn: function() {
      var pref = this._getPref("snap");
      if (pref === true || pref === false) return pref;
      return !!(this.el && this.el.dataset && this.el.dataset.snap === "true");
    },

    // Push every preference at whatever it controls. Called on load and after
    // any change, so there is one path from "what is preferred" to "what is
    // on screen" rather than each toggle remembering to do its own work.
    _applyPrefs: function() {
      var prefs = this._loadPrefs();
      this._applyGridPref(prefs.grid);
      this._applyPanelPref(prefs.panel);
      this._applyColorsPref(prefs[this._paletteKey()]);
      this._applyCompactParts(prefs.compact);
      // Connector anchors are resolved at the moment they would be shown
      // (`_connectorsAvailableFor` → `_connectorsOn`), so there is nothing
      // to push here. The dots currently on screen do have to go, though.
      if (!this._connectorsOn()) this._removeConnectorDots();
      // Same for the label corner dots: a label focused right now gets or
      // loses its dots the moment the switch flips, not on the next focus.
      // A point label's handles are not the switch's (they always show), so
      // they are left alone — rebuilding them here dropped the one a drag
      // had hold of whenever a panel sync landed mid-drag.
      if (this.editingTitleShape && !this._isPointLabel(this.editingTitleShape)) {
        if (this._titleHandlesOn()) this._renderTitleHandles(this.editingTitleShape);
        else this._removeTitleHandles();
      }
      // And the text-sizing dots on a text or callout already in edit
      // mode — same immediacy, same switch. Point frames' are not the
      // switch's either.
      if (this.editingShape && !this._isPointFrame(this.editingShape) &&
          (this.editingShape.kind === "text" ||
           this.editingShape.kind === "callout")) {
        this._renderHandles(this.editingShape);
      }
      this._refreshToolbarTools();
      this._refreshLabelSwatch();
    },

    // A palette the user has chosen for themselves. Null means they have not,
    // and the board's own palette stands — a host that ships brand colours
    // should not have them replaced by an empty preference.
    _applyColorsPref: function(list) {
      if (!Array.isArray(list) || !list.length) return;
      var slots = this._sanitizeColorSlots(list);
      if (!slots.length) return;
      this._colorSlots = slots;
      if (this._activeSlot >= slots.length) this._activeSlot = 0;
      this._refreshToolbarSwatches();
    },

    // Which sections the compact strip keeps, as a space-separated attribute
    // so the stylesheet can answer with `~=` rather than this reaching in and
    // setting `display` on individual rows.
    _applyCompactParts: function(parts) {
      if (!this.stylePanel) return;
      var list = Array.isArray(parts) ? parts : ["colors", "dash"];
      this.stylePanel.setAttribute("data-compact-parts", list.join(" "));
    },

    // Fresco owns the background dots — they are its canvas, not etcher's
    // overlay — so this asks rather than reaching into its DOM.
    _applyGridPref: function(on) {
      var h = this.handle;
      if (!h || typeof h.setGridVisible !== "function") return;
      try { h.setGridVisible(on !== false); } catch (_) {}
    },

    _pointerState: function() {
      if (!this._pointers) this._pointers = {};
      return this._pointers;
    },

    // Record where a pointer is now, and remember the trail behind it.
    _notePointer: function(id, x, y, opts) {
      var o = opts || {};
      var now = this._pointerNow();
      var all = this._pointerState();
      var p = all[id];
      if (!p) {
        p = all[id] = { trail: [], color: POINTER_COLOR, name: null };
      }
      if (o.color) p.color = o.color;
      if (o.name != null) p.name = o.name;
      p.x = x; p.y = y; p.seen = now;
      p.trail.push({ x: x, y: y, t: now });
      this._trimPointerTrail(p, now);
      this._startPointerLoop();
    },

    _trimPointerTrail: function(p, now) {
      var trail = p.trail;
      while (trail.length && now - trail[0].t > POINTER_TRAIL_MS) trail.shift();
      // A hard cap as well as the time window: a very fast machine can post
      // hundreds of samples inside the window, and every one of them is a
      // segment to draw.
      if (trail.length > 64) trail.splice(0, trail.length - 64);
    },

    _pointerNow: function() {
      try {
        if (typeof performance !== "undefined" && performance.now) {
          return performance.now();
        }
      } catch (_) {}
      return +new Date();
    },

    _dropPointer: function(id) {
      var all = this._pointerState();
      if (!all[id]) return;
      delete all[id];
      this._drawPointers();
    },

    // The trail fades on its own, so it has to keep being redrawn after the
    // last move — otherwise it freezes mid-fade the moment someone stops.
    // The loop stops itself once there is nothing left to draw.
    _startPointerLoop: function() {
      var self = this;
      if (self._pointerRaf) return;
      function tick() {
        self._pointerRaf = null;
        var live = self._drawPointers();
        if (live) {
          self._pointerRaf = (typeof requestAnimationFrame === "function")
            ? requestAnimationFrame(tick)
            : setTimeout(tick, 33);
        }
      }
      self._pointerRaf = (typeof requestAnimationFrame === "function")
        ? requestAnimationFrame(tick)
        : setTimeout(tick, 33);
    },

    _pointerLayerEl: function() {
      if (!this.svg) return null;
      if (this._pointerLayer && this._pointerLayer.parentNode === this.svg) {
        return this._pointerLayer;
      }
      var g = svgEl("g");
      g.classList.add("etcher-pointers");
      // Pointers sit above the drawing and take no clicks — the person
      // pointing is still able to use the board underneath.
      g.setAttribute("pointer-events", "none");
      this.svg.appendChild(g);
      this._pointerLayer = g;
      return g;
    },

    // Draw every pointer. Returns whether anything is still live, which is
    // what stops the animation loop.
    _drawPointers: function() {
      var layer = this._pointerLayerEl();
      if (!layer) return false;
      var all = this._pointerState();
      var now = this._pointerNow();
      var ids = Object.keys(all);
      var live = false;

      for (var i = 0; i < ids.length; i++) {
        var id = ids[i];
        var p = all[id];
        // Someone who closed their tab stops sending. Without this their dot
        // would sit on everyone else's board indefinitely.
        if (now - p.seen > POINTER_STALE_MS) { delete all[id]; continue; }
        this._trimPointerTrail(p, now);
        if (!p.trail.length) { delete all[id]; continue; }
        live = true;
      }

      // Pointers that went away take their elements with them. Tracked by
      // holding the element ON the pointer rather than looking it up by id:
      // an id comes from whatever the host calls its users, and putting that
      // in a selector is a needless escaping problem.
      var seen = [];
      ids = Object.keys(all);
      for (var k = 0; k < ids.length; k++) {
        var pp = all[ids[k]];
        this._drawOnePointer(layer, pp, now);
        if (pp.el) seen.push(pp.el);
      }
      var kids = [].slice.call(layer.childNodes);
      for (var c = 0; c < kids.length; c++) {
        if (seen.indexOf(kids[c]) === -1) layer.removeChild(kids[c]);
      }
      return live;
    },

    _drawOnePointer: function(layer, p, now) {
      var g = p.el;
      if (!g || g.parentNode !== layer) {
        g = svgEl("g");
        layer.appendChild(g);
        p.el = g;
      }
      var self = this;
      var pts = p.trail.map(function(s) {
        var c = self._imageToContainer({ x: s.x, y: s.y });
        return { x: c.x, y: c.y, t: s.t };
      });

      // The trail is drawn as separate segments rather than one path so each
      // can fade and taper by its own age. A single path can only carry one
      // opacity, which reads as a smear rather than a trail.
      var want = Math.max(0, pts.length - 1);
      var segs = [].slice.call(g.querySelectorAll("line"));
      while (segs.length > want) { g.removeChild(segs.pop()); }
      while (segs.length < want) {
        var ln = svgEl("line", { "stroke-linecap": "round" });
        g.insertBefore(ln, g.firstChild);
        segs.push(ln);
      }
      for (var i = 0; i < want; i++) {
        var a = pts[i], b = pts[i + 1];
        // 0 at the tail, 1 at the head.
        var age = 1 - Math.min(1, (now - b.t) / POINTER_TRAIL_MS);
        var seg = segs[i];
        seg.setAttribute("x1", a.x); seg.setAttribute("y1", a.y);
        seg.setAttribute("x2", b.x); seg.setAttribute("y2", b.y);
        seg.setAttribute("stroke", p.color || POINTER_COLOR);
        seg.setAttribute("stroke-width", Math.max(0.5, POINTER_DOT_R * 0.9 * age));
        seg.setAttribute("stroke-opacity", (0.55 * age).toFixed(3));
      }

      var head = g.querySelector("circle");
      if (!head) { head = svgEl("circle"); g.appendChild(head); }
      var hc = pts.length ? pts[pts.length - 1] : null;
      if (hc) {
        head.setAttribute("cx", hc.x);
        head.setAttribute("cy", hc.y);
        head.setAttribute("r", POINTER_DOT_R);
        head.setAttribute("fill", p.color || POINTER_COLOR);
        head.setAttribute("fill-opacity", "0.9");
        // A pale ring so the dot reads against a photo the same colour as it.
        head.setAttribute("stroke", "rgba(255,255,255,0.85)");
        head.setAttribute("stroke-width", "2");
      }
    },

    _renderAll: function() {
      var self = this;
      this.shapes.forEach(function(s) { self._renderShape(s); });
      this._sweepImageRings();
      this._sweepMediaEls();
      if (this.draftState && this.draftState.kind !== "polygon") {
        this._renderShape(this.draftState);
      }
      if (this.draftPolygon) {
        this._renderPolygonPreview(this._lastHover);
      }
      // Keep handles glued to whichever shape currently "owns" them —
      // edit-mode target if any, otherwise the active draft.
      if (this.editingShape) {
        this._positionAllHandles(this.editingShape);
      } else {
        var d = this._draftActive();
        if (d) this._positionAllHandles(d);
      }
      this._positionTransformBox();
      // Title-edit handles track the title bbox separately from shape
      // edit handles — both can technically be active in parallel only
      // for distinct shapes; we coexist with them rather than gating.
      if (this.editingTitleShape) {
        this._positionAllTitleHandles(this.editingTitleShape);
      }
      // Connector dots are positioned in container px like handles are, so
      // they need re-placing on every pan/zoom frame or they'd stay where
      // the shape used to be.
      this._positionConnectorDots();
      if (this._targetDotShape) {
        this._paintTargetDots({
          candidate: this._targetDotShape,
          snap: this._lastArrowSnap || null
        });
      }
      // An open label editor is positioned in container px like the
      // handles above, so it needs the same per-frame re-anchoring.
      if (this._textEditor) this._repositionTextEditor();
      // Keep an open tooltip glued to its anchor shape. `_renderAll`
      // runs on every pan/zoom animation frame (after the shapes above
      // have moved), so re-anchoring here makes the tooltip track the
      // shape instead of floating where it first appeared.
      if (this._tooltipShape && this.tooltipEl &&
          this.tooltipEl.style.display !== "none") {
        this._positionTooltip(this._tooltipShape);
      }
    },

    // -------------------------------------------------------------------------
    // Drawing handlers — dispatch to per-tool state machines
    // -------------------------------------------------------------------------

    // How many fingers are on the overlay, and which one is drawing.
    //
    // Pointer events arrive per finger with no notion of "how many are
    // down"; that has to be counted. The count is what tells a drawing
    // gesture from a navigation one, and the id is what keeps a second
    // finger from feeding a stroke the first one started.
    //
    // Released on the document rather than the wrapper: Fresco captures the
    // pointer for a pinch, so the `pointerup` for a finger that started on
    // the overlay is delivered elsewhere, and a count that only ever went up
    // would leave the tools switched off for the rest of the session.
    _resetTouchTracking: function() {
      this._touchIds = [];
      this._multiTouch = false;
      this._drawPointerId = null;
    },

    // A draft the pointer stream never closed, finished off the touch's own
    // release. It goes through the same path `pointerup` takes, so a dot
    // placed this way is the same shape, styled and emitted the same way, as
    // one placed with a mouse — and when `pointerup` did arrive, the draft is
    // already a shape and there is nothing here to do.
    _commitStrandedDraft: function(e, pointerId) {
      if (!this.draftState) return;
      var t = e.changedTouches && e.changedTouches[0];
      if (!t) return;

      this._onPointerUp({
        clientX: t.clientX,
        clientY: t.clientY,
        button: 0,
        pointerId: pointerId,
        pointerType: "touch",
        type: "pointerup"
      });
    },

    // A marquee abandoned rather than finished. Nothing is selected by it —
    // the press became part of a gesture that belongs to the canvas.
    _cancelBoxSelect: function() {
      var bs = this._boxSelect;
      if (!bs) return;
      this._boxSelect = null;
      if (bs.el && bs.el.parentNode) bs.el.parentNode.removeChild(bs.el);
      this._clearBoxSelectPreview(bs);
    },

    // A press-and-hold-free stepper button: one press, one step. The press
    // is stopped here so it never reaches the canvas underneath — every
    // other control in this panel does the same, and without it pressing
    // "+" would also be an empty-canvas press and clear the selection the
    // size is being changed for.
    _makeStepButton: function(glyph, title, mod) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = mod ? "etcher-step " + mod : "etcher-step";
      btn.textContent = glyph;
      btn.title = title;
      btn.setAttribute("aria-label", title);
      btn.addEventListener("pointerdown", function(e) { e.stopPropagation(); });
      // A number box loses focus to a press on its own stepper otherwise,
      // which on a phone closes the keyboard between every step.
      btn.addEventListener("mousedown", function(e) { e.preventDefault(); });
      return btn;
    },

    _dropStraySelection: function() {
      try {
        var sel = typeof window !== "undefined" && window.getSelection && window.getSelection();
        if (sel && !sel.isCollapsed) sel.removeAllRanges();
      } catch (_) {}
    },

    _trackTouch: function(e, down) {
      if (!e || e.pointerType !== "touch") {
        if (down) this._drawPointerId = e ? e.pointerId : null;
        return;
      }

      this._touchIds = this._touchIds || [];
      var at = this._touchIds.indexOf(e.pointerId);

      if (down) {
        if (at === -1) this._touchIds.push(e.pointerId);
        this._multiTouch = this._touchIds.length > 1;
        if (!this._multiTouch) this._drawPointerId = e.pointerId;
        this._wireTouchRelease();
        return;
      }

      if (at !== -1) this._touchIds.splice(at, 1);
      if (e.pointerId === this._drawPointerId) this._drawPointerId = null;
      // Still two fingers down? The gesture is still the canvas's. Lifting
      // to one finger does NOT hand drawing back mid-pinch — that would
      // draw a line from wherever the remaining finger happens to be.
      if (this._touchIds.length === 0) this._multiTouch = false;
    },

    _wireTouchRelease: function() {
      if (this._touchReleaseWired) return;
      this._touchReleaseWired = true;

      var self = this;
      var release = function(e) { self._trackTouch(e, false); };
      document.addEventListener("pointerup", release, true);
      document.addEventListener("pointercancel", release, true);
    },

    // ── Same-tool editing ─────────────────────────────────────────────
    //
    // A tool used to be for drawing only: adjusting what it drew meant a
    // trip to the cursor. Now the shapes a tool draws stay grabbable
    // WHILE it is armed — press a rectangle's edge with the rectangle
    // tool and you are moving that rectangle; a stationary press selects
    // it (corner dots and all, same as the cursor would); a double-click
    // opens its label. Everything else on the canvas still belongs to
    // the tool: pressing it draws.
    //
    // The ink tools are deliberately left out. Strokes come in flurries
    // and routinely start on top of one another, so a marker that
    // grabbed the previous stroke instead of starting the next would
    // fight exactly how those tools are used. So is `image`: pictures
    // get stacked on purpose, and a placement press on one must keep
    // opening the picker. (The eraser erases what it touches and the
    // grabber/pointer draw nothing — no shape carries their name, so
    // the kind test already excludes them.)
    _armedToolEdits: function(shape) {
      var t = this.activeTool;
      if (t == null || !shape) return false;
      if (shape.readonly || !shape.uuid) return false;
      if (t === "freehand" || t === "marker" || t === "highlighter" || t === "image") {
        return false;
      }
      if (this._armedFocusBars(shape)) return false;
      // The textbox tool draws `text` shapes (style.box: "fixed"), so the
      // text family is one kind for editing purposes — either tool grabs
      // either shape.
      if (t === "textbox") return shape.kind === "text";
      return shape.kind === t;
    },

    // While a tool is armed, the shape being worked on — the text being
    // typed, the box just drawn, a selection — holds the focus. Every other
    // shape is inert until a click away lets go of it: no hover, no grab,
    // no tap that selects it. Reported from the field: grabbing a text's
    // dashed outline right after typing it sometimes caught the shape
    // underneath instead. Shift is the way past it (it adds to the
    // selection), so a group can still be gathered with the tool in hand.
    //
    // A press is judged by the focus it STARTED with (`_pressFocus`, taken
    // on the window in the capture phase, ahead of the editor's commit and
    // anything else that might clear the selection mid-press). Between
    // presses — hover — the live selection answers.
    _liveFocus: function() {
      var list = [];
      if (this.editingShape) list.push(this.editingShape);
      // A label picked on its own (its frame up) holds the focus for its shape.
      if (this.editingTitleShape && list.indexOf(this.editingTitleShape) === -1) list.push(this.editingTitleShape);
      (this.selectedShapes || []).forEach(function(s) {
        if (s && list.indexOf(s) === -1) list.push(s);
      });
      return list.length ? list : null;
    },

    _armedFocusBars: function(shape) {
      if (!shape || !this.annotationMode) return false;
      var press = this._pressFocus;
      if (press && press.shift) return false;
      var focus = press ? press.focus : this._liveFocus();
      if (!focus) return false;
      for (var i = 0; i < focus.length; i++) {
        if (focus[i] === shape || (focus[i].uuid && focus[i].uuid === shape.uuid)) return false;
      }
      return true;
    },

    // Shift-press with a tool armed: the shape joins the selection (or
    // leaves it), the way it does under the cursor. What was being worked
    // on comes along, so the group starts from it rather than replacing it.
    _armedShiftAdd: function(shape, e) {
      if (!shape || shape.readonly || !shape.uuid) return;
      var press = this._pressFocus;
      var had = (press && press.focus) || this._liveFocus() || [];
      this._exitEditMode();
      for (var i = 0; i < had.length; i++) {
        if (had[i] !== shape && !had[i].readonly && had[i].uuid &&
            this.shapes.indexOf(had[i]) !== -1 && !this._isInSelection(had[i])) {
          this._addToSelection(had[i]);
        }
      }
      this._toggleInSelection(shape);
      // The doc-level tap tracker sees this press too; it must not turn
      // the same click into a plain select of this one shape on release.
      if (e) this._sameToolGrab = { pointerId: e.pointerId, at: Date.now() };
    },

    // Container px per image px around `pt` — the live zoom, measured by
    // projecting a unit vector and taking its LENGTH (one axis of it
    // reads ~0 at 90°/270° — see the rotation test), so the grab
    // tolerance below means the same distance on screen at every zoom
    // and rotation.
    _screenPerImagePx: function(pt) {
      try {
        var a = this._imageToContainer({ x: pt.x, y: pt.y });
        var b = this._imageToContainer({ x: pt.x + 1, y: pt.y });
        var k = Math.hypot(b.x - a.x, b.y - a.y);
        return k > 0 ? k : 1;
      } catch (_) {
        return 1;
      }
    },

    // The shape a tool-armed press GRABS instead of drawing over, or
    // null when the press is the canvas's and should draw.
    //
    // The filled kinds grab by their OUTLINE (within ~8 screen px), not
    // their footprint: the interior stays canvas, so a rectangle can
    // still be drawn inside a rectangle and a circle inside a circle.
    // The stroke-built kinds (line, arrow, dimension) and the text-ish
    // ones (text, callout) grab anywhere their hit-test lands — their
    // footprint IS their outline, or close enough that nobody draws
    // inside it on purpose. A shape's title satellite is a grab zone
    // whatever the kind: it is how a label is reached, and nothing is
    // ever deliberately drawn inside a label.
    _sameToolGrabTarget: function(pt) {
      // With a shape holding the focus, only it can be grabbed — tested on
      // its own, so its outline wins even where another shape lies on top.
      var press = this._pressFocus;
      var focus = press ? (press.shift ? null : press.focus) : this._liveFocus();
      var hit = null;
      if (focus) {
        for (var f = focus.length - 1; f >= 0; f--) {
          if (focus[f].uuid && this._shapeContainsPoint(focus[f], pt)) { hit = focus[f]; break; }
        }
      } else {
        hit = this._shapeAt(pt);
      }
      if (!this._armedToolEdits(hit)) return null;

      var tol = 8 / this._screenPerImagePx(pt);
      var g = hit.geometry;

      if (hit.titleGroup && hit._renderedTitleImage) {
        var tb = hit._renderedTitleImage;
        if (pt.x >= tb.x && pt.x <= tb.x + tb.w &&
            pt.y >= tb.y && pt.y <= tb.y + tb.h) {
          return hit;
        }
      }

      function segDist(p, a, b) {
        var vx = b[0] - a[0], vy = b[1] - a[1];
        var wx = p.x - a[0], wy = p.y - a[1];
        var l2 = vx * vx + vy * vy;
        var t = l2 > 0 ? Math.max(0, Math.min(1, (wx * vx + wy * vy) / l2)) : 0;
        return Math.hypot(p.x - (a[0] + t * vx), p.y - (a[1] + t * vy));
      }

      switch (hit.kind) {
        case "rectangle": {
          var nearEdge =
            Math.abs(pt.x - g.x) <= tol || Math.abs(pt.x - (g.x + g.w)) <= tol ||
            Math.abs(pt.y - g.y) <= tol || Math.abs(pt.y - (g.y + g.h)) <= tol;
          return nearEdge ? hit : null;
        }
        case "circle": {
          var d = Math.hypot(pt.x - g.cx, pt.y - g.cy);
          return Math.abs(d - g.r) <= tol ? hit : null;
        }
        case "polygon": {
          var pts = (g && g.points) || [];
          for (var i = 0; i < pts.length; i++) {
            if (segDist(pt, pts[i], pts[(i + 1) % pts.length]) <= tol) return hit;
          }
          return null;
        }
        default:
          return hit;
      }
    },

    _onPointerDown: function(e) {
      // Read and cleared first thing, before any of the early returns
      // below can skip it: the flag belongs to THIS press (the capture
      // -phase handler that set it ran moments ago, in the same dispatch)
      // and must never be left lying around for the next one.
      var afterLabelCommit = this._drawSuppressedUntilDrag;
      this._drawSuppressedUntilDrag = false;
      // Same story for a press that landed ON an open label editor (set
      // by `_textEditOutsideDown`): read and cleared here whatever this
      // press goes on to do.
      var onEditor = this._pressInsideTextEditor;
      this._pressInsideTextEditor = false;
      if (!this.annotationMode || !this.activeTool) return;
      if (e.button !== 0) return;
      // Clicking a dimension's own label, to put the caret back in it,
      // is not also a place-a-dimension-here. This press belongs to the
      // words: the tools never see it, not even as a drag.
      if (onEditor) return;
      var pt = this._toImage(e);

      // A draft left waiting for its second click owns this press: the
      // gesture already in flight finishes rather than a new one starting
      // on top of it.
      if (this._placeArmedDraft(pt)) {
        // The finishing click may have just opened a label or text editor;
        // its default action would move focus to where it landed and blur
        // that editor shut (an empty new text box is then thrown away).
        e.preventDefault();
        return;
      }

      // Shift on a shape gathers it into the selection — see _armedShiftAdd.
      // A shift-press on empty canvas still draws (shift constrains the
      // shape). The text tools gather any shape; a drawing tool only its
      // own kind, so shift-drawing a square over a picture still draws.
      if (e.shiftKey && !this.draftPolygon && !this.draftCallout &&
          this.activeTool !== "eraser") {
        var add = this._sameToolGrabTarget(pt);
        if (!add && (this.activeTool === "text" || this.activeTool === "textbox")) {
          add = this._shapeAt(pt);
        }
        if (add && !add.readonly) {
          this._pendingDraw = null;
          this._armedShiftAdd(add, e);
          e.preventDefault();
          return;
        }
      }

      // The press that just closed a label editor is a full stop, not a
      // new sentence. Since a tool stays armed after it draws, typing a
      // dimension's label and clicking away to set it ALSO started the
      // next dimension — one click doing two unrelated things, and the
      // second one unasked for.
      //
      // So that press draws nothing by itself. It is held here, and
      // released by `_onPointerMove` the moment it travels far enough to
      // be a drag: a drag is a deliberate "draw one here", a click is
      // just putting the label down. Held rather than dropped, so the
      // drag that does come still starts from where the finger landed.
      if (afterLabelCommit) {
        if (!this._pressLetsGoOnShape(pt)) this._pendingDraw = { pt: pt, shift: !!e.shiftKey };
        return;
      }

      // A press on a shape of the armed tool's own kind grabs that shape
      // instead of drawing — see _sameToolGrabTarget. Skipped mid-polygon
      // and mid-callout: those presses are vertex placements for the
      // draft already in flight, wherever they land.
      if (!this.draftPolygon && !this.draftCallout) {
        var grab = this._sameToolGrabTarget(pt);
        if (grab) {
          // The doc-level tap tracker sees this press too (touch does not
          // stop propagation at the wrapper) — leave it a note so it does
          // not ALSO track a tap on the same gesture.
          this._sameToolGrab = { pointerId: e.pointerId, at: Date.now() };
          if (this._isInSelection(grab) && this.selectedShapes.length > 1) {
            this._startMultiShapeMove(grab, e);
          } else {
            this._startShapeMove(grab, e);
          }
          return;
        }
      }

      // Something is selected — usually the shape just drawn — and the
      // press is somewhere else. That press is "I'm done with it": it
      // deselects, and draws only if it goes on to be a drag. A plain click
      // used to ALSO click-place a fresh default-sized shape where it
      // landed, so finishing one square left a second one behind every
      // time. Held the same way as the press that closes a label editor,
      // so a drag that does come draws from where it started. Not the
      // eraser (a tap on a shape is how it erases one) and not mid-polygon
      // or mid-callout (those presses are vertices of the draft).
      if (this._hasSelection() && this.activeTool !== "eraser" &&
          !this.draftPolygon && !this.draftCallout) {
        var onShape = this._pressLetsGoOnShape(pt);
        this._exitEditMode();
        if (typeof this._clearSelection === "function") this._clearSelection();
        if (!onShape) this._pendingDraw = { pt: pt, shift: !!e.shiftKey };
        return;
      }

      this._dispatchToolDown(pt, e);
    },

    // A press that lets go of the focused shape by landing on ANOTHER shape
    // only lets go: a drag from there must not draw a fresh shape on top of
    // the one the user was reaching for (see `_armedFocusBars`).
    _pressLetsGoOnShape: function(pt) {
      return this._armedFocusBars(this._shapeAt(pt));
    },

    // A shape being edited, or any shapes selected.
    _hasSelection: function() {
      return !!this.editingShape || !!(this.selectedShapes && this.selectedShapes.length);
    },

    // The per-tool half of `_onPointerDown`, so a press held back until it
    // becomes a drag can start the same way a plain press does.
    _dispatchToolDown: function(pt, e) {
      switch (this.activeTool) {
        case "rectangle": this._startRectangle(pt, e); break;
        case "circle":    this._startCircle(pt, e); break;
        case "polygon":   this._polygonClick(pt); break;
        case "freehand":  this._startFreehand(pt, e); break;
        case "marker":    this._startMarker(pt, e); break;
        case "highlighter": this._startMarker(pt, e); break;
        case "callout":
          this._calloutClick(pt);
          // Held like every other press-drag tool, so the release comes back
          // here wherever it lands. Without it a drag that ended over the
          // style panel (or any chrome outside the overlay) never reached
          // `_onPointerUp`, and the callout was left as a draft that nothing
          // finished — not saved, not undoable, just stuck on screen.
          if (e && e.target && e.pointerId != null) {
            try { e.target.setPointerCapture(e.pointerId); } catch (_) {}
          }
          break;
        case "text":      this._startText(pt, e); break;
        case "textbox":   this._startText(pt, e, true); break;
        case "dimension": this._startDimension(pt, e); break;
        case "line":      this._startLine(pt, e); break;
        case "arrow":     this._startArrow(pt, e); break;
        case "eraser":    this._startErase(pt, e); break;
      }
    },

    // Dimensions, lines and callouts can be drawn either way.
    //
    // Press, drag, release draws one in a single gesture, previewing as you
    // go. Or click once and click again where the far end goes — which is
    // how you place one whose ends are further apart than a comfortable
    // drag, and how you place one precisely without holding a button down.
    //
    // A click leaves the draft ARMED: it keeps following the cursor, so the
    // first click has visible, obvious consequences. (Two-click was tried
    // before without that preview and removed, because a first click that
    // changes nothing on screen reads as the tool being broken. The preview
    // is the difference.)
    //
    // Returns true when the press was consumed.
    _placeArmedDraft: function(pt) {
      var draft = null;
      if (this.draftState && this.draftState.armed) draft = this.draftState;
      else if (this.draftCallout && this.draftCallout.armed) draft = this.draftCallout;
      if (!draft) return false;

      var anchor = draft.kind === "callout"
        ? { x: draft.geometry.anchor[0], y: draft.geometry.anchor[1] }
        : (draft.kind === "circle" ? draft.center : draft.anchor);

      // The second click landed on the first, or near enough that there is
      // no shape between them. Almost always a double-click — people
      // double-click things — so the whole draft goes: someone who
      // double-clicks did not mean to start drawing, and leaving the draft
      // armed strands a preview trailing their cursor that they then have
      // to notice and dismiss. Clicking again is the way to start one.
      if (this._isTooCloseToPlace(anchor, pt)) {
        this._cancelDraft();
        return true;
      }

      if (draft === this.draftCallout) this._commitCallout(pt, true);
      else if (draft.kind === "rectangle") this._commitRectangle(pt);
      else if (draft.kind === "circle") this._commitCircle(pt);
      else if (draft.kind === "text") this._commitText(pt);
      else {
        // The placing click settles an arrow's head, snap included.
        if (draft.kind === "arrow") this._updateDimension(pt);
        this._commitShaftDraft(this._shaftGeometry(anchor, pt));
      }
      return true;
    },

    _onPointerMove: function(e) {
      // A press held back by `_onPointerDown` (it closed a label editor)
      // starts drawing here, from where it landed, once it has travelled
      // far enough to be a drag rather than a click.
      if (this._pendingDraw) {
        var from = this._pendingDraw.pt;
        var now = this._toImage(e);
        if (this._isClickGesture(from, now)) return;
        this._pendingDraw = null;
        this._dispatchToolDown(from, e);
      }
      if (!this.draftState) {
        if (this.activeTool === "polygon" && this.draftPolygon) {
          this._polygonHover(this._toImage(e));
        } else if (this.activeTool === "callout" && this.draftCallout) {
          var cPt = this._toImage(e);
          this._calloutHover(cPt);
          var dc = this.draftCallout;
          if (dc && !dc.armed && !dc.pulled && (e.buttons & 1) && dc.geometry && dc.geometry.anchor &&
              !this._isClickGesture({ x: dc.geometry.anchor[0], y: dc.geometry.anchor[1] }, cPt)) {
            dc.pulled = true;
            this._syncDraftHandles();
          }
        } else if (this.activeTool === "arrow") {
          this._arrowToolHover(e);
        } else if (this.activeTool === "eraser" && this._erasingActive) {
          this._eraserMove(this._toImage(e));
        } else if (this.activeTool === "eraser") {
          // Idle hover — preview the single shape under the cursor.
          this._eraserHover(this._toImage(e));
        }
        // Only the midpoint closest to the cursor is shown. Polygons →
        // "add vertex" dots; arrows → "add bend"; rectangles → "drag this
        // edge". Same machinery, different drag semantics.
        if (this.editingShape &&
            this._hasMidpointHandles(this.editingShape.kind) &&
            this.midpointHandles && this.midpointHandles.length) {
          this._updateClosestMidpoint(this._toImage(e));
        }
        return;
      }
      var pt = this._toImage(e);
      // The press has become a drag: the draft's dots come up now.
      var dPull = this.draftState;
      var pulledNow = false;
      if (!dPull.armed && !dPull.pulled && (e.buttons & 1)) {
        var pullFrom = dPull.anchor || dPull.center;
        if (pullFrom && !this._isClickGesture(pullFrom, pt)) {
          dPull.pulled = true;
          pulledNow = true;
        }
      }
      // Shift is read at use, not at pointerdown: constraints engage and
      // release mid-drag as the key goes down and up, which is how every
      // comparable tool behaves.
      this.draftState.shift = !!e.shiftKey;
      switch (this.draftState.kind) {
        case "rectangle": this._updateRectangle(pt); break;
        case "circle":    this._updateCircle(pt); break;
        case "freehand":
        case "highlighter":
        case "marker": {
          // Strokes consume every sample the browser captured, not just the
          // one position this event reports. A pointer is read far faster
          // than frames are delivered (120Hz+ on a decent trackpad, more on
          // a stylus), and the extra readings are batched into the single
          // event that lands each frame. Taking only that one turns a fast
          // stroke into a polygon of frame-length straight segments — the
          // faster you draw, the more angular it gets, which is the opposite
          // of what a marker should do.
          var samples = this._coalesced(e);
          for (var ci = 0; ci < samples.length; ci++) {
            var cp;
            try { cp = this._toImage(samples[ci]); } catch (_) { continue; }
            this._appendFreehand(cp);
          }
          break;
        }
        case "text":      this._updateText(pt); break;
        case "dimension": this._updateDimension(pt); break;
        case "line":      this._updateDimension(pt); break;
        case "arrow":     this._updateDimension(pt); break;
      }
      if (pulledNow) this._syncDraftHandles();
    },

    _onPointerUp: function(e) {
      // Released without travelling: the press was only ever the click
      // that set the label, so there is nothing to draw or clean up.
      this._pendingDraw = null;
      // Eraser commits independently of the draftState flow since it
      // doesn't build a shape — it grays hits during a press-and-drag
      // and flushes them on release.
      if (this.activeTool === "eraser" && this._erasingActive) {
        this._commitErase();
        return;
      }
      // Callout drafts live in `draftCallout`, not `draftState`, so they
      // need their own gate ahead of the guard below.
      if (this.activeTool === "callout" && this.draftCallout) {
        this._commitCallout(this._toImage(e));
        return;
      }
      if (!this.draftState) return;
      var pt = this._toImage(e);
      this.draftState.shift = !!e.shiftKey;
      switch (this.draftState.kind) {
        case "rectangle": this._commitRectangle(pt); break;
        case "circle":    this._commitCircle(pt); break;
        case "freehand":  this._commitFreehand(pt); break;
        case "marker":    this._commitFreehand(pt); break;
        case "highlighter": this._commitFreehand(pt); break;
        case "text":      this._commitText(pt); break;
        case "dimension": this._commitDimension(pt); break;
        case "line":      this._commitDimension(pt); break;
        case "arrow":     this._commitDimension(pt); break;
      }
    },

    _onDoubleClick: function(e) {
      if (this.activeTool === "polygon" && this.draftPolygon) {
        e.preventDefault();
        e.stopPropagation();
        this._commitPolygon();
      }
    },

    // Attach hover + click handlers to a single shape's SVG element.
    // Tooltip + selection work in any state except an active drawing
    // tool (CSS turns shape pointer-events off in that mode anyway, so
    // these handlers won't fire — the safety check is just defensive).
    _attachShapeInteractions: function(shape) {
      var self = this;
      var el = shape.el;
      if (!el || el._etcherWired) return;
      el._etcherWired = true;

      el.addEventListener("mouseenter", function() {
        if (self.annotationMode && self.activeTool != null) return;
        if (self._armedFocusBars(shape)) return;
        // Visual hover state (dashed outline, .is-hovered class) is
        // always applied so users can see which shape the cursor is
        // over — even when a different shape's tooltip is pinned.
        el.classList.add("is-hovered");
        // The tooltip, however, defers to the pin: a pinned tooltip
        // stays put until the user clicks elsewhere (or clicks the
        // same shape to unpin). Hovering another shape doesn't yank
        // the pin away.
        if (self.tooltipPinned) return;
        self._hoverTooltip(shape);
      });
      el.addEventListener("mouseleave", function() {
        // Always drop the hover styling so a pinned shape doesn't keep
        // a sticky dashed/selected outline after the cursor leaves.
        // The pin is for tooltip lifecycle, not visual hover state.
        el.classList.remove("is-hovered");
        // Whatever was about to open for this shape no longer has a
        // reason to.
        self._cancelTooltipOpen();
        // Leaving the shape starts closing it. This used to be the dwell
        // timer's job alone — the tooltip rode out its whole window
        // wherever the cursor went, which is what "takes way too long to
        // disappear" was. `_scheduleHideTooltip` leaves a short bridge so
        // the trip from shape to tooltip (for the delete button) doesn't
        // snap it shut, and pinned tooltips ignore it entirely.
        self._scheduleHideTooltip();
      });
      el.addEventListener("click", function(e) {
        // Direct DOM clicks on shapes are mostly historical now —
        // `.etcher-shape` is `pointer-events: none` so this rarely
        // fires. The doc-level tap handler in
        // `_wireGlobalShapeListeners` drives the common path. Kept
        // as a fallback in case a future caller temporarily flips
        // shape pointer events back on.
        if (self.annotationMode && self.activeTool != null &&
            !self._armedToolEdits(shape)) {
          return;
        }
        // The press of this click let go of another shape; it selects nothing.
        if (self._armedFocusBars(shape)) {
          e.stopPropagation();
          return;
        }
        // Stop propagation so OSD's canvas (which lives next to us
        // in the container) doesn't also receive the click and
        // trigger a click-to-zoom.
        e.stopPropagation();
        e.preventDefault();
        self._onShapeTap(shape);
      });

      // Double-click on a text or callout (in annotation mode, cursor
      // tool) jumps into inline-edit mode. Matches Figma/Miro muscle
      // memory and lets users tweak the callout's label without
      // re-opening the composer.
      el.addEventListener("dblclick", function(e) {
        // The shape the first click selected, not whatever this element is
        // — see `_dblClickTarget`.
        var target = self._dblClickTarget(shape, e);
        if (target.kind !== "text" && target.kind !== "callout" && target.kind !== "dimension") {
          return;
        }
        // Cursor mode, or the shape's own tool armed — a double-click on
        // a text shape with the text tool out means "edit this one", not
        // "draw two more here".
        if (self.annotationMode && self.activeTool != null &&
            !self._armedToolEdits(target)) {
          return;
        }
        if (!self.annotationMode) return;
        if (target.readonly) return; // locked → no inline text edit
        e.stopPropagation();
        e.preventDefault();
        self._enterEditMode(target);
        self._startTextEdit(target);
      });

      // In annotation cursor mode, the shape body is a grab-handle —
      // a pointerdown anywhere on the shape starts a drag-to-move
      // gesture without requiring a prior tap to enter edit mode. The
      // dead-zone inside `_startShapeMove` keeps stationary clicks
      // from emitting a no-op edit; on pointerup-without-drag, the
      // onUp falls back to `_onShapeTap` so single-click still
      // selects (enters edit mode + shows handles).
      el.addEventListener("pointerdown", function(e) {
        if (e.button !== 0) return;
        if (!self.annotationMode) return;
        if (self.activeTool != null) {
          // The shape's own tool grabs it like the cursor would; the
          // multi-select gestures below stay cursor-mode vocabulary.
          // stopPropagation keeps the live wrapper from reading the
          // same press as "draw here".
          if (!self._armedToolEdits(shape)) return;
          e.stopPropagation();
          if (e.shiftKey) {
            e.preventDefault();
            self._armedShiftAdd(shape, e);
          } else if (self._isInSelection(shape) && self.selectedShapes.length > 1) {
            self._startMultiShapeMove(shape, e);
          } else {
            self._startShapeMove(shape, e);
          }
          return;
        }
        e.stopPropagation();
        // Another shape holds the focus: this press only lets go of it —
        // unless it is the press that just finished typing (that one only
        // finishes; see `_pressCommittedText`).
        if (self._armedFocusBars(shape)) {
          if (self._pressCommittedText) { self._pressCommittedText = false; return; }
          self._clearSelection();
          self._exitEditMode();
          if (self.editingTitleShape) self._exitTitleEditMode();
          return;
        }
        // Shift extends the multi-selection; that lives on the doc-level
        // handler, and swallowing it here would make shift-clicking a
        // selected shape start a move instead.
        if (e.shiftKey) {
          if (shape.readonly) { e.preventDefault(); return; }
          // The shape being worked on joins the group too.
          self._armedShiftAdd(shape, e);
          e.preventDefault();
          return;
        }
        // A shape in a multi-selection drags the whole group. The doc-level
        // handler has this branch too, but never sees the event: a selected
        // shape has pointer events enabled, so a real press lands on the
        // shape itself and is stopped right here. Without this the press
        // fell through to a single-shape move, which quietly dropped the
        // group — the other shapes stayed put and this one entered edit
        // mode, reading as "the selection broke".
        if (self._isInSelection(shape)) {
          self._startMultiShapeMove(shape, e);
          return;
        }
        // Pressing a shape that ISN'T in the selection abandons it, the
        // same as pressing empty canvas would.
        if (self.selectedShapes && self.selectedShapes.length) {
          self._clearSelection();
        }
        self._startShapeMove(shape, e);
      });

      // Audio transport. Wired here rather than through the doc-level
      // hit-test because these are real controls, not shape body: pressing
      // play must not also start a move, and the press has to land on the
      // exact sub-element to tell "play" from "scrub".
      if (self._isMediaKind(shape.kind)) self._attachAudioControls(shape);

      // A dimension's label needs no wiring of its own any more: it is the
      // standard title sibling, so it inherits click-to-resize,
      // double-click-to-edit and drag from `_attachTitleInteractions`, and
      // the drag knows to slide it along the shaft.
    },

    // Shape "tap" — fired either from a direct DOM click on the
    // shape (rare, fallback) or from the doc-level tap detector
    // when the user clicked without dragging. Selects/edits the
    // shape per current mode.
    _onShapeTap: function(shape) {
      if (!shape) return;
      // Docked hosts with Etcher off: the shapes are part of the picture,
      // so a click on one does nothing — no selection, no blue outline,
      // no pinned tooltip. (Hover is already inert there; this is the
      // other half, and without it a tap still pinned the tooltip and
      // marked the shape selected.) Turning Etcher on is what makes the
      // drawing something you can act on.
      if (this._tooltipDocked() && !this.annotationMode) return;
      var id = shape.uuid;
      if (this.annotationMode && !shape.readonly) {
        // Edit handles only appear in annotation mode + cursor tool.
        this._enterEditMode(shape);
      } else {
        // Browse mode, OR a locked (readonly) shape in annotation mode:
        // pin the tooltip so the user can dwell on the comment preview
        // without it timing out. Clicking the same shape again unpins;
        // clicking another shape switches the pin.
        if (this.tooltipPinned && this._tooltipShape === shape) {
          this._unpinTooltip();
        } else {
          this._pinTooltipFor(shape);
        }
      }
      // Selection is local UI state in 0.3.x — no server event. (0.2.x's
      // `etcher:selected` event is dropped; consumers who tracked the
      // selected uuid for sidebar UI should drive that off a JS
      // CustomEvent or a future explicit hook if they need it back.)
    },

    // -------------------------------------------------------------------------
    // Global shape listeners — let pan/zoom pass through shapes by
    // making `.etcher-shape` `pointer-events: none` and re-detecting
    // hover + tap at the document level via image-px hit-testing.
    //
    // Without this, OSD's MouseTracker on the canvas sibling never
    // sees the wheel/pointerdown when the cursor is over a shape
    // (events bubble UP the DOM, not sideways), so scroll-zoom and
    // drag-pan stop working over annotations.
    // -------------------------------------------------------------------------

    _wireGlobalShapeListeners: function() {
      var self = this;
      if (self._globalShapeListenersWired) return;
      self._globalShapeListenersWired = true;

      // Gate every handler on "cursor is over the viewer's container."
      // Cheap to compute, keeps us off the hot path of unrelated
      // document mousemoves.
      // Inside the viewer's box, and pressed on something of the viewer's.
      // A host lays its own buttons over the picture (a close button, a
      // next-file tab) as siblings of the container, not children: the
      // coordinates alone would put a press on one of those onto the shape
      // underneath it as well.
      function overContainer(e) {
        if (!self.handle || !self.handle.container) return false;
        var c = self.handle.container;
        var t = e.target;
        if (t && t.nodeType === 1 && t !== document.documentElement &&
            t !== document.body && !c.contains(t)) return false;
        var r = c.getBoundingClientRect();
        return e.clientX >= r.left && e.clientX <= r.right &&
               e.clientY >= r.top && e.clientY <= r.bottom;
      }

      // The focus a press starts with — see `_armedFocusBars`. Window,
      // capture phase: ahead of every handler that could change the
      // selection under this same press. Dropped a tick after release, once
      // the press's click (and a double-click's) has been handled.
      self._focusPressDown = function(e) {
        self._pressFocus = self.annotationMode
          ? { focus: self._liveFocus(), shift: !!e.shiftKey }
          : null;
      };
      self._focusPressUp = function() {
        setTimeout(function() { self._pressFocus = null; }, 0);
      };
      window.addEventListener("pointerdown", self._focusPressDown, true);
      window.addEventListener("pointerup", self._focusPressUp, true);
      window.addEventListener("pointercancel", self._focusPressUp, true);

      self._docMouseMove = function(e) {
        // Sitting on a connector's grab zone doesn't count as leaving the
        // shape. The zones deliberately reach past its edge — that's the
        // half of them you aim at when pulling an arrow outward — and the
        // hit-test below would call that "off the shape" and delete the
        // very dot being reached for. Keep the current hover instead.
        if (e.target && e.target.classList &&
            e.target.classList.contains("etcher-connector-hit")) {
          return;
        }
        // A button is down, so this move is part of a gesture — dragging a
        // label along its line, moving a shape, pulling a handle, sweeping a
        // box-select, panning. Whatever it is, the shapes the pointer travels
        // over on the way are not being pointed AT, and lighting each one up
        // as it passes puts hover outlines and connector dots on top of the
        // thing actually being dragged.
        //
        // Read off the buttons rather than off a flag each gesture has to
        // remember to set and clear: there are a dozen places a drag can
        // begin, and any one of them forgetting would be a bug nobody
        // notices until it looks like this. Hover resumes on the first move
        // after release, when `buttons` is 0 again.
        if (e.buttons) {
          if (self._hoveredShape) self._setHoveredShape(null, false);
          return;
        }
        // Any armed tool: no hover, and no hit-test to find one. The board
        // should not light up under a press that is going to the tool —
        // whether that is a pan (grabber), a stroke (marker and every other
        // drawing tool), or a presenter's laser (pointer, whose audience
        // cannot see the highlights anyway). `_setHoveredShape` enforces
        // the same rule for callers that don't come through here; this is
        // where the wasted work is skipped.
        if (self.activeTool != null) {
          if (self._hoveredShape) self._setHoveredShape(null, false);
          // The red pointer is the one tool whose whole gesture IS the
          // move — there is nothing to press — so it still wants this.
          if (self.activeTool === "pointer" && overContainer(e)) {
            self._movePointer(e);
          }
          return;
        }
        // Over Etcher's own chrome (toolbar / popup / tooltip): no shape hover.
        if (e.target.closest &&
            e.target.closest(CHROME_SELECTOR)) {
          if (self._hoveredShape) self._setHoveredShape(null, false);
          return;
        }
        if (!overContainer(e)) {
          if (self._hoveredShape) self._setHoveredShape(null, false);
          return;
        }
        // Cursor sitting over a modal / dialog shouldn't light up the
        // shape behind it — that's a confusing affordance (the shape
        // looks tappable but it isn't, since the modal owns the click).
        if (isInputOwner(e.target, self.overlayWrapper)) {
          if (self._hoveredShape) self._setHoveredShape(null, false);
          return;
        }
        // Where the cursor is, for the tooltip to open at and measure
        // against. Kept here because this handler already runs on every
        // hover move and nothing else tracks it.
        self._lastPointerClient = { x: e.clientX, y: e.clientY };
        // Moved on? Then the tooltip's welcome is over, even over the
        // same shape.
        self._tooltipPeekMove(e);
        var pt;
        try { pt = self._toImage(e); } catch (_) { return; }
        var hit = self._shapeAt(pt);
        // If the cursor is on a shape's title satellite (the
        // movable label group), the user is most likely about to
        // grab/drag/edit the title — suppress the tooltip so it
        // doesn't cover the very thing they're trying to grab.
        // Hover styling on the parent stays applied either way.
        var onTitle = !!hit && self._pointOnTitleOf(hit, pt);
        if (hit !== self._hoveredShape || onTitle !== self._hoveredOnTitle) {
          self._setHoveredShape(hit, onTitle);
        }
      };

      // Tap-vs-drag tracker for shape clicks. Records pointerdown
      // over a shape; if the pointer doesn't move past the dead-zone
      // before pointerup, treat it as a tap and fire `_onShapeTap`.
      // If the pointer moves further (a pan-drag), the gesture
      // converts to a drag and we don't fire the tap. OSD owns the
      // actual pan since shapes are `pointer-events: none`.
      self._docPointerDown = function(e) {
        if (e.button !== 0) return;
        // Clicks on Etcher's own chrome (toolbar, popups, tooltip) aren't
        // canvas interactions — don't deselect, box-select, or tap. Without
        // this, clicking the Parameters/colors UI while shapes are selected
        // would wipe the selection (an empty-canvas press → box-select).
        if (e.target.closest &&
            e.target.closest(CHROME_SELECTOR)) {
          return;
        }
        if (!overContainer(e)) return;
        // A handle/title/toolbar/modal element under the cursor
        // handles its own event; don't shadow it with a shape tap.
        if (isInputOwner(e.target, self.overlayWrapper)) return;

        // The press that just closed a text editor: done typing, and the
        // shape stays selected — it is not also a press on the board.
        if (self._pressCommittedText && self.activeTool == null) {
          self._pressCommittedText = false;
          return;
        }

        // Fingers are counted here as well as on the drawing overlay, because
        // in cursor mode that overlay takes no events at all — these document
        // listeners are the whole of the interaction, and the marquee is
        // drawn from them.
        //
        // Two fingers are the canvas's: pan and pinch, the same as under
        // every drawing tool. Reported from a phone: with the select tool
        // out, "you put two fingers down and start panning around and it
        // starts creating a lot of select boxes between the two fingers,
        // selecting and deselecting non-stop" — because each finger opened a
        // marquee of its own and then both of them dragged it.
        self._trackTouch(e, true);
        if (self._multiTouch) {
          self._cancelBoxSelect();
          self._pendingTap = null;
          return;
        }

        // Grabber (hand) tool: the press is a pan, never a shape tap — with
        // one exception. The grabber is how someone looks around a board
        // they aren't editing, and following a link is a viewing action, so
        // a link card stays clickable under it. Nothing is intercepted: the
        // press is only recorded, and `_docPointerMove` drops it the moment
        // the pointer travels, so a pan still pans.
        if (self.activeTool === "grabber") {
          if (!self._linkUnfurlerTapCandidate(e)) return;
          return;
        }
        // Touch: `_hoveredShape` is unreliable. iOS synthesizes a
        // mousemove at the touchend point after every gesture, which
        // fires `_docMouseMove` and leaves the cache pinned to the
        // last-touched shape regardless of where the next finger
        // actually lands. Always hit-test fresh on touch — the next
        // tap on a different shape (or empty space) needs the real
        // coordinate, not the stale hover cache.
        //
        // Mouse / pen: `mousemove` keeps `_hoveredShape` in sync as
        // the cursor moves, so the cache is accurate. The empty-
        // cache branch is only reached on devices without hover at
        // all (rare for non-touch pointers); fall back to a fresh
        // hit-test there too.
        var hit;
        var ptDirect;
        if (e.pointerType === "touch") {
          try { ptDirect = self._toImage(e); } catch (_) { return; }
          hit = self._shapeAt(ptDirect);
        } else {
          hit = self._hoveredShape;
          if (!hit) {
            try { ptDirect = self._toImage(e); } catch (_) { return; }
            hit = self._shapeAt(ptDirect);
          }
        }
        // A shape holds the focus and this press is on another one: the
        // press lets go of it and does nothing else — see `_armedFocusBars`.
        // (A drawing tool's press reaches `_onPointerDown` for that.)
        if (hit && self._armedFocusBars(hit) && self.activeTool == null) {
          if (self.annotationMode) {
            self._clearSelection();
            self._exitEditMode();
            if (self.editingTitleShape) self._exitTitleEditMode();
          }
          return;
        }
        // Repeat taps in one spot reach past the topmost shape. This is the
        // path that picks a *new* selection; tapping the already-editing
        // shape is swallowed by its own listener and cycles in
        // `_startShapeMove` instead, sharing the same `_tapCycle` state.
        if (hit) hit = self._cycleTapTarget(hit, e);
        // Inside the selection's frame but off its ink: the press still
        // takes the whole selection (as it would on one of its shapes).
        if (!hit && !e.shiftKey && self._xf && !self._xf.drag && self.annotationMode &&
            self.activeTool == null) {
          var inPt = ptDirect;
          if (!inPt) { try { inPt = self._toImage(e); } catch (_) { inPt = null; } }
          if (self._xfContains(inPt)) {
            var xs = self._xf.targets;
            if (xs.length > 1 || self.editingShape !== xs[0]) {
              self._startMultiShapeMove(xs[0], e);
            } else {
              self._startShapeMove(xs[0], e);
            }
            return;
          }
        }
        if (!hit) {
          // Empty press in annotation cursor mode on a canvas: begin a
          // marquee box-select. Shift extends the current group; without it
          // the group resets now (a plain click with no drag just deselects).
          if (self.annotationMode && self.activeTool == null &&
              self.handleKind === "canvas") {
            if (!e.shiftKey) self._clearSelection();
            self._exitEditMode();
            self._boxSelect = {
              // Whose marquee this is. A second pointer reporting into it
              // would drag the box to wherever that one happens to be.
              pointerId: e.pointerId,
              startX: e.clientX,
              startY: e.clientY,
              startImg: ptDirect,
              additive: !!e.shiftKey,
              moved: false,
              el: null
            };
            return;
          }
          // Strip / browse: keep the deselect-on-empty-click affordance.
          if (self.annotationMode && self.activeTool == null && !e.shiftKey &&
              self.selectedShapes && self.selectedShapes.length > 0) {
            self._clearSelection();
          }
          return;
        }

        // Multi-select (Shift+click). Toggles the clicked shape in/
        // out of `selectedShapes` and skips the move/edit flow — the
        // user is grouping shapes for a batch action, not interacting
        // with one. Only meaningful in annotation cursor mode; in
        // browse mode or with a draw tool active, shift+click falls
        // through to the existing behavior.
        if (self.annotationMode && self.activeTool == null && e.shiftKey) {
          // Locked shapes can't join a multi-selection (no group move/edit).
          if (hit.readonly) { e.preventDefault(); return; }
          self._armedShiftAdd(hit, e);
          e.preventDefault();
          return;
        }

        // Click on a shape that's part of a multi-selection (no
        // modifier) drags the whole group together. Anything else
        // exits multi-selection and falls through to single-shape
        // behavior.
        if (self.annotationMode && self.activeTool == null &&
            self.selectedShapes && self.selectedShapes.length > 0) {
          if (self._isInSelection(hit)) {
            self._startMultiShapeMove(hit, e);
            return;
          }
          self._clearSelection();
        }

        // In annotation cursor mode, immediately enter shape-move so the
        // user can drag without first tapping to enter edit mode. Only
        // callouts and text shapes naturally bubble pointerdown to the
        // per-shape listener (their inner rect is pointer-events:all);
        // every other kind has `.etcher-shape { pointer-events: none }`
        // and only reaches us here at the doc level. _startShapeMove's
        // onUp falls back to _onShapeTap when no drag happens, so
        // click-to-select keeps working uniformly.
        if (self.annotationMode && self.activeTool == null) {
          self._startShapeMove(hit, e);
          return;
        }

        // A press the armed tool already routed into a same-kind grab
        // (`_onPointerDown`) must not also be tracked as a tap here —
        // touch presses reach both handlers, and two trackers on one
        // gesture would fire `_onShapeTap` twice on release.
        var grabbed = self._sameToolGrab;
        self._sameToolGrab = null;
        if (grabbed && grabbed.pointerId === e.pointerId &&
            Date.now() - grabbed.at < 1000) {
          return;
        }
        // The press is letting go of the focused shape, not picking this one.
        if (self._armedFocusBars(hit)) return;

        // Browse mode (or a draw tool is active): track for click-to-pin
        // only. _pendingTap survives until _docPointerUp; if the pointer
        // didn't move past the dead-zone, _onShapeTap fires.
        self._pendingTap = {
          shape: hit,
          startX: e.clientX,
          startY: e.clientY,
          startedAt: Date.now()
        };
      };
      self._docPointerMove = function(e) {
        if (self._boxSelect) {
          var bs = self._boxSelect;
          if (bs.pointerId != null && e.pointerId !== bs.pointerId) return;
          if (!bs.moved) {
            var bdx = e.clientX - bs.startX, bdy = e.clientY - bs.startY;
            if (bdx * bdx + bdy * bdy < 16) return; // 4px before the marquee shows
            bs.moved = true;
          }
          self._updateBoxSelect(e);
          return;
        }
        if (!self._pendingTap) return;
        var dx = e.clientX - self._pendingTap.startX;
        var dy = e.clientY - self._pendingTap.startY;
        if (dx * dx + dy * dy > 25) self._pendingTap = null; // 5px dead-zone
      };
      self._docPointerUp = function(e) {
        // A finger coming off a pinch finishes nothing: the marquee was
        // abandoned when the second finger landed, and a tap is not what
        // two fingers were doing.
        if (self._multiTouch) {
          self._pendingTap = null;
          return;
        }
        if (self._boxSelect) {
          var bs = self._boxSelect;
          self._boxSelect = null;
          if (bs.el && bs.el.parentNode) bs.el.parentNode.removeChild(bs.el);
          // Drop the preview styling first, then commit: the shapes that are
          // really selected get the class back from `_addToSelection` in the
          // same frame, and anything the marquee had merely been touching
          // goes back to normal instead of keeping a selected look.
          self._clearBoxSelectPreview(bs);
          // A real drag selects the enclosed shapes; a no-move press was
          // just a deselect click (already handled on pointerdown).
          if (bs.moved) self._commitBoxSelect(bs, e);
          return;
        }
        var tap = self._pendingTap;
        self._pendingTap = null;
        if (!tap) return;
        if (Date.now() - tap.startedAt > 500) return;
        // Re-verify the cursor is still over the same shape — guards
        // against the user dragging just under the dead-zone then
        // releasing far from the original shape.
        var pt;
        try { pt = self._toImage(e); } catch (_) { return; }
        if (self._shapeAt(pt) !== tap.shape) return;
        if (!tap.linkOnly) self._onShapeTap(tap.shape);
        self._maybeOpenLinkOnTap(tap.shape);
      };

      // Double-click on text/callout in annotation mode → inline edit.
      // Same container gate + hit-test pattern as the tap handler.
      self._docDblClick = function(e) {
        if (!self.annotationMode) return;
        if (!overContainer(e)) return;
        // Skip if the double-click landed inside a modal or other
        // input-owner — clicking in a comment composer that happens
        // to sit over a text shape shouldn't open the shape's editor.
        if (isInputOwner(e.target, self.overlayWrapper)) return;
        // While a freehand/marker curve is being edited, a double-click on
        // the stroke inserts a new node there (anchor dblclick is handled on
        // the handle itself and stops propagation, so it won't reach here).
        if (self.editingShape &&
            (self.editingShape.kind === "freehand" || self.editingShape.kind === "marker") &&
            self.editingShape.geometry && self.editingShape.geometry.nodes) {
          var ipt;
          try { ipt = self._toImage(e); } catch (_) { return; }
          if (self._freehandInsertNodeAt(self.editingShape, ipt)) {
            e.preventDefault();
            return;
          }
        }
        var pt;
        try { pt = self._toImage(e); } catch (_) { return; }
        var hit = self._shapeAt(pt);
        if (!hit) return;
        hit = self._dblClickTarget(hit, e);
        // With a tool armed, a double-click only reaches the shapes that
        // tool itself edits (same-tool editing — a dimension's label
        // opens under the dimension tool). On everything else, both
        // clicks already belonged to the tool.
        if (self.activeTool != null && !self._armedToolEdits(hit)) return;
        // A link card opens on double-click: the single click that started
        // it selected the card, which is what you need before moving or
        // scaling it. (Under the grabber there is nothing to select, so a
        // single tap opens instead — see `_docPointerDown`.)
        if (self._linkOf(hit)) {
          self._enterEditMode(hit);
          self._openLink(hit);
          return;
        }
        // Every shape that can carry a label opens its editor here, not
        // just the text-ish kinds — double-click is how a label gets
        // created in the first place. `_startTextEdit` no-ops for kinds
        // that have nowhere to put one.
        self._enterEditMode(hit);
        self._startTextEdit(hit);
      };

      document.addEventListener("pointermove", self._docMouseMove);
      document.addEventListener("pointerdown", self._docPointerDown);
      document.addEventListener("pointermove", self._docPointerMove);
      document.addEventListener("pointerup", self._docPointerUp);
      document.addEventListener("dblclick", self._docDblClick);
      // Where the last double-click landed, for the caret of the editor it
      // opens (see `_startTextEdit`).
      self._noteDblClick = function(e) {
        self._lastDblClick = { x: e.clientX, y: e.clientY, t: Date.now() };
      };
      document.addEventListener("dblclick", self._noteDblClick, true);
    },

    _unwireGlobalShapeListeners: function() {
      if (!this._globalShapeListenersWired) return;
      this._globalShapeListenersWired = false;
      if (this._docMouseMove)
        document.removeEventListener("pointermove", this._docMouseMove);
      if (this._docPointerDown)
        document.removeEventListener("pointerdown", this._docPointerDown);
      if (this._noteDblClick) {
        document.removeEventListener("dblclick", this._noteDblClick, true);
        this._noteDblClick = null;
      }
      if (this._valuePreviewOff) {
        document.removeEventListener("pointerdown", this._valuePreviewOff, true);
        this._valuePreviewOff = null;
      }
      if (this._focusPressDown) {
        window.removeEventListener("pointerdown", this._focusPressDown, true);
        window.removeEventListener("pointerup", this._focusPressUp, true);
        window.removeEventListener("pointercancel", this._focusPressUp, true);
      }
      if (this._docPointerMove)
        document.removeEventListener("pointermove", this._docPointerMove);
      if (this._docPointerUp)
        document.removeEventListener("pointerup", this._docPointerUp);
      if (this._docDblClick)
        document.removeEventListener("dblclick", this._docDblClick);
      this._clearBoxSelectPreview();
      if (this._boxSelect && this._boxSelect.el && this._boxSelect.el.parentNode) {
        this._boxSelect.el.parentNode.removeChild(this._boxSelect.el);
      }
      this._boxSelect = null;
      this._docMouseMove = this._docPointerDown = null;
      var dropTarget = this.handle && this.handle.container;
      if (dropTarget && this._dropHandler) {
        dropTarget.removeEventListener("dragover", this._dragOverHandler);
        dropTarget.removeEventListener("drop", this._dropHandler);
        this._dragOverHandler = this._dropHandler = null;
        this._dropWired = false;
      }
      this._docPointerMove = this._docPointerUp = this._docDblClick = null;
      this._pendingTap = null;
      this._setHoveredShape(null, false);
    },

    // Diff the currently-hovered shape against the new one. Fires
    // mouseenter/leave-style transitions: removes `.is-hovered` +
    // schedules tooltip hide on the previous shape; adds
    // `.is-hovered` + shows tooltip on the new one. Pinned tooltips
    // are left alone.
    //
    // `onTitle` (bool) means the cursor is specifically over the
    // shape's title satellite. Hover styling on the parent shape
    // still applies, but the tooltip is suppressed so it doesn't
    // sit on top of the very label the user is trying to grab.
    _setHoveredShape: function(next, onTitle) {
      onTitle = !!onTitle;
      // Nothing is hoverable while a tool is armed — see `_hoverAllowed`.
      // Coerced here rather than refused so a hover already on screen when
      // the tool is armed still gets cleaned up by the transition below.
      if (next && (!this._hoverAllowed() || this._armedFocusBars(next))) {
        next = null;
        onTitle = false;
      }
      var prev = this._hoveredShape;
      var prevOnTitle = this._hoveredOnTitle === true;

      // Hide the tooltip when we just moved ONTO the title (suppress
      // per the rule above so the tooltip doesn't cover the label).
      // Moving off a shape entirely no longer hides — the dwell timer
      // owns closing now, so the tooltip stays for its full window
      // regardless of where the cursor wanders.
      var hideTooltip = next && onTitle && !prevOnTitle;
      // Show the tooltip when:
      // 1. We just moved onto a NEW shape's body.
      // 2. We moved off the title back onto the body of the same shape.
      // (`next` is already null if hover isn't allowed, so an armed tool
      // never reaches either case.)
      var showTooltip =
        next && !onTitle &&
        (next !== prev || (next === prev && prevOnTitle));

      // `_hoveredShape` is assigned FIRST, before anything re-renders. A
      // video decides whether to show its controls by asking who is hovered,
      // so refreshing while the field still held the previous shape drew the
      // old answer both times — controls that never appeared on the one you
      // pointed at, and never left the one you'd left.
      this._hoveredShape = next;
      if (prev !== next && prev && prev.el) {
        prev.el.classList.remove("is-hovered");
        this._refreshImageRing(prev);
        this._refreshMediaChrome(prev);
      }
      if (prev !== next && next && next.el) {
        next.el.classList.add("is-hovered");
        this._refreshImageRing(next);
        this._refreshMediaChrome(next);
      }
      // Docked: a hover changes nothing about the panel — the section is
      // the selection's, and the pointer does not select. What a hover
      // still does here is what it does to the DRAWING: the outline under
      // the cursor, and the connector dots on the shape it is over.
      if (this._tooltipDocked()) {
        this._hoveredOnTitle = onTitle;
        if (this.overlayWrapper) {
          this.overlayWrapper.classList.toggle("is-shape-hovered", !!next);
        }
        this._syncConnectorDots(onTitle ? null : next);
        return;
      }
      if (hideTooltip && !this.tooltipPinned) {
        // Whatever was about to open no longer has anything to describe.
        this._cancelTooltipOpen();
        this._scheduleHideTooltip();
      }
      if (showTooltip && !this.tooltipPinned) this._hoverTooltip(next);

      this._hoveredOnTitle = onTitle;
      if (this.overlayWrapper) {
        this.overlayWrapper.classList.toggle("is-shape-hovered", !!next);
      }
      // Connector dots follow hover: eight bindable points on whatever the
      // cursor is over, gone the moment it leaves. Suppressed on the title
      // satellite — the user is reaching for the label there, and dots
      // under the pointer would take the grab.
      this._syncConnectorDots(onTitle ? null : next);
    },

    // Hover belongs to the cursor tool. Hover says "this shape is what a
    // press would act on" — and with a tool armed it isn't: the press goes
    // to the tool. So with anything armed the blue hover outline, the
    // tooltip and a video's hover transport all stay off, and the pointer
    // is aiming at where the next shape GOES rather than at what is
    // already there. (Connector dots were never on in this state —
    // `_connectorsAvailableFor` already requires `activeTool == null`.)
    //
    // Deliberate paths are untouched: the host's `selectShape(uuid)` pin,
    // and the re-show after a handle drag, are things someone asked for
    // rather than something the pointer wandered into. Tap-to-select is
    // unaffected too — it falls back to a fresh hit-test when the hover
    // cache is empty.
    _hoverAllowed: function() {
      if (this.annotationMode && this.activeTool != null) return false;
      // Panel-docked hosts: the tooltip lives in the style panel, which is
      // only up in annotation mode — so outside it a hover has nothing to
      // show, and an outline on a shape nobody can act on reads as the
      // picture lighting up under the cursor for no reason. The shapes are
      // part of the image until Etcher is switched on.
      if (this._tooltipDocked() && !this.annotationMode) return false;
      return true;
    },

    // True iff `pt` (image-px) lies inside `shape`'s title satellite
    // (only present for rect/circle/polygon/freehand with a non-blank
    // `metadata.title`). Used to suppress the tooltip while the user
    // is interacting with the movable title label.
    _pointOnTitleOf: function(shape, pt) {
      var box = shape && shape.titleGroup && shape._renderedTitleImage;
      if (!box) return false;
      return pt.x >= box.x && pt.x <= box.x + box.w &&
             pt.y >= box.y && pt.y <= box.y + box.h;
    },

    // Any tooltip button with a `data-etcher-action` other than Etcher's own
    // "delete" belongs to the HOST (slot HTML is consumer-authored, and the
    // tooltip's click handler stops propagation, so ordinary delegated
    // listeners never see these clicks). Re-dispatch as a bubbling
    // CustomEvent on the layer host element:
    //
    //   etcher:tooltip-action  { detail: { fresco_id, uuid, action } }
    //
    // so a consumer can wire "reply" / "resolve" / anything into its own
    // app without forking the tooltip.
    _dispatchTooltipAction: function(action) {
      var shape = this._tooltipShape;
      if (!shape || !this.el) return;
      try {
        this.el.dispatchEvent(new CustomEvent("etcher:tooltip-action", {
          bubbles: true,
          detail: {
            fresco_id: this.frescoId || null,
            uuid: shape.uuid || null,
            action: action
          }
        }));
      } catch (_) {}
    },

    _showTooltipFor: function(shape) {
      var tip = this.tooltipEl;
      if (!tip) return;

      this._cancelHideTooltip();
      this._cancelTooltipOpen();
      // A show landing inside a fade-out must not be cleaned up by it —
      // either half of one. The timer is the anchored fade; the frame is
      // the docked section leaving the panel. Both end by flipping
      // `display` unless the visible class is back, and the class only
      // arrives a frame from now, so whichever of them is still pending
      // would put this show away before it was ever painted.
      if (this._tooltipFadeTimer) {
        clearTimeout(this._tooltipFadeTimer);
        this._tooltipFadeTimer = null;
      }
      if (this._tooltipHideFrame) {
        cancelAnimationFrame(this._tooltipHideFrame);
        this._tooltipHideFrame = null;
      }
      this._tooltipShape = shape;

      // Delegate the three content regions to slot functions. Consumer
      // overrides win over defaults; a slot returning null/undefined
      // omits its row entirely.
      var headerHtml = resolveSlot("header", shape);
      var bodyHtml = resolveSlot("body", shape);
      var footerHtml = resolveSlot("footer", shape);

      var html = '<div class="etcher-tooltip-header">';
      html += '<span class="etcher-tooltip-kind">' + (headerHtml || "") + '</span>';
      // Host-declared action buttons, rendered as REAL buttons on the same
      // row as the trash — `window.Etcher.tooltipActions = (shape) =>
      // [{action, title, icon}]`. Clicks flow through the tooltip's click
      // handler and are re-dispatched as `etcher:tooltip-action` (see
      // `_dispatchTooltipAction`), so the host wires behavior without
      // touching the tooltip. Errors and non-arrays degrade to no buttons.
      var hostActions = [];
      if (typeof window.Etcher.tooltipActions === "function") {
        try {
          hostActions = window.Etcher.tooltipActions(shape) || [];
        } catch (e) {
          console.warn("[Etcher] tooltipActions threw:", e);
          hostActions = [];
        }
      }
      if (Array.isArray(hostActions)) {
        hostActions.forEach(function(a) {
          if (!a || !a.action || a.action === "delete") return;
          var label = escapeHtml(a.title || a.action);
          html += '<button type="button" class="etcher-tooltip-btn"' +
                  ' data-etcher-action="' + escapeHtml(a.action) + '"' +
                  ' title="' + label + '" aria-label="' + label + '">' +
                  (a.icon || label) + '</button>';
        });
      }
      // Trash button stays Etcher-controlled — delete is a core UX,
      // consumers shouldn't have to reimplement it. Only shown for
      // persisted shapes (temp drafts have no server-side uuid yet), and
      // never for locked (readonly) shapes — the viewer can't delete them.
      if (shape.uuid && !shape.readonly) {
        html += '<button type="button" class="etcher-tooltip-btn etcher-tooltip-delete"' +
                ' data-etcher-action="delete" title="Delete annotation"' +
                ' aria-label="Delete annotation">' + ICONS.trash + '</button>';
      }
      html += '</div>';

      if (footerHtml) {
        html += '<div class="etcher-tooltip-meta">' + footerHtml + '</div>';
      }
      // Body slot HTML is injected as-is. Consumers can wrap it in
      // any layout they want; Etcher exposes `.etcher-tooltip-body`,
      // `.etcher-tooltip-thumb`, `.etcher-tooltip-text`, and
      // `.etcher-tooltip-quote` as opt-in styling primitives.
      if (bodyHtml) {
        html += bodyHtml;
      }

      tip.innerHTML = html;

      // If the thumbnail image is broken (variant URL 404'd, blocked by
      // CSP, etc.), swap it for the paperclip placeholder rather than
      // leaving the user-agent's broken-image icon. Wired up after
      // innerHTML so the JS we attach isn't inlined and HTML-escaped.
      var thumbImg = tip.querySelector("img.etcher-tooltip-thumb");
      if (thumbImg) {
        thumbImg.addEventListener("error", function() {
          var span = document.createElement("span");
          span.className = "etcher-tooltip-thumb etcher-tooltip-thumb-icon";
          span.innerHTML = ICONS.paperclip;
          if (thumbImg.parentNode) thumbImg.parentNode.replaceChild(span, thumbImg);
        });
      }

      // Anchor it to the shape. Split out so the render loop can re-run
      // just the positioning math on every pan/zoom frame (the tooltip
      // tracks the shape) without rebuilding content or restarting the
      // dwell timer.
      this._positionTooltip(shape);

      // Where the cursor was when this opened — the origin the peek
      // measures against (see _tooltipPeekMove).
      this._tooltipCursorOrigin = this._lastPointerClient
        ? { x: this._lastPointerClient.x, y: this._lastPointerClient.y }
        : null;

      // Grace window after show: the next ~250 ms ignores
      // `_scheduleHideTooltip` calls. Defeats the iOS-synthesized
      // mousemove-on-just-shown-tooltip race that fires
      // `_setHoveredShape(null)` → `_scheduleHideTooltip` before
      // the tooltip's own `mouseenter` (which would cancel the
      // hide) gets a chance to run. Without this, the first
      // hover often shows-then-hides in the same frame.
      this._tooltipShowGraceUntil = Date.now() + 250;

      // Auto-close after the dwell window. Pinning cancels this (see
      // `_pinTooltipFor`); so does hovering the tooltip itself.
      this._startTooltipAutoClose();

      // Painted in on the NEXT frame: the element has to be laid out at
      // opacity 0 before the class can transition it, or the browser
      // collapses both into one style resolution and it simply appears.
      var fadeIn = tip;
      var selfShow = this;
      // Held so a hide arriving before this frame can cancel it. Without
      // that, a show and a hide issued in the same task both survive into
      // the frame — the paint lands first and the hide, which only flips
      // `display` when the class is absent, stands down. The element is
      // then left on screen with `_tooltipShape` already null, so every
      // later sync sees nothing to hide and the section outlives whatever
      // it was describing. (`_enterEditMode` does exactly that pair:
      // docked, it puts the shape's actions in the panel, then hides the
      // anchored bubble it would otherwise have opened.)
      if (this._tooltipFadeInFrame) {
        cancelAnimationFrame(this._tooltipFadeInFrame);
        this._tooltipFadeInFrame = null;
      }
      if (typeof requestAnimationFrame === "function") {
        this._tooltipFadeInFrame = requestAnimationFrame(function() {
          selfShow._tooltipFadeInFrame = null;
          if (fadeIn.style.display !== "none") fadeIn.classList.add("is-visible");
        });
      } else {
        tip.classList.add("is-visible");
      }

      this._dispatch("etcher:tooltip-show", {
        uuid: shape.uuid || null,
        anchor: { x: parseFloat(tip.style.left) || 0, y: parseFloat(tip.style.top) || 0 }
      });
    },

    // Hover asks through here; everything else (a pin, the tooltip coming
    // back after a handle drag) still calls _showTooltipFor directly and
    // appears at once, because the user just acted on that shape and is
    // waiting for the answer.
    _hoverTooltip: function(shape) {
      if (!shape) return;
      // Docked: hover shows nothing. The panel's section is the
      // selection's, and a cursor passing over a shape is not a choice —
      // see `_syncDockedTooltip`. (The hover OUTLINE stays: that is the
      // drawing answering the pointer, which costs the panel nothing.)
      if (this._tooltipDocked()) return;
      var up = this.tooltipEl && this.tooltipEl.style.display !== "none";
      // One already open: switch now. The delay is there to stop tooltips
      // appearing uninvited, not to slow down reading a second one.
      if (up) { this._showTooltipFor(shape); return; }
      var self = this;
      this._cancelTooltipOpen();
      this._tooltipOpenTimer = setTimeout(function() {
        self._tooltipOpenTimer = null;
        // Still the shape under the cursor? A hover that moved on in the
        // meantime has already cancelled this, but a pin or a hide landing
        // in the same window must win too.
        if (self.tooltipPinned) return;
        // Either signal counts. The shared pointermove path sets
        // `_hoveredShape`; the element's own mouseenter sets the class —
        // and a shape that moves under a still cursor gets the second
        // without the first, which asking only for `_hoveredShape` would
        // read as "not hovered" and swallow the tooltip entirely.
        var stillOn = self._hoveredShape === shape ||
          !!(shape.el && shape.el.classList &&
             shape.el.classList.contains("is-hovered"));
        if (!stillOn) return;
        self._showTooltipFor(shape);
      }, TOOLTIP_OPEN_DELAY_MS);
    },

    _cancelTooltipOpen: function() {
      if (this._tooltipOpenTimer) {
        clearTimeout(this._tooltipOpenTimer);
        this._tooltipOpenTimer = null;
      }
    },

    // Place the (already-populated, already-visible) tooltip element
    // above its anchor shape, flipping below + clamping horizontally to
    // stay inside the container. Pure positioning — no content rebuild,
    // no timer changes — so it's cheap enough to call every animation
    // frame from `_renderAll`, which is what keeps the tooltip glued to
    // the shape during pan/zoom instead of floating in stale space.
    // The point a tooltip should hang off for an open stroke: the middle of
    // the LINE, measured along it — not the middle of the box it happens to
    // occupy. On a diagonal those are nowhere near each other. A line drawn
    // corner to corner has a bbox whose top-centre sits out in empty canvas,
    // and that is exactly where the tooltip used to appear: floating far off
    // the thing it describes.
    //
    // Closed shapes are unaffected. A rectangle's or a circle's bbox top IS
    // its top, so they keep the anchor they have always had.
    _strokeMidpointImage: function(shape) {
      var g = shape && shape.geometry;
      if (!g) return null;
      var pts = null;
      switch (shape.kind) {
        case "line":
        case "dimension":
          if (!g.a || !g.b) return null;
          pts = [{ x: g.a[0], y: g.a[1] }, { x: g.b[0], y: g.b[1] }];
          break;
        case "arrow":
          // The drawn route — the curve through the bends — so a bent
          // arrow measures along the line it actually shows.
          pts = this._arrowRoute(g);
          break;
        case "marker":
        case "freehand":
          pts = (this._freehandFlatten(g) || []).map(function(p) {
            return { x: p[0], y: p[1] };
          });
          break;
        default:
          return null;
      }
      if (!pts || !pts.length) return null;
      if (pts.length === 1) return { x: pts[0].x, y: pts[0].y };

      // Half way by ARC LENGTH, not by index. A stroke is sampled densely
      // where the hand moved slowly and sparsely where it moved fast, so
      // the middle sample is wherever the sampling bunched up — which on a
      // hesitant stroke is not the middle of the line at all.
      var seg = [], total = 0, i, dx, dy;
      for (i = 0; i < pts.length - 1; i++) {
        dx = pts[i + 1].x - pts[i].x;
        dy = pts[i + 1].y - pts[i].y;
        var len = Math.sqrt(dx * dx + dy * dy);
        seg.push(len);
        total += len;
      }
      if (!(total > 0)) return { x: pts[0].x, y: pts[0].y };

      var half = total / 2, run = 0;
      for (i = 0; i < seg.length; i++) {
        if (run + seg[i] >= half) {
          var t = seg[i] > 0 ? (half - run) / seg[i] : 0;
          return {
            x: pts[i].x + (pts[i + 1].x - pts[i].x) * t,
            y: pts[i].y + (pts[i + 1].y - pts[i].y) * t,
            // The segment the midpoint fell on. Callers need the direction
            // the stroke RUNS IN there, and taking it from a pair of image
            // points rather than an angle means the projection to the
            // screen carries any rotation and zoom for free.
            ax: pts[i].x, ay: pts[i].y,
            bx: pts[i + 1].x, by: pts[i + 1].y
          };
        }
        run += seg[i];
      }
      var last = pts[pts.length - 1], prev = pts[pts.length - 2];
      return {
        x: last.x, y: last.y,
        ax: prev.x, ay: prev.y, bx: last.x, by: last.y
      };
    },

    // The stroke width a shape is actually drawn at, in container px — what
    // the tooltip has to stand clear of.
    _renderedStrokePx: function(shape) {
      if (this._isShaftKind(shape && shape.kind)) {
        return this._shaftStrokePx(shape, LINE_WEIGHT_PX);
      }
      var s = (shape && shape.style) || {};
      var w = typeof s.width === "number" && isFinite(s.width) ? s.width : 2;
      if ((shape && shape.kind === "marker") || s.width_units === "canvas") {
        var scale = 1;
        try { scale = this._markerScale() || 1; } catch (_) { scale = 1; }
        w = w * scale;
      }
      return Math.max(0.4, w);
    },

    // Every point the tooltip has to stay clear of, in container px: the
    // stroke's own path, plus the label and badge that travel with it.
    //
    // The path is the CENTRE line, so callers still have to allow for half
    // the stroke width on top. And the fill needs no separate treatment: a
    // body is bounded by its outline, so anything clear of the outline is
    // clear of the body too.
    _tooltipClearancePts: function(shape, containerRect) {
      var self = this;
      var g = shape && shape.geometry;
      var pts = null;
      switch (shape && shape.kind) {
        case "line":
        case "dimension":
          if (g && g.a && g.b) {
            pts = [{ x: g.a[0], y: g.a[1] }, { x: g.b[0], y: g.b[1] }];
          }
          break;
        case "arrow":
          pts = this._arrowRoute(g);
          break;
        case "marker":
        case "freehand":
          // Flattened, so a curve is followed where it actually goes rather
          // than between its sparse anchors — which is the whole point for a
          // stroke that wanders back across itself.
          pts = (this._freehandFlatten(g) || []).map(function(p) {
            return { x: p[0], y: p[1] };
          });
          break;
      }

      var out = [];
      (pts || []).forEach(function(p) {
        var c;
        try { c = self._imageToContainer(p); } catch (_) { return; }
        if (c) out.push(c);
      });
      [shape.titleGroup, shape._badgeEl].forEach(function(extra) {
        if (!extra || !extra.getBoundingClientRect) return;
        var r = extra.getBoundingClientRect();
        if (!r.width && !r.height) return;
        var l = r.left - containerRect.left, t = r.top - containerRect.top;
        var rr = r.right - containerRect.left, b = r.bottom - containerRect.top;
        out.push({ x: l, y: t }, { x: rr, y: t }, { x: rr, y: b }, { x: l, y: b });
      });
      return out;
    },

    // Park the tooltip beside the middle of a stroke: as near the middle as
    // it can be without lying across it.
    //
    // Hanging it above the bounding box put it out in empty canvas for a
    // diagonal; hanging it ON the midpoint covered the very line it
    // describes. Beside is both — pushed off the line along the PERPENDICULAR
    // at the midpoint, by exactly far enough that the bubble's own box stops
    // at the line and no further.
    //
    // That distance is the rectangle's support in the normal direction:
    // halfW·|nx| + halfH·|ny|. Offset a convex box along a normal by its own
    // support and it lands entirely on one side of the line through that
    // point — touching, never crossing. Which is the closest it can sit and
    // still not cover the stroke.
    //
    // Returns false when it has nothing to work with, so the caller falls
    // back to the bounding box.
    _placeTooltipBesideStroke: function(shape, tip, geom) {
      if (this.handleKind === "strip") return false;
      var mid = this._strokeMidpointImage(shape);
      if (!mid) return false;

      var midC, aC, bC;
      try {
        midC = this._imageToContainer({ x: mid.x, y: mid.y });
        aC = this._imageToContainer({ x: mid.ax, y: mid.ay });
        bC = this._imageToContainer({ x: mid.bx, y: mid.by });
      } catch (_) { return false; }
      if (!midC || !aC || !bC) return false;

      var dx = bC.x - aC.x, dy = bC.y - aC.y;
      var len = Math.sqrt(dx * dx + dy * dy);
      // A zero-length segment has no direction to be perpendicular to.
      if (!(len > 0.0001)) return false;

      // Of the two normals, take the one pointing UP. A tooltip above what
      // it labels is the convention everywhere else in this file, and for a
      // horizontal stroke it reproduces exactly the old placement.
      var nx = -dy / len, ny = dx / len;
      if (ny > 0) { nx = -nx; ny = -ny; }

      var halfW = geom.width / 2, halfH = geom.height / 2;
      var clearance = this._renderedStrokePx(shape) / 2 + 6;
      var clearPts = this._tooltipClearancePts(shape, geom.containerRect);

      // How far along a normal the bubble's CENTRE has to sit.
      //
      // `support` — halfW·|nx| + halfH·|ny| — is how far the bubble's own box
      // reaches in that direction, so offsetting a convex box by its support
      // lands it exactly touching the line through the origin point.
      //
      // `reach` is how far the SHAPE gets in that direction: the largest
      // projection of any of its points onto the normal. Clearing the
      // tangent at the midpoint was enough for a straight line and not for
      // anything that wanders — a freehand loop doubles back across its own
      // middle, so the bubble sat on a different part of the same stroke.
      // Going beyond the furthest point puts the bubble past the whole
      // shape, body included, in that direction.
      //
      // For a straight line both are zero-reach, so this comes out at
      // exactly the placement it had before: hugging the line.
      function offsetFor(sx2, sy2) {
        var support = halfW * Math.abs(sx2) + halfH * Math.abs(sy2);
        var reach = 0;
        for (var i = 0; i < clearPts.length; i++) {
          var proj = (clearPts[i].x - midC.x) * sx2 + (clearPts[i].y - midC.y) * sy2;
          if (proj > reach) reach = proj;
        }
        var d = reach + support + clearance;
        return { x: midC.x + sx2 * d, y: midC.y + sy2 * d, d: d };
      }

      function fits(at) {
        return at.x - halfW >= 4 && at.x + halfW <= geom.containerWidth - 4 &&
               at.y - halfH >= 4 && at.y + halfH <= geom.containerHeight - 4;
      }

      // Both normals are candidates. A stroke can wander much further to one
      // side than the other, and the near side is the one to use — "as close
      // as it can be" is the whole ask. Ties go to the upward normal, which
      // is what keeps a straight line's tooltip above it rather than below.
      var up = offsetFor(nx, ny);
      var down = offsetFor(-nx, -ny);
      var at = down.d < up.d ? down : up;
      // …unless the near side has no room in the viewer, in which case the
      // far side beats a bubble hanging off the edge.
      if (!fits(at)) {
        var other = at === up ? down : up;
        if (fits(other)) at = other;
      }

      // Last resort, when neither side fits: keep the bubble on screen. This
      // is the one case that can put it back over the shape — a shape big
      // enough to leave nowhere clear — and a tooltip you cannot read is
      // worse than one that overlaps.
      var minX = halfW + 4, maxX = geom.containerWidth - halfW - 4;
      at.x = maxX < minX ? minX : Math.max(minX, Math.min(maxX, at.x));
      var minY = halfH + 4, maxY = geom.containerHeight - halfH - 4;
      at.y = maxY < minY ? minY : Math.max(minY, Math.min(maxY, at.y));

      tip.style.left = (at.x + geom.scrollLeft) + "px";
      tip.style.top = (at.y + geom.scrollTop) + "px";
      tip.style.transform = "translate(-50%, -50%)";
      return true;
    },

    // Nudge a just-placed tooltip clear of the style panel and any open
    // popup. Zoom and pan can park a shape underneath the panel; brushing
    // its exposed edge on the way to a control then opened a tooltip ON
    // TOP of the very menu being aimed for — the tooltip out-stacks the
    // panel, so the colour controls vanished behind it. The panel hangs on
    // the right edge, so clear ground is to its left; the shift keeps the
    // container's left edge as a floor rather than trading one covered
    // control for an off-screen tooltip.
    _keepTooltipClearOfChrome: function(tip, containerRect) {
      var self = this;
      var blockers = [this.stylePanel, this.toolsPopup, this.colorsPopup,
                      this.paramsPopup, this.markerPopup];
      blockers.forEach(function(el) {
        if (!el || !el.getBoundingClientRect) return;
        var b = el.getBoundingClientRect();
        if (!b.width || !b.height) return; // hidden
        var t = tip.getBoundingClientRect();
        var overlaps = t.right > b.left && t.left < b.right &&
                       t.bottom > b.top && t.top < b.bottom;
        if (!overlaps) return;
        var dx = t.right - (b.left - 8);
        var left = parseFloat(tip.style.left) || 0;
        var newLeft = left - dx;
        // Floor: never push the tooltip's visible box past the container's
        // left edge. `left` may be centre- or corner-anchored depending on
        // the transform, so bound with the measured offset instead.
        var minLeft = left - (t.left - (containerRect.left + 4));
        if (newLeft < minLeft) newLeft = minLeft;
        tip.style.left = newLeft + "px";
      });
    },

    _positionTooltip: function(shape) {
      var tip = this.tooltipEl;
      if (!tip || !shape || !shape.el) return;

      // Docked: the tooltip is a section of the style panel, so it has no
      // coordinates of its own — it sits in the panel's flow under the
      // stroke and colour rows. Moved rather than copied, so the delete
      // button, the host's actions and the label editor keep working
      // exactly as they do anchored; the shape is not consulted at all,
      // and there is nothing for the per-frame re-anchor to do.
      if (this._tooltipDocked()) {
        tip.classList.add("is-docked");
        tip.style.display = "block";
        tip.style.left = "";
        tip.style.top = "";
        if (this.stylePanel && tip.parentNode !== this.stylePanel) {
          this.stylePanel.appendChild(tip);
        }
        return;
      }
      tip.classList.remove("is-docked");

      // Anchor the tooltip just above the shape's bounding rect, in
      // container px. `getBoundingClientRect` reflects the current
      // post-animation position so the tooltip sits where the shape is
      // *now*, not where it started.
      //
      // The tooltip is `position: absolute` inside the (relatively-
      // positioned) container, so `style.top` is interpreted in
      // CONTENT space — not viewport space. For strip mode, where
      // the container itself scrolls, we add `scrollTop` /
      // `scrollLeft` so the tooltip lands at the right CONTENT
      // coords. Canvas-mode containers pan via CSS transform and
      // never accumulate scroll, so adding zero is a safe no-op
      // — no per-mode branching needed.
      var shapeRect = shape.el.getBoundingClientRect();
      var containerRect = this.handle.container.getBoundingClientRect();
      // A video's element is only its transport strip, pinned to the bottom
      // of the picture — anchoring to that put the tooltip in the middle of
      // the video, over the very thing it labels. Media anchors to the box
      // the shape actually occupies instead.
      if (this._isMediaKind(shape.kind)) {
        var mBox = this._shapeBBoxImagePx(shape);
        if (mBox) {
          var mTL = this._imageToContainer({ x: mBox.x, y: mBox.y });
          var mBR = this._imageToContainer({
            x: mBox.x + mBox.w, y: mBox.y + mBox.h
          });
          shapeRect = {
            left: containerRect.left + Math.min(mTL.x, mBR.x),
            top: containerRect.top + Math.min(mTL.y, mBR.y),
            width: Math.abs(mBR.x - mTL.x),
            height: Math.abs(mBR.y - mTL.y)
          };
          shapeRect.bottom = shapeRect.top + shapeRect.height;
        }
      }
      // The anchor box is the whole ANNOTATION, not just the shape element:
      // the label sibling (titleGroup) usually floats above the body and the
      // badge hangs off its corner — anchoring to the shape alone opened the
      // tooltip exactly on top of the label it was describing. Union them so
      // "above" means above everything the annotation draws (and the flip
      // below clears all of it too).
      var uLeft = shapeRect.left;
      var uTop = shapeRect.top;
      var uRight = shapeRect.left + shapeRect.width;
      var uBottom = shapeRect.top + shapeRect.height;
      [shape.titleGroup, shape._badgeEl].forEach(function(extra) {
        if (!extra || !extra.getBoundingClientRect) return;
        var r = extra.getBoundingClientRect();
        if (!r.width && !r.height) return;
        if (r.left < uLeft) uLeft = r.left;
        if (r.top < uTop) uTop = r.top;
        if (r.right > uRight) uRight = r.right;
        if (r.bottom > uBottom) uBottom = r.bottom;
      });
      shapeRect = {
        left: uLeft,
        top: uTop,
        width: uRight - uLeft,
        height: uBottom - uTop,
        bottom: uBottom
      };

      var sx = this.handle.container.scrollLeft || 0;
      var sy = this.handle.container.scrollTop  || 0;

      // Shown before measuring: `display: none` measures zero.
      tip.style.display = "block";

      // An open stroke parks the tooltip beside the middle of its line.
      // A closed shape keeps the bounding box: a rectangle's box top IS its
      // top, so "above it" is already both near and clear.
      var measured = tip.getBoundingClientRect();
      var placed = this._placeTooltipBesideStroke(shape, tip, {
        width: measured.width,
        height: measured.height,
        containerRect: containerRect,
        containerWidth: containerRect.width,
        containerHeight: containerRect.height,
        scrollLeft: sx,
        scrollTop: sy
      });
      if (placed) {
        this._keepTooltipClearOfChrome(tip, containerRect);
        return;
      }

      var anchorX = shapeRect.left + shapeRect.width / 2 - containerRect.left;
      var anchorTop = shapeRect.top - containerRect.top;
      var anchorBottom = shapeRect.bottom - containerRect.top;

      var x = anchorX + sx;
      var aboveY = anchorTop - 8 + sy;
      tip.style.left = x + "px";
      tip.style.top = aboveY + "px";
      tip.style.transform = "translate(-50%, -100%)";

      // If the tooltip extends past the container's top edge (shape is
      // near the top), flip it to sit below the shape instead. Measure
      // after the display:block above so offsetHeight is accurate; if
      // the flip is needed, swap the y-translate too so `top` lands at
      // the shape's bottom edge rather than at its top minus tip
      // height. 4px breathing room either way.
      var tipRect = tip.getBoundingClientRect();
      if (tipRect.top < containerRect.top + 4) {
        var belowY = anchorBottom + 8 + sy;
        tip.style.top = belowY + "px";
        tip.style.transform = "translate(-50%, 0)";
        tipRect = tip.getBoundingClientRect();
      }

      // Horizontal clamp — keep the tooltip's visible bbox inside the
      // container. `transform: translate(-50%, …)` centers on `left`,
      // so the safe range for `x` is [halfW+4, containerWidth-halfW-4].
      var halfW = tipRect.width / 2;
      var minX = halfW + 4;
      var maxX = containerRect.width - halfW - 4;
      if (maxX < minX) {
        // Tooltip wider than the container — pin to the left edge with
        // a buffer; the right side will overflow gracefully.
        x = minX;
      } else {
        if (x < minX) x = minX;
        else if (x > maxX) x = maxX;
      }
      tip.style.left = x + "px";
      this._keepTooltipClearOfChrome(tip, containerRect);
    },

    // Cursor moved on: start closing, even though it is still over the
    // same shape. The tooltip earned its place by the cursor being still
    // and gives it up the same way. Runs on the hover move path AFTER the
    // chrome check — a move onto the tooltip never reaches here — and the
    // close is the usual 180ms bridge into the usual fade, so a reach that
    // lands on the tooltip cancels it on arrival.
    // Where a tooltip goes: beside its shape (default), or in the style
    // panel, under the stroke and colour controls. A host whose users are
    // annotating a photograph rather than inspecting a diagram does not
    // want a box appearing over the picture at all — and the panel is
    // already the place that answers "what is selected and what can I do
    // to it", so the shape's own actions belong with the rest of them.
    // A host's call about its own users, not something Etcher should
    // decide for everyone: opt-in, and every other consumer keeps exactly
    // what it has.
    //
    // The panel is only up in annotation mode, so this also means a
    // viewer who has not turned Etcher on hovers nothing and sees
    // nothing — the shapes are part of the picture until they are being
    // worked on (see `_hoverAllowed`).
    _tooltipDocked: function() {
      return !!(this.el && this.el.dataset &&
                this.el.dataset.tooltipDock === "panel");
    },

    // What the panel's shape section should be showing, from state rather
    // than from a sequence of timers: the SELECTED shape, or nothing.
    //
    // Selection is the whole rule. Click a shape and its actions are in
    // the panel for as long as it is selected, with nothing counting
    // down; click away and the section goes.
    //
    // Hover used to borrow the section to preview whatever the cursor
    // crossed. It read as flicker: pass the pointer over a board on the
    // way to somewhere else and the panel grows a section, swaps it for
    // the next shape, then hands it back — movement at the edge of
    // vision for something nobody asked to see. A preview of a shape you
    // have not chosen is not worth a change of shape in the panel.
    _syncDockedTooltip: function() {
      if (!this._tooltipDocked()) return;
      var want = this.editingShape || null;

      if (!want) {
        if (this._tooltipShape) this._hideTooltip();
        return;
      }
      var showing = this.tooltipEl && this.tooltipEl.style.display !== "none";
      if (this._tooltipShape === want && showing) return;
      this._showTooltipFor(want);
    },

    _tooltipPeekMove: function(e) {
      // The peek exists because an anchored tooltip covers the drawing —
      // a docked one covers nothing, so it can simply follow the hover
      // and leave when the cursor does (the close-on-leave paths below).
      if (this._tooltipDocked()) return;
      if (this.tooltipPinned) return;
      var tip = this.tooltipEl;
      if (!tip || tip.style.display === "none") return;
      // Already closing — re-arming on every move would push the close
      // further out the more the user moved, which is backwards.
      if (this._tooltipTimer) return;
      var origin = this._tooltipCursorOrigin;
      if (!origin) return;
      var dx = e.clientX - origin.x;
      var dy = e.clientY - origin.y;
      if (dx * dx + dy * dy < TOOLTIP_PEEK_PX * TOOLTIP_PEEK_PX) return;
      this._scheduleHideTooltip();
    },

    _scheduleHideTooltip: function() {
      // Pinned tooltips never auto-close — only an explicit click action
      // (same shape again, another shape, or outside) closes them.
      if (this.tooltipPinned) return;
      // Docked: nothing "hides" as such — the one state function decides
      // what the panel should show and does it now. A selected shape's
      // section holds (it is not on a clock and not the cursor's to
      // close, which is what makes its comment and delete buttons
      // reachable at all); a hover preview gives the section back to the
      // selection, or clears it when there is none. Routing a bare hide
      // through here instead would blank a live selection's section.
      if (this._tooltipDocked()) {
        this._syncDockedTooltip();
        return;
      }
      // Grace window from the most recent `_showTooltipFor` — no-op
      // for hides scheduled within ~250 ms of show (see comment in
      // `_showTooltipFor` for the iOS race this prevents).
      if (this._tooltipShowGraceUntil && Date.now() < this._tooltipShowGraceUntil) return;
      var self = this;
      self._cancelHideTooltip();
      // 180ms is long enough for a Fitts'-friendly diagonal move from
      // a small shape edge up to the tooltip without feeling laggy when
      // intentionally moving away.
      self._tooltipTimer = setTimeout(function() {
        self._tooltipTimer = null;
        self._hideTooltip();
      }, 180);
    },

    _cancelHideTooltip: function() {
      if (this._tooltipTimer) {
        clearTimeout(this._tooltipTimer);
        this._tooltipTimer = null;
      }
    },

    // Fixed-dwell auto-close for hover-shown tooltips. Starts on show
    // and fires `TOOLTIP_DWELL_MS` later regardless of cursor position
    // — this is what makes a tooltip "stay for 5 seconds, then close"
    // rather than vanishing the moment the cursor leaves the shape.
    // Restarting (each new show, or leaving the tooltip) resets the
    // window from scratch.
    _startTooltipAutoClose: function() {
      this._cancelTooltipAutoClose();
      // Docked: nothing times out. The section is only ever the selected
      // shape's now, and a panel row that disappeared while the thing it
      // describes was still selected is exactly the "it doesn't always
      // work" this mode answers. The dwell clock was for hover previews,
      // and there are none.
      if (this._tooltipDocked()) return;
      var self = this;
      this._tooltipAutoCloseTimer = setTimeout(function() {
        self._tooltipAutoCloseTimer = null;
        // Docked: a preview that times out hands the section back to the
        // selection rather than emptying the panel (the same rule every
        // other close follows here).
        if (self._tooltipDocked()) self._syncDockedTooltip();
        else self._hideTooltip();
      }, TOOLTIP_DWELL_MS);
    },

    _cancelTooltipAutoClose: function() {
      if (this._tooltipAutoCloseTimer) {
        clearTimeout(this._tooltipAutoCloseTimer);
        this._tooltipAutoCloseTimer = null;
      }
    },

    _hideTooltip: function() {
      var wasVisible = this.tooltipEl && this.tooltipEl.style.display !== "none";
      var hidShape = this._tooltipShape;
      this._cancelHideTooltip();
      this._cancelTooltipAutoClose();
      this._cancelTooltipOpen();
      // …and the paint a show left waiting for the next frame. It would
      // otherwise land after this hide and re-open what we just closed.
      if (this._tooltipFadeInFrame) {
        cancelAnimationFrame(this._tooltipFadeInFrame);
        this._tooltipFadeInFrame = null;
      }
      // _hideTooltip is the universal teardown; make sure pin state is
      // also reset so the next click-to-pin starts clean.
      this.tooltipPinned = false;
      this._removeTooltipOutsideClickHandler();
      this._tooltipShape = null;
      if (this.tooltipEl) {
        // Fade out, then leave the layout. `display` is what the rest of
        // the file reads as "is it up", so it flips only once the paint
        // is done — and immediately if it was not showing anyway.
        var tipEl = this.tooltipEl;
        tipEl.classList.remove("is-visible");
        if (this._tooltipFadeTimer) clearTimeout(this._tooltipFadeTimer);
        // Docked, the tooltip is a SECTION of the style panel — it holds
        // panel height, so when it leaves the panel changes shape. The rows
        // around it change too: losing the selection puts the fill row back,
        // through the frame-coalesced `_scheduleStyleInspectorSync`. Fading
        // out on a 110ms timer put those two in different paints — the panel
        // grew by a row with the section still in place, then collapsed a
        // tenth of a second later, which is the flash on every click-away.
        //
        // So the docked section leaves on the NEXT FRAME, which is the frame
        // the coalesced sync already owns: both registered from the same
        // task, both run before the same paint, and the panel changes shape
        // once. Same guard as the fade path — a show inside the window owns
        // the element and the hide stands down. The fade-IN is untouched;
        // `display` is set before the opacity runs there, so it costs no
        // layout change.
        var docked = this._tooltipDocked();
        if (!wasVisible) {
          this._tooltipFadeTimer = null;
          tipEl.style.display = "none";
        } else if (docked && typeof requestAnimationFrame === "function") {
          this._tooltipFadeTimer = null;
          var selfHideFrame = this;
          this._tooltipHideFrame = requestAnimationFrame(function() {
            selfHideFrame._tooltipHideFrame = null;
            if (!tipEl.classList.contains("is-visible")) tipEl.style.display = "none";
          });
        } else if (docked) {
          this._tooltipFadeTimer = null;
          tipEl.style.display = "none";
        } else {
          var selfHide = this;
          this._tooltipFadeTimer = setTimeout(function() {
            selfHide._tooltipFadeTimer = null;
            // A show inside the fade window owns the element now.
            if (!tipEl.classList.contains("is-visible")) tipEl.style.display = "none";
          }, TOOLTIP_FADE_MS);
        }
      }

      if (wasVisible) {
        this._dispatch("etcher:tooltip-hide", {
          uuid: (hidShape && hidShape.uuid) || null
        });
      }
    },

    // Pin / unpin — click-to-stick UX. Pinned tooltips ignore hover
    // events and only close on (a) clicking the same shape again,
    // (b) clicking another shape (which switches the pin), or
    // (c) clicking anywhere else on the page.
    _pinTooltipFor: function(shape) {
      // Switching the pin from one shape to another — drop the
      // previous shape's selected styling so only one shape ever
      // reads as "currently pinned".
      var prev = this._tooltipShape;
      if (prev && prev !== shape && prev.el) {
        prev.el.classList.remove("is-selected");
      }
      this._showTooltipFor(shape);
      this.tooltipPinned = true;
      // `_showTooltipFor` armed the dwell auto-close; a pinned tooltip
      // must dwell until an explicit click, so disarm it.
      this._cancelTooltipAutoClose();
      // Mark the pinned shape visually so its dashed outline persists
      // when the cursor leaves it — without this the shape would
      // appear deselected even though its tooltip is dwelling.
      if (shape && shape.el) shape.el.classList.add("is-selected");
      this._installTooltipOutsideClickHandler();
      this._highlightCommentsFor(shape.uuid);
      this._dispatch("etcher:tooltip-pin", { uuid: shape.uuid || null });
    },

    // Multi-image canvas: hit-test a shape's centroid against every
    // image rect on the canvas and return the matching image id, or
    // `null` when the shape sits in empty canvas space (e.g., a
    // freeform note between two pages). Used at draw time to tag
    // each new shape so the `image-visibility-change` listener can
    // hide its DOM when the host toggles that image off.
    //
    // No-op for single-image canvases — the `length > 1` gate keeps
    // every existing one-image consumer free of `image_id` tagging.
    _resolveCanvasImageId: function(kind, geometry) {
      if (this.handleKind !== "canvas") return null;
      if (!this.handle || typeof this.handle.getImages !== "function") return null;
      var images = this.handle.getImages();
      if (!images || images.length < 2) return null;
      var bbox = this._shapeBBoxImagePx({ kind: kind, geometry: geometry });
      if (!bbox) return null;
      var cx = bbox.x + bbox.w / 2;
      var cy = bbox.y + bbox.h / 2;
      for (var i = 0; i < images.length; i++) {
        var img = images[i];
        if (!img || !img.id) continue;
        if (cx >= img.x && cx <= img.x + img.width &&
            cy >= img.y && cy <= img.y + img.height) {
          return img.id;
        }
      }
      return null;
    },

    // Apply `_hiddenImageIds` across every shape: any shape whose
    // `image_id` is in the hidden set gets `display: none` on its
    // SVG element + title group (the satellite that hosts the
    // movable label). Currently-edited shapes drop out of edit
    // mode and any pinned tooltip on a hidden shape unpins — those
    // states would orphan with no visible anchor otherwise.
    //
    // Fast path: when nothing is hidden, just unset display on every
    // shape (covers the case where the host re-shows everything).
    _applyImageVisibility: function() {
      if (this.handleKind !== "canvas") return;
      var hidden = this._hiddenImageIds || null;
      var shapes = this.shapes || [];
      if (!hidden || hidden.size === 0) {
        shapes.forEach(function(s) {
          if (s.el) s.el.style.display = "";
          if (s.titleGroup) s.titleGroup.style.display = "";
        });
        return;
      }
      var self = this;
      shapes.forEach(function(s) {
        var shouldHide = !!(s.image_id && hidden.has(s.image_id));
        if (s.el) s.el.style.display = shouldHide ? "none" : "";
        if (s.titleGroup) s.titleGroup.style.display = shouldHide ? "none" : "";
        if (shouldHide && self.editingShape === s) self._exitEditMode();
        if (shouldHide && self._tooltipShape === s) self._hideTooltip();
      });
    },

    // Compute a shape's bounding box in image-natural pixels. Returns
    // `null` if geometry is malformed (e.g., an empty polygon). Used
    // by `_revealShape`; not called per-frame so it's fine to recompute
    // here rather than caching.
    // Public-facing snapshot of a shape returned by `getShape` /
    // `getShapes`. Includes the host-image identifier that matches
    // the active handle mode: `image_idx` for strip-mode shapes,
    // `image_id` for canvas-mode shapes on multi-image hosts.
    // Either field is present iff that mode is in use; both omitted
    // for single-image canvas. Consumer code routing UI to a shape
    // (deep-links, comment threads, mini-maps) reads these instead
    // of scraping `data-image-idx` / `data-image-id` off the DOM.
    _shapeDescriptor: function(s) {
      var d = {
        uuid: s.uuid,
        kind: s.kind,
        geometry: s.geometry,
        style: s.style || null,
        metadata: s.metadata || null
      };
      if (s.readonly) d.readonly = true;
      if (typeof s.image_idx === "number") d.image_idx = s.image_idx;
      if (typeof s.image_id === "string") d.image_id = s.image_id;
      return d;
    },

    _shapeBBoxImagePx: function(shape) {
      var g = shape && shape.geometry;
      if (!g) return null;
      function fromPoints(pts) {
        if (!pts || pts.length === 0) return null;
        var minX = pts[0].x, minY = pts[0].y, maxX = minX, maxY = minY;
        for (var i = 1; i < pts.length; i++) {
          var p = pts[i];
          if (p.x < minX) minX = p.x; else if (p.x > maxX) maxX = p.x;
          if (p.y < minY) minY = p.y; else if (p.y > maxY) maxY = p.y;
        }
        return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
      }
      switch (shape.kind) {
        case "image":
        case "rectangle":
        case "text":
        case "audio":
        case "video": {
          // A turned box takes the room its turned corners need.
          var bRot = this._shapeRot ? this._shapeRot(shape) : 0;
          if (bRot) {
            var bc = { x: g.x + g.w / 2, y: g.y + g.h / 2 };
            var selfB = this;
            return fromPoints([[g.x, g.y], [g.x + g.w, g.y], [g.x + g.w, g.y + g.h], [g.x, g.y + g.h]]
              .map(function(p) { return selfB._rotatePoint({ x: p[0], y: p[1] }, bc.x, bc.y, bRot); }));
          }
          return { x: g.x, y: g.y, w: g.w, h: g.h };
        }
        case "circle":
          return { x: g.cx - g.r, y: g.cy - g.r, w: 2 * g.r, h: 2 * g.r };
        case "polygon":
          // `g.points` are [x,y] tuples; `fromPoints` reads {x,y}.
          return fromPoints((g.points || []).map(function(p) {
            return { x: p[0], y: p[1] };
          }));
        case "marker":
        case "freehand": {
          // Flatten handles both formats: bezier `nodes` (sampled to a
          // polyline) and raw/legacy `points` (returned as-is) — both as
          // `[x, y]` tuples.
          var flat = this._freehandFlatten(g);
          if (!flat.length) return null;
          var aMinX = flat[0][0], aMinY = flat[0][1], aMaxX = aMinX, aMaxY = aMinY;
          for (var fi = 1; fi < flat.length; fi++) {
            var fp = flat[fi];
            if (fp[0] < aMinX) aMinX = fp[0]; else if (fp[0] > aMaxX) aMaxX = fp[0];
            if (fp[1] < aMinY) aMinY = fp[1]; else if (fp[1] > aMaxY) aMaxY = fp[1];
          }
          return { x: aMinX, y: aMinY, w: aMaxX - aMinX, h: aMaxY - aMinY };
        }
        case "line":
        case "dimension": {
          var ax = g.a[0], ay = g.a[1], bx = g.b[0], by = g.b[1];
          return {
            x: Math.min(ax, bx),
            y: Math.min(ay, by),
            w: Math.abs(bx - ax),
            h: Math.abs(by - ay)
          };
        }
        // Not just the endpoints: a routed arrow's bends can swing well
        // outside the box its two ends describe, and a bbox that missed
        // them would break reveal-into-view and box-select.
        case "arrow":
          // The route, not the raw bends: the curve can bow outside the
          // hull of its control points, and the bbox has to contain what
          // is drawn.
          return fromPoints(this._arrowRoute(g));
        case "callout": {
          var box = g.text_box;
          var anchorX = g.anchor ? g.anchor[0] : 0;
          var anchorY = g.anchor ? g.anchor[1] : 0;
          if (!box) return { x: anchorX - 1, y: anchorY - 1, w: 2, h: 2 };
          var x1 = Math.min(box.x, anchorX);
          var y1 = Math.min(box.y, anchorY);
          var x2 = Math.max(box.x + box.w, anchorX);
          var y2 = Math.max(box.y + box.h, anchorY);
          return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
        }
      }
      return null;
    },

    // -------------------------------------------------------------------------
    // Connectors — arrows that bind to a shape's anchor points and follow it.
    //
    // The binding is the source of truth; an arrow's stored `a`/`b` are a
    // cache of where those bindings last resolved to. Resolving at render
    // time (rather than rewriting arrows from each gesture that moves a
    // shape) is what makes a connector follow its endpoints through moves,
    // resizes, undo, and collab updates without any of those paths needing
    // to know connectors exist.
    // -------------------------------------------------------------------------

    // The box a shape's anchors sit on, in the shape's OWN frame: the box
    // itself, how far it is turned, and the point it turns about. A turned
    // shape's anchors turn with it — its "w" dot is on whatever side the
    // shape's left has gone to, and an arrow tied there follows it round.
    // (They used to sit on the upright bounding box, so after a half turn
    // an arrow tied to the left still came in from the left.)
    _anchorFrame: function(shape) {
      if (!shape || !shape.geometry) return null;
      if (this._isBoxRotatable(shape)) {
        var rb = this._rotBox(shape);
        if (!rb) return null;
        return { box: { x: rb.x, y: rb.y, w: rb.w, h: rb.h }, rot: this._shapeRot(shape),
                 c: { x: rb.x + rb.w / 2, y: rb.y + rb.h / 2 } };
      }
      var bb = this._shapeBBoxImagePx(shape);
      if (!bb) return null;
      var turn = this._shapeTurn(shape);
      var pts = turn ? this._turnablePoints(shape) : null;
      if (!pts || !pts.length) return { box: bb, rot: 0, c: null };
      // Turned back upright about the middle, measured, and turned again.
      var c = { x: bb.x + bb.w / 2, y: bb.y + bb.h / 2 };
      var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (var i = 0; i < pts.length; i++) {
        var q = this._rotatePoint({ x: pts[i][0], y: pts[i][1] }, c.x, c.y, -turn);
        if (q.x < minX) minX = q.x; if (q.x > maxX) maxX = q.x;
        if (q.y < minY) minY = q.y; if (q.y > maxY) maxY = q.y;
      }
      return { box: { x: minX, y: minY, w: maxX - minX, h: maxY - minY }, rot: turn, c: c };
    },

    // How far a point-built shape has been turned with the rotation knob
    // (`style.turn`, kept by `_startRotateDrag`). Box shapes carry theirs
    // in `style.rotation` instead — see `_shapeRot`.
    _shapeTurn: function(shape) {
      var t = shape && shape.style && shape.style.turn;
      return typeof t === "number" && isFinite(t) ? t : 0;
    },

    // The points a point-built shape is drawn through, as [x, y].
    _turnablePoints: function(shape) {
      var g = shape.geometry || {};
      switch (shape.kind) {
        case "polygon": return g.points || [];
        case "marker":
        case "freehand": return this._freehandFlatten(g);
        case "line":
        case "dimension": return g.a && g.b ? [g.a, g.b] : [];
      }
      return null;
    },

    _anchorAt: function(frame, a) {
      var p = { x: frame.box.x + frame.box.w * a.fx, y: frame.box.y + frame.box.h * a.fy };
      return frame.rot ? this._rotatePoint(p, frame.c.x, frame.c.y, frame.rot) : p;
    },

    // The eight bindable points on a shape, in image px.
    _anchorPointsFor: function(shape) {
      var frame = this._anchorFrame(shape);
      if (!frame) return [];
      var self = this;
      return ARROW_ANCHORS.map(function(a) {
        var p = self._anchorAt(frame, a);
        return { id: a.id, x: p.x, y: p.y };
      });
    },

    _anchorPointImage: function(shape, anchorId) {
      var frame = this._anchorFrame(shape);
      if (!frame) return null;
      for (var i = 0; i < ARROW_ANCHORS.length; i++) {
        var a = ARROW_ANCHORS[i];
        if (a.id === anchorId) return this._anchorAt(frame, a);
      }
      return null;
    },

    // Where a single binding currently resolves to, or null if it can't be
    // resolved — no binding, the target has been deleted, or it points at
    // another arrow (never offered, but a hand-authored payload could).
    _resolveBinding: function(binding) {
      if (!binding || !binding.uuid) return null;
      var target = this._shapeByUuid(binding.uuid);
      if (!target || target.kind === "arrow") return null;
      return this._anchorPointImage(target, binding.anchor);
    },

    // Refresh a connector's cached endpoints from its bindings. Unresolvable
    // ends keep their last coordinates, so an arrow whose target was deleted
    // stays exactly where it was drawn instead of collapsing to the origin.
    _resolveArrowEnds: function(shape) {
      if (!shape || shape.kind !== "arrow" || !shape.geometry) return;
      var g = shape.geometry;
      var from = this._resolveBinding(g.from);
      if (from) g.a = [from.x, from.y];
      var to = this._resolveBinding(g.to);
      if (to) g.b = [to.x, to.y];
    },

    // Every point an arrow is drawn through, in order: the tail, any bends
    // dropped while routing it, then the head. One accessor so the render,
    // the bbox, the hit test and the handle list can't drift on how a
    // waypointed arrow is laid out — and so an arrow saved before waypoints
    // existed (no `points` key) reads as the plain two-point case.
    _arrowPath: function(g) {
      if (!g || !g.a || !g.b) return [];
      var pts = [{ x: g.a[0], y: g.a[1] }];
      (g.points || []).forEach(function(p) { pts.push({ x: p[0], y: p[1] }); });
      pts.push({ x: g.b[0], y: g.b[1] });
      return pts;
    },

    // The DRAWN route of a shaft, as dense points. A two-point shaft
    // (every dimension, an unbent arrow) is its own route; a bent arrow is
    // drawn as a centripetal Catmull-Rom curve through its bends (same
    // parameterization as `_catmullRomPathD`, evaluated Barry-Goldman
    // style), so everything that follows the line — hit test, bbox, label
    // position, label drag, tooltip anchor — samples the CURVE the user
    // sees, not the chords it no longer follows.
    //
    // Invariant: ROUTE_SAMPLES points per raw segment, then the head —
    // `_arrowSegMidImage` indexes on it.
    _arrowRoute: function(g) {
      var raw = this._arrowPath(g);
      if (raw.length <= 2) return raw;
      var out = [];
      for (var i = 0; i < raw.length - 1; i++) {
        for (var s = 0; s < ROUTE_SAMPLES; s++) {
          out.push(this._crSample(raw, i, s / ROUTE_SAMPLES));
        }
      }
      out.push(raw[raw.length - 1]);
      return out;
    },

    // The centripetal Catmull-Rom point at parameter `f` (0-1) along the
    // segment raw[i] -> raw[i+1], via the Barry-Goldman pyramid. Knot
    // spacing matches `_catmullRomPathD`'s (distance^CATMULL_ALPHA), so
    // the sampled geometry sits on the drawn stroke.
    _crSample: function(raw, i, f) {
      var p0 = raw[i - 1] || raw[i];
      var p1 = raw[i];
      var p2 = raw[i + 1];
      var p3 = raw[i + 2] || p2;
      function knot(a, b) {
        var dx = b.x - a.x, dy = b.y - a.y;
        return Math.max(Math.pow(dx * dx + dy * dy, CATMULL_ALPHA / 2), 1e-6);
      }
      var t0 = 0;
      var t1 = t0 + knot(p0, p1);
      var t2 = t1 + knot(p1, p2);
      var t3 = t2 + knot(p2, p3);
      var t = t1 + (t2 - t1) * f;
      function lerp(a, b, u) { return { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u }; }
      function ratio(t, ta, tb) { return tb - ta > 1e-9 ? (t - ta) / (tb - ta) : 0; }
      var A1 = lerp(p0, p1, ratio(t, t0, t1));
      var A2 = lerp(p1, p2, ratio(t, t1, t2));
      var A3 = lerp(p2, p3, ratio(t, t2, t3));
      var B1 = lerp(A1, A2, ratio(t, t0, t2));
      var B2 = lerp(A2, A3, ratio(t, t1, t3));
      return lerp(B1, B2, ratio(t, t1, t2));
    },

    // Curve point half way along raw segment `i` — where the add-bend dot
    // sits, ON the drawn line rather than on the chord it may have bowed
    // away from. Route index per the invariant above.
    _arrowSegMidImage: function(g, i) {
      var raw = this._arrowPath(g);
      if (raw.length <= 2 || !raw[i + 1]) {
        var a = raw[i], b = raw[i + 1] || raw[i];
        return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      }
      return this._crSample(raw, i, 0.5);
    },

    // The point at arc-length fraction `t` (0-1) along the ROUTED shaft —
    // tail, bends, head — not along the a->b chord. A label whose offset
    // lerps the chord stays put when the middle of the arrow is dragged
    // into a bend (the endpoints never moved), hovering over where the
    // line used to be. Measured by arc length so t=0.5 is the middle of
    // the drawn line, wherever its bends take it; a two-point shaft (every
    // dimension, an unbent arrow) reduces to the plain lerp.
    _shaftPointAt: function(g, t) {
      var pts = this._arrowRoute(g);
      if (pts.length < 2) return null;
      var lens = [], total = 0, i;
      for (i = 1; i < pts.length; i++) {
        var d = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
        lens.push(d);
        total += d;
      }
      if (!(total > 0)) return { x: pts[0].x, y: pts[0].y };
      var target = Math.max(0, Math.min(1, t)) * total;
      for (i = 0; i < lens.length; i++) {
        if (target <= lens[i] || i === lens.length - 1) {
          var f = lens[i] > 0 ? Math.min(1, target / lens[i]) : 0;
          return {
            x: pts[i].x + (pts[i + 1].x - pts[i].x) * f,
            y: pts[i].y + (pts[i + 1].y - pts[i].y) * f
          };
        }
        target -= lens[i];
      }
      return { x: pts[pts.length - 1].x, y: pts[pts.length - 1].y };
    },

    // The inverse: the arc-length fraction of the routed shaft closest to
    // `pt`. This is what makes dragging a label along a BENT arrow feel
    // right — the pointer is projected onto whichever segment it is
    // actually near, not onto the chord between the endpoints.
    _shaftOffsetFor: function(g, pt) {
      var pts = this._arrowRoute(g);
      if (pts.length < 2) return null;
      var lens = [], total = 0, i;
      for (i = 1; i < pts.length; i++) {
        var d = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
        lens.push(d);
        total += d;
      }
      if (!(total > 0)) return null;
      var best = null, bestD = Infinity, run = 0;
      for (i = 0; i < lens.length; i++) {
        var ax = pts[i].x, ay = pts[i].y;
        var dx = pts[i + 1].x - ax, dy = pts[i + 1].y - ay;
        var l2 = dx * dx + dy * dy;
        var s = l2 > 0 ? ((pt.x - ax) * dx + (pt.y - ay) * dy) / l2 : 0;
        s = Math.max(0, Math.min(1, s));
        var cx = ax + dx * s, cy = ay + dy * s;
        var dd = (pt.x - cx) * (pt.x - cx) + (pt.y - cy) * (pt.y - cy);
        if (dd < bestD) { bestD = dd; best = run + lens[i] * s; }
        run += lens[i];
      }
      return best == null ? null : Math.max(0, Math.min(1, best / total));
    },

    // Remove the bend at handle index `idx`. Indices run over the whole
    // routed path (0 = tail, last = head), so only the interior ones are
    // bends — clicking an endpoint must not silently delete something, and
    // an endpoint is where a re-bind drag starts.
    _deleteArrowBend: function(shape, idx) {
      if (!shape || shape.kind !== "arrow") return;
      var g = shape.geometry;
      var bends = (g.points || []).map(function(p) { return [p[0], p[1]]; });
      if (idx <= 0 || idx > bends.length) return;
      var historyBefore = this._snapshotShape(shape);
      bends.splice(idx - 1, 1);
      shape.geometry = {
        a: [g.a[0], g.a[1]], b: [g.b[0], g.b[1]],
        points: bends,
        from: g.from || null, to: g.to || null
      };
      this._renderShape(shape);
      // Every index past the removed one has shifted, so the handle set has
      // to be rebuilt rather than repositioned — otherwise the next click
      // deletes the wrong bend.
      if (this.editingShape === shape) this._renderHandles(shape);
      if (shape.uuid) {
        this._emitChanged();
        this._pushUndo(shape.uuid, historyBefore, this._snapshotShape(shape));
      }
    },

    _arrowsBoundTo: function(uuid) {
      if (!uuid) return [];
      return (this.shapes || []).filter(function(s) {
        if (s.kind !== "arrow" || !s.geometry) return false;
        var g = s.geometry;
        return (g.from && g.from.uuid === uuid) ||
               (g.to && g.to.uuid === uuid);
      });
    },

    // Points for a V-arrowhead: the tip sits at `tip` and the two wings open
    // back toward `toward`. Returns a `points` string for a <polyline>, or
    // "" when the two coincide and there's no direction to open along — a
    // draft's first pointermove tick, before the drag has any length.
    _vArrowPoints: function(tip, toward, len, halfWidth) {
      var dx = toward.x - tip.x;
      var dy = toward.y - tip.y;
      var d = Math.sqrt(dx * dx + dy * dy);
      if (!(d > 0.001)) return "";
      var ux = dx / d, uy = dy / d;
      var bx = tip.x + ux * len, by = tip.y + uy * len;
      var px = -uy * halfWidth, py = ux * halfWidth;
      return (bx + px) + "," + (by + py) + " " +
             tip.x + "," + tip.y + " " +
             (bx - px) + "," + (by - py);
    },

    // The <g> a connector is drawn into: shaft + one head, both in
    // currentColor so `_applyShapeColor` recolors the pair in one stroke.
    // Shared by the drag preview and the hydrate path so a freshly drawn
    // arrow and a reloaded one are the same DOM.
    _makeArrowEl: function() {
      var g = svgEl("g");
      g.classList.add("etcher-arrow");
      // A <path>, not a polyline: a routed arrow draws its bends as a
      // smooth centripetal Catmull-Rom curve (like the marker's stroke),
      // and a straight one is just the two-point M/L case.
      var shaft = svgEl("path", {
        "stroke-width": "2",
        stroke: "currentColor",
        fill: "none",
        "stroke-linecap": "round",
        "stroke-linejoin": "round"
      });
      shaft.classList.add("etcher-arrow-shaft");
      var head = svgEl("polyline", {
        "stroke-width": "2",
        stroke: "currentColor",
        fill: "none",
        "stroke-linejoin": "round",
        "stroke-linecap": "round"
      });
      head.classList.add("etcher-arrow-head");
      g.appendChild(shaft);
      g.appendChild(head);
      return g;
    },

    // Can the user pull connectors out of this shape? Cursor tool only —
    // any other tool means they're drawing, and eight hit-targets sitting
    // on top of the shape would eat the first click of that stroke.
    //
    // Excluded in edit mode as well, because the resize handles occupy the
    // same eight points: the four corners exactly, and (for a rectangle)
    // the four side midpoints too. Two stacked hit-targets per position is
    // a coin toss between resizing and connecting. Connectors are the
    // hover affordance, handles are the selected one.
    // The cursor's hover dots: retired. Shapes are connected with the arrow
    // tool, which shows a shape's anchors as it nears it (`_arrowToolHover`,
    // `_updateDimension`); the cursor only selects and moves.
    _connectorsAvailableFor: function(shape) {
      return false && !!shape &&
        // Resolved through `_connectorsOn` — user pref, else host default,
        // else off.
        this._connectorsOn() &&
        this.annotationMode === true &&
        this.activeTool == null &&
        shape.kind !== "arrow" &&
        shape.readonly !== true &&
        this.editingShape !== shape &&
        // Nothing on offer while a group is selected. The dots invite a drag
        // that starts an arrow, which is not what a press on these shapes
        // means right now — it means move the group — and the two gestures
        // begin identically. Connectors are for one shape at a time.
        !(this.selectedShapes && this.selectedShapes.length) &&
        !this._arrowDrag;
    },

    // Show the eight connector dots on `shape` (or clear them for null).
    // Driven from hover, so this is called on every shape the cursor
    // crosses — it early-outs when the target hasn't changed.
    _syncConnectorDots: function(shape) {
      var target = this._connectorsAvailableFor(shape) ? shape : null;
      if (target === this._connectorDotShape) {
        if (target) this._positionConnectorDots();
        return;
      }
      this._removeConnectorDots();
      if (!target) return;
      var self = this;
      this._activateOverlayForShape(target);
      this._connectorDotShape = target;
      var hitR = this._connectorHitRadius(target);
      this._connectorDots = this._anchorPointsFor(target).map(function(pt) {
        // Two elements per anchor: an invisible disc that accepts the press,
        // and the small dot the user actually sees. Grabbing a 4.5px target
        // is a pixel-perfect ask, and the visible dot can't simply be grown
        // to match — at that size eight of them read as a ring of blobs
        // rather than a hint.
        //
        // The pair is adjacent in the DOM so the hit disc can light its own
        // dot on hover through a CSS sibling selector, keeping the feedback
        // on the element the pointer is really over.
        var hit = svgEl("circle", { r: hitR });
        hit.classList.add("etcher-connector-hit");
        hit.dataset.anchor = pt.id;
        var dot = svgEl("circle", { r: 4.5 });
        dot.classList.add("etcher-connector-dot");
        dot.dataset.anchor = pt.id;
        self.svg.appendChild(hit);
        self.svg.appendChild(dot);
        self._positionHandle(hit, pt);
        self._positionHandle(dot, pt);
        hit.addEventListener("pointerdown", function(e) {
          self._startArrowDrag(target, pt.id, e);
        });
        return { hit: hit, dot: dot };
      });
    },

    // Radius of a connector's grab zone, in container px.
    //
    // Clamped against the shape's rendered size, because eight zones of a
    // fixed radius would swallow a small shape whole — every press anywhere
    // near it would start an arrow, and the shape itself could never be
    // clicked to select or move. Recomputed as the shape is drawn, so
    // zooming out tightens the zones rather than letting them merge.
    _connectorHitRadius: function(shape) {
      var box = this._shapeBBoxImagePx(shape);
      if (!box) return CONNECTOR_HIT_RADIUS;
      var tl, br;
      try {
        tl = this._imageToContainer({ x: box.x, y: box.y });
        br = this._imageToContainer({ x: box.x + box.w, y: box.y + box.h });
      } catch (_) {
        return CONNECTOR_HIT_RADIUS;
      }
      var shorter = Math.min(Math.abs(br.x - tl.x), Math.abs(br.y - tl.y));
      return Math.max(
        CONNECTOR_HIT_RADIUS_MIN,
        Math.min(CONNECTOR_HIT_RADIUS, shorter * CONNECTOR_HIT_SHAPE_RATIO)
      );
    },

    _positionConnectorDots: function() {
      var shape = this._connectorDotShape;
      if (!shape || !this._connectorDots || !this._connectorDots.length) return;
      var pts = this._anchorPointsFor(shape);
      var hitR = this._connectorHitRadius(shape);
      var self = this;
      this._connectorDots.forEach(function(pair, i) {
        if (!pts[i]) return;
        self._positionHandle(pair.hit, pts[i]);
        self._positionHandle(pair.dot, pts[i]);
        pair.hit.setAttribute("r", hitR);
      });
    },

    _removeConnectorDots: function() {
      (this._connectorDots || []).forEach(function(pair) {
        if (pair.hit.parentNode) pair.hit.parentNode.removeChild(pair.hit);
        if (pair.dot.parentNode) pair.dot.parentNode.removeChild(pair.dot);
      });
      this._connectorDots = [];
      this._connectorDotShape = null;
    },

    // What a dragging arrow end would attach to at `pt`.
    //
    // `candidate` is the shape whose anchors should be on screen — the
    // topmost one whose box the pointer is inside or near. `snap` is set
    // only once the pointer is actually within snapping distance of one of
    // that shape's anchors. Keeping them separate is what makes the
    // magnetism legible: the dots appear first as an offer, and the end
    // jumps only when you close in on one.
    //
    // Distances are measured in container px so the feel is the same at
    // every zoom — an image-px threshold would be unusably tight zoomed out
    // and sloppy zoomed in.
    _arrowSnapState: function(pt, excludeUuid) {
      var none = { candidate: null, snap: null };
      var shapes = this.shapes || [];
      var cursor;
      try { cursor = this._imageToContainer(pt); } catch (_) { return none; }
      var stripMode = this.handleKind === "strip";
      var ptImageIdx =
        stripMode && typeof pt.imageIdx === "number" ? pt.imageIdx : null;

      for (var i = shapes.length - 1; i >= 0; i--) {
        var s = shapes[i];
        if (!s.uuid || s.uuid === excludeUuid || s.kind === "arrow") continue;
        if (stripMode && s.image_idx !== ptImageIdx) continue;
        var box = this._shapeBBoxImagePx(s);
        if (!box) continue;
        var tl = this._imageToContainer({ x: box.x, y: box.y });
        var br = this._imageToContainer({ x: box.x + box.w, y: box.y + box.h });
        var x1 = Math.min(tl.x, br.x) - ARROW_TARGET_PAD_PX;
        var x2 = Math.max(tl.x, br.x) + ARROW_TARGET_PAD_PX;
        var y1 = Math.min(tl.y, br.y) - ARROW_TARGET_PAD_PX;
        var y2 = Math.max(tl.y, br.y) + ARROW_TARGET_PAD_PX;
        if (cursor.x < x1 || cursor.x > x2 || cursor.y < y1 || cursor.y > y2) {
          continue;
        }
        // Topmost shape in range wins the offer, matching `_shapeAt`'s
        // reverse walk — the one drawn on top is the one you meant.
        var anchors = this._anchorPointsFor(s);
        var best = null;
        var bestDist = ARROW_SNAP_PX * ARROW_SNAP_PX;
        for (var j = 0; j < anchors.length; j++) {
          var ac = this._imageToContainer(anchors[j]);
          var dx = ac.x - cursor.x, dy = ac.y - cursor.y;
          var d2 = dx * dx + dy * dy;
          if (d2 <= bestDist) { bestDist = d2; best = anchors[j]; }
        }
        return {
          candidate: s,
          snap: best
            ? { shape: s, anchor: best.id, x: best.x, y: best.y }
            : null
        };
      }
      return none;
    },

    // Paint the candidate's anchors while a connector is being dragged,
    // highlighting the one that would take the end. Separate from the hover
    // dots so the source shape can keep showing its own set underneath.
    _paintTargetDots: function(state) {
      var candidate = state && state.candidate;
      var snapId = state && state.snap ? state.snap.anchor : null;
      // Remembered so a pan/zoom frame can re-place the dots without
      // re-running the search — `_renderAll` has no pointer to search from.
      this._lastArrowSnap = (state && state.snap) || null;
      if (candidate !== this._targetDotShape) {
        this._removeTargetDots();
        if (candidate) {
          var self = this;
          this._targetDotShape = candidate;
          this._targetDots = this._anchorPointsFor(candidate).map(function(pt) {
            var dot = svgEl("circle", { r: 4.5 });
            dot.classList.add("etcher-connector-dot", "is-target");
            dot.dataset.anchor = pt.id;
            self.svg.appendChild(dot);
            self._positionHandle(dot, pt);
            return dot;
          });
        }
      } else if (candidate) {
        // Same shape, but it may have moved under a pan — keep them glued.
        var pts = this._anchorPointsFor(candidate);
        var self2 = this;
        this._targetDots.forEach(function(dot, i) {
          if (pts[i]) self2._positionHandle(dot, pts[i]);
        });
      }
      (this._targetDots || []).forEach(function(dot) {
        dot.classList.toggle("is-snap", dot.dataset.anchor === snapId);
      });
    },

    _removeTargetDots: function() {
      (this._targetDots || []).forEach(function(d) {
        if (d.parentNode) d.parentNode.removeChild(d);
      });
      this._targetDots = [];
      this._targetDotShape = null;
    },

    // Pull a connector out of one of `source`'s anchor dots.
    //
    // Two gestures share this entry point, because the press that begins
    // them is identical and which one the user meant is only clear from
    // what they do next:
    //
    //   Drag — press, move, release. Commits on release. Direct, and the
    //   fast path for "join these two shapes".
    //
    //   Route — press and release without moving. The arrow stays live and
    //   follows the pointer; each click drops a bend and carries on, and a
    //   click on another shape finishes it. This is the yEd gesture, and
    //   it's the only way to snake an arrow around obstacles.
    //
    // The tail is bound to the anchor from the first frame either way; the
    // head binds to whatever the arrow finishes on.
    _startArrowDrag: function(source, anchorId, e) {
      if (e.button != null && e.button !== 0) return;
      // The dot sits over the shape — without this the press would also
      // start a body move, and the shape would travel with the arrow.
      e.preventDefault();
      e.stopPropagation();
      var self = this;
      var startPt;
      try { startPt = self._toImage(e); } catch (_) { return; }
      var tail = self._anchorPointImage(source, anchorId) || startPt;

      var el = self._makeArrowEl();
      el.classList.add("etcher-shape", "is-draft");
      self._applyShapeColor(el, self.activeColor);
      self.svg.appendChild(el);

      var draft = {
        kind: "arrow",
        geometry: {
          a: [tail.x, tail.y],
          b: [startPt.x, startPt.y],
          points: [],
          from: { uuid: source.uuid, anchor: anchorId },
          to: null
        },
        el: el,
        style: self._styleForNewShape("arrow")
      };
      self._arrowDrag = draft;
      // The source's own hover dots would sit under the pointer for the
      // whole gesture and re-render on every frame; the target dots are the
      // ones that matter now.
      self._removeConnectorDots();
      self._hideTooltip();
      self._renderShape(draft);

      // Tracked on `document` rather than on the dot the gesture started
      // from: `_removeConnectorDots` above just took that dot out of the
      // DOM, which drops both its listeners and any pointer capture it
      // held. The draft `<g>` is no good either — shapes are
      // `pointer-events: none` so pan/zoom can pass through them, and a
      // capture there would never deliver a move.
      var moved = false;
      var routing = false;
      // The source shape is under the pointer at the moment the arrow is
      // born, so without this its own anchors would be snap candidates
      // immediately and the very next click would finish a zero-length
      // self-arrow. Once the pointer has left, the source becomes a legal
      // target again — a shape pointing back at itself is a real diagram.
      var leftSource = false;

      // Follow the pointer with the head, snapping to an anchor when close.
      // Returns the snap so callers can decide whether a click finishes.
      function track(pt) {
        // Searched without exclusions, then filtered — asking the search to
        // exclude the source would make it invisible here too, and "have we
        // left it yet?" could never become true.
        var state = self._arrowSnapState(pt, null);
        if (!leftSource) {
          if (state.candidate && state.candidate.uuid === source.uuid) {
            state = { candidate: null, snap: null };
          } else {
            leftSource = true;
          }
        }
        self._paintTargetDots(state);
        if (state.snap) {
          draft.geometry.b = [state.snap.x, state.snap.y];
          draft.geometry.to = {
            uuid: state.snap.shape.uuid, anchor: state.snap.anchor
          };
        } else {
          draft.geometry.b = [pt.x, pt.y];
          draft.geometry.to = null;
        }
        self._renderShape(draft);
        return state.snap;
      }

      function teardown() {
        document.removeEventListener("pointermove", onMove, true);
        document.removeEventListener("pointerup", onUp, true);
        document.removeEventListener("pointercancel", onUp, true);
        document.removeEventListener("pointerdown", onRouteClick, true);
        document.removeEventListener("dblclick", onRouteDblClick, true);
        document.removeEventListener("keydown", onKey, true);
        self._arrowDrag = null;
        self._removeTargetDots();
      }

      function commit() {
        teardown();
        el.classList.remove("is-draft");
        self._finalizeShape("arrow", draft.geometry, el);
      }

      function abandon() {
        teardown();
        if (el.parentNode) el.parentNode.removeChild(el);
      }

      function onMove(ev) {
        var pt;
        try { pt = self._toImage(ev); } catch (_) { return; }
        if (!moved && !routing) {
          var a = self._imageToContainer(startPt);
          var b = self._imageToContainer(pt);
          var sdx = b.x - a.x, sdy = b.y - a.y;
          // Same 3px screen dead zone the body/title drags use, so the
          // press that opens routing mode isn't read as a tiny drag.
          if (sdx * sdx + sdy * sdy < 9) return;
          moved = true;
        }
        track(pt);
      }

      function onUp() {
        if (moved) { commit(); return; }
        // Released without moving: the user is routing, not dragging. Hand
        // the gesture over to the click handlers and leave the arrow live.
        routing = true;
        document.removeEventListener("pointerup", onUp, true);
        document.removeEventListener("pointercancel", onUp, true);
        document.addEventListener("pointerdown", onRouteClick, true);
        document.addEventListener("dblclick", onRouteDblClick, true);
      }

      function onRouteClick(ev) {
        // Right-click abandons, matching the usual escape from a modal
        // drawing gesture.
        if (ev.button != null && ev.button !== 0) {
          ev.preventDefault();
          ev.stopPropagation();
          abandon();
          return;
        }
        // Clicks on Etcher's own chrome are for the chrome — let the
        // toolbar work while an arrow is in flight.
        if (ev.target && ev.target.closest && ev.target.closest(CHROME_SELECTOR)) {
          return;
        }
        var pt;
        try { pt = self._toImage(ev); } catch (_) { return; }
        // Capture phase + stopPropagation: this click is the routing
        // gesture's, and must not also reach `_docPointerDown` and be read
        // as a shape tap or the start of a box-select.
        ev.preventDefault();
        ev.stopPropagation();
        var snap = track(pt);
        if (snap) { commit(); return; }
        // Empty canvas: drop a bend here and keep going. The head then
        // continues from this point on the next move.
        draft.geometry.points = (draft.geometry.points || []).concat([
          [pt.x, pt.y]
        ]);
        self._renderShape(draft);
      }

      function onRouteDblClick(ev) {
        // Finish where you are, with a free head. The second click of the
        // double already dropped a bend at this spot, so drop it back off
        // — otherwise every free-ended arrow gains a redundant bend sitting
        // exactly under its own head.
        ev.preventDefault();
        ev.stopPropagation();
        (draft.geometry.points || []).pop();
        commit();
      }

      function onKey(ev) {
        if (ev.key === "Escape") {
          ev.preventDefault();
          ev.stopPropagation();
          abandon();
        }
      }

      // Capture phase so the gesture wins over the doc-level hover/tap
      // handlers for its duration.
      document.addEventListener("pointermove", onMove, true);
      document.addEventListener("pointerup", onUp, true);
      document.addEventListener("pointercancel", onUp, true);
      document.addEventListener("keydown", onKey, true);
    },

    // Bring a shape into the viewport. Strip mode scrolls the strip
    // so the shape's bbox sits at the chosen alignment (default
    // center, clamped at the top edge); canvas mode delegates to
    // `handle.fitBounds`. Polls for late-mounted shapes for up to
    // `opts.timeout` ms (default 10s) — chapters that hydrate on
    // scroll, async backfill of an annotation set.
    //
    // Returns a Promise resolving with
    //   { uuid, image_idx?, image_id?, scrollTop?, cameraBounds? }
    // once the reveal call has been issued. Rejects with a
    // `{ reason }` object on timeout / missing geometry / handle
    // failure. Also dispatches an `etcher:shape-revealed` bubbling
    // CustomEvent on the layer host carrying the same payload.
    _revealShape: function(uuid, opts) {
      var self = this;
      opts = opts || {};
      var behavior = opts.behavior === "instant" ? "instant" : "smooth";
      var align = (opts.align === "top" || opts.align === "bottom") ?
        opts.align : "center";
      var pulse = !!opts.pulse;
      var pulseDuration = typeof opts.pulseDuration === "number" ?
        opts.pulseDuration : 1500;
      var timeout = typeof opts.timeout === "number" ? opts.timeout : 10000;
      var pad = typeof opts.padding === "number" ? opts.padding : 0;

      return new Promise(function(resolve, reject) {
        var startedAt = Date.now();

        function attempt() {
          var shape = (self.shapes || [])
            .find(function(s) { return s.uuid === uuid; });
          if (!shape) {
            if (Date.now() - startedAt >= timeout) {
              return reject({ reason: "timeout" });
            }
            return setTimeout(attempt, 120);
          }

          var bbox = self._shapeBBoxImagePx(shape);
          if (!bbox) return reject({ reason: "no_geometry" });

          var payload = { uuid: shape.uuid };
          if (typeof shape.image_idx === "number") {
            payload.image_idx = shape.image_idx;
          }
          if (typeof shape.image_id === "string") {
            payload.image_id = shape.image_id;
          }

          if (self.handleKind === "strip") {
            if (typeof shape.image_idx !== "number") {
              return reject({ reason: "no_image_idx" });
            }
            var page = (self.pages || [])[shape.image_idx];
            if (!page) {
              if (Date.now() - startedAt >= timeout) {
                return reject({ reason: "timeout" });
              }
              return setTimeout(attempt, 120);
            }
            // page.height is rendered px, page.naturalHeight is image px.
            var scale = page.naturalHeight > 0 ?
              page.height / page.naturalHeight : 1;
            var renderedTop = bbox.y * scale;
            var renderedH = bbox.h * scale;
            var viewportH = (self.handle.container &&
              self.handle.container.clientHeight) || 0;
            var yOffset;
            if (align === "top") {
              yOffset = renderedTop;
            } else if (align === "bottom") {
              yOffset = renderedTop + renderedH - viewportH;
            } else {
              yOffset = renderedTop + renderedH / 2 - viewportH / 2;
            }
            yOffset = Math.max(0, yOffset);
            payload.scrollTop = yOffset;
            try {
              self.handle.scrollTo({
                imageIdx: shape.image_idx,
                y: yOffset,
                behavior: behavior
              });
            } catch (e) {
              return reject({ reason: "scroll_failed", error: e });
            }
          } else if (self.handleKind === "canvas") {
            if (typeof self.handle.fitBounds !== "function") {
              return reject({ reason: "fitBounds_failed" });
            }
            var bounds = {
              x: bbox.x - pad,
              y: bbox.y - pad,
              w: bbox.w + pad * 2,
              h: bbox.h + pad * 2
            };
            payload.cameraBounds = bounds;
            try {
              self.handle.fitBounds(
                bounds,
                { animate: behavior !== "instant" }
              );
            } catch (e) {
              return reject({ reason: "fitBounds_failed", error: e });
            }
          } else {
            return reject({ reason: "unsupported_handleKind" });
          }

          if (pulse && shape.el) {
            // Smooth scroll needs a beat to settle before the flash
            // is visible. Instant scroll starts on the next frame.
            var delay = behavior === "instant" ? 16 : 350;
            setTimeout(function() {
              var el = shape.el;
              if (!el) return;
              el.classList.add("etcher-shape--pulse");
              setTimeout(function() {
                if (el && el.classList) {
                  el.classList.remove("etcher-shape--pulse");
                }
              }, pulseDuration);
            }, delay);
          }

          self._dispatch("etcher:shape-revealed", payload);
          resolve(payload);
        }

        attempt();
      });
    },

    _unpinTooltip: function() {
      var pinned = this._tooltipShape;
      this.tooltipPinned = false;
      this._removeTooltipOutsideClickHandler();
      this._clearCommentHighlights();
      // Clear hover + selected styling — the pinned-state mouseenter
      // guard skipped re-adding `is-hovered`, but the click that
      // originally pinned added it before the pin was set; and
      // `is-selected` is our pin-visual marker that should drop now.
      if (pinned && pinned.el) {
        pinned.el.classList.remove("is-hovered");
        pinned.el.classList.remove("is-selected");
      }
      this._hideTooltip();
      this._dispatch("etcher:tooltip-unpin", {
        uuid: (pinned && pinned.uuid) || null
      });
    },

    // Look up every element in the document that carries
    // `data-annotation-uuid="<uuid>"` (typically a comment row stamped
    // by the consumer's discussion thread, but the contract is purely
    // the data attribute) and highlight them. Scroll the first match
    // into view so the user doesn't have to hunt for the thread.
    _highlightCommentsFor: function(annotationUuid) {
      this._clearCommentHighlights();
      if (!annotationUuid) return;
      var matches = document.querySelectorAll(
        '[data-annotation-uuid="' + annotationUuid + '"]'
      );
      if (matches.length === 0) return;
      matches.forEach(function(el) { el.classList.add("etcher-comment-highlight"); });
      try {
        matches[0].scrollIntoView({ behavior: "smooth", block: "center" });
      } catch (_) {}
    },

    _clearCommentHighlights: function() {
      document.querySelectorAll(".etcher-comment-highlight").forEach(function(el) {
        el.classList.remove("etcher-comment-highlight");
      });
    },

    _installTooltipOutsideClickHandler: function() {
      if (this._tooltipOutsideClick) return;
      var self = this;
      this._tooltipOutsideClick = function(e) {
        // Clicks on a shape, on the tooltip itself, on an edit-mode
        // handle, or inside any registered input-owner (modals, etc.)
        // keep the pin alive. Anything else unpins. Shapes are
        // `pointer-events: none`, so a click on a shape lands on the
        // canvas/container at the DOM level — fall back to image-px
        // hit-test.
        if (e.target.closest(".etcher-shape")) return;
        if (isInputOwner(e.target, self.overlayWrapper)) return;
        try {
          var pt = self._toImage(e);
          if (self._shapeAt(pt)) return;
        } catch (_) {}
        self._unpinTooltip();
      };
      // Capture phase so we run before any inner stopPropagation can
      // swallow the click.
      document.addEventListener("click", this._tooltipOutsideClick, true);
    },

    _removeTooltipOutsideClickHandler: function() {
      if (this._tooltipOutsideClick) {
        document.removeEventListener("click", this._tooltipOutsideClick, true);
        this._tooltipOutsideClick = null;
      }
    },

    // ---- Multi-selection (shift-click) -----------------------------------
    //
    // A second selection mode independent of `editingShape`. Shift-
    // clicking a shape toggles it in/out of `selectedShapes`; the
    // group can then be dragged together or deleted with a single
    // Backspace / Delete keystroke. Clearing happens on:
    //   - clicking empty canvas (no modifier)
    //   - selecting any drawing tool (entering draw mode)
    //   - exiting annotation mode
    //   - completing a delete on the group

    _addToSelection: function(shape) {
      if (!shape || !shape.el) return;
      this.selectedShapes = this.selectedShapes || [];
      if (this.selectedShapes.indexOf(shape) !== -1) return;
      this.selectedShapes.push(shape);
      shape.el.classList.add("is-multi-selected");
      this._refreshImageRing(shape);
      this._syncTransformBox();
      // Dots raised by an earlier hover would otherwise hang around over a
      // shape that has just become part of a group — see
      // `_connectorsAvailableFor`. They come back on the next hover once the
      // selection is dropped.
      this._removeConnectorDots();
      this._syncArrangeButtons();
    },

    _removeFromSelection: function(shape) {
      var list = this.selectedShapes || [];
      var idx = list.indexOf(shape);
      if (idx === -1) return;
      list.splice(idx, 1);
      if (shape.el) shape.el.classList.remove("is-multi-selected");
      this._refreshImageRing(shape);
      this._syncArrangeButtons();
      this._syncTransformBox();
    },

    _toggleInSelection: function(shape) {
      var list = this.selectedShapes || [];
      if (list.indexOf(shape) === -1) this._addToSelection(shape);
      else this._removeFromSelection(shape);
    },

    _clearSelection: function() {
      this._freshShape = null;
      var list = this.selectedShapes || [];
      var self = this;
      list.forEach(function(s) {
        if (s && s.el) s.el.classList.remove("is-multi-selected");
        self._refreshImageRing(s);
      });
      this.selectedShapes = [];
      this._syncArrangeButtons();
      this._syncTransformBox();
    },

    // Arm a tool from its shortcut key. The cursor (null) is always
    // reachable; every other key works only when the board's `:tools`
    // allowlist offers that tool — a key silently arming a tool the
    // toolbar doesn't show would strand the user in a mode they can't
    // see, and the host page keeps the keystroke instead.
    // Arm the grabber, remembering what to come back to.
    //
    // Refused while a shape is in flight. Changing tools cancels the draft
    // (see `_selectTool`), so panning mid-shape would silently destroy the
    // rectangle you were dragging out or the arrow waiting for its second
    // click. Losing work to a key you pressed for a look around is a far
    // worse trade than not panning for a moment.
    _beginSpacePan: function() {
      if (this._spacePanning) return;
      if (this.draftState || this.draftPolygon || this.draftCallout ||
          this._arrowDrag) {
        return;
      }
      // A board that doesn't offer the grabber doesn't get space-pan
      // either — `_selectToolByShortcut` is what knows that.
      var from = this.activeTool;
      if (!this._selectToolByShortcut("grabber")) return;
      this._spacePanning = true;
      this._spacePanFrom = from;
    },

    // …and back. `null` is the cursor, which is a real tool to return to,
    // so the flag is what says whether a pan is in progress rather than
    // the remembered value being empty.
    _endSpacePan: function() {
      if (!this._spacePanning) return;
      var back = this._spacePanFrom;
      this._spacePanning = false;
      this._spacePanFrom = null;
      // Only if space is still what is driving it. Someone who picked
      // another tool mid-pan has said what they want, and yanking them back
      // on keyup would undo a deliberate choice.
      if (this.activeTool === "grabber") this._selectToolByShortcut(back);
    },

    _selectToolByShortcut: function(toolKey) {
      if (toolKey === null) {
        this._selectTool(null);
        return true;
      }
      if (!TOOL_DEFS[toolKey]) return false;
      if ((this.tools || []).indexOf(toolKey) === -1) return false;
      this._selectTool(toolKey);
      return true;
    },

    // One Escape, one rung — the panic key every comparable tool honors.
    // An in-flight draft dies first; then an armed tool drops back to the
    // cursor; then whatever is selected or being edited lets go. Returns
    // whether the press was consumed, so an Escape with nothing to do
    // falls through to the host page.
    _escapeLadder: function() {
      if (this.draftState || this.draftPolygon || this.draftCallout) {
        this._cancelDraft();
        return true;
      }
      if (this.activeTool != null) {
        this._selectTool(null);
        return true;
      }
      if ((this.selectedShapes && this.selectedShapes.length) ||
          this.editingShape || this.editingTitleShape) {
        this._exitEditMode();
        this._exitTitleEditMode();
        this._clearSelection();
        this._syncActionBar();
        return true;
      }
      return false;
    },

    // Select every editable shape on the board (⌘/Ctrl+A). Cursor mode
    // only — mid-draw, the keystroke belongs to whatever the host does
    // with it. Locked shapes stay out: selecting what cannot be moved or
    // deleted only sets up a surprise.
    _selectAll: function() {
      if (this.activeTool != null) return false;
      var self = this;
      var targets = (this.shapes || []).filter(function(s) {
        return s && s.el && !s.readonly;
      });
      if (!targets.length) return false;
      this._exitEditMode();
      this._exitTitleEditMode();
      this._clearSelection();
      targets.forEach(function(s) { self._addToSelection(s); });
      this._syncActionBar();
      return true;
    },

    // Nudge the selection by arrow key: 1 screen px, 10 with shift —
    // converted to image px so a nudge moves the same distance on screen
    // at every zoom. The whole burst of a held key becomes ONE undo entry
    // and ONE server emit, flushed after the keys go quiet: per-keypress
    // entries would bury the undo stack, and per-keypress emits would
    // spray the server with 60 intermediate positions.
    _nudgeSelection: function(dxScreen, dyScreen) {
      var self = this;
      var targets = (this.selectedShapes && this.selectedShapes.length)
        ? this.selectedShapes.slice()
        : (this.editingShape ? [this.editingShape] : []);
      targets = targets.filter(function(s) { return s && s.uuid && !s.readonly; });
      if (!targets.length) return false;

      var scale = 1;
      try { scale = this._markerScale() || 1; } catch (_) {}
      var dx = dxScreen / (scale > 0 ? scale : 1);
      var dy = dyScreen / (scale > 0 ? scale : 1);

      if (!this._nudgeBurst) {
        this._nudgeBurst = targets.map(function(s) {
          return { uuid: s.uuid, before: self._snapshotShape(s) };
        });
      }
      targets.forEach(function(shape) {
        shape.geometry = self._translateGeometry(shape.kind, shape.geometry, dx, dy);
        if (shape.metadata && shape.metadata.title_box) {
          var tb = shape.metadata.title_box;
          shape.metadata = Object.assign({}, shape.metadata, {
            title_box: { x: tb.x + dx, y: tb.y + dy, w: tb.w, h: tb.h }
          });
        }
        self._renderShape(shape);
      });
      if (this.editingShape && targets.indexOf(this.editingShape) !== -1) {
        if (this.freehandEditor) this._renderFreehandEditor(this.editingShape);
        else this._positionAllHandles(this.editingShape);
      }
      clearTimeout(this._nudgeFlushTimer);
      this._nudgeFlushTimer = setTimeout(function() { self._flushNudge(); }, 400);
      return true;
    },

    _flushNudge: function() {
      clearTimeout(this._nudgeFlushTimer);
      this._nudgeFlushTimer = null;
      var burst = this._nudgeBurst;
      this._nudgeBurst = null;
      if (!burst) return;
      var self = this;
      this._emitChanged();
      burst.forEach(function(rec) {
        var shape = self.shapes.find(function(s) { return s.uuid === rec.uuid; });
        if (shape) self._pushUndo(rec.uuid, rec.before, self._snapshotShape(shape));
      });
    },

    _isInSelection: function(shape) {
      return !!(this.selectedShapes &&
                this.selectedShapes.indexOf(shape) !== -1);
    },

    // Toggle a shape's render-time lock at runtime (public `setShapeReadonly`).
    // Updates the `data-readonly` attr for styling, and — if the shape just
    // became locked — drops it out of edit mode and any multi-selection.
    _setShapeReadonly: function(uuid, bool) {
      var shape = (this.shapes || []).find(function(s) { return s.uuid === uuid; });
      if (!shape) return false;
      shape.readonly = !!bool;
      if (shape.el) {
        if (shape.readonly) shape.el.setAttribute("data-readonly", "true");
        else shape.el.removeAttribute("data-readonly");
      }
      if (shape.readonly) {
        if (this.editingShape === shape) this._exitEditMode();
        if (this._isInSelection(shape)) this._removeFromSelection(shape);
      }
      return true;
    },

    // Draw/resize the marquee rectangle (container px) during a box-select.
    _updateBoxSelect: function(e) {
      var bs = this._boxSelect;
      if (!bs) return;
      if (!bs.el) {
        var box = document.createElement("div");
        box.className = "etcher-marquee";
        this.overlayWrapper.appendChild(box);
        bs.el = box;
      }
      var r = this.handle.container.getBoundingClientRect();
      var x1 = bs.startX - r.left, y1 = bs.startY - r.top;
      var x2 = e.clientX - r.left, y2 = e.clientY - r.top;
      bs.el.style.left = Math.min(x1, x2) + "px";
      bs.el.style.top = Math.min(y1, y2) + "px";
      bs.el.style.width = Math.abs(x2 - x1) + "px";
      bs.el.style.height = Math.abs(y2 - y1) + "px";
      // The rectangle itself is four style writes, so it tracks the pointer
      // directly. The preview walks every shape, so it gets a frame.
      this._scheduleBoxSelectPreview(bs, e);
    },

    // The image-px rect the marquee currently covers.
    _boxSelectRect: function(bs, e) {
      var s0 = bs && bs.startImg;
      if (!s0) return null;
      var endImg;
      try { endImg = this._toImage(e); } catch (_) { return null; }
      return {
        x1: Math.min(s0.x, endImg.x), y1: Math.min(s0.y, endImg.y),
        x2: Math.max(s0.x, endImg.x), y2: Math.max(s0.y, endImg.y)
      };
    },

    // Every selectable shape the marquee touches (bbox intersection, image
    // px). ONE answer, asked by both the live preview and the commit — which
    // is what makes the preview a promise rather than a good guess.
    _shapesInBox: function(r) {
      var self = this;
      if (!r) return [];
      return (self.shapes || []).filter(function(s) {
        if (!s.uuid) return false;
        if (s.readonly) return false; // locked shapes can't be selected/edited
        var bb = self._shapeBBoxImagePx(s);
        if (!bb) return false;
        return bb.x <= r.x2 && bb.x + bb.w >= r.x1 &&
               bb.y <= r.y2 && bb.y + bb.h >= r.y1;
      });
    },

    // Coalesced to a frame: a pointermove can fire several times between
    // paints, and the preview walks every shape on the board.
    _scheduleBoxSelectPreview: function(bs, e) {
      var self = this;
      // Only the coordinates are kept — the event object itself is reused by
      // the browser and must not be read a frame later.
      bs.previewAt = { clientX: e.clientX, clientY: e.clientY };
      if (typeof requestAnimationFrame !== "function") {
        self._previewBoxSelect(bs);
        return;
      }
      if (bs.previewFrame) return;
      bs.previewFrame = requestAnimationFrame(function() {
        bs.previewFrame = null;
        // The drag ended (or another started) while the frame was pending.
        if (self._boxSelect !== bs) return;
        self._previewBoxSelect(bs);
      });
    },

    // Light up what releasing right now would select, and draw one box
    // around the lot. The shapes wear `is-multi-selected` — the very class
    // the committed selection uses — so the preview IS what selection looks
    // like, rather than a lookalike that can drift from it. Selection state
    // itself (`selectedShapes`) is untouched until release.
    _previewBoxSelect: function(bs) {
      var self = this;
      var next = self._shapesInBox(self._boxSelectRect(bs, bs.previewAt || {}));
      // Shift-extending: shapes already in the group wear the class because
      // they are selected, not because of this drag. Leave them to the
      // selection — un-styling one when the marquee moves off it would be a
      // lie about what release does.
      next = next.filter(function(s) { return !self._isInSelection(s); });

      var prev = bs.preview || [];
      prev.forEach(function(s) {
        if (next.indexOf(s) !== -1) return;
        if (s.el) s.el.classList.remove("is-multi-selected");
        self._refreshImageRing(s);
      });
      next.forEach(function(s) {
        if (prev.indexOf(s) !== -1) return;
        if (s.el) s.el.classList.add("is-multi-selected");
        self._refreshImageRing(s);
      });
      bs.preview = next;
      self._syncBoxSelectHull(next);
    },

    // The box around the whole prospective group, in container px. Grows as
    // shapes join: one shape and it hugs that shape, a second and it spans
    // both, and so on. Includes anything already selected on a shift-extend,
    // since that is all going to end up in one group.
    _syncBoxSelectHull: function(shapes) {
      var self = this;
      var all = (shapes || []).concat(
        (self.selectedShapes || []).filter(function(s) {
          return (shapes || []).indexOf(s) === -1;
        })
      );

      var container = self.handle && self.handle.container;
      if (!all.length || !container || !self.overlayWrapper) {
        self._removeBoxSelectHull();
        return;
      }

      // One rect read for the whole pass — `_imageToContainer` takes its own
      // every call, which at four corners a shape is a forced layout per
      // corner per frame.
      var r = container.getBoundingClientRect();
      var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      all.forEach(function(s) {
        var bb = self._shapeBBoxImagePx(s);
        if (!bb) return;
        // All FOUR corners: on a rotated canvas an image-axis box maps to a
        // rotated quad, and two opposite corners would describe a rectangle
        // that is neither the shape's nor its enclosure.
        var corners = [
          [bb.x, bb.y], [bb.x + bb.w, bb.y],
          [bb.x, bb.y + bb.h], [bb.x + bb.w, bb.y + bb.h]
        ];
        for (var i = 0; i < corners.length; i++) {
          var page;
          try {
            page = self.handle.imageToScreen({ x: corners[i][0], y: corners[i][1] });
          } catch (_) { return; }
          var cx = page.x - r.left, cy = page.y - r.top;
          if (cx < minX) minX = cx;
          if (cy < minY) minY = cy;
          if (cx > maxX) maxX = cx;
          if (cy > maxY) maxY = cy;
        }
      });
      if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
        self._removeBoxSelectHull();
        return;
      }

      var hull = self._marqueeHull;
      if (!hull) {
        hull = document.createElement("div");
        hull.className = "etcher-marquee-hull";
        self.overlayWrapper.appendChild(hull);
        self._marqueeHull = hull;
      }
      // Stood off the shapes so it reads as a box AROUND the group rather
      // than an outline fighting the shape edges it sits on.
      var pad = 5;
      hull.style.left = (minX - pad) + "px";
      hull.style.top = (minY - pad) + "px";
      hull.style.width = Math.max(0, maxX - minX + pad * 2) + "px";
      hull.style.height = Math.max(0, maxY - minY + pad * 2) + "px";
    },

    _removeBoxSelectHull: function() {
      var hull = this._marqueeHull;
      if (hull && hull.parentNode) hull.parentNode.removeChild(hull);
      this._marqueeHull = null;
    },

    // Take the preview back down. Every exit from a box-select runs through
    // here — release, cancel, teardown — so a shape can't be left wearing
    // the selected look without being selected.
    _clearBoxSelectPreview: function(bs) {
      var self = this;
      bs = bs || self._boxSelect;
      if (bs) {
        if (bs.previewFrame && typeof cancelAnimationFrame === "function") {
          try { cancelAnimationFrame(bs.previewFrame); } catch (_) {}
        }
        bs.previewFrame = null;
        (bs.preview || []).forEach(function(s) {
          if (s.el) s.el.classList.remove("is-multi-selected");
          self._refreshImageRing(s);
        });
        bs.preview = null;
      }
      self._removeBoxSelectHull();
    },

    // Select every shape the marquee touches. Non-additive box-selects
    // already cleared the group on press. Asks `_shapesInBox` the same
    // question the preview did, so release commits what was lit up.
    _commitBoxSelect: function(bs, e) {
      var self = this;
      var rect = self._boxSelectRect(bs, e);
      if (!rect) return;
      self._shapesInBox(rect).forEach(function(s) {
        self._addToSelection(s);
      });
    },

    // Drag every multi-selected shape together. `anchorShape` is the
    // one the user grabbed; the gesture's image-space delta is applied
    // uniformly to every shape's geometry, so relative positions are
    // preserved. Title boxes (when present) translate alongside.
    _startMultiShapeMove: function(anchorShape, e) {
      var self = this;
      var startPt = self._toImage(e);
      // Snapshot geometry + title box for every selected shape.
      var snapshots = self.selectedShapes.map(function(s) {
        return {
          shape: s,
          geometry: JSON.parse(JSON.stringify(s.geometry)),
          titleBox: (s.metadata && s.metadata.title_box)
            ? Object.assign({}, s.metadata.title_box)
            : null,
          history: self._snapshotShape(s)
        };
      });

      var dragged = false;
      try { anchorShape.el.setPointerCapture(e.pointerId); } catch (_) {}
      self._hideTooltip();

      function onMove(ev) {
        var pt = self._toImage(ev);
        if (!dragged) {
          var a = self._imageToContainer(startPt);
          var b = self._imageToContainer(pt);
          var sdx = b.x - a.x, sdy = b.y - a.y;
          // Same 3-px dead zone as `_startShapeMove` — a stationary
          // click on a multi-selected shape shouldn't fire a network
          // round-trip.
          if (sdx * sdx + sdy * sdy < 9) return;
          dragged = true;
          snapshots.forEach(function(snap) {
            if (snap.shape.el) snap.shape.el.classList.add("is-moving");
          });
        }
        var dxI = pt.x - startPt.x;
        var dyI = pt.y - startPt.y;
        snapshots.forEach(function(snap) {
          snap.shape.geometry = self._translateGeometry(
            snap.shape.kind, snap.geometry, dxI, dyI
          );
          if (snap.titleBox) {
            snap.shape.metadata = Object.assign({}, snap.shape.metadata || {}, {
              title_box: {
                x: snap.titleBox.x + dxI,
                y: snap.titleBox.y + dyI,
                w: snap.titleBox.w,
                h: snap.titleBox.h
              }
            });
          }
          self._renderShape(snap.shape);
        });
      }

      function onUp(ev) {
        anchorShape.el.removeEventListener("pointermove", onMove);
        anchorShape.el.removeEventListener("pointerup", onUp);
        anchorShape.el.removeEventListener("pointercancel", onUp);
        try { anchorShape.el.releasePointerCapture(ev.pointerId); } catch (_) {}
        snapshots.forEach(function(snap) {
          if (snap.shape.el) snap.shape.el.classList.remove("is-moving");
        });
        if (!dragged) return;
        // One undo entry per shape — keeps the existing per-shape
        // restore logic; the user will need to ⌘Z N times to walk
        // the entire group back. (Undoing the whole-group move with
        // one keystroke would require a new bulk_move undo entry
        // type; out of scope here.)
        snapshots.forEach(function(snap) {
          if (snap.shape.uuid) {
            self._pushUndo(snap.shape.uuid, snap.history, self._snapshotShape(snap.shape));
          }
        });
        self._emitChanged();
      }

      anchorShape.el.addEventListener("pointermove", onMove);
      anchorShape.el.addEventListener("pointerup", onUp);
      anchorShape.el.addEventListener("pointercancel", onUp);
    },

    // ---- Transform box --------------------------------------------------
    //
    // A frame around what is selected — a group, or a single drawing made
    // by hand (marker, highlighter, freehand), whose own handles are its
    // pen anchors — with a dot on each corner that scales it all and a
    // knob above that turns it all. A corner scales evenly about the
    // opposite corner (Shift frees each axis); the knob turns about the
    // middle (Shift steps 15°). A press inside the frame moves the lot.
    // Everything works off a snapshot taken at the press, so a drag never
    // accumulates error, and lets go into one undo step per shape — the
    // same accounting the group move keeps.

    _xfOwnsSingle: function(shape) {
      return !!shape && !shape.readonly && (shape.kind === "marker" || shape.kind === "freehand");
    },

    _xfTargets: function() {
      if (!this.annotationMode || this.activeTool != null) return [];
      var self = this;
      var sel = (this.selectedShapes || []).filter(function(s) {
        return s && s.el && !s.readonly && self.shapes.indexOf(s) !== -1;
      });
      if (sel.length >= 2 || (sel.length === 1 && !this.editingShape)) return sel;
      if (this.editingShape && this._xfOwnsSingle(this.editingShape)) return [this.editingShape];
      return [];
    },

    // The union of the targets' boxes in image px, a stroke's own width
    // included so the frame stands around the ink, not its centre line.
    _xfUnion: function(targets) {
      var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      var self = this;
      targets.forEach(function(s) {
        var bb = self._shapeBBoxImagePx(s);
        if (!bb) return;
        var half = (s.kind === "marker" || s.kind === "freehand") &&
          s.style && s.style.width > 0 ? s.style.width / 2 : 0;
        minX = Math.min(minX, bb.x - half); minY = Math.min(minY, bb.y - half);
        maxX = Math.max(maxX, bb.x + bb.w + half); maxY = Math.max(maxY, bb.y + bb.h + half);
      });
      if (!isFinite(minX)) return null;
      return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    },

    _syncTransformBox: function() {
      var targets = this._xfTargets();
      var xf = this._xf;
      if (!targets.length) { this._removeTransformBox(); return; }
      var same = xf && xf.targets.length === targets.length &&
        xf.targets.every(function(s, i) { return s === targets[i]; });
      if (!same || !xf.frame.parentNode || xf.frame.ownerSVGElement !== this.svg) {
        this._removeTransformBox();
        this._buildTransformBox(targets);
      }
      this._positionTransformBox();
    },

    _buildTransformBox: function(targets) {
      var self = this;
      if (!this.svg) return;
      var frame = svgEl("path", {});
      frame.classList.add("etcher-xf-frame");
      this.svg.appendChild(frame);
      var stem = svgEl("line", {});
      stem.classList.add("etcher-rotate-stem");
      stem.style.color = "#3b82f6";
      this.svg.appendChild(stem);
      var corners = [0, 1, 2, 3].map(function(i) {
        var c = svgEl("circle", { r: 5 });
        c.classList.add("etcher-handle", "etcher-xf-corner");
        c.style.color = "#3b82f6";
        c.dataset.corner = String(i);
        self.svg.appendChild(c);
        var onDown = function(e) { self._startTransformScale(i, c, e); };
        c.addEventListener("pointerdown", onDown);
        self._addGrabHalo(c, HANDLE_GRAB_RADIUS, onDown);
        return c;
      });
      var knob = svgEl("circle", { r: 5 });
      knob.classList.add("etcher-handle", "etcher-rotate-handle", "etcher-xf-knob");
      knob.style.color = "#3b82f6";
      knob.setAttribute("aria-label", "Rotate");
      this.svg.appendChild(knob);
      var onRot = function(e) { self._startTransformRotate(knob, e); };
      knob.addEventListener("pointerdown", onRot);
      this._addGrabHalo(knob, HANDLE_GRAB_RADIUS, onRot);
      this._xf = { targets: targets.slice(), frame: frame, stem: stem, corners: corners, knob: knob, box: null, turn: null };
    },

    _removeTransformBox: function() {
      var xf = this._xf;
      if (!xf) return;
      var self = this;
      [xf.frame, xf.stem].forEach(function(el) { if (el && el.parentNode) el.parentNode.removeChild(el); });
      xf.corners.concat([xf.knob]).forEach(function(el) { self._dropHandleEl(el); });
      this._xf = null;
    },

    // Re-place the frame: on every pan and zoom, after every render of a
    // shape inside it, and through a drag (its start box, turned by the
    // knob's angle while one is being turned).
    _positionTransformBox: function() {
      var xf = this._xf;
      if (!xf) return;
      var box = xf.drag ? xf.drag.box : this._xfUnion(xf.targets);
      if (!box) return;
      xf.box = box;
      var scale = 1;
      try { scale = this._markerScale() || 1; } catch (_) { scale = 1; }
      var pad = 4 / scale;
      var b = { x: box.x - pad, y: box.y - pad, w: box.w + 2 * pad, h: box.h + 2 * pad };
      var c = { x: box.x + box.w / 2, y: box.y + box.h / 2 };
      var turn = xf.turn || 0;
      var self = this;
      var put = function(p) { return self._rotatePoint(p, c.x, c.y, turn); };
      var pts = [
        { x: b.x, y: b.y }, { x: b.x + b.w, y: b.y },
        { x: b.x + b.w, y: b.y + b.h }, { x: b.x, y: b.y + b.h }
      ].map(put);
      var scr = pts.map(function(p) { return self._imageToContainer(p); });
      xf.frame.setAttribute("d", "M" + scr.map(function(q) { return q.x + " " + q.y; }).join("L") + "Z");
      pts.forEach(function(p, i) { self._positionHandle(xf.corners[i], p); });
      var top = put({ x: c.x, y: b.y });
      var knobAt = put({ x: c.x, y: b.y - ROTATE_KNOB_PX / scale });
      this._positionHandle(xf.knob, knobAt);
      var ta = this._imageToContainer(top), tb = this._imageToContainer(knobAt);
      xf.stem.setAttribute("x1", ta.x); xf.stem.setAttribute("y1", ta.y);
      xf.stem.setAttribute("x2", tb.x); xf.stem.setAttribute("y2", tb.y);
      this._syncGrabRadii(xf.corners);
    },

    // One render of a shape in the frame asks for one re-place, a frame
    // later — a group drag renders every member each move.
    _xfNoteRender: function(shape) {
      var xf = this._xf;
      if (!xf || xf.drag || xf.targets.indexOf(shape) === -1 || xf.pending) return;
      var self = this;
      xf.pending = true;
      var run = function() { if (self._xf === xf) { xf.pending = false; self._positionTransformBox(); } };
      if (typeof requestAnimationFrame === "function") requestAnimationFrame(run); else setTimeout(run, 16);
    },

    // Whether a press is inside the frame (image px, unturned).
    _xfContains: function(pt) {
      var b = this._xf && this._xf.box;
      if (!b || !pt) return false;
      var scale = 1;
      try { scale = this._markerScale() || 1; } catch (_) { scale = 1; }
      var pad = 4 / scale;
      return pt.x >= b.x - pad && pt.x <= b.x + b.w + pad && pt.y >= b.y - pad && pt.y <= b.y + b.h + pad;
    },

    _xfSnapshot: function(targets) {
      var self = this;
      return targets.map(function(s) {
        return {
          shape: s,
          g: JSON.parse(JSON.stringify(s.geometry)),
          style: Object.assign({}, s.style || {}),
          meta: s.metadata ? Object.assign({}, s.metadata) : null,
          rot: self._shapeRot(s),
          turn: self._shapeTurn(s),
          history: self._snapshotShape(s)
        };
      });
    },

    // Drag plumbing shared by the corners and the knob: capture, the
    // live frame, and one undo step per shape on release.
    _xfDrag: function(el, e, onMove) {
      if (e.button != null && e.button !== 0) return null;
      var xf = this._xf;
      if (!xf || !xf.box) return null;
      e.preventDefault();
      e.stopPropagation();
      var self = this;
      try { el.setPointerCapture(e.pointerId); } catch (_) {}
      el.classList.add("is-dragging");
      this._hideTooltip();
      this._suspendMidpointHighlight();
      var snaps = this._xfSnapshot(xf.targets);
      xf.drag = { box: Object.assign({}, xf.box), snaps: snaps, changed: false };
      var refresh = function() {
        snaps.forEach(function(sn) { self._renderShape(sn.shape); });
        var ed = self.editingShape;
        if (ed && xf.targets.indexOf(ed) !== -1) {
          if (self.freehandEditor) self._renderFreehandEditor(ed);
          else self._positionAllHandles(ed);
        }
        self._positionTransformBox();
      };
      var move = function(ev) {
        onMove(ev, xf.drag);
        xf.drag.changed = true;
        refresh();
      };
      var up = function(ev) {
        el.classList.remove("is-dragging");
        el.removeEventListener("pointermove", move);
        el.removeEventListener("pointerup", up);
        el.removeEventListener("pointercancel", up);
        try { el.releasePointerCapture(ev.pointerId); } catch (_) {}
        if (self.svg) self.svg.classList.remove("is-rotating");
        self._resumeMidpointHighlight();
        var changed = xf.drag && xf.drag.changed;
        xf.drag = null;
        xf.turn = null;
        if (changed) {
          snaps.forEach(function(sn) {
            if (sn.shape.uuid) self._pushUndo(sn.shape.uuid, sn.history, self._snapshotShape(sn.shape));
          });
          self._emitChanged();
          self._syncStyleInspector();
        }
        if (self._xf === xf) self._positionTransformBox();
      };
      el.addEventListener("pointermove", move);
      el.addEventListener("pointerup", up);
      el.addEventListener("pointercancel", up);
      return xf.drag;
    },

    _startTransformScale: function(i, el, e) {
      var self = this;
      var b = this._xf && this._xf.box;
      if (!b) return;
      var C = [{ x: b.x, y: b.y }, { x: b.x + b.w, y: b.y }, { x: b.x + b.w, y: b.y + b.h }, { x: b.x, y: b.y + b.h }];
      var P = C[(i + 2) % 4], Q = C[i];
      var v0 = { x: Q.x - P.x, y: Q.y - P.y };
      var MIN = 0.02;
      this._xfDrag(el, e, function(ev, d) {
        var pt = self._toImage(ev);
        var v = { x: pt.x - P.x, y: pt.y - P.y };
        var sx, sy;
        var thinX = Math.abs(v0.x) < 1e-6, thinY = Math.abs(v0.y) < 1e-6;
        if (ev.shiftKey && !thinX && !thinY) {
          sx = Math.max(MIN, v.x / v0.x);
          sy = Math.max(MIN, v.y / v0.y);
        } else {
          var k = (v.x * v0.x + v.y * v0.y) / (v0.x * v0.x + v0.y * v0.y || 1);
          sx = sy = Math.max(MIN, k);
        }
        d.box = { x: Math.min(P.x, P.x + v0.x * sx), y: Math.min(P.y, P.y + v0.y * sy),
          w: Math.abs(v0.x * sx), h: Math.abs(v0.y * sy) };
        d.snaps.forEach(function(sn) { self._xfScaleShape(sn, P, sx, sy); });
      });
    },

    _startTransformRotate: function(el, e) {
      var self = this;
      var b = this._xf && this._xf.box;
      if (!b) return;
      var c = { x: b.x + b.w / 2, y: b.y + b.h / 2 };
      var p0 = this._toImage(e);
      var a0 = Math.atan2(p0.y - c.y, p0.x - c.x);
      var xf = this._xf;
      if (this._xfDrag(el, e, function(ev, d) {
        var p = self._toImage(ev);
        var deg = (Math.atan2(p.y - c.y, p.x - c.x) - a0) * 180 / Math.PI;
        if (ev.shiftKey) deg = Math.round(deg / 15) * 15;
        xf.turn = deg;
        d.snaps.forEach(function(sn) { self._xfRotateShape(sn, c, deg); });
      }) && this.svg) this.svg.classList.add("is-rotating");
    },

    _xfNorm: function(a) {
      a = ((a % 360) + 540) % 360 - 180;
      return Math.abs(a) < 0.01 ? 0 : a;
    },

    // One shape of the selection scaled by (sx, sy) about P, from its
    // snapshot: its points or its box, and what is measured in its units
    // — stroke, text size, corners, its label's box — by the even part.
    _xfScaleShape: function(sn, P, sx, sy) {
      var self = this;
      var s = sn.shape, g = sn.g;
      var k = Math.sqrt(Math.abs(sx * sy));
      var M = function(p) { return [P.x + (p[0] - P.x) * sx, P.y + (p[1] - P.y) * sy]; };
      var Mo = function(o) { return { x: P.x + (o.x - P.x) * sx, y: P.y + (o.y - P.y) * sy }; };
      var V = function(v) { return v ? [v[0] * sx, v[1] * sy] : v; };
      var out = JSON.parse(JSON.stringify(g));
      var style = Object.assign({}, sn.style);
      var centre = null;
      switch (s.kind) {
        case "polygon": case "freehand": case "marker":
        case "arrow": case "line": case "dimension":
          if (g.points) out.points = g.points.map(M);
          if (g.nodes) {
            out.nodes = g.nodes.map(function(n) {
              return Object.assign({}, n, { p: M(n.p), hIn: V(n.hIn), hOut: V(n.hOut) });
            });
          }
          if (g.a) out.a = M(g.a);
          if (g.b) out.b = M(g.b);
          break;
        case "circle": {
          var cc = Mo({ x: g.cx, y: g.cy });
          out.cx = cc.x; out.cy = cc.y; out.r = g.r * k;
          break;
        }
        case "callout": {
          var tb = self._calloutTextBoxImage(g);
          if (g.anchor) out.anchor = M(g.anchor);
          var tc = Mo({ x: tb.x + tb.w / 2, y: tb.y + tb.h / 2 });
          var tw = tb.w * Math.abs(sx), th = tb.h * Math.abs(sy);
          out.text_box = { x: tc.x - tw / 2, y: tc.y - th / 2, w: tw, h: th };
          break;
        }
        default: {
          // A box (rectangle, image, text, card): its middle goes where the
          // scale takes it; its sides scale along the frame's axes when it
          // is square to them, evenly when it is turned between.
          var r = ((Math.round(sn.rot) % 180) + 180) % 180;
          var upright = Math.abs(sn.rot - Math.round(sn.rot / 90) * 90) < 0.5;
          var fx = !upright ? k : (r === 90 ? Math.abs(sy) : Math.abs(sx));
          var fy = !upright ? k : (r === 90 ? Math.abs(sx) : Math.abs(sy));
          centre = Mo({ x: g.x + g.w / 2, y: g.y + g.h / 2 });
          out.w = g.w * fx; out.h = g.h * fy;
          out.x = centre.x - out.w / 2; out.y = centre.y - out.h / 2;
          if (self._isPointText(s) && Math.abs(fx - fy) > 1e-6) {
            // Point text stretches rather than skews: the even part is its
            // size, the rest its stretch along each axis.
            var ke = Math.sqrt(fx * fy);
            style.scale_x = (sn.style.scale_x || 1) * fx / ke;
            style.scale_y = (sn.style.scale_y || 1) * fy / ke;
          }
        }
      }
      if (sn.style.width > 0) style.width = sn.style.width * k;
      if (sn.style.font_size > 0) style.font_size = sn.style.font_size * k;
      if (sn.style.corner_size > 0) style.corner_size = sn.style.corner_size * k;
      s.geometry = out;
      s.style = style;
      if (sn.meta && sn.meta.title_box) {
        var lb = sn.meta.title_box;
        var lc = Mo({ x: lb.x + lb.w / 2, y: lb.y + lb.h / 2 });
        var lw = lb.w * k, lh = lb.h * k;
        s.metadata = Object.assign({}, sn.meta, { title_box: { x: lc.x - lw / 2, y: lc.y - lh / 2, w: lw, h: lh } });
      }
      // Point text writes its own box from its size: render it, then set
      // it back about the middle the scale asked for.
      if (centre && this._isPointText(s)) {
        this._renderShape(s);
        var gg = s.geometry;
        s.geometry = Object.assign({}, gg, { x: centre.x - gg.w / 2, y: centre.y - gg.h / 2 });
      }
    },

    // One shape of the selection turned by `deg` about C: a point-built
    // shape's points turn (and its turn count with them, as the knob keeps
    // it); a box's middle swings round and the box turns on it.
    _xfRotateShape: function(sn, C, deg) {
      var self = this;
      var s = sn.shape, g = sn.g;
      var R = function(o) { return self._rotatePoint(o, C.x, C.y, deg); };
      var style = Object.assign({}, sn.style);
      var out;
      switch (s.kind) {
        case "polygon": case "freehand": case "marker":
        case "arrow": case "line": case "dimension":
          out = this._rotateGeometry(s.kind, g, C, deg);
          style.turn = this._xfNorm(sn.turn + deg);
          if (!this._labelRidesShaft(s.kind)) {
            style.title_rotation = this._xfNorm((typeof sn.style.title_rotation === "number" ? sn.style.title_rotation : 0) + deg);
          }
          break;
        case "circle": {
          var cc = R({ x: g.cx, y: g.cy });
          out = Object.assign({}, g, { cx: cc.x, cy: cc.y });
          break;
        }
        case "callout": {
          var tb = this._calloutTextBoxImage(g);
          var a = g.anchor ? R({ x: g.anchor[0], y: g.anchor[1] }) : null;
          var tc = R({ x: tb.x + tb.w / 2, y: tb.y + tb.h / 2 });
          out = Object.assign({}, g, {
            anchor: a ? [a.x, a.y] : g.anchor,
            text_box: { x: tc.x - tb.w / 2, y: tc.y - tb.h / 2, w: tb.w, h: tb.h }
          });
          break;
        }
        default: {
          var bc = R({ x: g.x + g.w / 2, y: g.y + g.h / 2 });
          out = Object.assign({}, g, { x: bc.x - g.w / 2, y: bc.y - g.h / 2 });
          if (this._isBoxRotatable(s)) style.rotation = this._xfNorm(sn.rot + deg);
        }
      }
      s.geometry = out;
      s.style = style;
      if (sn.meta && sn.meta.title_box) {
        var lb = sn.meta.title_box;
        var lc = R({ x: lb.x + lb.w / 2, y: lb.y + lb.h / 2 });
        s.metadata = Object.assign({}, sn.meta, { title_box: { x: lc.x - lb.w / 2, y: lc.y - lb.h / 2, w: lb.w, h: lb.h } });
      }
    },

    // Batch-delete every multi-selected shape under a single
    // `bulk_delete` undo entry. One `etcher:annotations-changed` emit
    // covers the whole group so the consumer LV doesn't see a flurry
    // of full-array replays.
    _deleteSelectedShapes: function() {
      var self = this;
      var list = (self.selectedShapes || []).slice();
      if (list.length === 0) return;
      var withUuids = list.filter(function(s) { return s && s.uuid; });
      if (withUuids.length) self._pushUndoBulkDelete(withUuids);
      list.forEach(function(shape) {
        if (self.editingShape === shape) self._exitEditMode();
        var idx = self.shapes.indexOf(shape);
        if (idx === -1) return;
        if (shape.el && shape.el.parentNode) {
          shape.el.parentNode.removeChild(shape.el);
        }
        if (shape.titleGroup && shape.titleGroup.parentNode) {
          shape.titleGroup.parentNode.removeChild(shape.titleGroup);
        }
        self.shapes.splice(idx, 1);
      });
      self.selectedShapes = [];
      self._syncTransformBox();
      self._hideTooltip();
      self._emitChanged();
    },

    // -------------------------------------------------------------------------
    // Tap-cycling
    //
    // Selection resolves to the topmost shape under the point and stops
    // there, so anything fully covered is unreachable — you cannot select
    // it, which means you cannot arrange it out from under whatever covers
    // it. Most painful on touch, where there is no precision to hunt for an
    // uncovered sliver, but it bites with a mouse too.
    //
    // Tapping the same place repeatedly therefore walks *down* the stack,
    // reusing the same per-kind geometric hit-test ordinary selection runs
    // on (`_shapeContainsPoint`). It has to be geometric: shapes are
    // `pointer-events: none` so pan and zoom pass through them, which means
    // the DOM cannot hit-test them at all.
    // -------------------------------------------------------------------------

    // Screen px the pointer may wander between taps and still count as "the
    // same place" — a finger never lands twice on the same pixel.
    _TAP_CYCLE_SLOP: 14,
    // After this long, a tap reads as fresh intent, not a continuation.
    _TAP_CYCLE_MS: 1500,
    // Quicker than this, the second tap is the other half of a double-click
    // — "edit this one" — and must not walk down to the shape underneath.
    _TAP_CYCLE_DBL_MS: 500,

    // A double-click edits the shape its first click selected — never one
    // underneath it. Reported from the field: double-clicking the empty
    // part of a text box opened the callout beneath it instead. The DOM
    // target can't be trusted for this (a selected shape takes pointer
    // events; the one on top may not), so: if the point lies on the shape
    // being edited, that is the one; otherwise the one the caller found.
    _dblClickTarget: function(shape, e) {
      var cur = this.editingShape;
      if (!cur || cur === shape || cur.readonly) return shape;
      var pt;
      try { pt = this._toImage(e); } catch (_) { return shape; }
      return this._shapeContainsPoint(cur, pt) ? cur : shape;
    },

    // All shapes under an image-space point, topmost first.
    //
    // Deliberately built on `_shapeContainsPoint` — the same per-kind test
    // `_shapeAt` uses — rather than `document.elementsFromPoint`. Shapes are
    // `pointer-events: none` by design so pan and zoom pass through them, so
    // the DOM cannot hit-test them at all; selection has always been
    // geometric. Reusing that keeps cycling agreeing exactly with what a
    // plain click would have picked.
    _shapesAtImagePoint: function(pt) {
      var self = this;
      if (!self.shapes || !pt) return [];
      var stripMode = self.handleKind === "strip";
      var ptImageIdx = stripMode && typeof pt.imageIdx === "number" ? pt.imageIdx : null;

      var out = [];
      for (var i = self.shapes.length - 1; i >= 0; i--) {
        var s = self.shapes[i];
        if (!s.uuid) continue;
        if (stripMode && s.image_idx !== ptImageIdx) continue;
        if (self._shapeContainsPoint(s, pt)) out.push(s);
      }
      return out;
    },

    // Given the shape a plain hit-test picked, return the one the user
    // means: that same shape on a first tap, the next one down on a repeat.
    _cycleTapTarget: function(shape, e) {
      var self = this;
      if (!e || typeof e.clientX !== "number") return shape;
      // Only while selecting — with a drawing tool armed a tap is drawing,
      // and outside annotation mode it is browsing.
      if (!self.annotationMode || self.activeTool != null) return shape;

      var pt;
      try { pt = self._toImage(e); } catch (_) { return shape; }

      var now = (typeof performance !== "undefined" && performance.now)
        ? performance.now()
        : Date.now();
      var prev = self._tapCycle;
      var dx = prev ? e.clientX - prev.x : Infinity;
      var dy = prev ? e.clientY - prev.y : Infinity;
      var samePlace =
        !!prev &&
        (dx * dx + dy * dy) <= self._TAP_CYCLE_SLOP * self._TAP_CYCLE_SLOP &&
        (now - prev.t) <= self._TAP_CYCLE_MS;

      var stack = self._shapesAtImagePoint(pt);
      // Nothing overlapping here — still record the tap so a later one
      // nearby doesn't inherit a stale index.
      if (stack.length < 2) {
        self._tapCycle = { x: e.clientX, y: e.clientY, t: now, index: 0 };
        return shape;
      }

      var index;
      if (samePlace && now - prev.t < self._TAP_CYCLE_DBL_MS) {
        // The second half of a double-click: stay on the same shape.
        index = Math.min(prev.index, stack.length - 1);
      } else if (samePlace) {
        index = (prev.index + 1) % stack.length;
      } else {
        // Start from whatever a plain hit-test picked rather than assuming
        // the top, so the first tap agrees with an ordinary click.
        var at = stack.indexOf(shape);
        index = at === -1 ? 0 : at;
      }

      self._tapCycle = { x: e.clientX, y: e.clientY, t: now, index: index };
      return stack[index] || shape;
    },

    // -------------------------------------------------------------------------
    // Z-order (arrange)
    //
    // Paint order is simply position in `this.shapes`: `_renderAnnotation`
    // appends each new shape's node to the SVG, so the newest paints last and
    // covers whatever is under it. That's why an image pasted after a caption
    // hides it, with no way out — nothing reordered the list.
    //
    // The array is the source of truth (it's what `_emitChanged` serializes,
    // so consumers persist the order for free); the DOM is brought back in
    // line afterwards by `_syncShapeOrder`.
    // -------------------------------------------------------------------------

    // Shapes the arrange actions apply to: the multi-selection when there is
    // one, else the shape in edit mode. Mirrors `_paramsTargetShapes` but
    // without its kind filter — arranging means something for every kind, and
    // images and text are exactly the two that filter excludes.
    _arrangeTargets: function() {
      if (this.selectedShapes && this.selectedShapes.length) {
        return this.selectedShapes.slice();
      }
      if (this.editingShape) return [this.editingShape];
      return [];
    },

    _canArrange: function() {
      return !!this.annotationMode && this._arrangeTargets().length > 0;
    },

    // `where` is "front" | "back" | "forward" | "backward".
    _arrange: function(where) {
      var self = this;
      var targets = self._arrangeTargets().filter(function(s) {
        return s && self.shapes.indexOf(s) !== -1;
      });
      if (targets.length === 0) return false;

      var before = self.shapes.map(function(s) { return s.uuid; });
      if (!self._reorderShapes(targets, where)) return false;

      self._syncShapeOrder();
      self._pushUndoReorder(before, self.shapes.map(function(s) { return s.uuid; }));
      self._emitChanged();
      self._syncArrangeButtons();
      return true;
    },

    // Reorders `this.shapes` in place. Returns false when the move is a
    // no-op (already at the edge), so the caller can skip the undo entry and
    // the change event rather than filling history with nothing.
    _reorderShapes: function(targets, where) {
      var self = this;
      var picked = new Set(targets);
      var next;

      if (where === "front" || where === "back") {
        var rest = self.shapes.filter(function(s) { return !picked.has(s); });
        // Keep targets in their existing relative order rather than
        // selection order, or arranging a multi-selection reshuffles it.
        var group = self.shapes.filter(function(s) { return picked.has(s); });
        next = where === "front" ? rest.concat(group) : group.concat(rest);
      } else {
        // Step one position. Walk from the end for "forward" and from the
        // start for "backward" so a block of adjacent targets slides as a
        // unit instead of each one trampling the next.
        next = self.shapes.slice();
        var step = where === "forward" ? 1 : -1;
        var order = [];
        next.forEach(function(s, i) { if (picked.has(s)) order.push(i); });
        if (where === "forward") order.reverse();

        for (var k = 0; k < order.length; k++) {
          var i = order[k];
          var j = i + step;
          // At the edge, or blocked by another selected shape already
          // parked there — leave this one where it is.
          if (j < 0 || j >= next.length) continue;
          if (picked.has(next[j])) continue;
          var tmp = next[i];
          next[i] = next[j];
          next[j] = tmp;
        }
      }

      var changed = next.some(function(s, i) { return s !== self.shapes[i]; });
      if (!changed) return false;
      self.shapes = next;
      return true;
    },

    // Bring the SVG in line with the array.
    //
    // Shapes share the SVG with transient chrome — edit handles, the
    // in-progress draft, the inline text editor — all appended last and all
    // of which MUST stay on top to stay clickable. So rather than
    // re-appending shapes (which would bury that chrome), they're inserted
    // before the trailing run of nodes we don't own.
    _syncShapeOrder: function() {
      var svg = this.svg;
      if (!svg) return;

      var owned = new Set();
      this.shapes.forEach(function(s) {
        if (s.el) owned.add(s.el);
        if (s.titleGroup) owned.add(s.titleGroup);
      });

      var anchor = null;
      for (var i = svg.childNodes.length - 1; i >= 0; i--) {
        if (owned.has(svg.childNodes[i])) break;
        anchor = svg.childNodes[i];
      }

      // The sequence these elements should end up in, immediately before
      // `anchor`. A shape's title rides directly above it.
      var desired = [];
      this.shapes.forEach(function(s) {
        if (s.el && s.el.parentNode === svg) desired.push(s.el);
        if (s.titleGroup && s.titleGroup.parentNode === svg) desired.push(s.titleGroup);
      });

      // Move only what is out of place.
      //
      // `insertBefore` on a node that is already where it belongs still
      // detaches and re-attaches it, and the browser repaints it — images
      // re-decode. This ran over every shape on every call, and a consumer
      // re-imposing order after each remote edit therefore flashed the whole
      // board every time anyone moved anything.
      //
      // Walked backwards so each element only has to be compared against the
      // one that follows it, which is already in its final position.
      var ref = anchor;
      for (var d = desired.length - 1; d >= 0; d--) {
        if (desired[d].nextSibling !== ref) svg.insertBefore(desired[d], ref);
        ref = desired[d];
      }
    },

    // Restore a recorded order (undo/redo, or a peer's order in a
    // collaborative host). Shapes created since the entry was recorded
    // aren't in `uuids`; they keep their relative position at the end
    // rather than being dropped.
    _applyShapeOrder: function(uuids) {
      var remaining = {};
      this.shapes.forEach(function(s) { remaining[s.uuid] = s; });

      var next = [];
      (uuids || []).forEach(function(uuid) {
        var shape = remaining[uuid];
        if (shape) {
          next.push(shape);
          delete remaining[uuid];
        }
      });
      this.shapes.forEach(function(s) {
        if (remaining[s.uuid]) next.push(s);
      });

      this.shapes = next;
      this._syncShapeOrder();
      this._syncArrangeButtons();
    },

    _pushUndoReorder: function(before, after) {
      this._undoStack = this._undoStack || [];
      this._redoStack = this._redoStack || [];
      this._undoStack.push({ type: "reorder", before: before, after: after });
      if (this._undoStack.length > this._undoStackLimit) this._undoStack.shift();
      this._redoStack = [];
      this._refreshUndoButtons();
    },

    _deleteShape: function(shape) {
      if (!shape) return;
      // Capture for undo BEFORE we tear the shape down — the snapshot
      // is what `_recreateFromSnapshot` will use to rebuild the row.
      // Single deletes ride the same bulk path as the eraser so they
      // get matching undo + redo support out of the box.
      if (shape.uuid) this._pushUndoBulkDelete([shape]);
      var uuid = shape.uuid;
      if (this.editingShape === shape) this._exitEditMode();
      // Optimistic local removal so the UI feels instant. Server still
      // gets the etcher:deleted event below to persist the change.
      var idx = this.shapes.indexOf(shape);
      if (idx !== -1) {
        if (shape.el && shape.el.parentNode) shape.el.parentNode.removeChild(shape.el);
        if (shape.titleGroup && shape.titleGroup.parentNode) {
          shape.titleGroup.parentNode.removeChild(shape.titleGroup);
        }
        this.shapes.splice(idx, 1);
      }
      // A media element lives outside the overlay, so nothing above removes
      // it. Left behind, a deleted audio file goes on playing with no card to
      // stop it — the sound of a shape that is not there.
      this._releaseMediaEl(shape);
      this._sweepImageRings();
      this._sweepMediaEls();
      // Dots belonging to the shape that just went away would be left
      // floating over empty canvas, still draggable into a connector
      // bound to a uuid that no longer exists.
      if (this._connectorDotShape === shape) this._removeConnectorDots();
      // Connectors that pointed at it keep their last coordinates (see
      // `_resolveArrowEnds`) but must be redrawn, since until now they
      // were being positioned by a shape that's no longer in the list.
      var orphans = this._arrowsBoundTo(uuid);
      if (orphans.length) {
        var self = this;
        orphans.forEach(function(a) { self._renderShape(a); });
      }
      this._hideTooltip();
      this._emitChanged();
    },

    // -------------------------------------------------------------------------
    // Rectangle
    // -------------------------------------------------------------------------

    _startRectangle: function(pt, e) {
      var rect = svgEl("rect", { "stroke-width": "2" });
      rect.classList.add("etcher-shape", "is-draft");
      var rectStyle = this._styleForNewShape("rectangle");
      this._applyShapeColor(rect, this.activeColor, rectStyle);
      this.svg.appendChild(rect);
      var geom = { x: pt.x, y: pt.y, w: 0, h: 0 };
      // `style` on the draft is what makes `_renderShape` paint the real
      // thickness / dash / fill on every frame of the drag.
      this.draftState = {
        kind: "rectangle", anchor: pt, geometry: geom, el: rect, style: rectStyle
      };
      this._renderShape(this.draftState);
      this._syncDraftHandles();
      try { e.target.setPointerCapture(e.pointerId); } catch (_) {}
    },

    // Shift-constrain a rectangle drag to a square: the longer axis wins,
    // and the corner stays in the quadrant the drag went. Same convention
    // as every comparable tool.
    _constrainRectPoint: function(a, pt) {
      var dx = pt.x - a.x;
      var dy = pt.y - a.y;
      var side = Math.max(Math.abs(dx), Math.abs(dy));
      return {
        x: a.x + (dx < 0 ? -side : side),
        y: a.y + (dy < 0 ? -side : side)
      };
    },

    // Shift-constrain a shaft endpoint to 45° increments around its
    // anchor, length preserved — a shift-drawn dimension is how you get an
    // exactly horizontal or vertical measurement.
    _constrainShaftPoint: function(a, pt) {
      var dx = pt.x - a.x;
      var dy = pt.y - a.y;
      var len = Math.sqrt(dx * dx + dy * dy);
      if (!len) return pt;
      var step = Math.PI / 4;
      var ang = Math.round(Math.atan2(dy, dx) / step) * step;
      return { x: a.x + Math.cos(ang) * len, y: a.y + Math.sin(ang) * len };
    },

    _updateRectangle: function(pt) {
      var a = this.draftState.anchor;
      if (this.draftState.shift) pt = this._constrainRectPoint(a, pt);
      this.draftState.geometry = {
        x: Math.min(a.x, pt.x), y: Math.min(a.y, pt.y),
        w: Math.abs(pt.x - a.x), h: Math.abs(pt.y - a.y)
      };
      this._renderShape(this.draftState);
      this._positionAllHandles(this.draftState);
    },

    _commitRectangle: function(pt) {
      var a = this.draftState.anchor;
      if (this.draftState.shift) pt = this._constrainRectPoint(a, pt);
      var geom = {
        x: Math.min(a.x, pt.x),
        y: Math.min(a.y, pt.y),
        w: Math.abs(pt.x - a.x),
        h: Math.abs(pt.y - a.y)
      };
      if (this._isClickGesture(a, pt)) {
        // A bare click places nothing: it sets the first corner and ARMS the
        // draft, which follows the cursor until a click somewhere else sets
        // the opposite corner (`_placeArmedDraft`). A default-sized square
        // dropped on every click was a placeholder people then had to deal
        // with — and a click back on the same spot cancels, so clicking one
        // place over and over makes nothing at all.
        this.draftState.armed = true;
        this._syncDraftHandles();
        return;
      } else {
        // A real drag that still spans nothing on one axis (a fast
        // horizontal swipe has width but no height) can't make a usable
        // shape — drop it. Judged in screen px like the click check: the
        // old image-px compare canceled deliberate small drags at high
        // zoom, where 20 screen px of intent is under 2 image px.
        var rScale = 1;
        try { rScale = this._markerScale() || 1; } catch (_) {}
        if (geom.w * rScale < 2 || geom.h * rScale < 2) {
          this._cancelDraft();
          return;
        }
      }
      var el = this.draftState.el;
      el.classList.remove("is-draft");
      this._finalizeShape("rectangle", geom, el);
    },

    // -------------------------------------------------------------------------
    // Circle
    // -------------------------------------------------------------------------

    _startCircle: function(pt, e) {
      var circle = svgEl("circle", { "stroke-width": "2" });
      circle.classList.add("etcher-shape", "is-draft");
      var circleStyle = this._styleForNewShape("circle");
      this._applyShapeColor(circle, this.activeColor, circleStyle);
      this.svg.appendChild(circle);
      var geom = { cx: pt.x, cy: pt.y, r: 0 };
      this.draftState = {
        kind: "circle", center: pt, geometry: geom, el: circle, style: circleStyle
      };
      this._renderShape(this.draftState);
      this._syncDraftHandles();
      try { e.target.setPointerCapture(e.pointerId); } catch (_) {}
    },

    _updateCircle: function(pt) {
      var c = this.draftState.center;
      var dx = pt.x - c.x, dy = pt.y - c.y;
      this.draftState.geometry = { cx: c.x, cy: c.y, r: Math.sqrt(dx * dx + dy * dy) };
      this._renderShape(this.draftState);
      this._positionAllHandles(this.draftState);
    },

    _commitCircle: function(pt) {
      var c = this.draftState.center;
      var dx = pt.x - c.x, dy = pt.y - c.y;
      var r = Math.sqrt(dx * dx + dy * dy);
      if (this._isClickGesture(c, pt)) {
        // A bare click sets the centre and arms the draft; the next click
        // elsewhere sets the radius (see `_commitRectangle`).
        this.draftState.armed = true;
        this._syncDraftHandles();
        return;
      }
      // No sliver guard here: the radius IS the gesture distance, so
      // anything past the click threshold is already ≥ 5 screen px of
      // visible circle. (The old `r < 2` image-px cancel was dead weight at
      // low zoom and canceled deliberate small circles at high zoom.)
      var geom = { cx: c.x, cy: c.y, r: r };
      var el = this.draftState.el;
      el.classList.remove("is-draft");
      this._finalizeShape("circle", geom, el);
    },

    // -------------------------------------------------------------------------
    // Polygon — multi-click; double-click closes; escape cancels.
    // -------------------------------------------------------------------------

    _polygonClick: function(pt) {
      if (!this.draftPolygon) {
        var poly = svgEl("polyline", { "stroke-width": "2", fill: "none" });
        poly.classList.add("etcher-shape", "is-draft");
        // The polygon preview is drawn by `_renderPolygonPreview`, not
        // `_renderShape`, so it takes the params directly — same thickness,
        // dash and fill the finished polygon will have.
        var polyStyle = this._styleForNewShape("polygon");
        this._applyShapeColor(poly, this.activeColor, polyStyle);
        this._applyLineParams(poly, polyStyle, this._markerScale());
        this.svg.appendChild(poly);
        this.draftPolygon = { points: [[pt.x, pt.y]], el: poly };
        this._lastHover = null;
        this._renderPolygonPreview(null);
        this._syncDraftHandles();
        return;
      }

      var pts = this.draftPolygon.points;
      var here = this._imageToContainer(pt);

      // Click on (or very near) the first vertex closes the polygon —
      // standard vector-tool UX. Requires at least 3 vertices already so
      // we close into a real triangle, not a degenerate line.
      if (pts.length >= 3) {
        var first = this._imageToContainer({ x: pts[0][0], y: pts[0][1] });
        var fdx = here.x - first.x, fdy = here.y - first.y;
        if (fdx * fdx + fdy * fdy < 144) { // 12px radius around the start dot
          this._commitPolygon();
          return;
        }
      }

      // Ignore a click that lands on top of the last vertex — covers both
      // the second click of a double-click and a jittery hand. Otherwise
      // we'd stack two dots at the same point and the user would have to
      // notice and back out.
      var last = pts[pts.length - 1];
      var lastScreen = this._imageToContainer({ x: last[0], y: last[1] });
      var ldx = here.x - lastScreen.x, ldy = here.y - lastScreen.y;
      if (ldx * ldx + ldy * ldy < 9) return; // 3px duplicate threshold

      pts.push([pt.x, pt.y]);
      this._renderPolygonPreview(null);
      // Vertex count grew — recreate handles so the new one shows up.
      this._syncDraftHandles();
    },

    _polygonHover: function(pt) {
      this._lastHover = pt;
      this._renderPolygonPreview(pt);
      this._updatePolygonCloseHint(pt);
    },

    // Toggle the close-target class on the first vertex's handle when
    // the cursor is within the same 12px radius the click handler uses
    // for the close action. Matches the threshold so the visual hint
    // and the actual click target line up exactly.
    _updatePolygonCloseHint: function(pt) {
      if (!this.handles || this.handles.length === 0) return;
      var firstHandle = this.handles[0];
      if (!this.draftPolygon || this.draftPolygon.points.length < 3) {
        firstHandle.classList.remove("is-close-target");
        return;
      }
      var pts = this.draftPolygon.points;
      var first = this._imageToContainer({ x: pts[0][0], y: pts[0][1] });
      var here = this._imageToContainer(pt);
      var dx = here.x - first.x, dy = here.y - first.y;
      firstHandle.classList.toggle("is-close-target", dx * dx + dy * dy < 144);
    },

    _renderPolygonPreview: function(hover) {
      if (!this.draftPolygon) return;
      var self = this;
      var pts = this.draftPolygon.points.slice();
      if (hover) pts.push([hover.x, hover.y]);
      var screen = pts.map(function(p) {
        var s = self._imageToContainer({ x: p[0], y: p[1] });
        return s.x + "," + s.y;
      }).join(" ");
      this.draftPolygon.el.setAttribute("points", screen);

      // A polygon is placed vertex by vertex and drawn here rather than
      // through `_renderShape`, so it reports itself. `pts` already carries
      // the rubber-banded segment following the cursor, which is the part
      // worth watching.
      if (this._liveDrawHandler) {
        this._noteLiveDraw("polygon", { points: pts }, this._styleForNewShape("polygon"));
      }
    },

    _commitPolygon: function() {
      var pts = this.draftPolygon.points;
      if (pts.length < 3) {
        this._cancelDraft();
        return;
      }
      var el = this.draftPolygon.el;
      // Convert polyline preview into a closed polygon element so it
      // fills properly. Carry the active color over (the polyline only
      // showed stroke; the polygon now also fills).
      var polygon = svgEl("polygon", { "stroke-width": "2" });
      polygon.classList.add("etcher-shape");
      this._applyShapeColor(polygon, this.activeColor);
      this.svg.replaceChild(polygon, el);
      this.draftPolygon = null;
      this._lastHover = null;
      this._finalizeShape("polygon", { points: pts }, polygon);
      this._renderShape({ kind: "polygon", geometry: { points: pts }, el: polygon });
    },

    // -------------------------------------------------------------------------
    // Callout — leader line + resizable text bbox. Two-click input:
    // anchor first (what's being pointed at), text-bbox top-left second
    // (where the label sits). Between clicks, the line rubber-bands to
    // the cursor with a default-sized bbox preview. Once committed, the
    // text bbox behaves like a text shape: scale via 4 corner handles,
    // inline-edit on double-click. `metadata.title` holds the content.
    // -------------------------------------------------------------------------

    _calloutClick: function(pt) {
      if (!this.draftCallout) {
        var g = svgEl("g");
        g.classList.add("etcher-shape", "etcher-callout", "etcher-text", "is-draft");

        var line = svgEl("line", {
          "stroke-width": "2",
          stroke: "currentColor",
          fill: "none"
        });
        var underline = svgEl("line", {
          "stroke-width": "2",
          stroke: "currentColor",
          fill: "none"
        });
        underline.classList.add("etcher-callout-underline");
        var rect = svgEl("rect", {
          fill: "transparent",
          stroke: "transparent",
          "stroke-width": "2"
        });
        rect.classList.add("etcher-text-rect");
        var text = svgEl("text", {
          "text-anchor": "start",
          "dominant-baseline": "hanging",
          fill: "currentColor",
          stroke: "none"
        });
        text.classList.add("etcher-text-content");
        var dot = svgEl("circle", {
          r: "3",
          fill: "currentColor",
          stroke: "none"
        });

        g.appendChild(line);
        g.appendChild(underline);
        g.appendChild(rect);
        g.appendChild(text);
        g.appendChild(dot);

        this._applyShapeColor(g, this.activeColor);
        this.svg.appendChild(g);

        // Default-sized text bbox up-and-right of the anchor (see
        // `_calloutDefaultBox`) — where the preview starts. A drag moves the
        // box before release, a second click places it, and post-commit
        // handle drags refine it.
        var defaultBox = this._calloutDefaultBox(pt);

        this.draftCallout = {
          kind: "callout",
          geometry: { anchor: [pt.x, pt.y], text_box: defaultBox },
          metadata: {},
          el: g
        };
        this._renderShape(this.draftCallout);
        this._syncDraftHandles();
        return;
      }

      // A draft already exists — the pointerup commit in `_commitCallout`
      // owns completion now, so a second pointerdown reaching here means
      // that commit somehow didn't happen. Do nothing rather than stack a
      // second draft on the first.
    },

    // Pointerup ends the callout gesture, like every other tool. A bare
    // click commits the placeholder as previewed — anchor under the
    // cursor, default-sized text box a short hop away — and a
    // press-drag-release commits with the box centered on the release
    // point (the live preview `_calloutHover` was already showing).
    // Either way it drops straight into label editing: a callout IS a
    // label, so placing one and not asking for its text is a shape the
    // user still has to come back for. This replaced the two-click flow
    // (anchor click, rubber-band, placement click), which testers read as
    // the first click having done nothing.
    _commitCallout: function(pt, second) {
      var draft = this.draftCallout;
      if (!draft) return;
      var anchor = draft.geometry.anchor;
      var box = draft.geometry.text_box;
      // Release without a drag arms the draft for a second click instead of
      // committing where the label happens to be previewing. `second` is the
      // placing click itself, which is past the threshold by the time it
      // gets here — see `_placeArmedDraft`.
      if (!second && this._isClickGesture({ x: anchor[0], y: anchor[1] }, pt)) {
        draft.armed = true;
        this._syncDraftHandles();
        return;
      }
      var geom = {
        anchor: anchor,
        text_box: this._calloutBoxBeside(pt, box.w, box.h)
      };
      var el = draft.el;
      el.classList.remove("is-draft");
      this.draftCallout = null;
      this._finalizeLabeled("callout", geom, el);
    },

    // Where a fresh callout draft's text box starts: up-and-right of the
    // anchor, far enough that the leader reads as a real diagonal (≈40°)
    // rather than a nudge — a callout IS "a line pointing at something",
    // and a short hop draws a leader too short to read as one. It is what
    // the preview shows before the pointer has moved; where the label ends
    // up is wherever the drag or the second click puts it.
    _calloutDefaultBox: function(pt) {
      var basePx = this._textDefaultBoxInkPx();
      var h = basePx * 1.4;
      // Up-and-right on SCREEN, so the label starts where it would on an
      // unturned board. Laid out as the label's left edge at a point that
      // far up and over, the same box the image-axis formula gave at 0°.
      return this._calloutBoxAt(
        pt, basePx * 4, -(basePx * 3.5 + h), basePx * 6, h
      );
    },

    // A callout label's stored box, for a label laid out on SCREEN: its top-
    // left `dx`,`dy` image-px-at-this-zoom away from `pt` in screen
    // directions, `w` × `h` in size. The label stands upright whatever the
    // board's rotation (see the callout case in `_renderShape`), so where it
    // goes relative to the cursor has to be worked out on screen and then
    // carried back: in image axes, "to the right of the cursor" pointed
    // down, or left, on a turned board, and the label landed away from the
    // pointer that was placing it. Stored as the box with the same centre
    // and size — the inverse of `_orientedBox`. At 0° this is exactly
    // `{x: pt.x + dx, y: pt.y + dy, w, h}`.
    _calloutBoxAt: function(pt, dx, dy, w, h) {
      if (!this._canvasRotation()) return { x: pt.x + dx, y: pt.y + dy, w: w, h: h };
      var scl = this._markerScale() || 1;
      var p = this._imageToContainer(pt);
      var c = this._containerToImage({
        x: p.x + (dx + w / 2) * scl,
        y: p.y + (dy + h / 2) * scl
      });
      return { x: c.x - w / 2, y: c.y - h / 2, w: w, h: h };
    },

    // The label to the right of `pt`, vertically centred on it — where a drag
    // or a placing click puts it.
    _calloutBoxBeside: function(pt, w, h) {
      return this._calloutBoxAt(pt, 0, -h / 2, w, h);
    },

    _calloutHover: function(pt) {
      if (!this.draftCallout) return;
      var box = this.draftCallout.geometry.text_box;
      // Rubber-band the text bbox so its center tracks the cursor —
      // gives the user a live preview of where the label will land.
      this.draftCallout.geometry = {
        anchor: this.draftCallout.geometry.anchor,
        text_box: this._calloutBoxBeside(pt, box.w, box.h)
      };
      this._renderShape(this.draftCallout);
      this._positionAllHandles(this.draftCallout);
    },

    // -------------------------------------------------------------------------
    // Dimension — line with arrows at both ends + a slidable black label.
    // Drawn click-drag (rectangle pattern). `geometry = { a: [x,y], b: [x,y] }`
    // for the two endpoints; `metadata.title` carries the label text and
    // `metadata.title_offset` (0–1) is the label's position along the line
    // (default 0.5 = midpoint). The label fill is always black with a
    // white halo regardless of the shape's stroke color, so the user can
    // pick any arrow color and the label stays readable.
    // -------------------------------------------------------------------------

    _startDimension: function(pt, e) {
      var g = svgEl("g");
      g.classList.add("etcher-shape", "etcher-dimension", "is-draft");

      var shaft = svgEl("line", {
        "stroke-width": "2",
        stroke: "currentColor",
        fill: "none"
      });
      shaft.classList.add("etcher-dim-shaft");

      var arrowA = svgEl("polyline", {
        "stroke-width": "2",
        stroke: "currentColor",
        fill: "none",
        "stroke-linejoin": "round",
        "stroke-linecap": "round"
      });
      arrowA.classList.add("etcher-dim-arrow");

      var arrowB = svgEl("polyline", {
        "stroke-width": "2",
        stroke: "currentColor",
        fill: "none",
        "stroke-linejoin": "round",
        "stroke-linecap": "round"
      });
      arrowB.classList.add("etcher-dim-arrow");

      g.appendChild(shaft);
      g.appendChild(arrowA);
      g.appendChild(arrowB);

      this._applyShapeColor(g, this.activeColor);
      this.svg.appendChild(g);

      this.draftState = {
        kind: "dimension",
        anchor: pt,
        geometry: { a: [pt.x, pt.y], b: [pt.x, pt.y] },
        el: g,
        style: this._styleForNewShape("dimension")
      };
      this._renderShape(this.draftState);
      this._syncDraftHandles();
      try { e.target.setPointerCapture(e.pointerId); } catch (_) {}
    },

    _updateDimension: function(pt) {
      var ds = this.draftState;
      var a = ds.anchor;
      // An arrow's head snaps to the anchor of whatever shape it nears —
      // the same search, dots and reach as dragging an arrow's end — and is
      // tied there; Shift's angle lock applies only to a free head.
      var headSnap = null;
      if (ds.kind === "arrow" && this._connectorsOn()) {
        var state = this._arrowSnapState(pt, null);
        if (!ds.leftSource) {
          if (state.candidate && state.candidate.uuid === ds.sourceUuid) state = { candidate: null, snap: null };
          else ds.leftSource = true;
        }
        this._paintTargetDots(state);
        headSnap = state.snap;
      }
      if (headSnap) pt = { x: headSnap.x, y: headSnap.y };
      else if (ds.shift) pt = this._constrainShaftPoint(a, pt);
      // Merged rather than replaced: an arrow draft carries `points` /
      // `from` / `to` as well, and rebuilding the object from a and b alone
      // would drop them — leaving the render to read a routed path that no
      // longer exists.
      var patch = { a: [a.x, a.y], b: [pt.x, pt.y] };
      if (ds.kind === "arrow") {
        patch.to = headSnap ? { uuid: headSnap.shape.uuid, anchor: headSnap.anchor } : null;
      }
      ds.geometry = Object.assign({}, ds.geometry, patch);
      // No drag bookkeeping — click vs drag is decided once, on
      // pointerup, by `_isClickGesture` in `_commitDimension`.
      this._renderShape(this.draftState);
      this._positionAllHandles(this.draftState);
    },

    _commitDimension: function(pt) {
      // Drag → exactly the span that was dragged. Click → ARM: the draft
      // keeps following the cursor and the next click places the far end.
      //
      // A click used to commit a default-length horizontal stub. Nobody
      // wants a stub — it is a shape you then have to drag into place — and
      // it made a double-click produce two of them. Judged by
      // `_isClickGesture`, not by a pixel count, so a hand-jitter "drag"
      // still counts as a click at any zoom.
      var a = this.draftState.anchor;
      if (this._isClickGesture(a, pt)) {
        this.draftState.armed = true;
        this._syncDraftHandles();
        return;
      }
      // An arrow settles its head (snap included) at the release point.
      if (this.draftState.kind === "arrow") this._updateDimension(pt);
      else if (this.draftState.shift) pt = this._constrainShaftPoint(a, pt);
      this._commitShaftDraft(this._shaftGeometry(a, pt));
    },

    // The geometry a shaft draft commits with. Built off the draft's own so
    // an arrow keeps its `points` / `from` / `to`; a line or a dimension has
    // none and comes out as the plain pair it always did.
    _shaftGeometry: function(a, b) {
      var base = (this.draftState && this.draftState.geometry) || {};
      var out = Object.assign({}, base, { a: [a.x, a.y], b: [b.x, b.y] });
      // A tied end sits on its anchor, not where the pointer let go.
      if (base.from && base.a) out.a = base.a.slice();
      if (base.to && base.b) out.b = base.b.slice();
      return out;
    },

    // Finalize the dimension / line draft with the given endpoints.
    //
    // A dimension exists to carry a label, so it drops straight into label
    // editing — placed, selected, input open, waiting for text. A line
    // does not: consumers that open their own composer popup on
    // `etcher:shape-drawn` (taking the title via a composer field and
    // creating a linked comment in one flow) need the clean slate here,
    // and a line without a label is a complete shape in a way an
    // unlabeled dimension is not.
    _commitShaftDraft: function(geom) {
      var el = this.draftState.el;
      var kind = this.draftState.kind;
      el.classList.remove("is-draft");
      if (kind === "arrow") this._removeTargetDots();
      // The dimension drops straight into its label editor on release
      // (head dev's call, restoring the prompt his earlier call removed —
      // with the half that mattered kept): the editor opens focused for
      // typing, but it is SKIPPABLE. Enter on empty text or a click away
      // keeps the line, label-less — the empty-commit path discards only
      // kinds that ARE their text. A measurement usually wants its value
      // written on it; a plain pointer line usually doesn't, so lines and
      // arrows stay promptless and take a label by double-click.
      if (kind === "dimension") {
        this._finalizeLabeled(kind, geom, el);
      } else {
        this._finalizeShape(kind, geom, el);
      }
    },

    // Finalize + drop straight into label editing: selected with handles,
    // inline input open on the label position. The afterCreate hook is how
    // `_finalizeShape` hands the shape back, and passing one suppresses
    // its own enter-edit-mode — deliberately, since the order here matters
    // (edit mode first, then the text editor on top, same as the tap
    // path). The dismiss guard swallows the click the browser synthesizes
    // from the gesture, exactly as in `_finalizeShape`.
    _finalizeLabeled: function(kind, geom, el) {
      var made = null;
      this._finalizeShape(kind, geom, el, function(shape) { made = shape; });
      if (!made) return;
      this._suppressEditDismissUntil = Date.now() + 400;
      this._enterEditMode(made);
      // The same order-of-operations trap _finalizeShape documents about
      // itself: entering edit mode runs _exitEditMode's teardown, which
      // ends freshness — so re-set it AFTER, or a shape born through the
      // label prompt (dimension, callout) never mirrors panel edits to
      // the tool's defaults while every other kind does. The label SIZE
      // was the visible casualty: colour and thickness happen to fall
      // through to the global branch while the editor is open, but the
      // font path targets the editing shape and checks freshness.
      this._freshShape = made;
      this._startTextEdit(made);
    },

    // -------------------------------------------------------------------------
    // Line — two-endpoint stroke with no arrows and no inline label.
    // Shares geometry (`{ a: [x,y], b: [x,y] }`) and draft-state
    // machinery with dimension; title rides the standard sibling-
    // above-shape path (rendered by `_renderTitleSibling`, same as
    // rectangle/circle/polygon). Comment is collected by the host's
    // annotation composer via the `etcher:shape-drawn` event.
    // -------------------------------------------------------------------------

    _startLine: function(pt, e) {
      var g = svgEl("g");
      g.classList.add("etcher-shape", "etcher-line", "is-draft");

      var shaft = svgEl("line", {
        "stroke-width": "2",
        stroke: "currentColor",
        fill: "none"
      });
      shaft.classList.add("etcher-line-shaft");

      g.appendChild(shaft);

      this._applyShapeColor(g, this.activeColor);
      this.svg.appendChild(g);

      this.draftState = {
        kind: "line",
        anchor: pt,
        geometry: { a: [pt.x, pt.y], b: [pt.x, pt.y] },
        el: g,
        // Read by `_shaftStrokePx` / `_applyShaftStroke` on every render, so
        // the shaft previews at the weight and dash it will commit with.
        style: this._styleForNewShape("line")
      };
      this._renderShape(this.draftState);
      this._syncDraftHandles();
      try { e.target.setPointerCapture(e.pointerId); } catch (_) {}
    },

    // A free arrow: the same two-ended stroke a line is, with one head on
    // the end you drag to. It reuses the `arrow` KIND that connectors are
    // made of — same render, same handles, same bend-dropping, same
    // params — with no bindings, which is all a connector's `from` / `to`
    // ever were. Drawing it a second time as its own kind would have meant
    // a second copy of all of that.
    //
    // Unlike a dimension it does NOT open a label editor on release. A
    // dimension exists to carry a measurement and is unfinished without
    // one; an arrow is finished the moment it points at something, and a
    // label is available the way it is on every other shape — by
    // double-clicking it.
    // The arrow tool, before a press: the shape the pointer is near shows its
    // anchors, the nearest one lit when close enough to take the tail.
    _arrowToolHover: function(e) {
      if (!this._connectorsOn()) return;
      var pt;
      try { pt = this._toImage(e); } catch (_) { return; }
      this._paintTargetDots(this._arrowSnapState(pt, null));
    },

    _startArrow: function(pt, e) {
      // Pressed on (or near) a shape's anchor: the tail is tied there, and
      // follows the shape from now on. Anywhere else it starts in the air.
      var tailSnap = this._connectorsOn() ? this._arrowSnapState(pt, null).snap : null;
      if (tailSnap) pt = { x: tailSnap.x, y: tailSnap.y };
      var el = this._makeArrowEl();
      el.classList.add("etcher-shape", "is-draft");
      var style = this._styleForNewShape("arrow");
      this._applyShapeColor(el, this.activeColor);
      this.svg.appendChild(el);
      this.draftState = {
        kind: "arrow",
        anchor: pt,
        geometry: {
          a: [pt.x, pt.y], b: [pt.x, pt.y],
          // Empty rather than absent: the render, the bbox and the hit test
          // all read the routed path, and a free arrow is the no-bends case
          // of one rather than a different shape.
          points: [],
          from: tailSnap ? { uuid: tailSnap.shape.uuid, anchor: tailSnap.anchor } : null,
          to: null
        },
        // The source shape is under the pointer as the arrow is born; its
        // anchors only become targets once the pointer has left it (see
        // `_updateDimension`), or the release would tie the head right back.
        sourceUuid: tailSnap ? tailSnap.shape.uuid : null,
        leftSource: !tailSnap,
        el: el,
        style: style
      };
      this._renderShape(this.draftState);
      this._syncDraftHandles();
      try { e.target.setPointerCapture(e.pointerId); } catch (_) {}
    },

    // -------------------------------------------------------------------------
    // Freehand
    //
    // A freehand stroke is captured as a raw polyline while the pointer is
    // down (every ~2px sample), then on release it is *simplified and fitted*
    // into a sparse run of cubic-bezier nodes. The committed geometry is
    // `{ nodes: [{ p:[x,y], hIn:[dx,dy]|null, hOut:[dx,dy]|null, type }] }` in
    // image px — `p` is the anchor, `hIn`/`hOut` are control handles stored as
    // offsets from the anchor (null = no handle on that side, i.e. a straight
    // join or a stroke endpoint), `type` is "smooth" (handles colinear, drag
    // one and the other mirrors) or "corner" (handles move independently).
    // Rendered as an SVG <path>. Legacy strokes saved in the old
    // `{ points: [...] }` form still render as a <polyline> and are read via
    // `_freehandFlatten`, so old canvases keep working untouched.
    // -------------------------------------------------------------------------

    // Target error for the curve fit, expressed in IMAGE px but derived from a
    // fixed on-screen target so the simplification feels the same at every
    // zoom level. (1 container px ≈ `imgPerCont` image px at the current
    // zoom; strip mode is identity so this collapses to the raw target.)
    _freehandFitTolerance: function(targetScreenPx) {
      var ca = this._imageToContainer({ x: 0,   y: 0 });
      var cb = this._imageToContainer({ x: 100, y: 0 });
      var dx = cb.x - ca.x, dy = cb.y - ca.y;
      var contPer100 = Math.sqrt(dx * dx + dy * dy) || 100;
      var imgPerCont = 100 / contPer100;
      // Higher = looser fit = fewer anchors/handles. Freehand defaults to
      // ~8px (a handful of nodes); the marker passes a tighter target so it
      // tracks difficult shapes faithfully with more anchors.
      var TARGET_SCREEN_PX = targetScreenPx || 8;
      return Math.max(0.5, TARGET_SCREEN_PX * imgPerCont);
    },

    // Ramer–Douglas–Peucker: drop points that lie within `epsilon` (image px)
    // of the chord between their kept neighbours. A cheap denoise pass before
    // the bezier fit so pointer jitter doesn't seed spurious control points.
    _rdpSimplify: function(points, epsilon) {
      if (points.length < 3) return points.slice();
      var sqEps = epsilon * epsilon;
      function sqSegDist(p, a, b) {
        var x = a[0], y = a[1], dx = b[0] - x, dy = b[1] - y;
        if (dx !== 0 || dy !== 0) {
          var t = ((p[0] - x) * dx + (p[1] - y) * dy) / (dx * dx + dy * dy);
          if (t > 1) { x = b[0]; y = b[1]; }
          else if (t > 0) { x += dx * t; y += dy * t; }
        }
        dx = p[0] - x; dy = p[1] - y;
        return dx * dx + dy * dy;
      }
      function simplify(pts, first, last, out) {
        var maxSq = sqEps, index = -1;
        for (var i = first + 1; i < last; i++) {
          var sq = sqSegDist(pts[i], pts[first], pts[last]);
          if (sq > maxSq) { index = i; maxSq = sq; }
        }
        if (index !== -1) {
          if (index - first > 1) simplify(pts, first, index, out);
          out.push(pts[index]);
          if (last - index > 1) simplify(pts, index, last, out);
        }
      }
      var out = [points[0]];
      simplify(points, 0, points.length - 1, out);
      out.push(points[points.length - 1]);
      return out;
    },

    // Cleanup for a marker stroke on release. The marker renders as a
    // Catmull-Rom spline through these points, so the *render* supplies the
    // smoothness — here we just (1) RDP-simplify the raw samples down to a
    // sparse set of control points the spline can curve through, and (2)
    // round to whole canvas px (markers don't need sub-pixel precision). The
    // result looks the same — smooth, following the stroke — but stores
    // roughly 5–10× fewer, smaller points. Tune fidelity vs. size via
    // SIMPLIFY_PX (lower = more points, tighter to the stroke).
    _smoothStroke: function(points) {
      // One decimal, not whole numbers. Coordinates are in IMAGE px, so how
      // much a whole number is worth on screen depends entirely on zoom:
      // drawn zoomed in, one image px can span several screen px, and
      // rounding there quantized the stroke into visible stair-steps. A
      // tenth is far below anything that can be seen at any zoom the canvas
      // allows, and still keeps the stored payload compact.
      function round(p) {
        return [Math.round(p[0] * 10) / 10, Math.round(p[1] * 10) / 10];
      }
      if (!points || points.length < 3) return (points || []).map(round);
      // RDP tolerance (on-screen px) → control-point spacing. Halved from 2
      // once corners stopped being rounded off: with the thinning that
      // loose, a curve's samples end up far enough apart that the spline
      // between them wanders off the hand's path — measured at 1.5px on a
      // question mark's hook, and a third of that at 1.
      var SIMPLIFY_PX = 1;
      var pts = this._rdpSimplify(points, this._freehandFitTolerance(SIMPLIFY_PX));
      return pts.map(round);
    },

    // Schneider's "Algorithm for Automatically Fitting Digitized Curves"
    // (Graphics Gems, 1990) — the same fit Paper.js's path.simplify() uses.
    // Input: a polyline (array of [x,y]) + a max error in image px. Output: an
    // array of cubic beziers [[p0,c1,c2,p3], ...] sharing endpoints, each a
    // [x,y] tuple. Recursively fits one cubic to the whole span, reparameter-
    // izing with Newton–Raphson; if it can't hit the tolerance it splits at
    // the worst point and fits each half. Self-contained — all vector math is
    // local so it doesn't bloat the hook's method table.
    _fitCurve: function(points, maxError) {
      function sub(a, b) { return [a[0] - b[0], a[1] - b[1]]; }
      function add(a, b) { return [a[0] + b[0], a[1] + b[1]]; }
      function mul(a, s) { return [a[0] * s, a[1] * s]; }
      function dot(a, b) { return a[0] * b[0] + a[1] * b[1]; }
      function len(a) { return Math.sqrt(a[0] * a[0] + a[1] * a[1]); }
      function normalize(a) { var m = len(a) || 1; return [a[0] / m, a[1] / m]; }

      var pts = [];
      for (var i = 0; i < points.length; i++) {
        var p = points[i];
        if (!pts.length ||
            p[0] !== pts[pts.length - 1][0] || p[1] !== pts[pts.length - 1][1]) {
          pts.push([p[0], p[1]]);
        }
      }
      if (pts.length < 2) return [];

      function q(ctrl, t) {
        var mt = 1 - t;
        var a = mul(ctrl[0], mt * mt * mt);
        var b = mul(ctrl[1], 3 * mt * mt * t);
        var c = mul(ctrl[2], 3 * mt * t * t);
        var d = mul(ctrl[3], t * t * t);
        return [a[0] + b[0] + c[0] + d[0], a[1] + b[1] + c[1] + d[1]];
      }
      function qprime(ctrl, t) {
        var mt = 1 - t;
        var a = mul(sub(ctrl[1], ctrl[0]), 3 * mt * mt);
        var b = mul(sub(ctrl[2], ctrl[1]), 6 * mt * t);
        var c = mul(sub(ctrl[3], ctrl[2]), 3 * t * t);
        return [a[0] + b[0] + c[0], a[1] + b[1] + c[1]];
      }
      function qprimeprime(ctrl, t) {
        var a = mul(add(sub(ctrl[2], mul(ctrl[1], 2)), ctrl[0]), 6 * (1 - t));
        var b = mul(add(sub(ctrl[3], mul(ctrl[2], 2)), ctrl[1]), 6 * t);
        return [a[0] + b[0], a[1] + b[1]];
      }
      function chordLengthParameterize(d) {
        var u = [0];
        for (var i = 1; i < d.length; i++) u[i] = u[i - 1] + len(sub(d[i], d[i - 1]));
        var last = u[u.length - 1] || 1;
        for (var j = 0; j < u.length; j++) u[j] /= last;
        return u;
      }
      function generateBezier(d, u, t1, t2) {
        var first = d[0], last = d[d.length - 1];
        var bez = [first, null, null, last];
        var nPts = d.length;
        var A = [];
        for (var i = 0; i < nPts; i++) {
          var ui = u[i], mt = 1 - ui;
          A.push([mul(t1, 3 * mt * mt * ui), mul(t2, 3 * mt * ui * ui)]);
        }
        var C = [[0, 0], [0, 0]], X = [0, 0];
        for (var k = 0; k < nPts; k++) {
          var a = A[k];
          C[0][0] += dot(a[0], a[0]);
          C[0][1] += dot(a[0], a[1]);
          C[1][0] += dot(a[0], a[1]);
          C[1][1] += dot(a[1], a[1]);
          var tmp = sub(d[k], q([first, first, last, last], u[k]));
          X[0] += dot(a[0], tmp);
          X[1] += dot(a[1], tmp);
        }
        var det_C0_C1 = C[0][0] * C[1][1] - C[1][0] * C[0][1];
        var det_C0_X  = C[0][0] * X[1]    - C[1][0] * X[0];
        var det_X_C1  = X[0]    * C[1][1] - X[1]    * C[0][1];
        var alpha_l = det_C0_C1 === 0 ? 0 : det_X_C1 / det_C0_C1;
        var alpha_r = det_C0_C1 === 0 ? 0 : det_C0_X / det_C0_C1;
        var segLength = len(sub(last, first));
        var epsilon = 1e-6 * segLength;
        if (alpha_l < epsilon || alpha_r < epsilon) {
          var dist = segLength / 3;
          bez[1] = add(first, mul(t1, dist));
          bez[2] = add(last,  mul(t2, dist));
        } else {
          bez[1] = add(first, mul(t1, alpha_l));
          bez[2] = add(last,  mul(t2, alpha_r));
        }
        return bez;
      }
      function computeMaxError(d, u, bez) {
        var maxDist = 0, splitPoint = Math.floor(d.length / 2);
        for (var i = 1; i < d.length - 1; i++) {
          var v = sub(q(bez, u[i]), d[i]);
          var dist = v[0] * v[0] + v[1] * v[1];
          if (dist > maxDist) { maxDist = dist; splitPoint = i; }
        }
        return [maxDist, splitPoint];
      }
      function reparameterize(bez, d, u) {
        return u.map(function(uu, i) {
          var dd = sub(q(bez, uu), d[i]);
          var qp = qprime(bez, uu);
          var num = dot(dd, qp);
          var den = dot(qp, qp) + dot(dd, qprimeprime(bez, uu));
          return den === 0 ? uu : uu - num / den;
        });
      }

      var MaxErrorSq = maxError * maxError;
      function fitCubic(d, t1, t2) {
        if (d.length === 2) {
          var dist = len(sub(d[1], d[0])) / 3;
          return [[d[0], add(d[0], mul(t1, dist)), add(d[1], mul(t2, dist)), d[1]]];
        }
        var u = chordLengthParameterize(d);
        var bez = generateBezier(d, u, t1, t2);
        var res = computeMaxError(d, u, bez);
        var maxDist = res[0], splitPoint = res[1];
        if (maxDist < MaxErrorSq) return [bez];
        if (maxDist < MaxErrorSq * 4) {
          for (var i = 0; i < 20; i++) {
            var uPrime = reparameterize(bez, d, u);
            bez = generateBezier(d, uPrime, t1, t2);
            res = computeMaxError(d, uPrime, bez);
            maxDist = res[0]; splitPoint = res[1];
            if (maxDist < MaxErrorSq) return [bez];
            u = uPrime;
          }
        }
        var centerTangent = normalize(sub(d[splitPoint - 1], d[splitPoint + 1]));
        var left  = fitCubic(d.slice(0, splitPoint + 1), t1, centerTangent);
        var right = fitCubic(d.slice(splitPoint), mul(centerTangent, -1), t2);
        return left.concat(right);
      }

      if (pts.length === 2) {
        var t = normalize(sub(pts[1], pts[0]));
        var dd = len(sub(pts[1], pts[0])) / 3;
        return [[pts[0], add(pts[0], mul(t, dd)), sub(pts[1], mul(t, dd)), pts[1]]];
      }
      var leftTangent  = normalize(sub(pts[1], pts[0]));
      var rightTangent = normalize(sub(pts[pts.length - 2], pts[pts.length - 1]));
      return fitCubic(pts, leftTangent, rightTangent);
    },

    // Convert the fitter's bezier list into the stored node format. Adjacent
    // beziers share an anchor, so node[k] carries the incoming handle from
    // bez[k-1] and the outgoing handle into bez[k]. Interior nodes are
    // "smooth" (the fit makes their handles colinear at the join); the two
    // endpoints are "corner" with a single handle each.
    _beziersToNodes: function(beziers) {
      if (!beziers.length) return [];
      function sub(a, b) { return [a[0] - b[0], a[1] - b[1]]; }
      var first = beziers[0];
      var nodes = [
        { p: [first[0][0], first[0][1]], hIn: null, hOut: sub(first[1], first[0]), type: "corner" }
      ];
      for (var k = 0; k < beziers.length; k++) {
        var b = beziers[k];
        var p3 = b[3];
        var hIn = sub(b[2], p3);
        if (k < beziers.length - 1) {
          nodes.push({ p: [p3[0], p3[1]], hIn: hIn, hOut: sub(beziers[k + 1][1], p3), type: "smooth" });
        } else {
          nodes.push({ p: [p3[0], p3[1]], hIn: hIn, hOut: null, type: "corner" });
        }
      }
      return nodes;
    },

    // Sample a node run (or a legacy points array) into a dense polyline of
    // image-px [x,y] points. Used everywhere the curve needs to be treated as
    // a polygon: hit-testing, nearest-point for leader lines, and bbox.
    _freehandFlatten: function(g, steps) {
      if (!g) return [];
      if (g.points) return g.points;
      var nodes = g.nodes || [];
      if (nodes.length === 0) return [];
      if (nodes.length === 1) return [nodes[0].p.slice()];
      steps = steps || 16;
      function add(a, b) { return [a[0] + b[0], a[1] + b[1]]; }
      function cubicAt(p0, p1, p2, p3, t) {
        var mt = 1 - t, a = mt * mt * mt, b = 3 * mt * mt * t, c = 3 * mt * t * t, d = t * t * t;
        return [
          a * p0[0] + b * p1[0] + c * p2[0] + d * p3[0],
          a * p0[1] + b * p1[1] + c * p2[1] + d * p3[1]
        ];
      }
      var out = [nodes[0].p.slice()];
      for (var i = 1; i < nodes.length; i++) {
        var prev = nodes[i - 1], cur = nodes[i];
        var p0 = prev.p, p3 = cur.p;
        var p1 = prev.hOut ? add(prev.p, prev.hOut) : prev.p;
        var p2 = cur.hIn ? add(cur.p, cur.hIn) : cur.p;
        for (var s = 1; s <= steps; s++) out.push(cubicAt(p0, p1, p2, p3, s / steps));
      }
      return out;
    },

    // Build an SVG path `d` from nodes, projecting each anchor + handle through
    // `mapPt` (image→container). A null handle collapses that side's control
    // point onto the anchor, turning the segment into a straight line.
    _freehandPathD: function(nodes, mapPt) {
      if (!nodes || !nodes.length) return "";
      function addv(a, b) { return [a[0] + b[0], a[1] + b[1]]; }
      var d = "";
      for (var i = 0; i < nodes.length; i++) {
        var c = mapPt(nodes[i].p);
        if (i === 0) { d = "M " + c.x + " " + c.y; continue; }
        var prev = nodes[i - 1], cur = nodes[i];
        var c1 = mapPt(prev.hOut ? addv(prev.p, prev.hOut) : prev.p);
        var c2 = mapPt(cur.hIn ? addv(cur.p, cur.hIn) : cur.p);
        d += " C " + c1.x + " " + c1.y + " " + c2.x + " " + c2.y + " " + c.x + " " + c.y;
      }
      return d;
    },

    // Build a smooth cubic-bezier `d` that passes THROUGH every point — a
    // Catmull-Rom spline converted to beziers. Lets a marker store only a
    // sparse set of control points yet render as a smooth curve (the spline
    // fills in the curvature). Each point is projected via `mapPt` first, so
    // the math runs in container space. Endpoints are clamped (duplicated).
    // `opts.corners` turns on the sharp-turn rule below. It is for a hand's
    // stroke, where a corner is something the hand did; an arrow's curve is
    // built from waypoints a user placed deliberately, and its geometry
    // helper (`_crSample`, which answers what is hit, labelled and
    // measured) mirrors the plain spline. Breaking one and not the other
    // would draw a kink where the measuring still read a bow.
    _catmullRomPathD: function(points, mapPt, opts) {
      var n = points.length;
      if (!n) return "";
      var p = [];
      for (var i = 0; i < n; i++) { var c = mapPt(points[i]); p.push([c.x, c.y]); }
      var d = "M " + p[0][0] + " " + p[0][1];
      if (n === 1) return d;

      // Knot spacing raised to CATMULL_ALPHA — the "centripetal"
      // parameterization. Uniform spacing (alpha 0) is the textbook version
      // and the one this used to use; its failure mode is that when two
      // samples sit close together and the next is far away, the tangent it
      // derives overshoots, and the curve loops back on itself. On a marker
      // stroke that shows up as a little knot at every sharp corner, exactly
      // where the hand slowed down and the samples bunched. Centripetal
      // spacing is the standard fix and is provably free of cusps and
      // self-intersections (Yuksel et al., "Parameterization and Applications
      // of Catmull-Rom Curves").
      function knot(a, b) {
        var dx = b[0] - a[0], dy = b[1] - a[1];
        // Guard the zero case: coincident samples would otherwise divide by
        // zero below and blank the whole path with NaNs.
        return Math.max(Math.pow(dx * dx + dy * dy, CATMULL_ALPHA / 2), 1e-6);
      }

      // A deliberate corner is not a curve, and this spline has no way to
      // know the difference on its own: it reads three points and draws a
      // smooth arc through them, which on a right angle bows a long way off
      // the line that was drawn (11px, measured). The samples are sparse by
      // then — the thinning pass keeps a straight run's endpoints and
      // nothing between — so the neighbours the tangent is built from can be
      // the far ends of two long strokes, and the bow is proportional.
      //
      // A point where the stroke turns sharply is treated as an END instead:
      // both segments meeting there arrive and leave along their own chord,
      // the way the first and last points of the whole stroke already do. A
      // corner stays a corner; anything gentler is still drawn as one curve.
      var breakCorners = !!(opts && opts.corners);

      function sharpAt(i) {
        if (!breakCorners) return false;
        var a = p[i - 1], b = p[i], c = p[i + 1];
        if (!a || !c) return false;
        var ax = b[0] - a[0], ay = b[1] - a[1];
        var cx = c[0] - b[0], cy = c[1] - b[1];
        var la = Math.sqrt(ax * ax + ay * ay), lc = Math.sqrt(cx * cx + cy * cy);
        if (!la || !lc) return false;
        // cos of the turn: 1 is dead straight, -1 doubles back.
        return (ax * cx + ay * cy) / (la * lc) < CORNER_COS;
      }

      for (var j = 0; j < n - 1; j++) {
        var startsCorner = sharpAt(j);
        var endsCorner = sharpAt(j + 1);
        var p0 = (startsCorner ? null : p[j - 1]) || p[j];
        var p1 = p[j];
        var p2 = p[j + 1];
        var p3 = (endsCorner ? null : p[j + 2]) || p2;

        var d1 = knot(p0, p1), d2 = knot(p1, p2), d3 = knot(p2, p3);
        // Tangents at the two anchors, in non-uniform Catmull-Rom form.
        var m1x = (p1[0] - p0[0]) / d1 - (p2[0] - p0[0]) / (d1 + d2) + (p2[0] - p1[0]) / d2;
        var m1y = (p1[1] - p0[1]) / d1 - (p2[1] - p0[1]) / (d1 + d2) + (p2[1] - p1[1]) / d2;
        var m2x = (p2[0] - p1[0]) / d2 - (p3[0] - p1[0]) / (d2 + d3) + (p3[0] - p2[0]) / d3;
        var m2y = (p2[1] - p1[1]) / d2 - (p3[1] - p1[1]) / (d2 + d3) + (p3[1] - p2[1]) / d3;
        // Ends are clamped (p0 === p1, or p3 === p2), where the formula above
        // leaves a tangent pulling off the curve. Fall back to the chord so
        // the first and last segments leave and arrive straight.
        if (j === 0 || startsCorner) { m1x = (p2[0] - p1[0]) / d2; m1y = (p2[1] - p1[1]) / d2; }
        if (j === n - 2 || endsCorner) { m2x = (p2[0] - p1[0]) / d2; m2y = (p2[1] - p1[1]) / d2; }

        var c1x = p1[0] + m1x * d2 / 3;
        var c1y = p1[1] + m1y * d2 / 3;
        var c2x = p2[0] - m2x * d2 / 3;
        var c2y = p2[1] - m2y * d2 / 3;
        d += " C " + c1x + " " + c1y + " " + c2x + " " + c2y + " " + p2[0] + " " + p2[1];
      }
      return d;
    },

    _startFreehand: function(pt, e) { this._startStroke(pt, e, "freehand"); },
    _startMarker: function(pt, e) { this._startStroke(pt, e, "marker"); },

    // Shared capture for both freehand and marker. Freehand draws a raw
    // <polyline> draft that's fitted to bezier nodes on release; a marker
    // draws a <path> so its live preview (and committed stroke) render as a
    // Catmull-Rom spline through the sampled points. `_commitFreehand` does
    // the per-kind finalize. The other difference is styling — a marker wears
    // `.etcher-marker` and its thickness/opacity/dash.
    _startStroke: function(pt, e, kind) {
      var path = kind === "marker"
        ? svgEl("path", { "stroke-width": "2", fill: "none" })
        : svgEl("polyline", { "stroke-width": "2", fill: "none" });
      path.classList.add("etcher-shape", "is-draft");
      var strokeStyle = this._styleForNewShape(kind);
      if (kind === "marker") {
        path.classList.add("etcher-marker");
        this._applyMarkerStyle(path, strokeStyle, this._markerScale());
      } else {
        this._applyShapeColor(path, this.activeColor, strokeStyle);
        this._applyLineParams(path, strokeStyle, this._markerScale());
      }
      this.svg.appendChild(path);
      var geom = { points: [[pt.x, pt.y]] };
      // Carrying the style means `_renderShape` re-applies it every frame at
      // the current zoom, rather than the one-shot paint above going stale.
      this.draftState = { kind: kind, geometry: geom, el: path, style: strokeStyle };
      this._renderShape(this.draftState);
      try { e.target.setPointerCapture(e.pointerId); } catch (_) {}
    },

    // The default style new marker strokes are drawn with. Thickness/opacity/
    // dash are tool-level state (`markerStyle`) the style popup will edit;
    // color rides the shared `activeColor` palette like every other tool.
    _currentMarkerStyle: function() {
      // Markers pull thickness / opacity / dash from the global line params
      // (the Parameters button) — same source as the other stroke shapes.
      // Width is on-screen px; convert to image px so the rendered marker
      // tracks zoom (`_applyMarkerStyle` multiplies it back by scale).
      var lp = this.lineParams || {};
      return {
        color: this.activeColor || "#3b82f6",
        width: (lp.width || 2) / this._inkScale(),
        opacity: lp.opacity == null ? 1 : lp.opacity,
        dash: lp.dash || "solid"
      };
    },

    // Paint a marker style onto its <path>/<polyline> element: stroke color +
    // width + opacity + round caps, and a dash pattern derived from the width
    // (dotted = zero-length dashes rendered as dots by the round caps). No
    // fill — a marker is a stroke, not a filled region.
    // Container px per image px at the current zoom. Strip mode renders shapes
    // in image-px user units (the per-image SVG viewBox scales them), so its
    // markers already track the content — scale is 1 there.
    //
    // Measured as the LENGTH of the projected unit vector, not its
    // y-component. Rotation is isometric, so the length is the scale at any
    // angle; reading one axis collapses to ~0 at 90°/270° (an image-y offset
    // projects entirely onto container x there), and the `|| 1` guard then
    // handed back a scale of 1. Everything sized by this — stroke widths, hit
    // tolerances, default text boxes — came out at raw image-px on a rotated
    // canvas, which is how a 1.3px line rendered at 18.6px.
    _markerScale: function() {
      if (this.handleKind === "strip") return 1;
      try {
        var a = this._imageToContainer({ x: 0, y: 0 });
        var b = this._imageToContainer({ x: 0, y: 1 });
        return Math.hypot(b.x - a.x, b.y - a.y) || 1;
      } catch (e) {
        return 1;
      }
    },

    // A length written as on-screen px — a padding, a font floor, an arrow
    // head — expressed at the current zoom instead.
    //
    // Constants like these read as "6px of padding" and are right at 1:1, but
    // they don't know the board can be zoomed. Left alone they hold their
    // on-screen size while everything around them shrinks, so a play disc
    // ends up bulging out of the card it sits in and a 10px font floor turns
    // a label into the largest thing on a zoomed-out board. Scaling them
    // keeps the proportions the constants were chosen for.
    //
    // Note this applies to FLOORS as much as to sizes: `Math.max(7, …)` is
    // there to keep a deliberately small card usable, not to defeat zooming
    // out, and only scales correctly if the 7 scales too.
    _zoomPx: function(px) {
      return px * this._markerScale();
    },

    // The weight EVERY connector on this board is drawn with, in image px.
    //
    // One weight per board, so no two arrows can disagree. Deriving it
    // per-arrow kept producing arrows that looked mismatched to the person
    // drawing them: length, the zoom it was drawn at, and the sizes of the
    // shapes it happens to join can all differ between two arrows that read
    // as "the same connection".
    //
    // The size it is taken from is the board's own, not a fixed number of
    // canvas units. Canvas units mean different things on different boards —
    // media and images are inserted at a size divided by the zoom of the
    // moment, so one board's shapes span a few hundred units and another's
    // several thousand, and a constant would be invisible on one and
    // enormous on the other. The median shape is the board's natural scale,
    // and gives the 2px line connectors have always been drawn with for the
    // content a board is usually built from.
    //
    // Median rather than mean: one outsized background image, or a stray
    // scribble, should not set the weight for every arrow on the board.
    //
    // Returns null for a board with nothing measurable on it. That cannot
    // happen once an arrow exists — an arrow is drawn out of a shape — but
    // it leaves the caller a fallback rather than a division by zero.
    _boardArrowWeightImagePx: function() {
      var shapes = this.shapes || [];
      var sizes = [];
      for (var i = 0; i < shapes.length; i++) {
        var s = shapes[i];
        if (!s || s.kind === "arrow") continue;
        var b = this._shapeBBoxImagePx(s);
        if (!b) continue;
        var w = Math.abs(b.w), h = Math.abs(b.h);
        if (!(w > 0) || !(h > 0)) continue;
        // Geometric mean — the side of the square with the same area. It sits
        // between width and height instead of following either, so a long
        // thin banner is not read as though it were as big as it is wide.
        sizes.push(Math.sqrt(w * h));
      }
      if (!sizes.length) return null;
      sizes.sort(function(a, b) { return a - b; });
      var mid = Math.floor(sizes.length / 2);
      var median = sizes.length % 2
        ? sizes[mid]
        : (sizes[mid - 1] + sizes[mid]) / 2;
      return median * ARROW_WEIGHT_RATIO;
    },

    // The weight this board draws its connectors and dimensions at, in
    // screen px at the current zoom.
    //
    // One number for the whole board, so a connector and a dimension drawn
    // beside each other are the same weight, and no two of either can
    // disagree. `_boardArrowWeightImagePx` answers in image px; this is that
    // taken at the current zoom, which is what makes the line thin as the
    // board is zoomed out.
    //
    // The fallback is the designed weight, for a board with nothing
    // measurable on it. That cannot arise in practice — an arrow is drawn out
    // of a shape, and a dimension is itself a measurable shape.
    _boardLineWeightPx: function() {
      var img = this._boardArrowWeightImagePx();
      if (img == null) return LINE_WEIGHT_PX;
      return img * this._markerScale();
    },

    // The same weight as a multiplier on the designed sizes — the factor
    // every fixed length in a connector or a dimension is taken at, so each
    // stays in the proportion it was drawn in.
    _boardLineScale: function() {
      return this._boardLineWeightPx() / LINE_WEIGHT_PX;
    },

    _applyMarkerStyle: function(el, style, scale) {
      if (!el) return;
      var s = style || {};
      scale = scale || 1;
      // `style.width` is stored in IMAGE px; multiply by the zoom so the
      // stroke scales with the content (thicker zoomed in, thinner out) like
      // ink on the image, instead of a fixed on-screen thickness.
      var w = (s.width || 10) * scale;
      // …down to a floor, past which it stops being ink and starts being
      // nothing. A dot gets the same one: it is the cap on a stroke of this
      // width, and it is the same ink.
      w = Math.max(MARKER_MIN_SCREEN_PX, w);
      el.style.stroke = s.color || this.activeColor || "#3b82f6";
      el.style.fill = "none";
      el.style.fillOpacity = "";
      el.style.strokeOpacity = String(s.opacity == null ? 1 : s.opacity);
      el.style.strokeLinecap = "round";
      el.style.strokeLinejoin = "round";
      // Inline style (not a presentation attribute) so the generic
      // `.is-hovered` / `.is-selected` CSS rules — which set stroke-width:3 —
      // don't override and visually thin the marker on hover/select.
      el.style.strokeWidth = w + "px";
      el.removeAttribute("stroke-width");
      if (s.dash === "dashed") {
        el.setAttribute("stroke-dasharray", (w * 2.2) + " " + (w * 1.6));
      } else if (s.dash === "dotted") {
        el.setAttribute("stroke-dasharray", "0.01 " + (w * DOT_GAP_RATIO));
      } else {
        el.removeAttribute("stroke-dasharray");
      }
    },

    // Squared image-px distance from point `p` ({x,y}) to segment a→b
    // (each [x,y]). Standard clamp-to-segment projection.
    _sqDistToSeg: function(p, a, b) {
      var x = a[0], y = a[1], dx = b[0] - x, dy = b[1] - y;
      if (dx !== 0 || dy !== 0) {
        var t = ((p.x - x) * dx + (p.y - y) * dy) / (dx * dx + dy * dy);
        if (t > 1) { x = b[0]; y = b[1]; }
        else if (t > 0) { x += dx * t; y += dy * t; }
      }
      dx = p.x - x; dy = p.y - y;
      return dx * dx + dy * dy;
    },

    // True when `pt` lands on a marker's stroke — within half the stroke
    // width plus a grab pad. Width/pad are on-screen px, so convert to image
    // px at the current zoom before comparing to the flattened curve.
    _strokeNearPoint: function(shape, pt) {
      var flat = this._freehandFlatten(shape.geometry);
      if (!flat || flat.length < 1) return false;
      // Width is image px (zoom-anchored); half of it is the stroke radius in
      // image space. Add a 6px on-screen grab pad, converted to image px.
      var wImg = (shape.style && shape.style.width) || 10;
      var perImagePx = this._markerScale();
      var tolImg = wImg / 2 + 6 / perImagePx;
      var tol2 = tolImg * tolImg;
      if (flat.length === 1) {
        var ex = flat[0][0] - pt.x, ey = flat[0][1] - pt.y;
        return ex * ex + ey * ey <= tol2;
      }
      for (var i = 0; i < flat.length - 1; i++) {
        if (this._sqDistToSeg(pt, flat[i], flat[i + 1]) <= tol2) return true;
      }
      return false;
    },

    _appendFreehand: function(pt) {
      // Strip mode: a stroke can wander into the next image, where `pt` comes
      // back in THAT image's local coords (y resets near 0). Translate it
      // into the starting image's space so the stroke keeps extending instead
      // of snapping back to the top. Canvas mode has no `imageIdx` → pass-thru.
      var xy;
      if (this.handleKind === "strip" && this._stripActiveDraw &&
          typeof pt.imageIdx === "number") {
        xy = this._stripTranslatePoint(pt, this._stripActiveDraw.imageIdx);
      } else {
        xy = [pt.x, pt.y];
      }
      var draft = this.draftState;
      var pts = draft.geometry.points;

      // Markers run their input through an exponential filter before it
      // becomes a sample. The spline passes exactly through what it's given,
      // so unfiltered pointer noise — tremor, and a mouse's integer-pixel
      // steps — is drawn faithfully as wobble. Freehand skips it: that path
      // fits beziers to the whole stroke on release, which does its own
      // smoothing, and filtering first would smear the corners it's trying
      // to find.
      if (draft.kind === "marker") {
        var sm = draft._smoothed || (draft._smoothed = [xy[0], xy[1]]);
        // Smooth what is small enough to be tremor; follow what is not.
        var mdx = xy[0] - sm[0], mdy = xy[1] - sm[1];
        var reach = this._freehandFitTolerance(MARKER_TREMOR_PX);
        var moved = Math.sqrt(mdx * mdx + mdy * mdy);
        var carry = MARKER_STREAMLINE * Math.max(0, 1 - moved / reach);
        sm[0] += mdx * (1 - carry);
        sm[1] += mdy * (1 - carry);
        // Where the pointer really is, so the stroke can be pinned to it on
        // release rather than stopping short by the filter's lag.
        draft._rawLast = [xy[0], xy[1]];
        xy = [sm[0], sm[1]];
      }

      var last = pts[pts.length - 1];
      var dx = xy[0] - last[0], dy = xy[1] - last[1];
      var minStep = draft.kind === "marker"
        ? this._freehandFitTolerance(MARKER_SAMPLE_PX)
        : 2;
      if (dx * dx + dy * dy < minStep * minStep) return;
      pts.push(xy);
      this._renderShape(draft);
    },

    _commitFreehand: function(_pt) {
      var pts = this.draftState.geometry.points;
      var kind = this.draftState.kind || "freehand";
      var oldEl = this.draftState.el;

      // A click with a pen in hand is a dot, not a mistake.
      //
      // It used to be thrown away — the gesture was too small to be a
      // stroke, so nothing was drawn at all — which made the one mark you
      // cannot draw with a drag impossible: the dot under a question mark,
      // an i, a decimal point. The press already knows where it landed, and
      // a two-point stroke of no length paints as a disc under the round
      // cap these kinds already wear (the same trick the dotted dash
      // pattern uses).
      //
      // Both stroke tools, because both are drawing tools; every other kind
      // still treats a click as a cancelled drag, where it means "I changed
      // my mind" rather than "put one here".
      if (pts.length < 2 || this._strokeIsClick(pts)) {
        if (kind !== "marker" && kind !== "freehand") {
          this._cancelDraft();
          return;
        }
        var at = this.draftState._rawLast || pts[0];
        // A hair of length, not none. The round cap paints either as the
        // same disc, but a path of zero length has no hit region: the
        // browser could not find the dot under a click, so it drew and then
        // could not be selected, styled or deleted. A hundredth of an image
        // px is below anything visible at any zoom the canvas allows, and
        // enough for the stroke to be a thing the pointer can land on.
        oldEl.classList.remove("is-draft");
        this._finalizeShape(
          kind, { points: [[at[0], at[1]], [at[0] + DOT_LENGTH, at[1]]] }, oldEl
        );
        return;
      }

      // Markers keep a point-based stroke (no bezier nodes / pen editor), but
      // get a light smoothing pass on release so finger / mouse jitter reads
      // as clean curves — close to what was drawn, just prettier. Still fully
      // styleable + selectable. Freehand simplifies + fits to nodes below.
      if (kind === "marker") {
        // The filter lags the pointer, so the last sample sits short of where
        // the stroke was actually released — by more the faster it ended.
        // Land it on the real position, unless a sample is already there.
        var rawEnd = this.draftState._rawLast;
        if (rawEnd) {
          var tail = pts[pts.length - 1];
          var ex = rawEnd[0] - tail[0], ey = rawEnd[1] - tail[1];
          var snap = this._freehandFitTolerance(MARKER_SAMPLE_PX);
          if (ex * ex + ey * ey > snap * snap) pts.push([rawEnd[0], rawEnd[1]]);
        }
        oldEl.classList.remove("is-draft");
        this._finalizeShape("marker", { points: this._smoothStroke(pts) }, oldEl);
        return;
      }

      // Freehand: simplify (Ramer–Douglas–Peucker) + fit (Schneider) the raw
      // samples into editable bezier nodes. RDP runs a touch tighter than the
      // fit tolerance so it only strips jitter and leaves real curvature.
      var tol = this._freehandFitTolerance(8);
      var simplified = this._rdpSimplify(pts, tol * 0.6);
      var beziers = this._fitCurve(simplified, tol);
      // Degenerate fit (e.g. a single dot) — keep the raw polyline so the
      // stroke is never lost; it stays in the legacy {points} format.
      if (!beziers || !beziers.length) {
        oldEl.classList.remove("is-draft");
        this._finalizeShape(kind, { points: pts }, oldEl);
        return;
      }
      var nodes = this._beziersToNodes(beziers);
      var path = svgEl("path", { "stroke-width": "2", fill: "none" });
      path.classList.add("etcher-shape");
      this._applyShapeColor(path, this.activeColor);
      this.svg.appendChild(path);
      oldEl.remove();
      this.draftState.el = path;
      this.draftState.geometry = { nodes: nodes };
      this._finalizeShape("freehand", { nodes: nodes }, path);
    },

    // -------------------------------------------------------------------------
    // Text — freestanding text-label shape. Click-drag a bounding box,
    // release to commit and enter inline edit mode (HTML <input> hosted
    // in a <foreignObject> over the bbox). The text content is stored
    // in the annotation's `title` field — the same column an inline
    // title on other shapes uses, so a text shape is essentially "just
    // a title with a custom bbox."
    // -------------------------------------------------------------------------

    // Is this text shape a TEXT BOX — a box the user drew that stays the
    // size they drew it, with the text wrapping and sizing inside? The
    // flag rides `style.box` on committed shapes (so it persists over the
    // wire as plain style data) and `fixedBox` on the draft, which has no
    // style of its own yet.
    _textboxFixed: function(shape) {
      return !!(shape &&
        ((shape.style && shape.style.box === "fixed") || shape.fixedBox));
    },

    // Where a text box's text sits inside it: `style.text_align` {h, v},
    // left/centre/right and top/middle/bottom. Top-left when unset.
    _textboxAlign: function(shape) {
      var a = (shape && shape.style && shape.style.text_align) || {};
      return {
        h: a.h === "center" || a.h === "right" ? a.h : "left",
        v: a.v === "middle" || a.v === "bottom" ? a.v : "top"
      };
    },

    // A text box's corner type and reach in container px (0 = sharp),
    // never more than half the box's shorter side.
    _textboxCorner: function(shape, w, h) {
      var st = (shape && shape.style) || {};
      var type = TEXTBOX_CORNERS.indexOf(st.corner) >= 0 ? st.corner : "round";
      var size = typeof st.corner_size === "number" && isFinite(st.corner_size) && st.corner_size > 0
        ? st.corner_size : 0;
      var scale = 1;
      try { scale = this._markerScale() || 1; } catch (_) { scale = 1; }
      var r = Math.max(0, Math.min(size * scale, Math.min(w, h) / 2));
      return { type: type, r: r };
    },

    // The padding a text box's words need: its usual pad, or more once the
    // corners bite deep enough that text at that pad would run out through
    // them — the inset each corner type needs (TEXTBOX_CORNER_INSET), with
    // half the usual pad kept as breathing room past the cut.
    _textboxPadPx: function(shape, basePad, w, h) {
      var c = this._textboxCorner(shape, w, h);
      if (!(c.r > 0)) return basePad;
      return Math.max(basePad, c.r * TEXTBOX_CORNER_INSET[c.type] + basePad * 0.5);
    },

    // A text box is drawn by a frame path rather than its rect, so its
    // corners can take any shape: the frame wears the plate and the
    // hover/selection outline, the rect stays as the invisible hit area.
    _drawTextboxFrame: function(shape, el, x, y, w, h) {
      if (!el || !el.querySelector) return;
      el.classList.add("is-textbox");
      var frame = el.querySelector(".etcher-textbox-frame");
      if (!frame) {
        frame = svgEl("path", { "stroke-width": "1.5" });
        frame.classList.add("etcher-textbox-frame");
        el.insertBefore(frame, el.firstChild);
      }
      var c = this._textboxCorner(shape, w, h);
      frame.setAttribute("d", this._cornerPath(x, y, w, h, c.type, c.r));
      var bg = this._labelBgFor(shape);
      frame.style.fill = bg || "";
    },

    // The outline of a box with shaped corners, as path data.
    _cornerPath: function(x, y, w, h, type, r) {
      var R = x + w, B = y + h;
      if (!(r > 0)) return "M" + x + " " + y + "H" + R + "V" + B + "H" + x + "Z";
      var a = function(ex, ey, sweep) { return "A" + r + " " + r + " 0 0 " + sweep + " " + ex + " " + ey; };
      var p = "M" + (x + r) + " " + y + "H" + (R - r);
      if (type === "chamfer") {
        return p + "L" + R + " " + (y + r) + "V" + (B - r) + "L" + (R - r) + " " + B +
          "H" + (x + r) + "L" + x + " " + (B - r) + "V" + (y + r) + "Z";
      }
      if (type === "notch") {
        return p + "V" + (y + r) + "H" + R + "V" + (B - r) + "H" + (R - r) + "V" + B +
          "H" + (x + r) + "V" + (B - r) + "H" + x + "V" + (y + r) + "H" + (x + r) + "Z";
      }
      // Round bulges out (clockwise arcs); concave scoops in about the
      // corner point itself (the same arcs swept the other way).
      var sw = type === "concave" ? 0 : 1;
      return p + a(R, y + r, sw) + "V" + (B - r) + a(R - r, B, sw) +
        "H" + (x + r) + a(x, B - r, sw) + "V" + (y + r) + a(x + r, y, sw) + "Z";
    },

    // `fixedBox` is the textbox tool's variant: same draft, same element,
    // but the committed shape keeps the drawn box fixed (see TOOL_DEFS).
    // ── Point text ──────────────────────────────────────────────────────
    //
    // What the text tool makes, the way Affinity's Artistic Text works: the
    // text is a graphic, not a box the words flow into. Its lines are the
    // ones the author typed (Shift+Enter) and nothing ever wraps them; the
    // box is always exactly the text. It has a size (`style.font_size`, in
    // canvas units like every pinned size) and a stretch along each axis
    // (`style.scale_x` / `style.scale_y`, absent = 1). Its handles scale it
    // evenly, the opposite side holding still; with Shift they stretch it —
    // and all of that works while it is being typed into.
    //
    // `style.point` marks it. Text from before keeps rendering as it did
    // until someone picks it up (`_ensurePointText`).
    _isPointText: function(shape) {
      return !!(shape && shape.kind === "text" && shape.style &&
        shape.style.point === true && !this._textboxFixed(shape));
    },

    // A callout's words work the same way once `style.point` marks it:
    // sized by their font and stretch, never wrapped, its box written back
    // into `geometry.text_box`. The leader and anchor are untouched.
    _isPointCallout: function(shape) {
      return !!(shape && shape.kind === "callout" && shape.style &&
        shape.style.point === true);
    },

    // A shape's label (rectangle, circle, arrow, dimension…) likewise, once
    // `style.title_point` marks it. Its stretch is `style.title_scale_x` /
    // `title_scale_y` — style, not metadata, because the host keeps a
    // shape's style whole and only a named few metadata keys.
    _isPointLabel: function(shape) {
      return !!(shape && !this._isTextKind(shape.kind) && shape.style &&
        shape.style.title_point === true);
    },

    // Point text and point callouts: the frames that live on the shape's
    // own edit mode (a label's live on title-edit mode instead).
    _isPointFrame: function(shape) {
      return this._isPointText(shape) || this._isPointCallout(shape);
    },

    // The words a shape's editor types into work Affinity-style.
    _typesAsPoint: function(shape) {
      if (!shape) return false;
      if (shape.kind === "text") return this._isPointText(shape);
      if (shape.kind === "callout") return this._isPointCallout(shape);
      return this._isPointLabel(shape);
    },

    // Which of a shape's texts a point frame is about: "label" for a
    // shape's title, the shape's own kind otherwise.
    _pointPart: function(shape) {
      if (!shape) return null;
      return this._isTextKind(shape.kind) ? shape.kind : "label";
    },

    _pointStretch: function(shape, part) {
      var st = (shape && shape.style) || {};
      var label = (part || this._pointPart(shape)) === "label";
      var rx = label ? st.title_scale_x : st.scale_x;
      var ry = label ? st.title_scale_y : st.scale_y;
      var sx = typeof rx === "number" && isFinite(rx) && rx > 0 ? rx : 1;
      var sy = typeof ry === "number" && isFinite(ry) && ry > 0 ? ry : 1;
      return { x: sx, y: sy };
    },

    // The lines exactly as typed — spaces kept, nothing folded. While the
    // editor is open the draft is what shows, so the text on screen is the
    // text being made, keystroke by keystroke.
    _pointTextLines: function(shape) {
      var t = shape._draftTitle != null
        ? shape._draftTitle
        : ((shape.metadata && shape.metadata.title) || "");
      return String(t).split("\n");
    },

    // Point text's natural size, unstretched, in container px — the
    // measure every point frame (text, callout words, label) is laid out
    // from. Lines step 1.1em, as everywhere; an empty text keeps one
    // line's room so the caret has a home. The box runs from the top of
    // the tallest letters to the bottom of the descenders (y, p, g), with
    // the same pad all round, so the words sit centred in it and nothing
    // pokes out of a background.
    _pointMeasure: function(shape) {
      var fontPx = this._fontSizeFor(shape, this._zoomPx(DEFAULT_LABEL_FONT_SIZE));
      var pad = fontPx * 0.2;
      var family = "ui-sans-serif, system-ui, -apple-system, sans-serif";
      var lines = this._pointTextLines(shape);
      var widest = 0;
      for (var i = 0; i < lines.length; i++) {
        var lw = lines[i] ? this._measureTextWidth(lines[i], fontPx, family, "500") : 0;
        if (lw > widest) widest = lw;
      }
      var empty = lines.length === 1 && !lines[0];
      var met = this._pointTextMetrics(family);
      return {
        fontPx: fontPx, pad: pad, family: family, lines: lines, empty: empty, met: met,
        natW: Math.max(widest, empty ? fontPx * 0.5 : 0) + pad * 2,
        natH: (met.asc + met.desc) * fontPx + Math.max(0, lines.length - 1) * fontPx * 1.1 + pad * 2
      };
    },

    // Draw a measured point text with its top-left at (tx, ty), stretched
    // about that corner. `turn` is the rotation already on the rect and
    // text (a label turns them one by one), kept ahead of the stretch;
    // null when the turn sits on a parent or there is none. Returns the
    // layout the editor fits itself over.
    _pointDraw: function(shape, M, tx, ty, str, trect, ttext, turn) {
      var self = this;
      var fontPx = M.fontPx, pad = M.pad;
      var stretch = (str.x !== 1 || str.y !== 1)
        ? "matrix(" + str.x + " 0 0 " + str.y + " " + tx * (1 - str.x) + " " + ty * (1 - str.y) + ")"
        : null;
      var xf = [turn, stretch].filter(Boolean).join(" ");
      if (trect) {
        trect.setAttribute("x", tx);
        trect.setAttribute("y", ty);
        trect.setAttribute("width", M.natW);
        trect.setAttribute("height", M.natH);
        if (xf) trect.setAttribute("transform", xf);
        else trect.removeAttribute("transform");
        self._applyLabelBg(trect, shape);
      }
      if (ttext) {
        ttext.setAttribute("x", tx + pad);
        ttext.setAttribute("y", ty + pad + M.met.asc * fontPx);
        ttext.setAttribute("font-size", fontPx);
        ttext.setAttribute("font-family", M.family);
        ttext.setAttribute("font-weight", "500");
        ttext.setAttribute("dominant-baseline", "alphabetic");
        // Spaces are the author's: kept, not collapsed.
        ttext.setAttribute("xml:space", "preserve");
        ttext.style.whiteSpace = "pre";
        if (xf) ttext.setAttribute("transform", xf);
        else ttext.removeAttribute("transform");
        while (ttext.firstChild) ttext.removeChild(ttext.firstChild);
        if (!M.empty) {
          M.lines.forEach(function(line, idx) {
            var tspan = svgEl("tspan", { x: tx + pad, dy: idx === 0 ? "0" : "1.1em" });
            tspan.textContent = line || "\u00A0";
            ttext.appendChild(tspan);
          });
        }
      }
      shape._pointLayout = {
        tx: tx, ty: ty, pad: pad, fontPx: fontPx, met: M.met,
        sx: str.x, sy: str.y, natW: M.natW, natH: M.natH,
        turn: turn || null
      };
      return shape._pointLayout;
    },

    // An open editor rides its text: typing, a handle drag, a pan or a
    // panel edit all re-render, and every point render ends here.
    // Re-render a shape whose box may change size under it — point text
    // being typed into, or given a new font size. The box grows with the
    // words; a turned text turns about its box's middle, so a growing box
    // used to carry the whole text off — up, down, around. The box is set
    // back so its own top-left corner — where the words start, and the
    // corner the text grows away from — stays where it was.
    _renderKeepingCorner: function(shape) {
      var rot = this._shapeRot(shape);
      var g0 = shape.geometry;
      var c0 = rot && g0 ? { x: g0.x + g0.w / 2, y: g0.y + g0.h / 2 } : null;
      var w0 = g0 && g0.w, h0 = g0 && g0.h;
      this._renderShape(shape);
      var g = shape.geometry;
      if (c0 && g && (Math.abs(g.w - w0) > 1e-9 || Math.abs(g.h - h0) > 1e-9)) {
        this._recenterRotated(shape, c0, rot);
        this._renderShape(shape);
      }
    },

    _fitPointEditor: function(shape) {
      var ed = this._textEditor;
      if (ed && ed.shape === shape && ed.point && ed.fit) ed.fit();
    },

    _renderPointText: function(shape, el, g, trect, ttext) {
      var self = this;
      // Marks the box for the Affinity-style outline (black, dashed).
      if (el && el.classList) el.classList.add("is-point");
      var scale = 1;
      try { scale = self._markerScale() || 1; } catch (_) { scale = 1; }
      var str = self._pointStretch(shape, "text");
      var M = self._pointMeasure(shape);
      // Corners, as a text box and a callout have them (see the callout
      // render): padded in as far as they bite, the box growing to suit.
      var ptPad = self._textboxPadPx(shape, M.pad, M.natW * str.x, M.natH * str.y);
      if (ptPad > M.pad) {
        M.natW += 2 * (ptPad - M.pad);
        M.natH += 2 * (ptPad - M.pad);
        M.pad = ptPad;
      }
      // The geometry IS the box, always: written here so whatever reads it
      // (hit-tests, handles, the turn below, the server) agrees with what
      // is drawn.
      g.w = M.natW * str.x / scale;
      g.h = M.natH * str.y / scale;
      var tbox = self._orientedBox(g);
      tbox.deg = (tbox.deg || 0) + self._shapeRot(shape);
      self._setRotateTransform(el, tbox);
      self._pointDraw(shape, M, tbox.x, tbox.y, str, trect, ttext, null);
      self._drawTextboxFrame(shape, el, tbox.x, tbox.y, M.natW * str.x, M.natH * str.y);
      shape._pointLayout.turn = el.getAttribute("transform");
      shape._renderedBox = { x: g.x, y: g.y, w: g.w, h: g.h };
      self._fitPointEditor(shape);
    },

    // Whether a pointer event is on point text's outline: within a few
    // screen px of the drawn box's edge, a little more outside it than in,
    // so the band stays clear of the letters.
    _pointRectEl: function(shape) {
      var host = this._textEditHost(shape);
      return (host && host.querySelector && host.querySelector(".etcher-text-rect")) || null;
    },

    _onPointTextFrame: function(shape, e) {
      var rect = this._pointRectEl(shape);
      if (!rect || typeof e.clientX !== "number") return false;
      // Turned: test in the text's own frame, from its layout.
      var L = this._textboxFixed(shape) ? this._textboxFrameLayout(shape) : shape._pointLayout;
      var turn = L && this._parseTurn ? this._parseTurn(L.turn) : null;
      if (turn && this.svg) {
        var o = this.svg.getBoundingClientRect();
        var q = this._rotatePoint({ x: e.clientX - o.left, y: e.clientY - o.top }, turn.cx, turn.cy, -turn.deg);
        var bx = L.tx, by = L.ty, bw = L.natW * L.sx, bh = L.natH * L.sy;
        var O2 = POINT_FRAME_OUT_PX, I2 = POINT_FRAME_IN_PX;
        if (q.x < bx - O2 || q.x > bx + bw + O2 || q.y < by - O2 || q.y > by + bh + O2) return false;
        return !(q.x > bx + I2 && q.x < bx + bw - I2 && q.y > by + I2 && q.y < by + bh - I2);
      }
      var r = rect.getBoundingClientRect();
      var x = e.clientX, y = e.clientY;
      var OUT = POINT_FRAME_OUT_PX, IN = POINT_FRAME_IN_PX;
      if (x < r.left - OUT || x > r.right + OUT || y < r.top - OUT || y > r.bottom + OUT) return false;
      var inside = x > r.left + IN && x < r.right - IN && y > r.top + IN && y < r.bottom - IN;
      return !inside;
    },

    // A text box's frame in the shape of point text's layout, for the two
    // readers above: its box in the overlay's px, unturned, and the turn.
    _textboxFrameLayout: function(shape) {
      var host = this._textEditHost(shape);
      var frame = host && host.querySelector && host.querySelector(".etcher-textbox-frame");
      if (!frame || !frame.getBBox) return null;
      var b;
      try { b = frame.getBBox(); } catch (_) { return null; }
      return { tx: b.x, ty: b.y, natW: b.width, natH: b.height, sx: 1, sy: 1,
        turn: host.getAttribute("transform") || null };
    },

    // Lay the editor's grip band over point text's outline: the same band
    // `_onPointTextFrame` reads, as a ring (box grown by the outside reach,
    // less the box shrunk by the inside one) in the overlay's own px.
    _fitPointGrip: function(shape) {
      var grip = this._textEditor && this._textEditor.shape === shape && this._textEditor.grip;
      var rect = grip && this._pointRectEl(shape);
      if (!rect || !this.svg) return;
      var x, y, w, h;
      // Turned: the ring is laid out in the text's own frame and wears its
      // turn; upright, it is read off the drawn box.
      var L = this._textboxFixed(shape) ? this._textboxFrameLayout(shape) : shape._pointLayout;
      var turn = L && this._parseTurn ? this._parseTurn(L.turn) : null;
      if (turn) {
        x = L.tx; y = L.ty; w = L.natW * L.sx; h = L.natH * L.sy;
        grip.setAttribute("transform", L.turn);
      } else {
        var r = rect.getBoundingClientRect();
        var o = this.svg.getBoundingClientRect();
        x = r.left - o.left; y = r.top - o.top; w = r.width; h = r.height;
        grip.removeAttribute("transform");
      }
      var O = POINT_FRAME_OUT_PX, I = POINT_FRAME_IN_PX;
      var iw = Math.max(0, w - 2 * I), ih = Math.max(0, h - 2 * I);
      grip.setAttribute("d",
        "M" + (x - O) + " " + (y - O) + "h" + (w + 2 * O) + "v" + (h + 2 * O) + "h" + -(w + 2 * O) + "Z" +
        "M" + (x + I) + " " + (y + I) + "v" + ih + "h" + iw + "v" + -ih + "Z");
    },

    // Point text dragged by its outline while being typed into. The editor
    // stays open (the handles' `resizing` hold), the text and its handles
    // follow the pointer, Shift keeps it on one axis, and release saves
    // one undo step and hands the caret back where it was.
    _movePointTextWhileTyping: function(shape, e) {
      var self = this;
      var ed = self._textEditor;
      if (!ed) return;
      ed.resizing = true;
      var input = ed.input;
      var caret = [input.selectionStart, input.selectionEnd];
      var startPt = self._toImage(e);
      var startGeom = JSON.parse(JSON.stringify(shape.geometry));
      var part = self._pointPart(shape);
      var startLabel = part === "label" ? Object.assign({},
        shape._renderedTitleImage || self._shapeTitleBoxImage(shape, self._lastBboxTopImageFor(shape)) || {}) : null;
      var historyBefore = self._snapshotShape(shape);
      var moved = false;
      self._suspendMidpointHighlight();
      if (self.svg) self.svg.classList.add("is-over-text-frame");
      function onMove(ev) {
        var pt = self._toImage(ev);
        var dx = pt.x - startPt.x, dy = pt.y - startPt.y;
        if (ev.shiftKey) {
          if (Math.abs(dx) > Math.abs(dy)) dy = 0;
          else dx = 0;
        }
        moved = moved || dx !== 0 || dy !== 0;
        if (part === "label") {
          // As the label's own drag moves it: along its line for a shaft
          // rider, anywhere (and out of any alignment) for the rest.
          if (self._labelRidesShaft(shape.kind)) {
            var tProj = self._shaftOffsetFor(shape.geometry, pt);
            if (tProj != null) {
              shape.metadata = Object.assign({}, shape.metadata || {}, { title_offset: tProj });
            }
          } else {
            shape.metadata = Object.assign({}, shape.metadata || {}, {
              title_box: { x: startLabel.x + dx, y: startLabel.y + dy, w: startLabel.w, h: startLabel.h },
              title_align: null
            });
          }
        } else if (part === "callout") {
          // The words move; the anchor stays where it points.
          var cb = self._calloutTextBoxImage(startGeom);
          shape.geometry = {
            anchor: startGeom.anchor,
            text_box: { x: cb.x + dx, y: cb.y + dy, w: cb.w, h: cb.h }
          };
        } else {
          shape.geometry = self._translateGeometry(shape.kind, startGeom, dx, dy);
        }
        self._renderShape(shape);
        self._positionPointHandles(shape);
      }
      function onUp() {
        document.removeEventListener("pointermove", onMove, true);
        document.removeEventListener("pointerup", onUp, true);
        document.removeEventListener("pointercancel", onUp, true);
        self._resumeMidpointHighlight();
        if (self.svg) self.svg.classList.remove("is-over-text-frame");
        if (moved && shape.uuid) {
          self._emitChanged();
          self._pushUndo(shape.uuid, historyBefore, self._snapshotShape(shape));
          if (part === "label") self._syncLabelSection();
        }
        setTimeout(function() {
          if (self._textEditor !== ed) return;
          ed.resizing = false;
          try {
            input.focus({ preventScroll: true });
            input.setSelectionRange(caret[0], caret[1]);
          } catch (_) {}
        }, 0);
      }
      document.addEventListener("pointermove", onMove, true);
      document.addEventListener("pointerup", onUp, true);
      document.addEventListener("pointercancel", onUp, true);
    },

    // A font's reach as fractions of its size, for point text's box: `asc`
    // and `desc` are how far the letters really go above and below the
    // baseline (brackets and descenders included), `fontAsc`/`fontDesc` the
    // font's own line metrics, which place a textarea's baseline. Measured
    // once per family; typical values stand in where canvas can't say.
    _pointTextMetrics: function(family) {
      var cache = this._pointMetricsCache || (this._pointMetricsCache = {});
      if (cache[family]) return cache[family];
      var m = { asc: 0.76, desc: 0.24, fontAsc: 0.93, fontDesc: 0.24 };
      try {
        if (!this._measureCanvas) this._measureCanvas = document.createElement("canvas");
        var ctx = this._measureCanvas.getContext("2d");
        ctx.font = "500 " + TEXT_MEASURE_FONT_PX + "px " + family;
        var up = ctx.measureText("Hdlbk(|");
        var down = ctx.measureText("gjpqy(|");
        var n = TEXT_MEASURE_FONT_PX;
        if (up.actualBoundingBoxAscent > 0) m.asc = up.actualBoundingBoxAscent / n;
        if (down.actualBoundingBoxDescent > 0) m.desc = down.actualBoundingBoxDescent / n;
        if (up.fontBoundingBoxAscent > 0) m.fontAsc = up.fontBoundingBoxAscent / n;
        if (up.fontBoundingBoxDescent > 0) m.fontDesc = up.fontBoundingBoxDescent / n;
      } catch (_) {}
      cache[family] = m;
      return m;
    },

    // Text from before point text — wrapped at its box, its size often
    // derived from the box — becomes point text the moment someone picks
    // it up, looking exactly as it did: the lines it was showing become
    // the author's lines, and the size it was showing becomes its size.
    // Nothing is sent until the user actually changes something.
    //
    // Callouts and shapes' labels convert the same way (see
    // `_ensurePointLabel` for the label's own wrinkles).
    _ensurePointText: function(shape) {
      if (shape && shape.kind === "callout") return this._ensurePointCallout(shape);
      if (shape && !this._isTextKind(shape.kind)) return this._ensurePointLabel(shape);
      if (!shape || shape.kind !== "text" || this._textboxFixed(shape)) return false;
      if (this._isPointText(shape)) return false;
      var textEl = shape.el && shape.el.querySelector && shape.el.querySelector(".etcher-text-content");
      var fontPx = textEl && parseFloat(textEl.getAttribute("font-size"));
      var scale = 1;
      try { scale = this._markerScale() || 1; } catch (_) { scale = 1; }
      var lines = [];
      if (textEl) {
        var spans = textEl.querySelectorAll("tspan");
        for (var i = 0; i < spans.length; i++) {
          lines.push(String(spans[i].textContent || "").replace(/\u00A0/g, ""));
        }
      }
      var style = Object.assign({}, shape.style || {}, { point: true });
      if (fontPx > 0) style.font_size = fontPx / scale;
      else if (!this._hasPinnedFontSize(shape)) style.font_size = this._defaultLabelFontSize() / this._inkScale();
      shape.style = style;
      if (lines.length) {
        shape.metadata = Object.assign({}, shape.metadata || {}, { title: lines.join("\n") });
      }
      this._renderShape(shape);
      return true;
    },

    // The lines and font size an older text is showing, read off its
    // render: wrapped lines become the author's lines, the size it was
    // showing becomes its size (in canvas units).
    _shownTextOf: function(textEl) {
      var scale = 1;
      try { scale = this._markerScale() || 1; } catch (_) { scale = 1; }
      var fontPx = textEl && parseFloat(textEl.getAttribute("font-size"));
      var lines = [];
      if (textEl) {
        var spans = textEl.querySelectorAll("tspan");
        for (var i = 0; i < spans.length; i++) {
          lines.push(String(spans[i].textContent || "").replace(/\u00A0/g, ""));
        }
      }
      return { font: fontPx > 0 ? fontPx / scale : null, lines: lines };
    },

    _ensurePointCallout: function(shape) {
      if (!shape || shape.kind !== "callout" || this._isPointCallout(shape)) return false;
      var shown = this._shownTextOf(shape.el && shape.el.querySelector &&
        shape.el.querySelector(".etcher-text-content"));
      var style = Object.assign({}, shape.style || {}, { point: true });
      if (shown.font) style.font_size = shown.font;
      else if (!this._hasPinnedFontSize(shape)) style.font_size = this._defaultLabelFontSize() / this._inkScale();
      // A callout being made takes the remembered corners (sharp unless
      // chosen); one from before keeps none, i.e. sharp.
      if (!(shape.metadata && shape.metadata.title) && style.corner == null) {
        var cDef = this._textboxCornerDefaults("callout");
        style.corner = cDef.corner;
        style.corner_size = cDef.size > 0 ? cDef.size / this._inkScale() : 0;
      }
      shape.style = style;
      // A box only ever stored as the legacy `text_at` point gets its box.
      if (shape.geometry && !shape.geometry.text_box) {
        shape.geometry = {
          anchor: shape.geometry.anchor,
          text_box: Object.assign({}, this._calloutTextBoxImage(shape.geometry))
        };
      }
      if (shown.lines.length && (shape.metadata && shape.metadata.title)) {
        shape.metadata = Object.assign({}, shape.metadata || {}, { title: shown.lines.join("\n") });
      }
      this._renderShape(shape);
      return true;
    },

    // A shape's label as point text. Its lines never wrapped, so only the
    // size needs pinning to what it shows; where it sits stays as it was —
    // a dragged label keeps its corner, an aligned one its alignment, a
    // shaft rider its place on the line.
    _ensurePointLabel: function(shape) {
      if (!shape || this._isTextKind(shape.kind) || this._isPointLabel(shape)) return false;
      var shown = this._shownTextOf(shape.titleGroup &&
        shape.titleGroup.querySelector(".etcher-text-content"));
      var style = Object.assign({}, shape.style || {}, { title_point: true });
      if (shown.font) style.font_size = shown.font;
      else if (!this._hasPinnedFontSize(shape)) style.font_size = this._defaultLabelFontSize() / this._inkScale();
      shape.style = style;
      this._renderShape(shape);
      return true;
    },

    // Scaling point text changes its size, so the panel's Label size box
    // follows the pull as it happens.
    _syncPointSize: function() {
      try { if (this._syncFontRow) this._syncFontRow(); } catch (_) {}
    },

    // Handles on whichever point frame a shape shows: the label's when its
    // label is the one being worked on, the shape's own otherwise.
    _positionPointHandles: function(shape) {
      if (this.editingTitleShape === shape) this._positionAllTitleHandles(shape);
      else if (this.editingShape === shape) this._positionAllHandles(shape);
    },

    // A handle pulled on point text. Everything scales it evenly — its size
    // changes, its proportions never do: a corner about the opposite corner,
    // an edge about the middle of the opposite edge. Shift frees it: a
    // corner then scales each way on its own, an edge stretches one axis.
    // `start` is the text as the drag found it; `handle` is a corner index
    // 0-3 (TL, TR, BR, BL) or an edge "top" / "right" / "bottom" / "left".
    _resizePointText: function(shape, start, handle, pt, freeform) {
      var b = start.box;
      var right = b.x + b.w, bottom = b.y + b.h;
      var MIN_K = 0.05;
      var scale = 1;
      try { scale = this._markerScale() || 1; } catch (_) { scale = 1; }
      var font = start.font, sx = start.sx, sy = start.sy;
      var nx = b.x, ny = b.y, nw = b.w, nh = b.h;
      if (typeof handle === "string" && !freeform) {
        // An edge scales it evenly too: the opposite edge holds, and the
        // text grows or shrinks about that edge's middle.
        var horiz = handle === "left" || handle === "right";
        var ke = Math.max(MIN_K, horiz
          ? (handle === "right" ? pt.x - b.x : right - pt.x) / b.w
          : (handle === "bottom" ? pt.y - b.y : bottom - pt.y) / b.h);
        font = start.font * ke;
        nw = b.w * ke; nh = b.h * ke;
        if (horiz) {
          nx = handle === "left" ? right - nw : b.x;
          ny = b.y + (b.h - nh) / 2;
        } else {
          ny = handle === "top" ? bottom - nh : b.y;
          nx = b.x + (b.w - nw) / 2;
        }
      } else if (typeof handle === "string") {
        // Shift on an edge: stretch along that one axis instead.
        if (handle === "left" || handle === "right") {
          var kx = Math.max(MIN_K, (handle === "right" ? pt.x - b.x : right - pt.x) / b.w);
          sx = start.sx * kx;
          nw = b.w * kx;
          if (handle === "left") nx = right - nw;
        } else {
          var ky = Math.max(MIN_K, (handle === "bottom" ? pt.y - b.y : bottom - pt.y) / b.h);
          sy = start.sy * ky;
          nh = b.h * ky;
          if (handle === "top") ny = bottom - nh;
        }
      } else {
        // The anchor is the corner across from the one being pulled.
        var ax = (handle === 0 || handle === 3) ? right : b.x;
        var ay = (handle === 0 || handle === 1) ? bottom : b.y;
        var dirX = (handle === 0 || handle === 3) ? -1 : 1;
        var dirY = (handle === 0 || handle === 1) ? -1 : 1;
        var dx = (pt.x - ax) * dirX, dy = (pt.y - ay) * dirY;
        var k;
        if (freeform) {
          var fx = Math.max(MIN_K, dx / b.w), fy = Math.max(MIN_K, dy / b.h);
          // Height through the size (crisper than a stretch), width as the
          // stretch that is left over.
          font = start.font * fy;
          sx = start.sx * fx / fy;
          nw = b.w * fx; nh = b.h * fy;
        } else {
          // Projected onto the box's diagonal, so the pull tracks the
          // pointer smoothly whichever way it leans.
          k = Math.max(MIN_K, (dx * b.w + dy * b.h) / (b.w * b.w + b.h * b.h));
          font = start.font * k;
          nw = b.w * k; nh = b.h * k;
        }
        nx = dirX < 0 ? ax - nw : ax;
        ny = dirY < 0 ? ay - nh : ay;
      }
      // Never so small it cannot be found again.
      if (font * scale < 2) font = 2 / scale;
      var box = { x: nx, y: ny, w: nw, h: nh };
      if (start.part === "label") {
        shape.style = Object.assign({}, shape.style || {},
          { font_size: font, title_scale_x: sx, title_scale_y: sy });
        // A shaft rider stays on its line, centred where it was; any other
        // label is placed where the pull left it, out of any alignment —
        // as a drag of it would be.
        if (!this._labelRidesShaft(shape.kind)) {
          shape.metadata = Object.assign({}, shape.metadata || {}, { title_box: box, title_align: null });
        }
        return;
      }
      shape.style = Object.assign({}, shape.style || {}, { font_size: font, scale_x: sx, scale_y: sy });
      if (start.part === "callout") {
        shape.geometry = { anchor: shape.geometry.anchor, text_box: box };
      } else {
        shape.geometry = box;
      }
    },

    // The frame point text's handles sit on: the text's own box, but never
    // narrower or shorter than POINT_HANDLE_MIN_PX on screen. Freshly
    // started text is a letter wide and its eight handles otherwise pile up
    // over it in a knot that does not read as corners. It grows right and
    // down, the way the text itself grows.
    _pointHandleBox: function(shape, part) {
      part = part || (shape.kind === "text" || shape.kind === "callout" ? shape.kind : "label");
      var b = part === "label"
        ? (shape._renderedTitleImage || this._shapeTitleBoxImage(shape, this._lastBboxTopImageFor(shape)))
        : part === "callout"
          ? (shape._renderedBox || this._calloutTextBoxImage(shape.geometry))
          : (shape._renderedBox || shape.geometry);
      var scale = 1;
      try { scale = this._markerScale() || 1; } catch (_) { scale = 1; }
      var min = POINT_HANDLE_MIN_PX / scale;
      return { x: b.x, y: b.y, w: Math.max(b.w, min), h: Math.max(b.h, min) };
    },

    _pointTextDragStart: function(shape, part) {
      part = part || this._pointPart(shape);
      var str = this._pointStretch(shape, part);
      // From the frame the handles are drawn on, so the pull is measured
      // from where the user grabbed.
      return {
        part: part,
        box: this._pointHandleBox(shape, part),
        font: shape.style.font_size, sx: str.x, sy: str.y
      };
    },

    _startText: function(pt, e, fixedBox) {
      var g = svgEl("g");
      g.classList.add("etcher-shape", "etcher-text", "is-draft");
      // Hit-zone rect — invisible by default, dashed border while
      // dragging the draft so the user can see what they're sizing.
      var rect = svgEl("rect", {
        fill: "transparent",
        stroke: "transparent",
        "stroke-width": "2"
      });
      rect.classList.add("etcher-text-rect");
      var text = svgEl("text", {
        "text-anchor": "start",
        "dominant-baseline": "hanging",
        fill: "currentColor",
        stroke: "none"
      });
      text.classList.add("etcher-text-content");
      g.appendChild(rect);
      g.appendChild(text);
      // Preview in the colour the committed shape will get (see
      // _finalizeShape's text branch) so the draft doesn't flash a
      // different colour than the label it becomes.
      this._applyShapeColor(g, this._getPref("label_color") || this.activeColor);
      this.svg.appendChild(g);

      var geom = { x: pt.x, y: pt.y, w: 0, h: 0 };
      this.draftState = {
        kind: "text", anchor: pt, geometry: geom, el: g, fixedBox: !!fixedBox
      };
      this._renderShape(this.draftState);
      this._syncDraftHandles();
      try { e.target.setPointerCapture(e.pointerId); } catch (_) {}
    },

    _updateText: function(pt) {
      var a = this.draftState.anchor;
      this.draftState.geometry = {
        x: Math.min(a.x, pt.x), y: Math.min(a.y, pt.y),
        w: Math.abs(pt.x - a.x), h: Math.abs(pt.y - a.y)
      };
      this._renderShape(this.draftState);
      this._positionAllHandles(this.draftState);
    },

    _commitText: function(pt) {
      // The textbox tool's draft carries the flag (the commit switch keys
      // on the draft KIND, which is "text" for both tools).
      var fixedBox = !!this.draftState.fixedBox;
      var a = this.draftState.anchor;
      // A text box from a bare click is a placeholder too: the click sets
      // one corner and the next click elsewhere the other (see
      // `_commitRectangle`). The text tool's click still places text — a
      // caret where you click is what that tool is for.
      if (fixedBox && this._isClickGesture(a, pt)) {
        this.draftState.armed = true;
        this._syncDraftHandles();
        return;
      }
      var geom = {
        x: Math.min(a.x, pt.x),
        y: Math.min(a.y, pt.y),
        w: Math.abs(pt.x - a.x),
        h: Math.abs(pt.y - a.y)
      };
      // Tiny boxes from accidental clicks default to a sensible minimum
      // size in image px so the user gets a usable text bbox even on a
      // single click. The minimum is computed from the current zoom so
      // it looks roughly the same on screen across zoom levels.
      // The click-vs-drag THRESHOLD is a gesture judgement and stays
      // screen-relative; the minted default SIZE is ink and rides the
      // uniform policy — a clicked text box is the same relative size
      // whatever the zoom was when it was clicked.
      var minImagePx = this._textDefaultBoxImagePx();
      // Read BEFORE the clamps: whether the user actually drew a box, as
      // opposed to clicking — the clamped defaults are ours, not theirs.
      var drewBox = geom.h >= minImagePx;
      var boxPx = this._newTextBoxPx();
      if (geom.w < minImagePx) geom.w = boxPx * 4;
      if (geom.h < minImagePx) geom.h = boxPx * 1.2;

      var el = this.draftState.el;
      el.classList.remove("is-draft");
      var self = this;
      this._finalizeShape("text", geom, el, function(shape) {
        // The textbox tool: the box is the user's and the font is its
        // own, stamped at the default label size (the same formula
        // _startTextEdit uses). The two never derive from each other
        // again — resizing the box leaves the text, resizing the text
        // leaves the box.
        if (fixedBox) {
          // Sharp corners unless the user has chosen otherwise (the corner
          // controls with the tool armed, or tuning the last fresh box).
          var cornerDef = self._textboxCornerDefaults("textbox");
          shape.style = Object.assign({}, shape.style || {}, {
            box: "fixed",
            font_size: self._defaultLabelFontSize() / self._inkScale(),
            corner: cornerDef.corner,
            corner_size: cornerDef.size > 0 ? cornerDef.size / self._inkScale() : 0
          });
          self._renderShape(shape);
          // Selected while typing, as point text is: its box and handles
          // are up from the first keystroke, so it can be resized, turned
          // or moved without leaving the words (see `_startTextEdit`).
          self._suppressEditDismissUntil = Date.now() + 400;
          self._enterEditMode(shape);
          self._startTextEdit(shape);
          return;
        }
        // A DRAWN box is a size request: small box, small text; big box,
        // big text — the way every drawing program's text tool works. The
        // drawn height pins the font (the same 0.65 the box-drives-font
        // rule uses, stored in image units like every pinned size), and
        // that pin beats the remembered label-size default, which
        // otherwise made every new text the same size no matter what was
        // dragged. A plain CLICK keeps the default: nothing was asked.
        var scale = 1;
        try { scale = self._markerScale() || 1; } catch (_) { scale = 1; }
        if (!(scale > 0)) scale = 1;
        var target;
        if (drewBox) {
          target = geom.h * 0.65; // image units
          target = Math.max(FONT_SIZE_MIN / scale,
            Math.min(FONT_SIZE_MAX / scale, target));
        } else {
          // Floored at what can be read on the glass, as the old clicked
          // box was: on a picture zoomed far out (a phone, a big photo) the
          // default size in canvas units comes out a few pixels tall.
          target = Math.max(self._defaultLabelFontSize() / self._inkScale(),
            NEW_TEXT_MIN_SCREEN_PX * 0.75 / scale);
          // A click puts the middle of the first line on the click, the
          // way a caret lands where you click.
          shape.geometry = {
            x: pt.x - target * 0.2,
            y: pt.y - target * 0.7,
            w: 0, h: 0
          };
        }
        var ptCorner = self._textboxCornerDefaults("text");
        shape.style = Object.assign({}, shape.style || {}, {
          font_size: target,
          point: true,
          corner: ptCorner.corner,
          corner_size: ptCorner.size > 0 ? ptCorner.size / self._inkScale() : 0
        });
        self._renderShape(shape);
        // Selected AND typing: the box and its eight handles are up while
        // the words go in, so it can be scaled or stretched mid-sentence.
        //
        // A drag ends with a click at its far corner — off the new text,
        // which sits at the drag's start and is one empty line tall — and
        // the outside-click dismisser read that click as "clicked away" and
        // dropped the selection, so a dragged text had no box and no dots
        // until Enter. Same guard `_finalizeShape` sets for its own shapes.
        self._suppressEditDismissUntil = Date.now() + 400;
        self._enterEditMode(shape);
        self._startTextEdit(shape);
      });
    },

    // -------------------------------------------------------------------------
    // Eraser — press-and-drag to gray out shapes the cursor crosses,
    // release to bulk-delete them. Hit-tests against image-px geometry
    // (not DOM pointer-events, which are off for shapes while a tool
    // is active). All shapes erased in one stroke become a single
    // undo op so ⌘Z brings the whole sweep back at once.
    // -------------------------------------------------------------------------

    _startErase: function(pt, e) {
      this._erasingActive = true;
      this._erasingHits = [];
      this._erasingHitSet = new Set();
      // Adopt the hover-preview shape into the active sweep so the
      // user doesn't have to "re-hit" it on press.
      if (this._eraserHovered) {
        this._erasingHitSet.add(this._eraserHovered);
        this._erasingHits.push(this._eraserHovered);
        this._eraserHovered = null;
      }
      try { e.target.setPointerCapture(e.pointerId); } catch (_) {}
      this._hideTooltip();
      this._eraserMove(pt);
    },

    // Mouseover preview while the eraser is idle (no button held).
    // Grays out the single shape under the cursor so the user sees
    // what would be erased on click. Limited to one shape at a time;
    // sweep mode (press-and-drag) accumulates multiple.
    _eraserHover: function(pt) {
      var hit = null;
      for (var i = 0; i < this.shapes.length; i++) {
        var s = this.shapes[i];
        if (!s.uuid) continue;
        if (this._eraserHit(s, pt)) { hit = s; break; }
      }
      if (this._eraserHovered === hit) return;
      this._clearEraserHover();
      if (hit) {
        if (hit.el) hit.el.classList.add("is-erasing");
        if (hit.titleGroup) hit.titleGroup.classList.add("is-erasing");
        this._eraserHovered = hit;
      }
    },

    _clearEraserHover: function() {
      var prev = this._eraserHovered;
      if (!prev) return;
      if (prev.el) prev.el.classList.remove("is-erasing");
      if (prev.titleGroup) prev.titleGroup.classList.remove("is-erasing");
      this._eraserHovered = null;
    },

    _onPointerLeave: function() {
      this._clearEraserHover();
      this._clearClosestMidpoint();
    },

    _eraserMove: function(pt) {
      if (!this._erasingActive) return;
      var self = this;
      this.shapes.forEach(function(shape) {
        if (self._erasingHitSet.has(shape)) return;
        if (!shape.uuid) return;
        if (self._eraserHit(shape, pt)) {
          self._erasingHitSet.add(shape);
          self._erasingHits.push(shape);
          if (shape.el) shape.el.classList.add("is-erasing");
          if (shape.titleGroup) shape.titleGroup.classList.add("is-erasing");
        }
      });
    },

    _cancelErase: function() {
      // An erase in flight, abandoned. The eraser greys its hits during the
      // press and deletes them on release, so abandoning has to un-grey
      // them AND disarm the release: a second finger landing mid-stroke is
      // the user reaching for the canvas, not confirming a deletion, and
      // the pointerup that ends their pan would otherwise commit it.
      if (!this._erasingActive) return;
      this._erasingActive = false;
      var hits = this._erasingHits || [];
      this._erasingHits = null;
      this._erasingHitSet = null;
      hits.forEach(function(shape) {
        if (shape.el) shape.el.classList.remove("is-erasing");
        if (shape.titleGroup) shape.titleGroup.classList.remove("is-erasing");
      });
    },

    _commitErase: function() {
      if (!this._erasingActive) return;
      this._erasingActive = false;
      var hits = this._erasingHits || [];
      this._erasingHits = null;
      this._erasingHitSet = null;
      if (hits.length === 0) return;

      // Push a single compound undo op covering every shape in the
      // stroke so ⌘Z brings them all back together.
      this._pushUndoBulkDelete(hits);

      var self = this;
      hits.forEach(function(shape) {
        var uuid = shape.uuid;
        if (self.editingShape === shape) self._exitEditMode();
        if (self.editingTitleShape === shape) self._exitTitleEditMode();
        var idx = self.shapes.indexOf(shape);
        if (idx !== -1) {
          if (shape.el && shape.el.parentNode) shape.el.parentNode.removeChild(shape.el);
          if (shape.titleGroup && shape.titleGroup.parentNode) {
            shape.titleGroup.parentNode.removeChild(shape.titleGroup);
          }
          self.shapes.splice(idx, 1);
        }
      });
      self._hideTooltip();
      self._emitChanged();
    },

    // Hit-test in image px against each shape's geometry (or the
    // shape's title group, if it has one). Permissive — covers the
    // full visible footprint so users don't have to nick the exact
    // glyph or vertex to erase.
    _eraserHit: function(shape, pt) {
      // Eraser delegates to the shared per-kind point-in-shape test.
      // Kept as a thin alias so the eraser's call sites stay readable
      // ("hit one shape during a sweep") even though the underlying
      // logic now also drives the doc-level hover hit-test.
      // Locked (readonly) shapes can't be erased.
      if (shape.readonly) return false;
      return this._shapeContainsPoint(shape, pt);
    },

    // Returns the topmost persisted shape whose footprint contains
    // the image-px point, or `null`. Walks `self.shapes` in reverse
    // so visually-on-top shapes win when footprints overlap. Skips
    // shapes without a uuid (drafts, mid-create, recently-deleted).
    _shapeAt: function(pt) {
      if (!this.shapes) return null;
      // Strip mode: only test shapes that live on the same image as
      // `pt`. screenToImage on a strip handle returns `{imageIdx, x, y}`,
      // and shape coords are stored in per-image natural pixels, so
      // mixing pages would produce false positives.
      var stripMode = this.handleKind === "strip";
      var ptImageIdx = stripMode && typeof pt.imageIdx === "number" ? pt.imageIdx : null;
      for (var i = this.shapes.length - 1; i >= 0; i--) {
        var s = this.shapes[i];
        if (!s.uuid) continue;
        if (stripMode && s.image_idx !== ptImageIdx) continue;
        if (this._shapeContainsPoint(s, pt)) return s;
      }
      return null;
    },

    // Per-kind hit-test for "does this image-px point land on the
    // shape?". Used by the eraser sweep, the polygon midpoint
    // hover gate, and (now) the doc-level hover + click detection
    // that lets pan/zoom pass through shapes. Permissive — covers
    // the full visible footprint plus a title group when present,
    // so users don't have to nick the exact glyph or vertex.
    _shapeContainsPoint: function(shape, pt) {
      // A turned box shape is tested in its own frame.
      var hitRot = this._shapeRot ? this._shapeRot(shape) : 0;
      if (hitRot) {
        var hc = this._rotCenter(shape);
        var lp = this._rotatePoint(pt, hc.x, hc.y, -hitRot);
        var tb = shape.titleGroup && shape._renderedTitleImage;
        if (tb) {
          var tq = this._rotatePoint(pt, tb.x + tb.w / 2, tb.y + tb.h / 2, -this._labelRot(shape));
          if (tq.x >= tb.x && tq.x <= tb.x + tb.w && tq.y >= tb.y && tq.y <= tb.y + tb.h) return true;
        }
        var bx = this._rotBox(shape);
        return lp.x >= bx.x && lp.x <= bx.x + bx.w && lp.y >= bx.y && lp.y <= bx.y + bx.h;
      }
      var selfHit = this;
      function inRect(box, label) {
        if (!box) return false;
        var q = pt;
        var lr = label && selfHit._labelRot ? selfHit._labelRot(shape) : 0;
        if (lr) q = selfHit._rotatePoint(pt, box.x + box.w / 2, box.y + box.h / 2, -lr);
        return q.x >= box.x && q.x <= box.x + box.w &&
               q.y >= box.y && q.y <= box.y + box.h;
      }
      // Title group (when present) is its own hit zone — tested turned when
      // the label is.
      if (shape.titleGroup && shape._renderedTitleImage &&
          inRect(shape._renderedTitleImage, true)) {
        return true;
      }
      var g = shape.geometry;
      switch (shape.kind) {
        case "image":
        case "rectangle":
        case "audio":
        case "video":
          return inRect(g);
        case "text":
          return inRect(shape._renderedBox || g);
        case "circle": {
          var dx = pt.x - g.cx, dy = pt.y - g.cy;
          return dx * dx + dy * dy <= g.r * g.r;
        }
        case "marker":
        case "polygon":
        case "freehand":
          return this._shapeContainsImagePoint(shape, pt);
        case "callout": {
          var box = shape._renderedBox || this._calloutTextBoxImage(g);
          var labHit = this._calloutLabelScreen(shape);
          if (labHit) {
            // Upright on a turned board: test on screen, where it is drawn.
            var at;
            try { at = this._imageToContainer(pt); } catch (_) { at = null; }
            if (at && at.x >= labHit.x && at.x <= labHit.x + labHit.w &&
                at.y >= labHit.y && at.y <= labHit.y + labHit.h) return true;
          } else if (inRect(box)) return true;
          var ax = g.anchor[0], ay = g.anchor[1];
          var dax = pt.x - ax, day = pt.y - ay;
          // Small radius around the anchor dot so the user can erase
          // by clicking the leader endpoint as well as the label.
          var r = this._textDefaultBoxImagePx() * 0.6;
          if (dax * dax + day * day <= r * r) return true;
          // The leader itself. Without this the line joining the dot to the
          // label was not a thing you could click — the callout answered
          // only at its two ends, and the part you naturally aim at did
          // nothing. Tested in container px, the space the render computed
          // the leader in.
          var lead = shape._leaderContainer;
          if (lead) {
            var here;
            try { here = this._imageToContainer(pt); } catch (_) { here = null; }
            if (here) {
              // Half the stroke is already on the line; the rest is the same
              // grab pad every other stroke gets.
              var leadTol = Math.max(9.6, (lead.w || 2) / 2 + 4);
              if (this._nearSegment(here, lead.a, lead.b, leadTol)) return true;
            }
          }
          return false;
        }
        // Hit if the point is near ANY segment of the routed path — the
        // two-point case covers a straight arrow.
        case "arrow": {
          // The drawn curve, so clicking the middle of a bow hits it.
          var arPath = this._arrowRoute(g);
          var arTol = this._textDefaultBoxImagePx() * 0.6;
          // Half a heavy shaft is already on the line before any pad.
          arTol = Math.max(arTol, this._shaftHalfWidthImagePx(shape));
          for (var ai = 0; ai < arPath.length - 1; ai++) {
            if (this._nearSegment(pt, arPath[ai], arPath[ai + 1], arTol)) {
              return true;
            }
          }
          // The head, at the last point of the route. Measured as a disc
          // rather than along the segment the way a dimension's is: the
          // route can bend right at the head, so "how far back along the
          // shaft" is not a single direction here.
          var arHeadExt = this._dimHeadExtentImagePx(shape);
          if (arHeadExt && arPath.length) {
            var tip = arPath[arPath.length - 1];
            var htx = pt.x - tip.x, hty = pt.y - tip.y;
            var reach = Math.max(arHeadExt.len, arHeadExt.half);
            if (htx * htx + hty * hty <= reach * reach) return true;
          }
          return false;
        }
        case "line":
        case "dimension": {
          // Hit if the point is within ~tolerance image px of the
          // line segment from a to b. Tolerance scales with viewport
          // so the hit target stays comfortable at any zoom. Shared
          // between dimension + line since both store
          // `geometry = { a: [x,y], b: [x,y] }`.
          var dax2 = g.b[0] - g.a[0];
          var day2 = g.b[1] - g.a[1];
          var lenSq = dax2 * dax2 + day2 * day2;
          if (lenSq <= 0.0001) return false;
          var segLen = Math.sqrt(lenSq);
          var t = ((pt.x - g.a[0]) * dax2 + (pt.y - g.a[1]) * day2) / lenSq;
          t = Math.max(0, Math.min(1, t));
          var nearestX = g.a[0] + t * dax2;
          var nearestY = g.a[1] + t * day2;
          var ddx2 = pt.x - nearestX;
          var ddy2 = pt.y - nearestY;
          var tol = this._textDefaultBoxImagePx() * 0.6;
          // A heavy line is a bigger target than a hairline: half its width
          // is already ON it before any grab pad is added.
          tol = Math.max(tol, this._shaftHalfWidthImagePx(shape));

          // Near an end of a DIMENSION, the V-arrowhead is part of the
          // shape and has to be part of the target. It reaches out
          // perpendicular from the shaft, and on a heavy line it reaches a
          // long way — the heads scale with the stroke — so aiming at the
          // chevron, which is the most obvious thing to aim at, landed on
          // empty canvas while only the thin shaft answered.
          var head = this._dimHeadExtentImagePx(shape);
          if (head && Math.min(t, 1 - t) * segLen <= head.len) {
            tol = Math.max(tol, head.half);
          }
          return ddx2 * ddx2 + ddy2 * ddy2 <= tol * tol;
        }
        default:
          return false;
      }
    },

    // The point on segment `p`→`q` closest to `pt`. The parameter is clamped
    // to the segment, so the ends stop rather than extending into infinite
    // lines.
    // Half the drawn stroke width of a shaft kind, in image px.
    _shaftHalfWidthImagePx: function(shape) {
      var px = this._shaftStrokePx(shape, LINE_WEIGHT_PX);
      var scale = 1;
      try { scale = this._markerScale() || 1; } catch (_) { scale = 1; }
      return scale > 0 ? px / 2 / scale : px / 2;
    },

    // How far a dimension's V-arrowheads reach, in image px: `len` back
    // along the shaft from each end, `half` out to each side. Null for
    // anything that doesn't draw them.
    //
    // The numbers mirror the render's exactly — heads are drawn at 10 and 5
    // times `stroke / LINE_WEIGHT_PX`, which is how they keep their
    // proportion to a fattened shaft instead of staying hairlines on it. If
    // the render's ever change, these have to change with them, which is
    // what the test comparing the two is for.
    _dimHeadExtentImagePx: function(shape) {
      if (!shape) return null;
      var len, half;
      if (shape.kind === "dimension") {
        len = 10; half = 5;
      } else if (shape.kind === "arrow") {
        // An arrow's head is bigger than a dimension's and sits at ONE end,
        // but it is the same shape with the same problem: scaled off the
        // shaft, so on a heavy connector it reaches well past the line and
        // the most obvious thing to aim at was not a target.
        len = ARROW_HEAD_LEN; half = ARROW_HEAD_HALF_WIDTH;
      } else {
        return null;
      }
      var k = this._shaftStrokePx(shape, LINE_WEIGHT_PX) / LINE_WEIGHT_PX;
      var scale = 1;
      try { scale = this._markerScale() || 1; } catch (_) { scale = 1; }
      if (!(scale > 0)) scale = 1;
      return { len: len * k / scale, half: half * k / scale };
    },

    _nearestOnSegment: function(pt, p, q) {
      var dx = q.x - p.x, dy = q.y - p.y;
      var lenSq = dx * dx + dy * dy;
      // Degenerate segment (a bend dropped on top of its neighbour): the
      // segment IS the point.
      if (lenSq <= 0.0001) return { x: p.x, y: p.y };
      var t = ((pt.x - p.x) * dx + (pt.y - p.y) * dy) / lenSq;
      t = Math.max(0, Math.min(1, t));
      return { x: p.x + t * dx, y: p.y + t * dy };
    },

    // Is `pt` within `tol` image px of the segment `p`→`q`?
    _nearSegment: function(pt, p, q, tol) {
      var n = this._nearestOnSegment(pt, p, q);
      var ex = pt.x - n.x, ey = pt.y - n.y;
      return ex * ex + ey * ey <= tol * tol;
    },

    // Convert ~40 container px (a comfortable single-line text box at
    // 12px font) into image px at the current zoom. Falls back to a
    // safe constant if the viewport isn't initialized yet.
    _textDefaultBoxImagePx: function() {
      try {
        var perImagePx = this._markerScale();
        return 16 / perImagePx;
      } catch (e) {
        return 16;
      }
    },

    // The same unit for SIZES — how big a freshly clicked text box, a
    // callout's text box, a paste box or a label's first box comes out.
    // Rides _inkScale like every other new-ink size: by default a
    // constant 16 image px, so text minted at any zoom is the same
    // relative size (the lines got this treatment; the text default was
    // still zoom-baked through the function above). Screen-anchored only
    // with the ⋯ zoom toggle on. `_textDefaultBoxImagePx` (screen-
    // relative) remains for GESTURE thresholds and hit tolerances —
    // those are about fingers on glass, not about ink.
    // How big a text box minted by a TAP comes out, in image px.
    //
    // Ink by default, like every other new size: a constant number of image
    // px, so text minted at any zoom is the same size relative to the board
    // and a drawing does not end up with text at a dozen different scales.
    //
    // But ink alone is unreadable from far enough away. At 0.03 zoom — a
    // board seen whole — a 16 image px box is half a screen pixel tall,
    // which is "I just tap anywhere and it creates a super tiny text". So
    // the ink size is floored at what can be read on the glass. The larger
    // of the two wins, which is the ink size at 1:1 and anywhere zoomed in,
    // and a legible one on the way out.
    //
    // A box the user DREW is untouched by this: they said how big.
    _newTextBoxPx: function() {
      var ink = this._textDefaultBoxInkPx();
      var readable = ink;
      try {
        var scale = this._markerScale();
        if (scale > 0) readable = NEW_TEXT_MIN_SCREEN_PX / scale;
      } catch (_) {}
      return readable > ink ? readable : ink;
    },

    _textDefaultBoxInkPx: function() {
      try {
        return 16 / this._inkScale();
      } catch (e) {
        return 16;
      }
    },

    // Whether the draw gesture from `a` to `b` (image px) was a bare click.
    // Judged in SCREEN px so the answer doesn't change with zoom — the old
    // check compared image px, which at high zoom canceled deliberate small
    // drags and at low zoom accepted accidental jitter as a shape.
    // Is the far end too close to the anchor to be a shape? Measured on
    // SCREEN, like the click threshold, so it means the same thing at every
    // zoom — zoom in far enough and any two image points are miles apart.
    _isTooCloseToPlace: function(a, b) {
      var scale = 1;
      try { scale = this._markerScale() || 1; } catch (_) { scale = 1; }
      var dx = (b.x - a.x) * scale;
      var dy = (b.y - a.y) * scale;
      return dx * dx + dy * dy < MIN_SPAN_PX * MIN_SPAN_PX;
    },

    _isClickGesture: function(a, b) {
      var scale = 1;
      try { scale = this._markerScale() || 1; } catch (_) {}
      var dx = (b.x - a.x) * scale;
      var dy = (b.y - a.y) * scale;
      return dx * dx + dy * dy <
        CLICK_PLACE_THRESHOLD_PX * CLICK_PLACE_THRESHOLD_PX;
    },

    // The side of a click-placed shape in image px — `CLICK_PLACE_SIZE_PX`
    // on screen at the current zoom, same conversion the text default uses.
    _clickPlaceSizeImagePx: function() {
      try {
        var per = this._markerScale();
        if (per > 0) return CLICK_PLACE_SIZE_PX / per;
      } catch (_) {}
      return CLICK_PLACE_SIZE_PX;
    },

    // A stroke that is a dot: every point on top of every other, which is
    // what `_commitFreehand` stores for a press that never moved. Measured
    // against `DOT_LENGTH` — the hair of length a dot is given so the browser
    // has something to hit-test — rather than against a screen distance, so
    // the answer is the same at every zoom. A stroke small enough to be drawn
    // by hand is thousands of times longer than this.
    // One device pixel, expressed in the CSS px the path is written in. On a
    // phone at 3x that is a third of a CSS px — small enough that what you see
    // is the cap, which is the ink, which scales with the zoom.
    _dotRenderLength: function() {
      var dpr = 1;
      try { dpr = window.devicePixelRatio || 1; } catch (_) {}
      return DOT_RENDER_DEVICE_PX / (dpr > 0 ? dpr : 1);
    },

    _strokeIsDot: function(geometry) {
      var pts = geometry && geometry.points;
      if (!pts || !pts.length) return false;
      var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (var i = 0; i < pts.length; i++) {
        if (pts[i][0] < minX) minX = pts[i][0];
        if (pts[i][0] > maxX) maxX = pts[i][0];
        if (pts[i][1] < minY) minY = pts[i][1];
        if (pts[i][1] > maxY) maxY = pts[i][1];
      }
      return (maxX - minX) <= DOT_LENGTH * 4 && (maxY - minY) <= DOT_LENGTH * 4;
    },

    // A stroke whose every sample fits inside the click threshold is a tap,
    // not a drawing. The point count can't tell (a click with a pixel of
    // jitter yields several samples), and committing it placed a speck that
    // is nearly impossible to see, select or erase. The closed shapes turn
    // a tap into a default-sized shape instead; a pen stroke has no default
    // worth inventing, so it just doesn't happen.
    _strokeIsClick: function(pts) {
      var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (var i = 0; i < pts.length; i++) {
        if (pts[i][0] < minX) minX = pts[i][0];
        if (pts[i][0] > maxX) maxX = pts[i][0];
        if (pts[i][1] < minY) minY = pts[i][1];
        if (pts[i][1] > maxY) maxY = pts[i][1];
      }
      return this._isClickGesture({ x: minX, y: minY }, { x: maxX, y: maxY });
    },

    // Resolve a callout's text bbox in IMAGE coords. New callouts ship
    // `geometry.text_box = {x, y, w, h}`; legacy rows (pre-v118) carry
    // `geometry.text_at = [x, y]` instead — derive a sensible default
    // bbox at that point so they render without a migration.
    _calloutTextBoxImage: function(geometry) {
      if (geometry && geometry.text_box) return geometry.text_box;
      var basePx = this._textDefaultBoxInkPx();
      var w = basePx * 6;
      var h = basePx * 1.4;
      var p = (geometry && geometry.text_at) || [0, 0];
      // Center the bbox slightly above the legacy text-baseline point
      // so it looks roughly where the old single-line label sat.
      return { x: p[0], y: p[1] - h * 0.85, w: w, h: h };
    },

    // Pick the midpoint of the rect edge closest to a point (all in
    // container coords). Used to anchor a callout's leader line on
    // the side of the text bbox facing the anchor.
    _nearestRectEdgeMidpoint: function(rect, point) {
      var cx = rect.x + rect.w / 2;
      var cy = rect.y + rect.h / 2;
      var dx = point.x - cx;
      var dy = point.y - cy;
      // Compare absolute deltas scaled by the rect's aspect ratio so
      // wide bboxes prefer top/bottom edges and tall ones prefer
      // left/right — keeps the line short and natural-looking.
      var ax = Math.abs(dx) / (rect.w || 1);
      var ay = Math.abs(dy) / (rect.h || 1);
      if (ax > ay) {
        // left or right edge
        return dx >= 0
          ? { x: rect.x + rect.w, y: cy }
          : { x: rect.x,          y: cy };
      } else {
        // top or bottom edge
        return dy >= 0
          ? { x: cx, y: rect.y + rect.h }
          : { x: cx, y: rect.y          };
      }
    },

    // -------------------------------------------------------------------------
    // Inline text editor — a <foreignObject> overlay with an <input>
    // positioned exactly over the text shape's bbox. Pressing Enter (or
    // clicking outside) commits the title via `etcher:updated`; Esc
    // cancels and, if the shape has no title yet, deletes it (a freshly
    // drawn text shape with no content is just noise).
    // -------------------------------------------------------------------------

    // Kinds whose text IS the shape — deleting the text deletes them.
    // Every other kind merely carries a label in `metadata.title`. The
    // dimension used to be in this class; now that it draws unlabelled
    // and takes a label by double-click like any shaft, its text is a
    // LABEL — deleting it (or committing it empty) leaves the line.
    _isTextKind: function(kind) {
      return kind === "text" || kind === "callout";
    },

    // WHERE the inline editor sits, in image px — the box the label will
    // occupy. Factored out of _startTextEdit so the open editor can be
    // re-anchored on every pan/zoom frame (see _repositionTextEditor):
    // computed once at open, the box stayed put in container px while the
    // board moved under it, so the text being typed slid off its line and
    // snapped back on commit.
    _textEditBoxImage: function(shape) {
      if (!shape) return null;
      var g;
      if (shape.kind === "text") {
        g = shape.geometry;
      } else if (shape.kind === "callout") {
        g = this._calloutTextBoxImage(shape.geometry);
      } else if (this._labelRidesShaft(shape.kind)) {
        // Small box centered on the label's lerp position along the
        // shaft so the inline editor pops up exactly where the text
        // will land. Sized off `_textDefaultBoxImagePx` so it scales
        // with the current zoom. The arrow takes this branch too — its
        // label is a metadata title, but it LANDS on the shaft, so the
        // editor has to open there or the typed text jumps on commit.
        var dimT = (shape.metadata && typeof shape.metadata.title_offset === "number")
          ? shape.metadata.title_offset : 0.5;
        // The routed shaft, not the chord: the editor must open where the
        // label actually sits, bends included.
        var dimP = this._shaftPointAt(shape.geometry, dimT) ||
          { x: shape.geometry.a[0], y: shape.geometry.a[1] };
        var lblX = dimP.x;
        var lblY = dimP.y;
        var basePx = this._textDefaultBoxInkPx();
        var dlw = basePx * 6;
        var dlh = basePx * 1.4;
        g = { x: lblX - dlw / 2, y: lblY - dlh / 2, w: dlw, h: dlh };
      } else {
        g = this._shapeTitleBoxImage(shape, this._lastBboxTopImageFor(shape));
        // A shape with no label yet gets its label CENTERED on creation
        // (`_commitTextEdit` stamps center/middle), so the editor opens
        // where the label will land — double-clicking a rectangle used to
        // pop the input ABOVE it and the typed text then jumped into the
        // middle on commit. Only for label-less shapes: an existing label
        // keeps its editor wherever the label actually is (the aligned /
        // stored box `_shapeTitleBoxImage` already resolves), and
        // host-supplied labels keep their float-above default.
        var hasTitle =
          shape.metadata && String(shape.metadata.title || "").trim() !== "";
        if (!hasTitle && g) {
          var editBBox = this._shapeBBoxImagePx(shape);
          if (editBBox) {
            g = alignedBox(editBBox, { w: g.w, h: g.h },
              { h: "center", v: "middle" });
          }
        }
      }
      return g || null;
    },

    _startTextEdit: function(shape) {
      if (!shape) return;
      // text + callout + dimension edit their own bbox; rect/circle/
      // poly/freehand edit a title that lives in `metadata.title_box`
      // on the parent. A shape with no title yet is editable too — that
      // is how one gets added; the `if (!g) return` below drops the kinds
      // that have nowhere to place a title box.
      this._endTextEdit();

      // A BRAND-NEW label pins the default size the moment its editor
      // opens — before, an unpinned new label derived its editor font
      // from the default title box at the current zoom and its committed
      // font from a second, different box measurement: random-feeling
      // while typing, then visibly smaller at placement. Stamped here
      // (not at shape creation) so it also covers shapes that predate
      // the default; same ink-scaled unit a panel pick stores. Text and
      // callout keep their own sizing — their text IS the shape, and a
      // drag-drawn text box already pins from the drawn height.
      if (!this._isTextKind(shape.kind) &&
          !(shape.metadata && shape.metadata.title) &&
          !this._hasPinnedFontSize(shape)) {
        shape.style = Object.assign({}, shape.style || {}, {
          font_size: this._defaultLabelFontSize() / this._inkScale()
        });
      }

      var self = this;
      // Point text is typed into in place: older text is converted first,
      // so the editor below only ever meets the one model.
      this._ensurePointText(shape);
      var point = this._typesAsPoint(shape);
      // A label typed onto a shape that has none starts centred in it, as
      // commit used to put it — stamped now, because a point label shows
      // from the first keystroke and should grow from where it will stay.
      if (point && shape.kind !== "callout" && !this._isTextKind(shape.kind) &&
          !(shape.metadata && String(shape.metadata.title || "").trim()) &&
          !this._labelRidesShaft(shape.kind) &&
          !normalizeTitleAlign(shape.metadata && shape.metadata.title_align)) {
        shape.metadata = Object.assign({}, shape.metadata || {},
          { title_align: { h: "center", v: "middle" } });
      }
      var g = this._textEditBoxImage(shape);
      if (!g) return;
      var tl = this._imageToContainer({ x: g.x, y: g.y });
      var br = this._imageToContainer({ x: g.x + g.w, y: g.y + g.h });
      var w = Math.max(20, Math.abs(br.x - tl.x));
      var h = Math.max(16, Math.abs(br.y - tl.y));

      var fo = svgEl("foreignObject", {
        x: Math.min(tl.x, br.x),
        y: Math.min(tl.y, br.y),
        width: w,
        height: h
      });
      fo.classList.add("etcher-text-editor");
      // A textarea, not an input: Shift+Enter puts a real newline in the
      // label (Enter alone still commits), and multi-line is exactly the
      // lines the author broke — never lines a wrap invented.
      var input = document.createElement("textarea");
      input.rows = 1;
      input.wrap = "off";
      input.maxLength = 200;
      input.placeholder = "Type your label…";
      input.value = (shape.metadata && shape.metadata.title) || "";
      input.style.width = "100%";
      input.style.height = "100%";
      input.style.boxSizing = "border-box";
      input.style.resize = "none";
      input.style.overflow = "hidden";
      input.style.whiteSpace = "pre";
      input.style.lineHeight = "1.1";
      // A text BOX wraps while you type, exactly as the committed render
      // will — the single-line editor showed one endless line that only
      // became the wrapped box on Enter, so the editor was not the thing
      // being made. The walls do the wrapping; Enter still commits and
      // Shift+Enter still breaks a line by hand.
      var fixedEd = this._textboxFixed(shape);
      // Whether its frame and handles work while it is typed into — point
      // text's always have; a text box's work the same way, so the two
      // tools differ only in the box (wrapping inside it, or none).
      var live = point || fixedEd;
      if (fixedEd) {
        input.wrap = "soft";
        input.style.whiteSpace = "pre-wrap";
        input.style.wordBreak = "break-word";
      }
      // Point text: the words on screen while typing are the text itself,
      // rendered live from the draft on every keystroke (see
      // `_renderPointText`). The textarea lies exactly over them, wearing
      // their font, padding and stretch, with its own glyphs invisible — it
      // is only there for the caret, the selection and the keyboard. So
      // nothing can differ at commit: there is nothing to swap.
      if (point) {
        input.classList.add("etcher-point-editor");
        input.maxLength = 5000;
        input.placeholder = "";
        fo.style.overflow = "visible";
      }

      // The editor IS the label. It used to be a white box with a dashed
      // border and 14px black text — nothing like what commit would draw,
      // so every edit was typed blind into a form control and the real
      // look only appeared on Enter. Instead the input wears exactly what
      // the rendered label wears — its font at its rendered size, its own
      // ink, its plate (or nothing) behind it — so what is on screen while
      // typing is what will be on screen after.
      //
      // The font size comes off the RENDERED <text> when there is one:
      // that number already went through the box-drives-font rule, the
      // pinned-size override and the width-fit cap, and re-deriving it
      // here would just be a second copy of that pipeline to keep honest.
      // A label-less shape has no text yet, so its editor uses the same
      // base rule the first render will (box height × 0.65, floored).
      var edHost = this._textEditHost(shape);
      var edTextEl = edHost && edHost.querySelector &&
        edHost.querySelector("text");
      var edFontSize = edTextEl && parseFloat(edTextEl.getAttribute("font-size"));
      if (!(edFontSize > 0)) {
        edFontSize = this._fontSizeFor(shape, Math.max(this._zoomPx(10), h * 0.65));
      }
      edFontSize = this._editorFontPx(edFontSize);
      // Readability is the label's own problem now, which is the point: a
      // colour that vanishes over the photo vanishes while typing too, and
      // the plate toggle is right there. Pinning black here was the old
      // trade — legible editing, blind editing.
      var edColor = this._titleColorFor(shape) || "#000";
      var edBg = this._labelBgFor ? this._labelBgFor(shape) : null;
      input.style.border = "none";
      input.style.background = edBg || "transparent";
      if (edBg) {
        input.style.borderRadius =
          Math.round(LABEL_BG_RADIUS_RATIO * Math.min(w, h)) + "px";
      }
      input.style.color = point ? "transparent" : edColor;
      input.style.caretColor = edColor;
      if (point) input.style.background = "transparent";
      // A text box's plate is its frame, shaped corners and all, and it
      // stays drawn while typing — a rounded textarea over it would hide it.
      if (fixedEd) {
        input.style.background = "transparent";
        input.style.borderRadius = "";
      }
      // The render's own padding rule: a fraction of the box, or of the
      // font once a size is pinned (0.13 / 0.65 = 0.2 — same proportion).
      var edPad = this._hasPinnedFontSize(shape)
        ? edFontSize * 0.2
        : h * 0.13;
      // …and a text box's corners push it in as the render does.
      if (fixedEd) edPad = this._textboxPadPx(shape, edPad, w, h);
      // Padding on all sides now — the textarea top-aligns its text, so
      // the vertical pad is what centres a single line in the box.
      input.style.padding = Math.round(edPad) + "px";
      var edFamily = "ui-sans-serif, system-ui, -apple-system, sans-serif";
      input.style.font = "500 " + edFontSize + "px " + edFamily;
      // The `font` shorthand just reset the line height to "normal"; a text
      // box's lines step 1.1em in the render, so its editor's do too.
      if (fixedEd) input.style.lineHeight = "1.1";
      input.style.outline = "none";
      // Typing grows from where the committed text will sit: centred for a
      // shaft-rider, a fresh label (commit centres those in the shape) or
      // an aligned one. Left for a label whose stored box anchors it — and
      // for text and callout shapes, whose committed render draws from the
      // left edge of their own box: centring those made the words jump
      // left the moment Enter landed.
      var edHasTitle =
        shape.metadata && String(shape.metadata.title || "").trim() !== "";
      var edOwnBox = shape.kind === "text" || shape.kind === "callout";
      input.style.textAlign =
        this._labelRidesShaft(shape.kind) ||
        (!edOwnBox &&
          (!edHasTitle ||
            normalizeTitleAlign(shape.metadata && shape.metadata.title_align)))
          ? "center"
          : "left";

      // The box hugs the text, live — the render's own layout replayed on
      // every keystroke, so the editor is not "a field the label goes
      // into" but the label itself with a caret in it. No caps anywhere:
      // a longer label gets a WIDER box at exactly the font the label
      // renders in (never a smaller font — the size the user chose is the
      // size they get), and a Shift+Enter line makes it taller by exactly
      // one line. The anchor holds while the box breathes: centre-anchored
      // text resizes about its centre, left-anchored about its left edge.
      var selfEd = this;
      var edCx = (Math.min(tl.x, br.x) + Math.max(tl.x, br.x)) / 2;
      var edCy = (Math.min(tl.y, br.y) + Math.max(tl.y, br.y)) / 2;
      var edLeft = Math.min(tl.x, br.x);
      var edFit = function() {
        // Point text: lie over the rendered text, exactly. Its layout is
        // in container px; the textarea types at the editor font (floored
        // on touch screens, see `_editorFontPx`) and is scaled back to the
        // text's real size, then stretched as the text is.
        if (point) {
          var L = shape._pointLayout;
          if (!L) return;
          var edPx = selfEd._editorFontPx(L.fontPx);
          var k = L.fontPx / edPx;
          // The editor stays inside the dashed outline's grip band, so
          // nothing it does can sit over the grip: Safari draws a scaled
          // element in a foreignObject above the SVG after it, z-order or
          // not. The inset comes out of the padding, leaving the words
          // where the render has them; a pixel of padding is kept so a
          // caret at the end of the widest line never scrolls the field.
          var ix = Math.max(0, Math.min(POINT_FRAME_IN_PX, L.pad * L.sx - 1)) / L.sx;
          var iy = Math.max(0, Math.min(POINT_FRAME_IN_PX, L.pad * L.sy - 1)) / L.sy;
          var roomW = L.natW - 2 * ix, roomH = L.natH - 2 * iy;
          fo.setAttribute("x", L.tx + ix * L.sx);
          fo.setAttribute("y", L.ty + iy * L.sy);
          fo.setAttribute("width", roomW * L.sx);
          fo.setAttribute("height", roomH * L.sy);
          if (L.turn) fo.setAttribute("transform", L.turn);
          else fo.removeAttribute("transform");
          input.style.font = "500 " + edPx + "px " + edFamily;
          input.style.padding = ((L.pad - iy) / k) + "px " + ((L.pad - ix) / k) + "px";
          // The textarea puts its first baseline half a leading plus the
          // font's ascent down; the render puts it at the letters' own top.
          // The top pad makes up the difference so the caret sits on them.
          if (L.met) {
            var edBase = 0.55 + (L.met.fontAsc - L.met.fontDesc) / 2;
            input.style.paddingTop = Math.max(0, (L.pad - iy) / k + edPx * (L.met.asc - edBase)) + "px";
          }
          input.style.width = (roomW / k) + "px";
          input.style.height = (roomH / k) + "px";
          input.style.transformOrigin = "0 0";
          input.style.transform = "scale(" + (L.sx * k) + ", " + (L.sy * k) + ")";
          input.scrollLeft = 0;
          input.scrollTop = 0;
          selfEd._fitPointGrip(shape);
          return;
        }
        // A text BOX neither grows with the text nor recentres: the
        // editor IS the box, recomputed from the shape each fit so a
        // pan or zoom mid-edit moves it with the board. The textarea
        // wraps inside it (see its pre-wrap setup above).
        if (fixedEd) {
          var gF = selfEd._textEditBoxImage(shape);
          if (!gF) return;
          var tlF = selfEd._imageToContainer({ x: gF.x, y: gF.y });
          var brF = selfEd._imageToContainer({ x: gF.x + gF.w, y: gF.y + gF.h });
          fo.setAttribute("x", Math.min(tlF.x, brF.x));
          fo.setAttribute("y", Math.min(tlF.y, brF.y));
          fo.setAttribute("width", Math.max(20, Math.abs(brF.x - tlF.x)));
          fo.setAttribute("height", Math.max(16, Math.abs(brF.y - tlF.y)));
          // Typed where the text will sit: across by the alignment, down by
          // padding the room left under the lines.
          var alF = selfEd._textboxAlign(shape);
          input.style.textAlign = alF.h;
          input.style.paddingTop = edPad + "px";
          if (alF.v !== "top") {
            var boxHF = Math.max(16, Math.abs(brF.y - tlF.y));
            // The lines' own height: read with the field collapsed, since a
            // field taller than its text reports its own height instead.
            input.style.height = "0px";
            var contentF = input.scrollHeight - edPad * 2;
            input.style.height = "100%";
            // The render lays its lines out the way the field does (1.1em
            // line boxes), so the room is the same and the words do not
            // jump at Enter.
            var roomF = Math.max(0, boxHF - edPad * 2 - contentF);
            input.style.paddingTop = (edPad + (alF.v === "middle" ? roomF / 2 : roomF)) + "px";
          }
          // A turned box types turned: the editor wears the box's turn.
          var turnF = shape.el && shape.el.getAttribute("transform");
          if (turnF) fo.setAttribute("transform", turnF);
          else fo.removeAttribute("transform");
          return;
        }
        var linesEd = String(input.value || "").split("\n");
        var maxW = 0;
        for (var li = 0; li < linesEd.length; li++) {
          var lw = linesEd[li]
            ? selfEd._measureTextWidth(linesEd[li], edFontSize, edFamily, "500")
            : 0;
          if (lw > maxW) maxW = lw;
        }
        // Floor of ~2 characters so an empty label still shows a caret
        // box worth aiming at. Height allots the full 1.1em line box per
        // line PLUS descender headroom: the first cut of this maths gave
        // each line exactly 1.0em, and with overflow:hidden the textarea
        // clipped the bottom of every glyph while typing — the committed
        // SVG text (which nothing clips) then popped in whole, so the
        // editor looked "crooked" precisely until Enter.
        var boxW = Math.max(edFontSize * 2, maxW + edPad * 2);
        var boxH = edFontSize * (1.1 * linesEd.length + 0.2) + edPad * 2;
        fo.setAttribute("width", boxW);
        fo.setAttribute("height", boxH);
        fo.setAttribute("y", edCy - boxH / 2);
        fo.setAttribute("x", input.style.textAlign === "center"
          ? edCx - boxW / 2
          : edLeft);
        // A shaft-riding label breaks its line — and it must break WHILE
        // TYPING, sized to the editor as it grows. The text is the line's
        // own colour now, so an unbroken shaft runs straight through the
        // words (same colour on same colour, unreadable with the plate
        // off) — and the break popping in only on commit read as the line
        // being deleted out from under the text at placement. The mask
        // tracks the editor's box per keystroke; the commit render then
        // re-syncs it to the label's real rect, or closes the line back
        // up if nothing was typed.
        if (selfEd._labelRidesShaft(shape.kind)) {
          // The box the COMMITTED label will have — same formulas the
          // render's shrink-wrap uses — so the break is identical while
          // typing and after Enter. The editor's own box is taller by the
          // headroom its textarea needs, and cutting to that made the gap
          // settle visibly at commit.
          var gapW = Math.max(maxW + edPad * 2, edFontSize);
          var gapH = Math.max(
            edFontSize + (linesEd.length - 1) * edFontSize * 1.1 + edPad * 2,
            edFontSize * 1.2
          );
          selfEd._syncShaftLabelGap(shape, {
            x: edCx - gapW / 2, y: edCy - gapH / 2, w: gapW, h: gapH
          });
        }
      };
      input.addEventListener("input", function() {
        if (point) {
          shape._draftTitle = input.value;
          // Renders the text and re-fits this editor over it.
          selfEd._renderKeepingCorner(shape);
          selfEd._positionPointHandles(shape);
          return;
        }
        edFit();
      });
      if (point) {
        shape._draftTitle = input.value;
        this._renderShape(shape);
        var typingHost = this._textEditHost(shape);
        if (typingHost) typingHost.classList.add("is-typing");
        // A label being typed is the label being worked on: its own frame
        // and handles come up, usable while typing, as point text's are.
        if (!this._isTextKind(shape.kind) && shape.titleGroup &&
            this.editingTitleShape !== shape) {
          this._enterTitleEditMode(shape);
        }
      }
      edFit();

      fo.appendChild(input);
      // Under the handles, which are what a press on the box's edge is for.
      var firstHandle = live && this.svg.querySelector(":scope > .etcher-grab, :scope > .etcher-handle");
      if (firstHandle) this.svg.insertBefore(fo, firstHandle);
      else this.svg.appendChild(fo);
      // A text box's fit measures its lines, which needs the field on the
      // page — the first fit above ran before it was.
      if (fixedEd) edFit();
      // The dashed outline's grip: an invisible band over it, on top of the
      // editor and under the handles, wearing the move cursor. A real
      // element rather than a cursor swapped on hover, because a browser
      // (Safari above all) repaints the cursor when the pointer crosses
      // into another element, not when a style changes under it.
      var grip = null;
      if (live) {
        grip = svgEl("path", {});
        grip.classList.add("etcher-text-grip");
        this.svg.insertBefore(grip, fo.nextSibling);
      }
      this._textEditor = {
        grip: grip,
        fo: fo,
        input: input,
        shape: shape,
        // For _refreshTextEditorStyle: re-fit after a panel edit, and
        // swap the closure's font size so the fit measures at the new one.
        fit: edFit,
        point: point,
        fixed: fixedEd,
        setFontSize: function(s) { edFontSize = s; },
        // For _repositionTextEditor: the board moves under an open editor
        // on every pan/zoom frame, so its anchor and its metrics have to
        // be replaceable, not frozen at open time.
        setAnchor: function(cx, cy, left) { edCx = cx; edCy = cy; edLeft = left; },
        setPad: function(p) { edPad = p; }
      };
      if (grip) this._fitPointGrip(shape);
      // Hide the visible <text> while editing — the input shows the
      // current content live, and overlapping them blurs the readout.
      // For title edits, the visible text lives on the title group
      // (not the parent shape's element).
      var hostEl = this._textEditHost(shape);
      var existing = !point && hostEl && hostEl.querySelector(".etcher-text-content");
      if (existing) existing.setAttribute("visibility", "hidden");
      // The rect too — the input wears the plate and the box now, and the
      // committed rect frozen at its old extent behind a live-resizing
      // editor reads as two labels fighting.
      var exRect = !point && hostEl && hostEl.querySelector(".etcher-text-rect");
      if (exRect) exRect.setAttribute("visibility", "hidden");

      input.addEventListener("keydown", function(e) {
        if (e.key === "Enter") {
          // Shift+Enter is the author's line break; plain Enter commits, as
          // it always has. Point text puts the break in itself rather than
          // trusting the textarea's default, which a host's key handling
          // or an IME can swallow.
          if (e.shiftKey) {
            if (!point) return;
            e.preventDefault();
            var a0 = input.selectionStart, b0 = input.selectionEnd;
            input.setRangeText("\n", a0, b0, "end");
            input.dispatchEvent(new Event("input", { bubbles: true }));
            return;
          }
          e.preventDefault();
          self._commitTextEdit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          self._cancelTextEdit();
        }
      });
      // Blur path (click outside the input but inside the overlay,
      // viewport edge, etc.) commits as well — matches the muscle
      // memory from inline text editors elsewhere.
      input.addEventListener("blur", function() {
        // Defer so synchronous Enter/Esc handling above wins.
        setTimeout(function() {
          if (self._textEditor && self._textEditor.input === input) {
            // A handle of this text is being pulled: still editing.
            if (self._textEditor.resizing) return;
            // Focus moved into the panel (a number input, a slider): the
            // user is styling the text they are about to type, not done
            // with it. Same exclusion as the pointerdown path above.
            var a = document.activeElement;
            if (a && a.closest && a.closest(CHROME_SELECTOR)) return;
            // A panel control that takes the press without taking focus
            // (a button that preventDefaults) leaves focus on <body> —
            // the same trip to the menus, so the box waits here too. A
            // real click-away is committed by the pointerdown path
            // below, which knows where the press landed; this one is
            // only a backstop for focus leaving the page.
            if (self._textEditor.chromeSinceFocus && (!a || a === document.body)) return;
            self._commitTextEdit();
          }
        }, 0);
      });
      // Blur alone cannot be trusted for click-away: the canvas's pointer
      // handlers preventDefault, which suppresses the focus change, so a
      // click on the board never blurred the input — the typed label sat
      // uncommitted and could be discarded by a later teardown. A capture-
      // phase pointerdown commits it first, and the click then goes on to
      // do whatever it was for (deselect, draw, pick another shape).
      self._textEditOutsideDown = function(e) {
        if (!self._textEditor || self._textEditor.input !== input) return;
        // The dashed outline of point text being typed into is a grip: a
        // press on it moves the text, and the caret comes back after. Ahead
        // of the editor check, since the editor covers the box's inside.
        if (live && self._pointFrameUp(shape) && self._onPointTextFrame(shape, e) &&
            !(e.target.closest && e.target.closest(".etcher-handle, .etcher-grab"))) {
          e.preventDefault();
          e.stopPropagation();
          self._movePointTextWhileTyping(shape, e);
          return;
        }
        if (e.target === input || (fo.contains && fo.contains(e.target))) {
          // A press ON the editor is about the words, not the board. The
          // canvas hears it too (the editor sits inside the overlay), and
          // with a tool still armed it read a click on a dimension's own
          // label as "start a dimension here": the caret landed AND a new
          // shape was born from the middle of the label. Hold this press
          // off the tools entirely — unlike the commit case below, not
          // even a drag out of the box should draw, or selecting the
          // label's own text would start one.
          self._pressInsideTextEditor = true;
          setTimeout(function() { self._pressInsideTextEditor = false; }, 0);
          // Back in the box: the next canvas press is an ordinary
          // click-away again, not the return trip from the panel.
          self._textEditor.chromeSinceFocus = false;
          return;
        }
        // One of this text's own handles: scaling or stretching it is part
        // of making it, Affinity-style — the editor stays open through the
        // drag and the caret comes back after it.
        if (live && self._pointFrameUp(shape) && e.target.closest &&
            e.target.closest(".etcher-handle, .etcher-grab")) {
          var edR = self._textEditor;
          edR.resizing = true;
          // …where it was: a focus alone lands it at the start in Firefox.
          var caretR = [input.selectionStart, input.selectionEnd];
          var back = function() {
            document.removeEventListener("pointerup", back, true);
            document.removeEventListener("pointercancel", back, true);
            setTimeout(function() {
              if (self._textEditor !== edR) return;
              edR.resizing = false;
              try {
                input.focus({ preventScroll: true });
                input.setSelectionRange(caretR[0], caretR[1]);
              } catch (_) {}
            }, 0);
          };
          document.addEventListener("pointerup", back, true);
          document.addEventListener("pointercancel", back, true);
          return;
        }
        // Etcher's own chrome — the style panel, its popups, the toolbar —
        // does not close the editor. Reaching for a size or a colour is
        // the most natural thing to do with a fresh text box open, and
        // committing here threw the (empty) box away mid-thought. The
        // editor stays; the panel edit lands on it live; typing or a
        // click on the canvas commits as before.
        if (e.target.closest && e.target.closest(CHROME_SELECTOR)) {
          // Remember the detour. The caret is in the panel now, so the
          // next press on the canvas is the user coming BACK to the box
          // (see below) rather than walking away from it.
          self._textEditor.chromeSinceFocus = true;
          return;
        }
        // The first canvas press after a trip to the panel means "done
        // with the menus, back to typing": it puts the caret back where
        // it was instead of throwing the freshly-dressed element away.
        // Scoped three ways so nothing else changes meaning: only while
        // empty (text present -> click-away commits, as ever), only
        // straight after the chrome was touched (a box nobody has been
        // away from still dismisses on click-away — the change-of-mind
        // gesture), and only on EMPTY canvas (a press on another shape is
        // about that shape). One trip back per detour: the flag is spent
        // here, so the NEXT press away from the box dismisses it — the
        // only way to abandon a label, and unreachable for a styled box
        // under the old flag, which was never cleared.
        var ed = self._textEditor;
        if (ed && ed.chromeSinceFocus && !(input.value || "").trim()) {
          var onShape = null;
          try { onShape = self._shapeAt(self._toImage(e)); } catch (_) {}
          if (!onShape || onShape === ed.shape) {
            ed.chromeSinceFocus = false;
            e.preventDefault();
            e.stopPropagation();
            setTimeout(function() { try { input.focus(); } catch (_) {} }, 0);
            return;
          }
        }
        // This press is closing the editor; whatever tool is armed must
        // not also treat it as "draw one here" (see `_onPointerDown`).
        self._drawSuppressedUntilDrag = true;
        // And under the cursor it is not also a click away from the shape:
        // finishing the words leaves the shape selected (see
        // `_keepOrDropSelection`); the NEXT press deselects.
        self._pressCommittedText = true;
        // Belt and braces: if this press never reaches `_onPointerDown`
        // at all (it landed somewhere that does not draw), the flag still
        // dies with the gesture rather than eating the next press.
        setTimeout(function() {
          self._drawSuppressedUntilDrag = false;
          self._pressCommittedText = false;
        }, 0);
        self._commitTextEdit();
      };
      document.addEventListener("pointerdown", self._textEditOutsideDown, true);
      // Focused twice, and both of them matter.
      //
      // Now, in the same task as the gesture that asked for it: WebKit opens
      // the on-screen keyboard only for a focus that happens inside a user
      // gesture, and a `setTimeout` of nought is already outside it. A phone
      // was left looking at an empty box it could not type into — the editor
      // was open, the caret was elsewhere — and the way through was to tap
      // the box a second time, which is a gesture of its own. Reported as
      // "it just puts the placeholder there, enough to tap it again to start
      // typing".
      //
      // And again on the next tick, which is what this always did: by then
      // the foreignObject is attached, and a browser that would not take the
      // first focus takes this one. Harmless where the first worked — the
      // caret is already here, and nothing can have been typed in between.
      //
      // Point text opens with a caret at the start of the text, not the text
      // selected: it reads as "you are in the text now", and a keystroke
      // adds to it instead of wiping it. Other labels keep select-all.
      // A text box opens the same way: caret at the start, arrows or a
      // click to move it, nothing selected to be typed over.
      //
      // Opened by a double-click on the words, the caret goes where the
      // double-click landed, as in any text editor.
      var dbl = self._lastDblClick;
      var dblFresh = dbl && Date.now() - dbl.t < 800;
      var placeCaret = function() {
        input.focus();
        if (point || fixedEd) {
          var at = dblFresh ? self._caretOffsetAt(input, dbl.x, dbl.y) : null;
          at = at == null ? 0 : at;
          input.setSelectionRange(at, at);
        } else input.select();
      };
      try { placeCaret(); } catch (_) {}
      setTimeout(function() { try { placeCaret(); } catch (_) {} }, 0);

      // The editor's shape just became the panel's target (see
      // _fontTargetShapes) — sync now, or the label rows only appear
      // after some unrelated interaction happens to refresh them.
      this._syncStyleInspector();
    },

    // Re-dress the OPEN inline editor after a panel edit — size, ink,
    // plate — in place, without recreating it, so the caret and the text
    // typed so far survive. The setters that can restyle a text shape all
    // call this; with no editor open it is a no-op.
    // Keep an open editor glued to its shape through pan and zoom. The box
    // is positioned in CONTAINER px, and the board moves underneath it on
    // every frame — so without this the text being typed slides off its
    // line (visibly left and right as the zoom changes) and only snaps
    // into place on commit. Same job `_renderAll` already does for
    // handles, connector dots and the tooltip.
    _repositionTextEditor: function() {
      var ed = this._textEditor;
      if (ed && ed.point) { ed.fit(); return; }
      if (!ed || !ed.shape || !ed.fo || !ed.setAnchor) return;
      var g = this._textEditBoxImage(ed.shape);
      if (!g) return;
      var tl = this._imageToContainer({ x: g.x, y: g.y });
      var br = this._imageToContainer({ x: g.x + g.w, y: g.y + g.h });
      var left = Math.min(tl.x, br.x);
      var top = Math.min(tl.y, br.y);
      var boxH = Math.max(16, Math.abs(br.y - tl.y));
      ed.setAnchor(
        (left + Math.max(tl.x, br.x)) / 2,
        (top + Math.max(tl.y, br.y)) / 2,
        left
      );
      // The label's size tracks the zoom (a pinned size is stored in canvas
      // units and multiplied back by the scale), so the text being typed
      // has to track it too — and the fit measures at that size, so both
      // move before the fit runs. Read from the rendered <text> when there
      // is one, exactly as _refreshTextEditorStyle does; that flag it sets
      // is deliberately NOT touched here — a zoom is not the user styling
      // the label.
      var host = this._textEditHost(ed.shape);
      var tEl = host && host.querySelector && host.querySelector("text");
      var size = tEl && parseFloat(tEl.getAttribute("font-size"));
      if (!(size > 0)) {
        size = this._fontSizeFor(
          ed.shape, parseFloat(ed.input.style.fontSize) || 14
        );
      }
      size = this._editorFontPx(size);
      if (size > 0 && ed.setFontSize) {
        ed.setFontSize(size);
        ed.input.style.fontSize = size + "px";
        var pad = this._hasPinnedFontSize(ed.shape) ? size * 0.2 : boxH * 0.13;
        if (this._textboxFixed(ed.shape)) {
          pad = this._textboxPadPx(ed.shape, pad, Math.abs(br.x - tl.x), boxH);
        }
        if (ed.setPad) ed.setPad(pad);
        ed.input.style.padding = Math.round(pad) + "px";
      }
      ed.fit();
    },

    _refreshTextEditorStyle: function() {
      var ed = this._textEditor;
      if (!ed || !ed.shape || !ed.input) return;
      var shape = ed.shape;
      var host = this._textEditHost(shape);
      var tEl = host && host.querySelector && host.querySelector("text");
      var size = tEl && parseFloat(tEl.getAttribute("font-size"));
      if (!(size > 0)) {
        size = this._fontSizeFor(
          shape, parseFloat(ed.input.style.fontSize) || 14
        );
      }
      // A panel edit landing on the open editor is a trip to the menus,
      // the same as the press that carried the user there — the outside-
      // click handler owes an empty box one click back to its caret
      // rather than discarding it (someone who just picked its colour
      // has not changed their mind about wanting it). Set here as well
      // as on the press because a control can be reached by keyboard.
      ed.chromeSinceFocus = true;
      if (ed.point) {
        // The text re-renders in its new dress and the editor re-fits over
        // it; only the caret's colour is the editor's own.
        ed.input.style.caretColor = this._titleColorFor(shape) || "#000";
        this._renderShape(shape);
        this._positionPointHandles(shape);
        return;
      }
      size = this._editorFontPx(size);
      if (ed.setFontSize) ed.setFontSize(size);
      ed.input.style.fontSize = size + "px";
      var color = this._titleColorFor(shape) || "#000";
      ed.input.style.color = color;
      ed.input.style.caretColor = color;
      var bg = this._labelBgFor ? this._labelBgFor(shape) : null;
      ed.input.style.background = (bg && !this._textboxFixed(shape)) ? bg : "transparent";
      if (this._textboxFixed(shape)) {
        // The frame re-renders in its new dress (plate, corners) under the
        // editor, and the padding follows the corners.
        this._renderShape(shape);
        var tbH = parseFloat(ed.fo.getAttribute("height")) || 0;
        var tbW = parseFloat(ed.fo.getAttribute("width")) || 0;
        var tbPad = this._hasPinnedFontSize(shape) ? size * 0.2 : tbH * 0.13;
        tbPad = this._textboxPadPx(shape, tbPad, tbW, tbH);
        if (ed.setPad) ed.setPad(tbPad);
        ed.input.style.padding = Math.round(tbPad) + "px";
      }
      if (ed.fit) ed.fit();
    },

    _commitTextEdit: function() {
      var ed = this._textEditor;
      if (!ed) return;
      var shape = ed.shape;
      // Snapshot the *pre-edit* state for undo before we overwrite
      // metadata.title locally. Captured here (not inside the
      // metadata-mutation block below) so the snapshot still reflects
      // the previous title even on no-op commits we early-return on.
      var historyBefore = this._snapshotShape(shape);
      // Point text keeps the author's spaces and lines exactly; only blank
      // space trailing off the end goes, as it draws nothing.
      var newTitle = ed.point
        ? ed.input.value.replace(/\s+$/, "")
        : ed.input.value.trim();
      var prevTitle = (shape.metadata && shape.metadata.title) || "";

      // Mirror the new title locally so the <text> renders with it
      // immediately, before the server round-trip.
      var patch = { title: newTitle };
      // A label typed onto a shape starts centered inside it. The floating-
      // above-with-a-leader default belongs to labels the HOST supplies —
      // those annotate something already on the canvas and shouldn't cover
      // it — but someone double-clicking a rectangle to name it expects the
      // name to land in the rectangle. Only on creation, so re-editing the
      // text never moves a label the user has since placed.
      if (newTitle && !prevTitle && !this._isTextKind(shape.kind) &&
          !this._labelRidesShaft(shape.kind) &&
          !normalizeTitleAlign(shape.metadata && shape.metadata.title_align)) {
        patch.title_align = { h: "center", v: "middle" };
      }
      // A brand-new label starts in the colour the user last gave a label
      // (saved by the swatch path when a focused label is recoloured) —
      // labelling three parts of a drawing in blue shouldn't mean
      // recolouring each one by hand. Creation only, and only where no
      // explicit colour exists, so re-editing text never repaints a label.
      // Text and callout are excluded — their text IS the shape and takes
      // the shape's colour. Shaft-riding labels (dimension, arrow) are
      // excluded too: they default to their LINE's colour (a red arrow, a
      // red label — head dev's call), which a remembered preference must
      // not override; recolouring one by hand still writes title_color.
      if (newTitle && !prevTitle &&
          shape.kind !== "text" && shape.kind !== "callout" &&
          !this._labelRidesShaft(shape.kind) &&
          !(shape.metadata && shape.metadata.title_color)) {
        var rememberedLabelColor = this._getPref("label_color");
        if (typeof rememberedLabelColor === "string" && rememberedLabelColor) {
          patch.title_color = rememberedLabelColor;
        }
      }
      shape.metadata = Object.assign({}, shape.metadata || {}, patch);
      this._endTextEdit();
      // The shape stays selected, handles up — see _keepOrDropSelection.
      // Here rather than at either commit site, because there are three of
      // them (Enter, a click outside, the blur that a click on Etcher's own
      // chrome comes through) and they must all end the same way.
      this._keepOrDropSelection(shape);
      this._renderShape(shape);
      // The box is sized by the text now, so committing new text moves the
      // label's corners — and the title-edit handles sit ON those corners.
      // Without this they stay at the old extent, framing where the label
      // used to end.
      if (this.editingTitleShape === shape) {
        // Committed empty: the label is gone, and its frame with it.
        if (shape.titleGroup) this._positionAllTitleHandles(shape);
        else this._exitTitleEditMode();
      }
      // Whether the label controls apply turns on `metadata.title`, which
      // just changed without the selection changing — the selection-driven
      // syncs won't fire, so the section would stay as it was.
      this._syncLabelSection();

      if (newTitle === "" && !prevTitle) {
        // Brand-new text shape with no content typed — clean up. UUIDs
        // are local in 0.3.x so the delete is immediate, no tmp_id
        // round-trip / pending-title bookkeeping needed.
        //
        // Only for the kinds that ARE their text. Now that double-click
        // labels any shape, this path is also reached by opening a
        // rectangle's label editor and typing nothing — deleting the
        // rectangle for that would be catastrophic.
        if (this._isTextKind(shape.kind)) this._discardEmptyTextShape(shape);
        return;
      }

      if (newTitle === prevTitle) return;

      this._emitChanged();
      this._pushUndo(shape.uuid, historyBefore, this._snapshotShape(shape));
    },

    _cancelTextEdit: function() {
      var ed = this._textEditor;
      if (!ed) return;
      var shape = ed.shape;
      this._endTextEdit();

      // Same rule as the commit path: an untitled shape is only discarded
      // when the text WAS the shape. Escaping out of a rectangle's label
      // editor must leave the rectangle alone.
      var hasContent = shape.metadata && shape.metadata.title;
      if (!hasContent && this._isTextKind(shape.kind)) {
        this._discardEmptyTextShape(shape);
        return;
      }
      // Escaping the label is not escaping the shape: under its own tool
      // it stays selected, same as a commit (see _keepOrDropSelection).
      this._keepOrDropSelection(shape);
      this._renderShape(shape);
      if (this.editingTitleShape === shape && !shape.titleGroup) this._exitTitleEditMode();
    },

    // The end of a label edit leaves the shape selected — Enter, a click
    // away or Escape alike, under the cursor or any tool. Finishing the
    // words is when you reach for the corners: to scale it, turn it, move
    // it. (Cursor mode used to make it a full stop that dropped the
    // selection, so the handles vanished just as they were wanted.) The
    // handles come back up (the editor may have replaced them), positioned
    // against the just-committed text, and the dismiss guard swallows the
    // click that a commit-by-clicking-away still has in flight — it already
    // did its job. The NEXT click away deselects.
    _keepOrDropSelection: function(shape) {
      this._suppressEditDismissUntil = Date.now() + 400;
      if (this.editingShape === shape) {
        // Already in edit mode (the created-flow: selected, then the
        // editor on top) — but the editor's lifetime may have torn the
        // dots down, and the text just resized the shape's box, so
        // rebuild rather than trust what is there.
        this._renderHandles(shape);
      } else {
        this._enterEditMode(shape);
      }
    },

    _endTextEdit: function() {
      var ed = this._textEditor;
      if (!ed) return;
      if (this.svg) this.svg.classList.remove("is-over-text-frame");
      if (ed.grip && ed.grip.parentNode) ed.grip.parentNode.removeChild(ed.grip);
      if (this._textEditOutsideDown) {
        document.removeEventListener("pointerdown", this._textEditOutsideDown, true);
        this._textEditOutsideDown = null;
      }
      if (ed.fo && ed.fo.parentNode) ed.fo.parentNode.removeChild(ed.fo);
      if (ed.shape) {
        delete ed.shape._draftTitle;
        if (ed.shape.el) ed.shape.el.classList.remove("is-typing");
        if (ed.shape.titleGroup) ed.shape.titleGroup.classList.remove("is-typing");
      }
      var hostEl = ed.shape && this._textEditHost(ed.shape);
      var existing = hostEl && hostEl.querySelector(".etcher-text-content");
      if (existing) existing.removeAttribute("visibility");
      var exRect = hostEl && hostEl.querySelector(".etcher-text-rect");
      if (exRect) exRect.removeAttribute("visibility");
      this._textEditor = null;
    },

    // Which DOM element hosts the visible <text class="etcher-text-content">?
    // text + callout: the shape's own `<g>` — their text IS the shape.
    // Everything else with a title — dimension included — renders it
    // through the standard title-sibling group. The dimension used to be
    // in the first list, from the era when its label was a <text> welded
    // into the shape's own element; once the label moved to the sibling
    // path, the stale mapping meant the editor's hide-while-editing found
    // nothing to hide, and the OLD label stayed visible as a phantom
    // under the editor until the new text committed.
    // Whether a point text's frame and handles are up: its shape selected,
    // or — for a label — the label in title-edit mode.
    _pointFrameUp: function(shape) {
      return this.editingShape === shape || this.editingTitleShape === shape;
    },

    _textEditHost: function(shape) {
      if (!shape) return null;
      if (shape.kind === "text" || shape.kind === "callout") {
        return shape.el;
      }
      return shape.titleGroup || null;
    },

    // Strip a text shape that was abandoned mid-creation (no title ever
    // typed). Every shape has a local uuid from the moment it was
    // drawn, so deletion is immediate — no tmp_id round-trip needed.
    _discardEmptyTextShape: function(shape) {
      if (!shape) return;
      this._removeShape(shape.uuid);
    },

    // -------------------------------------------------------------------------
    // Shared finalize + cancel
    // -------------------------------------------------------------------------

    _finalizeShape: function(kind, geometry, el, afterCreate) {
      var uuid = genUuidV7();
      el.setAttribute("data-uuid", uuid);
      // Suppress Fresco's `tap` event when the user later taps this
      // shape — Fresco probes for this attribute under the tap point
      // via `document.elementsFromPoint`. Shape `<g>` / `<rect>` /
      // etc. elements all carry `pointer-events: none` (so
      // pan/zoom passes through), which would hide them from any
      // hit-test that relied on the topmost-element heuristic; the
      // data attr is the explicit-opt-in path.
      el.setAttribute("data-fresco-suppress-tap", "");
      var style = this._styleForNewShape(kind);
      var shape = {
        uuid: uuid,
        kind: kind,
        geometry: geometry,
        style: style,
        el: el
      };
      // Strip mode: tag the shape with the image it was drawn on so the
      // pushed `etcher:annotations-changed` payload carries `image_idx`,
      // and so subsequent hit-tests / handle renders / interactions can
      // route to the correct per-image overlay.
      if (this.handleKind === "strip" && this._stripActiveDraw) {
        shape.image_idx = this._stripActiveDraw.imageIdx;
        el.setAttribute("data-image-idx", String(shape.image_idx));
      }
      // Multi-image canvas: tag the shape with its host image id so
      // visibility toggling can hide it when the host display:nones
      // that image. Single-image canvases skip this (no `image_id`
      // returned), keeping the on-the-wire payload backwards-compat.
      if (this.handleKind === "canvas") {
        var imageId = this._resolveCanvasImageId(kind, geometry);
        if (imageId) {
          shape.image_id = imageId;
          el.setAttribute("data-image-id", imageId);
        }
      }
      this.shapes.push(shape);
      this._renderShape(shape);
      if (this._isStrokeShape(kind)) this._applyLineParams(el, style, this._markerScale());
      this._attachShapeInteractions(shape);
      this._pushUndoCreate(shape);

      this._emitChanged();

      // Swallow Fresco's next `tap` (Fresco >= 0.5.9). The OS-
      // synthesized mousedown/mouseup that follows a drag's
      // touchend triggers a `tap` emit on Fresco, which races
      // etcher's mode-flip and can fire a consumer's tap-zone
      // navigation immediately after the user committed a shape
      // (a paged reader would page-turn the just-drawn annotation
      // off-screen). Older Fresco versions don't expose the method
      // — guard with typeof and no-op there.
      if (this.handle && typeof this.handle.suppressNextTap === "function") {
        this.handle.suppressNextTap(250);
      }

      // Dedicated user-draw event. `annotations-changed` fires on every
      // mutation (including undo/redo, drags, color picks) and is the
      // right channel for persistence sync. But "open the composer for
      // this shape" is about user intent, not state diff — undo of a
      // delete restores a shape via _emitChanged too, and that should
      // NOT re-open the composer. So consumers wanting to react to "user
      // just drew a brand-new shape" subscribe to this event instead.
      if (this.pushEventTo) {
        this.pushEventTo(this.el, "etcher:shape-drawn", {
          fresco_id: this.frescoId || null,
          uuid: shape.uuid,
          kind: shape.kind
        });
      }

      this.draftState = null;
      this._syncDraftHandles();

      // Per-kind post-create hook (e.g. text → inline edit). Runs on
      // the just-pushed shape so callers can capture it without
      // re-finding by uuid.
      if (typeof afterCreate === "function") {
        try { afterCreate(shape); } catch (_) {}
      }

      // The tool STAYS ARMED. Dropping back to the cursor after every
      // create meant re-picking the tool for every shape in a row, which
      // is most of what drawing is — the marker was already exempt for
      // exactly that reason, and head dev's call is that the rest work
      // the same way. Cursor mode is now something the user asks for:
      // the toolbar, V, or Escape.

      // ...and select what was just drawn. Finishing a stroke and then
      // having to click it AGAIN before the thickness / dash / color
      // controls would target it was the top piece of user feedback — every
      // comparable canvas tool selects the new shape on release. Edit mode
      // is that selection: `_paramsTargetShapes` targets `editingShape`, so
      // the params popup and the swatches restyle the fresh shape
      // immediately, and the handles double as \"this is now selected\".
      //
      // Skipped when an afterCreate hook took over — text goes straight
      // into inline typing, which is the richer editing state, and the
      // media-insert path returns to its dialog flow. The marker skips
      // too: a sketch tool's strokes come in flurries, and handles under
      // the next one would fight it. Every other kind keeps the selection
      // even though its tool is still armed — that is what lets a panel
      // edit tune BOTH the shape just drawn and the tool's defaults (see
      // _freshTargets), and the next stroke simply starts on the canvas,
      // dismissing it.
      //
      // The guard timestamp swallows the click the browser synthesizes
      // from the drawing gesture's own pointerup: it lands after
      // `_enterEditMode` registered its outside-click dismisser, at
      // coordinates that may sit outside the shape (a circle's drag handle,
      // a polygon's closing click), and would otherwise tear the fresh
      // selection straight back down.
      if (kind !== "marker" && typeof afterCreate !== "function") {
        this._suppressEditDismissUntil = Date.now() + 400;
        this._enterEditMode(shape);
      }

      // From here until the user clicks away, this is the FRESH shape:
      // panel edits write through to the tool's defaults (see
      // _freshTargets). Set last, after _enterEditMode's own
      // _exitEditMode teardown has run, or the flag would clear itself.
      this._freshShape = shape;
    },

    // Returns the shape's bottom-left corner in container px (the
    // coordinate space the host LV's overlay div uses). Falls back to
    // the viewer center if the shape's bounding rect isn't available
    // yet for some reason.
    _shapeAnchorBottomLeft: function(shape) {
      try {
        var sr = shape.el.getBoundingClientRect();
        var cr = this.handle.container.getBoundingClientRect();
        return { x: sr.left - cr.left, y: sr.bottom - cr.top + 8 };
      } catch (_) {
        var cr2 = this.handle.container.getBoundingClientRect();
        return { x: cr2.width / 2 - 160, y: cr2.height / 2 };
      }
    },

    _cancelDraft: function() {
      // A press being held until it becomes a drag is a draft-in-waiting;
      // anything that cancels drafts cancels it too (tool change, mode
      // off, Escape).
      this._pendingDraw = null;
      if (this.draftState && this.draftState.el && this.draftState.el.parentNode) {
        this.draftState.el.parentNode.removeChild(this.draftState.el);
      }
      this.draftState = null;
      if (this.draftPolygon && this.draftPolygon.el && this.draftPolygon.el.parentNode) {
        this.draftPolygon.el.parentNode.removeChild(this.draftPolygon.el);
      }
      this.draftPolygon = null;
      if (this.draftCallout && this.draftCallout.el && this.draftCallout.el.parentNode) {
        this.draftCallout.el.parentNode.removeChild(this.draftCallout.el);
      }
      this.draftCallout = null;
      // Committed or abandoned, the drawing is over: peers drop the
      // provisional shape and wait for the edit. Multi-click tools reach
      // this and nothing else — their pointer went up several clicks ago.
      if (this._liveDrawHandler) this._endLiveDraw();
      this._syncDraftHandles();
    },

    // -------------------------------------------------------------------------
    // Initial render + helpers
    // -------------------------------------------------------------------------

    _renderInitial: function() {
      var self = this;
      // Hydrate from `extensions.etcher` set by the consumer via
      // Fresco.Canvas.put_extension/3 (or the matching scroll_strip
      // `extensions={}` attr). Replaces 0.2.x's server-rendered
      // `data-initial-annotations` attribute.
      var ext = (self.handle && typeof self.handle.getExtension === "function")
        ? self.handle.getExtension("etcher")
        : null;
      var annotations = (ext && Array.isArray(ext.annotations)) ? ext.annotations : [];
      var stripMode = self.handleKind === "strip";
      annotations.forEach(function(ann) {
        // Strip annotations are stored in per-image natural-pixel space.
        // Switch the active overlay before each render so `self.svg`
        // appendChild sites (in `_renderAnnotation` / `_renderShape`)
        // attach into the matching page. Annotations missing a valid
        // `image_idx` are skipped with a console hint — they almost
        // certainly came from a canvas-mode export.
        if (stripMode) {
          var idx = typeof ann.image_idx === "number" ? ann.image_idx : -1;
          if (!self.pageOverlays || !self.pageOverlays[idx]) {
            console.warn(
              "[Etcher] Skipping annotation with missing/unknown image_idx:",
              ann.uuid, "image_idx:", ann.image_idx
            );
            return;
          }
          self._activateOverlayForImage(idx);
        }
        self._renderAnnotation(ann);
      });

      // Hydrated shapes may already live outside the picture — the pan
      // must reach them from the first frame, not from the first edit.
      self._syncPanBounds();

      // If any pre-0.4.7 canvas shapes got their `image_id`
      // backfilled during the loop above, push one bulk emit so the
      // consumer persists the new ids. The next mount sees them in
      // `ann.image_id` and skips this branch entirely.
      if (self._backfilledImageId) {
        self._backfilledImageId = false;
        self._emitChanged();
      }

      // Ensure the color palette is seeded even for headless layers
      // (toolbar disabled) so `getColors()` is populated. The toolbar
      // build already seeds when present; this only fills an empty
      // palette and never clobbers a user edit.
      if (!self._colorSlots || !self._colorSlots.length) self._seedColorSlots();
    },

    // Re-hydrate the overlay after a Fresco `setSources` swap (canvas mode):
    // tear down the current shapes + interaction state so nothing leaks
    // across the swap, re-render from the new `extensions.etcher`, re-seed
    // the per-canvas palette, and re-emit `annotations-changed` for consumer
    // save handlers. Per-shape `readonly` flows in naturally — it lives on
    // each annotation in the new array.
    _rehydrateFromExtension: function() {
      // Media elements outlive their shapes unless stopped here — a source
      // swap would otherwise leave the previous canvas's audio playing over
      // the new one.
      var selfRehydrate = this;
      (this.shapes || []).forEach(function(s) { selfRehydrate._releaseMediaEl(s); });
      // Drop all interaction state tied to the outgoing shapes.
      this._exitEditMode();
      this._exitTitleEditMode();
      this._clearSelection();
      this._unpinTooltip();
      this._hideTooltip();

      // Remove every shape element (and its title sibling) from the overlay.
      (this.shapes || []).forEach(function(s) {
        if (s.el && s.el.parentNode) s.el.parentNode.removeChild(s.el);
        if (s.titleGroup && s.titleGroup.parentNode) {
          s.titleGroup.parentNode.removeChild(s.titleGroup);
        }
      });
      this.shapes = [];

      // Undo/redo across a full image swap is meaningless — reset it.
      this._undoStack = [];
      this._redoStack = [];
      this._refreshUndoButtons();

      // Re-render from the NEW canvas state. Fresco's setSources already
      // replaced `data-extensions`, so `getExtension` returns the new set.
      this._renderInitial();

      // Re-seed the per-canvas palette from the new extension (per-user
      // `:colors` attr still wins).
      this._reseedColorsOnSwap();

      // Tell consumer save handlers the live set changed.
      this._emitChanged();
      this._syncPanBounds();
    },

    // Re-seed color slots from the swapped-in `extensions.etcher.colors`,
    // unless the consumer supplied a per-user palette via the `:colors` attr
    // (which always wins). No-op when the new extension carries no colors —
    // the current palette is kept rather than reset.
    _reseedColorsOnSwap: function() {
      if (this.el && this.el.dataset && this.el.dataset.colors) return;
      var ext = (this.handle && typeof this.handle.getExtension === "function")
        ? this.handle.getExtension("etcher") : null;
      if (!ext || !Array.isArray(ext.colors) || !ext.colors.length) return;
      this._colorSlots = this._sanitizeColorSlots(ext.colors);
      if (this._activeSlot >= this._colorSlots.length) this._activeSlot = 0;
      this.activeColor = this._colorSlots[this._activeSlot];
      this._refreshToolbarSwatches();
    },

    // `opts.ghost` builds and paints the shape and stops there: it is not
    // pushed onto `shapes`, gets no interactions, and is handed back to the
    // caller to hold on to. That is what a peer's in-progress drawing is —
    // something to look at, owned by nothing. Everything above the
    // registration below is the element factory, which is exactly what a
    // ghost needs in order to look like the shape that will replace it.
    _renderAnnotation: function(ann, opts) {
      if (!ann || !ann.kind || !ann.geometry) return;
      var ghost = !!(opts && opts.ghost);
      var el;

      switch (ann.kind) {
        case "rectangle": el = svgEl("rect");                       break;
        // Image object: an <image> positioned like a rectangle box. Shares
        // rectangle's move / corner-resize / hit-test everywhere below; only
        // its render + this element differ. `preserveAspectRatio="none"` lets
        // a corner-drag resize scale it freely to the box.
        case "image":
          el = svgEl("image", { preserveAspectRatio: "none" });
          el.classList.add("etcher-image");
          break;
        // Audio: a player card rather than a picture of one. Everything is
        // SVG — no <foreignObject> — so it pans, zooms, layers and exports
        // like every other shape; the <audio> element that actually plays is
        // kept out of the overlay entirely (see `_audioElFor`).
        case "audio":
        case "video": el = this._makeAudioEl(ann.kind); break;
        case "circle":    el = svgEl("circle");                     break;
        case "polygon":   el = svgEl("polygon");                    break;
        case "marker":
        case "freehand":
          // Markers always render as a <path> (Catmull-Rom spline through
          // their points). Freehand uses a <path> for fitted bezier nodes,
          // else a <polyline> for legacy raw points.
          el = (ann.kind === "marker" || (ann.geometry && ann.geometry.nodes))
            ? svgEl("path", { fill: "none" })
            : svgEl("polyline", { fill: "none" });
          if (ann.kind === "marker") el.classList.add("etcher-marker");
          break;
        case "text": {
          // <g> wrapping a hit-zone <rect> and a content <text>. The
          // group bind to `currentColor` so _applyShapeColor can recolor
          // the rect border and text fill in one stroke.
          //
          // The rect's stroke ATTRIBUTE is "transparent" (here and at every
          // other .etcher-text-rect creation site): the attribute is only
          // the fallback for a context where the injected stylesheet isn't
          // in effect, and the correct fallback is the at-rest look —
          // invisible. It used to be "currentColor", which cost nothing
          // while the CSS ruled it out but painted a solid box around every
          // label the moment anything rendered these shapes without the
          // stylesheet. The hover/draft/selected borders all come from
          // class rules, which override presentation attributes, so no
          // interactive state changes.
          el = svgEl("g");
          el.classList.add("etcher-text");
          var trect = svgEl("rect", {
            fill: "transparent",
            stroke: "transparent",
            "stroke-width": "2"
          });
          trect.classList.add("etcher-text-rect");
          var ttext = svgEl("text", {
            "text-anchor": "start",
            "dominant-baseline": "hanging",
            fill: "currentColor",
            stroke: "none"
          });
          ttext.classList.add("etcher-text-content");
          el.appendChild(trect);
          el.appendChild(ttext);
          break;
        }
        case "callout": {
          // Blueprint-style callout: a horizontal underline spanning
          // the full bottom of the text bbox + a leader line from the
          // anchor up to the bottom corner of the bbox that sits
          // closer to the anchor. Both lines, the bbox rect, the
          // content text, and the anchor dot live inside the same <g>
          // so _applyShapeColor recolors the whole composition via
          // `style.color`. Lines render first so the rect/text/dot
          // overlay them.
          el = svgEl("g");
          el.classList.add("etcher-callout", "etcher-text");
          el.appendChild(svgEl("line", {
            "stroke-width": "2",
            stroke: "currentColor",
            fill: "none"
          }));
          var coUnderline = svgEl("line", {
            "stroke-width": "2",
            stroke: "currentColor",
            fill: "none"
          });
          coUnderline.classList.add("etcher-callout-underline");
          el.appendChild(coUnderline);
          var coRect = svgEl("rect", {
            fill: "transparent",
            stroke: "transparent",
            "stroke-width": "2"
          });
          coRect.classList.add("etcher-text-rect");
          el.appendChild(coRect);
          var coText = svgEl("text", {
            "text-anchor": "start",
            "dominant-baseline": "hanging",
            fill: "currentColor",
            stroke: "none"
          });
          coText.classList.add("etcher-text-content");
          el.appendChild(coText);
          el.appendChild(svgEl("circle", {
            r: "3",
            fill: "currentColor",
            stroke: "none"
          }));
          break;
        }
        case "dimension": {
          // Same composition as `_startDimension` so persisted
          // dimensions re-hydrate identically: shaft + 2 V-arrows in
          // currentColor. The label is the standard title sibling, built
          // by `_renderTitleSibling` alongside the shape rather than
          // inside it.
          el = svgEl("g");
          el.classList.add("etcher-dimension");
          var dimShaft = svgEl("line", {
            "stroke-width": "2",
            stroke: "currentColor",
            fill: "none"
          });
          dimShaft.classList.add("etcher-dim-shaft");
          el.appendChild(dimShaft);
          var dimArrowA = svgEl("polyline", {
            "stroke-width": "2",
            stroke: "currentColor",
            fill: "none",
            "stroke-linejoin": "round",
            "stroke-linecap": "round"
          });
          dimArrowA.classList.add("etcher-dim-arrow");
          el.appendChild(dimArrowA);
          var dimArrowB = svgEl("polyline", {
            "stroke-width": "2",
            stroke: "currentColor",
            fill: "none",
            "stroke-linejoin": "round",
            "stroke-linecap": "round"
          });
          dimArrowB.classList.add("etcher-dim-arrow");
          el.appendChild(dimArrowB);
          break;
        }
        case "line": {
          // <g> wrapping a single stroked <line>. No arrows, no inline
          // label — title (if any) rides the sibling-above-shape path.
          el = svgEl("g");
          el.classList.add("etcher-line");
          var lnShaft = svgEl("line", {
            "stroke-width": "2",
            stroke: "currentColor",
            fill: "none"
          });
          lnShaft.classList.add("etcher-line-shaft");
          el.appendChild(lnShaft);
          break;
        }
        // Connector — shaft + one head. Same builder the drag preview
        // uses, so a reloaded arrow is the same DOM as a fresh one.
        case "arrow": el = this._makeArrowEl(); break;
        default: return;
      }

      // Non-group shapes get a uniform stroke-width on the root.
      // Callout / text / dimension / line / arrow are <g> wrappers — their
      // visible strokes live on the inner children, so leaving the
      // group unstroked avoids painting a bogus border on the wrapper.
      if (ann.kind !== "callout" && ann.kind !== "text" &&
          ann.kind !== "dimension" && ann.kind !== "line" &&
          ann.kind !== "arrow" && !this._isMediaKind(ann.kind)) {
        el.setAttribute("stroke-width", "2");
      }
      el.classList.add("etcher-shape");
      if (ann.uuid) el.setAttribute("data-uuid", ann.uuid);
      // Mirror `_finalizeShape`'s Fresco-tap-suppress opt-in so
      // hydrated annotations behave identically to freshly-drawn
      // ones — see the comment in `_finalizeShape`.
      el.setAttribute("data-fresco-suppress-tap", "");
      this.svg.appendChild(el);

      var shape = {
        uuid: ann.uuid,
        kind: ann.kind,
        geometry: ann.geometry,
        metadata: ann.metadata || null,
        label: ann.label || null,
        style: ann.style || null,
        // Render-time-only lock. `true` disables edit / move / delete / pen-
        // editor / box-select while keeping hover + tooltip. Never echoed in
        // `etcher:annotations-changed` — the consumer recomputes it from its
        // own ownership data each render. See `data-readonly` for styling.
        readonly: ann.readonly === true,
        el: el
      };
      if (shape.readonly) el.setAttribute("data-readonly", "true");
      // Strip mode: keep `image_idx` on the shape so future emits round-
      // trip cleanly and so `_shapeAt` can filter hit-tests by page.
      if (this.handleKind === "strip" && typeof ann.image_idx === "number") {
        shape.image_idx = ann.image_idx;
        el.setAttribute("data-image-idx", String(shape.image_idx));
      }
      // Canvas multi-image: hydrate `image_id` so the visibility
      // toggle path knows which image owns this shape. Falls through
      // for canvas annotations that don't carry one (single-image
      // canvases, or shapes that landed in empty canvas space).
      if (this.handleKind === "canvas" && typeof ann.image_id === "string") {
        shape.image_id = ann.image_id;
        el.setAttribute("data-image-id", shape.image_id);
      }
      // Backfill `image_id` for pre-0.4.7 shapes on a multi-image
      // canvas. Without this, annotations persisted before this
      // version stay untagged after hydration and the visibility
      // filter can't hide them when their host page is `display:
      // none` — they ghost into adjacent viewport bands. The
      // `_resolveCanvasImageId` lookup is gated on
      // `getImages().length > 1`, so single-image consumers and
      // shapes that already carry an id skip this branch.
      // `_backfilledImageId` accumulates the work; `_renderInitial`
      // emits one `etcher:annotations-changed` after the full pass
      // so the consumer persists the new ids once, not N times.
      if (this.handleKind === "canvas" && !shape.image_id) {
        var resolvedImageId = this._resolveCanvasImageId(shape.kind, shape.geometry);
        if (resolvedImageId) {
          shape.image_id = resolvedImageId;
          el.setAttribute("data-image-id", resolvedImageId);
          this._backfilledImageId = true;
        }
      }
      if (ghost) {
        // Transient by construction: no uuid to be addressed by, no entry in
        // `shapes` to be found in, and marked in the DOM so it is obvious in
        // a debugger and to anything walking the overlay.
        shape.uuid = null;
        el.setAttribute("data-etcher-ghost", "");
        el.style.pointerEvents = "none";
      } else {
        this.shapes.push(shape);
      }
      this._renderShape(shape);
      // Apply persisted appearance. Markers are styled (and zoom-scaled) by
      // `_renderShape` above; other kinds restore their persisted color here.
      // No style → CSS default.
      if (shape.kind !== "marker" && shape.style && shape.style.color) {
        this._applyShapeColor(el, shape.style.color);
      }
      // Restore persisted line params (thickness / opacity / dash) on stroke
      // shapes; older annotations without them fall back to the 2px default.
      if (this._isStrokeShape(shape.kind)) this._applyLineParams(el, shape.style, this._markerScale());
      // A ghost answers to nobody: no drag, no select, no tooltip, no
      // double-click editor. The real shape replaces it the moment its
      // author lets go.
      if (ghost) return shape;
      // Built a real one: if somebody was watched drawing it, this is where
      // their ghost steps aside — same task, so the swap costs no frame.
      this._handOverGhost(shape);
      this._attachShapeInteractions(shape);
    },

    _selectShape: function(uuid) {
      this.svg.querySelectorAll(".etcher-shape.is-selected").forEach(function(s) {
        s.classList.remove("is-selected");
      });
      var hit = this.svg.querySelector('.etcher-shape[data-uuid="' + uuid + '"]');
      if (hit) hit.classList.add("is-selected");
    },

    // -------------------------------------------------------------------------
    // Edit mode — click a shape to show vertex handles; drag a handle to
    // reshape; release to commit. Only one shape edits at a time.
    // -------------------------------------------------------------------------

    _enterEditMode: function(shape) {
      // Only persisted shapes are editable — temp shapes haven't been
      // ack'd by the server yet so an `etcher:updated` event for them
      // would point at a non-existent uuid.
      if (!shape || !shape.uuid) return;
      // Locked shapes never enter edit mode (no handles, no color pickup,
      // no move). Hover + tooltip-pin still work via `_onShapeTap`.
      if (shape.readonly) return;
      if (this.editingShape === shape) return;
      this._exitEditMode();
      this._exitTitleEditMode();
      // Older text becomes point text as it is picked up, looking the same.
      this._ensurePointText(shape);

      this.editingShape = shape;
      shape.el.classList.add("is-editing");
      this._refreshImageRing(shape);
      // Docked: selecting a shape is what puts its actions in the panel,
      // and they stay there for as long as it is selected.
      this._syncDockedTooltip();
      // Hand the eight points over to the resize handles about to be drawn
      // there — see `_connectorsAvailableFor`.
      if (this._connectorDotShape === shape) this._removeConnectorDots();
      this._syncArrangeButtons();
      this._showLinkMenuFor(shape);
      // Anchored, entering edit mode closes the bubble: it floats over the
      // drawing, and the drawing is what you are about to work on. Docked,
      // the section IS the selection's UI — `_syncDockedTooltip` above just
      // put this shape's actions in the panel, and hiding them here undid
      // that. It only ever appeared because the show's paint and this
      // hide's cleanup both waited a frame and the paint happened to land
      // first; now that each cancels the other properly, the accident is
      // gone and the guard has to be real.
      if (!this._tooltipDocked()) this._hideTooltip();
      // Pick up the shape's color into the toolbar (active swatch + the color
      // new shapes draw with), so selecting a shape "switches" to its color.
      this._syncToolbarColorToShape(shape);
      // Fitted freehand AND marker curves get the dedicated pen editor
      // (anchors + bezier handles) — a marker is just a freehand stroke with
      // marker styling, so it edits identically. Every other kind — and
      // legacy {points} strokes — uses the generic vertex handles.
      if ((shape.kind === "freehand" || shape.kind === "marker") &&
          shape.geometry && shape.geometry.nodes) {
        this._renderFreehandEditor(shape);
      } else {
        this._renderHandles(shape);
      }

      // Midpoint handles follow the cursor's nearest edge (polygon /
      // rectangle) or segment (arrow). The wrapper has pointer-events: none
      // in cursor mode, so pointermove there doesn't fire when the
      // user is outside the shape's filled body — listen on document
      // instead so the highlight tracks the cursor everywhere.
      if (this._hasMidpointHandles(shape.kind)) {
        this._wireMidpointTracker();
      }
      this._syncTransformBox();

      // Dismiss on any click outside the shape, its handles, the
      // tooltip, or the toolbar. Capture phase so we run before stop-
      // propagation handlers on inner elements. Shapes are
      // `pointer-events: none`, so a click on a shape lands on OSD's
      // canvas at the DOM level — fall back to an image-px hit-test
      // against the currently-edited shape (and the shape we'd
      // re-enter edit mode on next) so the user can click a NEW
      // shape without the handler tearing down edit mode in between.
      var self = this;
      this._outsideClickHandler = function(e) {
        // Click on a shape keeps edit mode alive (the user might be
        // switching focus to a sibling). Click inside Etcher's own
        // chrome or any registered input-owner (modals, dialogs)
        // also keeps edit mode alive — the gesture belongs to that UI.
        if (e.target.closest(".etcher-shape")) return;
        // A click on any edit handle (vertex, bezier control, anchor) is
        // part of editing — never let it tear down edit mode. Bezier
        // handles in particular sit out in empty space off the curve.
        if (e.target.closest(".etcher-handle")) return;
        // Nor the outline grip and the editor of the text being typed: a
        // drag by the outline ends in a click on the grip, wherever the
        // text has moved to.
        if (e.target.closest(".etcher-text-grip, .etcher-text-editor")) return;
        // Clicks on Etcher's own toolbar / popups (e.g. opening the marker
        // style popup for the selected stroke, or dragging its sliders) are
        // part of editing, not a dismissal.
        if (e.target.closest(CHROME_SELECTOR)) return;
        if (isInputOwner(e.target, self.overlayWrapper)) return;
        try {
          var pt = self._toImage(e);
          if (self._shapeAt(pt)) return;
        } catch (_) {}
        // The click synthesized from the draw gesture that JUST created and
        // auto-selected this shape (see `_finalizeShape`). Time-boxed rather
        // than a consume-once flag: on touch, no click is synthesized at
        // all, and a lingering flag would silently eat the user's next real
        // dismissal.
        if (Date.now() < (self._suppressEditDismissUntil || 0)) return;
        self._exitEditMode();
      };
      document.addEventListener("click", this._outsideClickHandler, true);
    },

    _exitEditMode: function() {
      // Leaving the shape ends its freshness for good — a later
      // re-select is a one-time edit (see _freshTargets).
      this._freshShape = null;
      // Docked: the section belongs to the selection, so losing the
      // selection re-asks what should be showing — the shape under the
      // cursor if there is one, else nothing. Asked AFTER `editingShape`
      // is cleared below, or it would answer with the shape being
      // deselected.
      var wasDocked = this._tooltipDocked() && !!this.editingShape;
      if (!this.editingShape) return;
      this.editingShape.el.classList.remove("is-editing");
      this._refreshImageRing(this.editingShape);
      this._hideLinkMenu();
      this._clearVertexSelection();
      this._removeHandles();
      this._removeFreehandEditor();
      this._unwireMidpointTracker();
      this.editingShape = null;
      if (this._outsideClickHandler) {
        document.removeEventListener("click", this._outsideClickHandler, true);
        this._outsideClickHandler = null;
      }
      this._syncArrangeButtons();
      this._syncTransformBox();
      if (wasDocked) this._syncDockedTooltip();
    },

    // Mark polygon vertices as the Backspace / Delete target. Plain
    // click resets the selection to just `idx`; shift-click toggles
    // `idx` in/out of the existing set so the user can pick multiple
    // points to delete in one keystroke. Stored as a Set of indices
    // against the live `editingShape` so subsequent re-renders can
    // re-apply `.is-selected` to the same dots.
    _selectVertex: function(shape, idx, additive) {
      if (!shape ||
          (shape.kind !== "polygon" && shape.kind !== "freehand" && shape.kind !== "marker")) {
        return;
      }
      if (this.editingShape !== shape) return;
      if (!this.selectedVertexIndices) this.selectedVertexIndices = new Set();
      if (additive) {
        if (this.selectedVertexIndices.has(idx)) {
          this.selectedVertexIndices.delete(idx);
        } else {
          this.selectedVertexIndices.add(idx);
        }
      } else {
        this.selectedVertexIndices.clear();
        this.selectedVertexIndices.add(idx);
      }
      this._refreshVertexSelectionClass();
    },

    _clearVertexSelection: function() {
      if (!this.selectedVertexIndices || this.selectedVertexIndices.size === 0) {
        return;
      }
      this.selectedVertexIndices.clear();
      this._refreshVertexSelectionClass();
    },

    _refreshVertexSelectionClass: function() {
      var sel = this.selectedVertexIndices;
      // Freehand anchors live in the pen editor, not the generic `handles`
      // array, so highlight them by node index there.
      if (this.freehandEditor) {
        this.freehandEditor.controls.forEach(function(c) {
          if (c.type === "anchor") {
            c.el.classList.toggle("is-selected", !!(sel && sel.has(c.nodeIdx)));
          }
        });
        return;
      }
      var handles = this.handles || [];
      for (var i = 0; i < handles.length; i++) {
        handles[i].classList.toggle(
          "is-selected",
          !!(sel && sel.has(i))
        );
      }
    },

    // Splice the currently-selected vertices out of the editing polygon.
    // Refuses to drop below 3 points (a polygon needs at least 3
    // vertices to still be a polygon; the regular shape-delete
    // keystroke covers full-shape removal). Splices from the highest
    // index down so earlier indices stay valid mid-loop. Single undo
    // entry for the whole batch.
    _deleteSelectedVertex: function() {
      var shape = this.editingShape;
      var sel = this.selectedVertexIndices;
      if (!shape || !sel || sel.size === 0) return false;
      // Freehand / marker: drop the selected node(s); a curve needs at least
      // 2 nodes (one segment) to survive. The new first/last nodes shed their
      // now-dangling outer handles so the endpoints stay clean.
      if ((shape.kind === "freehand" || shape.kind === "marker") &&
          shape.geometry && shape.geometry.nodes) {
        var nodes = shape.geometry.nodes;
        if (nodes.length - sel.size < 2) return false;
        var fHistory = this._snapshotShape(shape);
        var fIdx = Array.from(sel).sort(function(a, b) { return b - a; });
        var nextNodes = nodes.slice();
        for (var fi = 0; fi < fIdx.length; fi++) nextNodes.splice(fIdx[fi], 1);
        nextNodes[0].hIn = null;
        nextNodes[nextNodes.length - 1].hOut = null;
        shape.geometry = { nodes: nextNodes };
        this.selectedVertexIndices.clear();
        this._renderShape(shape);
        this._renderFreehandEditor(shape);
        if (shape.uuid) {
          this._emitChanged();
          this._pushUndo(shape.uuid, fHistory, this._snapshotShape(shape));
        }
        return true;
      }
      if (shape.kind !== "polygon") return false;
      var pts = (shape.geometry && shape.geometry.points) || [];
      if (pts.length - sel.size < 3) return false;
      var historyBefore = this._snapshotShape(shape);
      var idxList = Array.from(sel).sort(function(a, b) { return b - a; });
      var nextPts = pts.slice();
      for (var i = 0; i < idxList.length; i++) {
        nextPts.splice(idxList[i], 1);
      }
      shape.geometry = { points: nextPts };
      this.selectedVertexIndices.clear();
      this._renderShape(shape);
      this._renderHandles(shape);
      if (shape.uuid) {
        this._emitChanged();
        this._pushUndo(shape.uuid, historyBefore, this._snapshotShape(shape));
      }
      return true;
    },

    _wireMidpointTracker: function() {
      var self = this;
      if (self._midpointTracker) return;
      self._midpointTracker = function(e) {
        // A point label's side grabbers, in title-edit mode, ride along.
        if (!self._pointEdgeTarget()) {
          if (!self.editingShape) return;
          if (!self._hasMidpointHandles(self.editingShape.kind)) return;
        }
        if (!self.midpointHandles || !self.midpointHandles.length) return;
        try { self._updateClosestMidpoint(self._toImage(e)); } catch (_) {}
      };
      document.addEventListener("pointermove", self._midpointTracker);
    },

    _unwireMidpointTracker: function() {
      if (this._midpointTracker) {
        document.removeEventListener("pointermove", this._midpointTracker);
        this._midpointTracker = null;
      }
      this._clearClosestMidpoint();
      // Backstop. The suspend/resume pairs are matched, but a drag whose
      // release never arrives (a lost pointer, a shape deleted mid-gesture)
      // would otherwise leave the dots switched off for the rest of the
      // session. Leaving edit mode is the natural place to reset, since the
      // dots only exist inside it.
      this._resumeMidpointHighlight();
    },

    _renderHandles: function(shape, opts) {
      opts = opts || { interactive: true };
      this._removeHandles();
      var self = this;
      // Strip mode: handles must land in the same per-image overlay as
      // the shape they decorate. `self.svg` may still be pointing at
      // a different page from the last interaction.
      this._activateOverlayForShape(shape);
      var positions = this._handlePositions(shape);
      var handleColor = self._handleColor(shape);

      this.handles = positions.map(function(pt, idx) {
        var h = svgEl("circle", { r: 5 });
        h.classList.add("etcher-handle");
        // Draft dots are markers, not grab targets — the class is what
        // the `.is-drawing` stylesheet rule keys on to let the active
        // tool drag straight over them, while a committed shape's real
        // handles stay live under its own tool (same-tool editing).
        if (!opts.interactive) h.classList.add("etcher-handle--inert");
        h.style.color = handleColor;
        h.dataset.index = idx;
        self.svg.appendChild(h);
        if (opts.interactive) {
          var onDown = function(e) { self._startHandleDrag(shape, idx, h, e); };
          h.addEventListener("pointerdown", onDown);
          self._addGrabHalo(h, HANDLE_GRAB_RADIUS, onDown);
        }
        self._positionHandle(h, pt);
        return h;
      });

      // Per-kind edge midpoint helpers — rendered as a single shared
      // set of "midpoint" handles that follow the same closest-only
      // highlight behavior driven by `_updateClosestMidpoint`.
      // Polygons get insert-new-vertex semantics and arrows insert-new-bend;
      // rectangles get resize-one-edge. Drafts skip all three (the shape is
      // still being built).
      if (opts.interactive) {
        if (shape.kind === "polygon" || shape.kind === "arrow") {
          self._renderMidpointHandles(shape);
        } else if (shape.kind === "rectangle" || self._isPointFrame(shape) ||
                   self._textboxFixed(shape)) {
          // A text box's sides pull like a rectangle's: one wall moves,
          // the text re-wraps inside.
          self._renderRectEdgeHandles(shape);
        }
        self._renderExtraHandles(shape);
      }
    },

    // The knob above a shape that turns it, and — on a text box — the dot
    // past its bottom-right corner that scales the box and its text as one.
    _renderExtraHandles: function(shape) {
      var self = this;
      this._removeExtraHandles();
      // A hand-drawn stroke is turned (and scaled) by its selection frame.
      if (this._canRotate(shape) && !this._xfOwnsSingle(shape)) {
        var stem = svgEl("line", {});
        stem.classList.add("etcher-rotate-stem");
        stem.style.color = POINT_HANDLE_COLOR;
        this.svg.appendChild(stem);
        var knob = svgEl("circle", { r: 5 });
        knob.classList.add("etcher-handle", "etcher-rotate-handle");
        knob.style.color = this._isPointFrame(shape) ? POINT_HANDLE_COLOR : this._handleColor(shape);
        knob.setAttribute("aria-label", "Rotate");
        this.svg.appendChild(knob);
        var onRot = function(e) { self._startRotateDrag(shape, knob, e); };
        knob.addEventListener("pointerdown", onRot);
        this._addGrabHalo(knob, HANDLE_GRAB_RADIUS, onRot);
        this.rotateHandle = knob;
        this.rotateStem = stem;
      }
      if (shape.kind === "text" && this._textboxFixed(shape)) {
        var sd = svgEl("circle", { r: 4.5 });
        sd.classList.add("etcher-handle", "etcher-scale-handle");
        sd.style.color = this._handleColor(shape);
        sd.setAttribute("aria-label", "Scale the box and its text");
        this.svg.appendChild(sd);
        var onScale = function(e) { self._startTextboxScaleDrag(shape, sd, e); };
        sd.addEventListener("pointerdown", onScale);
        this._addGrabHalo(sd, HANDLE_GRAB_RADIUS, onScale);
        this.scaleHandle = sd;
      }
      this._positionExtraHandles(shape);
    },

    _removeExtraHandles: function() {
      if (this.rotateHandle) this._dropHandleEl(this.rotateHandle);
      if (this.rotateStem && this.rotateStem.parentNode) this.rotateStem.parentNode.removeChild(this.rotateStem);
      if (this.scaleHandle) this._dropHandleEl(this.scaleHandle);
      this.rotateHandle = this.rotateStem = this.scaleHandle = null;
    },

    // Where the knob and the scale dot go: the knob a fixed distance on
    // screen above the middle of the shape's top edge (its turned top edge,
    // for a turned box), the scale dot the same way out past the bottom-
    // right corner along the diagonal.
    _positionExtraHandles: function(shape) {
      if (!this.rotateHandle && !this.scaleHandle) return;
      var scale = 1;
      try { scale = this._markerScale() || 1; } catch (_) { scale = 1; }
      var box = this._isBoxRotatable(shape)
        ? (this._isPointText(shape) ? this._pointHandleBox(shape) : this._rotBox(shape))
        : this._shapeBBoxImagePx(shape);
      if (!box) return;
      var self = this;
      var put = function(local) { return self._rotPts(shape, [local])[0]; };
      if (this.rotateHandle) {
        var top = put({ x: box.x + box.w / 2, y: box.y });
        var knobAt = put({ x: box.x + box.w / 2, y: box.y - ROTATE_KNOB_PX / scale });
        this._positionHandle(this.rotateHandle, knobAt);
        var a = this._imageToContainer(top), b = this._imageToContainer(knobAt);
        this.rotateStem.setAttribute("x1", a.x); this.rotateStem.setAttribute("y1", a.y);
        this.rotateStem.setAttribute("x2", b.x); this.rotateStem.setAttribute("y2", b.y);
      }
      if (this.scaleHandle) {
        // Straight out at 45° from the corner, whatever the box's shape.
        var off = SCALE_DOT_PX * Math.SQRT1_2 / scale;
        var at = put({ x: box.x + box.w + off, y: box.y + box.h + off });
        this._positionHandle(this.scaleHandle, at);
      }
    },

    // Turn a shape about its middle by the knob. Shift steps it in 15°.
    _startRotateDrag: function(shape, knob, e) {
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      var self = this;
      try { knob.setPointerCapture(e.pointerId); } catch (_) {}
      knob.classList.add("is-dragging");
      this._hideTooltip();
      this._suspendMidpointHighlight();
      var ed = this._textEditor;
      if (ed && ed.shape === shape) ed.resizing = true;
      var historyBefore = this._snapshotShape(shape);
      var c = this._rotCenter(shape);
      var box = this._isBoxRotatable(shape);
      var startRot = this._shapeRot(shape);
      var startTitleRot = (shape.style && typeof shape.style.title_rotation === "number") ? shape.style.title_rotation : 0;
      var startTurn = this._shapeTurn(shape);
      var startGeom = JSON.parse(JSON.stringify(shape.geometry));
      var p0 = this._toImage(e);
      var a0 = Math.atan2(p0.y - c.y, p0.x - c.x);
      var turned = false;
      if (this.svg) this.svg.classList.add("is-rotating");
      function onMove(ev) {
        var p = self._toImage(ev);
        var delta = (Math.atan2(p.y - c.y, p.x - c.x) - a0) * 180 / Math.PI;
        var next = startRot + delta;
        if (ev.shiftKey) next = Math.round(next / 15) * 15;
        next = ((next % 360) + 540) % 360 - 180;
        if (Math.abs(next) < 0.01) next = 0;
        turned = true;
        if (box) {
          shape.style = Object.assign({}, shape.style || {}, { rotation: next });
        } else {
          shape.geometry = self._rotateGeometry(shape.kind, startGeom, c, next - startRot);
          // How far it has turned in all, so its anchors turn with it (see
          // `_anchorFrame`).
          var tn = ((startTurn + next) % 360 + 540) % 360 - 180;
          shape.style = Object.assign({}, shape.style || {}, { turn: Math.abs(tn) < 0.01 ? 0 : tn });
          // Its label turns along (see `_labelRot`).
          if (!self._labelRidesShaft(shape.kind)) {
            var tr = ((startTitleRot + next) % 360 + 540) % 360 - 180;
            shape.style = Object.assign({}, shape.style || {}, { title_rotation: Math.abs(tr) < 0.01 ? 0 : tr });
          }
        }
        self._renderShape(shape);
        self._positionAllHandles(shape);
      }
      function onUp(ev) {
        knob.classList.remove("is-dragging");
        knob.removeEventListener("pointermove", onMove);
        knob.removeEventListener("pointerup", onUp);
        knob.removeEventListener("pointercancel", onUp);
        try { knob.releasePointerCapture(ev.pointerId); } catch (_) {}
        if (self.svg) self.svg.classList.remove("is-rotating");
        self._resumeMidpointHighlight();
        if (ed && self._textEditor === ed) {
          setTimeout(function() {
            if (self._textEditor !== ed) return;
            ed.resizing = false;
            try { ed.input.focus({ preventScroll: true }); } catch (_) {}
          }, 0);
        }
        if (turned && shape.uuid) {
          self._emitChanged();
          self._pushUndo(shape.uuid, historyBefore, self._snapshotShape(shape));
        }
        self._showTooltipFor(shape);
      }
      knob.addEventListener("pointermove", onMove);
      knob.addEventListener("pointerup", onUp);
      knob.addEventListener("pointercancel", onUp);
    },

    // A text box scaled as one: the box and its text (and its corners)
    // grow or shrink together, the top-left corner holding still.
    _startTextboxScaleDrag: function(shape, dot, e) {
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      var self = this;
      try { dot.setPointerCapture(e.pointerId); } catch (_) {}
      dot.classList.add("is-dragging");
      this._hideTooltip();
      this._suspendMidpointHighlight();
      var historyBefore = this._snapshotShape(shape);
      var g0 = JSON.parse(JSON.stringify(shape.geometry));
      var st0 = Object.assign({}, shape.style || {});
      var rot = this._shapeRot(shape);
      var c0 = this._rotCenter(shape);
      var scaled = false;
      function onMove(ev) {
        var p = self._toImage(ev);
        if (rot) p = self._rotatePoint(p, c0.x, c0.y, -rot);
        var dx = p.x - g0.x, dy = p.y - g0.y;
        var k = (dx * g0.w + dy * g0.h) / (g0.w * g0.w + g0.h * g0.h);
        k = Math.max(0.1, k);
        scaled = true;
        shape.geometry = Object.assign({}, g0, { w: g0.w * k, h: g0.h * k });
        var patch = {};
        if (st0.font_size > 0) patch.font_size = st0.font_size * k;
        if (st0.corner_size > 0) patch.corner_size = st0.corner_size * k;
        shape.style = Object.assign({}, st0, patch);
        if (rot) {
          // Held at the top-left corner as it is on screen.
          var tl0 = self._rotatePoint({ x: g0.x, y: g0.y }, c0.x, c0.y, rot);
          var g = shape.geometry;
          var cn = { x: g.x + g.w / 2, y: g.y + g.h / 2 };
          var tl1 = self._rotatePoint({ x: g.x, y: g.y }, cn.x, cn.y, rot);
          shape.geometry = Object.assign({}, g, { x: g.x + tl0.x - tl1.x, y: g.y + tl0.y - tl1.y });
        }
        self._renderShape(shape);
        self._positionAllHandles(shape);
        self._syncPointSize();
      }
      function onUp(ev) {
        dot.classList.remove("is-dragging");
        dot.removeEventListener("pointermove", onMove);
        dot.removeEventListener("pointerup", onUp);
        dot.removeEventListener("pointercancel", onUp);
        try { dot.releasePointerCapture(ev.pointerId); } catch (_) {}
        self._resumeMidpointHighlight();
        if (scaled && shape.uuid) {
          self._emitChanged();
          self._pushUndo(shape.uuid, historyBefore, self._snapshotShape(shape));
        }
        self._syncStyleInspector();
        self._showTooltipFor(shape);
      }
      dot.addEventListener("pointermove", onMove);
      dot.addEventListener("pointerup", onUp);
      dot.addEventListener("pointercancel", onUp);
    },

    // Render a ghost handle at the midpoint of each edge (polygon) or
    // segment (arrow). The dots are invisible until hovered — the user sees
    // their shape's color "appear" along the edge — and pointer-events: all
    // keeps them hit-targetable even while invisible. Drag one → it becomes
    // a real vertex, or a real bend, via `_startMidpointDrag`.
    _renderMidpointHandles: function(shape) {
      this._removeMidpointHandles();
      if (!shape || (shape.kind !== "polygon" && shape.kind !== "arrow")) return;
      var mids = this._midpointPositionsForShape(shape);
      if (!mids.length) return;
      var self = this;
      var handleColor = self._handleColor(shape);
      this.midpointHandles = [];
      for (var i = 0; i < mids.length; i++) {
        var midImage = mids[i];
        var h = svgEl("circle", { r: 6 });
        h.classList.add("etcher-handle", "etcher-handle-midpoint");
        h.style.color = handleColor;
        h.dataset.edgeIndex = i;
        self.svg.appendChild(h);
        (function(edgeIdx, handleEl) {
          var onDown = function(e) {
            self._startMidpointDrag(shape, edgeIdx, handleEl, e);
          };
          handleEl.addEventListener("pointerdown", onDown);
          self._addGrabHalo(handleEl, HANDLE_GRAB_RADIUS, onDown);
        })(i, h);
        self._positionHandle(h, midImage);
        this.midpointHandles.push(h);
      }
    },

    _removeMidpointHandles: function() {
      var self = this;
      (this.midpointHandles || []).forEach(function(h) { self._dropHandleEl(h); });
      this.midpointHandles = [];
    },

    // Edge-midpoint handles for a rectangle in edit mode: one dot at
    // the center of each of the four sides. Grabbing one slides that
    // edge — the two corners on that side travel with the drag while
    // the opposite edge stays anchored. Reuses the same shared
    // `midpointHandles` array so the closest-to-cursor highlight,
    // pan/zoom positioning, and `_updateClosestMidpoint` machinery
    // work without per-kind branching at the call sites.
    _renderRectEdgeHandles: function(shape, part) {
      this._removeMidpointHandles();
      // `part` "label": a shape's point label in title-edit mode.
      var point = part === "label" || this._isPointFrame(shape);
      if (!shape || (shape.kind !== "rectangle" && !point && !this._textboxFixed(shape))) return;
      var g = point ? this._pointHandleBox(shape, part) : shape.geometry;
      var positions = this._rectEdgeMidpoints(g);
      positions = part === "label" ? this._labelRotPts(shape, positions) : this._rotPts(shape, positions);
      var self = this;
      var handleColor = self._handleColor(shape);
      this.midpointHandles = positions.map(function(pt, idx) {
        var horizontal = idx === 0 || idx === 2;
        // 18 long, 4.2 thick — slim bars along the side, not chunky pills.
        var w = horizontal ? 18 : 4.2;
        var hgt = horizontal ? 4.2 : 18;
        var rect = svgEl("rect", { width: w, height: hgt, rx: 1.5, ry: 1.5 });
        rect.classList.add(
          "etcher-handle",
          "etcher-handle-edge",
          horizontal ? "etcher-handle-edge--h" : "etcher-handle-edge--v"
        );
        rect.style.color = point ? POINT_HANDLE_COLOR : handleColor;
        rect.dataset.edgeIndex = idx;
        // Point text's grabbers wear its corner dots' black and white; they
        // light up when the cursor nears their side (`_updateClosestMidpoint`).
        if (point) rect.classList.add("is-point");
        self.svg.appendChild(rect);
        self._positionHandle(rect, pt);
        (function(edgeIdx, handleEl) {
          handleEl.addEventListener("pointerdown", function(e) {
            self._startRectEdgeDrag(shape, edgeIdx, handleEl, e, part);
          });
        })(idx, rect);
        return rect;
      });
      if (point) this._syncPointEdgeRoom(shape);
      this._turnEdgeHandles(shape, this.midpointHandles, part === "label" ? this._labelRot(shape) : undefined);
    },

    // A turned shape's side grabbers lie along its turned sides. Inline, so
    // it holds through the hover/drag scale the stylesheet gives them.
    _turnEdgeHandles: function(shape, handles, rotOverride) {
      var rot = rotOverride != null ? rotOverride : (this._shapeRot ? this._shapeRot(shape) : 0);
      (handles || []).forEach(function(h) {
        if (!h || !h.classList || !h.classList.contains("etcher-handle-edge")) return;
        h.style.transform = rot ? "rotate(" + rot + "deg)" : "";
      });
    },

    // Image-px midpoints of a rect's 4 sides. Order: top, right,
    // bottom, left. Used both at handle-creation time and by the
    // closest-midpoint highlight + pan/zoom reposition.
    _rectEdgeMidpoints: function(g) {
      return [
        { x: g.x + g.w / 2, y: g.y           }, // 0: top
        { x: g.x + g.w,     y: g.y + g.h / 2 }, // 1: right
        { x: g.x + g.w / 2, y: g.y + g.h     }, // 2: bottom
        { x: g.x,           y: g.y + g.h / 2 }  // 3: left
      ];
    },

    // Drag a single rect edge. The opposite edge stays put; the two
    // corners on the grabbed edge slide along the perpendicular
    // axis. Normalizes negatives so a user dragging an edge past
    // its opposite still produces a sane rectangle.
    _startRectEdgeDrag: function(shape, edgeIdx, handleEl, e, part) {
      // Left button only. These handles sit above the canvas and swallow
      // the event, so without this a middle-press on one would start a
      // drag AND block the middle-drag pan from ever reaching Fresco.
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      try { handleEl.setPointerCapture(e.pointerId); } catch (_) {}
      handleEl.classList.add("is-dragging");
      this._hideTooltip();

      var self = this;
      var historyBefore = self._snapshotShape(shape);
      var g0 = JSON.parse(JSON.stringify(shape.geometry));
      var pointStart = part === "label" ? self._pointTextDragStart(shape, "label")
        : self._isPointFrame(shape) ? self._pointTextDragStart(shape) : null;
      var EDGE_NAMES = ["top", "right", "bottom", "left"];
      var startTitleBox =
        shape.metadata && shape.metadata.title_box
          ? Object.assign({}, shape.metadata.title_box)
          : null;

      var edgeRot = part === "label" ? self._labelRot(shape) : self._shapeRot(shape);
      var edgeC0 = null;
      if (edgeRot && part === "label") {
        var eb = pointStart.box;
        edgeC0 = { x: eb.x + eb.w / 2, y: eb.y + eb.h / 2 };
      } else if (edgeRot) {
        edgeC0 = self._rotCenter(shape);
      }
      function onMove(ev) {
        var pt = self._toImage(ev);
        if (edgeRot) pt = self._rotatePoint(pt, edgeC0.x, edgeC0.y, -edgeRot);
        if (pointStart) {
          self._resizePointText(shape, pointStart, EDGE_NAMES[edgeIdx], pt, !!ev.shiftKey);
          if (edgeRot && part === "label") self._recenterLabel(shape, edgeC0, edgeRot);
          else if (edgeRot) self._recenterRotated(shape, edgeC0, edgeRot);
          self._renderShape(shape);
          self._positionPointHandles(shape);
          self._syncPointSize();
          return;
        }
        var nx = g0.x, ny = g0.y, nw = g0.w, nh = g0.h;
        switch (edgeIdx) {
          case 0: ny = pt.y;        nh = (g0.y + g0.h) - pt.y; break; // top
          case 1: nw = pt.x - g0.x; break;                             // right
          case 2: nh = pt.y - g0.y; break;                             // bottom
          case 3: nx = pt.x;        nw = (g0.x + g0.w) - pt.x; break; // left
        }
        if (nw < 0) { nx += nw; nw = -nw; }
        if (nh < 0) { ny += nh; nh = -nh; }
        shape.geometry = Object.assign({}, g0, { x: nx, y: ny, w: nw, h: nh });
        if (edgeRot) self._recenterRotated(shape, edgeC0, edgeRot);
        self._renderShape(shape);
        self._positionAllHandles(shape);
      }
      function onUp(ev) {
        handleEl.classList.remove("is-dragging");
        handleEl.removeEventListener("pointermove", onMove);
        handleEl.removeEventListener("pointerup", onUp);
        handleEl.removeEventListener("pointercancel", onUp);
        try { handleEl.releasePointerCapture(ev.pointerId); } catch (_) {}
        if (shape.uuid) {
          self._emitChanged();
          self._pushUndo(shape.uuid, historyBefore, self._snapshotShape(shape));
        }
        if (part === "label") { self._syncLabelSection(); return; }
        self._showTooltipFor(shape);
      }
      handleEl.addEventListener("pointermove", onMove);
      handleEl.addEventListener("pointerup", onUp);
      handleEl.addEventListener("pointercancel", onUp);
    },

    // The point frame whose side grabbers are up, if any: a label in
    // title-edit mode, or point text / a point callout in edit mode.
    _pointEdgeTarget: function() {
      if (this.editingTitleShape && this._isPointLabel(this.editingTitleShape)) {
        return { shape: this.editingTitleShape, part: "label" };
      }
      if (this.editingShape && this._isPointFrame(this.editingShape)) {
        return { shape: this.editingShape, part: this._pointPart(this.editingShape) };
      }
      return null;
    },

    // Mark the midpoint closest to the cursor as `.is-active` so its
    // CSS rule reveals it. Threshold gates the highlight: if the
    // cursor is far away from every midpoint the polygon's edges
    // stay clean. Hidden during a drag — the dragging handle already
    // has `.is-dragging` and tracks the pointer directly.
    _updateClosestMidpoint: function(pt) {
      if (this._midpointHighlightSuspended) return;
      if (!this.midpointHandles || !this.midpointHandles.length) return;
      var shape = this.editingShape;
      var self = this;
      var threshold = this._midpointActivationRadiusImagePx();
      // Point text's grabbers wait until the cursor is at their side, not
      // anywhere within reach: all four lit at once is too much on a word.
      var pointTarget = this._pointEdgeTarget();
      if (pointTarget) {
        shape = pointTarget.shape;
        threshold = threshold * POINT_EDGE_REACH_PX / 80;
      }
      var closestIdx = -1;
      var closestDist = Infinity;

      // Arrows measure to the whole segment, not to its midpoint: on a long
      // leg the middle is far away, so midpoint-distance meant you could sit
      // exactly on the line and still see nothing. (A polygon's edges are
      // short enough that the distinction doesn't arise.)
      //
      // The dot still SITS at the midpoint. Sliding it along to meet the
      // cursor was the obvious next step and is wrong: it then wanders over
      // the bend handles at either end of the segment and covers the very
      // points you were reaching for. Fixed at the middle, the two never
      // collide — existing bends stay grabbable, and the middle is where a
      // new one gets added.
      var segments = pointTarget
        ? this._pointEdgeSegments(pointTarget.shape, pointTarget.part)
        : this._midpointSegmentsForShape(shape);
      if (segments.length) {
        segments.forEach(function(seg, i) {
          var near = self._nearestOnSegment(pt, seg.p, seg.q);
          var ex = pt.x - near.x, ey = pt.y - near.y;
          var d2 = ex * ex + ey * ey;
          if (d2 < closestDist) { closestDist = d2; closestIdx = i; }
        });
      } else {
        var positions = this._midpointPositionsForShape(shape);
        if (!positions || !positions.length) return;
        for (var i = 0; i < positions.length; i++) {
          var dx = pt.x - positions[i].x;
          var dy = pt.y - positions[i].y;
          var pd2 = dx * dx + dy * dy;
          if (pd2 < closestDist) { closestDist = pd2; closestIdx = i; }
        }
      }
      if (closestDist > threshold * threshold) closestIdx = -1;

      this.midpointHandles.forEach(function(h, i) {
        h.classList.toggle("is-active", i === closestIdx);
      });
    },

    // Segments a shape measures cursor proximity against, as `{p, q}` pairs
    // in image px. Only arrows: a polygon's edges are short enough that the
    // distance to an edge and the distance to its midpoint amount to the
    // same thing, and its own gate stays on the midpoint.
    _midpointSegmentsForShape: function(shape) {
      if (shape && this._isPointFrame(shape)) return this._pointEdgeSegments(shape);
      if (!shape || shape.kind !== "arrow") return [];
      var path = this._arrowPath(shape.geometry);
      var segs = [];
      for (var i = 0; i < path.length - 1; i++) {
        segs.push({ p: path[i], q: path[i + 1] });
      }
      return segs;
    },

    // A point frame's box sides, in handle order (top, right, bottom,
    // left), so the grabber lights anywhere along its side.
    _pointEdgeSegments: function(shape, part) {
      var b = this._pointHandleBox(shape, part);
      var c = [{ x: b.x, y: b.y }, { x: b.x + b.w, y: b.y }, { x: b.x + b.w, y: b.y + b.h }, { x: b.x, y: b.y + b.h }];
      if (part === "label") { if (this._labelRotPts) c = this._labelRotPts(shape, c); }
      else if (this._rotPts) c = this._rotPts(shape, c);
      return [{ p: c[0], q: c[1] }, { p: c[1], q: c[2] }, { p: c[2], q: c[3] }, { p: c[3], q: c[0] }];
    },

    // Image-px positions of every midpoint a shape currently
    // exposes. Polygons → edge midpoints (one per edge). Rectangles
    // → four edge midpoints (top/right/bottom/left). Arrows → the
    // midpoint of each segment of the routed path. Other kinds
    // return [].
    _midpointPositionsForShape: function(shape) {
      if (!shape) return [];
      if (shape.kind === "polygon") {
        var pts = (shape.geometry && shape.geometry.points) || [];
        var out = [];
        for (var i = 0; i < pts.length; i++) {
          var next = pts[(i + 1) % pts.length];
          out.push({ x: (pts[i][0] + next[0]) / 2, y: (pts[i][1] + next[1]) / 2 });
        }
        return out;
      }
      if (shape.kind === "rectangle" || this._textboxFixed(shape)) {
        return this._rotPts(shape, this._rectEdgeMidpoints(shape.geometry));
      }
      if (this._isPointFrame(shape)) {
        return this._rotPts(shape, this._rectEdgeMidpoints(this._pointHandleBox(shape)));
      }
      // Open path, so segments rather than a closed ring — no wrap from the
      // head back round to the tail.
      if (shape.kind === "arrow") {
        var path = this._arrowPath(shape.geometry);
        var mids = [];
        for (var j = 0; j < path.length - 1; j++) {
          // ON the drawn curve, which may bow away from the chord — a dot
          // floating beside the line reads as broken. One per raw segment,
          // same indexing the insertion math relies on.
          mids.push(this._arrowSegMidImage(shape.geometry, j));
        }
        return mids;
      }
      return [];
    },

    // Convert a generous container-px radius (~80px on screen) into
    // image px so the activation zone feels the same regardless of
    // zoom. Generous because the user only needs to be "near" an
    // edge, not directly on it.
    _midpointActivationRadiusImagePx: function() {
      try {
        var perImagePx = this._markerScale();
        return 80 / perImagePx;
      } catch (e) {
        return 80;
      }
    },

    // Kinds that expose midpoint ghosts while in edit mode. One list so the
    // hover tracker, the draft-path highlight and the render can't disagree
    // about which shapes have them — a mismatch shows dots nothing updates,
    // or updates dots that were never drawn.
    _hasMidpointHandles: function(kind) {
      // "text": point text's side grabbers. A text box without them has no
      // midpointHandles, which `_updateClosestMidpoint` already bails on.
      return kind === "polygon" || kind === "rectangle" || kind === "arrow" ||
        kind === "text" || kind === "callout";
    },

    _clearClosestMidpoint: function() {
      (this.midpointHandles || []).forEach(function(h) {
        h.classList.remove("is-active");
      });
    },

    // Stop the "add a point here" dots reacting to the pointer, and drop any
    // that are already lit. Held for the length of a drag — see the call in
    // `_startHandleDrag`. Paired with `_resumeMidpointHighlight`.
    _suspendMidpointHighlight: function() {
      this._midpointHighlightSuspended = true;
      this._clearClosestMidpoint();
    },

    _resumeMidpointHighlight: function() {
      this._midpointHighlightSuspended = false;
    },

    _positionAllMidpointHandles: function(shape) {
      if (!this.midpointHandles || !this.midpointHandles.length) return;
      var positions = this._midpointPositionsForShape(shape);
      var self = this;
      this.midpointHandles.forEach(function(h, i) {
        if (positions[i]) self._positionHandle(h, positions[i]);
      });
      this._turnEdgeHandles(shape, this.midpointHandles);
      this._syncPointEdgeRoom(shape);
      this._syncGrabRadii(this.handles, this.midpointHandles);
    },

    // An edge grabber on point text needs a side long enough to sit between
    // its two corner dots without covering them; on a shorter side it steps
    // aside (the corners still do everything) and comes back as the text
    // grows.
    _syncPointEdgeRoom: function(shape) {
      if (!this.midpointHandles) return;
      var label = this.editingTitleShape === shape && this._isPointLabel(shape);
      if (!label && !this._isPointFrame(shape)) return;
      var b = this._pointHandleBox(shape, label ? "label" : null);
      var scale = 1;
      try { scale = this._markerScale() || 1; } catch (_) { scale = 1; }
      var wPx = b.w * scale, hPx = b.h * scale;
      this.midpointHandles.forEach(function(h, i) {
        var side = (i === 0 || i === 2) ? wPx : hPx;
        h.classList.toggle("is-cramped", side < POINT_EDGE_MIN_PX);
      });
    },

    // Insert a new point at the midpoint under the ghost handle — a vertex
    // on a polygon, a bend on an arrow — then run a drag so the user can
    // place it immediately. Pre-insert state goes onto the undo stack so ⌘Z
    // removes the inserted point entirely.
    _startMidpointDrag: function(shape, edgeIdx, handleEl, e) {
      // Left button only. These handles sit above the canvas and swallow
      // the event, so without this a middle-press on one would start a
      // drag AND block the middle-drag pan from ever reaching Fresco.
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      try { handleEl.setPointerCapture(e.pointerId); } catch (_) {}
      handleEl.classList.add("is-dragging");
      this._hideTooltip();
      // The dot being dragged is becoming a real point; the others must stop
      // offering themselves while that happens. `.is-dragging` keeps this one
      // visible regardless of the highlight.
      this._suspendMidpointHighlight();

      var self = this;
      var isArrow = shape.kind === "arrow";
      // Inserting shifts every index >= newIdx by one; drop any prior vertex
      // selection to avoid the highlight landing on the wrong dot after the
      // splice.
      self._clearVertexSelection();
      var historyBefore = self._snapshotShape(shape);

      // Where the new point lands in whichever list owns it, and how to
      // write that list back. An arrow's bends live in `geometry.points`
      // alongside `a`/`b`/`from`/`to`, which all have to survive; a polygon's
      // geometry IS its point list. `edgeIdx` counts segments, and segment N
      // runs from path point N to N+1 — so on an arrow, whose path is
      // `[a, ...bends, b]`, a bend inserted into segment N becomes bend N.
      var writePoints = isArrow
        ? function(pts) {
            var g = shape.geometry;
            shape.geometry = {
              a: [g.a[0], g.a[1]], b: [g.b[0], g.b[1]],
              points: pts,
              from: g.from || null, to: g.to || null
            };
          }
        : function(pts) { shape.geometry = { points: pts }; };

      var listPoints = function() {
        return isArrow
          ? (shape.geometry.points || []).map(function(p) { return [p[0], p[1]]; })
          : shape.geometry.points.slice();
      };

      var mid = self._midpointPositionsForShape(shape)[edgeIdx];
      var pts = listPoints();
      var newIdx;
      if (isArrow) {
        newIdx = edgeIdx;
      } else {
        var pa = pts[edgeIdx];
        var pb = pts[(edgeIdx + 1) % pts.length];
        newIdx = edgeIdx + 1;
        mid = { x: (pa[0] + pb[0]) / 2, y: (pa[1] + pb[1]) / 2 };
      }
      pts.splice(newIdx, 0, [mid.x, mid.y]);
      writePoints(pts);
      self._renderShape(shape);
      // Reposition existing handles to account for the new index shift;
      // midpoint handles stay where they are until the gesture ends, when we
      // re-render the full set.
      self._positionAllHandles(shape);

      function onMove(ev) {
        var pt = self._toImage(ev);
        var newPts = listPoints();
        newPts[newIdx] = [pt.x, pt.y];
        writePoints(newPts);
        self._renderShape(shape);
        self._positionAllHandles(shape);
        // Position the dragging dot itself (it's the same DOM element
        // the user grabbed, just tracking the new point now).
        self._positionHandle(handleEl, { x: pt.x, y: pt.y });
      }
      function onUp(ev) {
        handleEl.classList.remove("is-dragging");
        handleEl.removeEventListener("pointermove", onMove);
        handleEl.removeEventListener("pointerup", onUp);
        handleEl.removeEventListener("pointercancel", onUp);
        try { handleEl.releasePointerCapture(ev.pointerId); } catch (_) {}
        if (shape.uuid) {
          self._emitChanged();
          self._pushUndo(shape.uuid, historyBefore, self._snapshotShape(shape));
        }
        self._resumeMidpointHighlight();
        // Refresh the full handle set so the new vertex picks up a
        // real vertex dot and the two new edges get their own
        // midpoint ghosts.
        if (self.editingShape === shape) self._renderHandles(shape);
        self._showTooltipFor(shape);
      }
      handleEl.addEventListener("pointermove", onMove);
      handleEl.addEventListener("pointerup", onUp);
      handleEl.addEventListener("pointercancel", onUp);
    },

    // Resolve the color that vector handles should paint themselves
    // with for `shape`. Picks the explicitly-styled color first,
    // falls back to the in-progress active swatch (for drafts) and
    // finally to the same default blue the shape stroke uses, so a
    // shape that's never had a custom color picked still has matching
    // handles instead of an unrelated orange.
    _handleColor: function(shape) {
      // Point text wears black handles whatever its colour, as Affinity's
      // do: they are the tool's controls, not part of the drawing, and a
      // red text's red dots disappeared into it.
      if (this._isPointFrame(shape)) return POINT_HANDLE_COLOR;
      // A text box's handles wear its text's colour.
      if (shape && shape.kind === "text" && this._textboxFixed(shape)) {
        var ink = this._titleColorFor(shape);
        if (ink) return ink;
      }
      if (shape && shape.style && shape.style.color) return shape.style.color;
      if (this.activeColor) return this.activeColor;
      return "#3b82f6";
    },

    // Returns the currently-in-progress draft shape as a shape-like
    // object suitable for `_handlePositions` — unifying the rectangle/
    // circle/freehand draftState and the polygon draftPolygon code
    // paths so renderers can treat them the same way.
    _draftActive: function() {
      if (this.draftState) return this.draftState;
      if (this.draftPolygon) {
        return {
          kind: "polygon",
          geometry: { points: this.draftPolygon.points },
          el: this.draftPolygon.el
        };
      }
      if (this.draftCallout) return this.draftCallout;
      return null;
    },

    _syncDraftHandles: function() {
      var d = this._draftActive();
      if (!d) {
        if (!this.editingShape) this._removeHandles();
        return;
      }
      // A draft waiting for its second click shows only its preview. Its
      // dots sat on the first click as a dot of their own, which read as
      // a stray mark; they come with the finished shape instead. (A
      // press-drag keeps them — there the box is being pulled by them.)
      // Nor does a press that hasn't moved yet — it may be the first of two
      // clicks, and dots raised on the press and dropped on the release
      // flashed for a moment on every click. They come once the press has
      // travelled far enough to be a drag (see `_onPointerMove`).
      if (d.armed || (d.kind !== "polygon" && !d.pulled)) {
        if (!this.editingShape) this._removeHandles();
        return;
      }
      // Recreate (rather than reposition) because polygon clicks grow
      // the vertex count between calls. Cheap enough — drafts cap at a
      // few dozen vertices and `_renderAll`'s per-frame path uses
      // `_positionAllHandles` instead.
      this._renderHandles(d, { interactive: false });
    },

    _positionAllHandles: function(shape) {
      // The card's `⋯` rides its top-right corner, so it moves with every
      // pan, zoom and resize the handles do.
      this._positionLinkMenu();
      if (this.handles && this.handles.length) {
        var positions = this._handlePositions(shape);
        var self = this;
        this.handles.forEach(function(h, idx) {
          if (positions[idx]) self._positionHandle(h, positions[idx]);
        });
        // They just moved, so the gaps between them changed.
        this._syncGrabRadii(this.handles, this.midpointHandles);
      }
      // Midpoints aren't part of `_handlePositions` (they aren't
      // editable vertices), so keep them in sync on pan/zoom via
      // a dedicated path.
      this._positionAllMidpointHandles(shape);
      this._positionExtraHandles(shape);
      // Freehand pen-editor anchors/handles live outside the generic
      // `handles` array — reposition them on the same pan/zoom tick.
      if (this.freehandEditor) this._positionFreehandEditor();
    },


    // Pair `h` with the invisible disc that gives a fingertip something to
    // land on. Inserted BEFORE the dot so the dot still paints on top and
    // still takes the press when the pointer is precise enough to hit it;
    // `onDown` is the dot's own handler, so a grab through the disc starts
    // exactly the same drag, `.is-dragging` and all.
    _addGrabHalo: function(h, radius, onDown) {
      if (!h || h._grab) return h;
      var halo = svgEl("circle", { r: radius || HANDLE_GRAB_RADIUS });
      halo.classList.add("etcher-grab");
      if (h.parentNode) h.parentNode.insertBefore(halo, h);
      else this.svg.appendChild(halo);
      h._grab = halo;
      if (onDown) halo.addEventListener("pointerdown", onDown);
      return h;
    },

    // Remove a handle and the disc that came with it. Every teardown path
    // goes through here so a halo can never be orphaned on the canvas —
    // invisible, but still taking presses.
    _dropHandleEl: function(el) {
      if (!el) return;
      if (el._grab) {
        if (el._grab.parentNode) el._grab.parentNode.removeChild(el._grab);
        el._grab = null;
      }
      if (el.parentNode) el.parentNode.removeChild(el);
    },

    // Size every zone in a group against the gap to its nearest neighbour,
    // and re-do it whenever they move.
    //
    // The measure is the distance between HANDLES, not the size of the
    // shape: a horizontal dimension is a zero-height box whose two ends are
    // half the picture apart, so judging by the box would have handed the
    // case that most needs a big target the smallest one going. Half the
    // closest gap means neighbouring zones meet without overlapping —
    // whichever handle is nearer is the one you get — and the clamp keeps
    // that between "big enough for a fingertip" and "a landing pad".
    //
    // Groups that compete for the same finger are passed in together: a
    // midpoint sits between two corners, so sizing them apart would let a
    // corner's zone swallow it.
    _syncGrabRadii: function() {
      var els = [];
      for (var a = 0; a < arguments.length; a++) {
        (arguments[a] || []).forEach(function(h) {
          if (h && h._grab && h._grab.parentNode) els.push(h._grab);
        });
      }
      if (!els.length) return;
      var pts = els.map(function(g) {
        return { x: parseFloat(g.getAttribute("cx")) || 0,
                 y: parseFloat(g.getAttribute("cy")) || 0 };
      });
      var min = Infinity;
      for (var i = 0; i < pts.length; i++) {
        for (var j = i + 1; j < pts.length; j++) {
          var dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y;
          var d = Math.sqrt(dx * dx + dy * dy);
          if (d < min) min = d;
        }
      }
      var r = min === Infinity
        ? HANDLE_GRAB_RADIUS
        : Math.max(HANDLE_GRAB_RADIUS_MIN,
                   Math.min(HANDLE_GRAB_RADIUS, min / 2));
      els.forEach(function(g) { g.setAttribute("r", r); });
    },

    _positionHandle: function(h, imagePt) {
      var c = this._imageToContainer(imagePt);
      // Circles use cx/cy; rect-shaped edge handles position by their
      // top-left so we offset by half their dimensions to keep them
      // centered on the supplied image point.
      if (h.tagName && h.tagName.toLowerCase() === "rect") {
        var w = parseFloat(h.getAttribute("width")) || 0;
        var hgt = parseFloat(h.getAttribute("height")) || 0;
        h.setAttribute("x", c.x - w / 2);
        h.setAttribute("y", c.y - hgt / 2);
      } else {
        h.setAttribute("cx", c.x);
        h.setAttribute("cy", c.y);
      }
      if (h._grab) {
        h._grab.setAttribute("cx", c.x);
        h._grab.setAttribute("cy", c.y);
      }
    },

    _removeHandles: function() {
      var self = this;
      (this.handles || []).forEach(function(h) { self._dropHandleEl(h); });
      this.handles = [];
      this._removeMidpointHandles();
      this._removeExtraHandles();
    },

    // -----------------------------------------------------------------------
    // Freehand node editor — the pen-tool surface for a fitted vector curve.
    // Each node gets a draggable anchor plus (where present) its two bezier
    // control handles, drawn as small dots tethered to the anchor by a thin
    // line. Dragging an anchor slides the node and carries its handles along
    // (they're stored as offsets); dragging a control handle reshapes the
    // curve, mirroring the opposite handle when the node is "smooth".
    // Double-click an anchor toggles smooth/corner. Lives parallel to the
    // generic `handles` array because a bezier node has more than one
    // editable point, so the flat index-by-position model doesn't fit.
    // -----------------------------------------------------------------------

    _vLen: function(v) { return Math.sqrt(v[0] * v[0] + v[1] * v[1]); },

    _renderFreehandEditor: function(shape) {
      this._removeFreehandEditor();
      if (!shape || shape.readonly ||
          (shape.kind !== "freehand" && shape.kind !== "marker") ||
          !shape.geometry || !shape.geometry.nodes) {
        return;
      }
      var self = this;
      this._activateOverlayForShape(shape);
      var nodes = shape.geometry.nodes;
      var color = this._handleColor(shape);
      var ed = { shape: shape, controls: [], lines: [] };

      nodes.forEach(function(node, i) {
        // Control handles (drawn first so the anchor dot sits on top).
        ["in", "out"].forEach(function(side) {
          if (!(side === "in" ? node.hIn : node.hOut)) return;
          var line = svgEl("line", {});
          line.classList.add("etcher-handle-line");
          line.style.color = color;
          self.svg.appendChild(line);
          ed.lines.push({ el: line, nodeIdx: i, side: side });

          var dot = svgEl("circle", { r: 4 });
          dot.classList.add("etcher-handle", "etcher-bezier-handle");
          dot.style.color = color;
          self.svg.appendChild(dot);
          ed.controls.push({ el: dot, type: side, nodeIdx: i });
          var onDotDown = function(e) {
            self._startBezierHandleDrag(shape, i, side, dot, e);
          };
          dot.addEventListener("pointerdown", onDotDown);
          self._addGrabHalo(dot, HANDLE_GRAB_RADIUS, onDotDown);
        });

        var anchor = svgEl("circle", { r: 5 });
        anchor.classList.add("etcher-handle", "etcher-anchor-handle");
        if (node.type === "corner") anchor.classList.add("is-corner");
        anchor.style.color = color;
        self.svg.appendChild(anchor);
        ed.controls.push({ el: anchor, type: "anchor", nodeIdx: i });
        var onAnchorDown = function(e) {
          self._startAnchorDrag(shape, i, anchor, e);
        };
        anchor.addEventListener("pointerdown", onAnchorDown);
        self._addGrabHalo(anchor, HANDLE_GRAB_RADIUS, onAnchorDown);
        anchor.addEventListener("dblclick", function(e) {
          e.preventDefault();
          e.stopPropagation();
          self._toggleNodeType(shape, i);
        });
      });

      this.freehandEditor = ed;
      this._positionFreehandEditor();
      // Re-apply any node selection so the red highlight survives rebuilds
      // (after an add/delete/toggle or an undo/redo).
      this._refreshVertexSelectionClass();
    },

    _positionFreehandEditor: function() {
      var ed = this.freehandEditor;
      if (!ed) return;
      var self = this;
      var nodes = (ed.shape.geometry && ed.shape.geometry.nodes) || [];
      ed.lines.forEach(function(l) {
        var node = nodes[l.nodeIdx];
        var h = node && (l.side === "in" ? node.hIn : node.hOut);
        if (!h) { l.el.style.display = "none"; return; }
        l.el.style.display = "";
        var a = self._imageToContainer({ x: node.p[0], y: node.p[1] });
        var b = self._imageToContainer({ x: node.p[0] + h[0], y: node.p[1] + h[1] });
        l.el.setAttribute("x1", a.x); l.el.setAttribute("y1", a.y);
        l.el.setAttribute("x2", b.x); l.el.setAttribute("y2", b.y);
      });
      ed.controls.forEach(function(c) {
        var node = nodes[c.nodeIdx];
        if (!node) return;
        var h = c.type === "in" ? node.hIn : c.type === "out" ? node.hOut : null;
        if (c.type !== "anchor" && !h) { c.el.style.display = "none"; return; }
        c.el.style.display = "";
        var pt = c.type === "anchor"
          ? { x: node.p[0], y: node.p[1] }
          : { x: node.p[0] + h[0], y: node.p[1] + h[1] };
        self._positionHandle(c.el, pt);
      });
      // Anchors and their bezier dots sit close together by design, so
      // they are sized as one group against each other.
      this._syncGrabRadii(ed.controls.map(function(c) { return c.el; }));
    },

    _removeFreehandEditor: function() {
      var ed = this.freehandEditor;
      if (!ed) return;
      var selfEd = this;
      ed.controls.forEach(function(c) { selfEd._dropHandleEl(c.el); });
      ed.lines.forEach(function(l) { if (l.el.parentNode) l.el.parentNode.removeChild(l.el); });
      this.freehandEditor = null;
    },

    // Drag an anchor: translate the node by the pointer delta. Its handles
    // ride along automatically because they're stored relative to the anchor.
    _startAnchorDrag: function(shape, idx, handleEl, e) {
      // Left button only. These handles sit above the canvas and swallow
      // the event, so without this a middle-press on one would start a
      // drag AND block the middle-drag pan from ever reaching Fresco.
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      try { handleEl.setPointerCapture(e.pointerId); } catch (_) {}
      handleEl.classList.add("is-dragging");
      this._hideTooltip();
      var self = this;
      var historyBefore = self._snapshotShape(shape);
      var startP = shape.geometry.nodes[idx].p.slice();
      var startPt = self._toImage(e);
      var dragged = false;
      function onMove(ev) {
        var pt = self._toImage(ev);
        if (!dragged) {
          var aC = self._imageToContainer(startPt), bC = self._imageToContainer(pt);
          if ((bC.x - aC.x) * (bC.x - aC.x) + (bC.y - aC.y) * (bC.y - aC.y) < 9) return;
          dragged = true;
        }
        shape.geometry.nodes[idx].p = [
          startP[0] + (pt.x - startPt.x),
          startP[1] + (pt.y - startPt.y)
        ];
        self._renderShape(shape);
        self._positionFreehandEditor();
      }
      function onUp(ev) {
        handleEl.classList.remove("is-dragging");
        handleEl.removeEventListener("pointermove", onMove);
        handleEl.removeEventListener("pointerup", onUp);
        handleEl.removeEventListener("pointercancel", onUp);
        try { handleEl.releasePointerCapture(ev.pointerId); } catch (_) {}
        if (dragged) {
          self._clearVertexSelection();
          if (shape.uuid) {
            self._emitChanged();
            self._pushUndo(shape.uuid, historyBefore, self._snapshotShape(shape));
          }
        } else {
          // Pure click on an anchor → select it (red) so Backspace / Delete
          // removes just this node; shift-click extends the selection set.
          self._selectVertex(shape, idx, !!ev.shiftKey);
        }
      }
      handleEl.addEventListener("pointermove", onMove);
      handleEl.addEventListener("pointerup", onUp);
      handleEl.addEventListener("pointercancel", onUp);
    },

    // Drag a bezier control handle: set this side's offset to the cursor. If
    // the node is "smooth" and the opposite handle exists, swing it to stay
    // colinear while keeping its own length — the classic smooth-node feel.
    _startBezierHandleDrag: function(shape, idx, side, handleEl, e) {
      // Left button only. These handles sit above the canvas and swallow
      // the event, so without this a middle-press on one would start a
      // drag AND block the middle-drag pan from ever reaching Fresco.
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      try { handleEl.setPointerCapture(e.pointerId); } catch (_) {}
      handleEl.classList.add("is-dragging");
      this._hideTooltip();
      var self = this;
      var historyBefore = self._snapshotShape(shape);
      var node = shape.geometry.nodes[idx];
      var thisSide = side === "in" ? "hIn" : "hOut";
      var oppSide = side === "in" ? "hOut" : "hIn";
      var startPt = self._toImage(e);
      var dragged = false;
      function onMove(ev) {
        var pt = self._toImage(ev);
        if (!dragged) {
          var aC = self._imageToContainer(startPt), bC = self._imageToContainer(pt);
          if ((bC.x - aC.x) * (bC.x - aC.x) + (bC.y - aC.y) * (bC.y - aC.y) < 9) return;
          dragged = true;
        }
        var v = [pt.x - node.p[0], pt.y - node.p[1]];
        node[thisSide] = v;
        if (node.type === "smooth" && node[oppSide]) {
          // Symmetrical: the opposite handle mirrors this one exactly —
          // equal length, opposite direction.
          node[oppSide] = [-v[0], -v[1]];
        }
        self._renderShape(shape);
        self._positionFreehandEditor();
      }
      function onUp(ev) {
        handleEl.classList.remove("is-dragging");
        handleEl.removeEventListener("pointermove", onMove);
        handleEl.removeEventListener("pointerup", onUp);
        handleEl.removeEventListener("pointercancel", onUp);
        try { handleEl.releasePointerCapture(ev.pointerId); } catch (_) {}
        if (dragged && shape.uuid) {
          self._emitChanged();
          self._pushUndo(shape.uuid, historyBefore, self._snapshotShape(shape));
        }
      }
      handleEl.addEventListener("pointermove", onMove);
      handleEl.addEventListener("pointerup", onUp);
      handleEl.addEventListener("pointercancel", onUp);
    },

    // Flip a node between smooth and corner. Going smooth re-colinearizes the
    // two existing handles around their averaged direction so the curve snaps
    // tangent-continuous; going corner just frees them to move independently.
    _toggleNodeType: function(shape, idx) {
      var node = shape.geometry.nodes[idx];
      if (!node) return;
      var historyBefore = this._snapshotShape(shape);
      if (node.type === "smooth") {
        node.type = "corner";
      } else {
        node.type = "smooth";
        if (node.hIn && node.hOut) {
          var inLen = this._vLen(node.hIn) || 1;
          var outLen = this._vLen(node.hOut) || 1;
          // Average the outgoing direction with the reversed incoming one,
          // then give both handles that direction and a single shared length
          // — a clean symmetrical (mirrored, equal-length) node.
          var ax = -node.hIn[0] / inLen + node.hOut[0] / outLen;
          var ay = -node.hIn[1] / inLen + node.hOut[1] / outLen;
          var aLen = Math.sqrt(ax * ax + ay * ay) || 1;
          ax /= aLen; ay /= aLen;
          var len = (inLen + outLen) / 2;
          node.hOut = [ax * len, ay * len];
          node.hIn = [-ax * len, -ay * len];
        }
      }
      this._renderShape(shape);
      this._renderFreehandEditor(shape);
      if (shape.uuid) {
        this._emitChanged();
        this._pushUndo(shape.uuid, historyBefore, this._snapshotShape(shape));
      }
    },

    // Insert a node on the curve at the image-px point nearest `pt` (a double-
    // click). Finds the closest segment + parameter t by sampling, bails if
    // the click isn't close enough to the curve, then splits that cubic at t
    // with De Casteljau — the split preserves the curve's exact shape, only
    // adding an editable anchor. Returns true if a node was inserted.
    _freehandInsertNodeAt: function(shape, pt) {
      var nodes = shape.geometry && shape.geometry.nodes;
      if (!nodes || nodes.length < 2) return false;
      function add(a, b) { return [a[0] + b[0], a[1] + b[1]]; }
      function sub(a, b) { return [a[0] - b[0], a[1] - b[1]]; }
      function lerp(a, b, t) { return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]; }
      function cubicAt(p0, p1, p2, p3, t) {
        var mt = 1 - t, a = mt * mt * mt, b = 3 * mt * mt * t, c = 3 * mt * t * t, d = t * t * t;
        return [
          a * p0[0] + b * p1[0] + c * p2[0] + d * p3[0],
          a * p0[1] + b * p1[1] + c * p2[1] + d * p3[1]
        ];
      }
      var STEPS = 24;
      var bestDist = Infinity, bestSeg = -1, bestT = 0;
      for (var i = 0; i < nodes.length - 1; i++) {
        var n0 = nodes[i], n1 = nodes[i + 1];
        var p0 = n0.p, p1 = n0.hOut ? add(n0.p, n0.hOut) : n0.p;
        var p2 = n1.hIn ? add(n1.p, n1.hIn) : n1.p, p3 = n1.p;
        for (var s = 0; s <= STEPS; s++) {
          var t = s / STEPS;
          var q = cubicAt(p0, p1, p2, p3, t);
          var dx = q[0] - pt.x, dy = q[1] - pt.y;
          var d2 = dx * dx + dy * dy;
          if (d2 < bestDist) { bestDist = d2; bestSeg = i; bestT = t; }
        }
      }
      if (bestSeg < 0) return false;
      // Only insert if the click landed reasonably on the stroke.
      var tol = this._textDefaultBoxImagePx() * 0.8;
      if (bestDist > tol * tol) return false;

      var t = Math.min(0.999, Math.max(0.001, bestT));
      var historyBefore = this._snapshotShape(shape);
      var a0 = nodes[bestSeg], a1 = nodes[bestSeg + 1];
      var c0 = a0.p, c1 = a0.hOut ? add(a0.p, a0.hOut) : a0.p;
      var c2 = a1.hIn ? add(a1.p, a1.hIn) : a1.p, c3 = a1.p;
      var l1 = lerp(c0, c1, t), t12 = lerp(c1, c2, t), r3 = lerp(c2, c3, t);
      var l2 = lerp(l1, t12, t), r2 = lerp(t12, r3, t);
      var m = lerp(l2, r2, t);
      // Trim the neighbours' inner handles to the split (only where they had
      // one — a straight join keeps its null handle and stays straight).
      if (a0.hOut) a0.hOut = sub(l1, c0);
      if (a1.hIn) a1.hIn = sub(r3, c3);
      var newNode = { p: [m[0], m[1]], hIn: sub(l2, m), hOut: sub(r2, m), type: "smooth" };
      nodes.splice(bestSeg + 1, 0, newNode);

      // A pending node selection by index is now stale (indices shifted).
      this._clearVertexSelection();
      this._renderShape(shape);
      this._renderFreehandEditor(shape);
      if (shape.uuid) {
        this._emitChanged();
        this._pushUndo(shape.uuid, historyBefore, this._snapshotShape(shape));
      }
      return true;
    },

    // Returns image-px positions for each handle, in an order each kind's
    // drag handler can reference by index.
    _handlePositions: function(shape) {
      // A turned box: its handles sit on its turned corners.
      if (!this._handlesUnturned && this._shapeRot && this._shapeRot(shape)) {
        this._handlesUnturned = true;
        var local;
        try { local = this._handlePositions(shape); } finally { this._handlesUnturned = false; }
        // A callout's or any kind's index 0 that is not a corner keeps its
        // place — only box kinds turn, and all of theirs are corners.
        return this._rotPts(shape, local);
      }
      var g = shape.geometry;
      switch (shape.kind) {
        case "image":
        case "rectangle":
        case "audio":
        case "video":
          return [
            { x: g.x,         y: g.y },          // 0: top-left
            { x: g.x + g.w,   y: g.y },          // 1: top-right
            { x: g.x + g.w,   y: g.y + g.h },    // 2: bottom-right
            { x: g.x,         y: g.y + g.h }     // 3: bottom-left
          ];
        case "text": {
          // The four corners are TEXT-resize dots, and those are opt-in
          // (the ⋯ toggle, same as a label's): the Label size input is
          // the primary way to size text, and dots that only duplicate a
          // number the panel already offers stay hidden until asked for.
          //
          // A text BOX is the exception: its box and its font are
          // independent (style.box: "fixed"), so the corners are the only
          // way to resize the box at all — they always show.
          // Point text always has its eight: scaling and stretching it is
          // what its handles are for (see `_resizePointText`).
          if (!this._textboxFixed(shape) && !this._isPointText(shape) &&
              !this._titleHandlesOn()) return [];
          // Handles ride the shrunk-to-text bbox so users grab where
          // they see the box, not the (often wider) storage envelope.
          var tBox = this._isPointText(shape) ? this._pointHandleBox(shape)
            : (shape._renderedBox || g);
          return [
            { x: tBox.x,           y: tBox.y           },
            { x: tBox.x + tBox.w,  y: tBox.y           },
            { x: tBox.x + tBox.w,  y: tBox.y + tBox.h  },
            { x: tBox.x,           y: tBox.y + tBox.h  }
          ];
        }
        case "circle":
          return [{ x: g.cx + g.r, y: g.cy }];   // 0: east, controls radius
        case "polygon":
          return (g.points || []).map(function(p) { return { x: p[0], y: p[1] }; });
        case "callout": {
          // Use the shrunk-to-text rendered bbox when available so the
          // 4 text-corner handles snap to what's drawn, not the wider
          // storage envelope.
          var cbox = shape._renderedBox || this._calloutTextBoxImage(g);
          var lab = this._calloutLabelScreen(shape);
          // Point words always have their corners, as point text does,
          // on the frame its handles keep (never a knot on a fresh one).
          if (this._isPointCallout(shape)) {
            var pb = this._pointHandleBox(shape, "callout");
            return [
              { x: g.anchor[0], y: g.anchor[1] },
              { x: pb.x,        y: pb.y        },
              { x: pb.x + pb.w, y: pb.y        },
              { x: pb.x + pb.w, y: pb.y + pb.h },
              { x: pb.x,        y: pb.y + pb.h }
            ];
          }
          // The anchor dot is POSITIONAL — where the callout points — and
          // always stays. The four text corners are text-resize dots and
          // ride the same ⋯ opt-in as every other text-sizing dot; index
          // 0 keeps meaning "anchor" either way, so the handle-drag
          // mapping is untouched.
          if (!this._titleHandlesOn()) {
            return [{ x: g.anchor[0], y: g.anchor[1] }];
          }
          // On a turned board the label stands upright, so its corners are
          // screen corners — taken from the label as drawn and carried back
          // into image px, so the dots land exactly on them.
          if (lab) {
            return [
              { x: g.anchor[0], y: g.anchor[1] },
              this._containerToImage({ x: lab.x,         y: lab.y }),
              this._containerToImage({ x: lab.x + lab.w, y: lab.y }),
              this._containerToImage({ x: lab.x + lab.w, y: lab.y + lab.h }),
              this._containerToImage({ x: lab.x,         y: lab.y + lab.h })
            ];
          }
          return [
            { x: g.anchor[0],            y: g.anchor[1]            },  // 0: anchor
            { x: cbox.x,                 y: cbox.y                 },  // 1: text TL
            { x: cbox.x + cbox.w,        y: cbox.y                 },  // 2: text TR
            { x: cbox.x + cbox.w,        y: cbox.y + cbox.h        },  // 3: text BR
            { x: cbox.x,                 y: cbox.y + cbox.h        }   // 4: text BL
          ];
        }
        case "dimension":
        case "line":
          return [
            { x: g.a[0], y: g.a[1] },  // 0: endpoint A
            { x: g.b[0], y: g.b[1] }   // 1: endpoint B
          ];
        // 0 = tail, last = head, everything between = a bend the user can
        // drag to re-route. Same order as `_arrowPath`, which is what lets
        // `_applyHandleDrag` map an index straight onto it.
        case "arrow":
          return this._arrowPath(g);
        // Freehand (node format) is edited via the dedicated pen editor
        // (`_renderFreehandEditor`), not this flat vertex-handle list.
        // Legacy {points} freehand falls through here → delete and redraw.
        default:
          return [];
      }
    },

    _startHandleDrag: function(shape, idx, handleEl, e) {
      // Left button only. These handles sit above the canvas and swallow
      // the event, so without this a middle-press on one would start a
      // drag AND block the middle-drag pan from ever reaching Fresco.
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      try { handleEl.setPointerCapture(e.pointerId); } catch (_) {}
      handleEl.classList.add("is-dragging");
      // Drag starts under the cursor — the tooltip is now anchored to a
      // stale shape position, so hide it for the duration and bring it
      // back on release.
      this._hideTooltip();
      // The midpoint tracker listens on `document`, so it keeps firing
      // through this drag and lights up whichever segment the pointer
      // sweeps past — a dot flashing on and off next to the point being
      // dragged, offering to add a bend nobody asked for. The user is
      // committed to moving this handle; nothing else should be on offer
      // until they let go.
      this._suspendMidpointHighlight();

      var shiftHeld = !!e.shiftKey;
      var self = this;
      // Full snapshot of the shape's pre-drag state for the undo stack
      // — captured before any mutation so the inverse op can restore
      // exactly what was on screen.
      var historyBefore = self._snapshotShape(shape);
      // Snapshot the starting geometry so corner drags derive from the
      // *original* opposite corner, not the live one that's moving.
      // Text shapes + callouts snap their handles to the shrunk-to-
      // text bbox (`_renderedBox`); the drag math has to start from
      // there too or the cursor and the bbox edge will diverge.
      // Callouts use FULL geometry (not _renderedBox) for drag math
      // so the corner-resize delta accumulates against the user-set
      // text_box rather than the shrunk visual. Without this, every
      // drag bakes the shrink-fit offset back into geometry and the
      // callout visibly shrinks on each interaction. _applyHandleDrag
      // gets startPt for callout text-corner drags and uses the
      // pointer DELTA against startGeom; visual continues to
      // shrink-fit independently. Text shapes still snap geometry to
      // the rendered box on release (existing 0.2.x behavior).
      // A point callout's index 0 is its anchor, which still just moves;
      // 1-4 are its words' corners.
      var pointCorner = self._isPointCallout(shape) ? idx - 1 : idx;
      var pointStart = self._isPointFrame(shape) && pointCorner >= 0
        ? self._pointTextDragStart(shape) : null;
      var startGeom;
      if (shape.kind === "text" && shape._renderedBox) {
        startGeom = JSON.parse(JSON.stringify(shape._renderedBox));
      } else {
        startGeom = JSON.parse(JSON.stringify(shape.geometry));
      }
      var startPt = self._toImage(e);
      var dragged = false;
      // A turned box resizes in its own frame (see `_recenterRotated`).
      var dragRot = self._shapeRot(shape);
      var dragC0 = dragRot ? self._rotCenter(shape) : null;
      var startPtL = dragRot ? self._rotatePoint(startPt, dragC0.x, dragC0.y, -dragRot) : startPt;

      function onMove(ev) {
        var ptW = self._toImage(ev);
        var pt = dragRot ? self._rotatePoint(ptW, dragC0.x, dragC0.y, -dragRot) : ptW;
        if (!dragged) {
          // 3px screen-space dead zone — distinguishes "drag to
          // resize" from "I'm just clicking on a handle". Without
          // this, a bare click on a text-shape or callout handle
          // falls through to onUp and snaps geometry to the shrunk
          // `_renderedBox`, then round-trips through the LiveView.
          // Each click trims a little off the box, so the callout /
          // text shape visibly shrinks every time the user touches
          // a handle. Same gating the body-drag and title-drag
          // handlers already use.
          var aC = self._imageToContainer(startPt);
          var bC = self._imageToContainer(ptW);
          if ((bC.x - aC.x) * (bC.x - aC.x) + (bC.y - aC.y) * (bC.y - aC.y) < 9) return;
          dragged = true;
        }
        // Read live rather than from the pointerdown: the user can press or
        // release Shift part-way through a drag and expect it to take.
        if (pointStart) self._resizePointText(shape, pointStart, pointCorner, pt, !!ev.shiftKey);
        else self._applyHandleDrag(shape, idx, pt, startGeom, startPtL, !!ev.shiftKey);
        if (dragRot) self._recenterRotated(shape, dragC0, dragRot);
        self._renderShape(shape);
        self._positionAllHandles(shape);
        if (pointStart) self._syncPointSize();
      }
      function onUp(ev) {
        handleEl.classList.remove("is-dragging");
        handleEl.removeEventListener("pointermove", onMove);
        handleEl.removeEventListener("pointerup", onUp);
        handleEl.removeEventListener("pointercancel", onUp);
        try { handleEl.releasePointerCapture(ev.pointerId); } catch (_) {}
        self._resumeMidpointHighlight();
        // An arrow endpoint drag paints the target's anchors as it goes;
        // they're a drag-time readout and shouldn't outlive the release.
        self._removeTargetDots();
        if (!dragged) {
          // Pure click on a vertex — select it so Backspace / Delete
          // can remove just that point instead of the whole shape.
          // Polygon-only for now (rectangles can't lose corners,
          // circle has no vertices, freehand has too many).
          // Shift-click toggles the vertex in the selection set so the
          // user can pick multiple points to delete in one keystroke.
          if (shape.kind === "polygon") {
            self._selectVertex(shape, idx, shiftHeld);
          } else if (shape.kind === "arrow") {
            // A click on a bend removes it — the inverse of dragging a
            // midpoint ghost to add one, so a route can be corrected with
            // the same two gestures that built it. No selection step: a
            // bend carries no state worth selecting, and requiring a
            // follow-up keystroke to undo a misplaced click would be a
            // worse trade than ⌘Z.
            self._deleteArrowBend(shape, idx);
          }
          self._showTooltipFor(shape);
          return;
        }
        // Drag committed — any prior vertex selection is stale.
        self._clearVertexSelection();
        // Text shapes still persist the shrunk-to-text bbox so the
        // stored geometry matches what's drawn — they don't have
        // the callout's drag-math complexity (no anchor, geometry
        // IS the box). Callouts intentionally skip this snap: the
        // delta-based drag math in _applyHandleDrag already keeps
        // the user-set text_box intact, and snapping to _renderedBox
        // would re-introduce the shrink cascade across drags.
        if (shape.kind === "text" && shape._renderedBox) {
          shape.geometry = {
            x: shape._renderedBox.x,
            y: shape._renderedBox.y,
            w: shape._renderedBox.w,
            h: shape._renderedBox.h
          };
        }
        if (shape.uuid) {
          self._emitChanged();
          self._pushUndo(shape.uuid, historyBefore, self._snapshotShape(shape));
        }
        self._showTooltipFor(shape);
      }
      handleEl.addEventListener("pointermove", onMove);
      handleEl.addEventListener("pointerup", onUp);
      handleEl.addEventListener("pointercancel", onUp);
    },

    // Translate the whole shape via a body grab. Mirrors the handle-drag
    // flow but applies a uniform offset to every geometry field. Uses a
    // small dead-zone so a stationary click on the shape body doesn't
    // emit a no-op `etcher:updated` event.
    // -------------------------------------------------------------------------
    // Snap & alignment guides — while a shape's body is dragged, its edges
    // and centers magnetize to the edges and centers of every other shape
    // within a few screen px, and cyan guide lines show what aligned with
    // what. Candidates are collected ONCE at drag start (nothing else
    // moves during a drag), and ⌘/Ctrl held bypasses snapping entirely —
    // the standard escape hatch for "no, I really want it 2px off".
    // -------------------------------------------------------------------------

    // The best snap correction for a bbox at its tentative position, per
    // axis: the smallest delta (within `thr`) that lands one of the box's
    // [start, center, end] on one of a candidate's. `axes` masks an axis
    // out — a shift-locked drag must not be nudged off its lock.
    _computeSnap: function(box, candidates, thr, axes) {
      function xs(b) { return [b.x, b.x + b.w / 2, b.x + b.w]; }
      function ys(b) { return [b.y, b.y + b.h / 2, b.y + b.h]; }
      var best = { x: null, y: null };
      function consider(axis, dv, cv) {
        var d = cv - dv;
        if (Math.abs(d) > thr) return;
        if (best[axis] === null || Math.abs(d) < Math.abs(best[axis].delta)) {
          best[axis] = { delta: d, coord: cv };
        }
      }
      for (var i = 0; i < candidates.length; i++) {
        var c = candidates[i];
        var j, k;
        if (!axes || axes.x) {
          var bx = xs(box), cx = xs(c);
          for (j = 0; j < 3; j++) for (k = 0; k < 3; k++) consider("x", bx[j], cx[k]);
        }
        if (!axes || axes.y) {
          var by = ys(box), cy = ys(c);
          for (j = 0; j < 3; j++) for (k = 0; k < 3; k++) consider("y", by[j], cy[k]);
        }
      }
      return best;
    },

    // The guide lines for a SNAPPED box: for each of its edge/center
    // coordinates that now matches a candidate's, one line at that
    // coordinate spanning from the box to the farthest matching candidate
    // — so the guide visibly connects everything that lines up, not just
    // the nearest neighbour.
    _snapGuidesFor: function(box, candidates) {
      var EPS = 0.01;
      var guides = [];
      function collect(axis, coords, spanLo, spanHi, candCoords, candLo, candHi) {
        coords.forEach(function(v) {
          var lo = spanLo, hi = spanHi, matched = false;
          candidates.forEach(function(c) {
            var cc = candCoords(c);
            for (var i = 0; i < 3; i++) {
              if (Math.abs(cc[i] - v) < EPS) {
                matched = true;
                lo = Math.min(lo, candLo(c));
                hi = Math.max(hi, candHi(c));
                break;
              }
            }
          });
          if (matched) guides.push({ axis: axis, at: v, from: lo, to: hi });
        });
      }
      collect("v", [box.x, box.x + box.w / 2, box.x + box.w],
        box.y, box.y + box.h,
        function(c) { return [c.x, c.x + c.w / 2, c.x + c.w]; },
        function(c) { return c.y; }, function(c) { return c.y + c.h; });
      collect("h", [box.y, box.y + box.h / 2, box.y + box.h],
        box.x, box.x + box.w,
        function(c) { return [c.y, c.y + c.h / 2, c.y + c.h]; },
        function(c) { return c.x; }, function(c) { return c.x + c.w; });
      return guides;
    },

    // Every other shape's bbox, collected once at drag start. Locked
    // shapes ARE candidates — they can't move, which makes them the most
    // reliable reference geometry on the board. Strip mode snaps within
    // the same page only: cross-page coordinates share numbers but not
    // meaning.
    _snapCandidatesFor: function(dragged) {
      var self = this;
      var out = [];
      (this.shapes || []).forEach(function(s) {
        if (!s || !s.el || s === dragged) return;
        if (s.image_idx !== dragged.image_idx) return;
        var box = self._shapeBBoxImagePx(s);
        if (box) out.push(box);
      });
      return out;
    },

    _renderSnapGuides: function(guides) {
      var self = this;
      this._snapGuideEls = this._snapGuideEls || [];
      while (this._snapGuideEls.length < guides.length) {
        var ln = svgEl("line");
        ln.classList.add("etcher-snap-guide");
        this.svg.appendChild(ln);
        this._snapGuideEls.push(ln);
      }
      while (this._snapGuideEls.length > guides.length) {
        var extra = this._snapGuideEls.pop();
        if (extra.parentNode) extra.parentNode.removeChild(extra);
      }
      guides.forEach(function(g, i) {
        var el = self._snapGuideEls[i];
        var a, b;
        if (g.axis === "v") {
          a = self._imageToContainer({ x: g.at, y: g.from });
          b = self._imageToContainer({ x: g.at, y: g.to });
        } else {
          a = self._imageToContainer({ x: g.from, y: g.at });
          b = self._imageToContainer({ x: g.to, y: g.at });
        }
        el.setAttribute("x1", a.x);
        el.setAttribute("y1", a.y);
        el.setAttribute("x2", b.x);
        el.setAttribute("y2", b.y);
      });
    },

    _clearSnapGuides: function() {
      (this._snapGuideEls || []).forEach(function(el) {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
      this._snapGuideEls = null;
    },

    // Duplicate a shape exactly in place and return the copy — the
    // alt-drag half of duplication: the copy is what the drag then moves,
    // the original stays put. Rides `_addShape` (render, interactions,
    // server emit) plus a create-undo entry, same as the ⌘D path.
    _cloneShapeInPlace: function(shape) {
      function clone(v) {
        if (v == null) return v;
        try { return JSON.parse(JSON.stringify(v)); } catch (_) { return v; }
      }
      var payload = { kind: shape.kind, geometry: clone(shape.geometry) };
      if (shape.style != null) payload.style = clone(shape.style);
      if (shape.metadata != null) payload.metadata = clone(shape.metadata);
      if (typeof shape.image_idx === "number") payload.image_idx = shape.image_idx;
      if (typeof shape.image_id === "string") payload.image_id = shape.image_id;
      var uuid = this._addShape(payload);
      if (!uuid) return null;
      var made = this.shapes.find(function(s) { return s.uuid === uuid; });
      if (made) this._pushUndoCreate(made);
      return made || null;
    },

    _startShapeMove: function(shape, e) {
      // Tapping the same spot again reaches for whatever is underneath —
      // without this, a shape covered by a larger one can never be selected,
      // and so can never be arranged out from under it.
      //
      // While a shape holds the focus, the walk happens only on a TAP: a
      // press that goes on to drag still moves the shape it landed on, so
      // grabbing the selected shape a second time never catches the one
      // underneath it instead.
      var cycled = this._cycleTapTarget(shape, e);
      var tapCycle = null;
      if (cycled !== shape && this._armedFocusBars(cycled)) tapCycle = cycled;
      else shape = cycled;
      // Locked shape: no drag-to-move. Fall through to the tap path so a
      // click still pins the tooltip (browse-mode behavior).
      if (shape.readonly) { this._onShapeTap(shape); return; }
      // Alt-grab drags a COPY — the tldraw/Figma duplication gesture. The
      // clone is created in place before the drag starts, so the rest of
      // this function simply moves it; the original never moves, and the
      // clone conveniently snaps against it (candidates exclude only the
      // dragged shape). Alt read at grab time: it decides WHAT is dragged,
      // which cannot change mid-gesture.
      if (e.altKey && shape.uuid) {
        var altCopy = this._cloneShapeInPlace(shape);
        if (altCopy) shape = altCopy;
      }
      var self = this;
      var el = shape.el;
      // Strip mode: make sure handle / drag-preview elements created
      // during the move land in the same per-image overlay as the
      // shape being dragged.
      self._activateOverlayForShape(shape);
      var startPt = self._toImage(e);
      var startGeom = JSON.parse(JSON.stringify(shape.geometry));
      // Full pre-move snapshot for the undo stack.
      var historyBefore = self._snapshotShape(shape);

      // Select on grab — `_onShapeTap` enters edit mode (annotation
      // cursor mode) or pins the tooltip (browse mode), idempotent if
      // we're already in edit mode for this shape. This used to fire
      // only from onUp's no-drag fallback, which meant dragging never
      // visually selected the shape until release. Calling here makes
      // the handles appear the instant the user grabs, so drag feels
      // like "select and move" rather than "move then select."
      self._onShapeTap(shape);
      // If the shape carries a title bbox, snapshot it too so we can
      // translate the title alongside the shape on body-grab.
      var startTitleBox =
        shape.metadata && shape.metadata.title_box
          ? Object.assign({}, shape.metadata.title_box)
          : null;
      // Callout: a grab that lands on the TEXT BOX moves only the box —
      // the anchor marks what the callout points at, and dragging the
      // label to a better spot must not drag the pointer off its target.
      // Grabbing the leader line or the anchor dot still moves the whole
      // shape. Hit-tested against the shrunk-to-text rendered box when
      // available, so the grab region matches what is drawn rather than
      // the (often wider) storage envelope.
      var calloutBoxGrab = false;
      if (shape.kind === "callout") {
        var grabBox = shape._renderedBox || self._calloutTextBoxImage(startGeom);
        calloutBoxGrab = !!grabBox &&
          startPt.x >= grabBox.x && startPt.x <= grabBox.x + grabBox.w &&
          startPt.y >= grabBox.y && startPt.y <= grabBox.y + grabBox.h;
      }
      // Snap setup — every other shape's bbox, taken once. Only when the
      // user (or host) has snapping ON — it is an acquired taste, off by
      // default like the connector anchors. The callout box-only drag
      // doesn't snap either way: its bbox includes the anchor it is
      // deliberately NOT moving, so edge alignment would lie.
      var snapStartBox = (calloutBoxGrab || !self._snapOn())
        ? null
        : self._shapeBBoxImagePx(shape);
      var snapCandidates = snapStartBox ? self._snapCandidatesFor(shape) : [];
      var dragged = false;
      try { el.setPointerCapture(e.pointerId); } catch (_) {}

      function onMove(ev) {
        var pt = self._toImage(ev);
        if (!dragged) {
          var a = self._imageToContainer(startPt);
          var b = self._imageToContainer(pt);
          var sdx = b.x - a.x, sdy = b.y - a.y;
          // 3px screen-space dead zone — distinguishes "drag to move"
          // from "I'm just hovering" so a stationary click on an
          // already-editing shape doesn't fire a network round-trip.
          if (sdx * sdx + sdy * sdy < 9) return;
          dragged = true;
          el.classList.add("is-moving");
          // The tooltip is anchored to the shape's old position — it
          // would float orphaned while the shape moves underneath.
          // Hide it for the duration; reshow on release.
          self._hideTooltip();
          // Same reason as the handle drags: the doc-level tracker would
          // otherwise light up midpoints under the travelling pointer.
          self._suspendMidpointHighlight();
        }
        var dxI = pt.x - startPt.x;
        var dyI = pt.y - startPt.y;
        // Shift locks the move to whichever axis has traveled further —
        // the standard "keep it on the same line" gesture. Read per move
        // event so the lock engages and releases with the key.
        var lockedAxis = null;
        if (ev.shiftKey) {
          if (Math.abs(dxI) > Math.abs(dyI)) { dyI = 0; lockedAxis = "y"; }
          else { dxI = 0; lockedAxis = "x"; }
        }
        // Snap the tentative position to nearby shapes' edges/centers.
        // ⌘/Ctrl bypasses; a shift-locked axis is never nudged off its
        // lock. Threshold in screen px so the pull feels the same at
        // every zoom.
        if (snapStartBox && snapCandidates.length && !ev.metaKey && !ev.ctrlKey) {
          var snScale = 1;
          try { snScale = self._markerScale() || 1; } catch (_) {}
          var tentative = {
            x: snapStartBox.x + dxI, y: snapStartBox.y + dyI,
            w: snapStartBox.w, h: snapStartBox.h
          };
          var sn = self._computeSnap(
            tentative, snapCandidates,
            SNAP_THRESHOLD_PX / (snScale > 0 ? snScale : 1),
            { x: lockedAxis !== "x", y: lockedAxis !== "y" }
          );
          if (sn.x) dxI += sn.x.delta;
          if (sn.y) dyI += sn.y.delta;
          if (sn.x || sn.y) {
            var snapped = {
              x: snapStartBox.x + dxI, y: snapStartBox.y + dyI,
              w: snapStartBox.w, h: snapStartBox.h
            };
            self._renderSnapGuides(self._snapGuidesFor(snapped, snapCandidates));
          } else {
            self._clearSnapGuides();
          }
        } else {
          self._clearSnapGuides();
        }
        if (calloutBoxGrab) {
          // Box-only: the anchor stays pinned and the leader stretches.
          var cbStart = self._calloutTextBoxImage(startGeom);
          shape.geometry = {
            anchor: [startGeom.anchor[0], startGeom.anchor[1]],
            text_box: {
              x: cbStart.x + dxI,
              y: cbStart.y + dyI,
              w: cbStart.w,
              h: cbStart.h
            }
          };
        } else {
          shape.geometry = self._translateGeometry(shape.kind, startGeom, dxI, dyI);
        }
        if (startTitleBox) {
          shape.metadata = Object.assign({}, shape.metadata || {}, {
            title_box: {
              x: startTitleBox.x + dxI,
              y: startTitleBox.y + dyI,
              w: startTitleBox.w,
              h: startTitleBox.h
            }
          });
        }
        self._renderShape(shape);
        self._positionAllHandles(shape);
        // If the title moved along with the parent, the title-edit
        // handles (when title-edit-mode is active for this shape)
        // need to follow too.
        if (startTitleBox && self.editingTitleShape === shape) {
          self._positionAllTitleHandles(shape);
        }
      }
      function onUp(ev) {
        el.classList.remove("is-moving");
        el.removeEventListener("pointermove", onMove);
        el.removeEventListener("pointerup", onUp);
        el.removeEventListener("pointercancel", onUp);
        try { el.releasePointerCapture(ev.pointerId); } catch (_) {}
        self._clearSnapGuides();
        self._resumeMidpointHighlight();
        if (dragged) {
          // Sync stored title_box to the shrunk-to-text bbox after
          // the body translation, matching the handle/drag paths and
          // keeping storage aligned with what's drawn.
          if (startTitleBox && shape._renderedTitleImage) {
            shape.metadata = Object.assign({}, shape.metadata || {}, {
              title_box: {
                x: shape._renderedTitleImage.x,
                y: shape._renderedTitleImage.y,
                w: shape._renderedTitleImage.w,
                h: shape._renderedTitleImage.h
              }
            });
          }
          if (shape.uuid) {
            self._emitChanged();
            self._pushUndo(shape.uuid, historyBefore, self._snapshotShape(shape));
          }
          // Cursor is still over the shape (we just released it there),
          // so the user expects the tooltip to come back.
          self._showTooltipFor(shape);
        }
        if (!dragged && tapCycle) self._onShapeTap(tapCycle);
        // No-drag case is otherwise a no-op here — `_onShapeTap` already
        // fired at the top of _startShapeMove, so the shape is already
        // selected / pinned by the time we hit pointerup.
        //
        // A link card is NOT opened here. Under the cursor tool a click
        // selects — you want handles before you move or scale something —
        // so opening is the double-click, handled in `_docDblClick`. The
        // grabber has no selection to make, so there a single tap opens.
      }
      el.addEventListener("pointermove", onMove);
      el.addEventListener("pointerup", onUp);
      el.addEventListener("pointercancel", onUp);
    },

    _translateGeometry: function(kind, geom, dx, dy) {
      switch (kind) {
        case "image":
          // Same box move as a rectangle, but href rides along in geometry —
          // rebuild explicitly or the source is dropped on every drag.
          return { x: geom.x + dx, y: geom.y + dy, w: geom.w, h: geom.h, href: geom.href };
        case "audio":
        case "video":
          // Same trap as image: everything the card needs to render lives in
          // geometry, so a move that rebuilds only the box silently strips
          // the file, its name and its duration.
          return {
            x: geom.x + dx, y: geom.y + dy, w: geom.w, h: geom.h,
            href: geom.href, title: geom.title, duration: geom.duration
          };
        case "rectangle":
        case "text":
          return { x: geom.x + dx, y: geom.y + dy, w: geom.w, h: geom.h };
        case "circle":
          return { cx: geom.cx + dx, cy: geom.cy + dy, r: geom.r };
        case "polygon":
        case "freehand":
        case "marker":
          // Node-based freehand (fitted beziers): shift each node's anchor;
          // hIn/hOut stay untouched — they're offsets relative to the anchor.
          if (geom.nodes) {
            return {
              nodes: geom.nodes.map(function(n) {
                return Object.assign({}, n, { p: [n.p[0] + dx, n.p[1] + dy] });
              })
            };
          }
          // Point-based strokes (markers, legacy freehand) and polygons.
          return {
            points: (geom.points || []).map(function(p) {
              return [p[0] + dx, p[1] + dy];
            })
          };
        case "callout": {
          var cbox = this._calloutTextBoxImage(geom);
          return {
            anchor:   [geom.anchor[0] + dx, geom.anchor[1] + dy],
            text_box: { x: cbox.x + dx, y: cbox.y + dy, w: cbox.w, h: cbox.h }
          };
        }
        case "dimension":
        case "line":
          return {
            a: [geom.a[0] + dx, geom.a[1] + dy],
            b: [geom.b[0] + dx, geom.b[1] + dy]
          };
        case "arrow":
          // Bindings must survive the translation — dropping them here
          // would silently unglue a connector the moment anything moved
          // it. A bound end snaps back on the next render anyway, so
          // dragging a fully bound arrow's body is a no-op by
          // construction; only free ends and bends actually travel.
          return {
            a: [geom.a[0] + dx, geom.a[1] + dy],
            b: [geom.b[0] + dx, geom.b[1] + dy],
            points: (geom.points || []).map(function(p) {
              return [p[0] + dx, p[1] + dy];
            }),
            from: geom.from || null,
            to: geom.to || null
          };
        default:
          return geom;
      }
    },

    _applyHandleDrag: function(shape, idx, pt, startGeom, startPt, freeform) {
      switch (shape.kind) {
        case "image": {
          // Corner resize identical to a rectangle; only difference is href
          // must survive into the rebuilt geometry.
          var ig = startGeom;
          var iright = ig.x + ig.w, ibottom = ig.y + ig.h;
          var inx, iny, inw, inh;
          switch (idx) {
            case 0: inx = pt.x;  iny = pt.y;  inw = iright - pt.x;  inh = ibottom - pt.y; break;
            case 1: inx = ig.x;  iny = pt.y;  inw = pt.x - ig.x;    inh = ibottom - pt.y; break;
            case 2: inx = ig.x;  iny = ig.y;  inw = pt.x - ig.x;    inh = pt.y - ig.y;    break;
            case 3: inx = pt.x;  iny = ig.y;  inw = iright - pt.x;  inh = pt.y - ig.y;    break;
            default: return;
          }
          if (inw < 0) { inx += inw; inw = -inw; }
          if (inh < 0) { iny += inh; inh = -inh; }

          // A distorted photograph reads as a mistake in a way a distorted
          // rectangle doesn't, so a corner drag keeps the proportions it
          // started with. Shift releases that and stretches freely.
          //
          // The lock is against the shape's aspect at the START of this
          // drag, not the image's natural one: a picture the user has
          // deliberately stretched should keep the shape they gave it
          // rather than snap back the first time they resize it.
          if (!freeform && ig.w > 0 && ig.h > 0) {
            // The larger of the two ratios, so the box follows whichever
            // axis the pointer pulled further — dragging mostly sideways
            // scales by width, mostly downward by height.
            var scale = Math.max(inw / ig.w, inh / ig.h);
            inw = Math.max(1, ig.w * scale);
            inh = Math.max(1, ig.h * scale);
            // Re-anchor: the corner opposite the handle has to stay where
            // it was, or fitting the aspect drifts the whole image.
            if (idx === 0)      { inx = iright - inw; iny = ibottom - inh; }
            else if (idx === 1) { inx = ig.x;         iny = ibottom - inh; }
            else if (idx === 2) { inx = ig.x;         iny = ig.y;          }
            else                { inx = iright - inw; iny = ig.y;          }
          }
          shape.geometry = { x: inx, y: iny, w: inw, h: inh, href: startGeom.href };
          break;
        }
        case "audio":
        case "video": {
          var mg = startGeom;
          var mright = mg.x + mg.w, mbottom = mg.y + mg.h;
          var mx, my, mw, mh;
          switch (idx) {
            case 0: mx = pt.x; my = pt.y; mw = mright - pt.x; mh = mbottom - pt.y; break;
            case 1: mx = mg.x; my = pt.y; mw = pt.x - mg.x;   mh = mbottom - pt.y; break;
            case 2: mx = mg.x; my = mg.y; mw = pt.x - mg.x;   mh = pt.y - mg.y;    break;
            case 3: mx = pt.x; my = mg.y; mw = mright - pt.x; mh = pt.y - mg.y;    break;
            default: return;
          }
          if (mw < 0) { mx += mw; mw = -mw; }
          if (mh < 0) { my += mh; mh = -mh; }

          // Video holds its proportions the way an image does — a stretched
          // picture reads as a mistake — with Shift to stretch anyway. Audio
          // does NOT: it's a control, not a picture, and making the card
          // wider without making it taller is a reasonable thing to want.
          if (shape.kind === "video" && !freeform && mg.w > 0 && mg.h > 0) {
            // Whichever axis was pulled further wins, so a mostly-sideways
            // drag scales by width and a mostly-downward one by height.
            var mscale = Math.max(mw / mg.w, mh / mg.h);
            mw = Math.max(1, mg.w * mscale);
            mh = Math.max(1, mg.h * mscale);
            // The corner opposite the handle stays put, or fitting the
            // aspect walks the whole shape across the canvas.
            if (idx === 0)      { mx = mright - mw; my = mbottom - mh; }
            else if (idx === 1) { mx = mg.x;        my = mbottom - mh; }
            else if (idx === 2) { mx = mg.x;        my = mg.y;         }
            else                { mx = mright - mw; my = mg.y;         }
          }

          // A floor in IMAGE px, scaled by zoom, so a card can't be dragged
          // down to something too small to grab a handle on and therefore
          // impossible to undo by dragging.
          var floorPx = 24;
          try { floorPx = 24 / (this._markerScale() || 1); } catch (_) {}
          if (mw < floorPx) {
            if (idx === 0 || idx === 3) mx = mright - floorPx;
            mw = floorPx;
          }
          if (mh < floorPx) {
            if (idx === 0 || idx === 1) my = mbottom - floorPx;
            mh = floorPx;
          }

          // Everything the card renders from lives in geometry, so rebuild
          // it explicitly — the same trap `_translateGeometry` has, where a
          // resize that returned only the box would strip the file, its name
          // and its duration.
          shape.geometry = {
            x: mx, y: my, w: mw, h: mh,
            href: mg.href, title: mg.title, duration: mg.duration
          };
          break;
        }
        case "rectangle":
        case "text": {
          var g = startGeom;
          var right = g.x + g.w, bottom = g.y + g.h;
          var nx, ny, nw, nh;
          switch (idx) {
            case 0: nx = pt.x;  ny = pt.y;  nw = right - pt.x;  nh = bottom - pt.y; break;
            case 1: nx = g.x;   ny = pt.y;  nw = pt.x - g.x;    nh = bottom - pt.y; break;
            case 2: nx = g.x;   ny = g.y;   nw = pt.x - g.x;    nh = pt.y - g.y;    break;
            case 3: nx = pt.x;  ny = g.y;   nw = right - pt.x;  nh = pt.y - g.y;    break;
            default: return;
          }
          // Normalize when the user drags a corner past its opposite.
          if (nw < 0) { nx += nw; nw = -nw; }
          if (nh < 0) { ny += nh; nh = -nh; }
          shape.geometry = { x: nx, y: ny, w: nw, h: nh };
          // A text shape IS its box, so resizing it is the drag-to-size
          // gesture and takes over from a pinned font size. A text BOX is
          // the opposite contract: the box and the font never derive from
          // each other, so dragging its corners reflows the text at the
          // size it already has.
          if (shape.kind === "text" && !this._textboxFixed(shape)) {
            this._unpinFontSize(shape);
          }
          break;
        }
        case "circle": {
          var dx = pt.x - startGeom.cx, dy = pt.y - startGeom.cy;
          shape.geometry = {
            cx: startGeom.cx,
            cy: startGeom.cy,
            r: Math.max(1, Math.sqrt(dx * dx + dy * dy))
          };
          break;
        }
        case "polygon": {
          var pts = (startGeom.points || []).map(function(p) { return [p[0], p[1]]; });
          if (pts[idx]) pts[idx] = [pt.x, pt.y];
          shape.geometry = { points: pts };
          break;
        }
        case "dimension":
        case "line": {
          // idx 0 = endpoint A, 1 = endpoint B. Each handle moves
          // its own endpoint to the pointer; the other end stays
          // anchored. No shrink-fit envelope, so absolute pt is
          // safe (no need for the delta math callouts use). Shared
          // between dimension + line — same two-endpoint geometry.
          var dimGeom = startGeom;
          if (idx === 0) {
            shape.geometry = { a: [pt.x, pt.y], b: [dimGeom.b[0], dimGeom.b[1]] };
          } else if (idx === 1) {
            shape.geometry = { a: [dimGeom.a[0], dimGeom.a[1]], b: [pt.x, pt.y] };
          }
          break;
        }
        case "arrow": {
          var arGeom = startGeom;
          var arBends = (arGeom.points || []).map(function(p) {
            return [p[0], p[1]];
          });
          var arLast = arBends.length + 1;
          if (idx > 0 && idx < arLast) {
            // A bend: it just follows the pointer. Bends are free points
            // with nothing to bind to, so no snap search here.
            arBends[idx - 1] = [pt.x, pt.y];
            shape.geometry = {
              a: [arGeom.a[0], arGeom.a[1]], b: [arGeom.b[0], arGeom.b[1]],
              points: arBends,
              from: arGeom.from || null, to: arGeom.to || null
            };
            break;
          }
          // An endpoint. Same drag as line/dimension, plus re-binding: the
          // end detaches from whatever it was on and re-attaches to whatever
          // anchor it's released near. Without this the handle would fight
          // the binding — the drag would move `a`/`b`, and the next render
          // would snap it straight back to the old anchor.
          //
          // Only while connectors are ON. This drag used to search and
          // paint anchors unconditionally, so with magnetic mode off the
          // dots stayed away while DRAWING an arrow (that path starts from
          // a dot, which off never shows) and then appeared the moment an
          // endpoint was dragged — the toggle applied to one gesture and
          // not the other. Off now means off everywhere: no dots, no snap,
          // no re-bind; the endpoint goes exactly where the pointer says.
          var arState = this._connectorsOn()
            ? this._arrowSnapState(pt, shape.uuid)
            : { candidate: null, snap: null };
          this._paintTargetDots(arState);
          var arSnap = arState.snap;
          var arEnd = arSnap ? { x: arSnap.x, y: arSnap.y } : pt;
          var arBind = arSnap
            ? { uuid: arSnap.shape.uuid, anchor: arSnap.anchor }
            : null;
          if (idx === 0) {
            shape.geometry = {
              a: [arEnd.x, arEnd.y], b: [arGeom.b[0], arGeom.b[1]],
              points: arBends,
              from: arBind, to: arGeom.to || null
            };
          } else if (idx === arLast) {
            shape.geometry = {
              a: [arGeom.a[0], arGeom.a[1]], b: [arEnd.x, arEnd.y],
              points: arBends,
              from: arGeom.from || null, to: arBind
            };
          }
          break;
        }
        case "callout": {
          // idx 0 = anchor (what's pointed at); idx 1-4 = text-bbox
          // corners (TL, TR, BR, BL), mirroring rectangle's resize
          // handlers but writing back into `geometry.text_box`.
          //
          // Callout corner drags use DELTA math (pt - startPt against
          // startGeom.text_box) instead of absolute pt against the
          // visual box. The visual rect shrink-wraps to the text
          // (smaller than `geometry.text_box`), so the user is
          // grabbing a handle at the SHRUNK corner. Absolute-pt math
          // would compute new geometry as (visual + delta), shrinking
          // geometry every drag. Delta math against the full
          // geometry preserves the user's drag intent: new geometry
          // = old geometry + delta. Anchor (idx 0) still uses
          // absolute pt — the anchor is rendered at its actual
          // position, no visual/storage offset.
          var startBox = this._calloutTextBoxImage(startGeom);
          if (idx === 0) {
            shape.geometry = {
              anchor: [pt.x, pt.y],
              text_box: { x: startBox.x, y: startBox.y, w: startBox.w, h: startBox.h }
            };
          } else if (this._canvasRotation() && startPt) {
            // Turned board: the label stands upright, so its corners move in
            // SCREEN directions — drag the right-hand dot right and the label
            // widens, whatever the rotation. Resize the upright screen box by
            // the screen delta, then write it back as an image box with the
            // same centre and size (`_orientedBox` is that mapping, inverted).
            var sb = this._orientedBox(startBox);
            var p0 = this._imageToContainer(startPt);
            var p1 = this._imageToContainer(pt);
            var dxS = p1.x - p0.x, dyS = p1.y - p0.y;
            var sx = sb.x, sy = sb.y, sw = sb.w, sh = sb.h;
            switch (idx) {
              case 1: sx += dxS; sy += dyS; sw -= dxS; sh -= dyS; break;
              case 2:            sy += dyS; sw += dxS; sh -= dyS; break;
              case 3:                       sw += dxS; sh += dyS; break;
              case 4: sx += dxS;            sw -= dxS; sh += dyS; break;
              default: return;
            }
            if (sw < 0) { sx += sw; sw = -sw; }
            if (sh < 0) { sy += sh; sh = -sh; }
            var scl = this._markerScale() || 1;
            var cImg = this._containerToImage({ x: sx + sw / 2, y: sy + sh / 2 });
            var iw = sw / scl, ih = sh / scl;
            shape.geometry = {
              anchor: startGeom.anchor,
              text_box: { x: cImg.x - iw / 2, y: cImg.y - ih / 2, w: iw, h: ih }
            };
            this._unpinFontSize(shape);
          } else {
            var dxC = startPt ? pt.x - startPt.x : 0;
            var dyC = startPt ? pt.y - startPt.y : 0;
            var nx, ny, nw, nh;
            switch (idx) {
              case 1: nx = startBox.x + dxC; ny = startBox.y + dyC; nw = startBox.w - dxC; nh = startBox.h - dyC; break;
              case 2: nx = startBox.x;       ny = startBox.y + dyC; nw = startBox.w + dxC; nh = startBox.h - dyC; break;
              case 3: nx = startBox.x;       ny = startBox.y;       nw = startBox.w + dxC; nh = startBox.h + dyC; break;
              case 4: nx = startBox.x + dxC; ny = startBox.y;       nw = startBox.w - dxC; nh = startBox.h + dyC; break;
              default: return;
            }
            if (nw < 0) { nx += nw; nw = -nw; }
            if (nh < 0) { ny += nh; nh = -nh; }
            shape.geometry = {
              anchor: startGeom.anchor,
              text_box: { x: nx, y: ny, w: nw, h: nh }
            };
            // Same gesture, same rule as a text shape's box: dragging it
            // hands the size back to the box.
            this._unpinFontSize(shape);
          }
          break;
        }
      }
    },

    _removeShape: function(uuid) {
      var idx = this.shapes.findIndex(function(s) { return s.uuid === uuid; });
      if (idx === -1) return;
      var shape = this.shapes[idx];
      if (this.editingShape === shape) this._exitEditMode();
      if (shape.el && shape.el.parentNode) shape.el.parentNode.removeChild(shape.el);
      if (shape.titleGroup && shape.titleGroup.parentNode) {
        shape.titleGroup.parentNode.removeChild(shape.titleGroup);
      }
      if (shape._badgeEl && shape._badgeEl.parentNode) {
        shape._badgeEl.parentNode.removeChild(shape._badgeEl);
        shape._badgeEl = null;
      }
      this.shapes.splice(idx, 1);
      // Removed shape's element can no longer fire mouseleave, so close
      // any tooltip that was anchored to it.
      this._hideTooltip();
    },

    // -------------------------------------------------------------------------
    // Undo / Redo — client-side history stack for in-session mutations
    // (geometry, style, metadata incl. title text + bbox). Creates and
    // deletes are intentionally NOT tracked: rolling them back would
    // require either preserving the server-assigned uuid through a
    // delete+recreate dance or a true server-side restore, both out of
    // scope for v1. The user can still cmd-z their corner drags, color
    // picks, title edits, and label repositions — the 90% case.
    //
    // Stack cap is intentionally short (50) so memory stays bounded
    // even on long editing sessions; older ops drop off the bottom.
    // -------------------------------------------------------------------------

    _undoStackLimit: 50,

    _snapshotShape: function(shape) {
      function clone(v) {
        if (v == null) return v;
        try { return JSON.parse(JSON.stringify(v)); } catch (_) { return v; }
      }
      return {
        geometry: clone(shape.geometry),
        style: clone(shape.style),
        metadata: clone(shape.metadata)
      };
    },

    // Push a state-snapshot pair onto the undo stack. Called by each
    // mutation site after the change is applied. Clearing the redo
    // stack on every push is standard "linear history" semantics —
    // doing anything new after an undo invalidates the redo chain.
    _pushUndo: function(uuid, before, after) {
      if (!uuid || !before || !after) return;
      this._undoStack = this._undoStack || [];
      this._redoStack = this._redoStack || [];
      this._undoStack.push({ type: "update", uuid: uuid, before: before, after: after });
      if (this._undoStack.length > this._undoStackLimit) this._undoStack.shift();
      this._redoStack = [];
      this._refreshUndoButtons();
    },

    // Compound delete op: snapshot every shape removed in one gesture
    // (a manual delete from the tooltip trash button is treated as a
    // bulk of size 1; the eraser tool sweeps multiple shapes into one
    // op). Each item carries its pre-deletion uuid + a `liveUuid`
    // In 0.3.x every shape has its uuid from the moment it's drawn
    // (client-side UUIDv7), so undo/redo of a bulk-delete can track
    // shapes by their permanent uuid — no more `tmpId` / `liveUuid` /
    // `pendingTmpId` dance waiting for the server to assign ids.
    _pushUndoBulkDelete: function(shapes) {
      this._undoStack = this._undoStack || [];
      this._redoStack = this._redoStack || [];
      function clone(v) {
        if (v == null) return v;
        try { return JSON.parse(JSON.stringify(v)); } catch (_) { return v; }
      }
      var items = shapes.map(function(shape) {
        var snapshot = {
          kind: shape.kind,
          geometry: clone(shape.geometry),
          style: clone(shape.style),
          metadata: clone(shape.metadata),
          originalUuid: shape.uuid
        };
        // Preserve the image tag so a redo-after-undo re-attaches the
        // shape to its page (strip) / host image (multi-image canvas)
        // instead of reviving it untagged.
        if (typeof shape.image_idx === "number") snapshot.image_idx = shape.image_idx;
        if (typeof shape.image_id === "string") snapshot.image_id = shape.image_id;
        return {
          snapshot: snapshot,
          // Set when this item gets recreated by undo; cleared on redo.
          // Lets a second redo find the live shape to delete again.
          liveUuid: null
        };
      });
      this._undoStack.push({ type: "bulk_delete", items: items });
      if (this._undoStack.length > this._undoStackLimit) this._undoStack.shift();
      this._redoStack = [];
      this._refreshUndoButtons();
    },

    // Create op — a freshly-drawn shape. Undo deletes it; redo recreates
    // it from the snapshot (bulk_delete's machinery, inverted). Without
    // this, drawing a shape leaves no history entry at all and the undo
    // button stays dead until the first drag/edit/delete.
    _pushUndoCreate: function(shape) {
      if (!shape || !shape.uuid) return;
      this._undoStack = this._undoStack || [];
      this._redoStack = this._redoStack || [];
      function clone(v) {
        if (v == null) return v;
        try { return JSON.parse(JSON.stringify(v)); } catch (_) { return v; }
      }
      var snapshot = {
        kind: shape.kind,
        geometry: clone(shape.geometry),
        style: clone(shape.style),
        metadata: clone(shape.metadata),
        originalUuid: shape.uuid
      };
      if (typeof shape.image_idx === "number") snapshot.image_idx = shape.image_idx;
      if (typeof shape.image_id === "string") snapshot.image_id = shape.image_id;
      this._undoStack.push({
        type: "create",
        item: { snapshot: snapshot, liveUuid: shape.uuid }
      });
      if (this._undoStack.length > this._undoStackLimit) this._undoStack.shift();
      this._redoStack = [];
      this._refreshUndoButtons();
    },

    // Shared deletion path for history ops (undo-of-create, redo-of-
    // delete): drop the live shape without recording a new history entry.
    _removeShapeForHistory: function(uuid) {
      if (!uuid) return;
      var shape = this.shapes.find(function(s) { return s.uuid === uuid; });
      if (!shape) return;
      if (this.editingShape === shape) this._exitEditMode();
      if (this.editingTitleShape === shape) this._exitTitleEditMode();
      var idx = this.shapes.indexOf(shape);
      if (idx !== -1) {
        if (shape.el && shape.el.parentNode) shape.el.parentNode.removeChild(shape.el);
        if (shape.titleGroup && shape.titleGroup.parentNode) {
          shape.titleGroup.parentNode.removeChild(shape.titleGroup);
        }
        this.shapes.splice(idx, 1);
      }
    },

    _undo: function() {
      this._undoStack = this._undoStack || [];
      this._redoStack = this._redoStack || [];
      var op = this._undoStack.pop();
      if (!op) return;
      if (op.type === "bulk_delete") {
        var self = this;
        op.items.forEach(function(item) {
          var uuid = self._recreateFromSnapshot(item.snapshot);
          item.liveUuid = uuid;
        });
        this._redoStack.push(op);
      } else if (op.type === "create") {
        this._removeShapeForHistory(op.item.liveUuid);
        op.item.liveUuid = null;
        this._emitChanged();
        this._redoStack.push(op);
      } else if (op.type === "update") {
        this._redoStack.push(op);
        this._applyHistorySnapshot(op.uuid, op.before);
      } else if (op.type === "reorder") {
        this._redoStack.push(op);
        this._applyShapeOrder(op.before);
        this._emitChanged();
      }
      this._refreshUndoButtons();
    },

    _redo: function() {
      this._undoStack = this._undoStack || [];
      this._redoStack = this._redoStack || [];
      var op = this._redoStack.pop();
      if (!op) return;
      if (op.type === "bulk_delete") {
        var self = this;
        op.items.forEach(function(item) {
          var uuid = item.liveUuid;
          if (!uuid) return;
          var shape = self.shapes.find(function(s) { return s.uuid === uuid; });
          if (!shape) return;
          if (self.editingShape === shape) self._exitEditMode();
          if (self.editingTitleShape === shape) self._exitTitleEditMode();
          var idx = self.shapes.indexOf(shape);
          if (idx !== -1) {
            if (shape.el && shape.el.parentNode) shape.el.parentNode.removeChild(shape.el);
            if (shape.titleGroup && shape.titleGroup.parentNode) {
              shape.titleGroup.parentNode.removeChild(shape.titleGroup);
            }
            self.shapes.splice(idx, 1);
          }
          item.liveUuid = null;
        });
        self._emitChanged();
        this._undoStack.push(op);
      } else if (op.type === "create") {
        op.item.liveUuid = this._recreateFromSnapshot(op.item.snapshot);
        this._undoStack.push(op);
      } else if (op.type === "update") {
        this._undoStack.push(op);
        this._applyHistorySnapshot(op.uuid, op.after);
      } else if (op.type === "reorder") {
        this._undoStack.push(op);
        this._applyShapeOrder(op.after);
        this._emitChanged();
      }
      this._refreshUndoButtons();
    },

    // Rebuild a freshly-deleted shape from its pre-deletion snapshot.
    // Returns the new uuid (so the undo's bulk_delete item can track it
    // for a subsequent redo). The original uuid is preserved in
    // `snap.originalUuid` — if a consumer wants to keep external state
    // tied to the same id across undo/redo, they can fish it out of the
    // emitted annotations list.
    _recreateFromSnapshot: function(snap) {
      if (!snap || !snap.kind || !snap.geometry) return null;
      var ann = {
        // Restore under the same uuid so re-deletes look like a no-op
        // from the consumer's perspective and tooltips don't get
        // re-keyed across an undo.
        uuid: snap.originalUuid || genUuidV7(),
        kind: snap.kind,
        geometry: snap.geometry,
        style: snap.style,
        metadata: snap.metadata
      };
      if (typeof snap.image_idx === "number") ann.image_idx = snap.image_idx;
      if (typeof snap.image_id === "string") ann.image_id = snap.image_id;
      this._renderAnnotation(ann);
      this._emitChanged();
      return ann.uuid;
    },

    // Apply a snapshot to a shape: restore local state and push the
    // updated annotations array. Tolerates a missing local shape —
    // happens if the row was deleted by another session.
    _applyHistorySnapshot: function(uuid, snap) {
      var shape = this.shapes.find(function(s) { return s.uuid === uuid; });
      if (!shape) return;
      if (snap.geometry != null) shape.geometry = JSON.parse(JSON.stringify(snap.geometry));
      shape.style = snap.style == null ? null : JSON.parse(JSON.stringify(snap.style));
      shape.metadata = snap.metadata == null ? null : JSON.parse(JSON.stringify(snap.metadata));
      this._renderShape(shape);
      // Markers are styled (zoom-scaled) by `_renderShape`; others recolor.
      if (shape.kind !== "marker" && shape.style && shape.style.color) {
        this._applyShapeColor(shape.el, shape.style.color);
      }
      if (this._isStrokeShape(shape.kind)) this._applyLineParams(shape.el, shape.style, this._markerScale());
      if (this.editingShape === shape) {
        // Rebuild rather than reposition: an undo/redo can change the node
        // count or a node's smooth/corner type, which the handle elements
        // need to reflect, not just their positions.
        if (this.freehandEditor) this._renderFreehandEditor(shape);
        else this._positionAllHandles(shape);
      }
      if (this.editingTitleShape === shape) this._positionAllTitleHandles(shape);

      this._emitChanged();
      this._syncPanBounds();
    },

    _refreshUndoButtons: function() {
      var u = (this._undoStack || []).length;
      var r = (this._redoStack || []).length;
      if (this.undoBtn) this.undoBtn.disabled = u === 0;
      if (this.redoBtn) this.redoBtn.disabled = r === 0;
      // Mirror onto the popup's history buttons so the overflow
      // copies disable in lockstep with the toolbar copies.
      if (this.popupUndoBtn) this.popupUndoBtn.disabled = u === 0;
      if (this.popupRedoBtn) this.popupRedoBtn.disabled = r === 0;
      // Fire a state-change event so consumers driving their own UI
      // off the public API can keep external undo/redo buttons in
      // sync without polling.
      this._dispatch("etcher:history-changed", { canUndo: u > 0, canRedo: r > 0 });
    }
  };
})();
